import GoingLivePanel from "./GoingLivePanel.jsx";
import UserCredits from "./UserCredits.jsx";
import EntryCreditSettingsPanel from "./EntryCreditSettingsPanel.jsx";
import SiteAccessPanel from "./SiteAccessPanel.jsx";
import LockInPanel from "./LockInPanel.jsx";
import TestConfigurationBox from "./TestConfigurationBox.jsx";
import RefundPolicyPanel from "./RefundPolicyPanel.jsx";
import AdminPaymentsPanel from "./AdminPaymentsPanel.jsx";
import React, { useEffect, useState, useRef } from "react";

// Every record and every admin action belongs to one of three modes.
// Kept in one place so the three names and the three colours can never
// disagree between a badge, a confirm dialog and a toast.
// Mirrors MODES in the backend deliberately: if the two ever disagree,
// a mode exists that one half of the app cannot see.
//
// DEMO LEFT THIS LIST ON 11 SEP 2026 — it is the set of modes an ADMIN
// CAN BE IN, and demo stopped being one of those.
const MODES = ["test", "live"];

// THE QUEUE PANEL IS A DIFFERENT QUESTION and keeps all three.
//
// There is still a demo QUEUE, still demo competitions, and still a
// Generate/Play button for them — what went is the ability to put the
// whole site into demo. Using MODES here would have deleted the demo
// row along with the switcher entry, which is the opposite of what the
// change was for.
const QUEUE_MODES = ["demo", "test", "live"];

// The competition tabs. The id and the label differ on purpose: the label
// says what happens NEXT rather than what went wrong.
//
// "To reschedule" is what the tab is FOR, which is the useful thing to
// say. It was "Not Run" (accusatory about something usually ordinary) and
// briefly "Reruns" (from a feature since removed — a competition that has
// run never comes back here). Everything in it is waiting for a date;
// once it has one it becomes Queued and leaves.
const COMPETITION_TABS = ["active", "queued", "not_run", "ended"];
const COMPETITION_TAB_LABEL = {
  active: "Active",
  queued: "Queued",
  not_run: "To reschedule",
  ended: "Ended",
};

// <input type="datetime-local"> speaks LOCAL time with no zone, so it
// cannot take a timestamp directly. These convert both ways and are the
// only place that conversion happens — doing it inline at each call site
// is how an off-by-one-timezone bug gets in.
function msToLocalInput(ms) {
  if (ms == null) return "";
  const d = new Date(ms);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
function localInputToMs(value) {
  if (!value) return null;
  const ms = new Date(value).getTime();
  return Number.isFinite(ms) ? ms : null;
}
const MODE_LABEL = { demo: "Demo", test: "Test", live: "Live" };
const MODE_COLOR = {
  demo: "var(--accent-yellow)",
  test: "var(--accent-blue)",
  live: "var(--border-color)",
};
// A record saved before modes existed reads as Live, matching the
// backend's own backfill. null is different again and means "nobody
// recorded a mode" — only the activity log has those, and it shows no
// badge at all rather than guessing.
const modeOf = (r) => (r && r.mode) || "live";
// The admin's OWN current mode. Defaults to "live" while modeStatus is
// still loading — matching the old boolean flag, which was false
// then. Written once because `effectiveMode !== "live"` on a null
// modeStatus would be TRUE, inverting every guard during load.
const adminMode = (ms) => ms?.effectiveMode || "live";

// "in 55 seconds" / "in 4 minutes" / "at 14:32".
//
// The queue panel used to say only "the queue is running", whether a
// competition was on screen or a minute away. Generated demo competitions
// are scheduled a MINUTE ahead, so the normal state right after
// generating is a running queue with nothing visible — which reads as
// broken.
//
// Falls back to a clock time beyond an hour, where a countdown stops
// being the useful form.
const countdownText = (at) => {
  const ms = at - Date.now();
  if (ms <= 0) return "any moment";
  const secs = Math.floor(ms / 1000);
  // Under a minute, words. Above it, m:ss — because this ticks every
  // second and "in 89 seconds" is not how a counting-down number reads.
  // Rounding to "in 2 minutes" was worse still: it sat on 2 for thirty
  // seconds and then jumped to 89.
  if (secs < 60) return `in ${secs} second${secs === 1 ? "" : "s"}`;
  if (secs < 3600) {
    const m = Math.floor(secs / 60);
    const rest = String(secs % 60).padStart(2, "0");
    return `in ${m}:${rest}`;
  }
  // Beyond an hour a countdown stops being the useful form.
  return `at ${siteTime(at)}`;
};
const modeLabel = (m) => MODE_LABEL[m] || "Live";
const modeColor = (m) => MODE_COLOR[m] || "var(--border-color)";
// A COMPETITION FROM ANOTHER MODE, LABELLED (1.437.0). With the Sitewide
// setting "List Test Competitions while in live mode" on, the finished-
// competition lists in Live also carry Test ones; this marks each of them.
// Nothing is shown for a row in the mode being viewed.
const OtherModeTag = ({ mode, viewing }) =>
  mode && mode !== viewing ? (
    <span
      title={`A ${modeLabel(mode)} competition, listed here by the Sitewide setting`}
      style={{ marginLeft: 6, fontSize: 11, padding: "0 6px", borderRadius: 4, border: `1px solid ${modeColor(mode)}`, color: modeColor(mode), whiteSpace: "nowrap" }}
    >
      {modeLabel(mode)}
    </span>
  ) : null;

import { NavLink, useNavigate } from "./Navigation.jsx";

import { useCurrencySymbol } from "./Currency.jsx";

import { wsRequest, onBroadcast, onMandatoryTotpRequired, triggerMandatoryTotpRequired, onSessionInvalid, triggerSessionInvalid } from "./wsClient.js";

import TotpSetup from "./TotpSetup.jsx";

import OtpCodeInput from "./OtpCodeInput.jsx";

import HtmlEditor from "./HtmlEditor.jsx";

import { showToast } from "./toast.js";

import { useTheme } from "./ThemeContext.jsx";

import TabBar from "./TabBar.jsx";

import { usePaginatedList } from "./usePaginatedList.js";
import DataModeFilter from "./DataModeFilter.jsx";

import ListDisplayControls from "./ListDisplayControls.jsx";

import CompetitionReplayModal from "./CompetitionReplayModal.jsx";
import AdminIssuesBell from "./AdminIssuesBell.jsx";
import GoLiveModal, { GoLiveChecklist } from "./GoLiveModal.jsx";
import EmailSettingsPanel from "./EmailSettingsPanel.jsx";

import { formatLongCountdown } from "./replayShared.jsx";

import { useAuthedImageUrl } from "./useAuthedImageUrl.js";
import { siteDate, siteDateTime, siteTime, setSiteTimeZone } from "./siteTime.js";



const API_BASE = ""; // same-origin — nginx proxies /api and /ws to the backend

const TOKEN_KEY = "589comps_admin_token";



// Every admin sub-tab (competitions, users, accounts, competition-

// management, sitewide, mock-data, server, activity-log) is a direct

// sibling here — e.g. /admin/competitions/stats, /admin/users,

// /admin/sitewide. There's only ever one visual top-level tab now

// (labeled "Root" for a root account, "Admin" for a regular admin — see

// availableSubTabs), so unlike the old /admin/root/accounts-style URLs,

// nothing wraps these in an extra segment for it. The URL is the single

// source of truth for which tab is showing; nothing duplicates it in

// separate component state, so back/forward and direct links both just

// work without any extra syncing logic.

function parseAdminPath(path) {

  const segments = (path || "").split("/").filter(Boolean); // ["admin", "competitions", "timeline"]

  const subTab = segments[1] || null;

  // Only meaningful when subTab is "competitions" — its own sub-tab.

  const competitionsSubTab = segments[2] || "timeline";

  // The id of the specific competition being charted, read when the

  // surrounding sub-tab is "competitionChart". Part of the URL (not local

  // component state) specifically so a page refresh doesn't lose it.

  //

  // Lives under competition-management now — /admin/competition-management/

  // competitionChart/<id> — since Chart moved there from the Competitions

  // tab. Same segment position either way, so one variable still covers it.

  const chartCompetitionId = segments[3] || null;

  // Only meaningful when subTab is "partners" — its own sub-tab, same

  // position/pattern as competitionsSubTab above (mutually exclusive with

  // it, since subTab is never both "competitions" and "partners" at once).

  const partnersSubTab = segments[2] || "keys";
  // Only meaningful when subTab is "finance" — same segment position and
  // pattern as partnersSubTab, and mutually exclusive with it. Defaults
  // to the credentials, since that is the first thing an operator sets
  // and the thing everything else here depends on.
  const financeSubTab = segments[2] || "providers";

  // Only meaningful when subTab is "testing" — same segment position as

  // partnersSubTab above, and mutually exclusive with it.

  const testingSubTab = segments[2] || "changelog";

  // Only meaningful when partnersSubTab is "api" - its own sub-tab, one
  // level deeper than partnersSubTab itself.
  //
  // THIS SEGMENT WAS PARSED AND READ BY NOTHING between 19 Sep 2026 and
  // 1.908.0. It used to carry an "API"/"Test" split, and defaulted to "live"
  // long after both of those tabs were removed; the comment here still
  // described the split as kept. It now carries Settings (the field
  // checkboxes) and Data points (the partner-facing catalogue).
  //
  // DEFAULTS TO THE CHECKBOXES, and anything unrecognised lands there too -
  // /admin/partners/api/live was a real bookmark until 19 Sep 2026, and it
  // still opens the panel it has always opened.
  const partnersApiSubTab = segments[3] || "settings";

  // Only meaningful when subTab is "email" — its own sub-tab, same

  // position/pattern as partnersSubTab above.

  const emailSubTab = segments[2] || "providers";

  // Only meaningful when subTab is "competition-management" — same

  // position/pattern again. Defaults to "settings", which is where all of

  // this tab's content lived before Competition History was added

  // alongside it, so an existing bookmark to /admin/competition-management

  // still lands exactly where it used to.

  const competitionManagementSubTab = segments[2] || "settings";
  // Only meaningful when subTab is "finance" (or its old id,
  // "payment-providers") - same position and pattern again. Defaults to
  // "providers" so an existing bookmark to /admin/payment-providers still
  // lands on the credentials screen it has always shown.
  const paymentsSubTab = segments[2] || "providers";

  return { subTab, competitionsSubTab, chartCompetitionId, partnersSubTab, partnersApiSubTab, emailSubTab, competitionManagementSubTab, testingSubTab, paymentsSubTab };

}



function useStoredToken() {

  // localStorage (not sessionStorage) so an admin login survives closing the

  // browser entirely, matching the backend's 30-day token lifetime.

  const [token, setTokenState] = useState(() => localStorage.getItem(TOKEN_KEY));

  const setToken = (t) => {

    if (t) localStorage.setItem(TOKEN_KEY, t);

    else localStorage.removeItem(TOKEN_KEY);

    setTokenState(t);

  };

  return [token, setToken];

}



function ChangePasswordWidget({ token }) {

  const [open, setOpen] = useState(false);

  const [form, setForm] = useState({ currentPassword: "", newPassword: "", confirmPassword: "" });

  const [busy, setBusy] = useState(false);

  const confirmPasswordRef = useRef(null);

  useEffect(() => {

    if (confirmPasswordRef.current) {

      confirmPasswordRef.current.setCustomValidity(form.confirmPassword && form.confirmPassword !== form.newPassword ? "Passwords don't match." : "");

    }

  }, [form.newPassword, form.confirmPassword]);



  const inputStyle = {

    padding: 6,

    marginBottom: 6,

    background: "var(--bg-input)",

    border: "1px solid var(--border-color-light)",

    borderRadius: 6,

    color: "var(--text-primary)",

    display: "block",

    width: "100%",

  };



  const submit = async (e) => {

    e.preventDefault();

    if (form.newPassword !== form.confirmPassword) {

      showToast("New password and confirmation don't match.", "error");

      return;

    }

    setBusy(true);

    try {

      await wsRequest("auth.changePassword", { currentPassword: form.currentPassword, newPassword: form.newPassword }, token);

      setForm({ currentPassword: "", newPassword: "", confirmPassword: "" });

      showToast("Password changed.");

    } catch (err) {

      showToast(err.message || "Could not reach backend.", "error");

    }

    setBusy(false);

  };



  if (!open) {

    return (

      <button onClick={() => setOpen(true)} style={{ fontSize: 12 }}>

        Change password

      </button>

    );

  }



  return (

    <form onSubmit={submit} style={{ textAlign: "left", width: 220, marginTop: 6 }}>

      <input

        type="password"

        placeholder="Current password"

        required

        value={form.currentPassword}

        onChange={(e) => setForm({ ...form, currentPassword: e.target.value })}

        style={inputStyle}

      />

      <input

        type="password"

        placeholder="New password"

        required

        minLength={8}

        value={form.newPassword}

        onChange={(e) => setForm({ ...form, newPassword: e.target.value })}

        style={inputStyle}

      />

      <input

        ref={confirmPasswordRef}

        type="password"

        placeholder="Confirm new password"

        required

        minLength={8}

        value={form.confirmPassword}

        onChange={(e) => setForm({ ...form, confirmPassword: e.target.value })}

        style={inputStyle}

      />

      <div style={{ display: "flex", gap: 6 }}>

        <button type="submit" disabled={busy}>

          {busy ? "Saving…" : "Save"}

        </button>

        <button type="button" onClick={() => setOpen(false)}>

          Cancel

        </button>

      </div>

    </form>

  );

}



// Site-wide financial summary shown at the top of the admin dashboard,

// between the title and the account controls — total profit, total prize

// money actually claimed, how many competitions have completed, and the

// average profit each one generates. Updates live via the same broadcast

// types the underlying figures are already tied to elsewhere in the admin

// panel (a competition resolving changes totalProfit/completedCompetitions,

// a purchase changes totalProfit, a prize being marked paid changes

// totalPrizeMoneyClaimed). Quietly renders nothing if the fetch fails

// (e.g. an admin without the manage_competitions permission this is gated

// behind, same as the Stats tab it's built from) rather than an error —

// a nice-to-have summary shouldn't disrupt the rest of the dashboard for

// an admin who isn't permitted to see it.

function DashboardSummaryBar({ token, modeStatus }) {

  const symbol = useCurrencySymbol();

  const [summary, setSummary] = useState(null);

  // LIVE OR TEST FIGURES (1.468.0). These add money up, so the one thing
  // that must never happen is a total nobody can place. The filter shows
  // one mode's, never a sum of both, and says so above the numbers.
  const [view, setView] = useState("live");




  // ONLY ASK WHILE THE SITE IS LIVE. The filter is not drawn in Test or
  // Demo - there is nothing to choose - but the state behind it still
  // says "live", and asking for Live from Test is refused by the same
  // rule that makes the filter pointless there. Sending nothing means
  // the admin's own mode, which is the right answer in every mode.
  const askMode = adminMode(modeStatus) === "live" ? view : undefined;

  const load = () => {

    wsRequest("admin.dashboardSummary.get", { mode: askMode }, token)

      .then(setSummary)

      .catch(() => {});

  };



  useEffect(load, [token, adminMode(modeStatus), askMode]);



  useEffect(() => onBroadcast((message) => {

    if (message.type === "competitions-changed" || message.type === "purchase") load();

  }), [token]);



  if (!summary) return null;



  const stat = (label, value) => (

    <div style={{ textAlign: "center" }}>

      <div style={{ fontSize: 18, fontWeight: 600 }}>{value}</div>

      <div style={{ fontSize: 11, opacity: 0.65, whiteSpace: "nowrap" }}>{label}</div>

    </div>

  );



  return (

    <div>

    <DataModeFilter value={view} onChange={setView} siteIsLive={adminMode(modeStatus) === "live"} what="figures" />

    <div className="admin-summary-grid">

      {stat("Total profit", `${symbol}${(summary.totalProfit || 0).toFixed(2)}`)}

      {stat("Prize money claimed", `${symbol}${(summary.totalPrizeMoneyClaimed || 0).toFixed(2)}`)}

      {stat("Competitions completed", summary.completedCompetitions)}

      {stat("Avg. profit/competition", `${symbol}${(summary.averageProfitPerCompetition || 0).toFixed(2)}`)}

    </div>

    </div>

  );

}



// Always-visible indicator of this admin's own current Live/Demo

// status — placed in the header area (not one specific sub-tab) since

// frozen-state treatment applies across every admin view, so the

// control to change it (or understand why it can't be changed right

// now) needs to be equally global. Hidden entirely for the common

// case (permanently Live, no permission, not forced) — nothing for

// that admin to ever do or need to know here. Receives status as a

// prop rather than fetching its own copy, since other views need this

// same information too (see AdminApp's own modeStatus state).

function LiveDemoModeBanner({ token, status, onStatusChange }) {

  const [busy, setBusy] = useState(false);
  // The mode being switched TO while its confirmation is showing, or null.
  // Leaving Live closes the public site, so it asks first; returning to
  // Live does not.
  const [confirming, setConfirming] = useState(null);



  const [showGoLive, setShowGoLive] = useState(false);

  const switchTo = async (next) => {
    if (!status || !status.canToggle || status.forcedByRoot) return;
    if (next === current) return;

    // GOING TO LIVE OPENS THE CHECKLIST FIRST.
    //
    // The server refuses anyway if anything is outstanding; this is so
    // root SEES what, rather than being refused and left guessing. The
    // modal does the switching itself once every row passes.
    //
    // Leaving Live is untouched — that is the emergency direction, and
    // the existing confirmation below covers it.
    if (next === "live") {
      setShowGoLive(true);
      return;
    }
    // LEAVING LIVE ASKS FIRST. Returning to it does not.
    //
    // Root's mode is what closes the public site: while it is not Live,
    // anyone without an address at the site's own domain sees a
    // maintenance notice and the public API answers 503. That is a large
    // consequence for a toggle that used to change only what THIS admin
    // saw.
    //
    // Going back to Live re-opens the site, which is never the dangerous
    // direction and must never sit behind a dialog — the same reasoning
    // as the live-payments switch.
    if (current === "live" && next !== "live" && confirming !== next) {
      setConfirming(next);
      return;
    }
    setConfirming(null);
    setBusy(true);
    try {
      const data = await wsRequest("admin.mode.setPreferred", { mode: next }, token);
      onStatusChange(data);
      showToast(`Switched to ${modeLabel(next)} mode.`);
    } catch (err) {
      showToast(err.message || "Could not reach backend.", "error");
    }
    setBusy(false);
  };

  if (!status || !(adminMode(status) !== "live" || status.canToggle)) return null;

  const current = adminMode(status);

  return (
    <>
      {/* THE GO-LIVE CHECKLIST, on root's Live button and nowhere else.
          It does the switching itself once every row passes. */}
      {showGoLive && (
        <GoLiveModal
          wsRequest={wsRequest}
          token={token}
          onClose={() => setShowGoLive(false)}
          onWentLive={(data) => data && onStatusChange(data)}
        />
      )}
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 12,
        padding: "8px 14px",
        borderRadius: 8,
        marginBottom: 12,
        border: `1px solid ${modeColor(current)}`,
        background:
          current === "demo"
            ? "rgba(232, 185, 77, 0.08)"
            : current === "test"
              ? "rgba(90, 155, 230, 0.08)"
              : "transparent",
        flexWrap: "wrap",
      }}
    >
      <strong style={{ fontSize: 13 }}>
        {current === "demo" ? "\u{1F7E0}" : current === "test" ? "\u{1F535}" : "\u{1F7E2}"} {modeLabel(current)} mode
      </strong>

      {/*
        Naming the mode is not enough on its own. An admin in Demo mode
        can create a competition, watch it start on the timeline, and
        then find nothing on the public site — because a demo
        competition is only ever visible to a demo session, and an
        ordinary visitor is never one. The same is true of Test.

        That is correct behaviour and it still misleads, because nothing
        connects the two facts at the moment it matters. These lines do.
      */}
      {current === "test" && (
        <span style={{ fontSize: 12.5, opacity: 0.75 }}>
          Test data — real providers using their <strong>test</strong> credentials, so no real money moves. But{" "}
          <strong>email and SMS are sent for real in Test mode, and SMS is charged.</strong>
        </span>
      )}

      {/* ---- THE OTHER LOCKDOWN, WHICH IS THE HALF THAT SURVIVES (1.904.0) ----

          A yellow line stood here for the flag: the mode-lockdown suppressed,
          the public site therefore reachable. The flag went in 1.903.0 and the
          key that gated this went with it, so that branch could not fire.

          BUT THE OTHER LOCKDOWN IS STILL REAL, and it was reported only INSIDE
          that dead branch - so taking the branch out took the one place on
          every admin screen that said the public site was closed by "Use API
          exclusively". A rule caught it. The placement argument was never about
          the flag: a state that changes what the PUBLIC can see belongs on the
          line an operator glances at, not only in a panel nobody visits by
          accident.

          So this says that, and nothing about the mode - the mode line above
          already carries it, and an inactive mode-lockdown means Live again
          exactly as it did before 1.274.0. (Deliberately does not spell the
          removed payload key, because a rule bans it and a comment quoting it
          would satisfy the ban. §92 of docs/mongodb-plan.md is the same
          lesson.) */}
      {status.publicSiteDisabled && (
        <span style={{ fontSize: 12.5, fontWeight: 600, color: "var(--accent-yellow)" }}>
          Public site CLOSED &mdash; "Use API exclusively" is on, so visitors see the API-only
          notice whatever mode the site is in. Competitions run on partner pages only.
        </span>
      )}

      {status.forcedByRoot && (
        <span style={{ fontSize: 12.5, opacity: 0.75 }}>
          Root has the entire site in {modeLabel(current)} mode right now — every admin sees this regardless of their
          own setting.
        </span>
      )}

      {!status.forcedByRoot && status.canToggle && (
        <span style={{ display: "flex", gap: 6, marginLeft: "auto" }}>
          {MODES.map((m) => (
            <button
              key={m}
              type="button"
              disabled={busy || m === current}
              onClick={() => switchTo(m)}
              title={
                m === current
                  ? `Already in ${modeLabel(m)} mode`
                  : `Switch to ${modeLabel(m)}`
              }
              style={{
                fontSize: 12,
                borderColor: m === current ? modeColor(m) : undefined,
                fontWeight: m === current ? 600 : undefined,
                opacity: m === current ? 1 : 0.8,
              }}
            >
              {modeLabel(m)}
            </button>
          ))}
        </span>
      )}

      {/* Names the consequence rather than asking "are you sure?" — the
          question is useless without knowing what happens, and what
          happens here is that the public site closes. */}
      {confirming && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.6)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
          }}
        >
          <div
            style={{
              background: "var(--bg-panel, #1c1c1c)",
              border: `1px solid ${modeColor(confirming)}`,
              borderRadius: 10,
              padding: 20,
              maxWidth: 480,
            }}
          >
            <h3 style={{ marginTop: 0 }}>Switch the site to {modeLabel(confirming)} mode?</h3>
            <p style={{ fontSize: 14 }}>
              <strong>This closes the public site.</strong> Anyone without an email address at
              this site&apos;s own domain will see a maintenance notice instead of the
              competition, and the public API will answer 503 — so a partner&apos;s widget shows
              maintenance too.
            </p>
            <p style={{ fontSize: 13, opacity: 0.8 }}>
              Admins and anyone at the site domain can still sign in. Switching back to Live
              re-opens everything immediately, with no confirmation.
            </p>
            <div style={{ display: "flex", gap: 10, marginTop: 16 }}>
              <button type="button" onClick={() => setConfirming(null)} disabled={busy}>
                Cancel
              </button>
              <button type="button" onClick={() => switchTo(confirming)} disabled={busy}>
                {busy ? "Switching\u2026" : `Yes, switch to ${modeLabel(confirming)}`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
    </>
  );
}



export default function AdminApp({ path }) {

  const { theme, toggleTheme, applyAccountTheme } = useTheme();

  const [token, setToken] = useStoredToken();


  const [user, setUser] = useState(null);

  // true for root always; otherwise checks editPermissions specifically

  // (not permissions, which only means view access) — see

  // wsRequireEditPermission's own reasoning on the backend. Passed down

  // as a canEdit prop so each tab can disable/hide its own create/edit/

  // delete controls for an admin who only has View level, rather than

  // showing full edit UI that the backend would then reject anyway.

  const canEdit = (perm) => user?.role === "root" || !!user?.editPermissions?.includes(perm);


  const [authChecked, setAuthChecked] = useState(false);

  // ---- IS THIS PAGE OLDER THAN THE SERVER IT IS TALKING TO? (1.709.0) ----
  //
  // __APP_VERSION__ is baked in at build time (vite.config.js defines it from
  // package.json), so it says what THIS BUNDLE was built from - not what the
  // box is running. The two diverge for one reason: a deploy restarted the
  // backend and republished the frontend while this tab stayed open, holding
  // the JavaScript it loaded before any of that.
  //
  // WHY IT IS WORTH SAYING OUT LOUD. On 10 Oct 2026 nine held releases went
  // out at once on srv1924928. The tab had been open since 1.697.0, so the
  // header said v1.697.0 while the Server Admin card beside it said
  // "running v1.708.0" - and the honest reading of two numbers disagreeing is
  // that the install failed. It had not; the page was stale. Ten minutes went
  // into establishing that, which is exactly the cost this removes.
  //
  // POLLED, NOT FETCHED ONCE, because the case this exists for is a restart
  // that happens WHILE the tab is open - a value read at mount would be the
  // old one for ever. Sixty seconds: a deploy takes twelve minutes, so there
  // is no hurry, and this must not be a meaningful load on a box.
  //
  // /api/v1/version, THE SAME ROUTE THE INSTALL POLL USES, and for the reason
  // given there: it answers on a box the panel's own poll cannot reach.
  // Unauthenticated and one small JSON object, so it costs nothing to ask
  // before sign-in.
  const [serverVersion, setServerVersion] = useState(null);
  useEffect(() => {
    let stopped = false;
    const ask = () => {
      fetch(`${API_BASE}/api/v1/version`)
        .then((res) => (res.ok ? res.json() : null))
        .then((body) => {
          // NO VERSION IS NOT A MISMATCH. A box that cannot answer, or answers
          // something unexpected, must leave the notice off rather than
          // telling an operator to reload for a number nobody has.
          if (!stopped && body && typeof body.version === "string") setServerVersion(body.version);
        })
        .catch(() => {});
    };
    ask();
    const id = setInterval(ask, 60000);
    // AND THE MOMENT THE TAB COMES BACK. Browsers throttle timers hard in a
    // background tab, and "deployed while I was in another window" is the
    // single most likely way to arrive here.
    const onVisible = () => {
      if (document.visibilityState === "visible") ask();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      stopped = true;
      clearInterval(id);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, []);
  // BOTH KNOWN AND DIFFERENT. A missing define must not throw, and must not
  // claim a mismatch either - the same guard the version line below has had
  // since it was added.
  const builtFrom = typeof __APP_VERSION__ === "string" ? __APP_VERSION__ : null;
  const pageIsStale = !!builtFrom && !!serverVersion && builtFrom !== serverVersion;

  const [email, setEmail] = useState("");

  const [password, setPassword] = useState("");

  const [loginError, setLoginError] = useState(null);

  const [forgotAdminMode, setForgotAdminMode] = useState(false);

  const [forgotAdminSent, setForgotAdminSent] = useState(false);

  // Set once auth.login signals a TOTP-enabled account needs its

  // second factor before a real session is issued — same pattern as

  // the main site's own AuthForm.

  const [pendingTotp, setPendingTotp] = useState(null); // { pendingToken }

  const [totpCode, setTotpCode] = useState("");

  const [usingBackupCode, setUsingBackupCode] = useState(false);

  const [backupCodeInput, setBackupCodeInput] = useState("");

  const [totpAttempt, setTotpAttempt] = useState(0); // bumped on each failed attempt, to remount OtpCodeInput fresh (re-focuses box 1)

  const [userSearchTerm, setUserSearchTerm] = useState("");

  // This admin's own Live/Demo status — lifted up here rather than

  // fetched independently by each view that needs it (the mode

  // banner, and eventually every frozen-state-aware list), since

  // several views need the exact same information and should never

  // show it inconsistently with each other.

  const [modeStatus, setModeStatus] = useState(null);
  // THE QUEUE BEING WORKED ON IN COMPETITION MANAGEMENT (1.435.0).
  //
  // Once this site is live, the partner's test site plays TEST
  // competitions, and root cannot leave Live while a live one runs - so
  // Test competitions are made and run from Live, by picking the queue.
  // Choosing "Test queue" hands the competitions list and the create form
  // a modeStatus whose effectiveMode is "test": everything they already
  // filter, label and freeze by follows it, and the server checks every
  // action against what this admin may manage (lib/manageable-queues.js).
  // The site's own mode never moves.
  const [workingQueue, setWorkingQueue] = useState("live");
  const siteIsLive = adminMode(modeStatus) === "live";
  const onTestQueue = siteIsLive && workingQueue === "test";
  const queueModeStatus = onTestQueue && modeStatus ? { ...modeStatus, effectiveMode: "test", workingQueue: "test" } : modeStatus;
  const queueSwitch = siteIsLive ? { value: onTestQueue ? "test" : "live", set: setWorkingQueue } : null;

  const navigate = useNavigate();

  const { subTab, competitionsSubTab, chartCompetitionId, partnersSubTab, partnersApiSubTab, emailSubTab, competitionManagementSubTab, testingSubTab, paymentsSubTab } = parseAdminPath(path);



  const authHeaders = { "Content-Type": "application/json", "x-auth-token": token || "" };



  // ---- resolve who's logged in, on load or when the token changes ----

  useEffect(() => {

    if (!token) {

      setAuthChecked(true);

      return;

    }

    wsRequest("auth.me", {}, token)

      .then((u) => {

        if (!u || (u.role !== "admin" && u.role !== "root")) {

          setToken(null);

          setUser(null);

        } else {

          setUser(u);

          applyAccountTheme(u.themePreference);

        }

        setAuthChecked(true);

      })

      .catch((err) => {

        // Only clear the session on a confirmed auth failure — a network

        // hiccup or a WS reconnect-in-progress shouldn't log the admin out.

        if (err.status === 401 || err.status === 403) {

          setToken(null);

          setUser(null);

          if (err.message) showToast(err.message, "error");

        }

        setAuthChecked(true);

      });

  }, [token]);



  const loadModeStatus = () => {

    if (!token) return;

    wsRequest("admin.mode.get", {}, token)

      .then((d) => {
        // Align our clock with the server's before anything counts down.
        setServerClockOffset(d?.serverTime);
        setModeStatus(d);
      })

      .catch(() => {});

  };

  useEffect(loadModeStatus, [token, user]);

  useEffect(() => onBroadcast((message) => {

    if (message.type === "users-changed") loadModeStatus();

  }), [token]);



  // Fires the moment any admin-gated request comes back 428 — this

  // account needs to set up mandatory 2FA before it can do anything

  // else here. See enforceMandatoryTotp on the backend and

  // onMandatoryTotpRequired/triggerMandatoryTotpRequired in

  // wsClient.js for how this gets triggered centrally rather than

  // checked at every individual call site throughout this file.

  const [needsMandatoryTotpSetup, setNeedsMandatoryTotpSetup] = useState(false);

  useEffect(() => onMandatoryTotpRequired(() => setNeedsMandatoryTotpSetup(true)), []);

  // A genuinely invalidated session (see onSessionInvalid's own comment

  // in wsClient.js) — e.g. a tab left open from before a fresh install

  // wiped every account, still showing as "logged in" with a token the

  // backend no longer recognizes at all. Without this, an action taken

  // from that stale tab would fail with a generic error toast but never

  // actually clear the stale session or return to the login screen,

  // leaving the page stuck looking logged in indefinitely.

  useEffect(

    () =>

      onSessionInvalid(() => {

        setToken(null);

        setUser(null);

        showToast("Your session is no longer valid — please log in again.", "error");

      }),

    []

  );

  // Clears back to false once totpEnabled actually flips true on the

  // account we already have (e.g. after finishing setup) — the

  // interstitial's own onComplete callback does this directly, but

  // this covers auth.me/login refreshing user with an already-enabled

  // account too, so the flag can never end up stuck stale.

  useEffect(() => {

    if (user?.totpEnabled) setNeedsMandatoryTotpSetup(false);

  }, [user?.totpEnabled]);



  // Real-time push from the backend the instant a newer login elsewhere

  // supersedes this session (see issueToken/notifySessionSuperseded on the

  // backend) — SiteHeader.jsx has the same listener for the public site,

  // but it doesn't render on /admin routes at all, so this needs its own

  // copy rather than inheriting that one. Without this, an admin tab

  // sitting idle wouldn't find out until its next request happened to

  // fail; this makes it immediate instead — logged out and back at the

  // login form the moment it actually happens, no refresh needed.

  useEffect(() => {

    return onBroadcast((message) => {

      if (message.type === "session-superseded") {

        setToken(null);

        setUser(null);

        if (message.message) showToast(message.message, "error");

      }

    });

  }, []);



  // Which sub-tabs this user can see, based on role/permissions — all

  // shown together under the single top-level tab (labeled "Root" for a

  // root account, "Admin" for a regular admin — see topLevelLabel below).

  // Account Management is deliberately included only for root — unlike

  // every other sub-tab here, it's not gated by any grantable permission,

  // since granting it would let a regular admin grant themselves (or

  // anyone else) every other permission, defeating the point of having a

  // permission system at all.

  const availableSubTabs = (u) => {

    if (!u) return [];

    const tabs = [];

    if (u.role === "root" || u.permissions.includes("manage_competitions")) {

      tabs.push({ id: "competitions", label: "Competitions" });

    }

    if (u.role === "root" || u.permissions.includes("manage_users")) {

      tabs.push({ id: "users", label: "Users" });

    }

    if (u.role === "root") {

      tabs.push({ id: "accounts", label: "Account Management" });

    }

    if (u.role === "root" || u.permissions.includes("manage_competition_defaults")) {

      tabs.push({ id: "competition-management", label: "Competition Management" });

    }

    if (u.role === "root" || u.permissions.includes("manage_settings")) {

      tabs.push({ id: "sitewide", label: "Sitewide" });

    }

    // manage_content alone since 18 Aug 2026 — manage_settings used to open

    // this tab too, which broke the one-permission-per-tab rule and meant

    // Sitewide implicitly carried Content with it. Legal documents moved

    // here at the same time (they are content), so this permission now

    // covers editing terms/privacy as well as the standalone pages.

    if (u.role === "root" || u.permissions.includes("manage_content")) {

      tabs.push({ id: "content", label: "Content" });

    }

    if (u.role === "root" || u.permissions.includes("manage_mock_data")) {

      tabs.push({ id: "mock-data", label: "Mock Data" });

    }

    if (u.role === "root" || u.permissions.includes("manage_server")) {

      tabs.push({ id: "server", label: "Server Admin" });

    }

    if (u.role === "root" || u.permissions.includes("view_activity_log")) {

      tabs.push({ id: "activity-log", label: "Activity Log" });

    }

    if (u.role === "root" || u.permissions.includes("manage_partners")) {

      tabs.push({ id: "partners", label: "Partners" });

    }

    if (u.role === "root" || u.permissions.includes("manage_email")) {

      tabs.push({ id: "email", label: "Email" });

    }

    if (u.role === "root" || u.permissions.includes("manage_sms")) {

      tabs.push({ id: "sms", label: "SMS" });

    }

    if (u.role === "root" || u.permissions.includes("manage_finance")) {

      // FINANCE, absorbing what used to be the Payment Providers tab.
      //
      // Everything money-related now sits here on ONE permission, which
      // is the rule this file already keeps elsewhere: the payment
      // panels used to live inside Sitewide, gated on manage_settings,
      // while each panel independently required
      // manage_payment_providers — so an admin with settings access but
      // not payments saw a tab with invisible sections in it.
      //
      // Partner credentials stay under Partners deliberately. They are a
      // property of that relationship, and splitting Partners across two
      // tabs would be worse than the duplication.
      tabs.push({ id: "finance", label: "Finance" });

    }

    // Promoted from a Partners sub-tab on 22 Aug 2026. Reading the

    // release history is useful to anyone operating the site, and gating

    // it on manage_partners meant the only way to grant it was to also

    // hand over the partner API keys.

    if (u.role === "root" || u.permissions.includes("view_testing")) {

      tabs.push({ id: "testing", label: "Testing" });

    }

    if (u.role === "root" || u.permissions.includes("manage_competitions")) {

      // NO LONGER A TOP-LEVEL TAB. It opens from the History button on the
      // Queue Control panel instead — same feature, one place, next to the
      // demo queue it belongs to. Removed 29 Aug 2026 after a duplicate of
      // it was built by mistake and thrown away.

    }

    // THE ORDER AN OPERATOR WORKS IN, not the alphabet.
    //
    // These were alphabetical from 13 Sep 2026, which was an improvement
    // on the order the features happened to be built in and still wrong:
    // it put Account Management first and Competitions fifth, on a site
    // whose entire purpose is running competitions.
    //
    // Grouped by what they are for: the competitions themselves, then the
    // people, then the site's own content and settings, then the ways it
    // talks to the outside world, then money, then the operational tabs
    // somebody visits when something needs checking.
    //
    // A TAB NOT IN THIS LIST GOES LAST rather than disappearing — a new
    // one should be visible and obviously unplaced, not silently sorted
    // into the middle.
    const TAB_ORDER = [
      "competitions",
      "competition-management",
      "accounts",
      "users",
      "sitewide",
      "content",
      "mock-data",
      "partners",
      "email",
      "sms",
      "finance",
      "activity-log",
      "testing",
      "server",
    ];
    const rank = (id) => {
      const at = TAB_ORDER.indexOf(id);
      return at < 0 ? TAB_ORDER.length : at;
    };
    return tabs.sort((a, b) => rank(a.id) - rank(b.id));

  };



  // Redirect to the first tab this user actually has access to, if the

  // current URL doesn't point at a valid one for them (e.g. landing on

  // bare /admin, or a tab they've since lost permission for).

  useEffect(() => {

    if (!user) return;

    const tabs = availableSubTabs(user);
  // Panels below render from the URL (`subTab === "x"`), not from
  // permissions. The redirect above normally keeps those in step, but it
  // bails when the admin has NO tabs at all - there is nowhere to send
  // them - which leaves the URL naming a panel they cannot use. The
  // result is a permanent "Loading..." while the server correctly
  // refuses every action behind it.
  //
  // Gating on the same list that builds the tabs covers every panel at
  // once, including any added later, rather than repeating a check in
  // fifteen places and eventually forgetting one.
  // "payment-providers" was this tab's id until 28 Aug 2026, when it
  // became "finance". Mapped rather than dropped so an existing bookmark
  // still lands on the credentials screen it has always shown — the
  // render condition accepts both ids, and the first click on any
  // sub-tab rewrites the URL to /admin/finance/...
  const requestedSubTab =
    subTab === "payment-providers" && tabs.some((t) => t.id === "finance")
      ? "finance"
      : subTab;
  const allowedSubTab = tabs.some((t) => t.id === requestedSubTab) ? requestedSubTab : null;

    if (tabs.length === 0) return;

    // requestedSubTab, NOT subTab — it carries the old-id mapping. Using
    // the raw value here bounced /admin/payment-providers to Competitions
    // before the mapping was ever consulted, which is exactly what a
    // bookmark to it did.
    if (!tabs.some((t) => t.id === requestedSubTab)) {

      const defaultTab = tabs[0].id;

      navigate(defaultTab === "competitions" ? "/admin/competitions/timeline" : `/admin/${defaultTab}`);

    }

  }, [user, subTab]);



  // THE TEST-TAB REDIRECT WENT WITH THE TEST TAB, 19 Sep 2026. It
  // existed because the Test option vanished when an admin left Test
  // mode while the URL still said /partners/api/test. There is one
  // panel now, rendered whatever the URL's last segment says.



  const completeAdminLogin = (data) => {

    if (data.user.role !== "admin" && data.user.role !== "root") {

      setLoginError("This account doesn't have admin access.");

      return;

    }

    setToken(data.token);

    setUser(data.user);

    applyAccountTheme(data.user.themePreference);

  };



  const login = async (e) => {

    e.preventDefault();

    setLoginError(null);

    try {

      const data = await wsRequest("auth.login", { email, password });

      if (data.needsTotp) {

        setPendingTotp({ pendingToken: data.pendingToken });

        return;

      }

      completeAdminLogin(data);

    } catch (err) {

      setLoginError(err.message || "Could not reach backend.");

    }

  };



  const submitForgotAdmin = async (e) => {

    e.preventDefault();

    setLoginError(null);

    try {

      await wsRequest("auth.forgotPassword", { email });

      // Always shows the same confirmation regardless of whether the

      // email actually belongs to an account — same non-revealing

      // behavior as the backend itself.

      setForgotAdminSent(true);

    } catch (err) {

      setLoginError(err.message || "Could not reach backend.");

    }

  };



  const submitTotpCode = async (e, explicitCode) => {

    e?.preventDefault?.();

    const codeToSubmit = explicitCode ?? (usingBackupCode ? backupCodeInput : totpCode);

    setLoginError(null);

    try {

      const data = await wsRequest("auth.totp.verifyLogin", { pendingToken: pendingTotp.pendingToken, code: codeToSubmit });

      completeAdminLogin(data);

    } catch (err) {

      setLoginError(err.message || "Could not reach backend.");

      setTotpCode("");

      setTotpAttempt((a) => a + 1);

    }

  };



  const logout = async () => {

    try {

      await wsRequest("auth.logout", {}, token);

    } catch {

      // ignore — logging out client-side regardless

    }

    setToken(null);

    setUser(null);

    // Back to /admin, so the panel is not left on a deep sub-tab whose

    // rendered contents outlive the session. Less exposed than the public

    // side (a null token re-renders to the login form) but the URL still

    // says which competition or user was being looked at.

    navigate("/admin");

  };



  const inputStyle = {

    width: "100%",

    padding: 8,

    marginTop: 4,

    marginBottom: 14,

    background: "var(--bg-input)",

    border: "1px solid var(--border-color-light)",

    borderRadius: 6,

    color: "var(--text-primary)",

  };



  if (!authChecked) return null;



  if (!user && pendingTotp) {

    return (

      <div style={{ color: "var(--text-primary)", padding: 20, maxWidth: 360, margin: "80px auto" }}>

        <h2>Admin login</h2>

        <form onSubmit={submitTotpCode}>

          {usingBackupCode ? (

            <>

              <p style={{ fontSize: 13, opacity: 0.8 }}>Enter one of your backup codes.</p>

              <label>

                Backup code

                <input

                  type="text"

                  inputMode="text"

                  maxLength={8}

                  required

                  autoFocus

                  value={backupCodeInput}

                  onChange={(e) => setBackupCodeInput(e.target.value.replace(/[^0-9a-fA-F]/g, "").slice(0, 8))}

                  style={inputStyle}

                />

              </label>

            </>

          ) : (

            <>

              <p style={{ fontSize: 13, opacity: 0.8 }}>Enter the 6-digit code from your authenticator app.</p>

              <div style={{ marginBottom: 14 }}>

                <OtpCodeInput

                  key={totpAttempt}

                  length={6}

                  value={totpCode}

                  onChange={setTotpCode}

                  onComplete={(fullCode) => submitTotpCode(null, fullCode)}

                  autoFocus

                />

              </div>

            </>

          )}

          {loginError && <p style={{ color: "var(--accent-red)" }}>{loginError}</p>}

          <button type="submit" disabled={usingBackupCode ? backupCodeInput.length < 6 : totpCode.length !== 6}>

            Verify

          </button>{" "}

          <button

            type="button"

            onClick={() => {

              setPendingTotp(null);

              setTotpCode("");

              setBackupCodeInput("");

              setUsingBackupCode(false);

              setLoginError(null);

            }}

          >

            Back

          </button>

          <p style={{ fontSize: 11, opacity: 0.55, marginTop: 6 }}>

            {usingBackupCode ? (

              <button

                type="button"

                onClick={() => {

                  setUsingBackupCode(false);

                  setBackupCodeInput("");

                  setLoginError(null);

                }}

                style={{ background: "none", border: "none", padding: 0, color: "var(--accent-blue)", textDecoration: "underline", cursor: "pointer", fontSize: 11 }}

              >

                Use a code from your app instead

              </button>

            ) : (

              <>

                Lost access to your authenticator app?{" "}

                <button

                  type="button"

                  onClick={() => {

                    setUsingBackupCode(true);

                    setTotpCode("");

                    setLoginError(null);

                  }}

                  style={{ background: "none", border: "none", padding: 0, color: "var(--accent-blue)", textDecoration: "underline", cursor: "pointer", fontSize: 11 }}

                >

                  Use one of your backup codes

                </button>

                .

              </>

            )}

          </p>

        </form>

      </div>

    );

  }



  if (!user) {

    if (forgotAdminMode) {

      return (

        <div style={{ color: "var(--text-primary)", padding: 20, maxWidth: 360, margin: "80px auto" }}>

          <h2>Reset password</h2>

          {forgotAdminSent ? (

            <>

              <p style={{ fontSize: 13, opacity: 0.85 }}>If that email belongs to an account, a reset link has been sent to it.</p>

              <button

                onClick={() => {

                  setForgotAdminMode(false);

                  setForgotAdminSent(false);

                }}

              >

                Back to login

              </button>

            </>

          ) : (

            <form onSubmit={submitForgotAdmin}>

              <label>

                Email

                <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} style={inputStyle} autoFocus />

              </label>

              {loginError && <p style={{ color: "var(--accent-red)" }}>{loginError}</p>}

              <button type="submit">Send reset link</button>{" "}

              <button type="button" onClick={() => setForgotAdminMode(false)}>

                Back to login

              </button>

            </form>

          )}

        </div>

      );

    }

    return (

      <div style={{ color: "var(--text-primary)", padding: 20, maxWidth: 360, margin: "80px auto" }}>

        <h2>Admin login</h2>

        <form onSubmit={login}>

          <label>

            Email

            <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} style={inputStyle} autoFocus />

          </label>

          <label>

            Password

            <input

              type="password"

              required

              value={password}

              onChange={(e) => setPassword(e.target.value)}

              style={inputStyle}

            />

          </label>

          {loginError && <p style={{ color: "var(--accent-red)" }}>{loginError}</p>}

          <button type="submit">Log in</button>

        </form>

        <p style={{ fontSize: 11, opacity: 0.55, marginTop: 6 }}>

          <button

            type="button"

            onClick={() => setForgotAdminMode(true)}

            style={{ background: "none", border: "none", padding: 0, color: "var(--accent-blue)", textDecoration: "underline", cursor: "pointer", fontSize: 11 }}

          >

            Forgot your password?

          </button>

        </p>

      </div>

    );

  }



  if ((user.role === "admin" || user.role === "root") && (!user.totpEnabled || needsMandatoryTotpSetup)) {

    return (

      <div style={{ color: "var(--text-primary)", padding: 20, maxWidth: 480, margin: "80px auto" }}>

        <h2>Set up two-factor authentication</h2>

        <p style={{ fontSize: 13, opacity: 0.8 }}>

          Required for every {user.role === "root" ? "root" : "admin"} account before you can continue — protects

          this dashboard even if your password is ever compromised.

        </p>

        <TotpSetup

          token={token}

          onComplete={() => {

            setNeedsMandatoryTotpSetup(false);

            setUser((prev) => (prev ? { ...prev, totpEnabled: true } : prev));

          }}

        />

      </div>

    );

  }



  const tabs = availableSubTabs(user);
  // Panels below render from the URL (`subTab === "x"`), not from
  // permissions. The redirect above normally keeps those in step, but it
  // bails when the admin has NO tabs at all - there is nowhere to send
  // them - which leaves the URL naming a panel they cannot use. The
  // result is a permanent "Loading..." while the server correctly
  // refuses every action behind it.
  //
  // Gating on the same list that builds the tabs covers every panel at
  // once, including any added later, rather than repeating a check in
  // fifteen places and eventually forgetting one.
  // "payment-providers" was this tab's id until 28 Aug 2026, when it
  // became "finance". Mapped rather than dropped so an existing bookmark
  // still lands on the credentials screen it has always shown — the
  // render condition accepts both ids, and the first click on any
  // sub-tab rewrites the URL to /admin/finance/...
  const requestedSubTab =
    subTab === "payment-providers" && tabs.some((t) => t.id === "finance")
      ? "finance"
      : subTab;
  const allowedSubTab = tabs.some((t) => t.id === requestedSubTab) ? requestedSubTab : null;

  // Both tabs involved in the Chart/Stats/Create move, so a cross-tab jump

  // is only ever offered to someone who can actually land on the far side.

  const canSeeCompetitions = tabs.some((t) => t.id === "competitions");

  const canSeeCompetitionManagement = tabs.some((t) => t.id === "competition-management");

  const topLevelLabel = user.role === "root" ? "Root" : "Admin";



  return (

    <div style={{ color: "var(--text-primary)", padding: 20, maxWidth: 1400, margin: "0 auto" }}>

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>

        <h2>

          <NavLink to="/admin" style={{ color: "inherit", textDecoration: "none" }}>

            Admin dashboard

          </NavLink>

        </h2>

        <div style={{ textAlign: "right" }}>

          <div style={{ opacity: 0.7, fontSize: 14 }}>

            {user.email} ({user.role})

          </div>

          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", alignItems: "flex-start" }}>

            <button

              onClick={() => toggleTheme(token)}

              title={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}

            >

              {theme === "dark" ? "☀️" : "🌙"}

            </button>

            {/* PROBLEMS THE SITE RAN INTO ON ITS OWN. Root and admins
                only — the public bell in the site header is a different
                list with no permission check at all. */}
            <AdminIssuesBell
              token={token}
              canSee={user?.role === "root" || user?.role === "admin"}
            />

            <ChangePasswordWidget token={token} />

            <button onClick={logout}>Log out</button>

          </div>

          {/* THE VERSION, in the admin as well as the public footer.
              It was only in the footer, which the admin panel does not
              render — so the one place somebody debugging actually works
              had no way to tell which build they were looking at.

              Below the buttons rather than among them: it is a thing to
              LOOK UP, not a control, and sitting in the row it competed
              with things people actually click.

              Same guard as the footer: a missing define must not throw on
              every admin page. */}
          <div
            style={{ fontSize: 11, opacity: 0.55, textAlign: "right", marginTop: 4 }}
            title="The version this admin panel was built from"
          >
            {builtFrom ? `v${builtFrom}` : ""}
          </div>
          {/* THE PAGE IS OLDER THAN THE SERVER (1.709.0). Deliberately louder
              than the version above it: that one is a thing to look up, this
              one is a thing to act on, and the whole point is that it is read
              before somebody concludes an install failed. */}
          {pageIsStale && (
            <div
              style={{
                fontSize: 11,
                textAlign: "right",
                marginTop: 2,
                color: "var(--accent-amber, #ffcc66)",
              }}
              title="This page is running JavaScript from an earlier release. Reload to get the current one."
            >
              this page is v{builtFrom} — the server is running v{serverVersion}.{" "}
              <button
                type="button"
                onClick={() => window.location.reload()}
                style={{
                  background: "none",
                  border: "none",
                  padding: 0,
                  font: "inherit",
                  color: "inherit",
                  textDecoration: "underline",
                  cursor: "pointer",
                }}
              >
                Reload
              </button>
            </div>
          )}

        </div>

      </div>



      {/* THE LOUDEST THING ON THE PAGE, and it should be. When this is
          set, "live" payments go to Cashflows' integration gateway and no
          real payment can be taken — which is exactly what somebody
          debugging a missing payment needs to know first. */}
      {modeStatus?.cashflowsLiveIsSandbox && (
        <div
          style={{
            padding: "12px 16px",
            marginBottom: 12,
            borderRadius: 8,
            border: "2px solid #d9534f",
            background: "rgba(217, 83, 79, 0.12)",
            fontWeight: 600,
          }}
        >
          LIVE PAYMENTS ARE GOING TO THE SANDBOX
          <div style={{ fontWeight: "normal", fontSize: 13, marginTop: 4, opacity: 0.85 }}>
            CASHFLOWS_LIVE_GATEWAY_IS_SANDBOX is set, so Live mode talks to Cashflows'
            integration gateway. No real money can move and no real payment can be taken.
            This exists for testing the live-mode rules — it must never be set on a site
            serving real buyers.
          </div>
        </div>
      )}

      <LiveDemoModeBanner token={token} status={modeStatus} onStatusChange={setModeStatus} />



      <div style={{ margin: "16px 0" }}>

        <DashboardSummaryBar token={token} modeStatus={modeStatus} />

      </div>



      {/* Only ever one top-level tab now — its content (every sub-tab

          below) is what used to be split across three separate top-level

          tabs (Competitions/Users/Root). Shown as a plain label rather

          than a single-item TabBar, since a tab bar with nothing to

          switch between isn't really a tab bar. */}

      <h3 style={{ marginBottom: 4 }}>{topLevelLabel}</h3>



      <TabBar

        tabs={tabs}

        activeId={subTab}

        onSelect={(id) => navigate(id === "competitions" ? "/admin/competitions/timeline" : `/admin/${id}`)}

      />



      {tabs.length === 0 && (

        <p style={{ opacity: 0.7, marginTop: 20 }}>

          Your account doesn't have any admin permissions assigned yet — ask root to grant you some.

        </p>

      )}



      {allowedSubTab === "competitions" && (

        <CompetitionsPanel

          authHeaders={authHeaders}

          isRoot={user?.role === "root"}

          token={token}

          tab={competitionsSubTab}

          onViewCompetitionChart={

            canSeeCompetitionManagement ? (id) => navigate(`/admin/competition-management/competitionChart/${id}`) : null

          }

          canEdit={canEdit("manage_competitions")}
          canEditDemoComps={canEdit("manage_competitions")}

          modeStatus={queueModeStatus}

          realModeStatus={modeStatus}

          queueSwitch={queueSwitch}

          onTabChange={(t, extra) => navigate(`/admin/competitions/${t}${extra ? `/${extra}` : ""}`)}

          onViewUser={(term) => {

            setUserSearchTerm(term);

            navigate("/admin/users");

          }}

        />

      )}

      {allowedSubTab === "users" && (

        <UsersPanel authHeaders={authHeaders} searchTerm={userSearchTerm} setSearchTerm={setUserSearchTerm} modeStatus={modeStatus} canEdit={canEdit("manage_users")} />

      )}

      {allowedSubTab === "accounts" && <AdminAccountsPanel authHeaders={authHeaders} modeStatus={modeStatus} />}

      {allowedSubTab === "competition-management" && (

        <>

          <TabBar

            tabs={[

              { id: "settings", label: "Settings" },

              { id: "history", label: "Competition History" },

              // Moved here from the Competitions tab, where they sat pushed

              // right of the status lists. They belong with competition

              // configuration rather than with the day-to-day lists, and

              // moving them means they take this tab's own permission

              // (manage_competition_defaults) instead of manage_competitions.

              { id: "chart", label: "Chart", pushRight: true },

              { id: "stats", label: "Stats" },

              { id: "create", label: "+ Create competition" },

            ]}

            // competitionChart is a drill-down of Chart rather than a tab of

            // its own, so it keeps Chart highlighted while you're inside it.

            activeId={competitionManagementSubTab === "competitionChart" ? "chart" : competitionManagementSubTab}

            onSelect={(id) => navigate(`/admin/competition-management/${id}`)}

          />

          {competitionManagementSubTab === "settings" && (

            <>

              <CompetitionDefaultsPanel authHeaders={authHeaders} canEdit={canEdit("manage_competition_defaults")} modeStatus={modeStatus} />


            </>

          )}

          {competitionManagementSubTab === "history" && <CompetitionHistoryPanel token={token} modeStatus={modeStatus} />}

          {competitionManagementSubTab === "chart" && (

            <ChartPanel

              token={token}

              onViewCompetition={(id) => navigate(`/admin/competition-management/competitionChart/${id}`)}

              modeStatus={modeStatus}

            />

          )}

          {competitionManagementSubTab === "competitionChart" && (

            <CompetitionChartPanel

              token={token}

              competitionId={chartCompetitionId}

              onBack={() => navigate("/admin/competition-management/chart")}

            />

          )}

          {competitionManagementSubTab === "stats" && (

            <CompetitionStatsPanel

              authHeaders={authHeaders}

              onViewUser={(term) => {

                setUserSearchTerm(term);

                navigate("/admin/users");

              }}

              onViewChart={(id) => navigate(`/admin/competition-management/competitionChart/${id}`)}

              modeStatus={modeStatus}

              queueSwitch={queueSwitch}

            />

          )}

          {competitionManagementSubTab === "create" && (

            <CreateCompetitionPanel

              token={token}

              canEdit={canEdit("manage_competition_defaults")}

              modeStatus={queueModeStatus}

              queueSwitch={queueSwitch}

              // Jumps to the list the new competition landed in — which is

              // under the Competitions tab, so this leaves Competition

              // Management entirely. Only offered to an admin who can

              // actually see that tab; otherwise they simply stay put.

              onCreated={(status) => {

                if (canSeeCompetitions) navigate(`/admin/competitions/${status}`);

              }}

            />

          )}

        </>

      )}

      {allowedSubTab === "sitewide" && (

        <>

          <SiteSettingsPanel authHeaders={authHeaders} token={token} canEdit={canEdit("manage_settings")} modeStatus={modeStatus} />

          <ThemesPanel token={token} canEdit={canEdit("manage_settings")} modeStatus={modeStatus} />

          {/* ENTRY CREDIT IS A SITEWIDE SETTING, so it lives here rather
              than under either payment tab — one switch for the whole
              instance, not a property of a credential set.

              Gated on manage_payment_providers, NOT the manage_settings
              governing the rest of this tab: that is what the backend
              enforces, and it is the same class of decision as Going
              Live. Anyone without it sees nothing here at all. */}
          {/* Site-wide, not financial — it governs who can reach the site
              at all. Gated on manage_settings like the rest of this tab. */}
          {canEdit("manage_settings") && (
            <SiteAccessPanel token={token} wsRequest={wsRequest} canEdit={canEdit("manage_settings")} modeStatus={modeStatus} />
          )}

          
          {/* MOVED TO THE FINANCE TAB on 28 Aug 2026 — entry credit, the
              test/live switch, the refund policy and the transaction list
              all live there now.

              They sat here gated on manage_payment_providers while this
              tab is gated on manage_settings, so an admin with settings
              access but not payments saw a tab with invisible sections in
              it. One permission per tab is the rule this file keeps
              elsewhere. */}

        </>

      )}

      {allowedSubTab === "testing" && (

        <>

          <TabBar

            tabs={[

              { id: "changelog", label: "Changelog" },

              { id: "coverage", label: "Coverage" },

            ]}

            activeId={testingSubTab}

            onSelect={(id) => navigate(`/admin/testing/${id}`)}

          />

          {testingSubTab === "changelog" && <ChangelogPanel token={token} />}

          {testingSubTab === "coverage" && <CoveragePanel token={token} />}

        </>

      )}

      {allowedSubTab === "content" && (

        <>
          <ContentPagesPanel token={token} canEdit={canEdit("manage_content")} modeStatus={modeStatus} />
        </>

      )}

      {allowedSubTab === "mock-data" && <MockDataPanel authHeaders={authHeaders} token={token} modeStatus={modeStatus} canEdit={canEdit("manage_mock_data")} />}

      {allowedSubTab === "server" && (

        <>

          {/* THE MACHINE FIRST (1.930.0) - everything below is about a box
              whose size this panel never mentioned.

              AND BESIDE IT, NOT ABOVE IT (1.1036.0). Both cards were capped at
              500 inside a container that allows 1360, so two thirds of the row
              was empty while the table inside Server control overflowed its own
              card for want of exactly that width. They wrap back into one
              column when there is not room for both.

              AND SERVER CONTROL IS PAINTED ON THE LEFT (1.1037.0), asked for
              after seeing 1.1036.0 on a real screen. row-reverse, NOT a swap of
              the two elements: 1.930.0 put this card first deliberately - the
              orienting fact, because everything else in the tab is about a
              machine whose size the panel never mentioned - and swapping them
              would have moved it BELOW Server control every time the pair wraps,
              and second in tab order for anyone not using a mouse. Reversing the
              row moves the painting and leaves the document alone. Measured in
              Chromium at 1400, 1000 and 700: Server control is left of This
              machine at every width, and when they stop fitting side by side the
              machine is the one on top. */}
          <div style={{ display: "flex", flexDirection: "row-reverse", gap: 20, alignItems: "flex-start", flexWrap: "wrap" }}>

            <MachinePanel token={token} />

            <ServerControlPanel authHeaders={authHeaders} canEdit={canEdit("manage_server")} modeStatus={modeStatus} />

          </div>

          {/* THE DATABASE BACKUP DIRECTORY (1.816.0). Read-only, plus the
              rehearsal, which restores into a scratch database and touches
              nothing the site reads. Restoring OVER the site's own database is
              deliberately not here yet. */}
          <DatabaseBackupPanel token={token} canEdit={canEdit("manage_server")} />

          {/* THE LIMITS ON THAT IMPORT (1.898.0), read next to the thing they
              limit. The ceiling is shown and not offered: it is a pm2 option
              applied at start, and nothing here restarts the site to save a
              number. */}
          <ServerLimitsPanel token={token} canEdit={canEdit("manage_server")} modeStatus={modeStatus} />
          {/* EXPORT AND IMPORT, moved here from Sitewide in 1.396.0. */}
          <SiteDataTransferPanel token={token} canEdit={canEdit("manage_server")} modeStatus={modeStatus} />

          <DeleteAllDataPanel token={token} modeStatus={modeStatus} canEdit={canEdit("manage_server")} />

        </>

      )}

      {allowedSubTab === "activity-log" && (

        <ActivityLogPanel

          token={token}

          modeStatus={modeStatus}

          onViewUser={(term) => {

            setUserSearchTerm(term);

            navigate("/admin/users");

          }}

        />

      )}

      {allowedSubTab === "partners" && (

        <>

          <TabBar

            tabs={[

              { id: "keys", label: "Partners API Key" },
              // QUICK START — writes a partner's first page from the
              // fields and widgets they tick. Beside the key, because
              // that is the other thing an operator hands over.
              { id: "quick-start", label: "Quick Start" },

              { id: "integration", label: "Integration" },

              { id: "api", label: "API" },
              // WHERE THIS PARTNER'S USERS PLAY, and anything else that
              // belongs to the partner rather than to a key. Added
              // 18 Sep 2026.
              { id: "settings", label: "Settings" },
              // PAYMENT PROVIDERS, EMAIL AND SMS ARE CONFIGURED ONCE, not twice.
              //
              // A partner uses this platform in ONE of two ways — through the API
              // or on the site natively — never both, and there is no host
              // merchant account. So these are the same credentials either way,
              // and asking for them here as well as under Finance, Email and SMS
              // only created two things that could disagree.
              //
              // Removed 6 Sep 2026. The fields behind them, and the panels, were
              // removed in 1.407.0: every instance is one partner's, so the
              // credentials under Finance, Email and SMS are theirs, and anything
              // an instance still held in the second set is moved into the one
              // set at startup (backend/lib/provider-consolidation.js).
              //
              // "Payments" is NOT one of them and stays: it lists payments rather
              // than configuring credentials. Removed by mistake first time round.



              // PARTNER-SIDE PAYMENTS REMOVED 6 Sep 2026.



              //



              // This toggled one API route: a partner reporting a pixel purchase



              // they had taken payment for themselves, which this site then had



              // to take on trust with nothing verifying it.



              //



              // It does not arise here. The site IS the partner's instance and



              // charges through the partner's own credentials under Finance, so



              // there is never a second party taking the money.



              //



              // REMOVED ENTIRELY IN 1.408.0: the route, its setting, the panel



              // behind this row, and the widget's onPurchaseRequested handoff.



              // Nobody was using it, and while the route refused, a partner page



              // that still handed off could charge a buyer who then got nothing.

              // GOING LIVE AND ENTRY CREDIT, gated on
              // manage_payment_providers rather than the manage_partners
              // that governs the rest of this tab — that is what the
              // backend enforces for both, and a tab someone cannot use
              // is worse than no tab.
              ...(canEdit("manage_finance")
                ? [
                    // GOING LIVE (PARTNER) REMOVED 6 Sep 2026.
                    //
                    // It reported readiness of partner.paymentProvider — the second set
                    // of credentials removed in 1.248.0. Nothing charges through those
                    // now, so it answered a question about fields no payment uses.
                    //
                    // NOT a duplicate of the Finance one, which was the first guess: it
                    // passed its own actions and heading. Same component, different
                    // subject. Finance -> Going Live reports the credentials that do
                    // take the money, and is the one to keep.
                  ]
                : []),

              { id: "preview", label: "Preview" },

              { id: "faq", label: "FAQ" },

            ]}

            activeId={partnersSubTab}

            onSelect={(id) => navigate(`/admin/partners/${id}`)}

          />

          {partnersSubTab === "keys" && (

            <PartnersPanel

              token={token}


              canEdit={canEdit("manage_partners")}

              modeStatus={modeStatus}

            />

          )}

          {partnersSubTab === "quick-start" && (
            <PartnerQuickStartPanel
              token={token}
              canEdit={canEdit("manage_partners")}
            />
          )}

          {partnersSubTab === "integration" && <IntegrationGuidePanel token={token} />}
          {partnersSubTab === "settings" && (
            <PartnerSettingsPanel token={token} canEdit={canEdit("manage_partners")} />
          )}

          {partnersSubTab === "api" && (

            <>

              {/* TWO TABS, AND ONLY ONE OF THEM WRITES ANYTHING. There
                  were an "API" tab and a "Test" tab here until
                  19 Sep 2026, and the second held a SECOND COPY of the
                  field checkboxes governing an API nobody could be
                  calling - two sets of settings that could disagree.
                  That is not what these are: Settings is still the one
                  and only place a field is ticked, and Data points is
                  read-only, a catalogue of what the API can serve for
                  a partner who wants to write their own widget. */}
              <TabBar
                tabs={[
                  { id: "settings", label: "Settings" },
                  { id: "data", label: "Data points" },
                ]}
                activeId={partnersApiSubTab === "data" ? "data" : "settings"}
                onSelect={(id) => navigate(`/admin/partners/api/${id}`)}
              />

              {partnersApiSubTab === "data" ? (
                <ApiCataloguePanel token={token} />
              ) : (
                <ApiSettingsPanel
                  token={token}
                  canEdit={canEdit("manage_partners")}
                  modeStatus={modeStatus}
                />
              )}

            </>

          )}


          {/* NO PARTNER-LEVEL PROVIDER PANELS SINCE 1.407.0. Email, SMS,
              payment providers and a partner "going live" view used to
              be reachable here by URL alone — /admin/partners/email and
              the rest — long after the 6 Sep 2026 merge removed their
              tabs, and each wrote to a second credential store. Every
              instance is a single partner's, so credentials are entered
              once, under Finance, Email and SMS. */}

          {partnersSubTab === "preview" && <PartnersPreviewPanel />}



          {partnersSubTab === "faq" && <PartnersFaqPanel />}

        </>

      )}



      {allowedSubTab === "email" && (

        <>

          <TabBar

            tabs={[

              { id: "providers", label: "Providers" },

              { id: "content", label: "Content" },

              { id: "subscribers", label: "Subscribers" },

              { id: "scheduler", label: "Scheduler" },

              { id: "activity", label: "Activity" },
              { id: "settings", label: "Settings" },

            ]}

            activeId={emailSubTab}

            onSelect={(id) => navigate(`/admin/email/${id}`)}

          />

          {emailSubTab === "settings" && (
            <EmailSettingsPanel token={token} canEdit={canEdit("manage_email")} />
          )}

          {emailSubTab === "providers" && (

            <EmailProviderPanel

              token={token}

              modeStatus={modeStatus}

              canEdit={canEdit("manage_email")}


            />

          )}

          {emailSubTab === "content" && <EmailContentPanel token={token} modeStatus={modeStatus} canEdit={canEdit("manage_email")} />}

          {emailSubTab === "subscribers" && <EmailSubscribersPanel token={token} canEdit={canEdit("manage_email")} />}

          {emailSubTab === "scheduler" && <EmailSchedulerPanel token={token} canEdit={canEdit("manage_email")} />}

          {emailSubTab === "activity" && <EmailActivityPanel token={token} modeStatus={modeStatus} canEdit={canEdit("manage_email")} />}

        </>

      )}

      {allowedSubTab === "sms" && <SmsProviderPanel token={token} modeStatus={modeStatus} canEdit={canEdit("manage_sms")} />}

      {/* "payment-providers" is still accepted as the id: it was the tab's
          name until 28 Aug 2026 and bookmarks to it should keep working. */}
        {(allowedSubTab === "finance" || allowedSubTab === "payment-providers") && (
        <>
          <TabBar
            tabs={[
              { id: "providers", label: "Providers" },
              { id: "going-live", label: "Going Live" },
              // THE PAYMENTS TAB IS GONE — the provider cards do its job.
              //
              // It chose between test and live payment credentials; a card IS a
              // provider-and-mode pair, so choosing one says both things at once
              // (1.250.0). Two places setting the same thing is how they drift.
              //
              // Its two unique facts were carried across FIRST: who switched live
              // payments on and when, and why they cannot be switched on. Removing
              // the tab without those would have deleted information rather than a
              // duplicate. Removed 6 Sep 2026.
              { id: "refunds", label: "Refunds" },
              { id: "transactions", label: "Transactions" },
            ]}
            activeId={paymentsSubTab}
            onSelect={(id) => navigate(`/admin/finance/${id}`)}
          />
          {paymentsSubTab === "providers" && <PaymentProviderPanel token={token} modeStatus={modeStatus} canEdit={canEdit("manage_finance")} />}
          {/* Deliberately NOT wrapped in ViewOnlyGate: this reports what
              has happened, it does not change anything, and a disabled
              fieldset around live numbers reads as broken. */}
          {paymentsSubTab === "going-live" && (
            <>
              <GoingLivePanel token={token} wsRequest={wsRequest} canEdit={canEdit("manage_finance")} />
              {/* THE SAME CHECKLIST ROOT SEES ON THE LIVE BUTTON. One
                  source, two views — reading it here should never
                  disagree with what the modal says, so it is the same
                  component rather than a second rendering of the same
                  idea.

                  ROOT ONLY: admin.liveReadiness.get requires root, and
                  the confirmations are statements about the business
                  rather than the software. */}
              {user?.role === "root" && (
                <div style={{ marginTop: 20 }}>
                  <GoLiveChecklist token={token} wsRequest={wsRequest} />
                </div>
              )}
            </>
          )}
          {paymentsSubTab === "refunds" && <RefundPolicyPanel token={token} wsRequest={wsRequest} canEdit={canEdit("manage_finance")} modeStatus={modeStatus} />}
          {paymentsSubTab === "transactions" && <AdminPaymentsPanel token={token} wsRequest={wsRequest} canEdit={canEdit("manage_finance")} modeStatus={modeStatus} />}
        </>
      )}


    </div>

  );

}



// canEdit here is a BOOLEAN (manage_competitions), already resolved by the
// caller — not the canEdit(permission) FUNCTION used at the top level.
// canEditDemoComps is separate because the Demo Comps panel this opens is
// governed by manage_demo_comps, a different permission entirely.
// LIVE QUEUE / TEST QUEUE (1.435.0) - see workingQueue in AdminApp. Shown
// only while the site is live: in Test mode there is only the Test queue.
function QueueSwitch({ queueSwitch }) {
  if (!queueSwitch) return null;
  const onTest = queueSwitch.value === "test";
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        flexWrap: "wrap",
        margin: "0 0 12px",
        padding: "8px 10px",
        borderRadius: 8,
        border: `1px solid ${onTest ? modeColor("test") : "var(--border-color)"}`,
      }}
    >
      <span style={{ fontSize: 12.5, opacity: 0.8 }}>Working on</span>
      <div role="group" aria-label="Which competition queue" style={{ display: "inline-flex", gap: 4 }}>
        {["live", "test"].map((q) => (
          <button
            key={q}
            type="button"
            aria-pressed={queueSwitch.value === q}
            onClick={() => queueSwitch.set(q)}
            style={{
              fontWeight: queueSwitch.value === q ? 700 : 400,
              borderColor: queueSwitch.value === q ? modeColor(q) : undefined,
            }}
          >
            {modeLabel(q)} queue
          </button>
        ))}
      </div>
      <span style={{ fontSize: 12, opacity: 0.7 }}>
        {onTest
          ? "Test competitions play on the partner's test site, with test payments. The site stays live."
          : "The live competitions."}
      </span>
    </div>
  );
}

function QueueControlPanel({ token, canEdit, canEditDemoComps, modeStatus }) {
  // Lifted out of PreviewLinks because the history panel now renders
  // BESIDE the queue box rather than inside it — the button lives in one
  // component and the panel in another, so the state has to sit above
  // both.
  const [showHistory, setShowHistory] = useState(false);
  // MEASURED, because CSS cannot express "match my sibling and no more".
  //
  // THE ROW MUST BE alignItems:flex-start, NOT stretch. With stretch the
  // queue box grows to the row height, so this measurement returns the
  // STRETCHED height — which is set by the panel beside it, which is then
  // capped at that same number. It measures itself and nothing ever caps.
  // That shipped once and the panel simply grew.
  //
  // With flex-start the queue box keeps its natural height, so this is a
  // real number to cap against. ResizeObserver rather than a one-off
  // read: the queue panel changes height when a preview link is disabled
  // or a queue starts.
  const queueBoxRef = useRef(null);
  const [queueHeight, setQueueHeight] = useState(null);
  useEffect(() => {
    const el = queueBoxRef.current;
    if (!el || typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver(() => setQueueHeight(el.offsetHeight));
    ro.observe(el);
    setQueueHeight(el.offsetHeight);
    return () => ro.disconnect();
  }, [showHistory]);

  // { running, hasActive, hasQueued } — hasActive/hasQueued are what let

  // the button distinguish "Start" (nothing active yet, only queued

  // competitions waiting) from "Resume" (paused, but a competition is

  // still actively running — pausing never stops one already active,

  // only holds back whatever's queued behind it) once paused, and "Play"

  // (nothing active, nothing queued — genuinely empty) from either.

  const [queueStatus, setQueueStatus] = useState(null);

  const [busy, setBusy] = useState(false);
  // WHICH ROW IS BUSY, not just whether something is.
  //
  // `busy` is one flag for the whole panel, and the demo row's spinner
  // condition included it unconditionally — so pressing the LIVE button
  // made the DEMO button flicker to "…". Harmless and unnerving: a
  // button you did not touch reacting to one you did.
  const [busyMode, setBusyMode] = useState(null);
  const refresh = () => {

    wsRequest("admin.queue.status", {}, token)

      .then(setQueueStatus)

      .catch(() => {});

  };

  useEffect(refresh, []);

  useEffect(() => onBroadcast((message) => {

    if (message.type === "site-settings-changed") refresh();

    if (message.type === "competitions-changed") {

      refresh();

    }

  }), []);



  const setAutoReschedule = async (mode, enabled) => {
    setBusy(true);
    try {
      await wsRequest("admin.queue.setAutoReschedule", { mode, enabled }, token);
      showToast(
        enabled
          ? `Auto Reschedule on for the ${modeLabel(mode)} queue.`
          : `Auto Reschedule off — fit missed competitions in yourself.`
      );
      refresh();
    } catch (err) {
      showToast(err.message || "Could not reach backend.", "error");
    }
    setBusy(false);
  };

  // GENERATE, THEN PLAY, THEN GENERATE AGAIN — the whole demo in one
  // button. It used to be five buttons on another tab in a required
  // order, then a trip back here to press Play.
  const regenerateDemo = async () => {
    setBusy(true);
    setBusyMode("demo");
    try {
      const data = await wsRequest("admin.demoQueue.regenerate", {}, token);
      showToast(
        `Demo ready — ${data.competitions} competitions and ${data.users} users. Press Play to run it.`
      );
      refresh();
    } catch (err) {
      showToast(err.message || "Could not reach backend.", "error");
    }
    setBusy(false);
  };

  // THE DEMO QUEUE HAS ITS OWN ACTION, and must have.
  //
  // `toggle` below used to send no mode, so the server routed it to
  // whichever queue matched the ACTING ADMIN's mode. That was right for
  // Test and Live, and wrong for a demo row that is pressable from
  // anywhere — pressing Play on the demo while in Live mode started and
  // paused the LIVE queue. (Since 1.435.0 toggle names its queue, and the
  // server still refuses one this admin may not manage; demo keeps its
  // own action because it also starts the simulation.)
  const startDemo = async () => {
    setBusy(true);
    setBusyMode("demo");
    try {
      await wsRequest("admin.demoQueue.setRunning", { running: true }, token);

      showToast("Demo running.");
      refresh();
    } catch (err) {
      showToast(err.message || "Could not reach backend.", "error");
    }
    setBusy(false);
  };

  // THE QUEUE PRESSED (1.435.0), not always the admin's own: from Live the
  // Test queue can be started and paused too - see `manageable` below.
  const toggle = async (m = adminMode(modeStatus)) => {

    setBusy(true);
    setBusyMode(m);

    try {

      const running = !(queueStatus.queues?.[m]?.running ?? queueStatus.running);
      const data = await wsRequest("admin.queue.toggle", { running, mode: m }, token);

      // data.running, not data.queueRunning — the action used to return
      // the whole siteSettings object, whose `queueRunning` is a per-mode
      // map. An object is always truthy, so this said "started" whichever
      // way the button was pressed.
      showToast(
        data.running
          ? `${modeLabel(data.mode)} queue started.`
          : `${modeLabel(data.mode)} queue paused.`
      );

      refresh();

    } catch (err) {

      showToast(err.message || "Could not reach backend.", "error");

    }

    setBusy(false);

  };



  if (queueStatus === null) return null;



  return (

    <div style={{ display: "flex", gap: 16, alignItems: "flex-start", marginBottom: 20, flexWrap: "wrap" }}>

    {/* SIDE BY SIDE, using the empty space to the right rather than
        pushing the page down. alignItems:stretch plus minHeight:0 inside
        the history means it matches this box's height and scrolls
        internally, so it can never make the row taller than the queue
        panel itself. */}
    <div ref={queueBoxRef} style={{ border: "1px solid var(--border-color)", borderRadius: 8, padding: 14, maxWidth: 500, flex: "0 1 auto", minWidth: 0 }}>

      <h4 style={{ marginTop: 0, display: "flex", alignItems: "center", gap: 6 }}>

        Queue Control Panel

        <span

          title="The competition queue is what actually starts, runs, and ends competitions on their own timers — mock or live, that's what the label reflects. The simulation only adds fake purchases/guesses on top of whatever's currently active; it has nothing to act against unless the competition queue is also running. Both on is the normal, working state for a full demo."

          style={{

            display: "inline-flex",

            alignItems: "center",

            justifyContent: "center",

            width: 15,

            height: 15,

            borderRadius: "50%",

            border: "1px solid var(--border-color)",

            fontSize: 10.5,

            opacity: 0.7,

            cursor: "help",

          }}

        >

          i

        </span>

      </h4>

      <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>

        {/*
          EVERY queue, not just the one this admin happens to be in. The
          whole point of three modes is that they run at once, so being
          unable to see whether Live is running while you work in Test
          would defeat it.

          But SEEING IS NOT ACTING: only a queue this admin may manage can
          be toggled - their own mode's, and since 1.435.0 the Test queue
          from Live, for the partner's test site. Stopping the Live queue
          is still something you have to be in Live mode to do.
        */}
        {QUEUE_MODES.map((m) => {
          const q = queueStatus.queues?.[m];
          if (!q) return null;
          // MANAGEABLE, not only "mine" (1.435.0): the server says which
          // queues this admin may start and pause - their own, and from
          // Live the Test queue too. An older server sends no list.
          const mine = Array.isArray(queueStatus.manageable)
            ? queueStatus.manageable.includes(m)
            : adminMode(modeStatus) === m;
          // NOTHING TO RUN IS CHECKED FIRST, before `running`.
          //
          // A queue flagged running with no active and no queued
          // competition showed "Pause" — and there was nothing to pause.
          // The state is real but transient: the maintenance tick stops an
          // empty queue on its next pass. Until then the button should
          // describe what is there, which is nothing.
          // A MISSED COMPETITION IS SOMETHING TO DO.
          //
          // Without hasNotRun this disabled Play whenever the only thing
          // left was a missed competition — so the queue could not be
          // started to rescue it, and Auto Reschedule only runs while the
          // queue is on. A deadlock, not an inconvenience.
          const nothingToRun = !q.hasActive && !q.hasQueued && !q.hasNotRun;
          // THE DEMO ROW IS ITS OWN THING, and reads differently from the
          // other two.
          //
          // Test and Live queues are operational: you pause them, resume
          // them, and you must be in that mode to do it, because stopping
          // a live queue is a real decision. The demo is a training aid —
          // it generates, it runs, it ends, and it cannot be paused
          // because there is nothing at stake in letting it finish.
          //
          // It is also actable from ANY mode. The rule that you must be
          // in a queue's own mode protects real things; demo data is not
          // real by construction, which is the whole reason it carries
          // mode "demo".
          const isDemo = m === "demo";
          const demoNeedsGenerating = isDemo && nothingToRun;
          const label = nothingToRun
            ? "\u25B6 Play"
            : q.running
              ? "\u23F8 Pause"
              : q.hasActive
                ? "\u25B6 Resume"
                : "\u25B6 Start";
          // Nothing to run means the button has nothing to do, whether or
          // not the queue flag happens to be set — the auto-stop-when-empty
          // clears that flag on the next maintenance tick anyway, so
          // offering "Pause" for it would be offering to do something
          // that is about to happen by itself.
          const nothingToDo = nothingToRun;
          return (
            <div key={m}>
              <div style={{ fontSize: 12, opacity: 0.7, marginBottom: 4 }}>
                {modeLabel(m)} competition queue
              </div>
              <button
                onClick={
                  demoNeedsGenerating
                    ? regenerateDemo
                    : isDemo
                      ? startDemo
                      : mine
                        ? () => toggle(m)
                        : undefined
                }
                disabled={
                  busy ||
                  !canEdit ||
                  // The demo row is pressable from any mode; the others
                  // still require being in their own.
                  (!isDemo && !mine) ||
                  // Generate always has something to do. Otherwise a
                  // queue with nothing in it has nothing to press.
                  (!demoNeedsGenerating && nothingToDo) ||
                  // A running demo cannot be paused — it ends by itself,
                  // and there is nothing at stake in letting it.
                  (isDemo && !demoNeedsGenerating && q.running)
                }
                style={{ borderColor: isDemo || mine ? modeColor(m) : undefined }}
                title={
                  !canEdit
                    ? "View only \u2014 your account doesn't have edit access here."
                    : demoNeedsGenerating
                      ? "Wipe any old demo data and build a fresh set"
                      : isDemo && q.running
                        ? "A demo runs to the end — it stops itself when it finishes"
                        : !isDemo && !mine
                          ? `Switch to ${modeLabel(m)} mode to control this queue`
                          : nothingToDo
                            ? "Nothing active or queued to play"
                            : undefined
                }
              >
                {busy && busyMode === m
                  ? "\u2026"
                  : demoNeedsGenerating
                    ? "Generate"
                    : isDemo && q.running
                      ? "Running\u2026"
                      : isDemo
                        ? "\u25B6 Play"
                        : label}
              </button>

              {/* THE WATCH LINK USED TO BE DUPLICATED HERE.
                  1.291.0 added an "Open this demo" link beside the button,
                  because starting a demo produced no way to look at it.
                  The real fix was making Play start the SIMULATION, which
                  is what mints the token — after which the preview link
                  section below fills in by itself. Two copies of one URL
                  is worse than one, so this is the one that went. */}

              {/* AUTO RESCHEDULE — test and live only.

                  A competition whose slot passed while the queue was
                  stopped sits in "To reschedule" and never starts on its own.
                  Off, an admin fits it back in with Propose Schedule and
                  chooses where it goes. On, the same proposal is applied
                  as gaps open.

                  Null for demo, which has Auto Load for this — so the
                  checkbox does not render there rather than rendering
                  something inert. */}
              {q.autoReschedule !== null && q.autoReschedule !== undefined && (
                <label
                  style={{ display: "flex", gap: 6, alignItems: "center", marginTop: 6, fontSize: 12 }}
                  title={
                    !mine
                      ? `Switch to ${modeLabel(m)} mode to change this`
                      : "Fit missed competitions into open slots automatically. Locked-in competitions are never moved."
                  }
                >
                  <input
                    type="checkbox"
                    checked={!!q.autoReschedule}
                    disabled={busy || !mine || !canEdit}
                    onChange={(e) => setAutoReschedule(m, e.target.checked)}
                  />
                  Auto Reschedule
                </label>
              )}
            </div>
          );
        })}

        {/*
          The Demo simulation used to have its own button here. It does
          not need one: a demo competition with no simulated buyers is a
          real competition nobody is entering — fog sitting still, a
          clock running down, resolving by lottery with no entrants.
          Correct behaviour and a useless demonstration, so the two were
          always started together anyway.

          Starting the DEMO QUEUE now starts the simulation with it (see
          admin.queue.toggle), and the reverse link already existed. The
          simulation can still be run on its own from the Mock Data
          tools, where the rest of the demo generators live.
        */}
        {/*
          The TEST preview link. Sits beside the queues because that is
          what it reaches: without it an admin can switch to Test mode
          but no buyer can ever land on a Test competition, since which
          PAGE you are on decides the mode and nothing else does.

          Not tied to a simulation the way the demo link is - Test has
          no simulation, and a partner needs a stable link while they
          work through the Going Live checks.
        */}
        <PreviewLinks token={token} canEdit={canEdit} showHistory={showHistory} setShowHistory={setShowHistory} />

      </div>

    </div>

    {showHistory && (
      <DemoCompsPanel
        token={token}
        modeStatus={modeStatus}
        canEdit={canEditDemoComps}
        maxHeight={queueHeight}
        onClose={() => setShowHistory(false)}
      />
    )}

    </div>

  );

}



function PreviewLinks({ token, canEdit = true, showHistory, setShowHistory }) {
  // showHistory comes from QueueControlPanel now: the button is here, the
  // panel renders beside the queue box, so neither owns the state.
  // Only the DEMO row offers the button — a demo link is filed when its
  // round finishes, and the test route has no rounds to finish.
  const [links, setLinks] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => {
    wsRequest("admin.previewLinks.status", {}, token)
      .then(setLinks)
      .catch(() => {});
  };
  useEffect(load, [token]);

  // A COUNTDOWN NEEDS A CLOCK. The reload below fires on broadcasts, and
  // nothing broadcasts while a queue merely WAITS for the next start — so
  // "starts in 55 seconds" would sit frozen at 55 until something else
  // happened.
  //
  // TICKED LOCALLY, NOT POLLED. The start time is already known; counting
  // down to it needs no server. An earlier version re-fetched every five
  // seconds, which made the number jump 55, 50, 45 and asked the backend
  // a question it had already answered.
  //
  // The server is still polled, but slowly and for a different reason: to
  // notice the competition has actually STARTED, since that changes the
  // line from a countdown to "live now".
  const pending = ["demo", "test"].some(
    (m) => links?.[m]?.queueRunning && !links[m].activeNow && links[m].nextStartAt
  );
  const [, setTick] = useState(0);
  useEffect(() => {
    if (!pending) return undefined;
    const tick = setInterval(() => setTick((n) => n + 1), 1000);
    const poll = setInterval(load, 15000);
    return () => {
      clearInterval(tick);
      clearInterval(poll);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pending]);

  // The queue state shown beside each link comes from the same
  // siteSettings the queue buttons above act on, so it has to follow
  // the same broadcasts. Without this the panel kept saying "the Test
  // queue is stopped" while the button beside it read "Pause" - worse
  // than saying nothing, which is why the line exists at all.
  useEffect(
    () =>
      onBroadcast((message) => {
        if (message.type === "site-settings-changed" || message.type === "competitions-changed") load();
      }),
    [token]
  );

  const call = async (action, payload) => {
    setBusy(true);
    try {
      await wsRequest(action, payload || {}, token);
      load();
    } catch (err) {
      showToast(err.message || "Could not reach backend.", "error");
    }
    setBusy(false);
  };

  if (!links) return null;

  // One row per mode that HAS a preview route. Live deliberately has
  // none: the live site is simply the site, reachable by anyone.
  return (
    <>
      {["demo", "test"].map((m) => {
        const info = links[m];
        if (!info) return null;
        const link = info.routeToken ? `${window.location.origin}/${m}-route-${info.routeToken}` : null;
        return (
          <div key={m} style={{ minWidth: 260, marginTop: 10 }}>
            <div style={{ fontSize: 12, opacity: 0.7, marginBottom: 4 }}>
              {modeLabel(m)} preview link
            </div>
            {!link ? (
              info.managed ? (
                <button onClick={() => call("admin.previewLinks.setEnabled", { enabled: true })} disabled={busy || !canEdit}>
                  {busy ? "\u2026" : "Create link"}
                </button>
              ) : (
                // Demo's link is minted by the simulation, so there is
                // nothing to press here — starting the Demo queue is
                // what brings it into existence.
                <div style={{ fontSize: 11.5, opacity: 0.7 }}>
                  Start the Demo queue to create this link.
                </div>
              )
            ) : (
              <>
                {/* THE LINK ITSELF, not a wall of URL with a Copy button
                    beside it. A preview link exists to be OPENED, and the
                    address is 60-odd characters of hex nobody reads — it
                    was taking four lines to say "click here".

                    New tab, because losing the admin you are working in to
                    look at the public site is not what anybody wanted. */}
                <div style={{ marginBottom: 6 }}>
                  <a
                    href={link}
                    target="_blank"
                    rel="noreferrer"
                    style={{ fontSize: 12.5, display: "inline-flex", alignItems: "center", gap: 5 }}
                  >
                    Open the {modeLabel(m).toLowerCase()} site
                    {/* The conventional "opens elsewhere" mark. Inline SVG
                        rather than an icon dependency, like everything
                        else in this file. */}
                    <svg
                      width="11"
                      height="11"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      aria-hidden="true"
                    >
                      <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                      <polyline points="15 3 21 3 21 9" />
                      <line x1="10" y1="14" x2="21" y2="3" />
                    </svg>
                  </a>
                </div>
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                  <button onClick={() => { navigator.clipboard.writeText(link); showToast(`${modeLabel(m)} link copied.`); }}>
                    Copy
                  </button>
                  {/* Rotating revokes a link that has been shared with
                      the wrong person — anyone holding the old one gets
                      the expired screen rather than data. */}
                  <button
                    onClick={() => call("admin.previewLinks.rotate", { mode: m })}
                    disabled={busy || !canEdit}
                    title="Replace this link, revoking the current one"
                  >
                    Rotate
                  </button>

                  {info.managed && (
                    <button
                      onClick={() => call("admin.previewLinks.setEnabled", { enabled: false })}
                      disabled={busy || !canEdit}
                      title={`Turn the ${modeLabel(m)} preview off entirely`}
                    >
                      Disable
                    </button>
                  )}
                </div>
              </>
            )}

              {/* BOTH states, not just the bad one. Saying nothing when
                  the queue is running made the absence of a warning mean
                  either "running" or "nobody checked", which is exactly
                  the ambiguity that costs someone ten minutes. */}
              <div style={{ fontSize: 11.5, opacity: 0.7, marginTop: 6 }}>
                {/* Three states, not two. A queue flagged running with
                    nothing in it used to report "running" while the
                    button beside it said "Pause" — the same
                    contradiction this line was added to prevent, in the
                    other direction. */}
                {!info.hasSomething
                  ? `No ${modeLabel(m)} competitions are queued, so nothing will go live on this link yet.`
                  : !info.queueRunning
                    ? `The ${modeLabel(m)} queue is stopped, so nothing will go live on this link yet.`
                    : info.activeNow
                      ? `The ${modeLabel(m)} queue is running, with a competition live now.`
                      : info.nextStartAt
                        ? `The ${modeLabel(m)} queue is running — the next competition starts ${countdownText(info.nextStartAt)}.`
                        : `The ${modeLabel(m)} queue is running, waiting for a competition to start.`}
              </div>

            {/* HISTORY, on the demo row, OUTSIDE the link conditional.

                It first sat beside Rotate — inside the branch that only
                renders when a preview link exists. The demo link exists
                only while a round is RUNNING, so the button vanished the
                moment a round finished: exactly when somebody wants the
                replay of it.

                Opens the Demo Comps panel — the five-competition
                walkthrough and its shareable replay links. */}
            {m === "demo" && (
              <button
                style={{ marginTop: 6 }}
                onClick={() => setShowHistory((v) => !v)}
                title="Past demo rounds and their shareable replay links"
              >
                {showHistory ? "Hide history" : "History"}
              </button>
            )}
          </div>
        );
      })}
    </>
  );
}

const TIME_UNIT_TO_MINUTES = { minutes: 1, hours: 60, days: 1440, weeks: 10080 };

// A LOTTERY HAS TO LAST LONG ENOUGH TO BE ONE (1.549.0).
//
// Asked for on 29 Sep 2026: "When setting up a competition that converts
// to a lottery the lottery must last for at least one minute." The
// lottery IS the lead time - it begins at end minus lead and draws at
// end - so the rule is a floor on that one field, and only where a
// lottery actually happens.
//
// A SECOND COPY OF THE SERVER'S RULE, deliberately: this file cannot
// import from server.js, and the server is the one that enforces it. A
// test asserts the two say the same thing, in the same words, which is
// the same bargain build-guide.mjs makes with its highlighter.
const MIN_LOTTERY_LEAD_MINUTES = 1;
const MIN_LOTTERY_LEAD_MESSAGE =
  "A competition that converts to a lottery must give the lottery at least 1 minute, " +
  "so the conversion lead time must be 1 minute or more.";



// Picks the largest unit that divides the value evenly, so an edit form

// pre-filled from an existing competition's stored milliseconds shows

// something readable ("7 days") rather than always falling back to a

// large raw minute count ("10080 minutes") just because that's the unit

// everything is stored in internally.

function msToValueUnit(ms) {

  const minutes = Math.round((ms || 0) / 60000);

  if (minutes > 0 && minutes % 10080 === 0) return { value: minutes / 10080, unit: "weeks" };
  if (minutes > 0 && minutes % 1440 === 0) return { value: minutes / 1440, unit: "days" };

  if (minutes > 0 && minutes % 60 === 0) return { value: minutes / 60, unit: "hours" };

  return { value: minutes, unit: "minutes" };

}



// The full competition form fields — shared by both Create and Edit so

// they can never again drift apart the way they did before (Edit was

// missing start-delay and private-view entirely). `imageFile`/

// `setImageFile` are only needed (and only rendered) when `showImageField`

// is true — Create requires a fresh upload, Edit doesn't touch the image

// at all, so there's nothing to show there.

// Drop-in replacement for window.confirm — same shape (call it, await a

// boolean) but renders a real modal instead of the browser's native

// dialog. Each component that needs confirmation calls this hook itself

// and renders the returned `confirmModal` element somewhere in its own

// tree; the hook's internal state means only one confirmation can be

// pending per component instance at a time, which matches how

// window.confirm already worked (blocking, one at a time) closely enough

// that call sites don't need to change beyond `window.confirm(msg)` →

// `await confirm(msg)`.

// Wraps a section of a panel's UI (typically the create form and/or

// per-item action buttons) so it's fully disabled for an admin who

// only has View-level access for that permission — see canEdit in the

// main AdminApp component. Uses a native disabled <fieldset> rather

// than disabling each button/input individually: it disables every

// form control inside at once, however deeply nested, is the correct

// accessible way to do this (keyboard/screen-reader navigation

// already skips disabled controls), and keeps each panel's own edits

// down to wrapping the relevant section once instead of touching every

// control separately. Border/padding/margin reset since a <fieldset>

// carries default browser chrome that would otherwise disturb layout.

function ViewOnlyGate({ canEdit, children, showNotice = false, style }) {

  if (canEdit) return children;

  return (

    <fieldset disabled style={{ border: "none", padding: 0, margin: 0, ...style }}>

      {showNotice && (

        <p style={{ opacity: 0.7, fontSize: 13, marginBottom: 10 }}>

          View only — your account doesn't have edit access here.

        </p>

      )}

      <div style={{ opacity: 0.55 }}>{children}</div>

    </fieldset>

  );

}



function useConfirm() {

  const [pending, setPending] = useState(null); // { message, resolve, typeToConfirm }

  const [typedValue, setTypedValue] = useState("");

  const typedValueRef = useRef(null);



  const confirm = (message, options) =>

    new Promise((resolve) => {

      setTypedValue("");

      setPending({ message, resolve, typeToConfirm: options?.typeToConfirm || null });

    });



  const resolve = (value) => {

    pending.resolve(value);

    setPending(null);

  };



  const canConfirm = !pending?.typeToConfirm || typedValue === pending.typeToConfirm;

  // Mirrors canConfirm above via the browser's own native validity state,

  // so the red/green border reflects "matches the required confirmation

  // text" specifically, not just "field is non-empty".

  useEffect(() => {

    if (typedValueRef.current) {

      typedValueRef.current.setCustomValidity(canConfirm ? "" : "Doesn't match.");

    }

  }, [canConfirm]);



  const confirmModal = pending && (

    <div

      onClick={() => resolve(false)}

      style={{

        position: "fixed",

        inset: 0,

        zIndex: 1000,

        background: "rgba(0,0,0,0.5)",

        display: "flex",

        alignItems: "center",

        justifyContent: "center",

      }}

    >

      <div

        onClick={(e) => e.stopPropagation()}

        style={{

          background: "var(--bg-panel-alt)",

          border: "1px solid var(--border-color)",

          borderRadius: 10,

          boxShadow: "0 4px 20px rgba(0,0,0,0.5)",

          padding: 24,

          maxWidth: 420,

          margin: 20,

        }}

      >

        <p style={{ marginTop: 0, whiteSpace: "pre-line" }}>{pending.message}</p>

        {pending.typeToConfirm && (

          <div style={{ marginBottom: 4 }}>

            <label style={{ fontSize: 12.5, opacity: 0.75, display: "block", marginBottom: 4 }}>

              Type <strong>{pending.typeToConfirm}</strong> to confirm:

            </label>

            <input

              ref={typedValueRef}

              type="text"

              required

              value={typedValue}

              onChange={(e) => setTypedValue(e.target.value)}

              autoFocus

              style={{

                width: "100%",

                padding: 8,

                background: "var(--bg-input)",

                border: "1px solid var(--border-color-light)",

                borderRadius: 6,

                color: "var(--text-primary)",

                boxSizing: "border-box",

              }}

            />

          </div>

        )}

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 16 }}>

          <button onClick={() => resolve(false)}>Cancel</button>

          <button

            onClick={() => resolve(true)}

            disabled={!canConfirm}

            style={{ background: "var(--accent-red)", color: "#fff", borderColor: "var(--accent-red)" }}

          >

            Confirm

          </button>

        </div>

      </div>

    </div>

  );



  return { confirm, confirmModal };

}



// What a price per pixel actually costs a buyer, worked out.

//

// Every image is resized to a fixed 1000x750, so this is exact, not a

// rough guide — the only variable is the price.

//

// This exists because price per pixel and prize money are set

// independently and nothing connected them. The first real competition

// anyone ran was priced at the old default of 1.00 per pixel against a

// 100 prize; a buyer took roughly a fifth of the image and was charged

// 161,989. The arithmetic was correct and nobody had ever seen it

// written down before it happened.

function PriceGuide({ pricePerPixel, prizeMoney, symbol }) {

  const price = Number(pricePerPixel);

  if (!Number.isFinite(price) || price <= 0) return null;



  const money = (n) =>

    `${symbol}${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;



  const wholeImage = price * 1000 * 750;

  const prize = Number(prizeMoney);

  // Worth flagging when one modest selection already exceeds the prize:

  // that is the shape of the mistake this is here to prevent, and it is

  // much easier to see as a sentence than as two unrelated numbers.

  const smallPatchCost = price * 100 * 100;

  const outOfProportion = Number.isFinite(prize) && prize > 0 && smallPatchCost > prize;



  return (

    <div style={{ fontSize: 12.5, opacity: 0.8, margin: "-4px 0 10px", lineHeight: 1.5 }}>

      At {money(price)} per pixel: a 20&times;20 patch costs {money(price * 400)}, 100&times;100 costs{" "}

      {money(smallPatchCost)}, and the whole 1000&times;750 image is {money(wholeImage)}.

      {outOfProportion && (

        <div style={{ color: "var(--accent-yellow)", marginTop: 4 }}>

          A single 100&times;100 selection would collect more than the {money(prize)} prize. That may be intended — worth

          a look before you start it.

        </div>

      )}

    </div>

  );

}



function CompetitionFormFields({ form, setForm, symbol, showImageField, imageFile, setImageFile, editing }) {

  const inputStyle = {

    width: "100%",

    padding: 8,

    marginTop: 4,

    marginBottom: 14,

    background: "var(--bg-input)",

    border: "1px solid var(--border-color-light)",

    borderRadius: 6,

    color: "var(--text-primary)",

  };



  // Live ceiling for private-view duration — it can't outlast the point

  // this competition actually converts to lottery, so it depends on

  // whatever duration/lottery-lead values are currently in the form.

  // Recomputed on every render, so it updates instantly as those change.

  const durationTotalMinutes = Number(form.durationValue) * TIME_UNIT_TO_MINUTES[form.durationUnit];

  const lotteryLeadTotalMinutes = Number(form.lotteryLeadValue) * TIME_UNIT_TO_MINUTES[form.lotteryLeadUnit];

  // A LOTTERY HAS TO LAST LONG ENOUGH TO BE ONE (1.549.0). Said here, in
  // the shared fields, so the create form and the edit form cannot
  // disagree about it - and said beside the box rather than only on
  // submit, which is how the private-view ceiling above has always
  // worked.
  const lotteryTooShort = form.lotteryEnabled !== false && lotteryLeadTotalMinutes < MIN_LOTTERY_LEAD_MINUTES;

  const maxPrivateViewMinutes = Math.max(0, durationTotalMinutes - lotteryLeadTotalMinutes);

  const currentPrivateViewMinutes = Number(form.privateViewHours) * 60 + Number(form.privateViewMinutes);

  const privateViewExceedsMax = currentPrivateViewMinutes > maxPrivateViewMinutes;



  return (

    <>

      <label>

        {editing ? "Answer (leave blank to keep the current one)" : 'Answer (e.g. "eiffel tower")'}

        {/* REQUIRED ONLY WHEN CREATING (1.517.0).

            Reported 28 Sep 2026: "I can't reschedule a competition because

            the answer has been encrypted and not showing on the form

            meaning it won't pass form validation."

            1.510.0 stopped pre-filling this on the edit card - the card

            shows asterisks, and a form that quietly typed the real answer

            back in would undo that - and taught the backend that a blank

            answer means leave it alone. The label was changed to say so.

            The `required` from the create form stayed, so the browser

            refused to submit the edit form AT ALL, whatever else had been

            changed on it: the whole card was unsavable, not just the

            answer. Typing one still replaces it, sealed like any other.

            A new competition still needs one - there is nothing to keep. */}

        <input

          value={form.answer}

          onChange={(e) => setForm({ ...form, answer: e.target.value })}

          style={inputStyle}

          required={!editing}

        />

      </label>

      <label>

        Answer matching

        <select

          value={form.answerMode}

          onChange={(e) => setForm({ ...form, answerMode: e.target.value })}

          style={inputStyle}

        >

          <option value="exact">Exact match required</option>

          <option value="fuzzy">Fuzzy match accepted</option>

        </select>

      </label>

      {/* ---- WHAT A BUYER WHO WAS BEATEN TO IT IS TOLD (1.789.1) ---- */}

      {/*                                                                  */}

      {/* 1.789.0 made a late entry its own outcome - evaluated, charged, */}

      {/* refunded in kind and answered with a sentence instead of a 404 - */}

      {/* and shipped the setting with no way to choose it. Every          */}

      {/* competition reads the default wording until an operator changes  */}

      {/* one here.                                                        */}

      {/*                                                                  */}

      {/* THE WORDING BOX SAYS WHEN IT IS NOT USED rather than sitting     */}

      {/* there inviting an operator to type something nothing will show.  */}

      <label>

        If someone is beaten to the answer

        <select

          value={form.missedNoticeMode}

          onChange={(e) => setForm({ ...form, missedNoticeMode: e.target.value })}

          style={inputStyle}

        >

          <option value="text">Show a message</option>

          <option value="margin">Show how far behind they were</option>

        </select>

      </label>

      <label>

        {form.missedNoticeMode === "margin"

          ? "Message (not shown while the margin is selected)"

          : "Message"}

        <input

          type="text"

          maxLength={200}

          value={form.missedNoticeText}

          placeholder="Someone ahead of you won"

          onChange={(e) => setForm({ ...form, missedNoticeText: e.target.value })}

          disabled={form.missedNoticeMode === "margin"}

          style={inputStyle}

        />

        {/* display:block, BECAUSE small IS INLINE AND THE NEXT LABEL RAN ON   */}

        {/* TO IT. Reported from a screenshot on 14 Oct 2026, where the page    */}

        {/* read "...a wrong guess is never told a margin.Hidden image file" -  */}

        {/* two unrelated sentences with no space between them, which reads as  */}

        {/* one broken one.                                                     */}

        <small style={{ opacity: 0.7, display: "block" }}>

          Left blank, the site says "Someone ahead of you won". Only shown to

          someone whose answer was right - a wrong guess is never told a margin.

        </small>

      </label>

      {showImageField && (

        <>

          <label>

            Hidden image file

            <input

              type="file"

              accept="image/*"

              onChange={(e) => setImageFile(e.target.files[0])}

              style={inputStyle}

              required

            />

          </label>

          <p style={{ fontSize: 12, opacity: 0.55, marginTop: -8, marginBottom: 14 }}>

            Every competition uses the same 1000×750 canvas — a smaller image is centered on a dark background to

            fit, a larger one is scaled down to fit within it, so fog tiling and pixel pricing stay consistent

            across every competition.

          </p>

        </>

      )}

      <label>

        Price per pixel ({symbol})

        <input

          type="number"

          step="0.01"

          min="0.01"

          value={form.pricePerPixel}

          onChange={(e) => setForm({ ...form, pricePerPixel: e.target.value })}

          style={inputStyle}

          required

        />

      </label>

      {/*

        What that price actually means, worked out.



        Price per pixel and prize money are set independently, and

        nothing used to connect them — so it was entirely possible to

        run a competition where a single ordinary-looking selection

        collected far more than the prize was worth. That happened on

        the first real competition anyone ran: a buyer took about a

        fifth of the image and was charged £161,989 against a £100

        prize, at the then-default of £1.00 per pixel.



        The arithmetic was right; nobody had ever seen it written down.

        Every image is resized to a fixed 1000x750, so these figures are

        exact rather than indicative.

      */}

      <PriceGuide pricePerPixel={form.pricePerPixel} prizeMoney={form.prizeMoney} symbol={symbol} />

      <label>

        Prize money ({symbol})

        <input

          type="number"

          step="1"

          min="0"

          value={form.prizeMoney}

          onChange={(e) => setForm({ ...form, prizeMoney: e.target.value })}

          style={inputStyle}

        />

      </label>

      <label>

        Credits per entry, after the free one

        <input

          type="number"

          step="0.01"

          min="0.01"

          value={form.creditsPerEntry}

          onChange={(e) => setForm({ ...form, creditsPerEntry: e.target.value })}

          style={inputStyle}

          required

        />

      </label>

        {/* The bonus field is gone. Credit earned by buying pixels is a
            SITEWIDE rate now (Competition management → Settings) rather
            than a per-competition one — set once, and granting CREDIT
            rather than free guesses. */}

      <label>

        Starts at

        <input

          type="datetime-local"

          value={form.scheduledStartLocal}

          onChange={(e) => setForm({ ...form, scheduledStartLocal: e.target.value })}

          style={inputStyle}

        />

        {/*
          An ABSOLUTE date and time, replacing "start delay after the
          previous competition is answered". That delay could not be
          turned into an answer to "when does the next one begin?" without
          simulating the queue, and the answer moved as competitions
          resolved — so it could never be advertised.

          The trade, and it is deliberate: a competition that misses this
          time does NOT slide. It moves to the "To reschedule" tab, keeping its image,
          answer and pricing, and waits for a new date.
        */}
        <div style={{ fontSize: 12, opacity: 0.6, marginTop: -8, marginBottom: 14 }}>

          Leave blank to start as soon as the queue reaches it. A competition that misses its slot moves to

          the "To reschedule" tab rather than sliding — it keeps everything else and just needs a new date.

        </div>

      </label>



      {/* THE LOTTERY IS THIS COMPETITION'S CHOICE, not the site's.



          It used to be a sitewide toggle read at the moment a competition

          STARTED, so you could not tell from a competition whether it

          would have an end, and changing the setting silently changed

          every competition not yet started.



          The two fields below are meaningless without a draw, so they are

          disabled when this is off — which is also the clearest way to

          show that such a competition has no end at all. */}

      <label style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>

        <input

          type="checkbox"

          checked={form.lotteryEnabled !== false}

          onChange={(e) => setForm({ ...form, lotteryEnabled: e.target.checked })}

          style={{ marginTop: 3 }}

        />

        <span>

          Ends in a lottery draw if nobody solves it

          <div style={{ fontSize: 12, opacity: 0.6, fontWeight: "normal" }}>

            Unticked, this competition has NO END — it runs until somebody wins it, and the two

            fields below do not apply. Nothing behind it can be given a confirmed start time either,

            since there is no end to schedule against.

          </div>

        </span>

      </label>



      <label style={{ opacity: form.lotteryEnabled === false ? 0.45 : 1 }}>

        Competition duration (once it starts)

        <div style={{ display: "flex", gap: 8 }}>

          <input

            type="number"

            step="1"

            min="1"

            required

            value={form.durationValue}

              disabled={form.lotteryEnabled === false}

            onChange={(e) => setForm({ ...form, durationValue: e.target.value })}

            style={{ ...inputStyle, flex: 1 }}

          />

          <select

            value={form.durationUnit}

              disabled={form.lotteryEnabled === false}

            onChange={(e) => setForm({ ...form, durationUnit: e.target.value })}

            style={{ ...inputStyle, flex: 1 }}

          >

            <option value="minutes">Minutes</option>

            <option value="hours">Hours</option>

            <option value="days">Days</option>

          </select>

        </div>

        <div style={{ fontSize: 12, opacity: 0.6, marginTop: -8, marginBottom: 14 }}>

          Measured from when it actually starts, not a fixed calendar date — so a delayed queue can't push it

          past its own deadline.

        </div>

      </label>



      <label style={{ opacity: form.lotteryEnabled === false ? 0.45 : 1 }}>

        Convert to lottery this long before the end, if still unsolved

        <div style={{ display: "flex", gap: 8 }}>

          <input

            type="number"

            step="1"

            min="0"

            value={form.lotteryLeadValue}

              disabled={form.lotteryEnabled === false}

            onChange={(e) => setForm({ ...form, lotteryLeadValue: e.target.value })}

            style={{ ...inputStyle, flex: 1 }}

          />

          <select

            value={form.lotteryLeadUnit}

              disabled={form.lotteryEnabled === false}

            onChange={(e) => setForm({ ...form, lotteryLeadUnit: e.target.value })}

            style={{ ...inputStyle, flex: 1 }}

          >

            <option value="minutes">Minutes</option>

            <option value="hours">Hours</option>

            <option value="days">Days</option>

          </select>

        </div>

        <div
          data-competition-lottery-lead-note=""
          style={{ fontSize: 12, marginTop: -8, marginBottom: 14, color: lotteryTooShort ? "var(--accent-red)" : undefined, opacity: lotteryTooShort ? 1 : 0.6 }}
        >

          {lotteryTooShort
            ? MIN_LOTTERY_LEAD_MESSAGE
            : "Once converted, guessing freezes (purchasing stays open \u2014 each purchase becomes more lottery tickets). A random ticket is drawn at the actual end time if it's still unsolved. Must be at least 1 minute, and less than the duration above."}

        </div>

      </label>



      <label>

        Private-view duration

        <div style={{ display: "flex", gap: 8 }}>

          <select

            value={form.privateViewHours}

            onChange={(e) => setForm({ ...form, privateViewHours: Number(e.target.value) })}

            style={{ ...inputStyle, flex: 1 }}

          >

            {Array.from({ length: 24 }, (_, h) => (

              <option key={h} value={h}>

                {h} hour{h === 1 ? "" : "s"}

              </option>

            ))}

          </select>

          <select

            value={form.privateViewMinutes}

            onChange={(e) => setForm({ ...form, privateViewMinutes: Number(e.target.value) })}

            style={{ ...inputStyle, flex: 1 }}

          >

            {Array.from({ length: 60 }, (_, m) => (

              <option key={m} value={m}>

                {m} minute{m === 1 ? "" : "s"}

              </option>

            ))}

          </select>

        </div>

        <div style={{ fontSize: 12, marginTop: -8, marginBottom: 14, color: privateViewExceedsMax ? "var(--accent-red)" : undefined, opacity: privateViewExceedsMax ? 1 : 0.6 }}>

          {privateViewExceedsMax

            ? `⚠ Exceeds the maximum of ${maxPrivateViewMinutes} minute${maxPrivateViewMinutes === 1 ? "" : "s"} allowed — reduce this, or increase the duration/lottery-lead settings above.`

            : `Up to ${maxPrivateViewMinutes} minute${maxPrivateViewMinutes === 1 ? "" : "s"} allowed, based on the duration/lottery-lead settings above.`}

        </div>

      </label>

    </>

  );

}





// The create-competition form, lifted out of CompetitionsPanel when Chart,

// Stats and this moved from the Competitions tab into Competition

// Management as sub-tabs of their own.

//

// Extracted rather than reached into from a shared parent because all of

// its state is genuinely its own — the form, the chosen image, the

// site-defaults prefill, the live ceiling on private-view duration — and

// none of it was ever read by the competition lists it used to sit beside.

//

// Gated on manage_competition_defaults now, inherited from the tab it

// lives under, rather than the manage_competitions it had before. That is

// a deliberate consequence of the move rather than an oversight: an admin

// holding one permission and not the other sees a different set of tools

// than they did before.

function CreateCompetitionPanel({ token, canEdit, modeStatus, onCreated, queueSwitch = null }) {

  const symbol = useCurrencySymbol();

  const [busy, setBusy] = useState(false);

  const [form, setForm] = useState({

    answer: "",

    answerMode: "exact",

    // ---- THE DEFAULTS MATCH THE BACKEND'S, AND IT DECLINES TO STORE THEM ----
    //
    // lib/missed-notice.js defaults the pair where it is READ, so a competition
    // with neither field set says the same thing as one set to these. These are
    // here so the controls have a value to show; missedNoticeStorable drops a
    // value equal to the default rather than putting a meaningless key on every
    // competition the panel creates.

    missedNoticeMode: "text",

    missedNoticeText: "",

    pricePerPixel: 0.02,

    privateViewHours: 0,

    privateViewMinutes: 1,

    prizeMoney: 100,

    creditsPerEntry: 5,

    

    scheduledStartLocal: "",

    durationValue: 7,

    durationUnit: "days", // minutes | hours | days

    lotteryLeadValue: 1,

    lotteryLeadUnit: "hours", // minutes | hours | days

    // THIS COMPETITION'S OWN LOTTERY CHOICE, seeded from the sitewide

    // setting below once it loads. Without a lottery a competition has no

    // end at all — it runs until somebody wins it — so the duration and

    // lottery lead are disabled when this is off.

    lotteryEnabled: true,

  });

  const [imageFile, setImageFile] = useState(null);



  // Pre-fill price-per-pixel/entry-price/bonus-guess-threshold from root's

  // site-wide defaults — an admin can still change them per competition,

  // this just sets a sensible starting point instead of arbitrary

  // hardcoded numbers. Bonus guess threshold is computed here (not stored

  // directly) since root expresses it as either a fixed rate or a

  // percentage of the default entry price — the actual per-competition

  // field has always just been "£ spent per bonus guess".

  useEffect(() => {

    wsRequest("siteSettings.get")

      .then((data) => {






        setForm((prev) => ({

          ...prev,

          pricePerPixel: data.defaultPricePerPixel ?? prev.pricePerPixel,

          prizeMoney: data.defaultPrizeMoney ?? prev.prizeMoney,

          creditsPerEntry: data.defaultCreditsPerEntry ?? prev.creditsPerEntry,


          // The sitewide setting is the DEFAULT for a new competition,

          // not the answer for it. Change it here and only this

          // competition is affected.

          lotteryEnabled: data.lotteryEnabled ?? prev.lotteryEnabled,

          // ---- AND THE LATE-ENTRY NOTICE, ASKED FOR ON 14 Oct 2026 ----

          //

          // Same rule as the line above, deliberately: the panel seeds a NEW

          // competition and nothing more. Change the default afterwards and

          // competitions already made keep what they were given - which is why

          // missedNoticeStorable still compares against the MODULE default

          // rather than this one. See lib/missed-notice.js's header.

          missedNoticeMode: data.defaultMissedNoticeMode ?? prev.missedNoticeMode,

          missedNoticeText: data.defaultMissedNoticeText ?? prev.missedNoticeText,

        }));

      })

      .catch(() => {});

  }, []);



  const unitToMinutes = { minutes: 1, hours: 60, days: 1440 };



  // Live ceiling for private-view duration — it can't outlast the point

  // this competition actually converts to lottery, so it depends on

  // whatever duration/lottery-lead values are currently in the form.

  // Recomputed on every render, so it updates instantly as those change.

  const durationTotalMinutes = Number(form.durationValue) * unitToMinutes[form.durationUnit];

  const lotteryLeadTotalMinutes = Number(form.lotteryLeadValue) * unitToMinutes[form.lotteryLeadUnit];

  // A LOTTERY HAS TO LAST LONG ENOUGH TO BE ONE (1.549.0) - the same rule
  // the server enforces, checked here so the Create button says so before
  // the request rather than after it.
  const lotteryTooShort = form.lotteryEnabled !== false && lotteryLeadTotalMinutes < MIN_LOTTERY_LEAD_MINUTES;

  const maxPrivateViewMinutes = Math.max(0, durationTotalMinutes - lotteryLeadTotalMinutes);

  const currentPrivateViewMinutes = Number(form.privateViewHours) * 60 + Number(form.privateViewMinutes);

  const privateViewExceedsMax = currentPrivateViewMinutes > maxPrivateViewMinutes;



  // Mirrors every check the submit handler runs — used to disable the

  // Create button live, so a problem is visible before even trying to

  // submit rather than only after.

  const formIsValid =

    imageFile &&

    form.answer.trim() &&

    Number.isFinite(Number(form.pricePerPixel)) && Number(form.pricePerPixel) >= 0.01 &&

    Number.isFinite(Number(form.creditsPerEntry)) && Number(form.creditsPerEntry) >= 1 &&

    Number(form.prizeMoney) >= 0 &&


    durationTotalMinutes > 0 &&

    lotteryLeadTotalMinutes >= 0 &&

    !lotteryTooShort &&

    lotteryLeadTotalMinutes < durationTotalMinutes &&

    currentPrivateViewMinutes > 0 &&

    !privateViewExceedsMax;



  const create = async (e) => {

    e.preventDefault();

    setBusy(true);

    if (!imageFile) {

      showToast("Please choose an image file for the hidden image.", "error");

      setBusy(false);

      return;

    }

    if (!form.answer.trim()) {

      showToast("Answer is required.", "error");

      setBusy(false);

      return;

    }

    if (!Number.isFinite(Number(form.pricePerPixel)) || Number(form.pricePerPixel) < 0.01) {

      showToast("Price per pixel must be at least 0.01.", "error");

      setBusy(false);

      return;

    }

    if (!Number.isFinite(Number(form.creditsPerEntry)) || Number(form.creditsPerEntry) < 1) {

      showToast("Credits per entry must be a whole number, at least 1.", "error");

      setBusy(false);

      return;

    }

    if (Number(form.prizeMoney) < 0) {

      showToast("Prize money must be zero or a positive number.", "error");

      setBusy(false);

      return;

    }


    const scheduledStartMs = localInputToMs(form.scheduledStartLocal);

    if (form.scheduledStartLocal && scheduledStartMs === null) {

      showToast("Starts at must be a valid date and time.", "error");

      setBusy(false);

      return;

    }

    if (durationTotalMinutes <= 0) {

      showToast("Competition duration must be a positive number.", "error");

      setBusy(false);

      return;

    }

    if (lotteryLeadTotalMinutes < 0) {

      showToast("Lottery conversion lead time must be zero or a positive number.", "error");

      setBusy(false);

      return;

    }

    if (lotteryLeadTotalMinutes >= durationTotalMinutes) {

      showToast("Lottery conversion lead time must be less than the competition duration.", "error");

      setBusy(false);

      return;

    }

    if (lotteryTooShort) {

      showToast(MIN_LOTTERY_LEAD_MESSAGE, "error");

      setBusy(false);

      return;

    }

    const totalPrivateViewMinutes = Number(form.privateViewHours) * 60 + Number(form.privateViewMinutes);

    if (totalPrivateViewMinutes <= 0) {

      showToast("Private-view duration must be more than 0 minutes.", "error");

      setBusy(false);

      return;

    }

    if (totalPrivateViewMinutes > maxPrivateViewMinutes) {

      showToast(

        `Private-view duration can't exceed ${maxPrivateViewMinutes} minute(s) given the current duration/lottery-lead settings.`,

        "error"

      );

      setBusy(false);

      return;

    }

    try {

      const body = new FormData();

      body.append("image", imageFile);
      // THE QUEUE (1.435.0): the one being worked on, which from Live can
      // be the Test queue. The server checks this admin may manage it.
      if (modeStatus) body.append("queue", adminMode(modeStatus));

      body.append("answer", form.answer);

      body.append("answerMode", form.answerMode);

      // BOTH SENT ON EVERY CREATE, which is deliberate: the backend treats an
      // absent field as "leave it alone", so a blank box could never clear a
      // wording if it were omitted. It declines to store a value equal to the
      // default, so sending them costs nothing.

      body.append("missedNoticeMode", form.missedNoticeMode);

      body.append("missedNoticeText", form.missedNoticeText);

      body.append("pricePerPixel", form.pricePerPixel);

      body.append("privateViewMinutes", totalPrivateViewMinutes);

      body.append("prizeMoney", form.prizeMoney);

      body.append("creditsPerEntry", form.creditsPerEntry);

      

      // Blank means "as soon as the queue reaches it", which the backend
      // reads as now. Anything else is an absolute moment.
      if (scheduledStartMs !== null) body.append("scheduledStartAt", scheduledStartMs);
      body.append("startDelayMinutes", 0);

      body.append("durationMinutes", durationTotalMinutes);

      body.append("lotteryLeadMinutes", lotteryLeadTotalMinutes);

    // THIS COMPETITION'S OWN LOTTERY CHOICE. Sent explicitly so the
    // backend does not have to fall back to the sitewide setting, which
    // could have changed between creating this and it starting.
    body.append("lotteryEnabled", form.lotteryEnabled ? "true" : "false");



      // NOTE: no Content-Type header here — the browser sets the correct

      // multipart boundary itself when the body is FormData. Sending our

      // usual JSON Content-Type would break the upload.

      const res = await fetch(`${API_BASE}/api/admin/competitions`, {

        method: "POST",

        headers: { "x-auth-token": token },

        body,

      });

      const data = await res.json();

      if (!res.ok) {

        if (res.status === 428) triggerMandatoryTotpRequired();

        if (res.status === 401) triggerSessionInvalid();

        showToast(data.error, "error");

        setBusy(false);

        return;

      }

      setForm({ ...form, answer: "" });

      setImageFile(null);

      // The backend warns when a competition cannot run at the time just
      // given — scheduling behind an open-ended one is ALLOWED, so that
      // it is recorded rather than blocking a partner's whole run, but it
      // will miss its slot and the operator should hear that now.
      // THE PAUSE IS SAID OUT LOUD (1.490.0). Creating a competition
      // stops its queue; that is deliberate, and it was silent, which is
      // how a round stopped between competitions on 26 Sep 2026 with
      // nothing on screen or in the activity log to explain it. Carried
      // beside the warning rather than instead of it - a competition can
      // both miss its slot and have paused the queue, and the operator
      // needs to hear both.
      const created = [data.warning, data.queuePaused].filter(Boolean);
      if (created.length) showToast(created.join(" "), data.warning ? "error" : "success");
      else showToast("Competition created.");

      // Hands the caller the new competition's status so it can jump to the

      // matching list. That list lives under a different top-level tab now,

      // so this is a route change rather than the local setTab it used to

      // be — and there is no local list left here to reload: the

      // Competitions panel refreshes itself off the competitions-changed

      // broadcast.

      onCreated?.(data.status);

    } catch {

      showToast("Could not reach backend.", "error");

    }

    setBusy(false);

  };



  return (

    <section style={{ marginTop: 20 }}>

      <h3>Create competition</h3>

      <ViewOnlyGate canEdit={canEdit} showNotice>

        <div style={{ maxWidth: 700 }}>

          <QueueSwitch queueSwitch={queueSwitch} />

          {modeStatus && (

            <p style={{ fontSize: 13, opacity: 0.75, marginTop: 0 }}>

              This will be created as{" "}

              <strong>{modeLabel(adminMode(modeStatus))}</strong> data — matching{" "}

              {modeStatus.workingQueue === "test"
                ? "the Test queue chosen above, while the site stays live"
                : modeStatus.forcedByRoot
                  ? "the mode root has the whole site in"
                  : "your own current mode"}{" "}
              above.

            </p>

          )}

          <form onSubmit={create}>

            <CompetitionFormFields

              form={form}

              setForm={setForm}

              symbol={symbol}

              showImageField

              imageFile={imageFile}

              setImageFile={setImageFile}

            />

            <button type="submit" disabled={busy || !formIsValid}>

              {busy ? "Creating…" : "Create competition"}

            </button>

          </form>

        </div>

      </ViewOnlyGate>

    </section>

  );

}



function CompetitionsPanel({ authHeaders, isRoot = false, token, onViewUser, tab, onTabChange, onViewCompetitionChart, canEdit, canEditDemoComps, modeStatus, realModeStatus = modeStatus, queueSwitch = null }) {



  // FOLLOW A COMPETITION WHEN IT MOVES TABS.
  //
  // Status is derived from the clock, so a competition crosses from Queued
  // to Active to "To reschedule" on its own. Without this the tab you are looking
  // at simply empties and the thing you were watching is somewhere else.
  //
  // Optional, because a tab that changes under you is startling if you did
  // not expect it — and disruptive if you are mid-edit on another tab.
  //
  // OFF BY DEFAULT since 6 Sep 2026. The same reasoning that made it
  // optional argues for it being off until asked for: somebody meeting
  // this admin for the first time should not have the page move under
  // them, and anybody who wants it can tick it.
  const [autoSelectTab, setAutoSelectTab] = useState(false);
  // What each competition's status was last time we looked, so a CHANGE
  // can be spotted. A ref rather than state: noticing a change must not
  // itself cause a render.

  const setTab = onTabChange; // URL is the source of truth now — see parseAdminPath in AdminApp

  const { confirm, confirmModal } = useConfirm();



  // Root's configured starting point for paginated admin lists.
  const [siteDefaults, setSiteDefaults] = useState(null);
  useEffect(() => {
    wsRequest("siteSettings.get")
      .then((data) => setSiteDefaults({ mode: data.listDisplayMode, pageSize: data.listPageSize }))
      .catch(() => {});
  }, []);

  // THE COUNTS FOR EVERY TAB, from whichever page was last fetched.
  //
  // They arrive with each page rather than in a call of their own: the tab
  // labels need them whatever page is showing, and a second request could
  // disagree with the first.
  const [counts, setCounts] = useState({});
  // The few facts the dead-air notice needs, answered by the server
  // because it has the whole list and this panel now has one page.
  const [summary, setSummary] = useState({ nextQueuedStartAt: null, lastEnded: null, servedQueue: null, unservedWaiting: 0 });

  // ONE PAGE OF THE CURRENT TAB, not the whole list.
  //
  // The panel used to load every competition and slice it in the browser.
  // Fine at twenty, not at two thousand — and the sort has moved to the
  // server with it, because a page is only meaningful if the order is
  // decided before it is cut.
  const {
    mode: listMode,
    setMode: setListMode,
    pageSize,
    setPageSize,
    page,
    setPage,
    items: competitions,
    totalMatching,
    totalPages,
    loading: listLoading,
    loadMore,
    hasMore,
    reload: load,
  } = usePaginatedList({
    listKey: "admin-competitions",
    defaultMode: siteDefaults?.mode,
    defaultPageSize: siteDefaults?.pageSize,
    deps: [tab, adminMode(modeStatus)],
    fetchPage: ({ offset, limit }) =>
      wsRequest(
        "admin.competitions.page",
        { status: tab === "timeline" ? null : tab, mode: adminMode(modeStatus), offset, limit },
        token
      ).then((data) => {
        setCounts(data.counts || {});
        setSummary({
          nextQueuedStartAt: data.nextQueuedStartAt ?? null,
          lastEnded: data.lastEnded ?? null,
          servedQueue: data.servedQueue ?? null,
          unservedWaiting: data.unservedWaiting ?? 0,
        });
        return data;
      }),
  });





  // WHEN SOMETHING MOVES, FOLLOW IT — from the COUNTS, not the list.
  //
  // This used to compare every competition's status against the previous
  // poll. With the list paginated it can only see one page, so it watches
  // the per-tab counts instead: a tab whose count went UP has gained a
  // competition, which is exactly the event worth following.
  //
  // Active wins when several change at once — something starting matters
  // more than something being missed or ending. Nothing happens on the
  // first load, when every count is new.
  const lastCounts = useRef(null);
  useEffect(() => {
    const previous = lastCounts.current;
    lastCounts.current = counts;
    if (!previous || !autoSelectTab) return;
    const rank = { active: 0, not_run: 1, queued: 2, ended: 3 };
    let best = null;
    for (const status of Object.keys(rank)) {
      const before = previous[status] || 0;
      const now = counts[status] || 0;
      if (now <= before) continue;
      if (best === null || rank[status] < rank[best]) best = status;
    }
    if (best && best !== tab) onTabChange(best);
  }, [counts, autoSelectTab, tab, onTabChange]);

  // NO POLL ANY MORE. The server broadcasts when a competition's status
  // changes — see statusSignature in server.js — so this list learns
  // about it the moment it happens rather than up to fifteen seconds
  // later. The broadcast handler below is what reloads.
  //
  // Status is derived from the clock, so nothing was written and nothing
  // was broadcast when a competition crossed a boundary; the server now
  // compares the statuses it is computing anyway and says so.






  // Real-time refresh — e.g. so this list updates the instant a buyer

  // correctly guesses and ends the active competition, without needing a

  // manual refresh here.

  // THE HANDLER MUST SEE THE CURRENT TAB.
  //
  // This was registered once with an empty dependency list, which was
  // harmless while `load` fetched every competition — it did not matter
  // which render's copy ran. Now that `load` fetches ONE TAB'S page, the
  // captured copy asked for whichever tab was showing when the panel
  // mounted, and displayed those rows under whatever tab you were on.
  //
  // The visible symptom was deleted and stale competitions appearing in
  // Queued and "To reschedule", clearing as soon as you clicked another tab and
  // back — because that path refetches with the current tab.
  //
  // A ref rather than a dependency: re-registering the listener on every
  // tab change would drop and re-add a websocket subscription for a
  // reason that has nothing to do with the socket.
  const loadRef = useRef(load);
  loadRef.current = load;
  useEffect(() => {

    return onBroadcast((message) => {

      if (message.type === "competitions-changed") loadRef.current();

    });

  }, []);







  const remove = async (id, title, mode) => {

    if (!(await confirm(`Delete ${modeLabel(mode)} competition "${title}"? This can't be undone.`))) return;

    try {

      await wsRequest("admin.competitions.delete", { id }, token);

      showToast(`${modeLabel(mode)} competition deleted.`);

    } catch (err) {

      showToast(err.message || "Could not reach backend.", "error");

    }

    load();

  };



  const patchCompetition = async (id, patch) => {

    try {

      await wsRequest("admin.competitions.edit", { id, ...patch }, token);

      load();

      return null;

    } catch (err) {

      return err.message || "Could not reach backend.";

    }

  };



  const statusColor = { active: "var(--accent-green)", queued: "var(--accent-yellow)", ended: "var(--text-secondary)", not_run: "var(--accent-red)" };



  return (

    <section style={{ marginTop: 20 }}>

      <h3>Competitions</h3>



      {/* THE QUEUE PANEL AND THE TABS SHARE A ROW.

          The tabs were a full-width strip under the queue panel, which
          left the space beside the panel empty. align-items: flex-end so
          the tab bar's underline lands level with the bottom of the
          panel, keeping the line that separates tabs from the list they
          control.

          flexWrap so a narrow screen drops the tabs back underneath —
          they are still directly above the list either way, which is the
          relationship that matters. */}
      {/* TWO COLUMNS: the queue on the left, the tabs and their content
          on the right.

          align-items: flex-start so the tabs line up with the TOP of the
          queue panel — the two columns start together and each runs as
          long as it needs.

          The right column is the wider of the two because it holds the
          competition list; the queue panel does not grow (flex-grow 0) so
          the space goes where the content is.

          flexWrap so a narrow screen stacks them, queue first. */}
      <div style={{ display: "flex", gap: 16, alignItems: "flex-start", flexWrap: "wrap" }}>
        {/* SIZED TO THE PANEL, not the other way round. The queue panel
            carries its own maxWidth of 500, so a 340px column could never
            contain it — it overflowed across the tabs. This lets it take
            the width it asks for and no more. */}
        <div style={{ flex: "0 1 500px", minWidth: 0 }}>
          {/* THE REAL MODE (1.435.0): this panel shows every queue and asks
              the server which it may manage, so it must not be handed the
              Test view the list below gets. */}
          <QueueControlPanel token={token} canEdit={canEdit} canEditDemoComps={canEditDemoComps} modeStatus={realModeStatus} />
        </div>

        {/* Basis 420, not 520: the two bases plus the gap decide when the
            row wraps, and 500 + 520 + 16 stacked a 1024px screen that had
            room for both. It still GROWS to fill whatever is left. */}
        <div style={{ flex: "1 1 420px", minWidth: 0 }}>
        <QueueSwitch queueSwitch={queueSwitch} />
        {competitions !== null && (
          <div>
            <TabBar
              tabs={[
                { id: "timeline", label: "Timeline" },
                ...COMPETITION_TABS.map((t) => ({
                  id: t,
                  label: `${COMPETITION_TAB_LABEL[t]} (${counts[t] ?? 0})`,
                  color: statusColor[t],
                })),
              ]}
              activeId={tab}
              onSelect={setTab}
            />
          </div>
        )}

        {/* WHICH QUEUE IS BEING SERVED (v1.389.0), and what is waiting in
            the other. Every tab shows both queues; the card's badge says
            which one each competition is in. */}
        {competitions !== null && summary.servedQueue && (
          <div style={{ fontSize: 12.5, opacity: 0.8, margin: "8px 0 12px", maxWidth: 700 }}>
            Serving the <strong>{summary.servedQueue === "api" ? "API" : "Native"} queue</strong>
            {" "}("Use API exclusively" is {summary.servedQueue === "api" ? "on" : "off"}). New
            competitions join it.
            {summary.unservedWaiting > 0 && (
              <>
                {" "}
                <span style={{ color: "var(--accent-yellow)", fontWeight: 600 }}>
                  {summary.unservedWaiting} competition{summary.unservedWaiting === 1 ? " is" : "s are"} waiting
                  in the {summary.servedQueue === "api" ? "Native" : "API"} queue
                </span>{" "}
                and will not start until the switch changes; once a slot passes it moves to To
                reschedule.
              </>
            )}
          </div>
        )}

        {competitions !== null && (
          <DeadAirNotice counts={counts} nextQueuedStartAt={summary.nextQueuedStartAt} lastEnded={summary.lastEnded} onGoToNotRun={() => setTab("not_run")} />
        )}




        {competitions !== null && (

          <label
            style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12.5, opacity: 0.75, margin: "6px 0 10px" }}
            title="A competition's status changes on its own as the clock passes. With this on, the tab follows it."
          >
            <input
              type="checkbox"
              checked={autoSelectTab}
              onChange={(e) => setAutoSelectTab(e.target.checked)}
            />
            Auto select tab — follow a competition when it moves
          </label>

        )}



        {/* The per-competition chart moved to Competition Management, so this

            drill-down is now a cross-tab route rather than a local setTab.

            onViewCompetitionChart is null for an admin who can't see that tab

            at all, and TimelinePanel renders the row unlinked in that case

            rather than offering a dead end. */}

        {tab === "timeline" && (
          <TimelinePanel
            token={token}
            onViewCompetition={onViewCompetitionChart}
            modeStatus={modeStatus}
            canEdit={canEdit}
          />
        )}



        {tab !== "timeline" && (

          <ViewOnlyGate canEdit={canEdit} showNotice>

            <div>

              {/*
                "To reschedule" needs explaining wherever it appears, because it is
                the one status that sounds like a failure and usually is
                not: a competition lands here when its scheduled time passed
                without it starting, most often because the one before it
                overran. That is ordinary rather than wrong.
              */}
              {tab === "not_run" && (
                <NotRunTools token={token} canEdit={canEdit} onDone={load} mode={adminMode(modeStatus)} />
              )}

              {/* ONE PAGE, ALREADY SORTED AND FILTERED BY THE SERVER.

                  The filtering and sorting that used to live here moved to
                  admin.competitions.page — a page is only meaningful if the
                  order is decided before it is cut. */}
              {competitions === null ? (

                <p>Loading…</p>

              ) : competitions.length === 0 ? (

                <p style={{ opacity: 0.7 }}>No {COMPETITION_TAB_LABEL[tab].toLowerCase()} competitions.</p>

              ) : (

                competitions.map((c) => <CompetitionRow key={c.id} c={c} onSave={patchCompetition} onRemove={remove} modeStatus={modeStatus} isRoot={isRoot} token={token} />)

              )}

              {competitions !== null && competitions.length > 0 && (
                <ListDisplayControls
                  listKey="admin-competitions"
                  mode={listMode}
                  setMode={setListMode}
                  pageSize={pageSize}
                  setPageSize={setPageSize}
                  page={page}
                  setPage={setPage}
                  totalPages={totalPages}
                  totalMatching={totalMatching}
                  itemsCount={competitions?.length || 0}
                  loading={listLoading}
                  loadMore={loadMore}
                  hasMore={hasMore}
                />
              )}

            </div>

          </ViewOnlyGate>

        )}






        </div>
      </div>

      {confirmModal}

    </section>

  );

}



// Chronological feed of competition-progress events — starts, wins (by

// guess or lottery), and reminder-subscription activity across all three

// stages of the staged provider pipeline (emails captured, uploaded to

// provider, provider-confirmed dispatched, confirmed delivered). See

// buildCompetitionTimeline on the backend for exactly what's included and

// why (aggregated per competition, capped to the most recent 100 events —

// this is a live activity view, not a permanent audit log). Updates live

// via the same broadcast types the underlying events are already tied to

// elsewhere in the admin panel.

function TimelinePanel({ token, onViewCompetition, modeStatus, canEdit }) {

  const symbol = useCurrencySymbol();

  const [events, setEvents] = useState(null);

  // THE TIMELINE FOLLOWS THE MODE YOU ARE IN. In Live mode it shows
  // live events; anywhere else it shows test and demo. There is no
  // switch, because there is no question to ask: the tab you are
  // looking at already says which site you are working on.
  const liveView = adminMode(modeStatus) === "live";
  const [clearing, setClearing] = useState(false);

  const [error, setError] = useState(null);



  const load = () => {

    wsRequest("admin.competitions.timeline", {}, token)

      .then(setEvents)

      .catch((err) => setError(err.message || "Could not reach backend."));

  };



  useEffect(load, [token]);



  useEffect(() => onBroadcast((message) => {

    if (message.type === "competitions-changed" || message.type === "reminders-changed") load();

  }), [token]);



  if (events === null) {

    return error ? <p style={{ color: "var(--accent-red)" }}>{error}</p> : <p>Loading…</p>;

  }

  if (events.length === 0) {

    return <p style={{ opacity: 0.7 }}>Nothing has happened yet.</p>;

  }



  const ICONS = {

    started: "🏁",

    won: "🏆",

    lottery_won: "🎟️",

    emails_captured: "📧",

    emails_uploaded: "📤",

    provider_confirmed: "📨",

    reminders_sent: "✉️",

    delivery_failed: "⚠️",

  };



  // Linked only when there's somewhere to go: the per-competition chart

  // lives under Competition Management, so an admin without that tab gets

  // the title as plain text rather than a button leading nowhere.

  const TitleLink = ({ e }) =>

    e.competitionId && onViewCompetition ? (

      <button

        onClick={() => onViewCompetition(e.competitionId)}

        style={{ background: "none", border: "none", color: "var(--accent-blue)", cursor: "pointer", padding: 0, textDecoration: "underline", font: "inherit" }}

      >

        {e.title}

      </button>

    ) : e.competitionId ? (

      <span>{e.title}</span>

    ) : (

      <em>the next competition</em>

    );



  const describe = (e) => {

    switch (e.type) {

      case "started":

        return (

          <>

            <TitleLink e={e} /> started

          </>

        );

      case "won":

        return (

          <>

            Won by <strong>{e.winnerUsername || "somebody with no username recorded"}</strong> who won {symbol}

            {(e.prizeMoney || 0).toFixed(2)} — <TitleLink e={e} />

          </>

        );

      case "lottery_won":

        return (

          <>

            <TitleLink e={e} /> went to a lottery draw — won by <strong>{e.winnerUsername || "somebody with no username recorded"}</strong> for {symbol}

            {(e.prizeMoney || 0).toFixed(2)}

          </>

        );

      case "emails_captured":

        return (

          <>

            {e.count} email{e.count === 1 ? " address" : " addresses"} captured for notification of <TitleLink e={e} />

          </>

        );

      case "emails_uploaded":

        return (

          <>

            {e.count} email{e.count === 1 ? "" : "s"} uploaded to provider for <TitleLink e={e} />

          </>

        );

      case "provider_confirmed":

        return (

          <>

            Provider has confirmed {e.count} email{e.count === 1 ? "" : "s"} have been sent for <TitleLink e={e} />

          </>

        );

      case "reminders_sent":

        return (

          <>

            {e.count} reminder email{e.count === 1 ? "" : "s"} confirmed sent to users about <TitleLink e={e} />

          </>

        );

      case "delivery_failed":

        return (

          <>

            Provider reported {e.count} email{e.count === 1 ? "" : "s"} couldn't be sent for <TitleLink e={e} />

          </>

        );

      default:

        return null;

    }

  };



  const shown = events.filter((e) =>
    liveView ? (e.mode || "live") === "live" : (e.mode || "live") !== "live"
  );

  const clearTimeline = async () => {
    setClearing(true);
    try {
      // The queue on screen (1.435.0): the Test view clears the test one.
      await wsRequest("admin.competitions.clearTimeline", { mode: adminMode(modeStatus) }, token);
      showToast("Timeline cleared.", "success");
      load();
    } catch (err) {
      showToast(err.message || "Could not reach backend.", "error");
    }
    setClearing(false);
  };

  return (

    <div style={{ maxWidth: 700 }}>

      {/* NO SWITCH — the Timeline shows the site you are working on.
          Clear records a watermark for THIS view only; nothing is
          deleted, and the competitions, activity log and delivery
          records all still say what happened. */}
      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 12 }}>
        {/* canEdit is a BOOLEAN here, not a function. CompetitionsPanel
            receives it already resolved — canEdit={canEdit("manage_competitions")}
            at its own call site — and calling it threw "i is not a
            function" the moment the tab rendered. */}
        {canEdit && shown.length > 0 && (
          <button type="button" disabled={clearing} onClick={clearTimeline} style={{ fontSize: 13 }}>
            {clearing ? "Clearing…" : "Clear"}
          </button>
        )}
      </div>

      {shown.length === 0 && (
        <p style={{ opacity: 0.7 }}>
          {liveView
            ? "Nothing has happened on the live site yet."
            : "Nothing has happened in test or demo yet."}
        </p>
      )}

      {shown.map((e, i) => (

        <div

          key={i}

          style={{ display: "flex", gap: 10, padding: "10px 0", borderBottom: "1px solid var(--border-color-subtle)" }}

        >

          <span style={{ fontSize: 18 }}>{ICONS[e.type]}</span>

          <div style={{ flex: 1 }}>

            <div style={{ fontSize: 14, color: e.type === "delivery_failed" ? "var(--accent-red)" : undefined }}>

              {describe(e)}

            </div>

            <div style={{ fontSize: 12, opacity: 0.6 }}>{siteDateTime(e.at)}</div>

          </div>

        </div>

      ))}

    </div>

  );

}



// Simple hand-rolled SVG line chart — deliberately not using a charting

// library (this project only depends on react/react-dom/express so far,

// and a couple of line charts don't justify adding a whole package plus

// the extra `npm install` step it'd add to every deploy). points is an

// array of data objects; series describes which numeric fields to plot as

// separate lines.

function SvgLineChart({ points, xKey, series, height = 220, formatX, formatY }) {

  const width = 640;

  const padding = { top: 16, right: 16, bottom: 32, left: 56 };

  const innerW = width - padding.left - padding.right;

  const innerH = height - padding.top - padding.bottom;



  if (!points || points.length === 0) {

    return <div style={{ opacity: 0.6, fontSize: 13, padding: 20 }}>No data yet.</div>;

  }



  const xs = points.map((p) => p[xKey]);

  const minX = Math.min(...xs);

  const maxX = Math.max(...xs);

  const xRange = maxX - minX || 1;



  const allYValues = series.flatMap((s) => points.map((p) => p[s.key]));

  const maxY = Math.max(...allYValues, 0);

  const yRange = maxY || 1;



  const xPos = (x) => padding.left + ((x - minX) / xRange) * innerW;

  const yPos = (y) => padding.top + innerH - (y / yRange) * innerH;



  const yTickCount = 4;

  const yTickValues = Array.from({ length: yTickCount + 1 }, (_, i) => (yRange / yTickCount) * i);



  return (

    <div>

      <svg viewBox={`0 0 ${width} ${height}`} style={{ width: "100%", height: "auto" }}>

        {yTickValues.map((v, i) => (

          <g key={i}>

            <line x1={padding.left} x2={width - padding.right} y1={yPos(v)} y2={yPos(v)} stroke="var(--border-color)" strokeWidth="1" />

            <text x={padding.left - 8} y={yPos(v) + 4} fontSize="10" fill="var(--text-secondary)" textAnchor="end">

              {formatY ? formatY(v) : Math.round(v)}

            </text>

          </g>

        ))}

        <text x={padding.left} y={height - 8} fontSize="10" fill="var(--text-secondary)" textAnchor="start">

          {formatX ? formatX(minX) : minX}

        </text>

        <text x={width - padding.right} y={height - 8} fontSize="10" fill="var(--text-secondary)" textAnchor="end">

          {formatX ? formatX(maxX) : maxX}

        </text>

        {series.map((s) => (

          <g key={s.key}>

            <polyline

              fill="none"

              stroke={s.color}

              strokeWidth="2"

              points={points.map((p) => `${xPos(p[xKey])},${yPos(p[s.key])}`).join(" ")}

            />

            {/* A polyline with only one point (or very few, all close together)

                can end up drawing nothing visible at all — dots make sure

                real data is always visible even when there's not enough of

                it yet to look like a proper line. */}

            {points.map((p, i) => (

              <circle key={i} cx={xPos(p[xKey])} cy={yPos(p[s.key])} r="3" fill={s.color} />

            ))}

          </g>

        ))}

      </svg>

      <div style={{ display: "flex", gap: 16, marginTop: 6, flexWrap: "wrap" }}>

        {series.map((s) => (

          <div key={s.key} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12 }}>

            <span style={{ width: 12, height: 2, background: s.color, display: "inline-block" }} />

            {s.label}

          </div>

        ))}

      </div>

    </div>

  );

}



function ChartPanel({ token, onViewCompetition, modeStatus }) {

  const symbol = useCurrencySymbol();

  const [data, setData] = useState(null);

  const [error, setError] = useState(null);

  // LIVE OR TEST (1.468.0). Cumulative prize and revenue lines, so the
  // same rule as the summary bar: one mode's, never a sum of both.
  const [view, setView] = useState("live");
  // ONLY ASK WHILE THE SITE IS LIVE. The filter is not drawn in Test or
  // Demo - there is nothing to choose - but the state behind it still
  // says "live", and asking for Live from Test is refused by the same
  // rule that makes the filter pointless there. Sending nothing means
  // the admin's own mode, which is the right answer in every mode.
  const askMode = adminMode(modeStatus) === "live" ? view : undefined;

  // Root's configured starting point for this and every other paginated

  // admin list — see usePaginatedList for how a viewer's own choice, once

  // made, takes over from this.

  const [siteDefaults, setSiteDefaults] = useState(null);



  useEffect(() => {

    wsRequest("siteSettings.get")

      .then((d) => setSiteDefaults({ mode: d.listDisplayMode, pageSize: d.listPageSize }))

      .catch(() => {});

  }, []);



  const load = () => {

    wsRequest("admin.chartData.get", { mode: askMode }, token)

      .then(setData)

      .catch((err) => setError(err.message || "Could not reach backend."));

  };



  useEffect(load, [token, adminMode(modeStatus), askMode]);



  // Real-time refresh — a new prize-money/revenue data point appears

  // every time a competition ends, which can happen from any buyer's

  // browser, not an action taken here.

  useEffect(() => onBroadcast((message) => {

    if (message.type === "competitions-changed") load();

  }), [token]);



  // Deliberately a separate, paginated fetch from admin.chartData.get

  // above — that one needs to stay a complete, unbroken dataset for the

  // two cumulative charts below to plot an accurate trend; this "Ended

  // competitions" list is just a navigational list of the same

  // competitions and is safe to page through independently of them.

  const {

    mode: endedMode,

    setMode: setEndedMode,

    pageSize: endedPageSize,

    setPageSize: setEndedPageSize,

    page: endedPage,

    setPage: setEndedPage,

    items: endedList,

    totalMatching: endedTotalMatching,

    totalPages: endedTotalPages,

    loading: endedLoading,

    loadMore: endedLoadMore,

    hasMore: endedHasMore,

    reload: reloadEndedList,

  } = usePaginatedList({

    listKey: "chart-ended-competitions",

    defaultMode: siteDefaults?.mode,

    defaultPageSize: siteDefaults?.pageSize,

    deps: [adminMode(modeStatus)],

    fetchPage: ({ offset, limit }) => wsRequest("admin.chartData.endedList", { offset, limit }, token),

  });



  useEffect(() => onBroadcast((message) => {

    if (message.type === "competitions-changed") reloadEndedList();

  }), [reloadEndedList]);



  const formatX = (t) => siteDate(t);

  const formatY = (v) => `${symbol}${Math.round(v)}`;



  if (error) return <p style={{ color: "var(--accent-red)" }}>{error}</p>;

  if (!data) return <p style={{ opacity: 0.6 }}>Loading…</p>;



  return (

    <div style={{ maxWidth: 700 }}>

      <DataModeFilter value={view} onChange={setView} siteIsLive={adminMode(modeStatus) === "live"} what="figures" />

      <h4>Prize money won over time</h4>

      {data.prizePoints.length === 0 ? (

        <p style={{ opacity: 0.6, fontSize: 13 }}>No competitions have been won yet.</p>

      ) : (

        <SvgLineChart

          points={data.prizePoints}

          xKey="t"

          series={[{ key: "cumulativePrizeMoney", label: "Cumulative prize money", color: "var(--accent-yellow)" }]}

          formatX={formatX}

          formatY={formatY}

        />

      )}



      <h4 style={{ marginTop: 30 }}>Revenue over time (site-wide)</h4>

      {data.revenuePoints.length === 0 ? (

        <p style={{ opacity: 0.6, fontSize: 13 }}>No purchases or guesses yet.</p>

      ) : (

        <SvgLineChart

          points={data.revenuePoints}

          xKey="t"

          series={[{ key: "cumulativeRevenue", label: "Cumulative revenue", color: "var(--accent-green)" }]}

          formatX={formatX}

          formatY={formatY}

        />

      )}



      <h4 style={{ marginTop: 30 }}>Ended competitions</h4>

      {endedTotalMatching === 0 ? (

        <p style={{ opacity: 0.6, fontSize: 13 }}>None yet.</p>

      ) : (

        <>

          <ListDisplayControls

            listKey="chart-ended-competitions"

            mode={endedMode}

            setMode={setEndedMode}

            pageSize={endedPageSize}

            setPageSize={setEndedPageSize}

            page={endedPage}

            setPage={setEndedPage}

            totalPages={endedTotalPages}

            totalMatching={endedTotalMatching}

            itemsCount={endedList?.length || 0}

            loading={endedLoading}

            loadMore={endedLoadMore}

            hasMore={endedHasMore}

          />

          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>

            {(endedList || []).map((p) => (

              <button

                key={p.id}

                onClick={() => onViewCompetition(p.id)}

                style={{

                  textAlign: "left",

                  background: "none",

                  border: "1px solid var(--border-color-light)",

                  borderRadius: 6,

                  padding: "6px 10px",

                  color: "var(--accent-blue)",

                  cursor: "pointer",

                }}

              >

                {p.title}
                <OtherModeTag mode={p.mode} viewing={adminMode(modeStatus)} />{" "}
                <span style={{ opacity: 0.6, fontSize: 12 }}>— {formatX(p.t)}</span>

              </button>

            ))}

          </div>

        </>

      )}

    </div>

  );

}



function CompetitionChartPanel({ token, competitionId, onBack }) {

  const symbol = useCurrencySymbol();

  const [data, setData] = useState(null);

  const [error, setError] = useState(null);



  const load = () => {

    if (!competitionId) return;

    wsRequest("admin.competitions.chartData", { id: competitionId }, token)

      .then(setData)

      .catch((err) => setError(err.message || "Could not reach backend."));

  };



  useEffect(load, [competitionId, token]);



  // Real-time refresh — this competition's own revenue/participants/

  // entries change with every purchase or guess anyone makes, and again

  // once it actually ends, none of which happen from this admin's own

  // action. Filtered to just this competition specifically on the

  // purchase broadcast (which includes competitionId) — no need to

  // refetch this chart over an unrelated competition's purchase.

  useEffect(() => onBroadcast((message) => {

    if (message.type === "competitions-changed") load();

    if (message.type === "purchase" && message.competitionId === competitionId) load();

  }), [competitionId, token]);



  const formatX = (t) => siteDateTime(t);



  return (

    <div style={{ maxWidth: 700 }}>

      <button onClick={onBack} style={{ marginBottom: 14 }}>

        ← Back to Chart

      </button>

      {error && <p style={{ color: "var(--accent-red)" }}>{error}</p>}

      {!error && !data && <p style={{ opacity: 0.6 }}>Loading…</p>}

      {data && (

        <>

          <h4 style={{ marginTop: 0 }}>{data.title}</h4>

          {data.points.length === 0 ? (

            <p style={{ opacity: 0.6, fontSize: 13 }}>No activity recorded for this competition yet.</p>

          ) : (

            <>

              <h4>Revenue</h4>

              <SvgLineChart

                points={data.points}

                xKey="t"

                series={[{ key: "revenue", label: "Cumulative revenue", color: "var(--accent-green)" }]}

                formatX={formatX}

                formatY={(v) => `${symbol}${Math.round(v)}`}

              />



              <h4 style={{ marginTop: 30 }}>Participants</h4>

              <SvgLineChart

                points={data.points}

                xKey="t"

                series={[{ key: "uniqueUsers", label: "Unique users", color: "var(--accent-blue)" }]}

                formatX={formatX}

              />



              <h4 style={{ marginTop: 30 }}>Entries</h4>

              <SvgLineChart

                points={data.points}

                xKey="t"

                series={[

                  { key: "pixelPurchases", label: "Pixel purchases", color: "var(--accent-yellow)" },

                  { key: "guessEntries", label: "Guess entries", color: "var(--accent-red)" },

                ]}

                formatX={formatX}

              />

            </>

          )}

        </>

      )}

    </div>

  );

}



// LIVE QUEUE / TEST QUEUE (1.463.0). The Stats tab is where a winner is
// paid, and since 1.435.0 a Test competition is run from Live - so this
// gets the same switch Competition Management has, and pays from
// whichever queue is being looked at. One queue at a time, never a mix:
// the figures here sum money, and a total that had quietly absorbed test
// payments is exactly what 1.437.0 refused to allow.
function CompetitionStatsPanel({ authHeaders, onViewUser, onViewChart, modeStatus, queueSwitch }) {

  // The queue being looked at: the one picked, or - with no switch on
  // screen (Test or Demo mode) - this admin's own mode, as before.
  const queue = queueSwitch ? queueSwitch.value : adminMode(modeStatus);

  const onTestQueue = queue === "test" && adminMode(modeStatus) === "live";


  const symbol = useCurrencySymbol();

  const token = authHeaders["x-auth-token"];

  const [busyId, setBusyId] = useState(null);

  const [ticketRanges, setTicketRanges] = useState(null);

  const [ticketRangesError, setTicketRangesError] = useState(null);

  const [ticketRangesOpen, setTicketRangesOpen] = useState(false);
  // Root's configured starting point for this and every other paginated

  // admin list — see usePaginatedList for how a viewer's own choice, once

  // made, takes over from this.

  const [siteDefaults, setSiteDefaults] = useState(null);



  useEffect(() => {

    wsRequest("siteSettings.get")

      .then((data) => setSiteDefaults({ mode: data.listDisplayMode, pageSize: data.listPageSize }))

      .catch(() => {});

  }, []);



  const openTicketRanges = (competitionId) => {

    setTicketRangesOpen(true);

    setTicketRanges(null);

    setTicketRangesError(null);

    wsRequest("admin.competitions.ticketRanges", { competitionId }, token)

      .then((data) => setTicketRanges(data))

      .catch((err) => setTicketRangesError(err.message || "Could not reach backend."));

  };



  const {

    mode,

    setMode,

    pageSize,

    setPageSize,

    page,

    setPage,

    items: stats,

    totalMatching,

    totalPages,

    loading,

    loadMore,

    hasMore,

    reload,

  } = usePaginatedList({

    listKey: "competition-stats",

    defaultMode: siteDefaults?.mode,

    defaultPageSize: siteDefaults?.pageSize,

    deps: [queue],

    fetchPage: ({ offset, limit }) => wsRequest("admin.competitionStats.get", { offset, limit, mode: queue }, token),

  });



  // Real-time refresh — a winner submitting payout details happens on a

  // completely different person's browser, so without this the table would

  // only ever update on the next manual refresh or admin-side action. Also

  // refreshes on "purchase" — the revenue/profit figures shown here change

  // with every pixel purchase on any active competition, not just once one

  // fully ends.

  useEffect(() => {

    return onBroadcast((message) => {

      if (message.type === "competitions-changed" || message.type === "purchase") reload();

    });

  }, [reload]);



  const markPrizePaid = async (s) => {

    if (s.winnerPhoneVerificationBlocking) {

      showToast(`${s.winnerEmail || "This winner"} hasn't verified their phone number yet — they need to before this prize can be marked as paid.`, "error");

      return;

    }

    const paidVia = window.prompt(

      "How was this paid? (e.g. PayPal, Bank transfer)",

      s.winnerPayoutMethod === "paypal" ? "PayPal" : s.winnerPayoutMethod === "bank_transfer" ? "Bank transfer" : s.winnerPayoutMethod === "other" ? "Other" : ""

    );

    if (paidVia === null) return; // cancelled

    // REQUIRED since 1.393.0 — the server refuses a blank one too. Said
    // here as well so the admin is told before anything is sent.
    if (!paidVia.trim()) {

      showToast("Say how the prize was paid — for example PayPal or Bank transfer.", "error");

      return;

    }

    setBusyId(s.id);

    try {

      await wsRequest("admin.competitions.markPrizePaid", { id: s.id, prizePaid: true, paidVia }, token);

      showToast("Marked as paid.");

    } catch (err) {

      showToast(err.message || "Could not reach backend.", "error");

    }

    reload();

    setBusyId(null);

  };



  const requestPayoutDetails = async (id) => {

    setBusyId(id);

    try {

      await wsRequest("admin.competitions.requestPayoutDetails", { id }, token);

      showToast("Payment details requested.");

    } catch (err) {

      showToast(err.message || "Could not reach backend.", "error");

    }

    reload();

    setBusyId(null);

  };



  // ABOVE THE TWO EARLY RETURNS BELOW, deliberately. The Test queue is
  // often empty and always starts that way, and a switch that disappears
  // along with the list is one you cannot use to get back.
  const queueHeader = (
    <>
      <QueueSwitch queueSwitch={queueSwitch} />
      {onTestQueue && (
        <p style={{ margin: "0 0 12px", fontSize: 12.5, opacity: 0.75 }}>
          These are the Test queue’s own figures. The totals above the tabs are always Live.
        </p>
      )}
    </>
  );

  if (stats === null) return <div>{queueHeader}<p>Loading…</p></div>;

  if (stats.length === 0 && totalMatching === 0) {
    return <div>{queueHeader}<p style={{ opacity: 0.7 }}>No competitions yet.</p></div>;
  }



  const th = { textAlign: "center", padding: "8px 10px", borderBottom: "1px solid var(--border-color-light)", fontSize: 12, opacity: 0.7, whiteSpace: "nowrap" };

  const td = { textAlign: "center", padding: "8px 10px", borderBottom: "1px solid var(--border-color-subtle)", fontSize: 13, whiteSpace: "nowrap" };



  return (

    <div>

      {queueHeader}

      <ListDisplayControls

        listKey="competition-stats"

        mode={mode}

        setMode={setMode}

        pageSize={pageSize}

        setPageSize={setPageSize}

        page={page}

        setPage={setPage}

        totalPages={totalPages}

        totalMatching={totalMatching}

        itemsCount={stats?.length || 0}

        loading={loading}

        loadMore={loadMore}

        hasMore={hasMore}

      />

      {stats.length === 0 ? (

        <p style={{ opacity: 0.7 }}>No competitions on this page.</p>

      ) : (

      <div style={{ overflowX: "auto" }}>

      <table style={{ borderCollapse: "collapse", width: "100%" }}>

        <thead>

          <tr>

            <th style={th}></th>

            <th style={th}>Title</th>

            <th style={th}>Status</th>

            <th style={th}>Started</th>

            <th style={th}>Ended</th>

            <th style={th}>Type</th>

            <th style={th}>Users entered</th>

            <th style={th}>Entries</th>

            <th style={th}>Image revenue</th>

            <th style={th}>Guess revenue</th>

            <th style={th}>Total collected</th>

            <th style={th}>Prize fund</th>

            <th style={th}>Prize status</th>

            <th style={th}>Profit</th>

            <th style={th}>Winner</th>

            <th style={th}>Payout</th>

            <th style={th}></th>

          </tr>

        </thead>

        <tbody>

          {stats.map((s) => {

            const complete = s.isAnswered && s.prizePaid;

            return (

              <tr key={s.id}>

                <td style={td}>

                  <span

                    title={complete ? "Complete: prize paid" : "Pending: prize not yet paid"}

                    style={{

                      display: "inline-block",

                      width: 10,

                      height: 10,

                      borderRadius: "50%",

                      background: complete ? "var(--accent-green)" : "var(--accent-red)",

                    }}

                  />

                </td>

                <td style={{ ...td, fontWeight: 600 }}>

                  {s.title}

                  {s.isAnswered && onViewChart && (

                    <button

                      onClick={() => onViewChart(s.id)}

                      title="View chart"

                      style={{ background: "none", border: "none", color: "var(--accent-blue)", cursor: "pointer", marginLeft: 6, padding: 0, fontSize: 13 }}

                    >

                      📊

                    </button>

                  )}

                </td>

                <td style={td}>{s.status}</td>

                

                <td style={td}>{s.startedAt ? siteDateTime(s.startedAt) : "—"}</td>

                <td style={td}>{s.endedAt ? siteDateTime(s.endedAt) : "—"}</td>

                <td style={td}>

                  {!s.isAnswered ? (

                    "—"

                  ) : s.wonByLottery ? (

                    <button

                      onClick={() => openTicketRanges(s.id)}

                      style={{ background: "none", border: "none", color: "var(--accent-blue)", cursor: "pointer", padding: 0, textDecoration: "underline", font: "inherit" }}

                    >

                      Lottery (#{s.winningTicket ?? "?"})

                    </button>

                  ) : (

                    "Competition"

                  )}

                </td>

                <td style={td}>{s.usersEntered}</td>

                <td style={td}>{s.entriesCount}</td>

                <td style={td}>{symbol}{(s.pixelRevenue || 0).toFixed(2)}</td>

                <td style={td}>{symbol}{(s.guessRevenue || 0).toFixed(2)}</td>

                <td style={td}>{symbol}{(s.totalCollected || 0).toFixed(2)}</td>

                <td style={td}>{symbol}{(s.prizeMoney || 0).toFixed(2)}</td>

                <td style={td}>

                  {!s.isAnswered

                    ? "n/a"

                    : s.prizePaid

                    ? "Paid"

                    : s.winnerPayoutMethod && s.winnerPayoutDetails

                    ? "Ready to pay"

                    : s.payoutRequestedAt

                    ? "Pending"

                    : "Awaiting request"}

                </td>

                <td style={{ ...td, fontWeight: 600 }}>{symbol}{(s.totalProfit || 0).toFixed(2)}</td>

                <td style={td}>

                  {s.winnerUsername ? (

                    <button

                      onClick={() => onViewUser(s.winnerUsername || s.winnerEmail)}

                      style={{ color: "var(--accent-blue)", background: "none", border: "none", cursor: "pointer", padding: 0, textDecoration: "underline" }}

                    >

                      {s.winnerUsername || s.winnerEmail || "—"}

                    </button>

                  ) : (

                    <span style={{ opacity: 0.6 }}>—</span>

                  )}

                </td>

                <td style={td} title={s.winnerPayoutDetails || ""}>

                  {s.prizeMoney <= 0 || !s.isAnswered ? (

                    <span style={{ opacity: 0.6 }}>—</span>

                  ) : s.winnerPayoutMethod && s.winnerPayoutDetails ? (

                    <span style={{ opacity: 0.85 }}>

                      {s.prizePaid ? "Paid via " : ""}

                      {s.winnerPayoutMethod === "paypal" ? "PayPal" : s.winnerPayoutMethod === "bank_transfer" ? "Bank transfer" : "Other"}

                      {" "}(hover for details)

                    </span>

                  ) : (

                    <span style={{ opacity: 0.5 }}>Not set up yet</span>

                  )}

                </td>

                <td style={td}>

                  {s.isAnswered && s.prizeMoney > 0 && (

                    s.prizePaid ? (

                      <button disabled>Prize Paid</button>

                    ) : s.winnerPayoutMethod && s.winnerPayoutDetails && s.winnerPhoneVerificationBlocking ? (

                      <button disabled title={`${s.winnerEmail || "This winner"} hasn't verified their phone number yet`}>

                        Phone not verified

                      </button>

                    ) : s.winnerPayoutMethod && s.winnerPayoutDetails ? (

                      <button onClick={() => markPrizePaid(s)} disabled={busyId === s.id}>

                        {busyId === s.id ? "…" : "Mark prize paid"}

                      </button>

                    ) : s.payoutRequestedAt ? (

                      <button disabled title="Waiting on the winner to add their payment details">

                        Pending

                      </button>

                    ) : (

                      <button onClick={() => requestPayoutDetails(s.id)} disabled={busyId === s.id}>

                        {busyId === s.id ? "…" : "Request Payment Details"}

                      </button>

                    )

                  )}

                </td>

              </tr>

            );

          })}

        </tbody>

      </table>

      </div>

      )}



      {ticketRangesOpen && (

        <div

          onClick={() => setTicketRangesOpen(false)}

          style={{

            position: "fixed",

            inset: 0,

            background: "rgba(0,0,0,0.6)",

            display: "flex",

            alignItems: "center",

            justifyContent: "center",

            zIndex: 50,

          }}

        >

          <div

            onClick={(e) => e.stopPropagation()}

            style={{

              background: "var(--bg-panel)",

              border: "1px solid var(--border-color)",

              borderRadius: 10,

              padding: 24,

              maxWidth: 520,

              width: "90%",

              maxHeight: "80vh",

              overflowY: "auto",

              color: "var(--text-primary)",

            }}

          >

            {ticketRangesError ? (

              <p style={{ color: "var(--accent-red)" }}>{ticketRangesError}</p>

            ) : !ticketRanges ? (

              <p>Loading…</p>

            ) : (

              <>

                <h3 style={{ marginTop: 0 }}>{ticketRanges.title} — lottery tickets</h3>

                <p style={{ fontSize: 13, opacity: 0.7 }}>

                  Winning ticket: <strong>#{ticketRanges.winningTicket}</strong>

                </p>

                {ticketRanges.ranges.length === 0 ? (

                  <p style={{ fontSize: 13, opacity: 0.6 }}>No eligible (account-bound) tickets were sold.</p>

                ) : (

                  ticketRanges.ranges.map((r, i) => (

                    <div

                      key={i}

                      style={{

                        fontSize: 13,

                        marginBottom: 6,

                        padding: 8,

                        borderRadius: 6,

                        background: r.isWinner ? "rgba(255,230,109,0.12)" : "transparent",

                        border: r.isWinner ? "1px solid var(--accent-yellow)" : "1px solid var(--border-color)",

                      }}

                    >

                      <button

                        onClick={() => onViewUser(r.username)}

                        style={{ background: "none", border: "none", color: "var(--accent-blue)", cursor: "pointer", padding: 0, textDecoration: "underline", font: "inherit" }}

                      >

                        {r.username}

                      </button>

                      {" — "}

                      {r.start === r.end ? `ticket #${r.start}` : `tickets #${r.start}–#${r.end}`}{" "}

                      <span style={{ opacity: 0.6 }}>({r.count} ticket{r.count === 1 ? "" : "s"})</span>

                      {r.isWinner && <span style={{ color: "var(--accent-yellow)", fontWeight: 600 }}> 🏆 winning ticket</span>}

                    </div>

                  ))

                )}

                <button onClick={() => setTicketRangesOpen(false)} style={{ marginTop: 12 }}>

                  Close

                </button>

              </>

            )}

          </div>

        </div>

      )}

    </div>

  );

}



// SAYS WHEN THERE IS NOTHING RUNNING AND WHY.
//
// Under a timetable an early win leaves DEAD AIR: the old model dated the
// next competition from the previous one being answered, so the gap closed
// itself, and a schedule deliberately gives that up in exchange for a start
// time that can be advertised.
//
// The cost is that dead air is otherwise invisible — you would only notice
// by reading the timetable and doing the arithmetic. This does the
// arithmetic, names the early win when that is the cause, and points at the
// competitions ready to fill the gap.
//
// Computed entirely from the list already loaded: nothing to fetch, and no
// chance of it disagreeing with the rows below it.
// FED BY THE SERVER, not by reading the whole list.
//
// This used to filter and sort every competition to answer three
// questions: is anything active, when does the next one start, and was
// the last one won early. With the list paginated it can only see one
// page, so admin.competitions.page answers those questions instead — it
// has the whole list in front of it anyway.
function DeadAirNotice({ counts, nextQueuedStartAt, lastEnded, onGoToNotRun }) {
  if ((counts.active || 0) > 0) return null;

  const upcoming = nextQueuedStartAt != null ? { scheduledStartAt: nextQueuedStartAt } : null;
  const gapMs = upcoming ? upcoming.scheduledStartAt - Date.now() : null;
  // An hour of quiet is a lull; anything less is just the gap between two
  // competitions and not worth a notice.
  const HOUR = 3600000;
  if (gapMs !== null && gapMs < HOUR) return null;

  const notRunCount = counts.not_run || 0;
  if (!upcoming && notRunCount === 0) return null;

  // Was the last one won early, and by how much? That is the usual reason
  // for a gap, and naming it is the difference between "something is wrong"
  // and "this is what an early win looks like".

  const earlyByMs =
    lastEnded && lastEnded.durationMs
      ? lastEnded.actualStartAt + lastEnded.durationMs - lastEnded.answeredAt
      : 0;
  const roughly = (ms) => {
    const days = Math.floor(ms / 86400000);
    if (days >= 1) return `${days} day${days === 1 ? "" : "s"}`;
    const hours = Math.round(ms / HOUR);
    return `${hours} hour${hours === 1 ? "" : "s"}`;
  };

  return (
    <div
      style={{
        border: "1px solid var(--accent-yellow)",
        borderRadius: 8,
        padding: 12,
        marginBottom: 20,
        maxWidth: 700,
        fontSize: 13,
      }}
    >
      <strong>Nothing is running right now.</strong>{" "}
      {earlyByMs > HOUR && lastEnded ? (
        <>
          {lastEnded.title} was won {roughly(earlyByMs)} early.{" "}
        </>
      ) : null}
      {upcoming ? (
        <>The next is scheduled for {siteDateTime(upcoming.scheduledStartAt)}.</>
      ) : (
        <>Nothing is scheduled.</>
      )}{" "}
      {notRunCount > 0 && (
        <>
          You have {notRunCount} in{" "}
          <button
            type="button"
            onClick={onGoToNotRun}
            style={{
              background: "none",
              border: "none",
              padding: 0,
              font: "inherit",
              color: "var(--accent-blue)",
              cursor: "pointer",
              textDecoration: "underline",
            }}
          >
            To reschedule
          </button>{" "}
          ready to fill the gap.
        </>
      )}
    </div>
  );
}

// The "To reschedule" tab's explanation and its auto-schedule button.
//
// PROPOSES BEFORE IT APPLIES, deliberately. Confirming a date is also
// confirming a batch of reminder emails, and a mis-click would have no
// undo — nothing keeps a history of what a competition used to be
// scheduled for.
function NotRunTools({ token, canEdit, onDone, mode = null }) {
  // CLICK, NOT HOVER. A title attribute is invisible on a phone and
  // invisible to anybody who does not think to hover — which is exactly
  // how the disabled Test configuration button managed to explain nothing
  // on 31 Aug 2026. A visible icon that opens on click works everywhere.
  const [explain, setExplain] = useState(false);
  const [plan, setPlan] = useState(null);
  const [busy, setBusy] = useState(false);

  const propose = async () => {
    setBusy(true);
    try {
      // THE QUEUE BEING WORKED ON (1.435.0) - the Test queue from Live.
      const data = await wsRequest("admin.competitions.proposeSchedule", mode ? { mode } : {}, token);
      if (!data?.proposals?.length) {
        showToast("Nothing to schedule.");
        setPlan(null);
      } else {
        setPlan(data);
      }
    } catch (err) {
      showToast(err.message || "Could not reach backend.", "error");
    }
    setBusy(false);
  };

  const apply = async () => {
    if (!plan?.proposals?.length) return;
    setBusy(true);
    try {
      // ONE ATOMIC CALL, not an edit each.
      //
      // This used to loop admin.competitions.edit. That could not survive
      // a clash check on rescheduling: as the first competition moves to
      // its new slot the second is still in its old one, so an
      // INTERMEDIATE state clashes even though the final state is fine.
      // applySchedule validates the FINAL state, which is the only one
      // that matters, and writes nothing if it would overlap.
      const res = await wsRequest(
        "admin.competitions.applySchedule",
        { proposals: plan.proposals.map((p) => ({ id: p.id, proposedStartAt: p.proposedStartAt })), ...(mode ? { mode } : {}) },
        token
      );
      showToast(
        res?.skipped
          ? `Scheduled ${res.moved}. ${res.skipped} left alone — locked in.`
          : `Scheduled ${res?.moved ?? 0}.`
      );
      setPlan(null);
      onDone();
    } catch (err) {
      showToast(err.message || "Could not reach backend.", "error");
      // Some may already have been saved, so refresh either way rather
      // than leaving the list showing a state that is no longer true.
      onDone();
    }
    setBusy(false);
  };


  return (
    <div style={{ marginBottom: 14 }}>
      {/* THE WHOLE RULE, WHERE IT IS NEEDED.

          This lived on the settings page, where it had no setting to
          belong to. It belongs here: this is the tab a person is looking
          at when they wonder why a competition came back. */}
      <p style={{ opacity: 0.7, fontSize: 13 }}>
        These missed their scheduled start and never ran. They keep everything — number, image, answer,
        pricing — and just need a new date.{" "}
        <button
          type="button"
          onClick={() => setExplain((v) => !v)}
          aria-expanded={explain}
          aria-label={explain ? "Hide how reruns work" : "How reruns work"}
          title="How reruns work"
          style={{
            border: "none",
            background: "none",
            padding: 0,
            margin: 0,
            font: "inherit",
            color: "var(--accent-blue)",
            cursor: "pointer",
          }}
        >
          &#9432;
        </button>
      </p>
      {explain && (
        <p
          style={{
            opacity: 0.75,
            fontSize: 12.5,
            marginTop: -4,
            marginBottom: 12,
            padding: "8px 10px",
            borderRadius: 6,
            background: "var(--bg-subtle, rgba(127,127,127,0.08))",
          }}
        >
          These missed their scheduled start and never ran — most often because the one before them
          overran, or because the queue was stopped past the start grace. They keep their competition
          number, their image, their answer and their pricing; all they need is a new date.
          <strong> A competition that has actually run never comes back here</strong>, whatever happened
          during it.
        </p>
      )}
      {!plan ? (
        <button type="button" onClick={propose} disabled={busy || !canEdit}>
          {busy ? "\u2026" : "Suggest dates"}
        </button>
      ) : (
        <div style={{ border: "1px solid var(--border-color)", borderRadius: 6, padding: 10, fontSize: 13 }}>
          <div style={{ marginBottom: 8 }}>
            Schedule {plan.proposals.length === 1 ? "this competition" : `these ${plan.proposals.length}`}?
          </div>
          {plan.proposals.map((p) => (
            <div key={p.id} style={{ marginBottom: 6 }}>
              <strong>{p.title}</strong> — {siteDateTime(p.proposedStartAt)}
              <span style={{ opacity: 0.6 }}>
                {" "}
                {p.fittedBetween ? "(fits an existing gap)" : "(after everything already scheduled)"}
              </span>
            </div>
          ))}
          <div style={{ opacity: 0.7, marginTop: 8, marginBottom: 8 }}>
            Placed in the earliest gaps that fit, no sooner than the lock-in allows. Anyone already told an
            old date will be emailed again.
          </div>
          <div style={{ display: "flex", gap: 6 }}>
            <button type="button" onClick={apply} disabled={busy || !canEdit}>
              {busy ? "\u2026" : plan.proposals.length === 1 ? "Schedule it" : "Schedule them"}
            </button>
            <button type="button" onClick={() => setPlan(null)} disabled={busy}>
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}


// A duration a person can read at a glance.
//
// Minutes below two hours, hours below two days, days above that. "480min"
// and "816h" are both technically correct and both useless — and an
// 8-hour competition running over the next one is exactly the mistake
// this line exists to make obvious.
function formatDuration(ms) {
  if (!ms) return "no duration";
  if (ms < 2 * 3600000) return `${Math.round(ms / 60000)}min`;
  if (ms < 2 * 86400000) return `${(ms / 3600000).toFixed(1)}h`;
  return `${(ms / 86400000).toFixed(1)} days`;
}

// How long is left before a competition's date freezes.
//
// The red border says a competition IS locked. This says how long until
// one is — which is the half that can still be acted on. Being told
// "forty minutes to fix that answer" is useful; being refused afterwards
// is not.
//
// Ticks once a second while it matters, and stops once it does not: a
// countdown that has gone stale is worse than none, because it reads as
// time you still have.
// THE SERVER'S CLOCK, NOT THE BROWSER'S.
//
// Every status on screen is decided by the server from its own clock.
// Counting down against the browser's meant that on a machine a few
// seconds adrift, a competition changed queue while its counter was still
// running — or sat at zero waiting for a change that had already happened.
//
// The offset is measured once, when admin.mode.get returns: the gap
// between the server's `serverTime` and ours at that moment. Network
// latency inflates it by a fraction of a second, which does not matter
// for a countdown shown to the second.
//
// This is NOT a polling problem. Status changes already arrive over the
// WebSocket broadcast; the two sides simply disagreed about the time.
let serverClockOffsetMs = 0;
export function setServerClockOffset(serverTime) {
  if (!Number.isFinite(serverTime)) return;
  serverClockOffsetMs = serverTime - Date.now();
}
function serverNow() {
  return Date.now() + serverClockOffsetMs;
}

function useCountdown(target) {
  const [now, setNow] = useState(() => serverNow());
  useEffect(() => {
    if (!target || target <= serverNow()) return undefined;
    const id = setInterval(() => {
      const t = serverNow();
      setNow(t);
      // Stop once it has passed. One more tick renders the locked state;
      // after that there is nothing left to count, and a list of
      // competitions would otherwise tick for ever.
      if (t >= target) clearInterval(id);
    }, 1000);
    return () => clearInterval(id);
  }, [target]);
  // Returns { label, passed }. `passed` is what lets the row lock ITSELF
  // the moment the countdown reaches zero, without waiting for a refresh.
  //
  // Lock state is derived from the clock on the server, so nothing is
  // written and nothing is broadcast when a competition locks — the red
  // border used to appear only on the next page load. The row is already
  // re-rendering every second for the countdown, so it can simply notice.
  if (!target) return { label: null, passed: false };
  const left = target - now;
  if (left <= 0) return { label: null, passed: true };
  // WEEKS AND DAYS, not 239 hours.
  //
  // This stopped at hours, so a competition ten days out read as
  // "239h 44m" — a number nobody converts in their head. Reported 6 Sep.
  //
  // Two units at a time, largest first: enough to be precise about when,
  // without a string that reads like a stopwatch. Seconds only appear
  // under an hour, where they are the part that matters.
  const totalSecs = Math.floor(left / 1000);
  const weeks = Math.floor(totalSecs / 604800);
  const days = Math.floor((totalSecs % 604800) / 86400);
  const hrs = Math.floor((totalSecs % 86400) / 3600);
  const mins = Math.floor((totalSecs % 3600) / 60);
  const secs = totalSecs % 60;
  // The smaller unit is dropped when it is zero: "3w", not "3w 0d".
  if (weeks > 0) {
    return { label: days > 0 ? `${weeks}w ${days}d` : `${weeks}w`, passed: false };
  }
  if (days > 0) {
    return { label: hrs > 0 ? `${days}d ${hrs}h` : `${days}d`, passed: false };
  }
  if (hrs > 0) {
    return { label: mins > 0 ? `${hrs}h ${mins}m` : `${hrs}h`, passed: false };
  }
  if (mins >= 1) return { label: `${mins}m ${String(secs).padStart(2, "0")}s`, passed: false };
  return { label: `${secs}s`, passed: false };
}

// One key/value pair in a competition card.
//
// Renders NOTHING when the value is missing, rather than an empty row or a
// dash: a competition that has not been answered has no "answered by", and
// a blank line invites the reader to wonder what should be there.
function CompetitionFact({ label, value, extra = null }) {
  if (value == null || value === "") return null;
  return (
    <>
      <dt style={{ opacity: 0.55, whiteSpace: "nowrap" }}>{label}</dt>
      {/* `extra` (1.510.0) is for a control that belongs TO a fact rather
          than beside it - the root-only Show on a masked answer. Laid out
          inline so the row keeps its height whether or not it is there. */}
      <dd style={{ margin: 0, display: "flex", alignItems: "center", gap: 8 }}>
        <span>{value}</span>
        {extra}
      </dd>
    </>
  );
}

function CompetitionRow({ c, onSave, onRemove, modeStatus, isRoot = false, token = null }) {

  const symbol = useCurrencySymbol();

  const lockCountdown = useCountdown(c.locksAt);
  // AND HOW LONG UNTIL IT GOES LIVE. The absolute time is already on the
  // line above ("starts 31 Aug 2026, 18:35"); what an operator watching a
  // queue actually wants is how long they have.
  // NOT FOR A COMPETITION THAT WILL NOT RUN. "Going live in 44s" on a
  // card in "To reschedule" is a promise the queue has already withdrawn.
  // NOR FOR ONE IN THE QUEUE THAT IS NOT BEING SERVED (v1.389.0): it
  // will not start however close its time is. `served` is absent from a
  // backend older than the queues, which counts as served.
  const unserved = c.served === false && !c.answeredAt && c.status !== "active";
  const liveCountdown = useCountdown(
    c.answeredAt || c.status === "not_run" || unserved ? null : c.effectiveStart
  );
  // AND WHEN IT ENDS, for a competition that is already running. "Going
  // live in" has nothing to say once it has gone live, and "Due at" is a
  // time in the past — what an operator wants then is how long is left.
  //
  // Null when there is no end: with the lottery off a competition runs
  // until somebody wins it.
  // ONLY WHILE IT IS ACTUALLY RUNNING.
  //
  // computeEffectiveEnd works from the scheduled start, so a competition
  // that never started still has an "end" in the future — and a card
  // sitting in "To reschedule" read "Ends in 3m 12s" as if it were live.
  // Reported 2 Sep.
  const endsCountdown = useCountdown(
    c.answeredAt || c.status !== "active" ? null : c.endsAt
  );

  // Locked as far as the SCREEN is concerned: either the server said so
  // when this list was fetched, or the countdown has since run out.
  // THE SERVER DECIDES, not the clock.
  //
  // This used to add `|| lockCountdown.passed` — "the lock window has
  // opened, so it must be locked". Since 1.215.0 that no longer follows:
  // a paused queue prevents a competition becoming locked even though the
  // moment has passed. The card showed "Locked in" on a competition
  // sitting in "To reschedule". Reported 2 Sep.
  //
  // `lockedIn` comes from lockInWindowFor on the server, which knows
  // about the pause. The countdown is still used to show the run-up.
  const showAsLocked = !!c.lockedIn;
  // AN ENDED COMPETITION IS A RECORD, not a queue entry — a winner, an
  // answer, purchases and a prize. Demo is exempt: those exist to be
  // generated and cleared. The backend refuses either way; this is so the
  // button says so rather than failing when pressed.
  const endedRecord = c.status === "ended" && (c.mode || "live") !== "demo";

  const [expanded, setExpanded] = useState(false);

  // WHAT ROOT ASKED TO SEE (1.510.0). Null until they press Show, which
  // calls a root-only action that writes an activity-log entry. Held per
  // card and per page load - reloading puts the asterisks back.

  const [shownAnswer, setShownAnswer] = useState(null);

  const [showingAnswer, setShowingAnswer] = useState(false);

  // Same shape the create form uses, so the two can share

  // CompetitionFormFields — converted from this competition's actual

  // stored values (milliseconds) via msToValueUnit, rather than starting

  // from create's own hardcoded defaults.

  const [form, setForm] = useState(() => {


    const duration = msToValueUnit(c.durationMs);

    const lotteryLead = msToValueUnit(c.lotteryLeadMs);

    const privateViewTotalMinutes = Math.round((c.privateViewMs || 0) / 60000);

    return {

      // NOT PRE-FILLED (1.510.0). The card shows asterisks until the
      // competition is over, and an edit form that quietly filled itself
      // in with the real answer would put it back on screen - which is
      // the one thing this change exists to stop. Blank means "leave it
      // alone", which the backend now enforces on its own side too.
      answer: "",

      answerMode: c.answerMode,

      // ---- ABSENT ON A COMPETITION NOBODY HAS SET, so the controls fall back
      // ---- to the same defaults the backend reads. Hydrating these as
      // ---- undefined would make both inputs uncontrolled and React would warn.

      missedNoticeMode: c.missedNoticeMode || "text",

      missedNoticeText: c.missedNoticeText || "",

      pricePerPixel: c.pricePerPixel,

      privateViewHours: Math.floor(privateViewTotalMinutes / 60),

      privateViewMinutes: privateViewTotalMinutes % 60,

      prizeMoney: c.prizeMoney,

      creditsPerEntry: c.creditsPerEntry,

      

      scheduledStartLocal: msToLocalInput(c.scheduledStartAt),

      // Its own choice if it has one; otherwise it behaves as the
      // sitewide setting says, which is what a competition created before
      // this field existed does. Editing it makes that explicit.
      lotteryEnabled: c.lotteryEnabled ?? true,
      durationValue: duration.value,

      durationUnit: duration.unit,

      lotteryLeadValue: lotteryLead.value,

      lotteryLeadUnit: lotteryLead.unit,

    };

  });

  const [busy, setBusy] = useState(false);

  const statusColor = { active: "var(--accent-green)", queued: "var(--accent-yellow)", ended: "var(--text-secondary)", not_run: "var(--accent-red)" };

  // "not_run" is editable BECAUSE it is the state that most needs it: a
  // missed competition is fully prepared and its only problem is the
  // date. Refusing here is what would make that tab a bin rather than a
  // holding pen.
  const editable = c.status === "queued" || c.status === "not_run";

  // Same rule the backend's own assertNotFrozen enforces — this

  // competition's live/demo status doesn't match what THIS admin is

  // currently working in, so nothing here could ever actually

  // succeed even if clicked. Disabled rather than hidden, so it's

  // still visible in context alongside data that IS actionable.

  const frozen = modeStatus ? modeOf(c) !== modeStatus.effectiveMode : false;

  // "Switch modes" is a real instruction for test and live data. For DEMO
  // data it names a mode nobody can switch to any more, so it says what
  // is actually true instead.
  const frozenTitle = !frozen
    ? undefined
    : modeOf(c) === "demo"
      ? "Demo data — generated and replaced wholesale by Generate on the competition queue"
      : `${modeLabel(modeOf(c))} data — switch modes to act on it`;



  const save = async (e) => {

    e.preventDefault();

    setBusy(true);


    const totalDurationMinutes = Number(form.durationValue) * TIME_UNIT_TO_MINUTES[form.durationUnit];

    const totalLotteryLeadMinutes = Number(form.lotteryLeadValue) * TIME_UNIT_TO_MINUTES[form.lotteryLeadUnit];

    const totalPrivateViewMinutes = Number(form.privateViewHours) * 60 + Number(form.privateViewMinutes);

    // A LOTTERY HAS TO LAST LONG ENOUGH TO BE ONE (1.549.0). The server
    // refuses this too, against the finished competition rather than
    // against what was sent - an edit can turn the lottery on and say
    // nothing about the lead, which reading the body would let through.
    // This is here so the answer arrives without a round trip.
    if (form.lotteryEnabled !== false && totalLotteryLeadMinutes < MIN_LOTTERY_LEAD_MINUTES) {

      showToast(MIN_LOTTERY_LEAD_MESSAGE, "error");

      setBusy(false);

      return;

    }

    const err = await onSave(c.id, {

      answer: form.answer,

      answerMode: form.answerMode,

      // BOTH SENT, for the create form's reason and one more: on the EDIT route
      // a blank wording DELETES the stored key, which is the only way to put a
      // competition back to the default once it has been given a wording.

      missedNoticeMode: form.missedNoticeMode,

      missedNoticeText: form.missedNoticeText,

      pricePerPixel: form.pricePerPixel,

      privateViewMinutes: totalPrivateViewMinutes,

      prizeMoney: form.prizeMoney,

      creditsPerEntry: form.creditsPerEntry,

      

      // Changing this is what takes a competition OUT of that tab — the
      // status is derived from the date, so there is no flag to clear.
      scheduledStartAt: localInputToMs(form.scheduledStartLocal),

      lotteryEnabled: form.lotteryEnabled !== false,
      durationMinutes: totalDurationMinutes,

      lotteryLeadMinutes: totalLotteryLeadMinutes,

    });

    if (err) showToast(err, "error");

    else showToast("Saved.");

    setBusy(false);

  };



  // A LOCKED COMPETITION IS OUTLINED IN RED. Its start time cannot be
  // moved and it cannot be deleted — a state to recognise BEFORE trying,
  // not after being refused. lockedIn comes from the server, which is also
  // what enforces it, so the two cannot disagree.
  return (

    <div style={{ border: c.status === "active" ? "2px solid var(--accent-green)" : showAsLocked ? "2px solid var(--accent-red)" : "1px solid var(--border-color)", borderRadius: 8, padding: 12, marginBottom: 10, opacity: frozen ? 0.55 : 1 }}>

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>

        <div>

          <strong>{c.title}</strong>{" "}

          <span style={{ color: statusColor[c.status], fontSize: 13 }}>({c.status === "not_run" ? "not run" : c.status})</span>{" "}

          <span

            style={{

              fontSize: 11,

              padding: "1px 6px",

              borderRadius: 10,

              border: `1px solid ${modeColor(modeOf(c))}`,

              opacity: 0.85,

            }}

          >

            {modeLabel(modeOf(c))}

          </span>{" "}

          {/* WHICH QUEUE IT IS IN — decided when it was created, by the
              switch at that moment. Dashed when that queue is not the one
              being served. */}
          {c.audience && (
            <span
              title={
                c.served === false
                  ? `In the ${c.audience === "api" ? "API" : "Native"} queue, which is not being served right now`
                  : `In the ${c.audience === "api" ? "API" : "Native"} queue, which is being served`
              }
              style={{
                fontSize: 11,
                padding: "1px 6px",
                borderRadius: 10,
                border: `1px ${c.served === false ? "dashed" : "solid"} var(--border-color)`,
                opacity: c.served === false ? 0.7 : 0.85,
              }}
            >
              {c.audience === "api" ? "API" : "Native"}
            </span>
          )}

        </div>

        <div style={{ display: "flex", gap: 8 }}>

          {editable && (

            <button disabled={frozen} title={frozenTitle} onClick={() => setExpanded(!expanded)}>

              {expanded ? "Hide edit" : "Edit"}

            </button>

          )}

          {/* NO DELETE BUTTON AT ALL for an active or ended competition.

                An active one is running; an ended one is the record of
                what was won. Neither can be deleted, so offering the
                button and disabling it just puts a dead control on every
                finished competition — the tab most likely to be full of
                them. Absence says it better than a tooltip nobody hovers.

                Demo is exempt: those exist to be generated and cleared. */}
            {c.status !== "active" && !endedRecord && (

            <button disabled={frozen || showAsLocked} title={showAsLocked ? "Locked in — this competition can no longer be deleted" : frozenTitle} onClick={() => onRemove(c.id, c.title, modeOf(c))}>

              Delete

            </button>

          )}

        </div>

      </div>

      {/* THE HEADLINE, then the facts.

          What an operator wants first is WHEN — going live in, or locking
          in. That is the number that changes while you watch, so it is
          the one at the top in larger type.

          Everything else is reference: read once, checked against
          something. Key and value in pairs is easier to scan than a run
          of dot-separated prose, and it survives a value being missing
          without leaving a stray separator. */}
      <div
        style={{
          marginTop: 10,
          padding: "10px 12px",
          borderRadius: 6,
          background: "var(--bg-subtle, rgba(127,127,127,0.06))",
        }}
      >
        <div
          style={{
            fontSize: 15,
            fontWeight: 600,
            marginBottom: 8,
            color: showAsLocked
              ? "var(--accent-red)"
              : unserved
                ? "var(--accent-yellow)"
                : liveCountdown.label || endsCountdown.label
                ? "var(--accent-green)"
                : "inherit",
          }}
        >
          {c.answeredAt
            ? // "ENDED", NOT "ANSWERED". A competition resolved by lottery
              // draw was not answered by anybody — nobody guessed it — so
              // "Answered" was wrong for every lottery outcome. "Ended"
              // is true of both. Who won, when there is a winner, is
              // already shown as its own fact below.
              `Ended ${siteDateTime(c.answeredAt)}`
            : unserved
              ? // ITS QUEUE IS NOT BEING SERVED. It will not start until the
                // switch changes; once its slot passes it is in "To
                // reschedule" like any other missed competition.
                c.status === "not_run" && c.effectiveStart && c.effectiveStart <= Date.now()
                ? `Missed its slot at ${siteDateTime(c.effectiveStart)} — the ${c.audience === "api" ? "API" : "Native"} queue was not being served`
                : `Waiting — the ${c.audience === "api" ? "API" : "Native"} queue is not being served. It starts only while "Use API exclusively" is ${c.audience === "api" ? "on" : "off"}.`
            : liveCountdown.label
              ? `Going live in ${liveCountdown.label}`
              : endsCountdown.label
                ? `Ends in ${endsCountdown.label}`
                : c.status === "active"
                  ? c.endsAt && c.endsAt <= Date.now()
                    ? // PAST ITS END AND NOT YET RESOLVED. The draw runs on
                      // the once-a-second tick, so this is normally
                      // invisible — it lasts if the backend was down at the
                      // moment it should have ended, e.g. a deploy.
                      //
                      // It used to fall through to "ends when somebody wins
                      // it", which is only true of a competition with NO
                      // lottery. Reported 2 Sep after a restart left one
                      // overdue.
                      "Overdue — waiting for the draw"
                    : "Running — ends when somebody wins it"
                  : c.status === "not_run"
                    ? !c.effectiveStart
                      ? "No date set — needs one before it can run"
                      : c.effectiveStart > Date.now()
                        ? // ITS SLOT IS STILL AHEAD. It reads not_run because
                          // the queue is stopped and it can no longer be
                          // confirmed in time — "missed" would be wrong.
                          `Cannot be confirmed while the queue is stopped — needs a new date`
                        : `Missed its slot at ${siteDateTime(c.effectiveStart)} — needs a new date`
                    : c.effectiveStart
                      ? `Due at ${siteDateTime(c.effectiveStart)}`
                      : "Waiting on the previous competition to be answered"}
        </div>

        {showAsLocked ? (
          <div style={{ fontSize: 12.5, color: "var(--accent-red)", marginBottom: 8 }}>
            Locked in — the start time can no longer be moved, and it cannot be deleted.
          </div>
        ) : lockCountdown.label && !unserved ? (
          // Not for an unserved one: it cannot lock, so there is no lock
          // to count down to.
          <div style={{ fontSize: 12.5, color: "var(--accent-yellow)", marginBottom: 8 }}>
            Editable for another {lockCountdown.label}, then the date locks.
          </div>
        ) : null}

        <dl
          style={{
            display: "grid",
            gridTemplateColumns: "auto 1fr",
            gap: "3px 12px",
            margin: 0,
            fontSize: 13,
          }}
        >
          {/* ASTERISKS UNTIL IT IS OVER (1.510.0). Asked for on 27 Sep
              2026. The answer is the product, and it sat on the card for
              anybody who walked past the screen, joined a screen-share or
              sat through a support call.

              HYGIENE, NOT ACCESS CONTROL, and the difference is worth
              being straight about: an admin holding manage_competitions
              can SET the answer, so they can always learn one by choosing
              it. What this stops is the answer being on display to
              everyone who opens the panel. */}

          <CompetitionFact

            label="Answer"

            value={

              c.answeredAt || shownAnswer

                ? `${shownAnswer || c.answer} (${c.answerMode})`

                : `\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022 (${c.answerMode})`

            }

            extra={

              /* ONCE (1.512.0). Once root has looked, the button is gone
                 and the card says when - so the next person sees that it
                 has been used rather than pressing it and being refused.
                 Setting a new answer clears it, which is the only way
                 back: a new answer is a new secret. */

              c.answerRevealedAt && !c.answeredAt && !shownAnswer ? (

                <span className="sub">shown once on {siteDateTime(c.answerRevealedAt)}</span>

              ) : !c.answeredAt && !shownAnswer && isRoot && token ? (

                <button

                  className="btn-quiet"

                  data-reveal-answer

                  disabled={showingAnswer}

                  title="Root only. Looking is recorded in the activity log."

                  onClick={async () => {

                    setShowingAnswer(true);

                    try {

                      const res = await wsRequest("admin.competitions.revealAnswer", { competitionId: c.id }, token);

                      setShownAnswer(res?.answer || "");

                    } catch {

                      /* The card keeps its asterisks, which is the
                         truthful thing to show when the look failed. */

                    }

                    setShowingAnswer(false);

                  }}

                >

                  {showingAnswer ? "\u2026" : "Show"}

                </button>

              ) : null

            }

          />
          <CompetitionFact
            label="Starts"
            value={c.scheduledStartAt ? siteDateTime(c.scheduledStartAt) : "when the queue reaches it"}
          />
          {/* A COMPETITION WITHOUT A LOTTERY HAS NO END, so a duration and an
              end date are fiction. It runs until somebody wins it.

              These were computed from durationMs regardless, so a
              no-lottery competition showed "7.0 days" and an end date a
              week out that nothing would ever act on. Reported 3 Sep. */}
          {/* Once it has ended, how long it ACTUALLY ran is known and more
              use than the configured duration — which it may well have
              finished well inside. */}
          <CompetitionFact
            label={c.answeredAt && c.actualStartAt ? "Ran for" : "Duration"}
            value={
              c.answeredAt && c.actualStartAt
                ? formatDuration(c.answeredAt - c.actualStartAt)
                : c.lotteryEnabled === false
                  ? "—"
                  : formatDuration(c.durationMs)
            }
          />
          {/* ONCE IT HAS ENDED, THE END IS KNOWN — whether or not it ever
              had a scheduled one.

              A no-lottery competition showed "— runs until somebody wins
              it" even after somebody had won it, which was true right up
              until it stopped being true. Reported 3 Sept. */}
          {/* Nothing once it has ended — the headline above already says
              "Ended" with the time, and repeating it in the facts is
              noise. "Ran for" is the part that adds something. */}
          <CompetitionFact
            label="Ends"
            value={
              c.answeredAt
                ? null
                : c.lotteryEnabled === false
                  ? "— runs until somebody wins it"
                  : c.scheduledStartAt && c.durationMs
                    ? siteDateTime(c.scheduledStartAt + c.durationMs)
                    : null
            }
          />
          <CompetitionFact label="Prize" value={`${symbol}${c.prizeMoney}`} />
          <CompetitionFact label="Pixels" value={`${symbol}${c.pricePerPixel} each`} />
          {/* WHAT AN ENTRY COSTS IN CREDIT, not a charge taken at the time.
              A guess spends entry credit; nobody is charged per guess.
              Credit comes from buying pixels or topping up directly —
              there is no "buy a guess".

              "£1.00 after the free one" read as a card charge that never
              happens. Credit is held in the site currency, so naming the
              amount is accurate and says nothing about how many credits
              make an entry, which is undecided. */}
          <CompetitionFact
            label="Entry"
            /* NULL WHEN ENTRY CREDIT IS OFF, so CompetitionFact drops the
               row entirely — it renders nothing for a missing value. The
               string was built first, which turned null into "0 credits":
               a price, on a site that does not charge. Reported 7 Sep. */
            value={
              c.creditsPerEntry
                ? `${c.creditsPerEntry} credit${c.creditsPerEntry === 1 ? "" : "s"}, after the free one`
                : null
            }
          />
          <CompetitionFact
            label="Private view"
            value={`${(c.privateViewMs / 60000).toFixed(1)} min`}
          />
          <CompetitionFact
            label={c.wonByLottery ? "Won by (lottery draw)" : "Answered by"}
            value={c.answeredAt ? c.answeredByEmail : null}
          />
        </dl>
      </div>

      {!editable && (

        <div style={{ fontSize: 12, opacity: 0.5, marginTop: 6 }}>

          {c.status === "active"

            ? "Active competitions can't be edited — only queued ones."

            : "Ended competitions can't be edited."}

        </div>

      )}



      {editable && expanded && (

        <form onSubmit={save} style={{ marginTop: 12, paddingTop: 12, borderTop: "1px solid var(--border-color)" }}>

          <CompetitionFormFields form={form} setForm={setForm} symbol={symbol} editing />

          <button type="submit" disabled={busy}>

            {busy ? "Saving…" : "Save changes"}

          </button>

        </form>

      )}

    </div>

  );

}



// THE SAME FOUR-STATE VOCABULARY THE INVENTORY BESIDE THIS USES, because
// two tables of marks on one panel meaning different things is worse than
// either. `empty` is its own state and deliberately NOT green: a
// collection switched on, connected, never diverged and holding nothing
// has either never been written to or been emptied, and "the flag is set"
// is exactly the reassurance that stops anybody looking. Same shape as an
// empty shadow reading as agreement (1.565.0) and a 40-byte archive
// reading as a backup (1.576.0).
const MONGO_ROW_MARK = {
  ok: "[ OK ]",
  empty: "[MISS]",
  diverged: "[MISS]",
  unreachable: "[STOP]",
  unknown: "[ ?? ]",
  off: "[ -- ]",
};
const MONGO_ROW_COLOUR = {
  ok: "var(--accent-green)",
  empty: "var(--accent-red)",
  diverged: "var(--accent-red)",
  unreachable: "var(--accent-red)",
};
const MONGO_STATE_COLOUR = {
  ready: "var(--accent-green)",
  failed: "var(--accent-red)",
  connecting: "inherit",
  off: "inherit",
};

// ---- THE COLUMN WIDTHS, IN ONE PLACE (1.880.4) ----
//
// REPORTED: the panel was "looking messy". Two faults, and they are the same
// fault twice.
//
// THE DESCRIPTION WAS THE ONLY COLUMN THAT COULD SHRINK. Every cell before it
// carries flexShrink: 0 and a minWidth - put there by 1.6xx.x after "[ OK ]"
// wrapped and dropped its bracket onto its own line - and the description was
// given neither, so the whole squeeze landed on it and it collapsed to
// min-content: one or two words a line, with empty space to the right of the
// row.
//
// AND THE FILE CELL WAS NOT ALWAYS THERE. It rendered only when the rule was
// something other than `equal`, so rows without it started their description in
// a different place and no two rows lined up. Worse, `equal` is the case a
// reader most needs: it means the file is STILL THE RECORD and is rewritten on
// every change, so that collection is written twice. The panel said nothing at
// all about exactly those rows - which is how "unfrozen" came to be read as
// "not in the database yet", when every row here reads AND writes the database.
//
// Declared once because the header below and the rows under it have to agree,
// and two copies of a width is how a header stops sitting over its column.
const MONGO_COLS = {
  mark: 54,
  // 150 UNTIL 1.1036.0, AND SIX PIXELS SHORT. "Going-live confirmations"
  // measures 156, and a flex item with minWidth but no maxWidth grows to its
  // content - so that one row pushed its own count, reads and file cells out
  // of line with the other thirty. Measured, not estimated.
  label: 180,
  count: 90,
  reads: 74,
  file: 86,
  // THE VERSION, PULLED OUT OF THE SENTENCE (1.1036.0). See the row.
  since: 76,
};

// THE SENTENCE BESIDE EACH ROW, in one place because it is rendered twice:
// once as the text and once as the title that carries it in full when the
// clamp cuts it. Two copies of a string that have to agree is how a tooltip
// comes to say something its own row does not.
function mongoRowSentence(c) {
  return c.diverged
    ? "a write FAILED since boot \u2014 the database is behind the file"
    : c.writes;
}

// TWO LINES, THEN AN ELLIPSIS. Declared once so the rows cannot drift apart
// and the header cannot be clamped by accident.
const MONGO_CLAMP = {
  display: "-webkit-box",
  WebkitLineClamp: 2,
  WebkitBoxOrient: "vertical",
  overflow: "hidden",
};

function ServerControlPanel({ authHeaders, canEdit, modeStatus }) {

  const token = authHeaders["x-auth-token"];

  const [status, setStatus] = useState(null);

  const [statusError, setStatusError] = useState(null);

  const [busyKey, setBusyKey] = useState(null); // e.g. "backend-restart", while that action is in flight

  const { confirm, confirmModal } = useConfirm();

  // No test/demo variant of "the actual running process" exists at

  // all — same reasoning as the backend's own assertNotFrozen check

  // on admin.server.action.

  // DEMO ONLY. Requiring Live mode did not make a restart safer — the
  // process is equally real either way — it just made the buttons
  // impossible to test. The server agrees; see assertSettingsEditable.
  const frozen = adminMode(modeStatus) === "demo";

  const frozenTitle = "Leave Demo mode to manage this";



  const refresh = () => {

    wsRequest("admin.server.status", {}, token)

      // CLEAR THE ERROR ON SUCCESS.
      //
      // This polls every 5 seconds, so it can heal itself — but it never
      // cleared the message, so one failed poll left "Could not reach
      // backend" on screen permanently, through any number of successful
      // ones after it. Only a page refresh removed it.
      //
      // Which is worse than not polling at all: the panel looked broken
      // while working perfectly. Reported 15 Sep 2026 — "I refreshed the
      // page and it went away".
      .then((data) => {
        setStatus(data);
        setStatusError("");
      })

      .catch((err) => setStatusError(err.message || "Could not reach backend."));

  };



  useEffect(refresh, []);

  useEffect(() => {

    const id = setInterval(refresh, 5000);

    return () => clearInterval(id);

  }, []);

  // ONCE, NOT ON THE POLL. The inventory spawns about a dozen short-lived

  // processes to ask what is installed; the status above polls every five

  // seconds. What a box HAS does not change between one poll and the next, and

  // hanging this off `refresh` would have meant a dozen processes every five

  // seconds for the life of every open admin tab.

  const [inventory, setInventory] = useState(null);

  const [inventoryError, setInventoryError] = useState("");

  useEffect(() => {

    // CLEARED BEFORE THE REQUEST AND AGAIN ON SUCCESS, even though this loads

    // once. permissions.test.mjs holds the rule that a panel which can retry

    // must be able to heal - ServerControlPanel polled for five seconds at a

    // time without ever clearing statusError, so one failed poll left "Could

    // not reach backend" on screen permanently.

    //

    // This one does not retry, so strictly it is exempt. It clears anyway:

    // the cost is a line, and the moment anybody gives this a refresh button

    // the exemption stops being true and nothing would have said so.

    setInventoryError("");

    wsRequest("admin.server.inventory", {}, token)

      .then((d) => {

        setInventory(d.components || []);

        setInventoryError("");

      })

      .catch((err) => setInventoryError(err.message || "Could not read what this server has."));

  }, []);



  // ---- AN UPDATE A DEPLOY HELD BACK (1.692.0) ----
  //
  // A deploy during a competition is HELD: installed, built, boot-checked and
  // through both suites, with the frontend symlink not flipped and the backend
  // not restarted. This is the button that finishes it.
  //
  // HTTP, NOT THE WEBSOCKET, which the rest of this panel uses. Two reasons,
  // and the second is the real one: the routes are HTTP (1.690.0), and EVERY
  // WebSocket action is refused while the install freeze is on (1.691.0) - so
  // a socket call would be refused by the very state it was asked to start.
  const [heldInfo, setHeldInfo] = useState(null);

  const [heldError, setHeldError] = useState("");

  const [publishing, setPublishing] = useState(false);

  // The version to wait for. Set once the POST is accepted; the restart is
  // finished when the RUNNING process reports it.
  const [installTarget, setInstallTarget] = useState("");

  const [installGaveUp, setInstallGaveUp] = useState(false);

  const refreshHeld = () => {

    fetch(`${API_BASE}/api/admin/server/held-update`, { headers: { "x-auth-token": token } })

      .then(async (res) => {

        const data = await res.json().catch(() => ({}));

        if (!res.ok) throw new Error(data.error || `Could not read what is waiting (${res.status}).`);

        return data;

      })

      // CLEARED ON SUCCESS, which is the rule permissions.test.mjs holds for
      // this panel: it polls, so one failed poll must not leave its message on
      // screen through every successful one after it.
      .then((data) => {

        setHeldInfo(data);

        setHeldError("");

      })

      .catch((err) => setHeldError(err.message || "Could not read what is waiting to be published."));

  };

  useEffect(refreshHeld, []);

  // PAUSED WHILE AN INSTALL IS BEING WAITED OUT. The backend is restarting
  // because this panel asked it to, so a poll that fails during it is the
  // expected course of events - and refreshHeld's error message on screen
  // would read as a fault in the install rather than as the restart it is.
  // The effect below is what watches that window.
  useEffect(() => {

    if (installTarget) return undefined;

    const id = setInterval(refreshHeld, 5000);

    return () => clearInterval(id);

  }, [installTarget]);

  // ---- WAITING OUT THE RESTART ----
  //
  // WHY /api/v1/version AND NOT THIS PANEL'S OWN POLL. Every WebSocket action
  // is refused while the freeze is on, and during the restart itself nothing
  // answers at all. This is a GET, which the freeze lets through, and it
  // reports what the RUNNING process loaded - so it changes at the moment the
  // install is finished and not a moment before.
  //
  // AND IT ANSWERS ON A BOX THAT IS NOT LIVE. Every other /api/v1 route
  // refuses with 503 during the not-live lockdown, which is the state a test
  // box with the API switched off sits in; /api/v1/version deliberately
  // carries no lockdown check, and backend-unavailable-page.test.mjs asserts
  // that it never gains one. Polling anything else under /api/v1 would sit at
  // 503 on such a box and give up after a minute on a successful install.
  //
  // AND IT RELOADS, because the frontend bundle itself has changed: the
  // symlink now points at the new build, and this page is the old one.
  //
  // IT GIVES UP AFTER A MINUTE rather than polling for ever. A restart that
  // never completes is something to be told about, not something to spin on -
  // the same reason the freeze itself expires.
  useEffect(() => {

    if (!installTarget) return undefined;

    const deadline = Date.now() + 60_000;

    let cancelled = false;

    const id = setInterval(() => {

      if (Date.now() > deadline) {

        clearInterval(id);

        if (!cancelled) {

          setInstallGaveUp(true);

          setPublishing(false);

        }

        return;

      }

      fetch(`${API_BASE}/api/v1/version`)

        .then((res) => (res.ok ? res.json() : null))

        .then((data) => {

          if (cancelled || !data) return;

          if (data.version === installTarget) {

            clearInterval(id);

            window.location.reload();

          }

        })

        // A failed poll during a restart is the ordinary case, not an error.
        .catch(() => {});

    }, 1500);

    return () => {

      cancelled = true;

      clearInterval(id);

    };

  }, [installTarget]);

  const publishHeld = async () => {

    const versions = (heldInfo?.held?.versions || []).join(", ") || "the held update";

    if (

      !(await confirm(

        `Publish ${versions}? The site is frozen while it installs — every change is refused for a few ` +

          `seconds — and the backend restarts, including this admin session. This page will reload itself. ` +

          `Are you sure?`

      ))

    ) {

      return;

    }

    setPublishing(true);

    setInstallGaveUp(false);

    try {

      const res = await fetch(`${API_BASE}/api/admin/server/publish-held`, {

        method: "POST",

        headers: { "x-auth-token": token },

      });

      const data = await res.json().catch(() => ({}));

      // ---- THREE REFUSALS, THREE REGISTERS (1.709.0) ----
      //
      // All three are 409s and all three used to become a red "could not
      // publish" toast. Two of them are not failures, and the one that reads
      // worst is the one that happens most: a DEPLOY published the updates a
      // minute ago, the marker is already cleared, and an operator who clicks
      // Install now is told "there is no held update" - which reads as "your
      // install did nothing" when the truth is "they are already in".
      //
      // BRANCHED ON A CODE, NOT ON THE ENGLISH. See the route's own note.
      if (!res.ok && data.reason === "nothing-held") {
        const running = data.runningVersion || heldInfo?.runningVersion || null;
        const built = typeof __APP_VERSION__ === "string" ? __APP_VERSION__ : null;
        // THE PAGE IS THE THING THAT IS BEHIND, and saying so here is the
        // whole point: an operator looking at a stale header beside a current
        // server is one keystroke from believing the install failed.
        const stale = !!running && !!built && running !== built;
        showToast(
          stale
            ? `Already up to date — the server is running v${running}. This page is v${built}; reload to catch up.`
            : running
              ? `Already up to date — the server is running v${running}.`
              : "Already up to date — nothing was waiting to be installed.",
          // A WARNING ONLY WHEN THERE IS SOMETHING TO DO. Up to date with a
          // current page is information; up to date with a stale one asks for
          // a reload, and the header's own notice carries the button, because
          // a toast is a string and cannot hold one.
          stale ? "warning" : "info"
        );
        setPublishing(false);
        refreshHeld();
        return;
      }
      // ---- A DEPLOY IS RUNNING (1.718.0) ----
      //
      // A WAIT WITH NOTHING TO DO, which is why it is a warning and not an
      // error: the deploy publishes the build itself and restarts when its
      // tests pass. The sentence comes from the backend's library, so this
      // says exactly what the button's own hover says.
      //
      // THE BUTTON SHOULD ALREADY BE DISABLED when this happens - heldInfo
      // carries `deploying` - so reaching here means the deploy started
      // between a poll and the click. That is the case the route exists for:
      // on 10 Oct 2026 the press landed eleven minutes into a deploy's suite
      // and published its half-tested build.
      if (!res.ok && data.reason === "deploying") {
        showToast(data.error || "A deploy is running on this box.", "warning");
        setPublishing(false);
        refreshHeld();
        return;
      }
      // A WAIT, NOT A FAULT. The message already says what is in the way and
      // when it clears.
      if (!res.ok && data.reason === "blocked") {
        showToast(data.error || "A competition is being played.", "warning");
        setPublishing(false);
        refreshHeld();
        return;
      }
      // Everything else, including reason "unreadable", is a real failure.
      if (!res.ok) throw new Error(data.error || `Could not publish (${res.status}).`);

      showToast(data.message || "Publishing — the backend is restarting.");

      // WHAT TO WAIT FOR: what the FILES say, which on a held box is ahead of
      // what is running. When the running process reports it, the install is
      // done.
      setInstallTarget(heldInfo?.filesVersion || "");

    } catch (err) {

      // NOTHING WAS PUBLISHED. Every refusal from that route happens before
      // the flip, so the site is exactly as it was - and the freeze lifts
      // itself if the flip was what failed.
      showToast(err.message || "Could not publish.", "error");

      setPublishing(false);

      refreshHeld();

    }

  };

  // ---- WHY THE BUTTON IS OFF, IN ONE PLACE (1.695.0) ----
  //
  // The button is on screen whether or not anything is waiting. Asked for
  // 9 Oct 2026: "the 'Install' button should always be present so the user
  // knows its there. It should be disabled by default and only enabled when
  // there updates which can be installed."
  //
  // WHICH MEANS IT IS USUALLY DISABLED - a held update is the rare state - so
  // every one of those states has to say why, or the panel has a permanently
  // greyed control that reads as broken rather than as idle.
  //
  // ORDERED BY WHAT THE OPERATOR COULD DO ABOUT IT: a permission they have to
  // be granted, then a mode they can leave, then a state they can only wait
  // out. The first match wins, so the most actionable reason is the one shown.
  const installButtonTitle = () => {

    if (!canEdit) return "Needs edit permission on Server Admin";

    // A HINT, NOT A BLOCK, exactly like the restart buttons above: Demo mode
    // does not disable anything in this panel, it explains itself.
    if (frozen) return frozenTitle;

    if (publishing) return "Installing — waiting for the backend to come back";

    if (heldError) return heldError;

    if (!heldInfo) return "Still reading what is waiting";

    // A DEPLOY FIRST, for the same reason the route checks it first: mid-
    // deploy the held marker has usually not been written yet, so the branch
    // below would say "Nothing is waiting" during the twelve minutes when
    // pressing this is most harmful.
    if (heldInfo.deploying) return heldInfo.deploying.why;

    if (heldInfo.installing) return "An install is already running on this box";

    if (!heldInfo.held) {

      return "Nothing is waiting. A deploy only holds an update back while a competition is being played.";

    }

    if (heldInfo.held.unreadable) return heldInfo.held.why;

    if (heldInfo.blocker) {

      // AND WHEN, on the hover too (1.698.0) - the button is the thing an
      // operator points at when they want to know why they cannot publish.
      const when =
        heldInfo.window && heldInfo.window.kind === "none"
          ? ` No window: ${heldInfo.window.why}.`
          : heldInfo.window && heldInfo.window.kind === "at"
            ? ` A window opens ${new Date(heldInfo.window.at).toLocaleString()}.`
            : "";

      return heldInfo.blocker.kind === "payment"

        ? `Waiting — ${heldInfo.blocker.why}`

        : `Waiting for ${heldInfo.blocker.title || "a competition"} — ${heldInfo.blocker.why}.${when}`;

    }

    // THE SERVER'S VERDICT IS THE LAST WORD. Every branch above is this
    // panel's reading of why canPublish is false; if it is false for a reason
    // none of them knows, say so rather than claiming the button is ready.
    if (!heldInfo.canPublish) return "Not right now — see above";

    return undefined;

  };

  const runAction = async (proc, action) => {

    const confirmations = {

      stop:

        `Stop the ${proc}? It will be unavailable until manually started again` +

        (proc === "backend" ? " — this will also break this admin dashboard's own API until it's restarted via SSH." : ".") +

        " Are you sure?",

      restart:

        `Restart the ${proc}? This causes a brief moment of downtime` +

        (proc === "backend" ? ", including for this admin session." : ".") +

        " Are you sure?",

    };

    if (confirmations[action] && !(await confirm(confirmations[action]))) return;



    setBusyKey(`${proc}-${action}`);

    try {

      const data = await wsRequest("admin.server.action", { process: proc, action }, token);

      showToast(data.message);

    } catch (err) {

      showToast(

        err.message || "Could not reach backend — if you just stopped or restarted it, this is expected for a moment.",

        "error"

      );

    }

    setBusyKey(null);

    setTimeout(refresh, 2000);

  };



  const statusColor = { online: "var(--accent-green)", stopped: "var(--accent-red)", errored: "var(--accent-red)" };



  const ProcessRow = ({ label, proc }) => {

    const s = status && status[proc];

    return (

      <div style={{ border: "1px solid var(--border-color)", borderRadius: 8, padding: 12, marginBottom: 10 }}>

        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>

          <div>

            <strong>{label}</strong>{" "}

            {s && (

              <span style={{ color: statusColor[s.status] || "var(--text-secondary)", fontSize: 13 }}>

                ({s.status}

                {s.restarts != null ? `, ${s.restarts} restart${s.restarts === 1 ? "" : "s"}` : ""})

              </span>

            )}

          </div>

          <div style={{ display: "flex", gap: 8 }}>

            <ViewOnlyGate canEdit={canEdit}>

              <fieldset disabled={frozen} style={{ border: "none", padding: 0, margin: 0, display: "flex", gap: 8, opacity: frozen ? 0.55 : 1 }}>

              <button onClick={() => runAction(proc, "start")} disabled={busyKey === `${proc}-start` || s?.status === "online"} title={frozen ? frozenTitle : undefined}>

                Start

              </button>

              <button onClick={() => runAction(proc, "restart")} disabled={busyKey === `${proc}-restart`} title={frozen ? frozenTitle : undefined}>

                Restart

              </button>

              <button onClick={() => runAction(proc, "stop")} disabled={busyKey === `${proc}-stop` || s?.status !== "online"} title={frozen ? frozenTitle : undefined}>

                Stop

              </button>

              </fieldset>

            </ViewOnlyGate>

          </div>

        </div>

      </div>

    );

  };



  // NOT CAPPED AT 500 ANY MORE (1.1036.0). It was, and the MongoDB table
  // inside it needs 664 - which is why that table's last column rendered
  // outside this card's border. It takes the width left beside This machine
  // and wraps under it when there is not enough for both.
  return (

    <div style={{ border: "1px solid var(--border-color)", borderRadius: 8, padding: 14, marginBottom: 20, flex: "1 1 600px" }}>

      <h4 style={{ marginTop: 0 }}>Server control</h4>

      <p style={{ fontSize: 13, opacity: 0.7, marginTop: -6 }}>

        Root-only. Stopping the backend breaks this dashboard's own API until it's restarted via SSH — there's no

        way to undo that from here.

      </p>

      {statusError && <p style={{ color: "var(--accent-red)", fontSize: 13 }}>{statusError}</p>}

      {frozen && (

        <p style={{ opacity: 0.7, fontSize: 13, marginBottom: 10 }}>

          Leave Demo mode to manage this — you're currently browsing in{" "}
          {modeLabel(adminMode(modeStatus))} mode.

        </p>

      )}

      <ProcessRow label="Backend" proc="backend" />

      <div style={{ border: "1px solid var(--border-color)", borderRadius: 8, padding: 12, marginBottom: 10 }}>

        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, flexWrap: "wrap" }}>

          <strong>Held update</strong>

          {heldInfo && (

            <span style={{ fontFamily: "monospace", fontSize: 12, opacity: 0.8 }}>

              running v{heldInfo.runningVersion}

              {heldInfo.filesVersion !== heldInfo.runningVersion && ` · files v${heldInfo.filesVersion}`}

            </span>

          )}

        </div>

        {heldError && <p style={{ color: "var(--accent-red)", fontSize: 13, marginBottom: 0 }}>{heldError}</p>}

        {!heldError && heldInfo && !heldInfo.held && (

          <div style={{ marginTop: 6, fontSize: 13, opacity: 0.75 }}>

            Nothing is waiting. A deploy only holds an update back while a competition is being played; when

            that happens it appears here.

          </div>

        )}

        {!heldError && heldInfo?.held && (

          <>

            <div style={{ marginTop: 6, fontSize: 13 }}>

              {heldInfo.held.unreadable ? (

                <span style={{ color: "var(--accent-red)" }}>{heldInfo.held.why}</span>

              ) : (

                <>

                  <strong>{heldInfo.held.versions.join(", ")}</strong> installed and proved, not yet live.

                  {heldInfo.held.why && <div style={{ marginTop: 4, opacity: 0.8 }}>{heldInfo.held.why}</div>}

                </>

              )}

            </div>

            {/* WHY THE BUTTON IS OFF, EVERY TIME IT IS OFF. A greyed-out
                button with no reason beside it is the thing an operator comes
                back to ask about. */}

            {heldInfo.blocker && (

              <div style={{ marginTop: 6, fontSize: 13, opacity: 0.85 }}>

                {/* A PAYMENT BLOCKER HAS NO COMPETITION TO NAME (1.693.0).
                    A credit top-up belongs to none, so the fallback below
                    would have said "Waiting for a competition" about somebody
                    sitting in 3-D Secure. */}

                {heldInfo.blocker.kind === "payment" ? (

                  <>Waiting — {heldInfo.blocker.why}.{" "}</>

                ) : (

                  <>

                    Waiting for{" "}

                    <strong>{heldInfo.blocker.title || "a competition"}</strong>

                    {heldInfo.blocker.kind === "locked"

                      ? " — it is locked in and about to start."

                      : heldInfo.blocker.kind === "locking"

                        ? " — it would lock in before an install could finish."

                        : ` — ${heldInfo.blocker.why}.`}{" "}

                  </>

                )}

                Publishing restarts the backend, which drops every open connection.

              </div>

            )}

            {/* ---- AND WHEN IT CLEARS (1.698.0) ----

                1.697.0's rule is strict by design: no update publishes while a
                competition is active or locked in. The question that leaves an
                operator with is WHEN, and nothing answered it - a card that
                says what is in the way and not when it clears is a card people
                route around. */}

            {heldInfo.window && heldInfo.window.kind !== "open" && (

              <div

                style={{

                  marginTop: 6,

                  fontSize: 13,

                  color: heldInfo.window.kind === "none" ? "var(--accent-red)" : undefined,

                  opacity: heldInfo.window.kind === "none" ? 1 : 0.85,

                }}

              >

                {heldInfo.window.kind === "at" ? (

                  <>

                    A window opens <strong>{new Date(heldInfo.window.at).toLocaleString()}</strong>

                    {heldInfo.window.until != null && (

                      <> and closes {new Date(heldInfo.window.until).toLocaleString()}, when the next one locks in</>

                    )}

                    .

                  </>

                ) : (

                  <>

                    {/* NO WINDOW IS THE ANSWER THAT MATTERS, and it is the one
                        the operator would otherwise discover hours later. */}

                    {heldInfo.window.kind === "none" ? <strong>No window: </strong> : null}

                    {heldInfo.window.why}.{heldInfo.window.advice ? ` ${heldInfo.window.advice}` : ""}

                  </>

                )}

              </div>

            )}

          </>

        )}

        {/* ---- FROM HERE ON, WHETHER OR NOT ANYTHING IS HELD (1.695.0) ----

            These three used to sit inside the branch above, so on an ordinary
            box - which is nearly every box, nearly all of the time - there was
            no button at all and nothing said there could be one. */}

        {heldInfo?.installing && !installTarget && (

          <div style={{ marginTop: 6, fontSize: 13, opacity: 0.85 }}>

            An install is already in progress on this box. Every change is refused until it finishes.

          </div>

        )}

        {installGaveUp && (

          <div style={{ marginTop: 6, fontSize: 13, color: "var(--accent-red)" }}>

            The backend has not come back with the new version after a minute. The frontend was published;

            check <code>pm2 list</code> and the backend log. Reload this page once it is answering.

          </div>

        )}

        {/* ---- WHAT IS WAITING, HOW LONG IT WILL TAKE, AND WHY NOT NOW (1.950.0) ----

            THE THREE QUESTIONS AN OPERATOR HAD TO GUESS AT. The route has
            answered all three since 1.698.0 - held.versions, the measured
            estimate, and `window` - and none of them reached the screen. The
            button was simply grey, and a grey button with no reason reads as a
            fault rather than as a wait.

            THE ESTIMATE IS THE SERVER'S PHRASE, not one built here. The same
            sentence goes in the daily notification, from the same function, so
            the panel and the reminder cannot disagree about the figure. */}

        {heldInfo?.held && !installTarget && (

          <div style={{ marginTop: 8, fontSize: 13, lineHeight: 1.5 }}>

            <div>

              <strong>Waiting to install:</strong> v{(heldInfo.held.versions || []).join(", v") || "unknown"}

              {/* THE SERVER'S PHRASE, NOT ONE BUILT HERE. This computed its own
                  hours until the 1.950.0 preview put the panel's wording beside
                  the notification's and they differed. One function answers it
                  now, so they cannot drift apart again. */}

              {heldInfo.heldForPhrase ? ` - built ${heldInfo.heldForPhrase}` : null}

            </div>

            <div style={{ opacity: 0.85 }}>

              Installing flips the frontend and restarts the backend: {heldInfo.estimatePhrase || "usually under a minute"}.

              {" "}The build, the test suite and the boot check have already run.

            </div>

            {!heldInfo.canPublish && heldInfo.blocker && (

              <div style={{ marginTop: 4, color: "var(--accent-amber, #d79b00)" }}>

                Not now: {heldInfo.blocker.why}

                {heldInfo.window?.advice ? ` ${heldInfo.window.advice}` : ""}

              </div>

            )}

          </div>

        )}

        <div style={{ marginTop: 10 }}>

          {/* ENABLED ONLY BY THE SERVER'S OWN canPublish, and `?.` because
              heldInfo is null until the first read answers - which must read
              as "not yet", not as "ready". */}

          <button

            onClick={publishHeld}

            disabled={!canEdit || publishing || !heldInfo?.canPublish}

            title={installButtonTitle()}

          >

            {publishing ? "Installing…" : "Install now"}

          </button>

          {installTarget && !installGaveUp && (

            <span style={{ marginLeft: 10, fontSize: 13, opacity: 0.8 }}>

              Waiting for v{installTarget} to answer, then reloading…

            </span>

          )}

        </div>

      </div>

      {status?.activityLog && (

        <div style={{ border: "1px solid var(--border-color)", borderRadius: 8, padding: 12, marginBottom: 10 }}>

          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, flexWrap: "wrap" }}>

            <strong>Activity log is being read from</strong>

            <span

              style={{

                fontFamily: "monospace",

                padding: "2px 8px",

                borderRadius: 4,

                border: `1px solid ${status.activityLog.servedFrom === "mongodb" ? "var(--accent-green)" : "var(--border-color)"}`,

                color: status.activityLog.servedFrom === "mongodb" ? "var(--accent-green)" : "inherit",

              }}

            >

              {status.activityLog.servedFrom === "mongodb" ? "MongoDB" : "its file"}

            </span>

          </div>

          <div style={{ marginTop: 6, fontSize: 13, opacity: 0.85 }}>{status.activityLog.why}</div>

          {status.activityLog.database.lastError && (

            <div style={{ marginTop: 6, fontSize: 12, fontFamily: "monospace", color: "var(--accent-red)" }}>

              {status.activityLog.database.lastError}

            </div>

          )}

        </div>

      )}

      {status?.mongo && (

        <div style={{ border: "1px solid var(--border-color)", borderRadius: 8, padding: 12, marginBottom: 10 }}>

          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, flexWrap: "wrap" }}>

            <strong>MongoDB</strong>

            <span

              style={{

                fontFamily: "monospace",

                padding: "2px 8px",

                borderRadius: 4,

                border: `1px solid ${MONGO_STATE_COLOUR[status.mongo.enabled ? status.mongo.connection : "off"] || "var(--border-color)"}`,

                color: MONGO_STATE_COLOUR[status.mongo.enabled ? status.mongo.connection : "off"] || "inherit",

              }}

            >

              {status.mongo.enabled ? status.mongo.connection : "not switched on"}

              {status.mongo.enabled && status.mongo.database ? ` · ${status.mongo.database}` : ""}

            </span>

          </div>

          <div style={{ marginTop: 6, fontSize: 13, opacity: 0.85 }}>{status.mongo.summary}</div>

          {status.mongo.lastError && (

            <div style={{ marginTop: 6, fontSize: 12, fontFamily: "monospace", color: "var(--accent-red)" }}>

              {status.mongo.lastError}

            </div>

          )}

          {/* THE HEADER, over columns that are the same widths as the rows
              below because both read MONGO_COLS. Without it every row had to
              label its own cells, repeating the same two words on every line -
              chrome that was half of why the panel read as noise. The words
              moved up here; the values stayed down there.

              AND THEY ARE NOT SPELLED OUT IN THIS COMMENT ON PURPOSE. An
              absence check in mongo-shadow-state.test.mjs watches for one of
              them appearing hardcoded in the markup, and a comment quoting the
              string a rule is counted by is this project's own recorded way of
              tripping it. */}
          <div
            style={{
              display: "flex",
              gap: 10,
              alignItems: "baseline",
              marginTop: 10,
              paddingBottom: 4,
              borderBottom: "1px solid var(--border-color)",
              fontSize: 11,
              textTransform: "uppercase",
              letterSpacing: "0.04em",
              opacity: 0.45,
            }}
          >
            <span style={{ minWidth: MONGO_COLS.mark, flexShrink: 0 }} />
            <span style={{ minWidth: MONGO_COLS.label, flexShrink: 0 }}>collection</span>
            <span style={{ minWidth: MONGO_COLS.count, flexShrink: 0 }}>documents</span>
            <span style={{ minWidth: MONGO_COLS.reads, flexShrink: 0 }}>reads</span>
            <span style={{ minWidth: MONGO_COLS.file, flexShrink: 0 }}>file</span>
            <span style={{ minWidth: MONGO_COLS.since, flexShrink: 0 }}>since</span>
            <span style={{ flex: "1 1 200px", minWidth: 160 }}>what is written to the database</span>
          </div>

          <div>

            {status.mongo.collections.map((c) => (

              <div key={c.collection} style={{ display: "flex", gap: 10, alignItems: "baseline", padding: "3px 0", fontSize: 13 }}>

                {/* A MARK WITH A SPACE IN IT IS A MARK THAT CAN WRAP. Reported
                    3 Oct 2026 with a screenshot: "[ OK" on one line and "]" on
                    the next, in green. nowrap and flexShrink: 0 are what stop
                    it, and mongo-shadow-state.test.mjs holds both. */}
                <span style={{ fontFamily: "monospace", whiteSpace: "nowrap", flexShrink: 0, minWidth: MONGO_COLS.mark, color: MONGO_ROW_COLOUR[c.state] || "inherit", opacity: c.state === "off" ? 0.6 : 1 }}>

                  {MONGO_ROW_MARK[c.state] || "[ ?? ]"}

                </span>

                <span style={{ minWidth: MONGO_COLS.label, flexShrink: 0, fontWeight: 600 }}>{c.label}</span>

                <span style={{ fontFamily: "monospace", whiteSpace: "nowrap", flexShrink: 0, minWidth: MONGO_COLS.count }}>

                  {c.count === null ? "\u2014" : `${c.count.toLocaleString()} docs`}

                </span>

                {/* WHERE THIS ROW'S READS COME FROM (1.663.0). The backend has
                    recorded this per collection since 1.656.0 and nothing read
                    it, which is how the line at the bottom of this panel came
                    to say the site reads from the files while sixteen
                    collections read from the database. `title` carries the
                    reason, because the reason is a sentence and the row is a
                    row. The word "reads:" went with the header (1.880.4). */}
                <span
                  title={c.readsWhy}
                  style={{
                    fontFamily: "monospace",
                    whiteSpace: "nowrap",
                    flexShrink: 0,
                    minWidth: MONGO_COLS.reads,
                    opacity: c.readsFrom === "database" ? 1 : 0.55,
                  }}
                >

                  {c.readsFrom === "database" ? "db" : "file"}

                </span>

                {/* WHAT THIS ROW'S FILE IS, AND THE WAY BACK FROM IT
                    (1.758.0, corrected 1.759.0; ALWAYS RENDERED 1.880.4).

                    IT SAID "snapshot" FOR EVERY FROZEN FILE. Two of the four
                    are a PREFIX - a faithful BEGINNING of the collection - and
                    on production the activity log is the only frozen file, so
                    the badge was wrong about the only row it appeared on there.
                    The rule is the fact, so the rule is what it shows.

                    AND `equal` NOW SHOWS AS "live" RATHER THAN NOTHING. A file
                    still rewritten on every change is the thing worth saying,
                    and rendering an empty cell for it both broke the alignment
                    of every row after it and hid the only case a reader has to
                    think about: that collection is written twice.

                    THE TITLE IS THE WAY BACK, per collection, because the
                    script, the switch and the cost of skipping the rebuild all
                    differ - lib/frozen-file-rules.js decides it in one place
                    and this renders it rather than paraphrasing. */}
                <span
                  title={
                    c.fileRule === "equal"
                      ? "This file is STILL THE RECORD and is rewritten on every change, so this collection is written twice - to the database and to disk - and the two must hold the same documents."
                      : c.fileWayBack || "This file has STOPPED being written."
                  }
                  style={{
                    fontFamily: "monospace",
                    whiteSpace: "nowrap",
                    flexShrink: 0,
                    minWidth: MONGO_COLS.file,
                    opacity: c.fileRule === "equal" ? 1 : 0.7,
                  }}
                >

                  {c.fileRule === "equal" ? "live" : c.fileRule || "\u2014"}

                </span>

                {/* THE VERSION, IN A COLUMN OF ITS OWN (1.1036.0). It used to be
                    the tail of the sentence beside it, so the one fact a reader
                    scans down the panel for sat at a different x on every row -
                    and it was the first thing lost the moment that sentence was
                    clipped. A column does not move. */}
                <span
                  style={{
                    fontFamily: "monospace",
                    whiteSpace: "nowrap",
                    flexShrink: 0,
                    minWidth: MONGO_COLS.since,
                    opacity: 0.75,
                  }}
                >

                  {`v${c.since}`}

                </span>

                {/* THE ONE COLUMN ALLOWED TO TAKE THE SLACK, and the one that
                    was given none until 1.880.4. `flex` plus a minWidth, so it
                    uses what is left instead of collapsing to its longest
                    word.

                    THE FLOOR WAS NEVER THE PROBLEM (1.1036.0). 1.880.4 gave this
                    column a floor because without one it collapsed to its longest
                    word; the floor is right and stays. What was wrong sat two
                    levels up - the panel was capped at 500px while this row needs
                    664, so the column was rendered 226px OUTSIDE the card's own
                    border. Reported the same way both times, as the panel
                    "looking messy".

                    AND CLAMPED TO TWO LINES, now that it fits. The longest of
                    these sentences is 152 characters, which made one row ten
                    lines tall beside a neighbour of one. The whole sentence is on
                    the element, so the clamp costs nothing a hover does not give
                    back - the bargain `reads` and `file` already strike one
                    column to the left. */}
                <span style={{ flex: "1 1 200px", minWidth: 160, opacity: 0.8 }}>

                  <span title={mongoRowSentence(c)} style={MONGO_CLAMP}>

                    {mongoRowSentence(c)}

                  </span>

                </span>

              </div>

            ))}

          </div>

          <div style={{ fontSize: 12, opacity: 0.7, marginTop: 10, lineHeight: 1.7 }}>

            {/* AN EMPTY LIST IS ITS OWN SENTENCE (1.593.0). Rendered the
                old way, the last collection moving turned this into
                "Still file-only: ." - a label with nothing after it - and
                left the line below it asserting something that had just
                stopped being true. Both halves change together, because
                the second one is only true while the first has entries. */}
            {status.mongo.notYet.length > 0 ? (
              <>
                <div><strong>Still file-only:</strong> {status.mongo.notYet.map((n) => n.label).join(", ")}.</div>
                <div>A database dump is not yet a backup of the site — see <code>scripts/mongo-backup.mjs</code>, which lists what it leaves out.</div>
              </>
            ) : (
              <>
                {/* THIS PANEL CANNOT SAY WHETHER A DUMP IS COMPLETE (1.596.0).
                    1.593.0 claimed it was, the moment the eighth collection was
                    registered. 1.594.0 replaced that with a list derived from
                    the SHAPE - and on a real box that named four FULL
                    collections as incomplete while saying nothing about the one
                    holding 0 of 67 accounts.

                    The readout counts what MongoDB holds and never reads the
                    files. Comparing the two is the verifier's whole job. So
                    this says which scripts answer the question rather than
                    answering it badly - the summary above already names any
                    collection holding nothing, which IS knowable from counts. */}
                {/* THE COUNT COMES FROM THE DATA (1.614.1). This said "all eight",
                    which was true when it was written and would be wrong the next
                    time a collection is registered - and it is operator-facing,
                    which is worse than a stale comment. It went unreachable in
                    1.614.1 when entry-credits.json was registered as still
                    file-only, and it becomes live again the day that store moves,
                    by which time "eight" would have been a lie on screen. */}
                <div>
                  <strong>Every collection is shadowed</strong> — all{" "}
                  {status.mongo.collections.length} are receiving writes.
                </div>
                <div>
                  Whether a dump is <em>complete</em> is a separate question, and this panel cannot
                  answer it: a shadow holds only what was written since it was switched on, and
                  nothing here reads the files to compare. Run{" "}
                  <code>scripts/backfill-mongo.mjs --dry-run</code> to see both sides per
                  collection, and <code>scripts/verify-mongo-shadow.mjs</code> for the verdict.
                </div>
                {/* THIS SENTENCE WAS WRONG ON SCREEN FOR NINE RELEASES (1.663.0).
                    It asserted, hardcoded, that every collection's reads still
                    came from the files and that nothing about that had changed.
                    It stopped being true in 1.654.0 when siteNotifications was
                    cut over, and by 1.662.0 sixteen of the thirty-one read from
                    the database while this still said none of them did.

                    THE OLD WORDING IS DESCRIBED RATHER THAN QUOTED, and that is
                    deliberate: a test asserts the claim is gone from this file,
                    and the first draft of this comment reproduced the sentence
                    verbatim and satisfied the test with its own quotation. The
                    eleventh time this project has met a comment containing the
                    string it is counted by, and the answer is the same every
                    time - change what is written, never the thing being
                    counted.

                    THE COMMENT ONE PARAGRAPH ABOVE PREDICTED IT. 1.614.1 took a
                    hardcoded "all eight" out of this same panel because an
                    operator-facing claim going stale "is worse than a stale
                    comment" - and the next div along was a hardcoded claim that
                    went stale. So this one comes from the data, and the
                    sentence itself is built in lib/mongo-shadow-state.js where
                    it can be tested. */}
                <div>{status.mongo.readsSummary}</div>
                <div>
                  A dump is a backup rather than something this site could run from: every file is
                  still written first and on every change. Since the accounts moved, the archive
                  carries a password hash for every user: see <code>scripts/mongo-backup.mjs</code>,
                  which says so on every run.
                </div>
              </>
            )}

          </div>

        </div>

      )}

      {(inventory || inventoryError) && (

        <div style={{ border: "1px solid var(--border-color)", borderRadius: 8, padding: 12, marginBottom: 10 }}>

          <strong>What this server has</strong>

          <div style={{ fontSize: 12, opacity: 0.7, marginTop: 2, marginBottom: 8 }}>

            The same check the installer runs, as it stands now.

          </div>

          {inventoryError && <div style={{ color: "var(--accent-red)", fontSize: 13 }}>{inventoryError}</div>}

          {(inventory || []).map((c) => {

            const mark = { ok: "[ OK ]", missing: "[MISS]", stopped: "[STOP]", absent: "[ -- ]" }[c.state] || "[ ?? ]";

            const colour = { ok: "var(--accent-green)", missing: "var(--accent-red)", stopped: "var(--accent-red)" }[c.state] || "inherit";

            return (

              <div key={c.name} style={{ display: "flex", gap: 10, alignItems: "baseline", padding: "3px 0", fontSize: 13 }}>

                <span style={{ fontFamily: "monospace", color: colour, opacity: c.state === "absent" ? 0.6 : 1 }}>{mark}</span>

                <span style={{ minWidth: 90, fontWeight: 600 }}>{c.name}</span>

                <span style={{ opacity: 0.8 }}>{c.detail}</span>

              </div>

            );

          })}

          <div style={{ fontSize: 11, opacity: 0.6, marginTop: 8, lineHeight: 1.7 }}>

            <div><code>[ OK ]</code> present and usable. <code>[MISS]</code> needed and not usable.</div>

            <div><code>[STOP]</code> installed but NOT running &mdash; a service, not a binary.</div>

            <div><code>[ -- ]</code> optional and absent &mdash; the site runs without it.</div>

          </div>

        </div>

      )}

      {confirmModal}

    </div>

  );

}



 // Root-only, and quite different from the demo's own Generate, which

 // only ever touches generated demo records; these clear an entire category

// outright, real data included. Deliberately four separate buttons

// rather than one "wipe everything" action, so root chooses exactly

// which category to reset — each still gets its own explicit modal

// warning regardless, since "which one did I just click" matters as much

// as "am I sure" once several destructive buttons sit next to each other.

// Mode-scoped, same as everywhere else — each button only ever affects

// whichever mode the viewing admin is currently in, and says so clearly

// on the button itself, not just in the confirmation modal.

// ---- THE DATABASE BACKUP DIRECTORY (1.816.0) ----
//
// WHAT THIS IS FOR, AND IT IS NOT "A LISTING". The CLI has printed one since
// 1.813.1. The fact an operator cannot get at today is the one a listing does
// not state: a directory whose nightly dump stopped three weeks ago looks
// IDENTICAL to a healthy one, because fourteen archives are present either
// way. So the first thing on this card is the age of the newest archive and a
// verdict about it, and the list of files is underneath.
//
// EVERY JUDGEMENT ARRIVES FROM THE ROUTE. Nothing here decides what counts as
// stale, which archives are ours, or whether a mode is wrong - lib/mongo-backup.js
// does, under test, with its own clock. This renders what it is told.
function DatabaseBackupPanel({ token, canEdit }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [rehearsal, setRehearsal] = useState(null);
  const { confirm, confirmModal } = useConfirm();

  const refresh = () => {
    fetch(`${API_BASE}/api/admin/server/backups`, { headers: { "x-auth-token": token } })
      .then(async (res) => {
        const body = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(body.error || `Could not read the backup directory (${res.status}).`);
        return body;
      })
      .then((body) => { setData(body); setError(""); })
      .catch((err) => setError(err.message));
  };
  useEffect(refresh, []);

  const rehearse = async (name) => {
    if (!(await confirm(
      `Restore ${name} into a scratch database and count what lands?\n\nThis does not touch the site's own data.`,
    ))) return;
    setBusy(true);
    setRehearsal(null);
    try {
      const res = await fetch(`${API_BASE}/api/admin/server/backups/rehearse`, {
        method: "POST",
        headers: { "x-auth-token": token, "Content-Type": "application/json" },
        body: JSON.stringify({ archive: name }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.error || `The rehearsal could not be started (${res.status}).`);
      setRehearsal(body);
      if (body.ok && body.summary) {
        showToast(`Rehearsed ${name} - ${body.summary.documents} documents across ${body.summary.collections} collections.`);
      } else if (body.ok) {
        // OK WITHOUT A SUMMARY IS NOT A SUCCESS WORTH A GREEN TOAST. The
        // script reached the end without the line that carries the figures,
        // and the output below is the only honest account of it.
        showToast("The rehearsal finished but reported no document count - read the output below.", "error");
      } else {
        showToast(body.error || "The rehearsal did not finish.", "error");
      }
    } catch (err) {
      showToast(err.message || "Could not reach backend.", "error");
    }
    setBusy(false);
  };

  const STATE_COLOUR = { fresh: "var(--accent-green)", stale: "var(--accent-red)", none: "var(--accent-red)", unknown: "var(--accent-yellow)" };
  const age = (h) => {
    if (h === null || h === undefined) return "age unknown";
    if (h < 1) return `${Math.round(h * 60)} min ago`;
    if (h < 48) return `${h.toFixed(1)} hours ago`;
    return `${Math.floor(h / 24)} days ago`;
  };
  const size = (b) => (b === null || b === undefined ? "size unknown" : b < 1024 * 1024 ? `${(b / 1024).toFixed(0)} KB` : `${(b / 1048576).toFixed(1)} MB`);

  return (
    <div style={{ border: "1px solid var(--border-color)", borderRadius: 8, padding: 14, marginBottom: 20, maxWidth: 700 }}>
      <h4 style={{ marginTop: 0 }}>Database backups</h4>
      <p style={{ fontSize: 13, opacity: 0.7, marginTop: -6, marginBottom: 14 }}>
        The nightly dump and what it has written. A directory holding fourteen archives looks the
        same whether the last one was taken this morning or a month ago, so the age of the newest is
        the first thing shown. Rehearsing an archive restores it into a separate scratch database
        and counts what lands - it never touches the data this site is serving.
      </p>

      {error && <p style={{ color: "var(--accent-red)", fontSize: 13 }}>{error}</p>}
      {!data && !error && <p style={{ fontSize: 13, opacity: 0.6 }}>Reading the backup directory...</p>}

      {data && !data.ok && (
        <p style={{ color: "var(--accent-red)", fontSize: 13 }}>
          <strong>{data.dir}</strong> - {data.refusal}
        </p>
      )}

      {data && data.ok && data.inventory && (
        <>
          <p style={{
            fontSize: 14, margin: "0 0 12px", padding: "8px 10px", borderRadius: 6,
            border: `1px solid ${STATE_COLOUR[data.inventory.health.state] || "var(--border-color)"}`,
          }}>
            <strong style={{ color: STATE_COLOUR[data.inventory.health.state] || "inherit" }}>
              {data.inventory.health.state === "fresh" ? "Up to date" : data.inventory.health.state === "stale" ? "Out of date" : data.inventory.health.state === "none" ? "No backups" : "Unknown"}
            </strong>
            {" - "}{data.inventory.health.why}
          </p>

          <p style={{ fontSize: 13, opacity: 0.75, margin: "0 0 4px" }}>
            <code>{data.dir}</code>, mode {data.dirMode || "unknown"}
            {data.dirModeOk === false && <span style={{ color: "var(--accent-yellow)" }}> - expected 0700</span>}
          </p>
          <p style={{ fontSize: 13, opacity: 0.75, margin: "0 0 12px" }}>
            {data.cron.installed
              ? <>Scheduled <code>{data.cron.schedule}</code> as {data.cron.user}{data.cron.keep !== null ? `, keeping ${data.cron.keep}` : ""}{data.cron.logPath ? <> - log <code>{data.cron.logPath}</code></> : null}</>
              : <span style={{ color: "var(--accent-yellow)" }}>Nothing is scheduled. {data.cron.why}</span>}
          </p>

          {data.inventory.mine.length > 0 && (
            <table style={{ width: "100%", fontSize: 13, borderCollapse: "collapse" }}>
              <tbody>
                {data.inventory.mine.map((a, i) => (
                  <tr key={a.name} style={{ borderTop: "1px solid var(--border-color)" }}>
                    <td style={{ padding: "6px 4px", fontFamily: "monospace", fontSize: 12 }}>
                      {a.name}
                      {i === 0 && <span style={{ opacity: 0.6 }}> (newest)</span>}
                    </td>
                    <td style={{ padding: "6px 4px", whiteSpace: "nowrap", opacity: 0.8 }}>{age(a.ageHours)}</td>
                    <td style={{ padding: "6px 4px", whiteSpace: "nowrap", opacity: 0.8 }}>{size(a.bytes)}</td>
                    <td style={{ padding: "6px 4px", whiteSpace: "nowrap" }}>
                      {a.modeOk === false && <span style={{ color: "var(--accent-yellow)" }} title="archives should be 0600">{a.mode}</span>}
                      {a.verdict && !a.verdict.ok && <span style={{ color: "var(--accent-red)" }} title={a.verdict.reason}> suspect</span>}
                    </td>
                    <td style={{ padding: "6px 4px", textAlign: "right" }}>
                      <ViewOnlyGate canEdit={canEdit}>
                        <button disabled={busy} onClick={() => rehearse(a.name)} style={{ fontSize: 12 }}>
                          {busy ? "..." : "Rehearse"}
                        </button>
                      </ViewOnlyGate>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {/* THE TWO KINDS OF FILE THAT ARE NOT OURS, counted rather than
              hidden: --keep does not count them and an operator wondering why
              the directory holds twenty files deserves the reason. */}
          {(data.inventory.others.length > 0 || data.inventory.strangers.length > 0) && (
            <p style={{ fontSize: 12, opacity: 0.6, marginTop: 10 }}>
              Also here, and left alone by the prune:
              {data.inventory.others.length > 0 && ` ${data.inventory.others.length} archive(s) of another database`}
              {data.inventory.others.length > 0 && data.inventory.strangers.length > 0 && ","}
              {data.inventory.strangers.length > 0 && ` ${data.inventory.strangers.length} file(s) this project did not write`}
              .
            </p>
          )}

          {rehearsal && (
            <div style={{ marginTop: 14, padding: 10, borderRadius: 6, border: `1px solid ${rehearsal.ok && rehearsal.summary ? "var(--accent-green)" : "var(--accent-red)"}` }}>
              <p style={{ margin: "0 0 6px", fontSize: 13 }}>
                <strong>{rehearsal.archive}</strong> into <code>{rehearsal.into}</code>, {rehearsal.seconds}s
                {rehearsal.summary && ` - ${rehearsal.summary.documents} documents across ${rehearsal.summary.collections} collections`}
                {rehearsal.error && <span style={{ color: "var(--accent-red)" }}> - {rehearsal.error}</span>}
              </p>
              <pre style={{ margin: 0, fontSize: 11, maxHeight: 220, overflow: "auto", opacity: 0.8, whiteSpace: "pre-wrap" }}>
                {rehearsal.output.join("\n")}
              </pre>
            </div>
          )}
        </>
      )}

      <p style={{ fontSize: 12, opacity: 0.55, marginTop: 14, marginBottom: 0 }}>
        Restoring over this site's own database is not on this page. It is a one-way action that
        needs the site stopped, and it stays a command an operator runs deliberately on the box.
      </p>

      {/* ---- THE FILES THE DUMP DOES NOT HOLD (1.883.0) ----
          The same three questions as above, because each of them was wrong
          somewhere on 19 Oct 2026: is a schedule installed, has it ever written
          anything, and how old is the newest. Production carried a file backup
          that had never once run, from 16 August, and nothing said so.
          REPORTING ONLY, and there is no control here for taking a copy away -
          a backup leaves the box through a shell, never through this page.
          The words that would describe such a control are deliberately not
          written in this comment: a rule counts them in this very section, and
          this project has four times had a source check find its own evidence
          in the prose beside it. */}
      {data && data.files && (
        <div style={{ borderTop: "1px solid var(--border-color)", marginTop: 16, paddingTop: 14 }}>
          <h4 style={{ marginTop: 0, marginBottom: 4 }}>File backups</h4>
          <p style={{ fontSize: 13, opacity: 0.7, marginTop: 0, marginBottom: 12 }}>
            <code>backend/data</code> and <code>backend/assets</code> &mdash; the files that are
            still the record, and the sealed images. The encryption keys are in neither this nor the
            database archive, deliberately, so that no single file holds both the locked data and the
            key that opens it. <strong>Copy those off by hand.</strong>
          </p>

          {!data.files.ok && (
            <p style={{ color: "var(--accent-yellow)", fontSize: 13 }}>
              <strong>{data.files.dir}</strong> &mdash; {data.files.refusal}
            </p>
          )}

          {data.files.ok && data.files.inventory && (
            <>
              <p style={{
                fontSize: 14, margin: "0 0 12px", padding: "8px 10px", borderRadius: 6,
                border: `1px solid ${data.files.inventory.stale ? "var(--accent-yellow)" : "var(--border-color)"}`,
              }}>
                <strong style={{ color: data.files.inventory.stale ? "var(--accent-yellow)" : "inherit" }}>
                  {data.files.inventory.neverWritten
                    ? "Never run"
                    : data.files.inventory.stale ? "Out of date" : "Up to date"}
                </strong>
                {" - "}
                {data.files.inventory.neverWritten
                  ? "no archive has ever been written here."
                  : <>newest {age(data.files.inventory.newest.ageHours)}, {size(data.files.inventory.newest.bytes)}, {data.files.inventory.count} kept.</>}
              </p>

              <p style={{ fontSize: 13, opacity: 0.75, margin: "0 0 4px" }}>
                <code>{data.files.dir}</code>, mode {data.files.dirMode || "unknown"}
                {data.files.dirModeOk === false && <span style={{ color: "var(--accent-yellow)" }}> - expected 0700</span>}
              </p>
              <p style={{ fontSize: 13, opacity: 0.75, margin: "0 0 4px" }}>
                {data.files.cron.installed
                  ? <>Scheduled <code>{data.files.cron.schedule}</code> as {data.files.cron.user}{data.files.cron.keep !== null ? `, keeping ${data.files.cron.keep}` : ""}{data.files.cron.logPath ? <> - log <code>{data.files.cron.logPath}</code></> : null}</>
                  : <span style={{ color: "var(--accent-yellow)" }}>Nothing is scheduled. {data.files.cron.why}</span>}
              </p>

              {data.files.inventory.newest && data.files.inventory.newest.modeOk === false && (
                <p style={{ fontSize: 13, color: "var(--accent-yellow)", margin: "0 0 4px" }}>
                  The newest archive is not 0600. An archive of the whole data directory that any
                  local account can read is not the file this script wrote.
                </p>
              )}

              {data.files.inventory.strangers.length > 0 && (
                <p style={{ fontSize: 12, opacity: 0.6, margin: "8px 0 0" }}>
                  {data.files.inventory.strangers.length} file(s) here were written by something
                  else. A prune never touches them, so they do not expire and are not counted above.
                </p>
              )}
            </>
          )}
        </div>
      )}

      {confirmModal}
    </div>
  );
}

function DeleteAllDataPanel({ token, modeStatus, canEdit }) {

  const [busy, setBusy] = useState(null);

  const [counts, setCounts] = useState(null); // { users, adminUsers, competitions, stats }

  const { confirm, confirmModal } = useConfirm();

  const currentModeLabel = modeLabel(adminMode(modeStatus));
  // THE SERVER REFUSES THIS IN LIVE MODE (1.398.1). The button is
  // disabled to match, so nobody types a confirmation for an action that
  // was never going to run.
  const liveMode = adminMode(modeStatus) === "live";



  const refreshCounts = () => {

    wsRequest("admin.deleteAll.counts", {}, token)

      .then(setCounts)

      .catch(() => {});

  };



  useEffect(refreshCounts, [adminMode(modeStatus)]);



  // Real-time refresh — a user signing up, an admin being added elsewhere,

  // a competition being created, or a purchase/guess adding a stat record

  // all happen independently of this browser tab.

  useEffect(() => onBroadcast((message) => {

    if (["users-changed", "competitions-changed", "purchase"].includes(message.type)) refreshCounts();

  }), []);



  const run = async (action, label, confirmMessage, typeToConfirm) => {

    if (!(await confirm(confirmMessage, { typeToConfirm }))) return;

    setBusy(action);

    try {

      const data = await wsRequest(action, {}, token);

      showToast(`Deleted ${data.deleted} ${label}.`);

      refreshCounts();

    } catch (err) {

      showToast(err.message || "Could not reach backend.", "error");

    }

    setBusy(null);

  };



  const buttonStyle = { marginRight: 10, marginBottom: 10 };



  return (

    <div style={{ border: "1px solid var(--accent-red)", borderRadius: 8, padding: 14, marginBottom: 20, maxWidth: 500 }}>

      <h4 style={{ marginTop: 0, color: "var(--accent-red)" }}>Delete all data</h4>

      <p style={{ fontSize: 13, opacity: 0.7, marginTop: -6, marginBottom: 14 }}>

        Root-only. Each button below wipes an entire category outright — real accounts and competitions included,

        not just generated mock/demo ones. Only ever affects <strong>{currentModeLabel}</strong> data, matching the mode

        shown in the banner above — switch modes to reset the other one instead. None of this can be undone.
        {" "}Live competitions are the exception: they are never deleted, in bulk or one at a time once they have ended.

      </p>

      <ViewOnlyGate canEdit={canEdit} showNotice>

        <button

          style={buttonStyle}

          disabled={!!busy || !counts?.users}

          title={counts?.users ? undefined : `No ${currentModeLabel} user accounts to delete`}

          onClick={() =>

            run(

              "admin.deleteAll.users",

              `${currentModeLabel} users`,

              `Delete every ${currentModeLabel} regular user account on the site? This cannot be undone.`,

              `delete ${currentModeLabel.toLowerCase()} users`

            )

          }

        >

          {busy === "admin.deleteAll.users" ? "Deleting…" : `Delete All ${currentModeLabel} Users (${counts?.users ?? 0})`}

        </button>

        <button

          style={buttonStyle}

          disabled={!!busy || !counts?.adminUsers}

          title={counts?.adminUsers ? undefined : `No ${currentModeLabel} admin accounts to delete`}

          onClick={() =>

            run(

              "admin.deleteAll.adminUsers",

              `${currentModeLabel} admin users`,

              `Delete every ${currentModeLabel} admin account on the site (root is never affected)? This cannot be undone.`,

              `delete ${currentModeLabel.toLowerCase()} admin users`

            )

          }

        >

          {busy === "admin.deleteAll.adminUsers" ? "Deleting…" : `Delete All ${currentModeLabel} Admin Users (${counts?.adminUsers ?? 0})`}

        </button>

        <button

          style={buttonStyle}

          disabled={!!busy || !counts?.competitions || liveMode}

          title={
            liveMode
              ? "Live competitions are never deleted — they are the record of what was run"
              : counts?.competitions
              ? undefined
              : `No ${currentModeLabel} competitions to delete`
          }

          onClick={() =>

            run(

              "admin.deleteAll.competitions",

              `${currentModeLabel} competitions`,

              `Delete every ${currentModeLabel} competition on the site, including any currently active, and their replays? This cannot be undone.`,

              `delete ${currentModeLabel.toLowerCase()} competitions`

            )

          }

        >

          {busy === "admin.deleteAll.competitions" ? "Deleting…" : `Delete All ${currentModeLabel} Competitions (${counts?.competitions ?? 0})`}

        </button>

        <button

          style={buttonStyle}

          disabled={!!busy || !counts?.stats}

          title={counts?.stats ? undefined : `No ${currentModeLabel} stat records to delete`}

          onClick={() =>

            run(

              "admin.deleteAll.stats",

              `${currentModeLabel} stat records`,

              `Delete all ${currentModeLabel} guess counts, spending totals, guess history, and payment records? This cannot be undone.`,

              `delete ${currentModeLabel.toLowerCase()} stats`

            )

          }

        >

          {busy === "admin.deleteAll.stats" ? "Deleting…" : `Delete All ${currentModeLabel} Stats (${counts?.stats ?? 0})`}

        </button>

      </ViewOnlyGate>

      {confirmModal}

    </div>

  );

}



function SiteSettingsPanel({ authHeaders, token, canEdit, modeStatus }) {
  // The win-notification address. Its own fetch rather than part of the
  // settings load: it lives on the partner record, and the action that
  // serves the rest of that record returns the API keys with it.
  const [winEmail, setWinEmail] = useState("");
  const [winBusy, setWinBusy] = useState(false);

  // THE WINNERS PAGE ON THIS SITE — a SITE setting, not an API one.
  //
  // The API's winners fields govern what a PARTNER may consume; this
  // governs whether this site's own /winners page and nav link exist.
  // Added 18 Sep 2026, after the page was wrongly following the API
  // toggles: "The API only handles 3rd party integrations, not how
  // things are displayed on the site."
  const [winnersPage, setWinnersPage] = useState(true);
  const [winnersPageBusy, setWinnersPageBusy] = useState(false);

  // LIST TEST COMPETITIONS WHILE IN LIVE MODE (1.437.0) — a Sitewide
  // setting, off by default. Read from admin.siteSettings.private, since it
  // is about what admins see; null until read, so the box never shows a
  // guess as the stored value.
  const [listTestInLive, setListTestInLive] = useState(null);
  const [listTestBusy, setListTestBusy] = useState(false);
  useEffect(() => {
    let cancelled = false;
    wsRequest("admin.siteSettings.private", {}, token)
      .then((data) => {
        if (!cancelled) setListTestInLive(data?.listTestCompetitionsInLive === true);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [token]);
  const saveListTestInLive = async (next) => {
    setListTestInLive(next);
    setListTestBusy(true);
    try {
      await wsRequest("admin.siteSettings.update", { listTestCompetitionsInLive: next }, token);
      showToast(
        next ? "Test competitions will be listed in Live mode." : "Live mode lists Live competitions only.",
        "success"
      );
    } catch (err) {
      setListTestInLive(!next);
      showToast(err?.message || "Could not save that.", "error");
    } finally {
      setListTestBusy(false);
    }
  };
  const saveWinnersPage = async (next) => {
    setWinnersPage(next);
    setWinnersPageBusy(true);
    try {
      await wsRequest("admin.siteSettings.update", { winnersPageEnabled: next }, token);
      showToast(next ? "Winners page enabled." : "Winners page hidden.", "success");
    } catch (err) {
      setWinnersPage(!next);
      showToast(err?.message || "Could not save that.", "error");
    } finally {
      setWinnersPageBusy(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    wsRequest("admin.partner.getNotificationEmail", {}, token)
      .then((data) => {
        if (!cancelled) setWinEmail(data?.notificationEmail || "");
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [token]);

  const saveWinEmail = async () => {
    setWinBusy(true);
    try {
      const data = await wsRequest(
        "admin.partner.setNotificationEmail",
        { notificationEmail: winEmail },
        token
      );
      setWinEmail(data?.notificationEmail || "");
      showToast(
        data?.notificationEmail ? "Notification email saved." : "Notification email cleared.",
        "success"
      );
    } catch (err) {
      showToast(err.message || "Could not reach backend.", "error");
    }
    setWinBusy(false);
  };


  const [settings, setSettings] = useState(null);

  const [siteName, setSiteName] = useState("");

  const [siteUrl, setSiteUrl] = useState("");

  const [currencyCode, setCurrencyCode] = useState("");

  const [file, setFile] = useState(null);

  const [busy, setBusy] = useState(false);

  const [listDisplayMode, setListDisplayMode] = useState("pagination");

  const [listPageSize, setListPageSize] = useState(25);



  const refresh = () => {

    wsRequest("siteSettings.get")

      .then((data) => {

        setSettings(data);

        setSiteName(data.siteName);

        setSiteUrl(data.siteUrl);

        setCurrencyCode(data.currencyCode);
        // DEFAULTS TO ON when a site's settings predate this — the
        // stored default is true, and an older file simply has no
        // value rather than a false one.
        setWinnersPage(data.winnersPageEnabled !== false);

        // The SITE's timezone, so every date in the admin reads in the
        // announced zone rather than this browser's. One call settles the
        // whole panel — see siteTime.js.
        setSiteTimeZone(data.siteTimeZone);
        setSiteTimeZoneValue(data.siteTimeZone || "Europe/London");

        setAvailableTimeZones(data.availableTimeZones || []);

        setListDisplayMode(data.listDisplayMode);

        setListPageSize(data.listPageSize);

      })

      .catch(() => {});

  };



  useEffect(refresh, []);



  const saveName = async (e) => {

    e.preventDefault();

    setBusy(true);

    try {

      await wsRequest("admin.siteSettings.update", { siteName }, token);

      showToast("Saved.");

      refresh();

    } catch (err) {

      showToast(err.message || "Could not reach backend.", "error");

    }

    setBusy(false);

  };



  const saveUrl = async (e) => {

    e.preventDefault();

    setBusy(true);

    try {

      await wsRequest("admin.siteSettings.update", { siteUrl }, token);

      showToast("Saved.");

      refresh();

    } catch (err) {

      showToast(err.message || "Could not reach backend.", "error");

    }

    setBusy(false);

  };



  // THE SITE'S TIMEZONE, saved on change like the currency.
  //
  // Every date the site shows — admin panels, buyer pages and the
  // server's own messages — renders in this zone rather than the
  // reader's. A competition is announced at a time, and that time has to
  // mean the same thing to everyone looking at it.
  const [siteTimeZoneValue, setSiteTimeZoneValue] = useState("Europe/London");
  const [availableTimeZones, setAvailableTimeZones] = useState([]);

  const saveTimeZone = async (tz) => {
    setSiteTimeZoneValue(tz);
    setBusy(true);
    try {
      await wsRequest("admin.siteSettings.update", { siteTimeZone: tz }, token);
      setSiteTimeZone(tz); // takes effect immediately, no reload
      showToast(`Times now shown in ${tz}.`);
      refresh();
    } catch (err) {
      showToast(err.message || "Could not reach backend.", "error");
    }
    setBusy(false);
  };

  const saveCurrency = async (newCode) => {

    setCurrencyCode(newCode);

    setBusy(true);

    try {

      await wsRequest("admin.siteSettings.update", { currencyCode: newCode }, token);

      showToast("Saved.");

      refresh();

    } catch (err) {

      showToast(err.message || "Could not reach backend.", "error");

    }

    setBusy(false);

  };



  const saveListDisplayDefaults = async (next) => {

    const nextMode = next.mode ?? listDisplayMode;

    const nextPageSize = next.pageSize ?? listPageSize;

    setListDisplayMode(nextMode);

    setListPageSize(nextPageSize);

    setBusy(true);

    try {

      await wsRequest("admin.siteSettings.update", { listDisplayMode: nextMode, listPageSize: nextPageSize }, token);

      showToast("Saved.");

      refresh();

    } catch (err) {

      showToast(err.message || "Could not reach backend.", "error");

    }

    setBusy(false);

  };



  const uploadLogo = async (e) => {

    e.preventDefault();

    if (!file) return;

    setBusy(true);

    try {

      const body = new FormData();

      body.append("image", file);

      const res = await fetch(`${API_BASE}/api/admin/site-logo`, {

        method: "POST",

        headers: { "x-auth-token": token },

        body,

      });

      const data = await res.json();

      if (!res.ok) {

        if (res.status === 428) triggerMandatoryTotpRequired();

        if (res.status === 401) triggerSessionInvalid();

        showToast(data.error, "error");

        setBusy(false);

        return;

      }

      setFile(null);

      showToast("Saved.");

      refresh();

    } catch {

      showToast("Could not reach backend.", "error");

    }

    setBusy(false);

  };



  const removeLogo = async () => {

    setBusy(true);

    try {

      await wsRequest("admin.siteLogo.delete", {}, token);

      showToast("Removed.");

    } catch (err) {

      showToast(err.message || "Could not reach backend.", "error");

    }

    refresh();

    setBusy(false);

  };



  // No test/demo variant of any of this exists at all — always the

  // one real site's own configuration, same reasoning as the

  // backend's own assertNotFrozen calls for every action this panel

  // triggers.

  // DEMO ONLY. Site settings are settings: freezing them in Test mode
  // meant switching to Live to save a form and switching back to carry on
  // testing. The server agrees — see assertSettingsEditable.
  const frozen = adminMode(modeStatus) === "demo";

  const frozenTitle = "Leave Demo mode to manage this";



  const inputStyle = {

    padding: 8,

    background: "var(--bg-input)",

    border: "1px solid var(--border-color-light)",

    borderRadius: 6,

    color: "var(--text-primary)",

  };



  return (

    <div style={{ border: "1px solid var(--border-color)", borderRadius: 8, padding: 14, marginBottom: 20, maxWidth: 500 }}>

      <h4 style={{ marginTop: 0 }}>Site branding</h4>



      {settings === null ? (

        <p>Loading…</p>

      ) : (

        <ViewOnlyGate canEdit={canEdit} showNotice>

          {frozen && (

            <p style={{ opacity: 0.7, fontSize: 13, marginBottom: 10 }}>

              Leave Demo mode to manage this — you're currently browsing in
              {modeLabel(adminMode(modeStatus))} mode.

            </p>

          )}

          <fieldset disabled={frozen} style={{ border: "none", padding: 0, margin: 0, opacity: frozen ? 0.55 : 1 }}>

          <form onSubmit={saveName} style={{ marginBottom: 20 }}>

            <label style={{ fontSize: 13 }}>

              Site name

              <div style={{ display: "flex", gap: 8, marginTop: 4 }}>

                <input

                  required

                  value={siteName}

                  onChange={(e) => setSiteName(e.target.value)}

                  style={{ ...inputStyle, flex: 1 }}

                  title={frozen ? frozenTitle : undefined}

                />

                <button type="submit" disabled={busy || siteName === settings.siteName} title={frozen ? frozenTitle : undefined}>

                  Save

                </button>

              </div>

            </label>

          </form>



          <form onSubmit={saveUrl} style={{ marginBottom: 20 }}>

            <label style={{ fontSize: 13 }}>

              Site URL

              <div style={{ fontSize: 11, opacity: 0.55, marginTop: 2, marginBottom: 4 }}>

                Used to build links in email content (e.g. "your prize is waiting — view it here"). Include

                https://, no trailing slash.

              </div>

              <div style={{ display: "flex", gap: 8 }}>

                <input

                  value={siteUrl}

                  onChange={(e) => setSiteUrl(e.target.value)}

                  placeholder="https://example.com"

                  style={{ ...inputStyle, flex: 1 }}

                  title={frozen ? frozenTitle : undefined}

                />

                <button type="submit" disabled={busy || siteUrl === settings.siteUrl} title={frozen ? frozenTitle : undefined}>

                  Save

                </button>

              </div>

            </label>

          </form>



          <div style={{ marginBottom: 20 }}>

            <label style={{ fontSize: 13 }}>

              Timezone

              <select

                value={siteTimeZoneValue}

                onChange={(e) => saveTimeZone(e.target.value)}

                disabled={busy}

                style={{ ...inputStyle, display: "block", marginTop: 4, width: "100%" }}

              >

                {(availableTimeZones.length ? availableTimeZones : [siteTimeZoneValue]).map((tz) => (

                  <option key={tz} value={tz}>{tz}</option>

                ))}

              </select>

            </label>

            <p style={{ fontSize: 12, opacity: 0.6, marginTop: 4, marginBottom: 0 }}>

              Every time on the site is shown in this zone — admin screens, buyer pages and
              emails alike — not in whatever zone the person reading happens to be in. A
              competition is announced at a time, and that time has to mean the same thing to
              everyone. Handles its own daylight saving.

            </p>

          </div>



          <div style={{ marginBottom: 20 }}>

            <label style={{ fontSize: 13 }}>

              Currency

              <select

                value={currencyCode}

                onChange={(e) => saveCurrency(e.target.value)}

                disabled={busy}

                style={{ ...inputStyle, display: "block", marginTop: 4, width: "100%" }}

              >

                {Object.entries(settings.supportedCurrencies).map(([code, sym]) => (

                  <option key={code} value={code}>

                    {code} ({sym})

                  </option>

                ))}

              </select>

            </label>

          </div>



          <div style={{ fontSize: 13, marginBottom: 8 }}>Logo</div>

          {settings.hasLogo && (

            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>

              <img

                src={`${API_BASE}/api/site-logo/square?t=${Date.now()}`}

                alt="Current logo"

                style={{ width: 48, height: 48, borderRadius: "50%", objectFit: "cover" }}

              />

              <button onClick={removeLogo} disabled={busy}>

                Remove logo

              </button>

            </div>

          )}

          <form onSubmit={uploadLogo} style={{ display: "flex", gap: 8, alignItems: "center" }}>

            <input type="file" accept="image/*" onChange={(e) => setFile(e.target.files[0])} style={inputStyle} />

            <button type="submit" disabled={busy || !file}>

              {settings.hasLogo ? "Replace" : "Upload"}

            </button>

          </form>

          <p style={{ fontSize: 12, opacity: 0.6, marginTop: 8, marginBottom: 0 }}>

            Automatically resized into a circular version (for the header) and a wider rectangular version, for use

            elsewhere around the site.

          </p>



          {/* THE WINNERS PAGE — a SITE setting. The API's winners fields
          decide what a PARTNER may consume; this decides whether this
          site shows the page at all. Added 18 Sep 2026. */}
      <div style={{ marginTop: 24, paddingTop: 20, borderTop: "1px solid var(--border-color)" }}>
        <h4 style={{ marginBottom: 4 }}>Winners page</h4>
        <p style={{ opacity: 0.7, fontSize: 13, marginTop: 0 }}>
          A public page listing every competition that has been won, newest
          first, with a link in the site's navigation. Every competition ends
          with its image revealed, so a finished one has nothing left to hide.
          Usernames are masked unless you turn that off under Partners &rarr; API, in the Winners group.
        </p>
        <ViewOnlyGate canEdit={canEdit} showNotice>
          <label style={{ fontSize: 13, display: "flex", alignItems: "center", gap: 8 }}>
            <input
              type="checkbox"
              checked={winnersPage}
              disabled={!canEdit || winnersPageBusy}
              onChange={(e) => saveWinnersPage(e.target.checked)}
            />
            Show the Winners page on this site
          </label>
        </ViewOnlyGate>
      </div>
      {/* LIST TEST COMPETITIONS WHILE IN LIVE MODE — added 22 Sep 2026
          (1.437.0). See backend/lib/listed-modes.js for the screens it
          widens and the ones it deliberately does not. */}
      <div style={{ marginTop: 24, paddingTop: 20, borderTop: "1px solid var(--border-color)" }}>
        <h4 style={{ marginBottom: 4 }}>Test competitions in Live mode</h4>
        <p style={{ opacity: 0.7, fontSize: 13, marginTop: 0 }}>
          While you work in Live mode, also list finished Test competitions — the ones the partner's test site
          plays — on Competition History (with playback) and the Charts tab's Ended competitions list. Each is
          labelled Test. The Stats tab, the dashboard figures and the prize and revenue charts stay Live only, so
          test money is never added to live totals.
        </p>
        <ViewOnlyGate canEdit={canEdit} showNotice>
          <label style={{ fontSize: 13, display: "flex", alignItems: "center", gap: 8 }}>
            <input
              type="checkbox"
              checked={listTestInLive === true}
              disabled={!canEdit || listTestBusy || listTestInLive === null}
              onChange={(e) => saveListTestInLive(e.target.checked)}
            />
            List Test Competitions while in live mode
          </label>
        </ViewOnlyGate>
      </div>

      <div style={{ marginTop: 24, paddingTop: 20, borderTop: "1px solid var(--border-color)" }}>

            <div style={{ fontSize: 13, marginBottom: 8 }}>

              Default list browsing — activity log, stats tables, and similar long lists across the admin panel.

              Whoever's viewing a given list can still switch this for themselves right on the page; this is only

              the starting point they see.

            </div>

            <div style={{ display: "flex", gap: 16, marginBottom: 10 }}>

              <label style={{ fontSize: 13, display: "flex", alignItems: "center", gap: 6 }}>

                <input

                  type="radio"

                  name="listDisplayMode"

                  checked={listDisplayMode === "pagination"}

                  disabled={busy}

                  onChange={() => saveListDisplayDefaults({ mode: "pagination" })}

                />

                Pagination

              </label>

              <label style={{ fontSize: 13, display: "flex", alignItems: "center", gap: 6 }}>

                <input

                  type="radio"

                  name="listDisplayMode"

                  checked={listDisplayMode === "infinite"}

                  disabled={busy}

                  onChange={() => saveListDisplayDefaults({ mode: "infinite" })}

                />

                Infinite Scroll

              </label>

            </div>

            <label style={{ fontSize: 13 }}>

              Records per page

              <select

                value={listPageSize}

                onChange={(e) => saveListDisplayDefaults({ pageSize: Number(e.target.value) })}

                disabled={busy}

                style={{ ...inputStyle, display: "block", marginTop: 4, width: 120 }}

              >

                {[10, 25, 50, 100].map((n) => (

                  <option key={n} value={n}>

                    {n}

                  </option>

                ))}

              </select>

            </label>

          </div>

          </fieldset>

        </ViewOnlyGate>

      )}


      {/* WIN NOTIFICATIONS, moved here from the Partners tab on
          12 Sep 2026. The address lives on the partner record, which is
          where it is stored rather than what it is about: one address for
          the whole site, nothing to do with the API. */}
      <div style={{ marginTop: 24, paddingTop: 20, borderTop: "1px solid var(--border-color-subtle)" }}>
        <h4 style={{ marginBottom: 4 }}>Win notifications</h4>
        <p style={{ opacity: 0.7, fontSize: 13, marginTop: 0 }}>
          When a partner's linked user wins a real competition, a heads-up email goes to the address below —
          the same content system as every other email this site sends (Email tab, Content sub-tab, "Partner
          Win Notification"). Left empty, no email is sent.
        </p>
        <ViewOnlyGate canEdit={canEdit} showNotice>
          <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
            <input
              type="email"
              value={winEmail}
              placeholder="ops@partner.example"
              disabled={winBusy}
              onChange={(e) => setWinEmail(e.target.value)}
              style={{ flex: 1, minWidth: 220, padding: "6px 8px" }}
            />
            <button type="button" disabled={winBusy} onClick={saveWinEmail}>
              Save
            </button>
          </div>
        </ViewOnlyGate>
      </div>

    </div>

  );

}



// A site-wide record of user and admin actions — purchases, guesses, free

// entries, payout changes, and admin actions on competitions/users/prizes

// — independent of the per-account "My Competitions"/"My Winnings" pages,

// which only ever show one account's own activity. Root-only given what's

// in it (password resets, account disabling included). Filtered and

// capped server-side (see buildActivityLogResponse) rather than shipping

// the whole log to the client, since it only grows over time.

const ACTIVITY_LOG_ACTIONS = [

  { id: "signup", label: "Signup" },

  { id: "pixel_purchase", label: "Pixel purchase" },

  { id: "free_entry_pixel", label: "Free pixel entry" },

  { id: "guess_submitted", label: "Guess submitted" },

  { id: "payout_details_updated", label: "Payout details updated" },

  { id: "admin_prize_paid", label: "Admin: prize paid" },

  { id: "admin_payout_requested", label: "Admin: payout requested" },

  { id: "admin_competition_created", label: "Admin: competition created" },

  { id: "admin_competition_edited", label: "Admin: competition edited" },

  { id: "admin_competition_deleted", label: "Admin: competition deleted" },

  { id: "admin_user_edited", label: "Admin: user edited" },

  { id: "admin_user_deleted", label: "Admin: user deleted" },

  { id: "admin_password_reset", label: "Admin: password reset" },

  { id: "admin_permissions_edited", label: "Admin: permissions edited" },

  { id: "admin_account_removed", label: "Admin: admin account removed" },

  { id: "admin_bulk_competitions_removed", label: "Admin: bulk competitions removed" },

  { id: "admin_bulk_users_removed", label: "Admin: bulk users removed" },

  { id: "admin_bulk_stats_removed", label: "Admin: bulk stats removed" },

  { id: "admin_mode_changed", label: "Admin: mode changed" },

  { id: "admin_partner_key_regenerated", label: "Admin: partner key regenerated" },

  { id: "admin_partner_edited", label: "Admin: partner edited" },



  // ---- ADDED 2 Oct 2026: the filter listed 22 of the 64 actions the log
  // actually contains. The other 42 could not be filtered for at all (this
  // is a <select> with no free-text option) AND were shown in the table as
  // raw snake_case ids, because actionLabel falls back to the id. Found by
  // asking why generating test users logged nothing when removing them did.
  //
  // A test now asserts this list and the actions server.js logs are the same
  // set, in both directions - a listed action that is never logged is a
  // filter option that always returns nothing, which is how
  // admin_partner_created survived here after its handler went away.

  { id: "admin_account_created", label: "Admin: admin account created" },

  { id: "admin_auto_reschedule_changed", label: "Admin: auto-reschedule changed" },

  { id: "admin_competition_answer_revealed", label: "Admin: competition answer revealed" },

  { id: "admin_demo_comps_link_created", label: "Admin: Demo Comps link created" },

  { id: "admin_demo_comps_link_deleted", label: "Admin: Demo Comps link deleted" },

  { id: "admin_demo_comps_link_extended", label: "Admin: Demo Comps link extended" },

  { id: "admin_demo_comps_started", label: "Admin: Demo Comps round started" },

  { id: "admin_email_broadcast_sent", label: "Admin: email broadcast sent" },

  { id: "admin_email_content_edited", label: "Admin: email content edited" },

  { id: "admin_email_provider_edited", label: "Admin: email provider edited" },

  { id: "admin_email_schedule_created", label: "Admin: email schedule created" },

  { id: "admin_email_schedule_deleted", label: "Admin: email schedule deleted" },

  { id: "admin_email_schedule_edited", label: "Admin: email schedule edited" },

  { id: "admin_email_subscriber_changed", label: "Admin: email subscriber changed" },

  // Server Admin publishing an update a deploy held back because a competition
  // was being played (1.690.0). Logged because the button restarts the backend,
  // which is the one thing on that tab somebody might need to account for
  // afterwards.
  { id: "admin_held_update_published", label: "Admin: held update published" },
  // THE REFUSAL, ADDED IN 1.719.0. The successful publish has been logged
  // since the button existed; a refused one was not, so "somebody pressed
  // publish during a deploy" left no trace in the application at all.
  { id: "admin_held_update_refused", label: "Admin: held update publish refused" },

  { id: "admin_mock_auto_toggle_changed", label: "Admin: mock auto-load/delete changed" },

  { id: "admin_mock_competitions_generated", label: "Admin: demo competitions generated" },

  { id: "admin_mock_competitions_removed", label: "Admin: demo competitions removed" },

  { id: "admin_mock_users_generated", label: "Admin: demo users generated" },

  { id: "admin_mock_users_removed", label: "Admin: demo users removed" },

  { id: "admin_payment_provider_edited", label: "Admin: payment provider edited" },

  { id: "admin_phone_verification_toggled", label: "Admin: phone verification toggled" },

  { id: "admin_queue_started", label: "Admin: queue started" },

  { id: "admin_queue_stopped", label: "Admin: queue stopped" },

  { id: "admin_schedule_applied", label: "Admin: schedule applied" },

  { id: "admin_settings_changed", label: "Admin: settings changed" },

  { id: "admin_simulation_paused", label: "Admin: simulation paused" },

  { id: "admin_simulation_toggled", label: "Admin: simulation switched on/off" },

  { id: "admin_sms_provider_edited", label: "Admin: SMS provider edited" },

  { id: "admin_test_users_generated", label: "Admin: test users generated" },

  { id: "admin_test_users_removed", label: "Admin: test users removed" },

  { id: "admin_theme_activated", label: "Admin: theme activated" },

  { id: "admin_theme_created", label: "Admin: theme created" },

  { id: "admin_theme_deleted", label: "Admin: theme deleted" },

  { id: "admin_theme_edited", label: "Admin: theme edited" },

  { id: "content_page_created", label: "Content page created" },

  { id: "content_page_deleted", label: "Content page deleted" },

  { id: "content_page_updated", label: "Content page updated" },
  // Logged by admin.install.useDefaultsFor - the install wizard writing a
  // content section's shipped defaults because the step was skipped. In the log
  // rather than only in siteSettings.installDefaultsUsed, because that field
  // says what is STILL untouched and this says when the decision was taken and
  // by whom.
  { id: "install_defaults_kept", label: "Install: shipped defaults kept" },

  { id: "email_auto_suppressed", label: "Email: address auto-suppressed" },

  { id: "email_schedule_fired", label: "Email: schedule fired" },

  { id: "email_suppression_reactivated", label: "Email: suppression lifted" },

  { id: "install_completed", label: "Install completed" },

  { id: "legal_doc_html_cleared", label: "Legal doc: text cleared" },

  { id: "legal_doc_html_saved", label: "Legal doc: text saved" },

  { id: "legal_doc_pdf_deleted", label: "Legal doc: PDF deleted" },

  { id: "legal_doc_pdf_uploaded", label: "Legal doc: PDF uploaded" },

  { id: "partner_consent_coverage_edited", label: "Partner: consent coverage edited" },

  { id: "partner_landing_url_cleared", label: "Partner: landing URL cleared" },

  { id: "partner_landing_url_set", label: "Partner: landing URL set" },

  { id: "partner_layout_saved", label: "Partner: layout saved" },

  { id: "partner_marked_ready_for_live", label: "Partner: marked ready for live" },

  { id: "partner_unmarked_ready_for_live", label: "Partner: unmarked ready for live" },

  { id: "password_reset_completed", label: "Password reset completed" },

  { id: "password_reset_requested", label: "Password reset requested" },

  { id: "payment_dispute_cleared", label: "Payment: dispute cleared" },

  { id: "payment_marked_disputed", label: "Payment: marked disputed" },

  { id: "phone_verified", label: "Phone verified" },

  { id: "preview_link_changed", label: "Preview link changed" },

  { id: "purchase_refunded", label: "Purchase refunded" },

  { id: "site_export", label: "Site exported" },

  { id: "site_import", label: "Site imported" },

  { id: "totp_disabled", label: "Two-factor disabled" },

  { id: "totp_enabled", label: "Two-factor enabled" },

  { id: "totp_force_disabled", label: "Two-factor force-disabled by admin" },
];



function ActivityLogPanel({ token, onViewUser, modeStatus }) {

  const [search, setSearch] = useState("");

  const [debouncedSearch, setDebouncedSearch] = useState("");

  const [action, setAction] = useState("");

  // LIVE OR TEST ENTRIES (1.468.0). Every entry already carries the mode
  // it was recorded in, so this is a filter rather than a migration. An
  // entry with no mode - a visitor signing up, say - shows in both.
  const [view, setView] = useState("live");
  // ONLY ASK WHILE THE SITE IS LIVE. The filter is not drawn in Test or
  // Demo - there is nothing to choose - but the state behind it still
  // says "live", and asking for Live from Test is refused by the same
  // rule that makes the filter pointless there. Sending nothing means
  // the admin's own mode, which is the right answer in every mode.
  const askMode = adminMode(modeStatus) === "live" ? view : undefined;

  // Root's configured starting point for this and every other paginated

  // admin list — see usePaginatedList for how a viewer's own choice, once

  // made, takes over from this.

  const [siteDefaults, setSiteDefaults] = useState(null);



  useEffect(() => {

    wsRequest("siteSettings.get")

      .then((data) => setSiteDefaults({ mode: data.listDisplayMode, pageSize: data.listPageSize }))

      .catch(() => {});

  }, []);



  // Debounced — re-querying on every keystroke would fire a request per

  // character typed into the search box; a short pause after typing stops

  // is enough to feel live without hammering the backend.

  useEffect(() => {

    const id = setTimeout(() => setDebouncedSearch(search), 250);

    return () => clearTimeout(id);

  }, [search]);



  const {

    mode,

    setMode,

    pageSize,

    setPageSize,

    page,

    setPage,

    items: entries,

    totalMatching,

    totalPages,

    loading,

    loadMore,

    hasMore,

    reload,

  } = usePaginatedList({

    listKey: "activity-log",

    defaultMode: siteDefaults?.mode,

    defaultPageSize: siteDefaults?.pageSize,

    deps: [debouncedSearch, action, askMode],

    fetchPage: ({ offset, limit }) =>

      wsRequest(
        "admin.activityLog.list",
        { search: debouncedSearch, action: action || undefined, offset, limit, mode: askMode },
        token
      ),

  });



  // Real-time refresh — every action this log tracks broadcasts

  // activity-log-changed the moment it's logged (see logActivity on the

  // backend), so this stays current without needing a manual refresh.

  useEffect(() => onBroadcast((message) => {

    if (message.type === "activity-log-changed") reload();

  }), [reload]);



  const actionLabel = (id) => ACTIVITY_LOG_ACTIONS.find((a) => a.id === id)?.label || id;



  return (

    <div style={{ marginTop: 16 }}>

      <h3>Activity Log</h3>

      <p style={{ opacity: 0.7, fontSize: 13, marginTop: -6 }}>

        Every purchase, guess, free entry, payout change, and admin action across the site — for tracing what

        happened if there's ever a dispute.

      </p>

      <DataModeFilter value={view} onChange={setView} siteIsLive={adminMode(modeStatus) === "live"} what="entries" />



      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 14 }}>

        <input

          type="text"

          placeholder="Search by person or competition…"

          value={search}

          onChange={(e) => setSearch(e.target.value)}

          style={{ flex: "1 1 240px", padding: 8 }}

        />

        <select value={action} onChange={(e) => setAction(e.target.value)} style={{ padding: 8 }}>

          <option value="">All actions</option>

          {ACTIVITY_LOG_ACTIONS.map((a) => (

            <option key={a.id} value={a.id}>

              {a.label}

            </option>

          ))}

        </select>

      </div>



      <ListDisplayControls

        listKey="activity-log"

        mode={mode}

        setMode={setMode}

        pageSize={pageSize}

        setPageSize={setPageSize}

        page={page}

        setPage={setPage}

        totalPages={totalPages}

        totalMatching={totalMatching}

        itemsCount={entries?.length || 0}

        loading={loading}

        loadMore={loadMore}

        hasMore={hasMore}

      />



      {entries === null ? (

        <p>Loading…</p>

      ) : entries.length === 0 ? (

        <p style={{ opacity: 0.7 }}>No activity matches this filter.</p>

      ) : (

        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>

          {entries.map((e) => (

            <div

              key={e.id}

              style={{

                border: "1px solid var(--border-color)",

                borderRadius: 8,

                padding: 10,

                fontSize: 13,

                display: "flex",

                justifyContent: "space-between",

                gap: 12,

                alignItems: "baseline",

                flexWrap: "wrap",

              }}

            >

              <div>

                <span style={{ opacity: 0.6 }}>[{actionLabel(e.action)}]</span> {e.summary}

                {e.actorLabel && (

                  <>

                    {" — "}

                    {e.actorRole === "user" ? (

                      <button

                        onClick={() => onViewUser(e.actorLabel)}

                        style={{

                          background: "none",

                          border: "none",

                          color: "var(--accent-blue)",

                          textDecoration: "underline",

                          cursor: "pointer",

                          padding: 0,

                          font: "inherit",

                        }}

                      >

                        {e.actorLabel}

                      </button>

                    ) : (

                      e.actorLabel

                    )}

                    {e.actorRole && e.actorRole !== "user" ? ` (${e.actorRole})` : ""}

                  </>

                )}

              </div>

              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>

                {e.mode !== null && (

                  <span

                    style={{

                      fontSize: 11,

                      padding: "1px 6px",

                      borderRadius: 10,

                      border: `1px solid ${modeColor(modeOf(e))}`,

                      opacity: 0.85,

                      whiteSpace: "nowrap",

                    }}

                  >

                    {modeLabel(modeOf(e))}

                  </span>

                )}

                <span style={{ opacity: 0.5, whiteSpace: "nowrap" }}>{siteDateTime(e.createdAt)}</span>

              </div>

            </div>

          ))}

        </div>

      )}

    </div>

  );

}



// Root-configurable public API — every exposable field, grouped, each

// with its own checkbox and a live preview of its current value, so

// root can see exactly what a field would expose before turning it on.

// A master switch above the groups turns the whole public API on/off

// regardless of which individual fields are enabled underneath it.

// NO VARIANT since 19 Sep 2026 — one set of API field settings, and
// one tab. The test variant governed an API nobody could be calling:
// it was only reachable while the whole SITE was in Test mode.
function ApiSettingsPanel({ token, canEdit, modeStatus }) {

  // The refusal text, when closing the site was declined because
  // something is still to run. Null when there is nothing to ask.
  const [deferPrompt, setDeferPrompt] = useState(null);


  const [settings, setSettings] = useState(null);

  const [busy, setBusy] = useState(false);

  const { confirm, confirmModal } = useConfirm();

  const updateAction = "admin.api.updateSettings";

  const changedEventType = "api-settings-changed";

  // Only the live variant can ever be frozen — the test variant is

  // only ever reachable from Demo mode in the first place, so there's

  // no equivalent state for it to be frozen against.

  // DEMO ONLY, like every other settings panel.
  //
  // This read `!== "live"`, inherited from when there were two
  // variants and the LIVE one was frozen outside Live mode — an
  // operator was meant to use the Test tab instead. With that tab gone
  // the same rule locked the whole panel in Test mode, so nothing
  // could be configured before going live.
  //
  // Safe: root's mode IS the site's mode, so an admin in Test mode is
  // on a site that serves the public API to nobody. Reported
  // 19 Sep 2026: "I can't check the Enable the public API checkbox."
  const frozen = adminMode(modeStatus) === "demo";

  const frozenTitle = "Leave Demo mode to manage this";



  const load = () => {

    wsRequest("admin.api.getSettings", {}, token)

      .then(setSettings)

      .catch(() => {});

  };



  useEffect(load, [token]);



  // Real-time refresh — lets a second admin with this tab open see

  // changes made elsewhere without needing to reload. Listens for

  // this variant's own changed event, plus site-settings-changed for

  // lastEntriesUnmasked specifically (live variant only — see

  // toggleUnmasked below), which can also be flipped via the

  // dedicated settings-API-key endpoint, not just this UI.

  useEffect(() => onBroadcast((message) => {

    if (message.type === changedEventType || (message.type === "site-settings-changed")) load();

  }), [token]);



  const save = async (next) => {

    setBusy(true);

    try {

      const data = await wsRequest(updateAction, next, token);

      setSettings(data);

      // SAY SO. This panel had an error toast and no success one, so a
      // save that worked was indistinguishable from one that did nothing —
      // the only way to know was to reload and look.
      //
      // It matters most for the control that needs it least often: "Use
      // API exclusively" closes the public site to everyone who is not a
      // partner's visitor. Silence there is the wrong answer whichever way
      // the toggle went.
      //
      // Named rather than a bare "Saved.", because the two things this
      // panel writes are not the same size.
      // A WAIT THAT WAS STORED IS NOT A CHANGE THAT HAPPENED. The same
      // success reply comes back for both, so the toast reads the state
      // rather than the request — v1.388.0 said "now closed" for a switch
      // that was still waiting.
      showToast(

        "publicSiteDisabled" in next

          ? // ASKING FOR THE CURRENT VALUE is how a waiting switch is
            // cancelled; nothing changed, so it must not say it did.
            !!next.publicSiteDisabled === !!settings.publicSiteDisabled && !data?.pendingApiSwitch

            ? "Cancelled — the waiting switch is cleared. The queues stay stopped until you restart them."

            : data?.pendingApiSwitch

            ? `Saved — it will switch to the ${data.pendingApiSwitch.to ? "API" : "Native"} queue once nothing is running. Every queue is stopped until then.`

            : next.publicSiteDisabled

              ? "Saved — serving the API queue. The public site is closed to everyone but partner visitors."

              : "Saved — serving the Native queue. The public site is open again, and the partner API is paused."

          : "Saved."

      );

    } catch (err) {

      // A 409 OFFERING A DEFERRAL IS A QUESTION, NOT A FAILURE. The
      // backend refuses to close the public site while a competition is
      // running or locked in, and offers to do it once that finishes.
      // Showing only the error would leave an operator watching the clock
      // and coming back — the thing they would forget.
      //
      // ON THE FLAG, not the wording. This matched "ask to close
      // automatically" until v1.388.0 reworded the message, and the offer
      // silently stopped appearing. The wording check stays as a fallback
      // for a backend older than the flag.
      //
      // AND busy IS RELEASED. This returned before setBusy(false), so the
      // dialog opened with both of its buttons disabled.
      if (
        "publicSiteDisabled" in next &&
        !next.deferIfBusy &&
        (err?.canDefer || /ask to close automatically|apply automatically once it has/.test(err?.message || ""))
      ) {

        setDeferPrompt({ message: err.message, to: !!next.publicSiteDisabled });

        setBusy(false);

        return;

      }


      showToast(err.message || "Could not reach backend.", "error");

    }

    setBusy(false);

  };



  const toggleField = (fieldId, checked) => {

    const current = settings.groups.flatMap((g) => g.fields.filter((f) => f.enabled).map((f) => `${g.group}.${f.key}`));

    const next = checked ? [...new Set([...current, fieldId])] : current.filter((id) => id !== fieldId);

    save({ enabledFields: next });

  };



  const toggleGroup = (group, checked) => {

    const current = settings.groups.flatMap((g) => g.fields.filter((f) => f.enabled).map((f) => `${g.group}.${f.key}`));

    const groupFieldIds = group.fields.map((f) => `${group.group}.${f.key}`);

    const next = checked

      ? [...new Set([...current, ...groupFieldIds])]

      : current.filter((id) => !groupFieldIds.includes(id));

    save({ enabledFields: next });

  };



  // lastEntriesUnmasked/recentPurchasesUnmasked live in siteSettings,

  // not apiSettings — a separate WS action from the field-toggle save()

  // above, even though both end up reflected in this same component's

  // local settings state (which mixes fields from both sources for this

  // one tab). Takes the field name explicitly since this same masking

  // pattern now applies to more than one group. Live variant only — see

  // the render below.

  const toggleUnmasked = async (field, checked) => {

    setBusy(true);

    try {

      await wsRequest("admin.siteSettings.update", { [field]: checked }, token);

      setSettings((prev) => ({ ...prev, [field]: checked }));

    } catch (err) {

      showToast(err.message || "Could not reach backend.", "error");

    }

    setBusy(false);

  };



  // copyToLive went with the Test tab on 19 Sep 2026 — there is one
  // set of API settings now, so there is nothing to promote.



  // Most field values are plain strings/numbers, shown as-is — but a

  // few (Latest Entries' individual entries) are objects with their own

  // sub-fields, which String() would otherwise render as unhelpful

  // "[object Object]" text in this live preview.

  const formatFieldValue = (value) => {

    if (value === null || value === undefined) return "—";

    if (typeof value === "object") return Object.values(value).join(" — ");

    return String(value);

  };



  if (settings === null) return <p>Loading…</p>;

  // A WAITING CHANGE, either direction: { to, requestedAt } or null.
  const pendingSwitch =
    settings.pendingApiSwitch ||
    (settings.pendingPublicSiteDisableAt ? { to: true, requestedAt: settings.pendingPublicSiteDisableAt } : null);



  return (

    <div style={{ marginTop: 16 }}>

      <h3>API</h3>

      <p style={{ opacity: 0.7, fontSize: 13, marginTop: -6 }}>
        A public, read-only API for third-party sites to consume — exposes
        only the fields checked below, nothing else. Fully open right now (no
        key required).
      </p>



      {/* The "Copy to Live" button went with the Test tab, 19 Sep 2026. */}



      <ViewOnlyGate canEdit={canEdit} showNotice>

        {frozen && (

          <p style={{ opacity: 0.7, fontSize: 13, marginBottom: 10 }}>

            Settings are read-only in Demo mode. Leave Demo to change what the
            public API exposes.
          </p>

        )}

        <fieldset disabled={frozen} style={{ border: "none", padding: 0, margin: 0, opacity: frozen ? 0.55 : 1 }}>

        <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 14, margin: "14px 0" }}>

          <input

            type="checkbox"

            checked={settings.enabled}

            disabled={busy}

            title={frozen ? frozenTitle : undefined}

            onChange={(e) => save({ enabled: e.target.checked })}

          />

          Enable the public API

        </label>

        {/* WHETHER IT WILL ACTUALLY ANSWER, which is not what the box above
            says. On 3 Oct 2026 a partner integration was chased for an
            afternoon with this box ticked: isNotLiveLockdown() sits in
            FRONT of the enabled check, so with root in Test every public
            endpoint answered 503 and nothing on this tab said so.

            THE WORST CASE IS BOTH AT ONCE - switched off AND locked down -
            because turning the box on then changes nothing a visitor can
            see. `blockers` carries both; `blockedBy` is the one the
            visitor actually hits, since the route checks the lockdown
            first. */}
        {settings.availability && !settings.availability.serving && (
          <div
            style={{
              border: "1px solid var(--accent-red)",
              borderRadius: 6,
              padding: "10px 12px",
              margin: "0 0 14px",
              fontSize: 13,
              lineHeight: 1.55,
            }}
          >
            <strong style={{ fontFamily: "monospace" }}>
              [{settings.availability.status}]
            </strong>{" "}
            <strong>The public API is not answering.</strong>
            <div style={{ marginTop: 4, opacity: 0.9 }}>{settings.availability.why}</div>
            {settings.availability.fix && (
              <div style={{ marginTop: 6, opacity: 0.9 }}>{settings.availability.fix}</div>
            )}
          </div>
        )}

        {/* ---- THE FLAG NOTICE IS GONE (1.904.0) ----
            A panel stood here for the case where the API answered ONLY because
            the process carried TREAT_INSTANCE_AS_LIVE, saying so and warning
            that the next clean start took it away. Its reason was sound -
            serving because of something that lasts one restart is not the same
            as serving - and the surviving half of it is the
            allowedWhileNotLive line below, which says the opposite about a
            SETTING: it survives a restart, so the panel states it plainly
            rather than as something temporary.
            1.903.0 dropped that key from apiAvailability, so this was a branch
            that could not fire. The key is deliberately not spelled here: a
            rule bans the panel from reading it, and a comment quoting it would
            satisfy the ban - the lesson §92 of docs/mongodb-plan.md records. */}



        {(

          <label

            style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 14, margin: "14px 0" }}

            title="On: serve the API queue — competitions run on partner sites only. Off: serve the Native queue — competitions run on the public site, and the partner API is paused."

          >

            {/* THE OFFER, when the backend refused because something is
                still to run. Same overlay shape as the live-payments
                confirmation and PhoneVerificationModal. */}
            {deferPrompt && (
              <div
                role="dialog"
                aria-modal="true"
                onClick={() => !busy && setDeferPrompt(null)}
                style={{
                  position: "fixed",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  zIndex: 100,
                  background: "var(--overlay-color)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  padding: 20,
                  boxSizing: "border-box",
                }}
              >
                <div
                  onClick={(e) => e.stopPropagation()}
                  style={{
                    background: "var(--bg-panel-alt)",
                    padding: 28,
                    borderRadius: 10,
                    border: "1px solid var(--border-color)",
                    maxWidth: 500,
                    width: "100%",
                    boxSizing: "border-box",
                  }}
                >
                  <h3 style={{ marginTop: 0 }}>Not yet — something is still to run</h3>
                  <p style={{ fontSize: 13.5, lineHeight: 1.5 }}>{deferPrompt.message}</p>
                  {/* BOTH DIRECTIONS WAIT since v1.388.0, so the offer says
                      which way it is going — it only ever said "close". */}
                  <p style={{ fontSize: 13.5, lineHeight: 1.5 }}>
                    {deferPrompt.to ? (
                      <>
                        Switch to the <strong>API queue</strong> automatically once it has
                        finished? That closes the public site to everyone but partner visitors.
                      </>
                    ) : (
                      <>
                        Switch to the <strong>Native queue</strong> automatically once it has
                        finished? That reopens the public site and pauses the partner API.
                      </>
                    )}{" "}
                    Every queue stops straight away, in every mode, so nothing new starts and
                    the one in flight is the last. Restart the queues yourself afterwards.
                  </p>
                  <div style={{ marginTop: 18, display: "flex", gap: 8 }}>
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => {
                        const to = deferPrompt.to;
                        setDeferPrompt(null);
                        save({ publicSiteDisabled: to, deferIfBusy: true });
                      }}
                    >
                      Switch once this finishes
                    </button>
                    <button type="button" disabled={busy} onClick={() => setDeferPrompt(null)}>
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* A PENDING CLOSE IS VISIBLE AND CANCELLABLE. It acts on its
                own, minutes or hours later; an intention nobody can see
                until it fires is worse than no feature. */}
            {/* EITHER DIRECTION since v1.388.0. It read "Closing" for a
                change back to the Native queue, which reopens the site.
                pendingPublicSiteDisableAt is the fallback for a backend
                that predates pendingApiSwitch — it only ever meant ON. */}
            {pendingSwitch && (
              <div style={{ color: "var(--accent-yellow)", fontSize: 12.5, fontWeight: 600, marginBottom: 8 }}>
                {pendingSwitch.to
                  ? "Switching to the API queue (closing the public site)"
                  : "Switching to the Native queue (reopening the public site)"}{" "}
                automatically once nothing is running
                {settings.pendingPublicSiteDisableWaitingOn
                  ? ` — waiting on "${settings.pendingPublicSiteDisableWaitingOn.title}"${
                      settings.pendingPublicSiteDisableWaitingOn.mode &&
                      settings.pendingPublicSiteDisableWaitingOn.mode !== "live"
                        ? ` (${modeLabel(settings.pendingPublicSiteDisableWaitingOn.mode)} mode)`
                        : ""
                    }`
                  : ""}
                . Every queue is stopped, so nothing new will start.{" "}
                {/* CANCELLING IS ASKING FOR THE CURRENT VALUE — the backend
                    treats that as "changed my mind". It sent false, which
                    only cancelled a pending ON. */}
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => save({ publicSiteDisabled: !!settings.publicSiteDisabled })}
                  style={{ fontSize: 12, padding: "2px 8px", marginLeft: 4 }}
                >
                  Cancel
                </button>
              </div>
            )}

            <input

              type="checkbox"

              checked={!!settings.publicSiteDisabled}

              disabled={busy || !!pendingSwitch}

              onChange={(e) => save({ publicSiteDisabled: e.target.checked })}

            />

            Use API exclusively

          </label>

        )}

        {/* WHICH QUEUE IS BEING SERVED, said in words. The checkbox is the
            control; this is the consequence, in every mode. */}
        <p style={{ fontSize: 12.5, opacity: 0.75, marginTop: -8, marginBottom: 14 }}>
          {(settings.servedQueue || (settings.publicSiteDisabled ? "api" : "native")) === "api" ? (
            <>
              Serving the <strong>API queue</strong>: competitions run on partner sites through
              the API, and the public site is closed. Competitions in the Native queue wait.
            </>
          ) : (
            <>
              Serving the <strong>Native queue</strong>: competitions run on the public site,
              and the partner API is paused. Competitions in the API queue wait.
            </>
          )}{" "}
          New competitions join the queue being served. The switch waits for anything running
          or locked in, in any mode.
        </p>

        {/* ---- SERVING THE API WHILE THE SITE IS NOT LIVE (1.886.0) ----
            The four partner reads refused whenever root was not in Live, and
            the reason was good: the data is not real, and a partner's visitors
            would be shown fiction as though it were a running competition. The
            cost nobody chose was that showing a partner ANYTHING before go-live
            needed TREAT_INSTANCE_AS_LIVE - a flag that lasted one deploy, which
            is why it lived on the test box through 28 restarts. That flag was
            removed in 1.903.0 and this setting is all there is now.
            The owner chose a setting over opening the API unconditionally, so a
            pre-launch box serves the public nothing until somebody here decides
            otherwise. The answer to the fiction is the `mode` every one of those
            responses already carries.
            PLACED BEFORE THE PAGE TOKEN because the order is the operator's
            question order: whether the API answers at all, and only then what it
            demands of the caller. */}
        <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 14, marginBottom: 6 }}>
          <input
            type="checkbox"
            checked={!!settings.allowApiWhileNotLive}
            disabled={busy}
            onChange={(e) => save({ allowApiWhileNotLive: e.target.checked })}
          />
          Serve the API while the site is not live
        </label>
        <p style={{ fontSize: 12.5, opacity: 0.75, marginTop: 0, marginBottom: 14 }}>
          {settings.availability && settings.availability.rootMode === "live" ? (
            <>
              <strong>Nothing to do right now.</strong> Root is in Live mode, so the not-live
              lockdown this setting turns off is not engaged. It takes effect if the site is ever
              put back into Test or Demo.
            </>
          ) : settings.availability && settings.availability.allowedWhileNotLive ? (
            <>
              <strong>In effect now.</strong> The field groups, the winners list, the next-up
              countdown and the partner demo session answer on this{" "}
              {settings.availability.rootMode}-mode site, and every response says{" "}
              <code>&quot;mode&quot;: &quot;{settings.availability.rootMode}&quot;</code> so a
              partner can tell. This site&apos;s own public pages are still closed.
            </>
          ) : (
            <>
              When off, every partner read answers <code>503</code> while the site is in Test or
              Demo, and this switch is the only thing that opens them. Turning it on serves the
              data for the mode the site is actually in, labelled as that mode, and it survives a
              restart. It does <strong>not</strong> open this site&apos;s own pages &mdash; those
              stay closed until root is in Live.
            </>
          )}
        </p>

        {/* ---- THE PAGE-TOKEN REQUIREMENT (1.881.0) ----
            Asked for by the owner on 19 Oct 2026, and the reason was the partner
            rather than the plumbing: tied to the switch above, enforcement is
            absent on any instance that also serves this site's own public pages -
            which is the instance a partner DEVELOPS against. They would build a
            working integration and meet 401s the day they moved to their own.
            DISABLED WHILE THE SWITCH IS ON because it is moot there: a dedicated
            instance enforces it either way, and a tick box that cannot change the
            answer is worse than one that is plainly not in charge. */}
        <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 14, marginBottom: 6 }}>
          <input
            type="checkbox"
            checked={!!settings.requirePartnerPageToken || !!settings.publicSiteDisabled}
            disabled={busy || !!settings.publicSiteDisabled}
            onChange={(e) => save({ requirePartnerPageToken: e.target.checked })}
          />
          Require a partner page token on the API reads
        </label>
        <p style={{ fontSize: 12.5, opacity: 0.75, marginTop: 0, marginBottom: 14 }}>
          {settings.publicSiteDisabled ? (
            <>
              <strong>Already required.</strong> &quot;Use API exclusively&quot; is on, so the
              field groups, the winners list and the next-up read answer only to a partner page
              token. There is no public site of this one for them to serve.
            </>
          ) : (
            <>
              When on, a read of a field group, the winners list or the next-up countdown is
              refused with <code>401</code> unless it carries a partner page token the
              partner&apos;s own server minted with their API key. Worth turning on wherever a
              partner builds their integration, so they meet the requirement while developing
              rather than on the day they go live.{" "}
              <strong>It takes effect at once:</strong> any integration not sending the header
              stops reading the moment this is ticked. This site&apos;s own pages are
              unaffected, because they read none of those three.
            </>
          )}
        </p>



        {settings.groups.map((group) => {

          const enabledCount = group.fields.filter((f) => f.enabled).length;

          return (

            <div key={group.group} style={{ border: "1px solid var(--border-color)", borderRadius: 8, padding: 14, marginBottom: 14 }}>

              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", flexWrap: "wrap", gap: 8 }}>

                <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 14 }}>

                  <input

                    type="checkbox"

                    checked={enabledCount === group.fields.length}

                    ref={(el) => {

                      if (el) el.indeterminate = enabledCount > 0 && enabledCount < group.fields.length;

                    }}

                    disabled={busy || !settings.enabled}

                    title={settings.enabled ? undefined : "Enable the public API to choose fields"}

                    onChange={(e) => toggleGroup(group, e.target.checked)}

                  />

                  <strong>{group.groupLabel}</strong>

                </label>

                <code style={{ fontSize: 12, opacity: 0.6 }}>/api/v1/{group.group}</code>

              </div>

              <div style={{ marginTop: 10, display: "grid", gridTemplateColumns: "auto 1fr auto", rowGap: 6, columnGap: 10, fontSize: 13 }}>

                {group.fields.map((field) => (

                  <React.Fragment key={field.key}>

                    <label style={{ display: "flex", alignItems: "center", gap: 8 }}>

                      <input

                        type="checkbox"

                        checked={field.enabled}

                        disabled={busy || !settings.enabled}

                        title={settings.enabled ? undefined : "Enable the public API to choose fields"}

                        onChange={(e) => toggleField(`${group.group}.${field.key}`, e.target.checked)}

                      />

                      {field.label}

                    </label>

                    <span style={{ opacity: 0.6 }}>{formatFieldValue(field.value)}</span>

                    <span />

                    {/* WHAT THE FIELD IS FOR (1.524.0), when its label does
                        not say. Asked on 28 Sep 2026 of the two date
                        fields: "why is there two similar options?"

                        A ROW OF ITS OWN, spanning all three columns - the
                        grid is `auto 1fr auto` and a fourth cell would
                        otherwise start a ragged fourth column.

                        FROM THE FIELD, NOT FROM HERE. server.js carries
                        `help` beside `maskedBy`; a field with nothing to
                        explain sends none and nothing is drawn. */}

                    {field.help && (

                      <p

                        data-api-field-help={`${group.group}.${field.key}`}

                        style={{ gridColumn: "1 / -1", fontSize: 12, opacity: 0.7, margin: "0 0 4px 26px" }}

                      >

                        {field.help}

                      </p>

                    )}

                  </React.Fragment>

                ))}

              </div>

              {group.group === "latest-entries" && (

                <div style={{ marginTop: 14, paddingTop: 14, borderTop: "1px solid var(--border-color-subtle)" }}>

                  <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13 }}>

                    <input

                      type="checkbox"

                      checked={settings.lastEntriesUnmasked}

                      disabled={busy}

                      onChange={(e) => toggleUnmasked("lastEntriesUnmasked", e.target.checked)}

                    />

                    Show real usernames and guess text (unmasked)

                  </label>

                  <p style={{ opacity: 0.7, fontSize: 12, margin: "6px 0 10px" }}>

                    Off by default — each entry above shows a masked username ("J***") with no guess text. Turning

                    this on switches every entry_N field to the real username and guess, the same detail this

                    site's own pages already show its own visitors.

                  </p>

                  <p style={{ opacity: 0.7, fontSize: 12, margin: "0 0 6px" }}>

                    Can also be toggled without an admin logging in, via{" "}

                    <code style={{ fontSize: 11 }}>PATCH /api/admin/settings/last-entries-masking</code>, header{" "}

                    <code style={{ fontSize: 11 }}>x-settings-api-key</code>, body{" "}

                    <code style={{ fontSize: 11 }}>{`{ "unmasked": true }`}</code>:

                  </p>

                  <div style={{ display: "flex", gap: 8, alignItems: "center" }}>

                    <code style={{ flex: 1, wordBreak: "break-all", fontSize: 12, opacity: 0.8 }}>{settings.settingsApiKey || "Hidden - you have view-only access to the API tab."}</code>

                    <button

                      type="button"

                      disabled={!settings.settingsApiKey}

                      onClick={() => {

                        navigator.clipboard?.writeText(settings.settingsApiKey);

                        showToast("Copied.");

                      }}

                    >

                      Copy

                    </button>

                  </div>

                </div>

              )}

              {/* THE THIRD MASKING SWITCH (1.462.0). The Sitewide Winners
                  page box has always said "Usernames are masked unless you
                  turn that off under the API's Winners fields" - and there
                  was nothing here to turn off. The setting itself has
                  worked all along; only the control was missing. */}
              {group.group === "winners" && (
                <div style={{ marginTop: 14, paddingTop: 14, borderTop: "1px solid var(--border-color-subtle)" }}>
                  <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13 }}>
                    <input
                      type="checkbox"
                      checked={!!settings.winnersUnmasked}
                      disabled={busy}
                      onChange={(e) => toggleUnmasked("winnersUnmasked", e.target.checked)}
                    />
                    Show real usernames (unmasked)
                  </label>
                  <p style={{ opacity: 0.7, fontSize: 12, margin: "6px 0 0" }}>
                    Off by default &mdash; each winner above shows a masked username (&ldquo;J***&rdquo;). This one
                    is a public list of people who recently received money, findable by anyone, so think before
                    turning it on: masked names largely defuse that and real ones do not.
                  </p>
                </div>
              )}
              {group.group === "recent-purchases" && (

                <div style={{ marginTop: 14, paddingTop: 14, borderTop: "1px solid var(--border-color-subtle)" }}>

                  <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13 }}>

                    <input

                      type="checkbox"

                      checked={settings.recentPurchasesUnmasked}

                      disabled={busy}

                      onChange={(e) => toggleUnmasked("recentPurchasesUnmasked", e.target.checked)}

                    />

                    Show real usernames (unmasked)

                  </label>

                  <p style={{ opacity: 0.7, fontSize: 12, margin: "6px 0 0" }}>

                    Off by default — each purchase above shows a masked username ("J***"). Turning this on switches

                    every purchase_N field to the real username, the same detail this site's own pages already show

                    its own visitors.

                  </p>

                </div>

              )}

            </div>

          );

        })}

        </fieldset>

      </ViewOnlyGate>

      {confirmModal}

    </div>

  );

}



// ---- THE PARTNER-FACING DATA-POINT CATALOGUE (1.908.0) ----
//
// READ-ONLY, AND DELIBERATELY SO. This says what the API can serve and what a
// caller may do with each thing in it; the Settings tab beside it is where an
// operator decides what it DOES serve. The half of the 20 Oct request that
// turns data points on and off FOR A THIRD PARTY SEPARATELY is not here - that
// needs `enabledFields` to split into two meanings, what the API serves at all
// and what a third party may read, and it is its own release.
//
// EVERY LINE ON THIS PAGE ARRIVES DERIVED. There is no list of endpoints or
// field names typed into the admin: admin.api.catalogue reads the routes out
// of server.js's own declarations, the data points out of API_FIELD_REGISTRY,
// and which widget fetches what out of the embed scripts themselves. A typed
// copy would have started wrong - the status widget reaches three of its
// endpoints only through a helper, which no reader would have listed - and
// would have drifted with the first route added after it.
//
// A HOLE IS SHOWN, NOT HIDDEN. When the derivation cannot resolve something a
// widget fetches, it says so on that widget's own tab. A partner builds
// against this page, so a list that is quietly one endpoint short becomes a
// 404 in their integration rather than a gap in ours.
function ApiCataloguePanel({ token }) {

  const [catalogue, setCatalogue] = useState(undefined); // undefined = loading

  const [error, setError] = useState(null);

  const [which, setWhich] = useState(null);

  const load = () => {
    wsRequest("admin.api.catalogue", {}, token)
      .then((data) => { setCatalogue(data); setError(null); })
      .catch((err) => setError(err?.message || "Could not read the API catalogue."));
  };

  useEffect(load, [token]);

  // THE TICKS HERE MUST NOT DISAGREE WITH THE CHECKBOXES ONE TAB AWAY. The
  // derivation is cached in the backend for the life of the process, but which
  // fields are ticked is not part of that cache - so a save on the Settings
  // tab, by this admin or another one, is re-read rather than left stale.
  useEffect(() => onBroadcast((message) => {
    if (message.type === "api-settings-changed" || message.type === "site-settings-changed") load();
  }), [token]);

  if (error) return <p style={{ color: "var(--accent-red)" }}>{error}</p>;

  if (catalogue === undefined) return <p>Loading&hellip;</p>;

  // THE WIDGETS' OWN ORDER, NOT THE ALPHABET. widgetLabels is an object the
  // backend writes in the order a partner meets them - the competition widget
  // first, because that is the one they embed. Anything the backend did not
  // name falls in after them rather than disappearing.
  const order = Object.keys(catalogue.widgetLabels || {});
  const rank = (id) => (order.indexOf(id) < 0 ? order.length : order.indexOf(id));
  const widgets = [...(catalogue.widgets || [])].sort(
    (a, b) => rank(a.id) - rank(b.id) || a.id.localeCompare(b.id)
  );

  const unusedGroups = (catalogue.groups || []).filter(
    (g) => (catalogue.unusedGroups || []).includes(g.name)
  );

  const tabs = [
    ...widgets.map((w) => ({ id: w.id, label: w.id.charAt(0).toUpperCase() + w.id.slice(1) })),
    ...(unusedGroups.length ? [{ id: "__other", label: "Other data points" }] : []),
  ];

  // AN UNKNOWN TAB FALLS BACK rather than rendering nothing. This panel is
  // reachable by URL, and /admin/partners/api/live was a real bookmark until
  // 19 Sep 2026.
  const active = tabs.some((t) => t.id === which) ? which : tabs[0]?.id;

  const widget = widgets.find((w) => w.id === active) || null;

  const fieldCount = (catalogue.groups || []).reduce((n, g) => n + g.fields.length, 0);

  const box = { border: "1px solid var(--border-color)", borderRadius: 8, padding: 14, marginBottom: 14 };

  const headCell = { fontSize: 12, opacity: 0.6, textTransform: "uppercase", letterSpacing: 0.4 };

  // WHAT THE PARTNER ACTUALLY REQUESTS, spelled as a URL they can call. A
  // script that builds `"/api/v1/replays/" + token` carries the prefix, which
  // is not callable on its own - so the prefix is marked as one and the real
  // routes are named underneath it.
  const shownPath = (e) => (e.path.endsWith("/") ? `${e.path}...` : e.path);

  const routeNote = (e) =>
    e.routePaths.length > 1 || e.routePaths[0] !== e.path ? e.routePaths.join(", ") : null;

  const served = (field) => {
    if (!catalogue.apiEnabled) return field.ticked ? "ticked, API off" : "not ticked";
    return field.ticked ? "served" : "not ticked";
  };

  const fieldTable = (fields, access) => (
    <div style={{ marginTop: 10, display: "grid", gridTemplateColumns: "auto 1fr auto auto", rowGap: 6, columnGap: 14, fontSize: 13, alignItems: "baseline" }}>

      <div style={headCell}>Data point</div>
      <div style={headCell}>What it is</div>
      <div style={headCell}>Access</div>
      <div style={headCell}>Right now</div>

      {fields.map((field) => (
        <React.Fragment key={field.key}>

          <code style={{ fontSize: 12 }}>{field.key}</code>

          <div>
            {field.label}
            {field.money && <span style={{ opacity: 0.6 }}> &mdash; an amount, no symbol</span>}
            {field.supporting && <span style={{ opacity: 0.6 }}> &mdash; an input, not a row</span>}
            {field.help && <div style={{ opacity: 0.6, fontSize: 12 }}>{field.help}</div>}
          </div>

          <div>{access || "—"}</div>

          <div style={{ opacity: field.ticked ? 1 : 0.6 }}>{served(field)}</div>

        </React.Fragment>
      ))}

    </div>
  );

  return (

    <div style={{ marginTop: 16 }}>

      <h3 style={{ margin: 0 }}>Data points</h3>

      <p style={{ opacity: 0.7, fontSize: 13, marginTop: 4, maxWidth: 820 }}>
        Everything this API can serve, read out of the API itself rather than written
        down here: {catalogue.routeCount} routes under <code>/api/v1</code> and {fieldCount} data
        points across {(catalogue.groups || []).length} field groups. A partner using the
        widgets provided does not need this page &mdash; the widgets already fetch all of it.
        It is here for a partner who wants to build their own.
      </p>

      <p style={{ opacity: 0.7, fontSize: 13, maxWidth: 820 }}>
        {catalogue.apiEnabled
          ? <>A data point in a field group is served only while its box is ticked on the Settings tab. Everything else about an endpoint &mdash; the rest of its response &mdash; is fixed, and the boxes do not apply to it.</>
          : <>The public API is switched off, so none of this is being served. The ticks below are what it would serve once it is switched on, from the Settings tab.</>}
      </p>

      {(catalogue.missingScripts || []).length > 0 && (
        <p style={{ color: "var(--accent-red)", fontSize: 13 }}>
          Not read: {catalogue.missingScripts.join(", ")}. Those widgets&rsquo; endpoints are missing
          from this page &mdash; the files are not where the backend looked for them.
        </p>
      )}

      <TabBar
        tabs={tabs}
        activeId={active}
        onSelect={setWhich}
      />

      {widget && (
        <>

          <p style={{ fontSize: 13, marginTop: 0 }}>
            <strong>{(catalogue.widgetLabels || {})[widget.id] || widget.id}</strong>
            {" "}&mdash; {widget.endpoints.length} endpoint{widget.endpoints.length === 1 ? "" : "s"}.
          </p>

          {widget.unresolved.length > 0 && (
            <p style={{ color: "var(--accent-red)", fontSize: 13 }}>
              {widget.unresolved.length} request{widget.unresolved.length === 1 ? "" : "s"} in this
              widget builds its path at runtime and could not be resolved to a route:{" "}
              {widget.unresolved.join(", ")}. Read the script for those.
            </p>
          )}

          {widget.unmatched.length > 0 && (
            <p style={{ color: "var(--accent-red)", fontSize: 13 }}>
              This widget fetches {widget.unmatched.join(", ")}, which no route in the backend
              serves. Either the widget or the route moved.
            </p>
          )}

          {widget.endpoints.map((e) => (
            <div key={e.path} style={box}>

              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", flexWrap: "wrap", gap: 8 }}>

                <code style={{ fontSize: 13 }}>{e.methods.join(", ")} {shownPath(e)}</code>

                <span style={{ fontSize: 12, opacity: 0.7 }}>{e.access}</span>

              </div>

              {routeNote(e) && (
                <div style={{ fontSize: 12, opacity: 0.6, marginTop: 4 }}>
                  served by <code>{routeNote(e)}</code>
                </div>
              )}

              {e.group ? (
                <>
                  <div style={{ fontSize: 12, opacity: 0.6, marginTop: 6 }}>
                    Field group <strong>{e.groupLabel}</strong> &mdash; {e.fields.length} data points,
                    each one ticked or not on the Settings tab.
                  </div>
                  {fieldTable(e.fields, e.access)}
                </>
              ) : (
                <div style={{ fontSize: 12, opacity: 0.6, marginTop: 6 }}>
                  Not a field group: what this returns is not chosen by the field checkboxes.
                </div>
              )}

            </div>
          ))}

        </>
      )}

      {active === "__other" && (
        <>

          <p style={{ fontSize: 13, marginTop: 0, maxWidth: 820 }}>
            These field groups are served by the API but none of the four widgets reads them.
            A partner writing their own widget can; the widgets provided simply have nothing
            to show them in.
          </p>

          {unusedGroups.map((g) => (
            <div key={g.name} style={box}>

              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", flexWrap: "wrap", gap: 8 }}>

                <code style={{ fontSize: 13 }}>{g.methods.join(", ")} {g.routePaths.join(", ")}</code>

                <span style={{ fontSize: 12, opacity: 0.7 }}>{g.access || "no route"}</span>

              </div>

              <div style={{ fontSize: 12, opacity: 0.6, marginTop: 6 }}>
                Field group <strong>{g.label}</strong> &mdash; {g.fields.length} data points.
              </div>

              {fieldTable(g.fields, g.access)}

            </div>
          ))}

        </>
      )}

    </div>

  );

}

// Root-configurable partner accounts for the Tier 2 server-to-server

// login flow — each one's API key is shown in full exactly once, right

// after creation or regeneration, then never again (same as any other

// secret-issuing flow), only its hash is ever kept.

// canGenerateDemoData was a prop here until 12 Sep 2026, for a Generate
// button that duplicated the Demo competition queue's own.
function PartnersPanel({ token, canEdit, modeStatus }) {

  const [partner, setPartner] = useState(null);

  // NO KEY TABS since 19 Sep 2026. Generating still names a mode — it
  // says which DOMAIN is being recorded, not which key — and defaults
  // to test, which is where a partner starts.
  const [domainBeingSet, setDomainBeingSet] = useState("test");

  const [busy, setBusy] = useState(false);

  const [revealedKey, setRevealedKey] = useState(null); // { mode, apiKey }

  // notificationEmail moved to the Sitewide tab on 12 Sep 2026.

  const [partnerDemoToken, setPartnerDemoToken] = useState(null);
  // The second gate. Enabling the API is not consent to hand out demo
  // sessions, so this is its own switch and starts off.
  const [demoCompsEnabled, setDemoCompsEnabled] = useState(false);



  const load = () => {

    wsRequest("admin.partner.get", {}, token)

      .then((data) => {

        setPartner(data);
        setPartnerDemoToken(data.partnerDemoToken || null);
        setDemoCompsEnabled(!!data.partnerDemoCompetitionsEnabled);

      })

      .catch(() => {});

  };



  useEffect(load, [token]);



  // THE DOMAIN THE BUYER MAY BE RETURNED TO, required since 1.277.0.
  // A checkout started with this key may only send the buyer back to this
  // domain or a subdomain of it; without one, a holder of the key can land
  // them anywhere immediately after a real payment.
  //
  // A MODAL RATHER THAN window.prompt: an alert box cannot show the
  // consequence of regenerating alongside the question, cannot validate as
  // you type, and looks like nothing else in this panel. The two questions
  // — which domain, and are you sure — belong on one screen because they
  // are one decision.
  const [generating, setGenerating] = useState(null);

  // MIRRORS backend/lib/partner-domain.js, and a test runs both over the
  // same corpus and requires them to agree. It cannot import it - that is
  // server code and this is a browser bundle - so the test is the only
  // thing holding them together.
  //
  // THE COMMENT THAT USED TO BE HERE NAMED THIS BUG AND THEN SHIPPED IT.
  // It said the rule was "deliberately LOOSER than the backend's own
  // check" because "a client-side rule that drifted stricter would refuse
  // something the server accepts" - and the rule underneath it tested the
  // regex against the RAW STRING while the server tests it against the
  // extracted HOSTNAME. `:` and `/` are not in the character class, so
  // every `https://...` was refused by the panel and would have been
  // accepted by the server.
  //
  // Reported 3 Oct 2026 with a screenshot: `https://589compstest.com`
  // typed in, button disabled, "That does not look like a domain." The
  // helper text one line above the field says "With or without https:// -
  // either is fine". A UI that refuses what its own help text promises is
  // worse than one with no help text.
  const partnerDomainFromInput = (value) => {
    const raw = String(value || "").trim().toLowerCase();
    if (!raw) return null;
    let host;
    try {
      // `new URL`, not a string strip. `https://589comps.com@evil.com` is
      // a URL whose host is evil.com; stripping the scheme leaves
      // `589comps.com@evil.com`, which looks like ours to a careless
      // regex. This returns what a browser would actually navigate to.
      host = new URL(raw.includes("://") ? raw : `https://${raw}`).hostname;
    } catch {
      return null;
    }
    return /^[a-z0-9.-]+\.[a-z]{2,}$/.test(host) ? host : null;
  };
  const domainLooksValid = (value) => partnerDomainFromInput(value) !== null;

  // BOTH DOMAINS, because they are set once with the key and cannot be
  // edited afterwards. A blank live site takes the test one.
  const generateKey = async (mode, domain, liveDomain) => {

    setBusy(true);

    try {

      const data = await wsRequest("admin.partner.generateKey", { mode, domain: domain.trim(), liveDomain: (liveDomain || "").trim() }, token);

      setGenerating(null);

      setRevealedKey({ mode, apiKey: data.apiKey });

      load();

    } catch (err) {

      showToast(err.message || "Could not reach backend.", "error");

    }

    setBusy(false);

  };



  // "API IS CONFIGURED AND READY TO RUN LIVE."
  //
  // The server refuses this without a key and without a live domain
  // recorded, so the failure path matters: the box must go back to
  // where it was rather than showing a state that was not saved.
  // THE TWO CONSENT SWITCHES (1.500.0). One call takes either or both,
  // so the two checkboxes share a handler and a partner file save.
  const setConsentCoverage = async (patch) => {
    setBusy(true);
    try {
      await wsRequest("admin.partner.setConsentCoverage", patch, token);
      load();
      showToast("Saved what the partner's own sign-up covers.", "success");
    } catch (err) {
      showToast(err.message || "Could not reach backend.", "error");
      load();
    }
    setBusy(false);
  };

  const setReadyForLive = async (readyForLive) => {
    setBusy(true);
    try {
      await wsRequest("admin.partner.setReadyForLive", { readyForLive }, token);
      load();
      showToast(
        readyForLive
          // NOT "will be returned" — nothing changes until the site
          // itself goes live, which is behind the checklist.
          ? "Marked ready to go live. Nothing changes until the site is live."
          : "No longer marked ready. The key works in Test mode only.",
        "success"
      );
    } catch (err) {
      showToast(err.message || "Could not reach backend.", "error");
      load();
    }
    setBusy(false);
  };

  // DISABLING ASKS FIRST; ENABLING DOES NOT.
  //
  // Disabling stops the partner's integration at once — same weight as
  // deleting, minus the permanence. Re-enabling only undoes that, so
  // making somebody confirm it would be noise.
  //
  // Requested 19 Sep 2026.
  const [confirmingDisable, setConfirmingDisable] = useState(false);
  const toggleKeyDisabled = async () => {
    setConfirmingDisable(false);

    setBusy(true);

    try {

      await wsRequest("admin.partner.setKeyDisabled", { disabled: !partner.key?.disabled }, token);

      load();

    } catch (err) {

      showToast(err.message || "Could not reach backend.", "error");

    }

    setBusy(false);

  };



  // A MODAL, not window.confirm.
  //
  // Deleting takes the domains with it and stops the partner's
  // integration immediately. A browser alert is the wrong weight for
  // that — it reads as the site breaking rather than asking.
  //
  // Reported 19 Sep 2026.
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const deleteKey = async () => {

    setConfirmingDelete(false);

    setBusy(true);

    try {

      // NO MODE — there is one key, and deleting it takes both domains
      // with it. That is what makes regenerating the only way to change
      // them.
      await wsRequest("admin.partner.deleteKey", {}, token);

      setRevealedKey(null);

      load();

    } catch (err) {

      showToast(err.message || "Could not reach backend.", "error");

    }

    setBusy(false);

  };









  const setDemoCompetitionsEnabled = async (enabled) => {
    setBusy(true);
    try {
      const data = await wsRequest(
        "admin.partner.setDemoCompetitionsEnabled",
        { enabled },
        token
      );
      setPartner(data);
      setDemoCompsEnabled(!!data.partnerDemoCompetitionsEnabled);
      showToast(
        enabled ? "API demo competitions enabled." : "API demo competitions disabled.",
        "success"
      );
    } catch (err) {
      showToast(err.message || "Could not reach backend.", "error");
    }
    setBusy(false);
  };

  const regenerateDemoToken = async () => {

    setBusy(true);

    try {

      const data = await wsRequest("admin.partner.regenerateDemoToken", {}, token);

      setPartner(data);

      setPartnerDemoToken(data.partnerDemoToken || null);
      setDemoCompsEnabled(!!data.partnerDemoCompetitionsEnabled);

      showToast("Partner demo token generated.", "success");

    } catch (err) {

      showToast(err.message || "Could not reach backend.", "error");

    }

    setBusy(false);

  };



  const clearDemoToken = async () => {

    setBusy(true);

    try {

      const data = await wsRequest("admin.partner.clearDemoToken", {}, token);

      setPartner(data);

      setPartnerDemoToken(null);

      showToast("Partner demo token cleared.", "success");

    } catch (err) {

      showToast(err.message || "Could not reach backend.", "error");

    }

    setBusy(false);

  };



  if (partner === null) return <p>Loading…</p>;



  const keySet = partner.key || {};

  // Same rule the backend's own assertNotFrozen now enforces for these

  // three actions specifically when mode === "live" — never relies on

  // this frontend check alone, just mirrors it so the buttons look

  // disabled rather than merely erroring on click.

  // ONLY REPLACING AN EXISTING KEY IS FROZEN outside Live mode, and
  // only when the live domain is what is being set — see the server.
  // MATCHES THE SERVER: it refuses these once the PARTNER is live, not
  // by which domain field happens to be selected.
  //
  // This read `domainBeingSet === "live"`, which disabled the buttons
  // for a partner who was not live at all and left them enabled for one
  // who was — the panel and the endpoint disagreeing in both
  // directions. Aligned 19 Sep 2026.
  // ON ONE LINE deliberately: a test collects every `!== "live"` gate
  // in this panel and requires each to be the liveFrozen one, so a
  // wrapped declaration reads as an unexplained second gate.
  const liveFrozen = !!keySet.readyForLive && adminMode(modeStatus) !== "live";
  // CREATING THE FIRST KEY IS CONFIGURATION, not destruction — there is
  // no integration to break. Replacing one is frozen outside Live mode,
  // which is what the server enforces.
  const liveFrozenForCreate = liveFrozen && !!keySet.hasKey;

  const liveFrozenTitle =
    "This partner is live — switch to Live mode to change their key";



  return (

    <div style={{ marginTop: 16, maxWidth: 640 }}>

      <h3>Partners</h3>

      <p style={{ opacity: 0.7, fontSize: 13, marginTop: -6 }}>

        The one third-party site that can vouch for its own already-logged-in users via a server-to-server call, so

        those users never see a login screen here at all. The key must stay on that server — never its browser.

      </p>

      {/* How many real accounts came through the partner's site. This

          once said which of two Email and Payments configurations served

          them; since 1.407.0 there is one, and it says so, because the

          old wording told the operator a second set existed. */}

      {partner && (

        <p style={{ fontSize: 12.5, opacity: 0.75, marginTop: -2 }}>

          <strong>{partner.linkedUserCount ?? 0}</strong>{" "}

          {partner.linkedUserCount === 1 ? "user is" : "users are"} currently linked to this partner — accounts

          that came through the partner's site. They are emailed, texted and charged through the same providers as

          everyone else.

        </p>

      )}



      {/* NO TEST/LIVE TABS since 19 Sep 2026 — there is one key. It
          carries both domains, and the readiness checkbox below
          decides which is in play. */}



      {revealedKey && (

        <div

          style={{

            border: "1px solid var(--accent-yellow)",

            borderRadius: 8,

            padding: 14,

            marginBottom: 20,

            background: "var(--bg-input)",

          }}

        >

          <strong>Copy this API key now — it won't be shown again.</strong>

          <div style={{ display: "flex", gap: 8, marginTop: 8, alignItems: "center" }}>

            <code style={{ flex: 1, wordBreak: "break-all", fontSize: 13 }}>{revealedKey.apiKey}</code>

            <button

              type="button"

              onClick={() => {

                navigator.clipboard?.writeText(revealedKey.apiKey);

                showToast("Copied.");

              }}

            >

              Copy

            </button>

          </div>

          <button type="button" style={{ marginTop: 10 }} onClick={() => setRevealedKey(null)}>

            Done

          </button>

        </div>

      )}



      <div style={{ fontSize: 13, marginBottom: 12 }}>

        {keySet.hasKey ? (

          <>

            <div>Status: {keySet.disabled ? <span style={{ color: "var(--accent-red)" }}>Disabled</span> : "Active"}</div>

            <div style={{ opacity: 0.7 }}>Created: {keySet.createdAt ? siteDateTime(keySet.createdAt) : "—"}</div>

            <div style={{ opacity: 0.7 }}>Last used: {keySet.lastUsedAt ? siteDateTime(keySet.lastUsedAt) : "Never"}</div>

            {/* THE DOMAIN, OR A WARNING THAT THERE ISN'T ONE.
                A key without a domain cannot have its returnUrl checked
                against anything, so a holder of it can land a buyer on any
                site immediately after a real payment. Keys generated before
                1.277.0 are in that state and keep working, which is the
                right call for not breaking a live integration mid-session —
                but it must not be silent, because nothing else on this
                screen would ever mention it. */}
            {keySet.hasKey && (
              (keySet.testDomain || keySet.liveDomain) ? (
                <div style={{ opacity: 0.7 }}>
                  Buyers return to: <strong>{keySet.readyForLive ? keySet.liveDomain || keySet.testDomain : keySet.testDomain || keySet.liveDomain}</strong> (and its subdomains)
                </div>
              ) : (
                <div style={{ color: "var(--accent-yellow)", fontWeight: 600 }}>
                  No domain on this key — buyers can be returned to any site. Regenerate it to
                  set one.
                </div>
              )
            )}

          </>

        ) : (

          <p style={{ opacity: 0.7 }}>No API key generated yet.</p>

        )}

        {/* THE TWO DOMAINS, AS TEXT. They belong to the key: a
            credential is issued for a partner's sites, and changing
            where buyers may be returned to is a different arrangement
            rather than an edit. Delete the key and issue a new one.
            Decided 19 Sep 2026. */}
        {keySet.hasKey && (
          <div style={{ marginTop: 16, paddingTop: 14, borderTop: "1px solid var(--border-color-subtle)" }}>
            <strong style={{ fontSize: 13 }}>The partner's sites</strong>
            <p style={{ fontSize: 12.5, opacity: 0.75, marginTop: 4, marginBottom: 0 }}>
              Test: <code>{keySet.testDomain || "none"}</code>{" "}
              &nbsp;&middot;&nbsp; Live: <code>{keySet.liveDomain || "none"}</code>
            </p>
            <p style={{ fontSize: 12.5, opacity: 0.7, marginTop: 6, marginBottom: 0 }}>
              Buyers may only be returned to the one in play, or a subdomain of
              it. To change either, delete this key and generate a new one —
              the domains go with it.
            </p>
          </div>
        )}

        {/* THE READINESS SWITCH. One key serves test and live; this
            is what says the partner has been proved. It decides
            which of their two domains a buyer may be returned to,
            and — once the SITE is live — whether the key works at
            all. Added 19 Sep 2026. */}
        {keySet.hasKey && (
          <div style={{ marginTop: 16, paddingTop: 14, borderTop: "1px solid var(--border-color-subtle)" }}>
            <ViewOnlyGate canEdit={canEdit} showNotice>
              <label style={{ fontSize: 13, display: "flex", alignItems: "center", gap: 8 }}>
                <input
                  type="checkbox"
                  checked={!!keySet.readyForLive}
                  disabled={!canEdit || busy}
                  onChange={(e) => setReadyForLive(e.target.checked)}
                />
                API is configured and ready to run live
              </label>
            </ViewOnlyGate>
            <p style={{ fontSize: 12.5, opacity: 0.75, marginTop: 6, marginBottom: 0 }}>
              This says the integration has been proved — it does not put
              anything live. Until this site itself is in Live mode, the key
              works in Test mode only, and a buyer can be returned to either{" "}
              <code>{keySet.testDomain || "the test domain"}</code> or{" "}
              <code>{keySet.liveDomain || "the live domain"}</code> — nothing
              real is charged. Once BOTH are true, only to{" "}
              <code>{keySet.liveDomain || "the live domain"}</code>. Shown on the
              go-live checklist.
            </p>
          </div>
        )}
        {/* WHAT THE PARTNER'S OWN SIGN-UP ALREADY COVERED (1.500.0).
            Asked for on 27 Sep 2026: a partner's users have already
            agreed to THEIR terms and, sometimes, to their emails — so
            this site should stop either assuming that or asking again.

            Two switches, not one, because they are two different claims
            and the second one decides who receives marketing email. Both
            default off: asserting on a partner's behalf that their users
            agreed to something is not a default to choose for them.

            Behind hasKey like the readiness switch above: with no key
            there are no vouched users, so there is nothing for either
            claim to be about. */}
        {keySet.hasKey && (
        <div data-partner-consent style={{ marginTop: 16, paddingTop: 14, borderTop: "1px solid var(--border-color-subtle)" }}>
          <strong style={{ fontSize: 13 }}>What the partner&rsquo;s own sign-up covers</strong>
          <ViewOnlyGate canEdit={canEdit} showNotice>
            <label style={{ fontSize: 13, display: "flex", alignItems: "flex-start", gap: 8, marginTop: 8 }}>
              <input
                type="checkbox"
                data-partner-terms-covered
                checked={!!partner.termsCoveredByPartner}
                disabled={!canEdit || busy}
                onChange={(e) => setConsentCoverage({ termsCoveredByPartner: e.target.checked })}
                style={{ marginTop: 2 }}
              />
              <span>
                Their terms &amp; conditions and privacy policy cover what this site does for their users
              </span>
            </label>
            <label style={{ fontSize: 13, display: "flex", alignItems: "flex-start", gap: 8, marginTop: 8 }}>
              <input
                type="checkbox"
                data-partner-email-consent
                checked={!!partner.emailConsentFromPartner}
                disabled={!canEdit || busy}
                onChange={(e) => setConsentCoverage({ emailConsentFromPartner: e.target.checked })}
                style={{ marginTop: 2 }}
              />
              <span>They can tell us which of their users agreed to be emailed</span>
            </label>
          </ViewOnlyGate>
          <p data-partner-consent-note style={{ fontSize: 12.5, opacity: 0.75, marginTop: 8, marginBottom: 0 }}>
            {partner.termsCoveredByPartner
              ? "A vouched account records that it is covered by this partner's terms, and by which domain — this site never claims the agreement as its own."
              : "A vouched account records no agreement at all. Nothing breaks; the account page simply shows nothing agreed, which is the truth until this is ticked."}
            {" "}
            {partner.emailConsentFromPartner
              ? "Email consent is taken per person from the partner's vouch call — never assumed for all of them — and only for a user whose real address they sent. Those users receive scheduled site updates and become subscribed contacts with your email provider."
              : "Nothing the partner sends about email consent is acted on, so no vouched user receives site updates."}
          </p>
        </div>
        )}

      </div>



      {liveFrozen && (

        <div

          style={{

            border: "1px solid var(--accent-yellow)",

            borderRadius: 8,

            padding: 10,

            marginBottom: 12,

            fontSize: 13,

            opacity: 0.9,

          }}

        >

          This partner is marked ready to run live, so real buyers depend
          on their key. Regenerating, disabling or deleting it is disabled
          outside Live mode — doing it from here would break their
          integration while it looks like nothing has real consequences.
          Switch to Live mode for those. Creating a key when none exists is
          still allowed: there is no integration to break.

        </div>

      )}



      {/* Same overlay shape as the live-payments confirmation and
          PhoneVerificationModal — the house pattern for a decision that
          has to be answered. */}
      {/* DELETING IS A MODAL, not a browser alert — it stops the
          partner's integration immediately and takes their domains
          with it. Added 19 Sep 2026. */}
      {/* DISABLING ASKS FIRST. It stops the partner's integration at
          once — the same weight as deleting, minus the permanence.
          Added 19 Sep 2026. */}
      {confirmingDisable && (
        <div
          role="dialog"
          aria-modal="true"
          onClick={() => !busy && setConfirmingDisable(false)}
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 100,
            background: "rgba(0,0,0,0.55)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: "var(--bg-secondary)",
              border: "1px solid var(--border-color)",
              borderRadius: 10,
              padding: 20,
              maxWidth: 460,
              width: "90%",
            }}
          >
            <h3 style={{ marginTop: 0 }}>Disable the API key?</h3>
            <p style={{ fontSize: 13, opacity: 0.85 }}>
              The partner's integration stops working straight away — every
              request with this key is refused until it is enabled again.
            </p>
            <p style={{ fontSize: 13, opacity: 0.85 }}>
              The key itself is kept, along with both domains, so enabling
              puts everything back as it was.
            </p>
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 16 }}>
              <button type="button" onClick={() => setConfirmingDisable(false)} disabled={busy}>
                Cancel
              </button>
              <button type="button" onClick={toggleKeyDisabled} disabled={busy}>
                Disable the key
              </button>
            </div>
          </div>
        </div>
      )}

      {confirmingDelete && (
        <div
          role="dialog"
          aria-modal="true"
          onClick={() => !busy && setConfirmingDelete(false)}
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 100,
            background: "rgba(0,0,0,0.55)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: "var(--bg-secondary)",
              border: "1px solid var(--border-color)",
              borderRadius: 10,
              padding: 20,
              maxWidth: 460,
              width: "90%",
            }}
          >
            <h3 style={{ marginTop: 0 }}>Delete the API key?</h3>
            <p style={{ fontSize: 13, opacity: 0.85 }}>
              The partner's integration stops working immediately, and both
              recorded domains go with it. Accounts already created through
              the key are not affected.
            </p>
            <p style={{ fontSize: 13, opacity: 0.85 }}>
              You will need to generate a new key and enter the domains
              again.
            </p>
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 16 }}>
              <button type="button" onClick={() => setConfirmingDelete(false)} disabled={busy}>
                Cancel
              </button>
              <button type="button" onClick={deleteKey} disabled={busy}>
                Delete the key
              </button>
            </div>
          </div>
        </div>
      )}

      {generating && (
        <div
          role="dialog"
          aria-modal="true"
          onClick={() => !busy && setGenerating(null)}
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 100,
            background: "var(--overlay-color)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 20,
            boxSizing: "border-box",
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: "var(--bg-panel-alt)",
              padding: 28,
              borderRadius: 10,
              border: "1px solid var(--border-color)",
              maxWidth: 460,
              width: "100%",
              boxSizing: "border-box",
            }}
          >
            <h3 style={{ marginTop: 0 }}>
              {partner.key?.hasKey ? "Regenerate" : "Generate"} the{" "}
              {generating.mode} API key
            </h3>

            {/* THE CONSEQUENCE, BEFORE THE FIELD. window.confirm used to
                carry this and it arrived after the domain had been typed,
                which is the wrong order for a decision that breaks a live
                integration. */}
            {partner.key?.hasKey && (
              <p style={{ fontSize: 13.5, lineHeight: 1.5, color: "var(--accent-yellow)" }}>
                The existing key stops working the moment this is generated. Any partner using
                it will need the new one before their site works again.
              </p>
            )}

            <label style={{ fontSize: 13, display: "block", marginBottom: 6, marginTop: 14 }}>
              The partner's TEST site
              <input
                type="text"
                autoFocus
                value={generating.domain}
                placeholder="partner.example"
                onChange={(e) => setGenerating({ ...generating, domain: e.target.value })}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && domainLooksValid(generating.domain) && !busy) {
                    generateKey(generating.mode, generating.domain, generating.liveDomain);
                  }
                }}
                style={{
                  // SPELLED OUT, not `...inputStyle`. That name is defined
                  // locally inside three OTHER components and is not in
                  // scope here — copying the line from one of them would
                  // have been a ReferenceError the moment this opened, and
                  // check-frontend is the check that exists to catch it.
                  width: "100%",
                  padding: 8,
                  marginTop: 4,
                  background: "var(--bg-input)",
                  border: "1px solid var(--border-color-light)",
                  borderRadius: 6,
                  color: "var(--text-primary)",
                  boxSizing: "border-box",
                }}
              />
            </label>
            {/* THE LIVE SITE, captured with the key because it cannot be
                edited afterwards — see the panel text. Left blank it
                takes the same domain as the test site, which is the
                ordinary case for a partner with one site. */}
            <label style={{ fontSize: 13, display: "block", marginBottom: 6 }}>
              The partner's LIVE site{" "}
              <span style={{ opacity: 0.6 }}>(blank = same as test)</span>
              <input
                type="text"
                value={generating.liveDomain || ""}
                placeholder="partner.example"
                onChange={(e) =>
                  setGenerating({ ...generating, liveDomain: e.target.value })
                }
                style={{ width: "100%", marginTop: 4 }}
              />
            </label>

            <div style={{ fontSize: 12.5, opacity: 0.75, lineHeight: 1.5 }}>
              Buyers may only be returned to this domain, or a subdomain of it, after paying.
              It is what stops a leaked key sending them somewhere else. With or without{" "}
              <code>https://</code> — either is fine.
            </div>

            {/* Says what is wrong only once there is something to be wrong
                about, so an empty field is not an error before it has been
                typed in. */}
            {generating.domain.trim() && !domainLooksValid(generating.domain) && (
              <div style={{ fontSize: 12.5, color: "var(--accent-red)", marginTop: 8 }}>
                That does not look like a domain. It needs a dot and a suffix — partner.example,
                not "partner" or a full page address.
              </div>
            )}

            <div style={{ marginTop: 18, display: "flex", gap: 8 }}>
              <button
                type="button"
                disabled={busy || !domainLooksValid(generating.domain)}
                onClick={() => generateKey(generating.mode, generating.domain, generating.liveDomain)}
              >
                {busy ? "Generating…" : "Generate API Key"}
              </button>
              <button type="button" disabled={busy} onClick={() => setGenerating(null)}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      <ViewOnlyGate canEdit={canEdit} showNotice>

        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>

          <button

            type="button"

            disabled={busy || liveFrozenForCreate}

            title={liveFrozenForCreate ? liveFrozenTitle : undefined}

            onClick={() =>
              // BOTH DOMAINS SEEDED from what the key already records.
              //
              // The dialog asks for both since domains became part of
              // the key, but this only filled the one matching the
              // mode — so regenerating showed an empty live field and
              // silently offered to set it to the test domain.
              //
              // Reported 19 Sep 2026.
              setGenerating({
                mode: domainBeingSet,
                domain: keySet.testDomain || "",
                liveDomain: keySet.liveDomain || "",
              })
            }

          >

            {keySet.hasKey ? "Regenerate key" : "Generate key"}

          </button>

          {keySet.hasKey && (

            <>

              <button

                type="button"

                disabled={busy || liveFrozen}

                title={liveFrozen ? liveFrozenTitle : undefined}

                onClick={() =>
                  keySet.disabled ? toggleKeyDisabled() : setConfirmingDisable(true)
                }

              >

                {keySet.disabled ? "Enable" : "Disable"}

              </button>

              <button

                type="button"

                disabled={busy || liveFrozen}

                title={liveFrozen ? liveFrozenTitle : undefined}

                onClick={() => setConfirmingDelete(true)}

              >

                Delete key

              </button>

            </>

          )}

        </div>

      </ViewOnlyGate>



      {/* THE "DEMO COMPETITIONS" CARD WAS HERE — a Generate button on
          the test-key tab.
      
          It called the same action the Demo competition queue's own
          Generate calls, and said so. Two buttons doing one thing, one
          of them on a tab with no other reason to visit it.
      
          Its description was also wrong after 12 Sep 2026: it said a
          third party "using the test key above" needed competitions to
          play through. Demo sessions use no key at all — that is the
          point of them. The instructions under the demo-sessions
          checkbox below say where competitions come from. */}



      {/* WIN NOTIFICATIONS MOVED TO SITEWIDE on 12 Sep 2026.
      
          It was here because the address lives on the partner record,
          which is storage, not subject matter: it is one address for the
          whole site and has nothing to do with the API this tab
          configures. The permission moved with it — manage_settings
          rather than manage_partners — so the tab and the action agree
          about who may change it. */}



      <div style={{ marginTop: 24, paddingTop: 20, borderTop: "1px solid var(--border-color-subtle)" }}>

        <h4 style={{ marginBottom: 4 }}>Partner demo sessions</h4>

        {/* THE SWITCH, above everything it governs. Enabling the public
            API is consent to serve the API — not consent to hand every
            visitor on a partner's site a session against the demo
            competitions. It starts off. */}
        <label
          style={{
            display: "flex",
            alignItems: "flex-start",
            gap: 8,
            margin: "10px 0 12px",
            cursor: adminMode(modeStatus) === "live" && canEdit ? "pointer" : "default",
          }}
        >
          <input
            type="checkbox"
            checked={demoCompsEnabled}
            disabled={busy || !canEdit || adminMode(modeStatus) === "demo"}
            onChange={(e) => setDemoCompetitionsEnabled(e.target.checked)}
            style={{ marginTop: 3 }}
          />
          <span style={{ fontSize: 13 }}>
            <strong>Use demo competitions</strong> — lets your own site show demo competitions to
            visitors with no account at all. Off by default, and separate from the API being enabled.
          </span>
        </label>

        {demoCompsEnabled && (
          <div
            style={{
              border: "1px solid var(--border-color-subtle)",
              borderRadius: 8,
              padding: "12px 14px",
              marginBottom: 14,
              fontSize: 12.5,
              lineHeight: 1.65,
            }}
          >
            <strong style={{ fontSize: 13 }}>1. Prepare your own site</strong>
            <ol style={{ margin: "8px 0 14px", paddingLeft: 18 }}>
              <li>
                <strong>Point your integration at this site.</strong> Set{" "}
                <code>COMPETITION_PROVIDER_URL</code> to this site's address. The demo token below
                only works against the deployment that issued it — a token from a different one is
                refused, and the error only says the token is invalid.
              </li>
              <li>
                <strong>Give each visitor an ID and keep it.</strong> The endpoint below requires a{" "}
                <code>visitorId</code> and refuses without one. Generate any string you like the first
                time someone arrives — a random one is fine — and store it in their browser.
                <br />
                The same ID always returns the same demo account, so a visitor who renews their
                session keeps the pixels they bought. A fresh ID creates a brand new account instead,
                which works but loses everything they had and leaves the old one behind.
              </li>
              <li>
                <strong>Call <code>POST /api/v1/partner/demo-session</code></strong> from the browser
                with <code>{"{ demoToken, visitorId }"}</code>. You get back a session token lasting
                60 minutes; renew it by calling the same endpoint again with the same visitor ID.
              </li>
              <li>
                <strong>Use that token as a buyer token.</strong> Pass it to the widget, or send it as
                the <code>x-auth-token</code> header on API calls — exactly as you would a real
                buyer's.
              </li>
            </ol>

            <strong style={{ fontSize: 13 }}>2. Set it up here</strong>
            <ol style={{ margin: "8px 0 14px", paddingLeft: 18 }}>
              <li>
                <strong>Generate the demo token</strong> below, and put it in{" "}
                <code>COMPETITION_PARTNER_DEMO_TOKEN</code> on your server. It is safe in
                browser-side code, unlike your API key — it can only ever reach demo data.
              </li>
              <li>
                <strong>Run a demo round.</strong> On the Competitions tab, Queue Control Panel, press{" "}
                <strong>Generate</strong> then <strong>Play</strong> on the demo queue. Your visitors
                see nothing until a round is running — and a round is five competitions and then it
                stops, so start another whenever you want the demo available again.
              </li>
            </ol>

            <p style={{ margin: "0 0 8px", opacity: 0.75 }}>
              Your visitors can buy pixels and submit guesses, not just watch — with no account, no
              card, and no payment provider contacted at any point. Everything they see is demo data:
              it never appears on this site publicly, and nothing they do touches a real competition
              or real money.
            </p>
            <p style={{ margin: 0, opacity: 0.75 }}>
              The <strong>Integration Guide</strong>, on this tab under Integration, covers this in full
              alongside the two logged-in methods — including a worked example page you can copy.
            </p>
          </div>
        )}


        <ViewOnlyGate canEdit={canEdit} showNotice>

          <fieldset disabled={adminMode(modeStatus) === "demo"} style={{ border: "none", padding: 0, margin: 0, opacity: adminMode(modeStatus) === "demo" ? 0.55 : 1 }}>

            <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>

              <code style={{ flex: 1, minWidth: 220, fontSize: 12, wordBreak: "break-all", opacity: partnerDemoToken ? 0.85 : 0.5 }}>

                {partnerDemoToken || "Not set — demo sessions won't work until one is generated."}

              </code>

              {partnerDemoToken && (

                <button

                  type="button"

                  disabled={busy}

                  onClick={() => navigator.clipboard?.writeText(partnerDemoToken)}

                  title={adminMode(modeStatus) === "demo" ? "Leave Demo mode to manage this" : undefined}

                >

                  Copy

                </button>

              )}

              <button

                type="button"

                disabled={busy}

                title={adminMode(modeStatus) === "demo" ? "Leave Demo mode to manage this" : undefined}

                onClick={regenerateDemoToken}

              >

                {partnerDemoToken ? "Regenerate" : "Generate"}

              </button>

              {partnerDemoToken && (

                <button

                  type="button"

                  disabled={busy}

                  title={adminMode(modeStatus) === "demo" ? "Leave Demo mode to manage this" : undefined}

                  onClick={clearDemoToken}

                >

                  Clear

                </button>

              )}

            </div>

            {/* !== "live", not a bare truthiness test. This read
                `adminMode(modeStatus) &&`, which is true for ANY mode, so
                the notice showed in Live too — telling an operator to
                switch to the mode they were already in. Eight other
                panels guard the same message correctly.

                Worded around the literal phrase on purpose: a test scans
                this file for hardcoded mode names and does not know a
                JSX block comment from live markup. */}
            {adminMode(modeStatus) === "demo" && (

              <p style={{ opacity: 0.7, fontSize: 13, marginTop: 8 }}>

                Leave Demo mode to manage this — you're currently browsing in
                {modeLabel(adminMode(modeStatus))} mode.

              </p>

            )}

          </fieldset>

        </ViewOnlyGate>

      </div>

    </div>

  );

}



// For an operator running their own, separate instance of this whole

// site — root selects one real payment provider (or none, staying on

// this site's own mock purchase flow) and enters that provider's own

// credentials. A Partners sub-tab, but root-only unlike Payments

// beside it (manage_partners) — see admin.paymentProvider.* on the

// backend for why (real payment credentials, a bigger step). Live

// credentials and the active-provider selector are frozen while

// browsing in Demo mode, same reasoning and pattern as the partner

// key's own Live key management — editing what affects real money

// shouldn't be reachable from a mode meant to be a safe preview.

// canEdit added 18 Aug 2026, when this tab stopped being root-only and

// became grantable via manage_payment_providers. It had never needed a

// view-only mode before — root is the only role that can't be limited to

// one — so the whole body is wrapped in ViewOnlyGate below rather than

// each control being disabled individually.

function PaymentProviderPanel({ token, modeStatus, canEdit = true }) {

  const [config, setConfig] = useState(null);
  // Which mode the site is actually taking payments in, so the credential
  // set genuinely IN USE can be marked. "Cashflows is active" is not the
  // same statement as "these are the credentials a buyer's card goes
  // through right now" — the second needs the payment mode too.
  const [paymentMode, setPaymentMode] = useState(null);

  const [busy, setBusy] = useState(false);

  // Draft text fields, keyed "provider:mode:field" — cleared back to

  // blank on every successful save, same "blank means leave it alone"

  // convention the backend already applies to the secret fields.

  const [drafts, setDrafts] = useState({});



  const load = () => {

    wsRequest("admin.paymentProvider.get", {}, token)

      .then(setConfig)

      .catch(() => {});

    wsRequest("admin.siteSettings.private", {}, token)

      .then(setLiveInfo)

      .catch(() => {});

    // Failure here is not surfaced: without it the highlight simply does
    // not appear, which is a worse-looking panel and not a broken one.
    // Its own action rather than admin.siteSettings.private, which needs
    // manage_payment_providers — this panel HAS that, but borrowing an
    // action for a permission it happens to share is how two panels ended
    // up hanging on "Loading…" this week.
    wsRequest("admin.paymentMode.get", {}, token)

      .then((d) => setPaymentMode(d?.paymentMode || null))

      .catch(() => {});

  };

  useEffect(load, [token]);



  const draftKey = (provider, mode, field) => `${provider}:${mode}:${field}`;

  const getDraft = (provider, mode, field, fallback = "") => drafts[draftKey(provider, mode, field)] ?? fallback;

  const setDraft = (provider, mode, field, value) => setDrafts((d) => ({ ...d, [draftKey(provider, mode, field)]: value }));



  // paymentProviderActive is a single, global flag, not scoped per

  // test/live the way credentials are below — changing it always

  // affects the real, Live site's actual purchase flow, so it's always

  // frozen while browsing in Demo mode, not just when switching to a

  // real provider.

  const activeFrozen = (adminMode(modeStatus) !== "live");

  const frozenTitle = "Leave Demo mode to manage this";

  // USE THESE CREDENTIALS: sets the provider AND the payment mode in one
  // act, because a card IS a provider-and-mode pair. That pairing is what
  // the separate Payments tab chose between.
  //
  // PROVIDER FIRST, THEN MODE. The backend refuses a live switch it is not
  // ready for, so a failure leaves the provider changed and the mode
  // alone — the safe half. Going live with the wrong provider selected,
  // even for an instant, is the outcome worth avoiding.
  //
  // Every refusal stays in the backend: switching to test is refused while
  // a live competition has entries, and going live is refused when there
  // is nowhere for the money to land. Those surface as toasts rather than
  // being reimplemented here.
  const [confirmingLive, setConfirmingLive] = useState(null);

  // CARRIED OVER FROM THE PAYMENTS TAB, which this panel replaces: WHO
  // switched live payments on and WHEN, and WHY they cannot be switched on
  // when they cannot.
  //
  // The cards can only disable a button; neither of these facts is
  // visible from a card, and removing the tab without them would have
  // deleted information rather than a duplicate.
  const [liveInfo, setLiveInfo] = useState(null);

  const useCredentials = async (provider, mode) => {
    setBusy(true);
    try {
      if (activeProviderIn(config, mode) !== provider) {
        await wsRequest("admin.paymentProvider.setActive", { active: provider, mode }, token);
      }
      if (paymentMode !== mode) {
        await wsRequest("admin.siteSettings.update", { paymentMode: mode }, token);
      }
      showToast(
        mode === "live"
          ? "Live payments are ON — buyers are charged for real."
          : "Using test credentials — no real money moves."
      );
      setConfirmingLive(null);
      load();
    } catch (err) {
      showToast(err.message || "Could not reach backend.", "error");
      setConfirmingLive(null);
    }
    setBusy(false);
  };

  const setActive = async (active) => {

    setBusy(true);

    try {

      // WITHOUT A MODE THE SERVER DEFAULTS TO THE LIVE SLOT, which is
      // what this control has always meant: the provider that takes real
      // payments. It stays Live-only, and the server refuses it from
      // Test mode whatever this sends.
      await wsRequest("admin.paymentProvider.setActive", { active, mode: "live" }, token);

      load();

    } catch (err) {

      showToast(err.message || "Could not reach backend.", "error");

    }

    setBusy(false);

  };



  const saveCashflows = async (mode) => {

    setBusy(true);

    try {

      await wsRequest(

        "admin.paymentProvider.setCashflowsCredentials",

        { mode, configurationId: getDraft("cashflows", mode, "configurationId"), apiKey: getDraft("cashflows", mode, "apiKey") },

        token

      );

      setDrafts((d) => ({ ...d, [draftKey("cashflows", mode, "apiKey")]: "" }));

      load();

      showToast("Cashflows credentials saved.", "success");

    } catch (err) {

      showToast(err.message || "Could not reach backend.", "error");

    }

    setBusy(false);

  };



  const savePaytriot = async (mode) => {

    setBusy(true);

    try {

      await wsRequest(

        "admin.paymentProvider.setPaytriotCredentials",

        {

          mode,

          merchantId: getDraft("paytriot", mode, "merchantId"),

          merchantCountryCode: getDraft("paytriot", mode, "merchantCountryCode"),

          signatureKey: getDraft("paytriot", mode, "signatureKey"),

        },

        token

      );

      setDrafts((d) => ({ ...d, [draftKey("paytriot", mode, "signatureKey")]: "" }));

      load();

      showToast("Paytriot credentials saved.", "success");

    } catch (err) {

      showToast(err.message || "Could not reach backend.", "error");

    }

    setBusy(false);

  };



  if (config === null) return <p>Loading…</p>;



  // "TEST CREDENTIALS" AND "LIVE CREDENTIALS", not "Test" and "Live".
  //
  // Those bare words are also the admin's own mode switcher — the queue
  // you are browsing — and the two are unrelated: a card sets which
  // credentials take the money, and never changes which mode you are in.
  //
  // The overlap is easy to miss because a Live card IS frozen unless the
  // admin is in Live mode, which ties them together on screen while they
  // mean different things. Reported 6 Sep, by somebody reading "switches
  // the mode" and asking which mode.
  const modes = [

    { id: "test", label: "Test credentials" },

    { id: "live", label: "Live credentials" },

  ];



  return (

    <ViewOnlyGate canEdit={canEdit} showNotice>

    <div style={{ marginTop: 16, maxWidth: 640 }}>

      <h3>Payment provider</h3>

      {/* WHO SWITCHED LIVE PAYMENTS ON, AND WHEN. The acknowledgement
          exists to leave a record; a record nobody can see is not one. */}
      {liveInfo?.paymentMode === "live" && liveInfo?.paymentModeLiveBy && (
        <div style={{ fontSize: 12, opacity: 0.75, marginBottom: 10 }}>
          Live payments switched on by <strong>{liveInfo.paymentModeLiveBy}</strong>
          {liveInfo.paymentModeLiveSince
            ? ` on ${siteDateTime(liveInfo.paymentModeLiveSince)}`
            : ""}
          .
        </div>
      )}

      {/* WHY LIVE CANNOT BE TURNED ON, said before a button is pressed.
          A refusal that only appears on click is one people work around
          rather than fix. The cards can only grey a button out; this says
          what is actually missing. */}
      {liveInfo?.paymentMode !== "live" && liveInfo?.canGoLive && !liveInfo.canGoLive.ok && (
        <div
          style={{
            marginBottom: 12,
            padding: "10px 12px",
            border: "1px solid var(--accent-red)",
            borderRadius: 8,
            fontSize: 13,
          }}
        >
          <strong>Live payments cannot be switched on yet.</strong>
          <ul style={{ margin: "6px 0 0", paddingLeft: 18 }}>
            {liveInfo.canGoLive.problems.map((problem, n) => (
              <li key={n}>{problem}</li>
            ))}
          </ul>
        </div>
      )}

      {/* CONFIRMING A LIVE SWITCH — a modal, so the choice is the only
          thing on screen. Going live charges real people; a panel further
          down the page can be scrolled past, and this cannot.

          Same overlay shape as PhoneVerificationModal, which is the
          house pattern for a decision that has to be answered. */}
      {confirmingLive && (
        <div
          role="dialog"
          aria-modal="true"
          onClick={() => !busy && setConfirmingLive(null)}
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 100,
            background: "var(--overlay-color)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 20,
            boxSizing: "border-box",
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: "var(--bg-panel-alt)",
              padding: 28,
              borderRadius: 10,
              border: "2px solid #d9534f",
              maxWidth: 460,
              width: "100%",
              boxSizing: "border-box",
            }}
          >
            <h3 style={{ marginTop: 0 }}>Turn on live payments?</h3>
            <p style={{ fontSize: 13.5, lineHeight: 1.5 }}>
              From the moment this is on, buyers are charged for real through{" "}
              <strong>
                {confirmingLive.provider === "cashflows" ? "Cashflows" : "Paytriot"}
              </strong>{" "}
              live credentials. Your account and the time are recorded against it.
            </p>
            <div style={{ marginTop: 16, display: "flex", gap: 8 }}>
              <button
                type="button"
                disabled={busy}
                onClick={() => useCredentials(confirmingLive.provider, confirmingLive.mode)}
              >
                {busy ? "Switching…" : "Yes — take real payments"}
              </button>
              <button type="button" disabled={busy} onClick={() => setConfirmingLive(null)}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      <div style={{ fontSize: 12.5, opacity: 0.75, border: "1px solid var(--border-color)", borderRadius: 6, padding: "8px 10px", marginBottom: 14 }}>

        <strong>What this charges:</strong> every purchase this instance takes payment for — on this site, in the

        widget, and through the API's hosted checkout. These are the partner's own credentials; there is no other

        set.

      </div>

      <p style={{ opacity: 0.7, fontSize: 13, marginTop: -6 }}>

        For accepting real payments directly on this instance. Off by default — this site's own mock purchase flow

        applies until a provider is selected below and its credentials are entered.

      </p>



      <h4 style={{ marginBottom: 4 }}>No provider</h4>
      <p style={{ opacity: 0.7, fontSize: 12.5, marginTop: 0 }}>
        This site's own mock flow. A purchase completes without any payment provider
        being involved — no money moves and nothing leaves the site.
      </p>
      {/* A CARD, like the providers, so everything is chosen the same way.
          It was a dropdown option, and the dropdown was the only route back
          to the mock flow — a provider could be turned on and never off.
          Removed 6 Sep 2026 in favour of this. */}
      <div
        style={{
          border: `1px solid ${!activeProviderIn(config, paymentMode) ? "var(--accent-green, #5ed46a)" : "var(--border-color)"}`,
          background: !activeProviderIn(config, paymentMode) ? "rgba(94,212,106,0.10)" : undefined,
          borderRadius: 8,
          padding: 14,
          marginBottom: 12,
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <strong>Mock flow</strong>
          {/* PER MODE. `active` is an object since 18 Sep 2026, so testing it
               for truthiness is always false and this block — the one that
               says the site is on the mock flow — would never appear. */}
          {!activeProviderIn(config, paymentMode) ? (
            /* SAYS WHETHER THIS WAS CHOSEN OR SIMPLY NEVER SET.
               Skipping the installer's payment step leaves active as null,
               which IS the mock flow — but "In use now" reads as a decision
               somebody made, and skipping is what you do to avoid making
               one. If no provider has credentials at all, nothing has been
               configured yet; if some has and active is still null, the
               mock flow was picked. Reported 7 Sep. */
            <span style={{ color: "var(--accent-green, #5ed46a)", fontSize: 12.5 }}>
              {config.cashflows?.test?.hasKey ||
              config.cashflows?.live?.hasKey ||
              config.paytriot?.test?.hasSignatureKey ||
              config.paytriot?.live?.hasSignatureKey
                ? "In use now"
                : "In use — no payment provider configured yet"}
            </span>
          ) : (
            <button
              type="button"
              disabled={busy || !canEdit || activeFrozen}
              title={activeFrozen ? frozenTitle : undefined}
              onClick={() => setActive(null)}
            >
              Use this
            </button>
          )}
        </div>
      </div>

      



      <h4 style={{ marginBottom: 4 }}>Cashflows</h4>

      <p style={{ opacity: 0.7, fontSize: 12.5, marginTop: 0 }}>Configuration ID and API key, from Cashflows Go — separate for test and live.</p>

      {modes.map((m) => {

        const creds = config.cashflows[m.id];

        const frozen = m.id === "live" && (adminMode(modeStatus) !== "live");

        // THE SET ACTUALLY IN USE: this provider is the active one AND
        // this is the mode the site is taking payments in. Both have to
        // match — "Cashflows is active" is not the same as "these are
        // the credentials a buyer's card will go through right now".
        const inUse = activeProviderIn(config, m.id) === "cashflows";

        // THE SAME GUARDS THE CARD ALREADY SHOWS: a live card is frozen
        // unless the admin is in Live mode, and credentials that are not
        // there cannot take a payment. hasKey means "configured" for both
        // providers, whatever fields each one needs.
        // CONFIGURED means different fields for each provider. Paytriot has
        // no hasKey at all — it needs a merchant id, a country code and a
        // signature key. Reading hasKey for it is always false.
        const configured = !!creds.hasKey;
        const cannotUse = frozen || !configured || inUse || busy || !canEdit;
        const useButton = inUse ? (
          <span style={{ color: "var(--accent-green, #5ed46a)", fontSize: 12.5 }}>In use now</span>
        ) : (
          <button
            type="button"
            disabled={cannotUse}
            title={frozen ? frozenTitle : !configured ? "Add credentials first" : undefined}
            onClick={() => (m.id === "live" ? setConfirmingLive({ provider: "cashflows", mode: m.id }) : useCredentials("cashflows", m.id))}
          >
            Use these
          </button>
        );

        return (

          <div key={m.id} style={{ border: `1px solid ${inUse ? "var(--accent-green, #5ed46a)" : "var(--border-color)"}`, background: inUse ? "rgba(94,212,106,0.10)" : undefined, borderRadius: 8, padding: 14, marginBottom: 12, opacity: frozen ? 0.55 : 1 }}>

            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>

              <strong>{m.label}</strong>

              {useButton}

              {creds.hasKey && <span style={{ opacity: 0.6, fontSize: 12.5 }}>✓ API key set</span>}

            </div>

            <label style={{ display: "block", fontSize: 12.5, marginBottom: 3 }}>Configuration ID</label>

            <input

              type="text"

              value={getDraft("cashflows", m.id, "configurationId", creds.configurationId)}

              disabled={busy || frozen}

              title={frozen ? frozenTitle : undefined}

              onChange={(e) => setDraft("cashflows", m.id, "configurationId", e.target.value)}

              style={{ width: "100%", padding: "6px 8px", fontSize: 13, marginBottom: 8, boxSizing: "border-box" }}

            />

            <label style={{ display: "block", fontSize: 12.5, marginBottom: 3 }}>API key {creds.hasKey && "(leave blank to keep the current one)"}</label>

            <input

              type="password"

              value={getDraft("cashflows", m.id, "apiKey")}

              disabled={busy || frozen}

              title={frozen ? frozenTitle : undefined}

              onChange={(e) => setDraft("cashflows", m.id, "apiKey", e.target.value)}

              style={{ width: "100%", padding: "6px 8px", fontSize: 13, marginBottom: 10, boxSizing: "border-box" }}

            />

            <button className="btn" disabled={busy || frozen} title={frozen ? frozenTitle : undefined} onClick={() => saveCashflows(m.id)}>

              Save {m.label} credentials

            </button>

            {/* Tests exactly the credentials shown above it, which is
                what somebody filling this box in wants to know. The
                single button under Going Live could only test whichever
                provider was ACTIVE, and only its test credentials. */}
            <TestConfigurationBox
              token={token}
              wsRequest={wsRequest}
              provider="cashflows"
              mode={m.id}
              disabled={!canEdit || frozen}
              disabledReason={frozen ? frozenTitle : undefined}
              hasCredentials={!!creds.hasKey}
            />

          </div>

        );

      })}



      <h4 style={{ marginBottom: 4, marginTop: 24 }}>Paytriot</h4>

      <p style={{ opacity: 0.7, fontSize: 12.5, marginTop: 0, marginBottom: 10 }}>

        <strong>Not yet verified against a real sandbox</strong> — what's publicly documented was thin and

        inconsistent enough not to trust with real money without testing it directly first. Get current integration

        docs from Paytriot's own onboarding team before selecting this as the active provider. See the Going Live

        doc.

      </p>

      {modes.map((m) => {

        const creds = config.paytriot[m.id];

        const frozen = m.id === "live" && (adminMode(modeStatus) !== "live");

        // THE SET ACTUALLY IN USE: this provider is the active one AND
        // this is the mode the site is taking payments in. Both have to
        // match — "Cashflows is active" is not the same as "these are
        // the credentials a buyer's card will go through right now".
        const inUse = activeProviderIn(config, m.id) === "paytriot";

        // THE SAME GUARDS THE CARD ALREADY SHOWS: a live card is frozen
        // unless the admin is in Live mode, and credentials that are not
        // there cannot take a payment. hasKey means "configured" for both
        // providers, whatever fields each one needs.
        // CONFIGURED means different fields for each provider. Paytriot has
        // no hasKey at all — it needs a merchant id, a country code and a
        // signature key. Reading hasKey for it is always false.
        const configured =
          !!creds.hasSignatureKey && !!creds.merchantId && !!creds.merchantCountryCode;
        const cannotUse = frozen || !configured || inUse || busy || !canEdit;
        const useButton = inUse ? (
          <span style={{ color: "var(--accent-green, #5ed46a)", fontSize: 12.5 }}>In use now</span>
        ) : (
          <button
            type="button"
            disabled={cannotUse}
            title={frozen ? frozenTitle : !configured ? "Add credentials first" : undefined}
            onClick={() => (m.id === "live" ? setConfirmingLive({ provider: "paytriot", mode: m.id }) : useCredentials("paytriot", m.id))}
          >
            Use these
          </button>
        );

        return (

          <div key={m.id} style={{ border: `1px solid ${inUse ? "var(--accent-green, #5ed46a)" : "var(--border-color)"}`, background: inUse ? "rgba(94,212,106,0.10)" : undefined, borderRadius: 8, padding: 14, marginBottom: 12, opacity: frozen ? 0.55 : 1 }}>

            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>

              <strong>{m.label}</strong>

              {useButton}

              {creds.hasSignatureKey && <span style={{ opacity: 0.6, fontSize: 12.5 }}>✓ Signature key set</span>}

            </div>

            <label style={{ display: "block", fontSize: 12.5, marginBottom: 3 }}>Merchant ID</label>

            <input

              type="text"

              value={getDraft("paytriot", m.id, "merchantId", creds.merchantId)}

              disabled={busy || frozen}

              title={frozen ? frozenTitle : undefined}

              onChange={(e) => setDraft("paytriot", m.id, "merchantId", e.target.value)}

              style={{ width: "100%", padding: "6px 8px", fontSize: 13, marginBottom: 8, boxSizing: "border-box" }}

            />

            <label style={{ display: "block", fontSize: 12.5, marginBottom: 3 }}>Merchant country code</label>

            <input

              type="text"

              value={getDraft("paytriot", m.id, "merchantCountryCode", creds.merchantCountryCode)}

              disabled={busy || frozen}

              title={frozen ? frozenTitle : undefined}

              onChange={(e) => setDraft("paytriot", m.id, "merchantCountryCode", e.target.value)}

              style={{ width: "100%", padding: "6px 8px", fontSize: 13, marginBottom: 8, boxSizing: "border-box" }}

            />

            <label style={{ display: "block", fontSize: 12.5, marginBottom: 3 }}>

              Signature key {creds.hasSignatureKey && "(leave blank to keep the current one)"}

            </label>

            <input

              type="password"

              value={getDraft("paytriot", m.id, "signatureKey")}

              disabled={busy || frozen}

              title={frozen ? frozenTitle : undefined}

              onChange={(e) => setDraft("paytriot", m.id, "signatureKey", e.target.value)}

              style={{ width: "100%", padding: "6px 8px", fontSize: 13, marginBottom: 10, boxSizing: "border-box" }}

            />

            <button className="btn" disabled={busy || frozen} title={frozen ? frozenTitle : undefined} onClick={() => savePaytriot(m.id)}>

              Save {m.label} credentials

            </button>

            {/* Tests exactly the credentials shown above it, which is
                what somebody filling this box in wants to know. The
                single button under Going Live could only test whichever
                provider was ACTIVE, and only its test credentials. */}
            <TestConfigurationBox
              token={token}
              wsRequest={wsRequest}
              provider="paytriot"
              mode={m.id}
              disabled={!canEdit || frozen}
              disabledReason={frozen ? frozenTitle : undefined}
              hasCredentials={!!creds.hasKey}
            />

          </div>

        );

      })}

    </div>

    </ViewOnlyGate>

  );

}

// A prospective-partner demo tool — generates the 5 demo

// competitions, runs them via the existing simulation, and once all 5

// have completed, produces a shareable, public replay link. See the

// backend's own admin.demoComps.* actions and demoReplayLinks comment

// for the full reasoning.

// maxHeight and onClose arrive when this opens from the History button on
// the Queue Control panel — its only home since 29 Aug 2026, when the
// top-level Demo Comps tab was removed as duplicated navigation. Absent
// anywhere else, and the panel then renders exactly as it always did.
function DemoCompsPanel({ token, modeStatus, canEdit, maxHeight, onClose }) {

  const [status, setStatus] = useState(null);

  const [busy, setBusy] = useState(false);

  const [expiryAmount, setExpiryAmount] = useState(3);

  const [expiryUnit, setExpiryUnit] = useState("days");

  // Per-link extend inputs, keyed by link id — one shared pair of

  // fields would apply whatever was last typed to whichever row was

  // clicked, which is exactly the kind of thing nobody notices until

  // they've extended the wrong link.

  const [extendBy, setExtendBy] = useState({});

  // Drives the countdown below. A single interval for the whole panel

  // rather than one per row, since every countdown is derived from the

  // same clock.

  // The SERVER's clock: it decides when a link has expired, and a

  // browser a few seconds adrift would show one as live after the

  // server had stopped honouring it.

  const [now, setNow] = useState(serverNow());

  useEffect(() => {

    const id = setInterval(() => setNow(serverNow()), 1000);

    return () => clearInterval(id);

  }, []);



  const load = () => {

    wsRequest("admin.demoComps.status", {}, token)

      .then(setStatus)

      .catch(() => {});

  };

  useEffect(load, [token]);



  // Real-time refresh for visit counts — a replay link can be visited

  // by anyone, at any time, from a tab that isn't this admin panel at

  // all, so there's no local action here to refresh off of. Matches

  // the same broadcast-then-refetch pattern used everywhere else in

  // this file rather than polling.

  useEffect(() => onBroadcast((message) => {

    if (message.type === "demo-replay-links-changed") load();

  }), [token]);



  // Live-polls while a batch is genuinely in progress (generated but

  // not yet all 5 complete) — this can take a few real minutes, since

  // the simulation drives mock buyers/guessers at a real-time pace,

  // not instantly. Stops polling once nothing is left to wait for.

  useEffect(() => {

    if (!status) return;

    const inProgress = status.competitionCount > 0 && !status.allComplete;

    if (!inProgress) return;

    const id = setInterval(load, 4000);

    return () => clearInterval(id);

    // eslint-disable-next-line react-hooks/exhaustive-deps

  }, [status?.competitionCount, status?.allComplete]);
  // THIS PANEL USED TO DISABLE ITSELF UNLESS THE OPERATOR WAS IN DEMO
  // MODE, on the grounds that it followed "the same convention every
  // other mock-data-generating action in this app already follows".
  //
  // TWO THINGS WERE WRONG WITH THAT. The convention changed on 11 Sep
  // 2026 when the demo became one button runnable from any mode — and
  // the backend never enforced it here anyway: admin.demoComps.* check
  // manage_demo_comps and nothing else. The panel was greying out work
  // the server would have done.



  const start = async () => {

    setBusy(true);

    try {

      await wsRequest("admin.demoComps.start", {}, token);

      load();

    } catch (err) {

      showToast(err.message || "Could not reach backend.", "error");

    }

    setBusy(false);

  };



  const generateLink = async () => {

    setBusy(true);

    try {

      await wsRequest("admin.demoComps.generateLink", { amount: expiryAmount, unit: expiryUnit }, token);

      load();

      showToast("Replay link created.", "success");

    } catch (err) {

      showToast(err.message || "Could not reach backend.", "error");

    }

    setBusy(false);

  };



  const extendLink = async (id) => {

    const entry = extendBy[id] || { amount: 1, unit: "days" };

    setBusy(true);

    try {

      await wsRequest("admin.demoComps.extendLink", { id, amount: Number(entry.amount), unit: entry.unit }, token);

      load();

      showToast("Expiry extended.", "success");

    } catch (err) {

      showToast(err.message || "Could not reach backend.", "error");

    }

    setBusy(false);

  };



  const deleteLink = async (id) => {

    setBusy(true);

    try {

      await wsRequest("admin.demoComps.deleteLink", { id }, token);

      load();

    } catch (err) {

      showToast(err.message || "Could not reach backend.", "error");

    }

    setBusy(false);

  };



  const copyLink = (link) => {

    const url = `${window.location.origin}/demo-comps/${link.token}`;

    navigator.clipboard?.writeText(url);

    showToast("Link copied.");

  };



  if (status === null) return <p>Loading…</p>;



  const inProgress = status.competitionCount > 0 && !status.allComplete;



  return (

    <div style={{
      marginTop: onClose ? 0 : 16,
      maxWidth: 640,
      // Beside the queue panel: fill the space, cap at its height, and
      // scroll internally rather than making the row taller. Only when
      // opened from History — rendered anywhere else it is unchanged.
      ...(onClose ? {
        flex: "1 1 380px",
        minHeight: 0,
          // WITHOUT THIS IT CANNOT SHRINK. A flex item defaults to
          // min-width: auto, which is its content's minimum — so this
          // panel refused to go below its natural width and overflowed
          // the column instead, across the competition tabs.
          minWidth: 0,
        maxHeight: maxHeight || undefined,
        overflowY: "auto",
        border: "1px solid var(--border-color)",
        borderRadius: 8,
        padding: "12px 14px",
      } : {}),
    }}>

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <h3 style={{ marginTop: 0 }}>Demo Comps</h3>
        {onClose && (
          <button type="button" onClick={onClose}>
            Close
          </button>
        )}
      </div>

      <p style={{ opacity: 0.7, fontSize: 13, marginTop: -6 }}>

        A quick way to show a prospective partner how competitions actually work — generates 5 demo competitions,

        runs them via the simulation, then produces a shareable, public replay link showing everything that

        happened (purchases, guesses, and the countdown/email-capture screen between competitions) at a chosen

        speed.

      </p>



      <ViewOnlyGate canEdit={canEdit} showNotice>


        <fieldset style={{ border: "none", padding: 0, margin: 0 }}>

          <div style={{ border: "1px solid var(--border-color)", borderRadius: 8, padding: 14, marginBottom: 16 }}>

            {status.competitionCount === 0 && (

              <>

                <p style={{ fontSize: 13, opacity: 0.8, marginTop: 0 }}>No demo round has been generated yet.</p>

                <button className="btn" disabled={busy} onClick={start}>

                  {busy ? "Starting…" : "Start"}

                </button>

              </>

            )}

            {inProgress && (

              <>

                <p style={{ fontSize: 13, opacity: 0.8, marginTop: 0 }}>

                  Running — competitions are being played out by the simulation. This can take a few real minutes.

                </p>

                <button className="btn" disabled>

                  Running…

                </button>

              </>

            )}

            {status.allComplete && (

              <>

                {/* A round can complete with NOTHING having happened —
                    with the simulation paused nothing is bought and
                    nothing is guessed, so every competition ends on its
                    clock as a lottery. That used to look identical to a
                    good round: five competitions, all resolved, green
                    tick, and a replay of five competitions where no one
                    did anything. */}
                {status.ranEmpty ? (
                  <p style={{ fontSize: 13, color: "var(--accent-yellow)", marginTop: 0 }}>
                    All 5 completed, but <strong>nothing happened in them</strong> — no pixels
                    were bought and no guesses were made, so every one ended on its clock as a
                    lottery. The simulation was not running. A replay of this round will show
                    five competitions with no activity.
                  </p>
                ) : (
                  <p style={{ fontSize: 13, color: "var(--accent-green)", marginTop: 0 }}>✓ All 5 demo competitions have completed.</p>
                )}

                <p style={{ fontSize: 12, opacity: 0.7, marginTop: -4 }}>

                  A replay link was created automatically when this round finished, expiring in 60 minutes — extend

                  it below to keep it. Every link is deleted, along with its stored copy of the round, once it

                  expires.

                </p>

                {/* ONLY WHEN THERE IS NO LINK. A round that has just finished
                    already has one, created automatically and extendable below,
                    so offering to generate another is a second way to do a thing
                    already done.
                
                    NOT REMOVED ALTOGETHER, because an auto link is swept after
                    its hour and the round marker stops it being recreated — so
                    for a completed round whose link has lapsed, this is the only
                    way back to it. */}
                {status.links.length === 0 && (
                  <>
                  <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 10 }}>
  
                    <span style={{ fontSize: 13 }}>Link expires in</span>
  
                    <input
  
                      type="number"
  
                      min="1"
  
                      value={expiryAmount}
  
                      disabled={busy}
  
                      onChange={(e) => setExpiryAmount(e.target.value)}
  
                      style={{ width: 60, padding: "6px 8px", fontSize: 13 }}
  
                    />
  
                    <select value={expiryUnit} disabled={busy} onChange={(e) => setExpiryUnit(e.target.value)} style={{ padding: "6px 8px", fontSize: 13 }}>
  
                      <option value="hours">Hours</option>
  
                      <option value="days">Days</option>
  
                      <option value="weeks">Weeks</option>
  
                    </select>
  
                  </div>
  
                  <button className="btn" disabled={busy} onClick={generateLink}>
  
                    Generate link
  
                  </button>
                  </>
                )}

                <p style={{ fontSize: 12, opacity: 0.6, marginTop: 10, marginBottom: 0 }}>

                  To start a fresh round, press Generate on the Demo competition queue — it wipes the old
                  demo and builds a new one in a single step.

                </p>

              </>

            )}

          </div>



          {status.links.length > 0 && (

            <div>

              <h4 style={{ marginBottom: 8 }}>Replay links</h4>

              {status.links.map((link) => (

                <div

                  key={link.id}

                  style={{ border: "1px solid var(--border-color)", borderRadius: 8, padding: 10, marginBottom: 8 }}

                >

                  <div style={{ display: "flex", gap: 8, alignItems: "center" }}>

                    <code style={{ flex: 1, fontSize: 12, wordBreak: "break-all", opacity: 0.85 }}>

                      {window.location.origin}/demo-comps/{link.token}

                    </code>

                    <span

                      style={{ fontSize: 12, whiteSpace: "nowrap", opacity: 0.75, color: link.expiresAt - now < 5 * 60 * 1000 ? "var(--accent-yellow)" : undefined }}

                      title={`Expires ${siteDateTime(link.expiresAt)}`}

                    >

                      {formatLongCountdown(link.expiresAt - now)} left

                    </span>

                    <button onClick={() => copyLink(link)}>Copy</button>

                    <button onClick={() => deleteLink(link.id)} disabled={busy}>

                      Delete

                    </button>

                  </div>

                  <div style={{ display: "flex", gap: 6, alignItems: "center", marginTop: 8, flexWrap: "wrap" }}>

                    <span style={{ fontSize: 12, opacity: 0.6 }}>Extend by</span>

                    <input

                      type="number"

                      min="1"

                      value={(extendBy[link.id] || {}).amount ?? 1}

                      disabled={busy}

                      onChange={(e) => setExtendBy((prev) => ({ ...prev, [link.id]: { unit: "days", ...(prev[link.id] || {}), amount: e.target.value } }))}

                      style={{ width: 56, padding: "4px 6px", fontSize: 12 }}

                    />

                    <select

                      value={(extendBy[link.id] || {}).unit || "days"}

                      disabled={busy}

                      onChange={(e) => setExtendBy((prev) => ({ ...prev, [link.id]: { amount: 1, ...(prev[link.id] || {}), unit: e.target.value } }))}

                      style={{ padding: "4px 6px", fontSize: 12 }}

                    >

                      <option value="hours">Hours</option>

                      <option value="days">Days</option>

                      <option value="weeks">Weeks</option>

                    </select>

                    <button onClick={() => extendLink(link.id)} disabled={busy} style={{ fontSize: 12 }}>

                      Extend

                    </button>

                    {link.auto && (

                      <span style={{ fontSize: 12, opacity: 0.6 }} title="Created automatically when this round completed. Extending it makes it an ordinary link.">

                        · created automatically

                      </span>

                    )}

                  </div>

                  <div style={{ fontSize: 12, opacity: 0.6, marginTop: 6 }}>

                    {link.visits.total} visit{link.visits.total === 1 ? "" : "s"}

                    {link.visits.total > 0 &&

                      ` · ${Object.entries(link.visits.byBrowser)

                        .sort((a, b) => b[1] - a[1])

                        .map(([browser, count]) => `${browser} ${count}`)

                        .join(", ")}`}

                  </div>

                </div>

              ))}

            </div>

          )}

        </fieldset>

      </ViewOnlyGate>

    </div>

  );

}



// Each provider has a genuinely different credential shape (unlike

// the email providers, which all share the same fromAddress/fromName/

// apiKey shape) — Twilio needs an Account SID alongside its Auth

// Token, Vonage needs an API Key and Secret, MessageBird needs just

// one Access Key. Field specs drive both the rendered inputs and the

// save payload, rather than a generic loop assuming a uniform shape.

const SMS_PROVIDERS = [

  {

    id: "twilio",

    label: "Twilio",

    action: "admin.smsProvider.setTwilioCredentials",

    fields: [

      { key: "accountSid", label: "Account SID", type: "text", hasFlag: "hasAccountSid" },

      { key: "authToken", label: "Auth Token", type: "password", hasFlag: "hasKey" },

    ],

  },

  {

    id: "vonage",

    label: "Vonage",

    action: "admin.smsProvider.setVonageCredentials",

    fields: [

      { key: "apiKey", label: "API Key", type: "text", hasFlag: "hasKey" },

      { key: "apiSecret", label: "API Secret", type: "password", hasFlag: "hasSecret" },

    ],

  },

  {

    id: "messagebird",

    label: "MessageBird",

    action: "admin.smsProvider.setMessagebirdCredentials",

    fields: [{ key: "apiKey", label: "Access Key", type: "password", hasFlag: "hasKey" }],

  },

  {

    id: "mock",

    label: "Mock (testing — no real SMS sent)",

    action: "admin.smsProvider.setMockCredentials",

    fields: [{ key: "apiKey", label: "Test key (any value works)", type: "text", hasFlag: "hasKey", placeholder: "test_key_123" }],

  },

];



function SmsProviderPanel({ token, modeStatus, canEdit }) {

  const [config, setConfig] = useState(null);

  const [busy, setBusy] = useState(false);

  // Draft text fields, keyed "provider:field" — same "blank means

  // leave it alone" convention the backend applies to every secret

  // field, cleared back to blank on every successful save.

  const [drafts, setDrafts] = useState({});

  const [testRecipient, setTestRecipient] = useState("");



  const load = () => {

    wsRequest("admin.smsProvider.get", {}, token)

      .then(setConfig)

      .catch(() => {});

  };

  useEffect(load, [token]);



  const draftKey = (provider, field) => `${provider}:${field}`;

  const getDraft = (provider, field, fallback = "") => drafts[draftKey(provider, field)] ?? fallback;

  const setDraft = (provider, field, value) => setDrafts((d) => ({ ...d, [draftKey(provider, field)]: value }));



  // No test/live split, same reasoning as the email providers — every

  // mutating action always requires Live mode.

  // DEMO ONLY. These are settings, and the server agrees — see
  // assertSettingsEditable. Freezing them in Test mode meant switching
  // to Live to save a form and back again to carry on testing.
  const frozen = adminMode(modeStatus) === "demo";

  const frozenTitle = "Leave Demo mode to manage this";



  const setActive = async (active) => {

    setBusy(true);

    try {

      await wsRequest("admin.smsProvider.setActive", { active }, token);

      load();

    } catch (err) {

      showToast(err.message || "Could not reach backend.", "error");

    }

    setBusy(false);

  };



  const saveCredentials = async (provider) => {

    setBusy(true);

    try {

      const payload = { fromNumber: getDraft(provider.id, "fromNumber", config[provider.id].fromNumber) };

      for (const f of provider.fields) payload[f.key] = getDraft(provider.id, f.key);

      await wsRequest(provider.action, payload, token);

      setDrafts((d) => {

        const next = { ...d };

        for (const f of provider.fields) delete next[draftKey(provider.id, f.key)];

        return next;

      });

      load();

      showToast(`${provider.label} credentials saved.`, "success");

    } catch (err) {

      showToast(err.message || "Could not reach backend.", "error");

    }

    setBusy(false);

  };



  const sendTest = async () => {

    setBusy(true);

    try {

      await wsRequest("admin.smsProvider.sendTest", { to: testRecipient }, token);

      showToast(config.active === "mock" ? "Mock send succeeded — no real text was sent." : "Test message sent.", "success");

    } catch (err) {

      showToast(err.message || "Could not reach backend.", "error");

    }

    setBusy(false);

  };



  const setPhoneVerificationEnabled = async (enabled) => {

    setBusy(true);

    try {

      await wsRequest("admin.smsProvider.setPhoneVerificationEnabled", { enabled }, token);

      load();

    } catch (err) {

      showToast(err.message || "Could not reach backend.", "error");

    }

    setBusy(false);

  };



  if (config === null) return <p>Loading…</p>;



  return (

    <div style={{ marginTop: 16, maxWidth: 640 }}>

      <h3>SMS provider</h3>

      <div style={{ fontSize: 12.5, opacity: 0.75, border: "1px solid var(--border-color)", borderRadius: 6, padding: "8px 10px", marginBottom: 14 }}>

        <strong>Who this sends to:</strong> everybody this instance texts — users who signed up here and users who

        came through the partner's site alike.

      </div>

      <p style={{ opacity: 0.7, fontSize: 13, marginTop: -6 }}>

        For phone verification before prize payout — a 6-digit code texted to the number a user provides, entered

        back to confirm. Off by default, and nothing on this site sends real texts until a provider is selected

        and its credentials are entered.

      </p>



      <ViewOnlyGate canEdit={canEdit} showNotice>

        <div style={{ marginBottom: 20 }}>

          <label style={{ display: "block", fontSize: 13, marginBottom: 6 }}>

            Active provider {frozen && <span style={{ opacity: 0.6, fontWeight: 400 }}>— switch to Live mode to change this</span>}

          </label>

          <select

            value={config.active || ""}

            disabled={busy || frozen}

            title={frozen ? frozenTitle : undefined}

            onChange={(e) => setActive(e.target.value || null)}

            style={{ padding: "6px 10px", fontSize: 14, opacity: frozen ? 0.55 : 1 }}

          >

            <option value="">None — nothing sends real texts</option>

            {SMS_PROVIDERS.map((p) => (

              <option key={p.id} value={p.id}>

                {p.label}

              </option>

            ))}

          </select>

          {config.active === "mock" && (

            <p style={{ color: "var(--accent-yellow)", fontSize: 12.5, marginTop: 8, marginBottom: 0 }}>

              ⚠️ Mock provider active — no real texts are sent, and phone verification isn't actually providing

              any protection right now. For testing only; switch to a real provider before relying on this.

            </p>

          )}

        </div>



        {SMS_PROVIDERS.map((p) => {

          const creds = config[p.id];

          return (

            <div key={p.id} style={{ border: "1px solid var(--border-color)", borderRadius: 8, padding: 14, marginBottom: 12, opacity: frozen ? 0.55 : 1 }}>

              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>

                <strong>{p.label}</strong>

                {p.fields.every((f) => creds[f.hasFlag]) && <span style={{ opacity: 0.6, fontSize: 12.5 }}>✓ credentials set</span>}

              </div>

              <label style={{ display: "block", fontSize: 12.5, marginBottom: 3 }}>

                {p.id === "messagebird" ? "Originator (number or up to 11 alphanumeric characters)" : "From number (E.164, e.g. +14155551234)"}

              </label>

              <input

                type="text"

                value={getDraft(p.id, "fromNumber", creds.fromNumber)}

                disabled={busy || frozen}

                title={frozen ? frozenTitle : undefined}

                onChange={(e) => setDraft(p.id, "fromNumber", e.target.value)}

                style={{ width: "100%", padding: "6px 8px", fontSize: 13, marginBottom: 8, boxSizing: "border-box" }}

              />

              {p.fields.map((f) => (

                <React.Fragment key={f.key}>

                  <label style={{ display: "block", fontSize: 12.5, marginBottom: 3 }}>

                    {f.label} {creds[f.hasFlag] && "(leave blank to keep the current one)"}

                  </label>

                  <input

                    type={f.type}

                    value={getDraft(p.id, f.key)}

                    placeholder={f.placeholder}

                    disabled={busy || frozen}

                    title={frozen ? frozenTitle : undefined}

                    onChange={(e) => setDraft(p.id, f.key, e.target.value)}

                    style={{ width: "100%", padding: "6px 8px", fontSize: 13, marginBottom: 8, boxSizing: "border-box" }}

                  />

                </React.Fragment>

              ))}

              <button className="btn" disabled={busy || frozen} title={frozen ? frozenTitle : undefined} onClick={() => saveCredentials(p)} style={{ marginTop: 2 }}>

                Save {p.label} credentials

              </button>

            </div>

          );

        })}



        <div style={{ marginTop: 20, paddingTop: 16, borderTop: "1px solid var(--border-color)" }}>

          <h4 style={{ marginBottom: 4 }}>Send a test message</h4>

          <p style={{ opacity: 0.7, fontSize: 12.5, marginTop: 0, marginBottom: 10 }}>

            The way to confirm the active provider's credentials actually work, before relying on them for real

            phone verification.

          </p>

          <div style={{ display: "flex", gap: 8 }}>

            <input

              type="text"

              placeholder="+14155551234"

              value={testRecipient}

              disabled={busy || frozen || !config.active}

              title={frozen ? frozenTitle : !config.active ? "Select an active provider first" : undefined}

              onChange={(e) => setTestRecipient(e.target.value)}

              style={{ flex: 1, padding: "6px 8px", fontSize: 13 }}

            />

            <button

              className="btn"

              disabled={busy || frozen || !config.active || !testRecipient.startsWith("+")}

              title={frozen ? frozenTitle : !config.active ? "Select an active provider first" : undefined}

              onClick={sendTest}

            >

              Send test

            </button>

          </div>

        </div>



        <div style={{ marginTop: 20, paddingTop: 16, borderTop: "1px solid var(--border-color)" }}>

          <h4 style={{ marginBottom: 4 }}>Phone verification for payout</h4>

          <p style={{ opacity: 0.7, fontSize: 12.5, marginTop: 0, marginBottom: 10 }}>

            When on, a prize can't be marked as paid for a winner whose phone isn't verified — buying, guessing,

            and free entry are never gated by this. An account created before this was first turned on is

            grandfathered — never retroactively locked out.

          </p>

          <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, marginBottom: 10 }}>

            <input

              type="checkbox"

              checked={!!config.phoneVerificationEnabled}

              disabled={busy || frozen || !config.active}

              title={frozen ? frozenTitle : !config.active ? "Select an active provider first" : undefined}

              onChange={(e) => setPhoneVerificationEnabled(e.target.checked)}

            />

            Require phone verification before payout

          </label>

          {config.phoneVerificationRequiredSince && (

            <p style={{ opacity: 0.6, fontSize: 12 }}>

              First turned on {siteDateTime(config.phoneVerificationRequiredSince)} — accounts created before

              that moment are exempt.

            </p>

          )}

          <div style={{ fontSize: 12.5, opacity: 0.7, marginTop: 10 }}>

            <strong>Rate limits</strong> (fixed, not adjustable here):

            <ul style={{ margin: "4px 0 0", paddingLeft: 18 }}>

              <li>{config.rateLimits.resendCooldownSeconds}s between resend attempts</li>

              <li>{config.rateLimits.maxAttempts} incorrect tries before a code is invalidated</li>

              <li>Codes expire after {config.rateLimits.codeTtlMinutes} minutes</li>

              <li>{config.rateLimits.maxSendsPerPhonePerDay} sends per phone number per day</li>

            </ul>

          </div>

        </div>

      </ViewOnlyGate>

    </div>

  );

}



function EmailProviderPanel({ token, modeStatus, canEdit }) {

  const [config, setConfig] = useState(null);

  const [busy, setBusy] = useState(false);

  // Draft text fields, keyed "provider:field" — cleared back to blank

  // on every successful save, same "blank means leave it alone"

  // convention the backend already applies to the API key field.

  const [drafts, setDrafts] = useState({});

  const [testRecipient, setTestRecipient] = useState("");

  // Local UI toggle, not itself a saved value — derived from

  // config?.active so it starts on the right side once config loads,

  // but switching it doesn't commit anything to the backend by

  // itself; it only decides which section (the self-hosted form, or

  // the third-party dropdown + its forms) is shown. Defaults to

  // "third_party" before config has loaded at all (config?.active is

  // undefined on first render) — the effect below corrects it the

  // moment the real value arrives. Must live above the "loading"

  // early return below, since hooks can never be called conditionally.

  const [mode, setMode] = useState("third_party");

  // WHAT THE LAST KEY PROBE FOUND (1.505.0). Not a toast: a
  // full-access key in the sending box is not an error - the save
  // worked and mail will send - but it is something to go and change
  // in Resend, and a message that disappears after four seconds is a
  // poor way to say so. Cleared when the next save answers.

  const [resendKeyNotice, setResendKeyNotice] = useState(null);

  useEffect(() => {

    if (config) setMode(config.active === "self_hosted" ? "own" : "third_party");

  }, [config?.active]);



  const load = () => {

    wsRequest("admin.emailProvider.get", {}, token)

      .then(setConfig)

      .catch(() => {});

  };

  useEffect(load, [token]);



  const draftKey = (provider, field) => `${provider}:${field}`;

  const getDraft = (provider, field, fallback = "") => drafts[draftKey(provider, field)] ?? fallback;

  const setDraft = (provider, field, value) => setDrafts((d) => ({ ...d, [draftKey(provider, field)]: value }));



  // Unlike the payment providers, there's no test/live split here at

  // all — every mutating action always requires Live mode (see the

  // backend's own assertNotFrozen calls), so this is a single,

  // unconditional frozen state rather than a per-mode one.

  // DEMO ONLY. These are settings, and the server agrees — see
  // assertSettingsEditable. Freezing them in Test mode meant switching
  // to Live to save a form and back again to carry on testing.
  const frozen = adminMode(modeStatus) === "demo";

  const frozenTitle = "Leave Demo mode to manage this";



  // THE SENDING SWITCH (1.466.0). Asked for on 23 Sep 2026 after Resend
  // disabled this site's webhook over undelivered mail.
  const setSendingEnabled = async (enabled) => {
    setBusy(true);
    try {
      await wsRequest("admin.emailProvider.setSendingEnabled", { enabled }, token);
      showToast(enabled ? "Email sending switched on." : "Email sending switched off.");
      load();
    } catch (err) {
      showToast(err.message || "Could not change it.", "error");
    }
    setBusy(false);
  };

  const setActive = async (active) => {

    setBusy(true);

    try {

      await wsRequest("admin.emailProvider.setActive", { active }, token);

      load();

    } catch (err) {

      showToast(err.message || "Could not reach backend.", "error");

    }

    setBusy(false);

  };



  const saveCredentials = async (provider, action, label) => {

    setBusy(true);

    try {

      const res = await wsRequest(

        action,

        { fromAddress: getDraft(provider, "fromAddress"), fromName: getDraft(provider, "fromName"), apiKey: getDraft(provider, "apiKey") },

        token

      );

      // ONLY RESEND ANSWERS THIS, and only when a key was actually typed.

      if (provider === "resend" && getDraft(provider, "apiKey")) {

        const power = res?.keyCheck?.sending?.power || null;

        setResendKeyNotice(

          power === "full"

            ? {

                tone: "warn",

                text: "That key has FULL ACCESS to your Resend account \u2014 far more than sending needs. It is saved and mail will send, but anything that reads this key can also read, change and delete your contacts and domains. Make a key marked sending access in Resend and paste that here instead.",

              }

            : power === "sending"

              ? { tone: "ok", text: "Checked with Resend: that key is restricted to sending, which is what this one should be." }

              : power

                ? { tone: "warn", text: `Resend could not confirm what that key can do: ${res?.keyCheck?.sending?.reason || "no reason given"}. It has been saved.` }

                : null

        );

      }

      setDrafts((d) => ({ ...d, [draftKey(provider, "apiKey")]: "" }));

      load();

      showToast(`${label} credentials saved.`, "success");

    } catch (err) {

      showToast(err.message || "Could not reach backend.", "error");

    }

    setBusy(false);

  };



  const saveSelfHostedCredentials = async () => {

    setBusy(true);

    try {

      await wsRequest(

        "admin.emailProvider.setSelfHostedCredentials",

        {

          fromAddress: getDraft("self_hosted", "fromAddress"),

          fromName: getDraft("self_hosted", "fromName"),

          host: getDraft("self_hosted", "host"),

          port: getDraft("self_hosted", "port"),

          secure: getDraft("self_hosted", "secure", undefined),

          username: getDraft("self_hosted", "username"),

          password: getDraft("self_hosted", "password"),

        },

        token

      );

      setDrafts((d) => ({ ...d, [draftKey("self_hosted", "password")]: "" }));

      load();

      showToast("Self-hosted SMTP credentials saved.", "success");

    } catch (err) {

      showToast(err.message || "Could not reach backend.", "error");

    }

    setBusy(false);

  };



  // Holds a just-generated webhook secret/credentials so it can be

  // shown once for root to copy — same "shown once, never

  // retrievable again" convention as backup codes and partner API

  // keys elsewhere in this admin panel.

  const [generatedWebhookInfo, setGeneratedWebhookInfo] = useState(null); // { provider, secret } | { provider, username, password } | null



  const saveResendWebhookSecret = async () => {

    setBusy(true);

    try {

      await wsRequest(

        "admin.emailProvider.setResendCredentials",

        { fromAddress: getDraft("resend", "fromAddress", config.resend.fromAddress), fromName: getDraft("resend", "fromName", config.resend.fromName), webhookSecret: getDraft("resend", "webhookSecret") },

        token

      );

      setDrafts((d) => ({ ...d, [draftKey("resend", "webhookSecret")]: "" }));

      load();

      showToast("Resend webhook secret saved.", "success");

    } catch (err) {

      showToast(err.message || "Could not reach backend.", "error");

    }

    setBusy(false);

  };



  // WHAT RESEND ACTUALLY HAS (1.501.0). Everything else on this screen
  // reports what WE believe is in step; this asks the far end. Added
  // because answering "is it really there" needed the API key in a human's
  // hand, and this codebase deliberately makes that impossible - so the
  // check belongs here, where the key already is.
  const [resendCheck, setResendCheck] = useState(null);
  const checkResendContacts = async () => {
    setResendCheck({ busy: true });
    try {
      const data = await wsRequest("admin.emailProvider.resendContactCheck", {}, token);
      setResendCheck({ ...data, at: Date.now() });
    } catch (err) {
      setResendCheck({ ok: false, reason: "failed", error: err.message || "Could not reach backend." });
    }
  };

  // THE TWO CONTACT LISTS (1.499.0). Sent on its own rather than folded
  // into the webhook save: an operator pasting a segment id has not
  // touched the webhook secret, and a save that carried an empty string
  // for it would wipe one.
  const saveResendSegments = async () => {

    setBusy(true);

    try {

      const res = await wsRequest(

        "admin.emailProvider.setResendCredentials",

        {
          liveSegmentId: getDraft("resend", "liveSegmentId", config.resend.liveSegmentId || ""),
          testSegmentId: getDraft("resend", "testSegmentId", config.resend.testSegmentId || ""),
          // Set-only, like every other secret in this panel: blank means
          // leave it alone, so saving a segment id cannot wipe the key.
          contactsApiKey: getDraft("resend", "contactsApiKey"),
        },

        token

      );

      // A SENDING-ONLY KEY NEVER GETS THIS FAR (1.505.0) - the backend

      // refuses it and the catch below says so. What is left to report is

      // the check that could not be made.

      if (getDraft("resend", "contactsApiKey")) {

        const power = res?.keyCheck?.contacts?.power || null;

        setResendKeyNotice(

          power === "full"

            ? { tone: "ok", text: "Checked with Resend: that key has the full access the contact mirror needs." }

            : power

              ? { tone: "warn", text: `Resend could not confirm what that key can do: ${res?.keyCheck?.contacts?.reason || "no reason given"}. It has been saved, so watch the first sync.` }

              : null

        );

      }

      setDrafts((d) => ({ ...d, [draftKey("resend", "contactsApiKey")]: "" }));

      load();

      showToast("Resend contact lists saved. Anything that had failed will be retried.", "success");

    } catch (err) {

      showToast(err.message || "Could not reach backend.", "error");

    }

    setBusy(false);

  };



  const generateBrevoWebhookSecret = async () => {

    setBusy(true);

    try {

      const result = await wsRequest("admin.emailProvider.generateBrevoWebhookSecret", {}, token);

      setGeneratedWebhookInfo({ provider: "brevo", secret: result.secret });

      load();

    } catch (err) {

      showToast(err.message || "Could not reach backend.", "error");

    }

    setBusy(false);

  };



  const generatePostmarkWebhookCredentials = async () => {

    setBusy(true);

    try {

      const result = await wsRequest("admin.emailProvider.generatePostmarkWebhookCredentials", {}, token);

      setGeneratedWebhookInfo({ provider: "postmark", username: result.username, password: result.password });

      load();

    } catch (err) {

      showToast(err.message || "Could not reach backend.", "error");

    }

    setBusy(false);

  };



  const sendTest = async () => {

    setBusy(true);

    try {

      await wsRequest("admin.emailProvider.sendTest", { to: testRecipient }, token);

      showToast("Test email sent.", "success");

    } catch (err) {

      showToast(err.message || "Could not reach backend.", "error");

    }

    setBusy(false);

  };



  if (config === null) return <p>Loading…</p>;

  // !== false, the same reading as the server's: an older backend that
  // has never heard of this switch answers without the key, and a
  // missing switch is not a switch turned off. Read as falsy it would
  // tell an operator their site sends nothing, which is both alarming
  // and untrue.
  const sendingOn = config.sendingEnabled !== false;



  const providers = [

    { id: "resend", label: "Resend" },

    { id: "brevo", label: "Brevo" },

    { id: "postmark", label: "Postmark" },

  ];



  const shCreds = config.self_hosted;

  const selfHostedFrozen = frozen;

  const selfHostedTitle = frozenTitle;



  return (

    <div style={{ marginTop: 16, maxWidth: 640 }}>

      <h3>Email provider</h3>

      {/* SENDING, AS A SWITCH (1.466.0), above everything it governs.
          Asked for on 23 Sep 2026: "resend has disabled the webhook.
          Looks like its because of undelivered mail. Can we have an
          option in the email settings to enable/disable the sending of
          email?" Removing the credentials was the only way to stop
          before this, which loses them and takes the test sends away at
          the moment they are most needed. */}
      <div
        data-email-sending={sendingOn ? "on" : "off"}
        style={{
          border: `1px solid ${sendingOn ? "var(--border-color)" : "var(--accent-yellow)"}`,
          borderRadius: 8,
          padding: "10px 12px",
          marginBottom: 14,
        }}
      >
        <label style={{ display: "flex", gap: 8, alignItems: "center", fontSize: 14, fontWeight: 600 }}>
          <input
            type="checkbox"
            checked={sendingOn}
            disabled={!canEdit || busy || frozen}
            title={frozen ? frozenTitle : undefined}
            onChange={(e) => setSendingEnabled(e.target.checked)}
          />
          Send email
        </label>
        {sendingOn ? (
          <p style={{ margin: "6px 0 0", fontSize: 12.5, opacity: 0.8 }}>
            On. Turn it off to stop the site sending anything &mdash; useful when a provider is complaining about
            undelivered mail, or has disabled your webhook over it. Your credentials stay where they are.
          </p>
        ) : (
          <>
            <p style={{ margin: "6px 0 0", fontSize: 13, color: "var(--accent-yellow)", fontWeight: 600 }}>
              This site is sending no email at all.
            </p>
            <p style={{ margin: "4px 0 0", fontSize: 12.5, opacity: 0.85 }}>
              Nothing automatic goes out &mdash; results, payout confirmations, reminders, broadcasts. Held messages
              are listed under Email &rarr; Activity, so you can see what was not sent. A winner will not be told
              they have won.
            </p>
          </>
        )}
        <p style={{ margin: "6px 0 0", fontSize: 12, opacity: 0.7 }}>
          Still works either way: the two <em>Send test</em> buttons, so you can check a provider before switching
          sending back on. Your provider also keeps reporting on mail already sent, so bounces and complaints carry
          on suppressing addresses while this is off &mdash; which is how the backlog clears.
        </p>
      </div>

      {/* ONE SET SINCE 1.407.0. This said partner-linked users were emailed

          through a partner-level provider set under Partners → Email — a

          screen removed on 6 Sep 2026 — so it described a path that, on most

          instances, silently sent nothing. */}

      <div style={{ fontSize: 12.5, opacity: 0.75, border: "1px solid var(--border-color)", borderRadius: 6, padding: "8px 10px", marginBottom: 14 }}>

        <strong>Who this sends to:</strong> everybody this instance emails — users who signed up here and users

        who came through the partner's site alike. These are the partner's own credentials; there is no other set.

      </div>

      <p style={{ opacity: 0.7, fontSize: 13, marginTop: -6 }}>

        For sending real email — reminders, receipts, password resets, and every box under Content calls through

        whichever provider is active here. Off by default, and nothing sends real email until one is selected and

        its credentials are entered.

      </p>



      <ViewOnlyGate canEdit={canEdit} showNotice>

        <div style={{ marginBottom: 16, display: "flex", gap: 18 }}>

          <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13.5, cursor: selfHostedFrozen ? "default" : "pointer" }}>

            <input

              type="radio"

              name="email-mode"

              checked={mode === "own"}

              disabled={busy || selfHostedFrozen}

              title={selfHostedTitle}

              onChange={() => {

                setMode("own");

                setActive("self_hosted");

              }}

            />

            Own email system

          </label>

          <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13.5, cursor: frozen ? "default" : "pointer" }}>

            <input

              type="radio"

              name="email-mode"

              checked={mode === "third_party"}

              disabled={busy || frozen}

              title={frozen ? frozenTitle : undefined}

              onChange={() => setMode("third_party")}

            />

            3rd party email provider

          </label>

        </div>



        {mode === "own" ? (

          <div style={{ border: "1px solid var(--border-color)", borderRadius: 8, padding: 14, marginBottom: 12, opacity: selfHostedFrozen ? 0.55 : 1 }}>

            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>

              <strong>SMTP details</strong>

              {config.active === "self_hosted" && <span style={{ opacity: 0.6, fontSize: 12.5 }}>✓ active</span>}

            </div>

            <p style={{ opacity: 0.7, fontSize: 12.5, marginTop: 0, marginBottom: 10 }}>

              For a site operator's own, already-existing email system — enter that system's real SMTP server

              details below, the same way you'd configure any other email client to send through it.

            </p>

            <label style={{ display: "block", fontSize: 12.5, marginBottom: 3 }}>SMTP host</label>

            <input

              type="text"

              placeholder="smtp.yourdomain.com"

              value={getDraft("self_hosted", "host", shCreds.host)}

              disabled={busy || selfHostedFrozen}

              title={selfHostedTitle}

              onChange={(e) => setDraft("self_hosted", "host", e.target.value)}

              style={{ width: "100%", padding: "6px 8px", fontSize: 13, marginBottom: 8, boxSizing: "border-box" }}

            />

            <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>

              <div style={{ flex: 1 }}>

                <label style={{ display: "block", fontSize: 12.5, marginBottom: 3 }}>Port</label>

                <input

                  type="number"

                  value={getDraft("self_hosted", "port", shCreds.port)}

                  disabled={busy || selfHostedFrozen}

                  title={selfHostedTitle}

                  onChange={(e) => setDraft("self_hosted", "port", e.target.value)}

                  style={{ width: "100%", padding: "6px 8px", fontSize: 13, boxSizing: "border-box" }}

                />

              </div>

              <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12.5, marginTop: 20 }}>

                <input

                  type="checkbox"

                  checked={getDraft("self_hosted", "secure", shCreds.secure)}

                  disabled={busy || selfHostedFrozen}

                  title={selfHostedTitle}

                  onChange={(e) => setDraft("self_hosted", "secure", e.target.checked)}

                />

                Use TLS (usually on for port 465)

              </label>

            </div>

            <label style={{ display: "block", fontSize: 12.5, marginBottom: 3 }}>From address</label>

            <input

              type="email"

              value={getDraft("self_hosted", "fromAddress", shCreds.fromAddress)}

              disabled={busy || selfHostedFrozen}

              title={selfHostedTitle}

              onChange={(e) => setDraft("self_hosted", "fromAddress", e.target.value)}

              style={{ width: "100%", padding: "6px 8px", fontSize: 13, marginBottom: 8, boxSizing: "border-box" }}

            />

            <label style={{ display: "block", fontSize: 12.5, marginBottom: 3 }}>From name</label>

            <input

              type="text"

              value={getDraft("self_hosted", "fromName", shCreds.fromName)}

              disabled={busy || selfHostedFrozen}

              title={selfHostedTitle}

              onChange={(e) => setDraft("self_hosted", "fromName", e.target.value)}

              style={{ width: "100%", padding: "6px 8px", fontSize: 13, marginBottom: 8, boxSizing: "border-box" }}

            />

            <label style={{ display: "block", fontSize: 12.5, marginBottom: 3 }}>Username (optional)</label>

            <input

              type="text"

              value={getDraft("self_hosted", "username", shCreds.username)}

              disabled={busy || selfHostedFrozen}

              title={selfHostedTitle}

              onChange={(e) => setDraft("self_hosted", "username", e.target.value)}

              style={{ width: "100%", padding: "6px 8px", fontSize: 13, marginBottom: 8, boxSizing: "border-box" }}

            />

            <label style={{ display: "block", fontSize: 12.5, marginBottom: 3 }}>

              Password (optional) {shCreds.hasPassword && "(leave blank to keep the current one)"}

            </label>

            <input

              type="password"

              value={getDraft("self_hosted", "password")}

              disabled={busy || selfHostedFrozen}

              title={selfHostedTitle}

              onChange={(e) => setDraft("self_hosted", "password", e.target.value)}

              style={{ width: "100%", padding: "6px 8px", fontSize: 13, marginBottom: 10, boxSizing: "border-box" }}

            />

            <button className="btn" disabled={busy || selfHostedFrozen} title={selfHostedTitle} onClick={saveSelfHostedCredentials}>

              Save SMTP details

            </button>

          </div>

        ) : (

          <>

            {providers.map((p) => {

              const creds = config[p.id];

              const action = `admin.emailProvider.set${p.label}Credentials`;

              const isActive = config.active === p.id;

              // Covers both another third-party provider AND

              // self-hosted being active — config.active holds

              // whichever one it is, and neither counts as "this

              // provider" here.

              const anotherActive = !!config.active && !isActive;

              const activateDisabled = busy || frozen || anotherActive;

              const activateTitle = frozen

                ? frozenTitle

                : anotherActive

                ? `Deactivate ${config.active === "self_hosted" ? "Own email system" : providers.find((x) => x.id === config.active)?.label || "the active provider"} first`

                : undefined;

              return (

                <div

                  key={p.id}

                  style={{ border: `1px solid ${isActive ? "var(--accent-green)" : "var(--border-color)"}`, borderRadius: 8, padding: 14, marginBottom: 12, opacity: frozen ? 0.55 : 1 }}

                >

                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8, gap: 8 }}>

                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>

                      <strong>{p.label}</strong>

                      {isActive && <span style={{ opacity: 0.7, fontSize: 12.5, color: "var(--accent-green)" }}>✓ active</span>}

                      {creds.hasKey && <span style={{ opacity: 0.6, fontSize: 12.5 }}>API key set</span>}

                    </div>

                    <button

                      className="btn"

                      disabled={activateDisabled}

                      title={activateTitle}

                      onClick={() => setActive(isActive ? null : p.id)}

                    >

                      {isActive ? "Deactivate" : "Activate"}

                    </button>

                  </div>

                  {/* WHAT THE LAST KEY CHECK FOUND (1.505.0). Stays put

                      rather than toasting: "this key can delete your whole

                      Resend account" is an instruction to go and do

                      something, not a notification. */}

                  {p.id === "resend" && resendKeyNotice && (

                    <div

                      data-resend-key-notice={resendKeyNotice.tone}

                      style={{

                        border: `1px solid ${resendKeyNotice.tone === "warn" ? "var(--accent-amber, #d4a55e)" : "var(--border-color)"}`,

                        borderRadius: 4,

                        padding: "8px 10px",

                        marginBottom: 10,

                        fontSize: 12.5,

                        display: "flex",

                        justifyContent: "space-between",

                        gap: 10,

                        alignItems: "flex-start",

                      }}

                    >

                      <span>{resendKeyNotice.text}</span>

                      <button className="btn-quiet" onClick={() => setResendKeyNotice(null)} title="Dismiss">

                        Dismiss

                      </button>

                    </div>

                  )}

                  <label style={{ display: "block", fontSize: 12.5, marginBottom: 3 }}>From address</label>

                  <input

                    type="email"

                    value={getDraft(p.id, "fromAddress", creds.fromAddress)}

                    disabled={busy || frozen}

                    title={frozen ? frozenTitle : undefined}

                    onChange={(e) => setDraft(p.id, "fromAddress", e.target.value)}

                    style={{ width: "100%", padding: "6px 8px", fontSize: 13, marginBottom: 8, boxSizing: "border-box" }}

                  />

                  <label style={{ display: "block", fontSize: 12.5, marginBottom: 3 }}>From name</label>

                  <input

                    type="text"

                    value={getDraft(p.id, "fromName", creds.fromName)}

                    disabled={busy || frozen}

                    title={frozen ? frozenTitle : undefined}

                    onChange={(e) => setDraft(p.id, "fromName", e.target.value)}

                    style={{ width: "100%", padding: "6px 8px", fontSize: 13, marginBottom: 8, boxSizing: "border-box" }}

                  />

                  <label style={{ display: "block", fontSize: 12.5, marginBottom: 3 }}>{p.id === "resend" ? "Sending API key" : "API key"} {creds.hasKey && "(leave blank to keep the current one)"}</label>

                  {/* WHICH KEY THIS IS (1.505.0). It said only "API key",
                      one box above "Full-access key for contacts" - so on
                      27 Sep 2026 the full-access key went in here and every
                      send carried it. Resend is the only provider with two
                      levels, so only Resend says this. */}

                  {p.id === "resend" && (

                    <p style={{ opacity: 0.7, fontSize: 12, margin: "0 0 6px" }} data-resend-sending-key-hint>

                      Sending access is all this one needs. It is <em>not</em> the full-access key the contact mirror uses &mdash;
                      that has its own box under Contact lists below.

                    </p>

                  )}

                  <input

                    type="password"

                    value={getDraft(p.id, "apiKey")}

                    disabled={busy || frozen}

                    title={frozen ? frozenTitle : undefined}

                    onChange={(e) => setDraft(p.id, "apiKey", e.target.value)}

                    style={{ width: "100%", padding: "6px 8px", fontSize: 13, marginBottom: 10, boxSizing: "border-box" }}

                  />

                  <button className="btn" disabled={busy || frozen} title={frozen ? frozenTitle : undefined} onClick={() => saveCredentials(p.id, action, p.label)}>

                    Save {p.label} credentials

                  </button>



                  <div style={{ marginTop: 14, paddingTop: 12, borderTop: "1px solid var(--border-color)" }}>

                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>

                      <strong style={{ fontSize: 12.5 }}>Webhook (bounce/complaint tracking)</strong>

                      {(p.id === "resend" || p.id === "brevo") && creds.hasWebhookSecret && (

                        <span style={{ opacity: 0.6, fontSize: 12 }}>✓ set</span>

                      )}

                      {p.id === "postmark" && creds.hasWebhookPassword && <span style={{ opacity: 0.6, fontSize: 12 }}>✓ set</span>}

                    </div>



                    {p.id === "resend" && (

                      <>

                        <p style={{ opacity: 0.7, fontSize: 12, margin: "0 0 8px" }}>

                          Create a webhook in Resend's dashboard pointing at{" "}

                          <code style={{ fontSize: 11.5 }}>{`${window.location.origin}/api/webhooks/email/resend`}</code>, then paste

                          the signing secret Resend shows you below.

                        </p>

                        <input

                          type="password"

                          placeholder="whsec_..."

                          value={getDraft("resend", "webhookSecret")}

                          disabled={busy || frozen}

                          title={frozen ? frozenTitle : undefined}

                          onChange={(e) => setDraft("resend", "webhookSecret", e.target.value)}

                          style={{ width: "100%", padding: "6px 8px", fontSize: 13, marginBottom: 8, boxSizing: "border-box" }}

                        />

                        <button className="btn" disabled={busy || frozen} title={frozen ? frozenTitle : undefined} onClick={saveResendWebhookSecret}>

                          Save webhook secret

                        </button>

                      </>

                    )}







                    {p.id === "brevo" && (

                      <>

                        <p style={{ opacity: 0.7, fontSize: 12, margin: "0 0 8px" }}>

                          Create a webhook in Brevo's dashboard pointing at{" "}

                          <code style={{ fontSize: 11.5 }}>{`${window.location.origin}/api/webhooks/email/brevo`}</code>, and add a

                          custom header named <code style={{ fontSize: 11.5 }}>X-Webhook-Secret</code> with the value below.

                        </p>

                        <button className="btn" disabled={busy || frozen} title={frozen ? frozenTitle : undefined} onClick={generateBrevoWebhookSecret}>

                          Generate new webhook secret

                        </button>

                        {generatedWebhookInfo?.provider === "brevo" && (

                          <div style={{ marginTop: 8, padding: 8, background: "var(--bg-secondary)", borderRadius: 6 }}>

                            <code style={{ fontSize: 11.5, wordBreak: "break-all" }}>{generatedWebhookInfo.secret}</code>

                            <p style={{ opacity: 0.6, fontSize: 11, margin: "6px 0 0" }}>Copy this now — it won't be shown again.</p>

                          </div>

                        )}

                      </>

                    )}



                    {p.id === "postmark" && (

                      <>

                        <p style={{ opacity: 0.7, fontSize: 12, margin: "0 0 8px" }}>

                          Create a webhook in Postmark's dashboard using this URL (includes credentials):

                        </p>

                        <button

                          className="btn"

                          disabled={busy || frozen}

                          title={frozen ? frozenTitle : undefined}

                          onClick={generatePostmarkWebhookCredentials}

                        >

                          Generate new webhook credentials

                        </button>

                        {generatedWebhookInfo?.provider === "postmark" && (

                          <div style={{ marginTop: 8, padding: 8, background: "var(--bg-secondary)", borderRadius: 6 }}>

                            <code style={{ fontSize: 11.5, wordBreak: "break-all" }}>

                              {`https://${generatedWebhookInfo.username}:${generatedWebhookInfo.password}@${window.location.host}/api/webhooks/email/postmark`}

                            </code>

                            <p style={{ opacity: 0.6, fontSize: 11, margin: "6px 0 0" }}>Copy this now — it won't be shown again.</p>

                          </div>

                        )}

                      </>

                    )}

                  </div>

                  {/* THE TWO CONTACT LISTS (1.499.0). Beside the webhook
                      secret because it is the same kind of thing: a value
                      read off Resend's own dashboard and pasted in. Shown
                      in full - a segment id is not a secret, and a masked
                      one is an id nobody can check against the dashboard. */}

                  {p.id === "resend" && (

                    <div data-resend-contact-lists style={{ marginTop: 14, paddingTop: 12, borderTop: "1px solid var(--border-color)" }}>

                      <strong style={{ fontSize: 12.5 }}>Contact lists</strong>

                      <p style={{ opacity: 0.7, fontSize: 12, margin: "4px 0 8px" }}>

                        Every address under Email → Subscribers is mirrored into Resend as a contact, and
                        unsubscribing one here marks it unsubscribed there. Open a segment in Resend and
                        paste its id below — the live and test queues go into separate segments so the two
                        can never be mailed together.

                      </p>

                      {/* THE SECOND KEY (1.501.0). Resend has only two
                          permission levels, so contacts need one that can
                          do everything - which is exactly why it is not the
                          key used for every send. */}

                      <label style={{ display: "block", fontSize: 12, marginBottom: 8 }}>

                        <span style={{ opacity: 0.7 }}>

                          Full-access key for contacts{creds.hasContactsKey ? " \u2014 \u2713 set" : ""}

                        </span>

                        <input

                          type="password"

                          data-resend-contacts-key

                          placeholder={creds.hasContactsKey ? "Stored \u2014 type a new one to replace it" : "re_..."}

                          value={getDraft("resend", "contactsApiKey")}

                          disabled={busy || frozen}

                          title={frozen ? frozenTitle : undefined}

                          onChange={(e) => setDraft("resend", "contactsApiKey", e.target.value)}

                          style={{ width: "100%", padding: "6px 8px", fontSize: 13, marginTop: 3, boxSizing: "border-box" }}

                        />

                      </label>

                      <p style={{ opacity: 0.7, fontSize: 12, margin: "0 0 10px" }}>

                        A Resend key is either <em>sending access</em> or <em>full access</em>, with nothing in
                        between &mdash; and contacts need full access. That is far more than sending needs, so the
                        two are kept apart: make a second key in Resend marked full access, paste it here, and
                        leave the sending key above restricted to sending. This one can read, change and delete
                        everything in your Resend account, and nothing but the contact mirror uses it.

                      </p>

                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 8 }}>

                        {[
                          { field: "liveSegmentId", label: "Live queue", attr: "live" },
                          { field: "testSegmentId", label: "Test queue", attr: "test" },
                        ].map((seg) => (

                          <label key={seg.field} style={{ display: "block", fontSize: 12 }}>

                            <span style={{ opacity: 0.7 }}>{seg.label}</span>

                            <input

                              type="text"

                              data-resend-segment={seg.attr}

                              placeholder="78261eea-8f8b-4381-83c6-79fa7120f1cf"

                              value={getDraft("resend", seg.field, creds[seg.field] || "")}

                              disabled={busy || frozen}

                              title={frozen ? frozenTitle : undefined}

                              onChange={(e) => setDraft("resend", seg.field, e.target.value)}

                              style={{ width: "100%", padding: "6px 8px", fontSize: 13, marginTop: 3, boxSizing: "border-box" }}

                            />

                          </label>

                        ))}

                      </div>

                      {/* WHAT IS ACTUALLY HAPPENING, in the same order the
                          backend decides it (resendContactSync): active
                          provider, then a key, then a segment per queue. A
                          note that said "being mirrored" while Resend sat
                          inactive would be the one sentence an operator
                          would believe and should not. */}

                      <p data-resend-segments-note style={{ opacity: 0.7, fontSize: 12, margin: "0 0 8px" }}>

                        {!creds.liveSegmentId && !creds.testSegmentId
                          ? "No segment set yet, so nothing is being mirrored to Resend."
                          : !creds.hasContactsKey
                            ? "Add the full-access key above before anything can be mirrored — the sending key cannot touch contacts."
                            : !isActive
                              ? "Resend is not the active provider, so nothing is being mirrored yet."
                              : !creds.testSegmentId
                                ? "Test addresses are not being mirrored — no test segment is set."
                                : !creds.liveSegmentId
                                  ? "Live addresses are not being mirrored — no live segment is set."
                                  : "Both queues are being mirrored to Resend."}

                      </p>

                      <button className="btn" disabled={busy || frozen} title={frozen ? frozenTitle : undefined} onClick={saveResendSegments}>

                        Save contact lists

                      </button>

                      {" "}

                      {/* THE FAR END (1.501.0). Read-only, no arguments, and
                          it uses the stored key rather than asking anybody
                          to hold one. */}

                      <button
                        className="btn"
                        type="button"
                        data-resend-check
                        disabled={busy || resendCheck?.busy}
                        onClick={checkResendContacts}
                      >

                        {resendCheck?.busy ? "Checking…" : "Check what Resend has"}

                      </button>

                      {resendCheck && !resendCheck.busy ? (

                        <div data-resend-check-result style={{ marginTop: 8, fontSize: 12.5, opacity: 0.85 }}>

                          {resendCheck.reason === "no-key"
                            ? "No full-access key stored, so there is nothing to ask with."
                            : resendCheck.reason === "no-segments"
                              ? "No segment id stored, so there is nothing to look in."
                              : resendCheck.reason === "failed"
                                ? resendCheck.error
                                : null}

                          {(resendCheck.segments || []).map((seg) => (

                            <div key={seg.queue}>

                              <strong>{seg.queue === "live" ? "Live" : "Test"}</strong>{" "}

                              {seg.error
                                ? <span style={{ color: "var(--accent-red, #e2686d)" }}>{seg.error}</span>
                                : `${seg.count} contact${seg.count === 1 ? "" : "s"}, ${seg.unsubscribed} unsubscribed${seg.capped ? " (first 100)" : ""}`}

                            </div>

                          ))}

                          {resendCheck.ok && (resendCheck.inBoth || []).length ? (

                            <div style={{ color: "var(--accent-red, #e2686d)", marginTop: 4 }}>

                              In BOTH segments, which should never happen: {resendCheck.inBoth.join(", ")}

                            </div>

                          ) : resendCheck.ok ? (

                            <div style={{ opacity: 0.75, marginTop: 4 }}>No address is in both segments.</div>

                          ) : null}

                        </div>

                      ) : null}

                    </div>

                  )}


                </div>

              );

            })}

          </>

        )}



        <div style={{ marginTop: 20, paddingTop: 16, borderTop: "1px solid var(--border-color)" }}>

          <h4 style={{ marginBottom: 4 }}>Send a test email</h4>

          <p style={{ opacity: 0.7, fontSize: 12.5, marginTop: 0, marginBottom: 10 }}>

            The way to confirm the active provider's credentials actually work.

          </p>

          <div style={{ display: "flex", gap: 8 }}>

            <input

              type="email"

              placeholder="you@example.com"

              value={testRecipient}

              disabled={busy || frozen || !config.active}

              title={frozen ? frozenTitle : !config.active ? "Select an active provider first" : undefined}

              onChange={(e) => setTestRecipient(e.target.value)}

              style={{ flex: 1, padding: "6px 8px", fontSize: 13 }}

            />

            <button

              className="btn"

              disabled={busy || frozen || !config.active || !testRecipient.includes("@")}

              title={frozen ? frozenTitle : !config.active ? "Select an active provider first" : undefined}

              onClick={sendTest}

            >

              Send test

            </button>

          </div>

        </div>

      </ViewOnlyGate>

    </div>

  );

}

function EmailContentPanel({ token, modeStatus, canEdit }) {

  const [boxes, setBoxes] = useState(null);

  const [content, setContent] = useState(null);

  const [busy, setBusy] = useState(false);

  const [expanded, setExpanded] = useState({});

  // Draft text fields, keyed "box:field" — same "unsaved edit, cleared

  // on successful save" convention as the provider panels, except here

  // subject/html start pre-filled from the loaded content (there's no

  // "leave blank to keep the current one" secret-field convention to

  // preserve — this is plain text root should see and edit directly).

  const [drafts, setDrafts] = useState({});

  const [testRecipients, setTestRecipients] = useState({});



  const load = () => {

    wsRequest("admin.emailContent.get", {}, token)

      .then((data) => {

        setBoxes(data.boxes);

        setContent(data.content);

      })

      .catch(() => {});

  };

  useEffect(load, [token]);



  const draftKey = (box, field) => `${box}:${field}`;

  const getDraft = (box, field) => drafts[draftKey(box, field)] ?? content[box][field];

  const setDraft = (box, field, value) => setDrafts((d) => ({ ...d, [draftKey(box, field)]: value }));



  // DEMO ONLY. These are settings, and the server agrees — see
  // assertSettingsEditable. Freezing them in Test mode meant switching
  // to Live to save a form and back again to carry on testing.
  const frozen = adminMode(modeStatus) === "demo";

  const frozenTitle = "Leave Demo mode to manage this";



  const toggleExpanded = (box) => setExpanded((e) => ({ ...e, [box]: !e[box] }));



  const save = async (box) => {

    setBusy(true);

    try {

      await wsRequest("admin.emailContent.set", { box, subject: getDraft(box, "subject"), html: getDraft(box, "html") }, token);

      load();

      showToast(`${boxes[box].label} content saved.`, "success");

    } catch (err) {

      showToast(err.message || "Could not reach backend.", "error");

    }

    setBusy(false);

  };



  const sendTest = async (box) => {

    setBusy(true);

    try {

      await wsRequest("admin.emailContent.sendTest", { box, to: testRecipients[box] }, token);

      showToast("Test email sent.", "success");

    } catch (err) {

      showToast(err.message || "Could not reach backend.", "error");

    }

    setBusy(false);

  };



  const sendBroadcast = async (box) => {

    setBusy(true);

    try {

      const result = await wsRequest("admin.emailContent.sendBroadcast", { box }, token);

      showToast(`Sent to ${result.recipientCount} recipient(s).`, "success");

    } catch (err) {

      showToast(err.message || "Could not reach backend.", "error");

    }

    setBusy(false);

  };



  if (boxes === null || content === null) return <p>Loading…</p>;



  return (

    <div style={{ marginTop: 16, maxWidth: 640 }}>

      <h3>Email content</h3>

      <p style={{ opacity: 0.7, fontSize: 13, marginTop: -6 }}>

        The actual subject and HTML for every email this site sends. Each box below fires automatically at its own

        trigger point, except "Other" — a one-off broadcast you send manually, on demand.

      </p>



      <ViewOnlyGate canEdit={canEdit} showNotice>

        {Object.entries(boxes).map(([boxKey, box]) => {

          const isBroadcast = box.variables.length === 0;

          const isOpen = !!expanded[boxKey];

          return (

            <div key={boxKey} style={{ border: "1px solid var(--border-color)", borderRadius: 8, marginBottom: 10, opacity: frozen ? 0.55 : 1 }}>

              <div

                onClick={() => toggleExpanded(boxKey)}

                style={{ padding: 14, cursor: "pointer", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10 }}

              >

                <div>

                  <strong>{box.label}</strong>

                  <div style={{ fontSize: 12.5, opacity: 0.7, marginTop: 2 }}>{box.description}</div>

                </div>

                <span style={{ opacity: 0.6, fontSize: 13, flexShrink: 0 }}>{isOpen ? "▲ Hide" : "▼ Edit"}</span>

              </div>



              {isOpen && (

                <div style={{ padding: "0 14px 14px" }}>

                  {/* NO SUBJECT ON THE FOOTER. It is appended to other emails and
                      never sent on its own, so a subject box there is a field that
                      can only ever be ignored. The backend marks that box isFooter. */}
                  {!box.isFooter && (
                    <>
                    <label style={{ display: "block", fontSize: 12.5, marginBottom: 3 }}>Subject</label>
  
                    <input
  
                      type="text"
  
                      value={getDraft(boxKey, "subject")}
  
                      disabled={busy || frozen}
  
                      title={frozen ? frozenTitle : undefined}
  
                      onChange={(e) => setDraft(boxKey, "subject", e.target.value)}
  
                      style={{ width: "100%", padding: "6px 8px", fontSize: 13, marginBottom: 8, boxSizing: "border-box" }}
  
                    />
                    </>
                  )}

                  <label style={{ display: "block", fontSize: 12.5, marginBottom: 3 }}>HTML</label>

                  {/* THE SAME EDITOR THE CONTENT PAGES USE, with its HTML/rich-text
                      toggle. It was a bare textarea here, which is fine for somebody
                      who writes HTML and no use to anybody who does not — and these
                      boxes are the words a buyer actually reads. The toggle matters
                      for email specifically: inline styles, a table, or a variable
                      inside an href are all things execCommand will not produce. */}
                  <HtmlEditor
                    value={getDraft(boxKey, "html")}
                    disabled={busy || frozen}
                    onChange={(html) => setDraft(boxKey, "html", html)}
                    rows={8}
                  />
                  <div style={{ marginBottom: 8 }} />

                  {box.variables.length > 0 && (

                    <p style={{ fontSize: 12, opacity: 0.65, marginTop: 0, marginBottom: 10 }}>

                      Available: {box.variables.map((v) => `{{${v}}}`).join(", ")}

                    </p>

                  )}

                  <button className="btn" disabled={busy || frozen} title={frozen ? frozenTitle : undefined} onClick={() => save(boxKey)}>

                    Save

                  </button>



                  <div style={{ marginTop: 16, paddingTop: 14, borderTop: "1px solid var(--border-color)" }}>

                    <h4 style={{ marginBottom: 8, fontSize: 13.5 }}>Send a test</h4>

                    <div style={{ display: "flex", gap: 8 }}>

                      <input

                        type="email"

                        placeholder="you@example.com"

                        value={testRecipients[boxKey] || ""}

                        disabled={busy || frozen}

                        title={frozen ? frozenTitle : undefined}

                        onChange={(e) => setTestRecipients((t) => ({ ...t, [boxKey]: e.target.value }))}

                        style={{ flex: 1, padding: "6px 8px", fontSize: 13 }}

                      />

                      <button

                        className="btn"

                        disabled={busy || frozen || !(testRecipients[boxKey] || "").includes("@")}

                        title={frozen ? frozenTitle : undefined}

                        onClick={() => sendTest(boxKey)}

                      >

                        Send test

                      </button>

                    </div>

                  </div>



                  {isBroadcast && (

                    <div style={{ marginTop: 16, paddingTop: 14, borderTop: "1px solid var(--border-color)" }}>

                      <h4 style={{ marginBottom: 8, fontSize: 13.5 }}>Send now</h4>

                      <p style={{ fontSize: 12.5, opacity: 0.7, marginTop: 0, marginBottom: 10 }}>

                        Sends immediately to every account with email updates turned on. Save your content above

                        first — this sends whatever's currently saved, not what's still unsaved in the boxes above.

                      </p>

                      <button className="btn" disabled={busy || frozen} title={frozen ? frozenTitle : undefined} onClick={() => sendBroadcast(boxKey)}>

                        Send broadcast now

                      </button>

                    </div>

                  )}

                </div>

              )}

            </div>

          );

        })}

      </ViewOnlyGate>

    </div>

  );

}



// RULES THAT SEND ON A COMPETITION'S CLOCK (1.492.0). Asked for on
// 27 Sep 2026: "a Scheduler subtab under Email too so that emails can be
// pushed to the active email provider at certain times automatically, X
// amount of time before the next competition starts".
//
// A RULE IS OFF WHEN IT IS MADE, and the row says when it would next go
// and how many addresses that is BEFORE anybody switches it on. A panel
// that emails four hundred people on a saved form is a panel nobody
// should trust.
const SCHEDULE_TRIGGER_LABELS = {
  beforeStart: "before a competition starts",
  afterEnd: "after a competition ends",
};
const SCHEDULE_AUDIENCE_LABELS = {
  subscribers: "everyone subscribed",
  reminders: "reminder sign-ups",
  accounts: "account holders opted in",
  entrants: "that competition's entrants",
};
// "any" is the built-in reminder's and only its: a sign-up names the
// competition, so the competition's own mode decides. See
// ensureBuiltInSchedules.
const SCHEDULE_MODE_LABELS = { live: "Live", test: "Test", any: "every" };
const SCHEDULE_MINUTE_CHOICES = [5, 15, 30, 60, 120, 360, 720, 1440];
const scheduleMinuteLabel = (m) =>
  m < 60 ? `${m} minutes` : m % 60 === 0 ? `${m / 60} hour${m === 60 ? "" : "s"}` : `${m} minutes`;
function EmailSchedulerPanel({ token, canEdit }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(null);
  const [form, setForm] = useState(null);

  const load = () => {
    wsRequest("admin.email.schedules", {}, token)
      .then(setData)
      .catch((err) => showToast(err.message || "Could not load the rules.", "error"));
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(load, [token]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => onBroadcast((m) => {
    if (m.type === "email-schedules-changed" || m.type === "email-provider-changed") load();
  }), [token]);

  const act = async (what, action, payload, done) => {
    setBusy(what);
    try {
      const r = await wsRequest(action, payload, token);
      load();
      if (done) done(r);
    } catch (err) {
      showToast(err.message || "That did not work.", "error");
    } finally {
      setBusy(null);
    }
  };

  if (!data) return <p className="sub">Loading the rules…</p>;
  const when = (ms) => (ms ? new Date(ms).toLocaleString() : "—");
  const blank = { name: "", box: "", trigger: { kind: "beforeStart", minutes: 60 }, audience: "subscribers", mode: "live" };
  const editing = form || null;
  const boxesFor = (kind) => data.boxes.filter((b) => b.triggers.includes(kind));

  return (
    <div data-email-scheduler="">
      <p style={{ fontSize: 13, opacity: 0.8, margin: "0 0 10px" }}>
        Rules that send one of your email boxes on a competition&rsquo;s clock. A rule you make is created switched{" "}
        <strong>off</strong>, and its row says when it would next go and how many addresses that is, so nothing is a
        surprise. Unsubscribed, bounced and complained addresses are never included.
      </p>
      <p data-scheduler-reminder-note="" style={{ fontSize: 13, opacity: 0.8, margin: "0 0 10px" }}>
        The <strong>reminder</strong> people sign up for is one of these rules &mdash; the built-in one. Every email
        a rule sends is handed to the provider and then tracked per address: confirmed, reported failed, or still
        with them. Competitions &rarr; Timeline shows the same stages against each competition.
      </p>
      {!data.sendingEnabled && (
        <p data-scheduler-sending-off="" style={{ fontSize: 12.5, color: "var(--accent-yellow)", margin: "0 0 10px" }}>
          <strong>Email sending is switched off</strong> under Providers, so no rule will fire while that is the
          case &mdash; and a rule that was due while it was off does not catch up afterwards.
        </p>
      )}
      {!data.rules.length && !editing ? (
        <p className="sub">No rules yet.</p>
      ) : null}
      {data.rules.map((rule) => (
        <div key={rule.id} data-schedule-row={rule.id} style={{ ...QUICK_START_CARD, display: "block", marginBottom: 10 }}>
          <div style={{ display: "flex", gap: 10, alignItems: "baseline", flexWrap: "wrap" }}>
            <strong style={{ fontSize: 13 }}>{rule.name}</strong>
            <span style={{ fontSize: 12.5, opacity: 0.85 }}>
              {data.boxes.find((b) => b.key === rule.box)?.label || rule.box} &middot;{" "}
              {rule.trigger?.minutes} min {SCHEDULE_TRIGGER_LABELS[rule.trigger?.kind]} &middot;{" "}
              {SCHEDULE_AUDIENCE_LABELS[rule.audience]} &middot;{" "}
              {SCHEDULE_MODE_LABELS[rule.mode] || "Live"} queue
            </span>
            {rule.builtin && (
              <span
                data-schedule-builtin={rule.id}
                style={{ fontSize: 11.5, padding: "1px 6px", borderRadius: 4, background: "var(--bg-panel-alt)", border: "1px solid var(--border-color)", opacity: 0.9 }}
              >
                Built in
              </span>
            )}
            <span
              data-schedule-state={rule.enabled ? "on" : "off"}
              style={{ fontSize: 12.5, marginLeft: "auto", color: rule.enabled ? "var(--accent-green)" : "var(--text-secondary)" }}
            >
              {rule.enabled ? "On" : "Off"}
            </span>
          </div>
          <p style={{ margin: "6px 0 0", fontSize: 12.5, opacity: 0.8 }}>
            {rule.nextDueAt ? (
              <>
                Next due <strong>{when(rule.nextDueAt)}</strong> for &ldquo;{rule.nextCompetition?.title}&rdquo;,
                reaching <strong>{rule.wouldReach}</strong> address{rule.wouldReach === 1 ? "" : "es"}.
              </>
            ) : (
              <>Nothing due: no competition in the {rule.mode === "test" ? "Test" : "Live"} queue matches it yet.</>
            )}
            {rule.lastFiredAt ? <> Last sent {when(rule.lastFiredAt)}.</> : null}
          </p>
          {rule.builtin && (
            <p data-schedule-builtin-note="" style={{ margin: "6px 0 0", fontSize: 12.5, opacity: 0.8 }}>
              This is the reminder people sign up for on the site and through the widget. It watches every queue,
              and its lead time is the same number as <strong>Reminder lead time</strong> under Sitewide settings
              &mdash; change it in either place and the other follows. It can be switched off but not deleted.
            </p>
          )}
          {rule.delivery && (rule.delivery.staged || rule.delivery.sent || rule.delivery.failed) ? (
            <p data-schedule-delivery={rule.id} style={{ margin: "6px 0 0", fontSize: 12.5, opacity: 0.8 }}>
              Delivery: <strong>{rule.delivery.sent}</strong> confirmed,{" "}
              <strong>{rule.delivery.failed}</strong> reported failed,{" "}
              <strong>{rule.delivery.staged}</strong> still with the provider.
            </p>
          ) : null}
          {!rule.boxWritten && (
            <p style={{ margin: "6px 0 0", fontSize: 12.5, color: "var(--accent-yellow)" }}>
              That email box is empty &mdash; write it under Content, or this rule can never send.
            </p>
          )}
          <div style={{ display: "flex", gap: 10, marginTop: 8, flexWrap: "wrap" }}>
            <button
              type="button"
              data-schedule-toggle={rule.id}
              disabled={!canEdit || busy === rule.id}
              onClick={() => act(rule.id, "admin.email.setScheduleEnabled", { id: rule.id, enabled: !rule.enabled })}
            >
              {rule.enabled ? "Switch off" : "Switch on"}
            </button>
            <button type="button" disabled={!canEdit} onClick={() => setForm({ ...rule })}>
              Edit
            </button>
            <button
              type="button"
              data-schedule-test={rule.id}
              disabled={!canEdit || busy === `test-${rule.id}`}
              onClick={() =>
                act(`test-${rule.id}`, "admin.email.testSchedule", { id: rule.id }, (r) =>
                  showToast(`Sent to ${r.sentTo}, built from "${r.competitionTitle}".`, "success")
                )
              }
            >
              Send me a test
            </button>
            {!rule.builtin && (
              <button
                type="button"
                data-schedule-delete={rule.id}
                disabled={!canEdit || busy === `del-${rule.id}`}
                onClick={() => act(`del-${rule.id}`, "admin.email.deleteSchedule", { id: rule.id })}
              >
                Delete
              </button>
            )}
          </div>
        </div>
      ))}
      {editing ? (
        <form
          data-schedule-form=""
          style={{ ...QUICK_START_CARD, display: "block", marginTop: 12 }}
          onSubmit={(e) => {
            e.preventDefault();
            act("save", "admin.email.saveSchedule", editing, () => setForm(null));
          }}
        >
          <strong style={{ fontSize: 13 }}>
            {editing.builtin ? "Edit the reminder" : editing.id ? "Edit rule" : "New rule"}
          </strong>
          {editing.builtin && (
            <p data-schedule-form-builtin="" style={{ margin: "6px 0 0", fontSize: 12.5, opacity: 0.8 }}>
              What it sends and how far ahead are yours to change. Who it goes to and when are not: it is the
              reminder, so it always goes to that competition&rsquo;s own sign-ups before it starts. The lead time
              here is the site&rsquo;s <strong>Reminder lead time</strong> &mdash; the email capture form closes at
              the same number.
            </p>
          )}
          <div style={{ display: "grid", gap: 8, marginTop: 8, maxWidth: 560 }}>
            <label style={{ fontSize: 12.5 }}>
              Name
              <input
                type="text"
                value={editing.name}
                onChange={(e) => setForm({ ...editing, name: e.target.value })}
                style={{ width: "100%", fontSize: 13 }}
              />
            </label>
            <label style={{ fontSize: 12.5 }}>
              Send
              <select
                value={editing.trigger.minutes}
                onChange={(e) => setForm({ ...editing, trigger: { ...editing.trigger, minutes: Number(e.target.value) } })}
                style={{ fontSize: 13, marginLeft: 6 }}
              >
                {/* A SITE MAY ALREADY HAVE A LEAD TIME THAT IS NOT ONE OF
                    THESE - the setting takes any positive number - and a
                    select with no matching option silently shows the
                    first. Its own value is offered alongside them. */}
                {[...new Set([...SCHEDULE_MINUTE_CHOICES, Number(editing.trigger.minutes)])]
                  .filter((m) => Number.isFinite(m) && m > 0)
                  .sort((a, b) => a - b)
                  .map((m) => (
                    <option key={m} value={m}>{scheduleMinuteLabel(m)}</option>
                  ))}
              </select>
              <select
                value={editing.trigger.kind}
                disabled={!!editing.builtin}
                onChange={(e) => {
                  const kind = e.target.value;
                  // THE TRIGGER DECIDES THE BOXES, so a box the new
                  // trigger cannot fill is dropped rather than left to
                  // fail on save.
                  const stillAllowed = boxesFor(kind).some((b) => b.key === editing.box);
                  setForm({ ...editing, trigger: { ...editing.trigger, kind }, box: stillAllowed ? editing.box : "" });
                }}
                style={{ fontSize: 13, marginLeft: 6 }}
              >
                {Object.entries(SCHEDULE_TRIGGER_LABELS).map(([k, label]) => (
                  <option key={k} value={k}>{label}</option>
                ))}
              </select>
            </label>
            <label style={{ fontSize: 12.5 }}>
              This email
              <select
                data-schedule-box=""
                value={editing.box}
                onChange={(e) => setForm({ ...editing, box: e.target.value })}
                style={{ fontSize: 13, marginLeft: 6 }}
              >
                <option value="">Choose…</option>
                {boxesFor(editing.trigger.kind).map((b) => (
                  <option key={b.key} value={b.key}>{b.label}{b.written ? "" : " (empty)"}</option>
                ))}
              </select>
              <span style={{ display: "block", opacity: 0.75, marginTop: 4 }}>
                Only boxes this trigger can fill in are offered &mdash; an email asking for a winner&rsquo;s name
                cannot be sent before the competition has run.
              </span>
            </label>
            <label style={{ fontSize: 12.5 }}>
              To
              <select
                value={editing.audience}
                disabled={!!editing.builtin}
                onChange={(e) => setForm({ ...editing, audience: e.target.value })}
                style={{ fontSize: 13, marginLeft: 6 }}
              >
                {data.audiences.map((a) => (
                  <option key={a} value={a}>{SCHEDULE_AUDIENCE_LABELS[a]}</option>
                ))}
              </select>
              <select
                value={editing.mode}
                disabled={!!editing.builtin}
                onChange={(e) => setForm({ ...editing, mode: e.target.value })}
                style={{ fontSize: 13, marginLeft: 6 }}
              >
                <option value="live">Live queue</option>
                <option value="test">Test queue</option>
                {editing.builtin && <option value="any">Every queue</option>}
              </select>
            </label>
          </div>
          <div style={{ display: "flex", gap: 10, marginTop: 10 }}>
            <button type="submit" disabled={!canEdit || busy === "save"}>Save</button>
            <button type="button" onClick={() => setForm(null)}>Cancel</button>
          </div>
        </form>
      ) : (
        <button type="button" data-schedule-new="" disabled={!canEdit} onClick={() => setForm(blank)} style={{ marginTop: 8 }}>
          New rule
        </button>
      )}
    </div>
  );
}
// EVERY ADDRESS THE SITE KNOWS (1.491.0). Asked for on 27 Sep 2026: a
// Subscribers subtab under Email listing the addresses, with a status,
// the last time each was pushed to the active provider, and a way to
// subscribe or unsubscribe each one.
//
// THE STATUS IS THE ADDRESS'S, not the account's. Suppression used to
// live on the user record, so an address with no account - the widget's
// "Notify me" on a partner's site - could bounce and keep being emailed.
// That is why this list exists as much as the column does.
//
// PAGED AND SEARCHED ON THE SERVER: the list has no ceiling.
const SUBSCRIBER_STATUS = {
  subscribed: { label: "Subscribed", colour: "var(--accent-green)" },
  unsubscribed: { label: "Unsubscribed", colour: "var(--text-secondary)" },
  bounced: { label: "Bounced", colour: "var(--accent-red)" },
  complained: { label: "Complained", colour: "var(--accent-red)" },
};
const PROVIDER_LABELS = { resend: "Resend", brevo: "Brevo", postmark: "Postmark" };
function EmailSubscribersPanel({ token, canEdit }) {
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  // WHICH QUEUE (1.495.0). An address captured on a partner's test site
  // and one captured on their live site are different lists, and mailing
  // one about the other is the mistake this filter exists to prevent.
  const [mode, setMode] = useState("");
  const [page, setPage] = useState(1);
  const [busy, setBusy] = useState(null);

  const load = () => {
    wsRequest("admin.email.subscribers", { q, status, mode, page, perPage: 50 }, token)
      .then(setData)
      .catch((err) => showToast(err.message || "Could not load the subscribers.", "error"));
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(load, [token, q, status, mode, page]);
  // The same idiom as the timeline: onBroadcast returns its own
  // unsubscribe, which is what the effect hands back.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => onBroadcast((message) => {
    if (message.type === "email-subscribers-changed") load();
  }), [token, q, status, mode, page]);

  const setRowStatus = async (email, next) => {
    setBusy(email);
    try {
      await wsRequest("admin.email.setSubscriberStatus", { email, status: next }, token);
      load();
      showToast(next === "subscribed" ? `${email} will receive email again.` : `${email} unsubscribed.`, "success");
    } catch (err) {
      showToast(err.message || "Could not change that address.", "error");
    } finally {
      setBusy(null);
    }
  };

  // THE WHOLE LIST, not the page on screen - the server builds it, for
  // the same reason it pages: what is on screen is not what was asked for.
  const downloadCsv = async () => {
    setBusy("csv");
    try {
      const { filename, csv } = await wsRequest("admin.email.subscribersCsv", {}, token);
      const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      showToast(err.message || "Could not build the CSV.", "error");
    } finally {
      setBusy(null);
    }
  };

  if (!data) return <p className="sub">Loading the subscribers…</p>;
  const provider = PROVIDER_LABELS[data.activeProvider] || "the provider";
  const pages = Math.max(1, Math.ceil(data.total / data.perPage));
  const when = (ms) => (ms ? new Date(ms).toLocaleString() : "—");

  return (
    <div data-email-subscribers="">
      <p style={{ fontSize: 13, opacity: 0.8, margin: "0 0 10px" }}>
        Every address the site knows: reminder sign-ups from your own site and from a partner&rsquo;s, account
        holders, users a partner vouched for who gave a real address, and anything {provider} has reported on. <strong>An address that is not subscribed receives no
        email at all</strong>, whether or not it has an account here.
      </p>
      <p data-subscribers-queue-note="" style={{ fontSize: 13, opacity: 0.8, margin: "0 0 10px" }}>
        <strong>Queue</strong> is which side an address belongs to, and it is what keeps the two apart: a
        Scheduler rule on the Live queue never emails an address captured on your partner&rsquo;s test site. Where
        an address arrived through the API, the domain it came from is shown beside it &mdash; one that has
        arrived on <em>both</em> is highlighted, because that is the case worth looking at.
      </p>
      {/* THE HEADLINE NUMBER (1.500.0), because the status counts in the
          filter below answer a different question and "subscribed (10)"
          reads as "ten people want mail". */}
      <p data-subscribers-headline="" style={{ fontSize: 13, margin: "0 0 10px" }}>
        <strong>{(data.updatesCounts?.wants ?? 0) + (data.updatesCounts?.no ?? 0)}</strong> addresses, of which{" "}
        <strong>{data.updatesCounts?.wants ?? 0}</strong>{" "}
        {(data.updatesCounts?.wants ?? 0) === 1 ? "wants" : "want"} site updates &mdash; that second number is
        what a scheduled email to &ldquo;everyone subscribed&rdquo; actually reaches.
      </p>
      <p data-subscribers-updates-note="" style={{ fontSize: 13, opacity: 0.8, margin: "0 0 10px" }}>
        <strong>Wants updates</strong> is a different question from the status. The status decides whether the
        address receives <em>anything</em> &mdash; bounced, complained or unsubscribed and it gets nothing, not
        even a receipt. Wanting updates decides whether a scheduled email to <em>everyone subscribed</em> reaches
        them: a reminder sign-up asked for them, and an account holder is asked on their own account page. Someone
        who has an account but never ticked the box still gets their receipts; they are simply not broadcast to.
      </p>
      {/* THE RESEND MIRROR (1.499.0). Shown only when Resend is the
          active provider, because with another provider there is no
          mirror to describe. When it IS active and switched off, that is
          the sentence most worth having: the reason is a blank field on
          another tab, which nobody would think to look at. */}
      {data.activeProvider === "resend" && (
        <p data-subscribers-sync-note="" style={{ fontSize: 13, opacity: 0.8, margin: "0 0 10px" }}>
          <strong>Resend contacts.</strong>{" "}
          {!data.contactSync?.on ? (
            <>
              Nothing on this list is being mirrored to Resend yet &mdash;{" "}
              {data.contactSync && data.contactSync.hasContactsKey === false
                ? "contacts need a full-access Resend key, which the sending key is not."
                : "paste a segment id"}{" "}
              under <em>Email &rarr; Providers</em> to set it up.
            </>
          ) : (
            <>
              Each address here is a contact in Resend, in the segment for its queue, and unsubscribing one
              here marks it unsubscribed there.{" "}
              {data.contactSync.pending
                ? `${data.contactSync.pending} waiting to be sent.`
                : "Everything is in step."}{" "}
              {data.contactSync.failing
                ? `${data.contactSync.failing} could not be updated — the reason is shown beside the address.`
                : ""}
              {!data.contactSync.testSegment ? " Test addresses are not mirrored: no test segment is set." : ""}
              {!data.contactSync.liveSegment ? " Live addresses are not mirrored: no live segment is set." : ""}
              {" "}An address <em>already</em> in your Resend account is added to its queue&rsquo;s segment as well
              as being updated, so every address here ends up in the right list whether we created it or not.
              It is not <em>removed</em> from any other segment it was already in.
            </>
          )}
        </p>
      )}
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center", marginBottom: 12 }}>
        <input
          type="search"
          value={q}
          placeholder="Search addresses"
          onChange={(e) => { setPage(1); setQ(e.target.value); }}
          style={{ fontSize: 13, minWidth: 220 }}
          aria-label="Search addresses"
        />
        <select
          value={status}
          onChange={(e) => { setPage(1); setStatus(e.target.value); }}
          style={{ fontSize: 13 }}
          aria-label="Filter by status"
        >
          <option value="">All ({Object.values(data.counts || {}).reduce((a, b) => a + b, 0)})</option>
          {Object.entries(SUBSCRIBER_STATUS).map(([key, s]) => (
            <option key={key} value={key}>{`${s.label} (${data.counts?.[key] || 0})`}</option>
          ))}
        </select>
        <select
          data-subscribers-queue=""
          value={mode}
          onChange={(e) => { setPage(1); setMode(e.target.value); }}
          style={{ fontSize: 13 }}
          aria-label="Filter by queue"
        >
          <option value="">Every queue ({Object.values(data.modeCounts || {}).reduce((a, b) => a + b, 0)})</option>
          <option value="live">Live ({data.modeCounts?.live || 0})</option>
          <option value="test">Test ({data.modeCounts?.test || 0})</option>
          <option value="demo">Demo ({data.modeCounts?.demo || 0})</option>
        </select>
        <button type="button" onClick={downloadCsv} disabled={busy === "csv"}>
          {busy === "csv" ? "Building…" : "Download CSV"}
        </button>
      </div>
      {!data.rows.length ? (
        <p className="sub">
          {q || status ? "No address matches that." : "No addresses yet — the first sign-up or account will appear here."}
        </p>
      ) : (
        <div style={{ overflowX: "auto" }}>
          <table data-subscribers-table="" style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr>
                {/* "Wants updates" IS ITS OWN COLUMN (1.501.0) - see the
                    cell below for why. */}
                {["Address", "Status", "Wants updates", "Queue", "Where from", `Last pushed to ${provider}`, "Last report", ""].map((h) => (
                  <th key={h} style={{ textAlign: "left", padding: "6px 10px 6px 0", borderBottom: "1px solid var(--border-color)", opacity: 0.7 }}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.rows.map((r) => {
                const meta = SUBSCRIBER_STATUS[r.status] || { label: r.status, colour: "inherit" };
                return (
                  <tr key={r.email} data-subscriber-row={r.email}>
                    <td style={{ padding: "6px 10px 6px 0", borderBottom: "1px solid var(--border-color)" }}>
                      {r.email}
                      {r.userId ? <span style={{ opacity: 0.6 }}> · account</span> : null}
                    </td>
                    <td style={{ padding: "6px 10px 6px 0", borderBottom: "1px solid var(--border-color)", color: meta.colour }}>
                      {meta.label}
                      {r.statusReason ? <span style={{ opacity: 0.7, color: "var(--text-secondary)" }}> — {r.statusReason}</span> : null}
                      {r.statusByProvider ? <span style={{ opacity: 0.7, color: "var(--text-secondary)" }}> ({PROVIDER_LABELS[r.statusByProvider] || r.statusByProvider})</span> : null}
                    </td>
                    {/* WOULD A BROADCAST REACH THEM (1.497.0), in a column
                        of its own since 1.501.0. It sat under "Where from"
                        - which is not where from - so the status and this
                        read as one answer when they are two, and the
                        Unsubscribe button looked like it acted on this one.
                        Beside the status is where it belongs: the two
                        questions about email, side by side, plainly
                        different. */}
                    <td data-subscriber-updates={r.email} style={{ padding: "6px 10px 6px 0", borderBottom: "1px solid var(--border-color)", opacity: 0.8 }}>
                      {r.askedForUpdates ? "Yes" : "No"}
                    </td>
                    {/* THE QUEUE, AND THE DOMAIN IT ARRIVED ON (1.495.0).
                        A row with no queue recorded reads as Live, which
                        is what it did before the column existed. */}
                    <td data-subscriber-queue={r.email} style={{ padding: "6px 10px 6px 0", borderBottom: "1px solid var(--border-color)", opacity: 0.8 }}>
                      {r.mode === "test" ? "Test" : r.mode === "demo" ? "Demo" : "Live"}
                      {(r.sites || []).length ? (
                        <span
                          style={{
                            // NOT DIMMED WHEN IT IS THE WARNING. The cell
                            // is already at 0.8; 0.7 on top of that put
                            // the highlight back to the colour of
                            // everything around it - measured, not judged.
                            opacity: (r.sites || []).length > 1 ? 1 : 0.7,
                            color: (r.sites || []).length > 1 ? "var(--accent-yellow)" : "var(--text-secondary)",
                          }}
                          title={r.lastCapturedHost || ""}
                        >
                          {" "}&middot; {(r.sites || []).map((x) => (x === "test" ? "test site" : "live site")).join(" + ")}
                        </span>
                      ) : null}
                    </td>
                    <td style={{ padding: "6px 10px 6px 0", borderBottom: "1px solid var(--border-color)", opacity: 0.8 }}>
                      {(r.sources || []).join(", ") || "—"}
                      {/* WHY RESEND REFUSED THIS ONE (1.499.0). Beside the
                          address rather than in a column of its own: it is
                          empty for every row on a healthy site, and a column
                          that is always blank teaches people to stop
                          reading it. */}
                      {r.resendSyncError ? (
                        <span
                          data-subscriber-sync-error={r.email}
                          title={r.resendSyncError}
                          style={{ display: "block", fontSize: 12, color: "var(--accent-red, #e2686d)" }}
                        >
                          Resend: {r.resendSyncError}
                        </span>
                      ) : null}
                    </td>
                    <td style={{ padding: "6px 10px 6px 0", borderBottom: "1px solid var(--border-color)", opacity: 0.8 }}>
                      {when(r.lastPushedAt)}
                      {r.lastPushedBox ? <span style={{ opacity: 0.7 }}> · {r.lastPushedBox}</span> : null}
                    </td>
                    <td style={{ padding: "6px 10px 6px 0", borderBottom: "1px solid var(--border-color)", opacity: 0.8 }}>
                      {r.lastEvent ? `${r.lastEvent} · ${when(r.lastEventAt)}` : "—"}
                    </td>
                    <td style={{ padding: "6px 0", borderBottom: "1px solid var(--border-color)", textAlign: "right" }}>
                      <button
                        type="button"
                        data-subscriber-toggle={r.email}
                        // WHICH OF THE TWO IT ACTS ON (1.501.0). Asked on
                        // 27 Sep 2026 whether this should read "Subscribe"
                        // for a row that does not want updates. It should
                        // not - it governs ALL email - but nothing on the
                        // button said so.
                        title={
                          r.status === "subscribed"
                            ? "Stops every email to this address, receipts and password resets included. This is not the Wants updates column."
                            : "Lets this site email the address again. This is not the Wants updates column."
                        }
                        disabled={!canEdit || busy === r.email}
                        onClick={() => setRowStatus(r.email, r.status === "subscribed" ? "unsubscribed" : "subscribed")}
                      >
                        {r.status === "subscribed" ? "Unsubscribe" : "Subscribe"}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {pages > 1 && (
        <div style={{ display: "flex", gap: 10, alignItems: "center", marginTop: 12, fontSize: 13 }}>
          <button type="button" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={data.page <= 1}>
            Previous
          </button>
          <span style={{ opacity: 0.8 }}>
            Page {data.page} of {pages} — {data.total} address{data.total === 1 ? "" : "es"}
          </span>
          <button type="button" onClick={() => setPage((p) => Math.min(pages, p + 1))} disabled={data.page >= pages}>
            Next
          </button>
        </div>
      )}
      {/* REINSTATING A BOUNCE IS A DECISION, so it says so rather than
          being a button that quietly undoes what a provider reported. */}
      <p style={{ fontSize: 12.5, opacity: 0.75, marginTop: 12 }}>
        Subscribing an address that bounced or complained tells this site to email it again; it does not tell{" "}
        {provider} anything. If the address is genuinely undeliverable it will simply bounce again, and a repeated
        spam complaint is what damages a sending domain. Every change here is recorded in the activity log.{" "}
        <strong>Subscribe and Unsubscribe govern the Status column, not Wants updates</strong> &mdash;
        unsubscribing somebody here stops their receipts and password resets too, which is rarely what is meant
        by &ldquo;they don&rsquo;t want the newsletter&rdquo;. Wanting updates is the person&rsquo;s own setting on
        their account page.
      </p>
    </div>
  );
}
function EmailActivityPanel({ token, modeStatus, canEdit }) {

  const [data, setData] = useState(null);

  const [busy, setBusy] = useState(false);



  const load = () => {

    wsRequest("admin.emailActivity.list", {}, token)

      .then(setData)

      .catch(() => {});

  };

  useEffect(load, [token]);



  // DEMO ONLY. These are settings, and the server agrees — see
  // assertSettingsEditable. Freezing them in Test mode meant switching
  // to Live to save a form and back again to carry on testing.
  const frozen = adminMode(modeStatus) === "demo";

  const frozenTitle = "Leave Demo mode to manage this";



  const reactivate = async (userId) => {

    setBusy(true);

    try {

      await wsRequest("admin.emailActivity.reactivate", { userId }, token);

      load();

      showToast("Account reactivated.", "success");

    } catch (err) {

      showToast(err.message || "Could not reach backend.", "error");

    }

    setBusy(false);

  };



  if (data === null) return <p>Loading…</p>;



  const eventLabels = { delivered: "Delivered", hard_bounce: "Hard bounce", soft_bounce: "Soft bounce", spam_complaint: "Spam complaint" };

  const providerColumns = [

    { key: "resend", label: "Resend" },

    { key: "brevo", label: "Brevo" },

    { key: "postmark", label: "Postmark" },

    { key: "total", label: "Total" },

  ];



  return (

    <div style={{ marginTop: 16, maxWidth: 720 }}>

      <h3>Email activity</h3>

      <p style={{ opacity: 0.7, fontSize: 13, marginTop: -6 }}>

        Bounce, complaint, and delivery events reported by whichever third-party provider webhooks are configured

        (see Providers) — broken down per provider, since root can switch which one's active over time and a single

        combined count wouldn't tell you whether a problem is specific to one of them. A hard bounce or spam

        complaint automatically suppresses the account from every future email, transactional included, until

        manually reactivated below.

      </p>



      <table style={{ width: "100%", borderCollapse: "collapse", marginBottom: 24, fontSize: 13 }}>

        <thead>

          <tr>

            <th style={{ textAlign: "left", padding: "6px 10px", borderBottom: "1px solid var(--border-color)", opacity: 0.65, fontWeight: 500 }}>

              &nbsp;

            </th>

            {providerColumns.map((p) => (

              <th

                key={p.key}

                style={{

                  textAlign: "right",

                  padding: "6px 10px",

                  borderBottom: "1px solid var(--border-color)",

                  fontWeight: p.key === "total" ? 600 : 500,

                  opacity: p.key === "total" ? 1 : 0.65,

                }}

              >

                {p.label}

              </th>

            ))}

          </tr>

        </thead>

        <tbody>

          {Object.entries(eventLabels).map(([typeKey, typeLabel]) => (

            <tr key={typeKey}>

              <td style={{ padding: "6px 10px", borderBottom: "1px solid var(--border-color)", opacity: 0.8 }}>{typeLabel}</td>

              {providerColumns.map((p) => (

                <td

                  key={p.key}

                  style={{

                    textAlign: "right",

                    padding: "6px 10px",

                    borderBottom: "1px solid var(--border-color)",

                    fontWeight: p.key === "total" ? 600 : 400,

                  }}

                >

                  {data.stats[p.key]?.[typeKey] || 0}

                </td>

              ))}

            </tr>

          ))}

        </tbody>

      </table>



      <h4 style={{ marginBottom: 8 }}>Suppressed accounts ({data.suppressed.length})</h4>

      {data.suppressed.length === 0 ? (

        <p style={{ opacity: 0.6, fontSize: 13 }}>No accounts are currently suppressed.</p>

      ) : (

        <div style={{ marginBottom: 24 }}>

          {data.suppressed.map((s) => (

            <div

              key={s.userId}

              style={{

                display: "flex",

                justifyContent: "space-between",

                alignItems: "center",

                border: "1px solid var(--border-color)",

                borderRadius: 8,

                padding: "8px 12px",

                marginBottom: 8,

              }}

            >

              <div>

                <strong style={{ fontSize: 13.5 }}>{s.email}</strong>

                <div style={{ fontSize: 12, opacity: 0.65 }}>

                  {s.username ? `${s.username} — ` : ""}

                  {s.reason || "unknown reason"}

                  {s.provider ? ` via ${s.provider}` : ""}

                  {s.suppressedAt ? ` — ${siteDateTime(s.suppressedAt)}` : ""}

                </div>

              </div>

              {canEdit && (

                <button className="btn" disabled={busy || frozen} title={frozen ? frozenTitle : undefined} onClick={() => reactivate(s.userId)}>

                  Reactivate

                </button>

              )}

            </div>

          ))}

        </div>

      )}



      <h4 style={{ marginBottom: 8 }}>Recent events</h4>

      {data.events.length === 0 ? (

        <p style={{ opacity: 0.6, fontSize: 13 }}>No events recorded yet.</p>

      ) : (

        <div style={{ maxHeight: 400, overflowY: "auto", border: "1px solid var(--border-color)", borderRadius: 8 }}>

          {data.events.map((e) => (

            <div key={e.id} style={{ padding: "6px 12px", borderBottom: "1px solid var(--border-color)", fontSize: 12.5 }}>

              <span style={{ opacity: 0.6 }}>{siteDateTime(e.createdAt)}</span> — {eventLabels[e.type] || e.type} —{" "}

              <strong>{e.email}</strong> — <span style={{ opacity: 0.6 }}>{e.provider}</span>

            </div>

          ))}

        </div>

      )}

    </div>

  );

}

// uses (JS, HTML, PHP, HTTP/curl-style), not a full, precise

// per-language parser. Verified separately to reconstruct every

// sample and real file tested (up to 80KB) back to its exact original

// text — critical, since any mismatch here would mean losing or

// corrupting displayed code.

const HIGHLIGHT_TOKEN_PATTERNS = [

  { type: "comment", re: /^(?:\/\/[^\n]*|\/\*[\s\S]*?\*\/|<!--[\s\S]*?-->)/ },

  { type: "comment", re: /^#[^\n]*/ }, // bash/PHP-style line comments

  { type: "string", re: /^(?:'(?:[^'\\]|\\.)*'|"(?:[^"\\]|\\.)*"|`(?:[^`\\]|\\.)*`)/ },

  { type: "tag", re: /^<\/?[a-zA-Z][a-zA-Z0-9-]*/ },

  { type: "variable", re: /^\$[a-zA-Z_][a-zA-Z0-9_]*/ },

  {

    type: "keyword",

    re: /^\b(function|const|let|var|return|if|else|for|foreach|while|new|async|await|throw|try|catch|class|extends|import|export|from|this|typeof|instanceof|null|true|false|undefined|public|private|require|echo|exit|use)\b/,

  },

  { type: "method", re: /^\b(GET|POST|PATCH|DELETE|PUT|Header|Body)\b(?=[\s:])/ },

  { type: "number", re: /^\b\d+\.?\d*\b/ },

  { type: "attr", re: /^\b[a-zA-Z-]+(?==)/ }, // html attribute names

];



const HIGHLIGHT_COLORS = {

  comment: "#8b949e",

  string: "#a5d6ff",

  keyword: "#ff7b72",

  number: "#79c0ff",

  variable: "#ffa657",

  tag: "#7ee787",

  attr: "#79c0ff",

  method: "#d2a8ff",

};



function highlightCode(code) {

  const tokens = [];

  let i = 0;

  while (i < code.length) {

    let matched = false;

    const remaining = code.slice(i);

    for (const { type, re } of HIGHLIGHT_TOKEN_PATTERNS) {

      const m = re.exec(remaining);

      if (m && m.index === 0 && m[0].length > 0) {

        tokens.push({ text: m[0], type });

        i += m[0].length;

        matched = true;

        break;

      }

    }

    if (!matched) {

      if (tokens.length && tokens[tokens.length - 1].type === "plain") {

        tokens[tokens.length - 1].text += code[i];

      } else {

        tokens.push({ text: code[i], type: "plain" });

      }

      i += 1;

    }

  }

  return tokens;

}



// GuideCode, GuideEndpoint, GuideTable, GuideNote and GuideWarn were
// here — 209 lines of presentation for a guide this file no longer
// contains. The panel renders the built HTML now, which carries its own
// styling, so nothing referenced them.

function HighlightedCode({ code }) {

  const tokens = React.useMemo(() => highlightCode(code), [code]);

  return (

    <>

      {tokens.map((t, i) =>

        t.type === "plain" ? (

          t.text

        ) : (

          <span key={i} style={{ color: HIGHLIGHT_COLORS[t.type] }}>

            {t.text}

          </span>

        )

      )}

    </>

  );

}



// Small, consistent building blocks the guide below is built from —

// kept separate so the actual content (the part that has to stay

// accurate) isn't buried in repeated inline styling. Renders

// syntax-highlighted by default ("Source"), with a toggle to plain,

// unhighlighted text ("Raw") for easy copy-pasting.
















// The in-page equivalent of the downloadable PDF — same content, same

// order, deliberately kept in sync by hand (see the comment on the PDF

// source file itself) so a reader sees the same guide regardless of

// which format they pick.

// Shared by IntegrationGuidePanel's PDF link and WidgetDownloadPanel's

// zip link below — both now sit behind requirePermission on the

// backend, so a plain <a href download> can no longer work (it can't

// attach the x-auth-token header); this fetches with that header

// attached, then triggers the actual save via the same blob+object-URL

// pattern WidgetCodeBlock already uses elsewhere in this file.

// ALWAYS pass a same-origin url (use `${API_BASE}/...`), never an

// absolute one pointing at a particular deployment.

//

// This sends the admin's auth token as a header, so an absolute URL

// sends one deployment's admin token to another deployment's server.

// Four call sites on this screen did exactly that — hardcoded to

// 589comps.app — which meant a second partner's admin clicking

// "download the guide" would have shipped their token to a server that

// was never going to accept it anyway.

function downloadWithAuth(url, token, filename, onError) {

  fetch(url, { headers: { "x-auth-token": token } })

    .then((res) => {

      if (!res.ok) throw new Error("Download failed — you may need to log in again.");

      return res.blob();

    })

    .then((blob) => {

      const a = document.createElement("a");

      a.href = URL.createObjectURL(blob);

      a.download = filename;

      a.click();

      URL.revokeObjectURL(a.href);

    })

    .catch((err) => onError?.(err.message || "Download failed."));

}





// Testing -> Coverage. How much of the backend the test suite actually

// runs.

//

// DELIBERATELY NOT A SINGLE REASSURING NUMBER.

//

// One site-wide percentage hides everything worth knowing: a codebase

// can sit at a comfortable-looking 80% while the payment paths are at

// 30%. So this shows per-file figures, worst first, and gives the count

// of unexecuted lines as much room as the percentage.

//

// It also says plainly what the number is not. Coverage measures lines

// EXECUTED, not behaviour VERIFIED — a line that runs during a test

// asserting nothing about it counts exactly the same as one that is

// carefully checked. Low coverage is real evidence of a gap; high

// coverage is only the absence of that particular evidence.

function CoverageBar({ pct }) {

  // Red through amber to green, but the thresholds are deliberately

  // unflattering: 90% is not "done", it is "nothing obviously missing".

  const colour = pct >= 90 ? "var(--accent-green)" : pct >= 60 ? "var(--accent-yellow)" : "var(--accent-red)";

  return (

    <div style={{ background: "var(--bg-input)", borderRadius: 3, height: 8, overflow: "hidden", minWidth: 90 }}>

      <div style={{ width: `${Math.max(0, Math.min(100, pct))}%`, background: colour, height: "100%" }} />

    </div>

  );

}



function CoveragePanel({ token }) {

  const [report, setReport] = useState(undefined); // undefined = loading

  const [error, setError] = useState(null);



  useEffect(() => {

    fetch(`${API_BASE}/api/coverage`, { headers: { "x-auth-token": token } })

      .then(async (res) => {

        const body = await res.json();

        if (!res.ok) throw new Error(body.error || "Could not load the coverage report.");

        return body;

      })

      .then(setReport)

      .catch((err) => setError(err.message));

  }, [token]);



  if (error) return <p style={{ color: "var(--accent-red)" }}>{error}</p>;

  if (report === undefined) return <p style={{ opacity: 0.7 }}>Loading…</p>;



  if (!report.generated) {

    return (

      <div>

        <h3 style={{ marginTop: 0 }}>Coverage</h3>

        <p style={{ opacity: 0.8, fontSize: 13.5, lineHeight: 1.6 }}>

          No coverage report has shipped with this version. The report is generated once per release by

          <code> ./coverage-run.sh</code> and committed with the code, so every installation carries the same

          measurement for the version it is running — a canonical figure taken on a known configuration, rather than

          whatever this particular server happens to reach. The suite skips some tests depending on how an instance is

          set up, so the same code can measure differently in different places.

        </p>

      </div>

    );

  }



  const total = report.total || {};

  const generated = report.generatedAt ? new Date(report.generatedAt) : null;

  // A version comparison, not an age. "Nine days old" says nothing on

  // its own — a report from last month is perfectly accurate if the code

  // has not changed since. What matters is whether it describes the

  // version actually running.

  const stale = report.version && report.runningVersion && report.version !== report.runningVersion;



  return (

    <div>

      <h3 style={{ marginTop: 0 }}>Coverage</h3>



      <p style={{ fontSize: 13, opacity: 0.75, lineHeight: 1.6, maxWidth: 720 }}>

        The share of backend lines the destructive suite actually executes. <strong>Executed is not verified</strong> — a

        line that runs during a test which asserts nothing about it counts the same as one that is carefully checked.

        Read a low figure as evidence of a gap, and a high one only as the absence of that evidence.

      </p>



      <p style={{ fontSize: 12.5, opacity: 0.75 }}>

        Measured against <strong>v{report.version || "unknown"}</strong>

        {generated && ` on ${siteDate(generated)}`}. This site is running v{report.runningVersion}.

      </p>



      {stale && (

        <>

        <p style={{ fontSize: 12.5, color: "var(--accent-yellow)" }}>

          These figures describe v{report.version}, not the version running here. The code has changed since, so treat

          them as indicative until the report is regenerated.

        </p>

        {/* THE COMMAND, not a button — and whether that is right is an
            OPEN QUESTION, recorded in TODO.md.

            The argument for committing is that the report is a CANONICAL
            measurement: what the suite reached on a known configuration.
            It is NOT simply "a property of the code", which is what the
            empty-state text above still claims — the suite SKIPS tests
            based on instance state (no payment provider configured, no
            confirmed payments), so the same code genuinely produces
            different figures on differently-configured servers.

            The argument against is that a manual commit step is a step
            that gets skipped, which is exactly why these figures sat six
            days out of date.

            Two practical constraints hold either way: the run takes the
            test instance out of service for a few minutes, and
            coverage-run.sh writes outside the repo deliberately, because
            a dirty working copy blocks the next deploy. */}
        <p style={{ fontSize: 12.5, opacity: 0.75 }}>

          To regenerate: run <code style={{ fontFamily: "monospace" }}>./coverage-run.sh</code> on a server, copy the
          summary it writes into the working repo, and release it. It takes a few minutes and holds the test instance
          while it runs, so it is not part of a deploy.

        </p>

        {/* A COPY BUTTON, NOT A RUN BUTTON, and deliberately.
            Running this from here would stop the test instance for a few
            minutes on a click, and write into the working repo — a dirty
            copy blocks the next deploy, which is exactly why the script is
            invoked by hand.
            What was actually missing was remembering what it is called.
            Added 6 Sep 2026. */}
        <div style={{ display: "flex", gap: 6, alignItems: "center", marginBottom: 10 }}>
          <button
            type="button"
            onClick={() => {
              navigator.clipboard?.writeText("cd /var/www/589comps && ./coverage-run.sh");
              showToast("Command copied — run it on a server.");
            }}
          >
            Copy the command
          </button>
          <span style={{ fontSize: 12, opacity: 0.6, fontFamily: "monospace" }}>
            cd /var/www/589comps &amp;&amp; ./coverage-run.sh
          </span>
        </div>

        </>

      )}



      {report.testsPassed === false && (

        <p style={{ fontSize: 12.5, color: "var(--accent-yellow)" }}>

          The suite did not pass on the run that produced these figures, so they describe an incomplete run.

        </p>

      )}



      {["lines", "branches", "functions"].map((kind) =>

        total[kind] ? (

          <div key={kind} style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 8 }}>

            <div style={{ width: 80, fontSize: 13, textTransform: "capitalize" }}>{kind}</div>

            <div style={{ flex: "0 0 200px" }}>

              <CoverageBar pct={total[kind].pct} />

            </div>

            <div style={{ fontSize: 13, minWidth: 60 }}>{total[kind].pct}%</div>

            <div style={{ fontSize: 12.5, opacity: 0.7 }}>

              {(total[kind].total - total[kind].covered).toLocaleString()} of {total[kind].total.toLocaleString()} never

              run

            </div>

          </div>

        ) : null

      )}



      <h4 style={{ marginTop: 24, marginBottom: 8 }}>By file, least covered first</h4>

      <div style={{ fontSize: 12.5, opacity: 0.7, marginBottom: 10 }}>

        This ordering is the point — the files at the top are where a bug would go unnoticed.

      </div>

      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>

        <thead>

          <tr style={{ textAlign: "left", opacity: 0.7 }}>

            <th style={{ padding: "6px 8px 6px 0" }}>File</th>

            <th style={{ padding: "6px 8px", width: 220 }}>Lines</th>

            <th style={{ padding: "6px 8px", width: 140 }}>Never run</th>

          </tr>

        </thead>

        <tbody>

          {(report.files || []).map((f) => (

            <tr key={f.file} style={{ borderTop: "1px solid var(--border-color)" }}>

              <td style={{ padding: "6px 8px 6px 0", fontFamily: "monospace", fontSize: 12.5 }}>{f.file}</td>

              <td style={{ padding: "6px 8px" }}>

                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>

                  <div style={{ flex: 1 }}>

                    <CoverageBar pct={f.lines.pct} />

                  </div>

                  <span style={{ minWidth: 44, textAlign: "right" }}>{f.lines.pct}%</span>

                </div>

              </td>

              <td style={{ padding: "6px 8px", opacity: 0.8 }}>

                {(f.lines.total - f.lines.covered).toLocaleString()}

              </td>

            </tr>

          ))}

        </tbody>

      </table>

    </div>

  );

}



// The Changelog tab. The release record, read in place.

//

// Its own top-level tab since 22 Aug 2026, gated on view_changelog —

// it used to live under Partners, which meant granting it required

// granting the partner API keys too.

//

// Rendered as plain preformatted text rather than parsed markdown,

// deliberately: adding a markdown renderer would mean a new dependency

// and a sanitisation question, for a document whose whole value is being

// scannable. The headings and bullets read perfectly well as-is.

function ChangelogPanel({ token }) {

  const [text, setText] = useState(null);

  const [version, setVersion] = useState(null);

  const [error, setError] = useState(null);

  const [downloadError, setDownloadError] = useState(null);



  useEffect(() => {

    fetch(`${API_BASE}/api/changelog`, { headers: { "x-auth-token": token } })

      .then((res) => res.json().then((body) => ({ ok: res.ok, body })))

      .then(({ ok, body }) => {

        if (!ok) throw new Error(body.error || "Could not load the changelog.");

        setText(body.text);

        setVersion(body.version || null);

      })

      .catch((err) => setError(err.message || "Could not reach backend."));

  }, [token]);



  return (

    <div style={{ marginTop: 16, maxWidth: 760 }}>

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", flexWrap: "wrap", gap: 10 }}>

        <h3 style={{ margin: 0 }}>

          Changelog

          {version && (

            <span style={{ fontSize: 13, fontWeight: 400, opacity: 0.7, marginLeft: 10 }}>

              running v{version}

            </span>

          )}

        </h3>

        <button

          type="button"

          onClick={() => downloadWithAuth(`${API_BASE}/api/changelog?download=1`, token, "CHANGELOG.md", setDownloadError)}

          style={{ fontSize: 13, background: "none", border: "none", color: "var(--accent-blue)", cursor: "pointer", padding: 0 }}

        >

          ⬇ Download (.md)

        </button>

      </div>

      <p style={{ opacity: 0.7, fontSize: 13, marginTop: 4 }}>

        {version

          ? `This server is running v${version} — reported by the backend process itself, not read from a file, so it reflects what is actually running rather than what was last deployed. `

          : ""}

        What changed in each release, and whether it can break anything. Versions follow the policy under{" "}

        <em>Integration</em> — the app is versioned separately from the <code>/api/v1/</code> and{" "}

        <code>/embed/v1/</code> partner contract, so an app release doesn't imply a contract change.

      </p>

      {downloadError && <p style={{ color: "var(--accent-red)", fontSize: 12.5 }}>{downloadError}</p>}

      {error && <p style={{ color: "var(--accent-red)", fontSize: 13 }}>{error}</p>}

      {!error && text === null && <p style={{ opacity: 0.7 }}>Loading…</p>}

      {text !== null && (

        <pre

          style={{

            whiteSpace: "pre-wrap",

            wordBreak: "break-word",

            fontSize: 12.5,

            lineHeight: 1.55,

            background: "var(--bg-input)",

            border: "1px solid var(--border-color)",

            borderRadius: 8,

            padding: 14,

            marginTop: 12,

            maxHeight: "70vh",

            overflowY: "auto",

          }}

        >

          {text}

        </pre>

      )}

    </div>

  );

}



// RENDERS THE ONE GUIDE, rather than being a second copy of it.
//
// This component used to BE the guide: 2,041 lines of JSX, hand-kept in
// step with docs/integration-guide-pdf-source.html, which the panel below
// described as "the same content ... kept in sync with it". Nothing made
// that true. On 12 Sep 2026 one sentence about what the software does was
// stale in six places, and a partner asking "is the same content in both?"
// is what found it.
//
// Now the backend serves the built HTML — the same file the PDF is made
// from, with the syntax highlighting baked in — and this shows it.
//
// IN AN IFRAME, on purpose. It is a whole HTML document with its own
// styles; injecting it into this page would let those styles loose on the
// admin. The iframe also means what you read here is exactly what prints,
// which is the entire point of the change.
function IntegrationGuidePanel({ token }) {
  const [downloadError, setDownloadError] = useState(null);
  const [html, setHtml] = useState(null);
  const [loadError, setLoadError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    fetch(`${API_BASE}/api/integration-guide.html`, {
      headers: { "x-auth-token": token },
    })
      .then((res) => {
        if (!res.ok) throw new Error(`The guide could not be loaded (${res.status}).`);
        return res.text();
      })
      .then((text) => {
        if (!cancelled) setHtml(text);
      })
      .catch((err) => {
        if (!cancelled) setLoadError(err.message || "The guide could not be loaded.");
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  return (
    <div style={{ marginTop: 16 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", flexWrap: "wrap", gap: 10 }}>
        <h3 style={{ margin: 0 }}>Integration Guide</h3>
        <button
          type="button"
          onClick={() => downloadWithAuth(`${API_BASE}/api/integration-guide.pdf`, token, "integration-guide.pdf", setDownloadError)}
          style={{ fontSize: 13, background: "none", border: "none", color: "var(--accent-blue)", cursor: "pointer", padding: 0 }}
        >
          Download as PDF
        </button>
        {/* THE .env TEMPLATE. Everything a partner sets on their own
            server, in one file, with this site's address already filled
            in.

            It carries a PLACEHOLDER for the API key, not the key: it is
            stored hashed and shown once when generated, so nobody here
            can read it back. The file says so rather than leaving an
            empty line somebody puzzles over. */}
        <button
          type="button"
          onClick={() =>
            downloadWithAuth(
              `${API_BASE}/api/integration.env`,
              token,
              "integration.env",
              (msg) => showToast(msg, "error")
            )
          }
          style={{ fontSize: 13, background: "none", border: "none", color: "var(--accent-blue)", cursor: "pointer" }}
        >
          Download .env template
        </button>
      </div>

      {downloadError && <p style={{ color: "var(--accent-red)", fontSize: 12.5 }}>{downloadError}</p>}

      <p style={{ opacity: 0.7, fontSize: 13, marginTop: 4 }}>
        Everything a third-party site needs to show live competition data and let their own visitors play. This is
        the same document as the PDF — one file, shown here and printed there, so the two cannot disagree.
      </p>

      {loadError && <p style={{ color: "var(--accent-red)", fontSize: 12.5 }}>{loadError}</p>}

      {!html && !loadError && <p style={{ opacity: 0.6, fontSize: 13 }}>Loading the guide…</p>}

      {html && (
        <iframe
          title="Integration Guide"
          srcDoc={html}
          style={{
            width: "100%",
            height: "75vh",
            border: "1px solid var(--border-color-light)",
            borderRadius: 8,
            background: "#fff",
          }}
        />
      )}

      {/* THE FILES THEMSELVES, which the guide can only talk about.
          They lived inside the old JSX prose, and deleting it took a
          partner's only route to the widget and the example page with
          it — a capability, not a paragraph. They belong out here
          anyway: the guide is the reading, this is the tooling. */}
      <div style={{ marginTop: 20, paddingTop: 16, borderTop: "1px solid var(--border-color-subtle)" }}>
        <h4 style={{ margin: "0 0 8px" }}>Files to download</h4>
        <WidgetDownloadPanel token={token} />
      </div>
    </div>
  );
}



function FaqQ({ q, children }) {

  return (

    <div style={{ marginBottom: 18 }}>

      <div style={{ fontWeight: 600, fontSize: 14.5, marginBottom: 5 }}>{q}</div>

      <div style={{ fontSize: 13.5, lineHeight: 1.65, opacity: 0.85 }}>{children}</div>

    </div>

  );

}



// Fetches one of the served widget files at runtime and shows it with

// copy/download — deliberately not duplicating ~19KB of widget source

// as a JSX string literal here, which would bloat this file and create

// a real risk of the two copies silently drifting out of sync with

// whatever's actually served.

function fileTypeInfo(filename) {

  if (filename.endsWith(".php")) return { mime: "application/x-httpd-php", label: "Download .php" };

  if (filename.endsWith(".html")) return { mime: "text/html", label: "Download .html" };

  return { mime: "text/javascript", label: "Download .js" };

}



function WidgetCodeBlock({ url, filename, label }) {

  const [code, setCode] = useState(null);

  const [error, setError] = useState(null);

  const [copied, setCopied] = useState(false);

  const [raw, setRaw] = useState(false);

  const { mime, label: downloadLabel } = fileTypeInfo(filename);



  useEffect(() => {

    let cancelled = false;

    fetch(url)

      .then((res) => {

        if (!res.ok) throw new Error("Could not load this file.");

        return res.text();

      })

      .then((text) => {

        if (!cancelled) setCode(text);

      })

      .catch((err) => {

        if (!cancelled) setError(err.message);

      });

    return () => {

      cancelled = true;

    };

  }, [url]);



  const handleCopy = () => {

    if (!code) return;

    navigator.clipboard.writeText(code).then(() => {

      setCopied(true);

      setTimeout(() => setCopied(false), 2000);

    });

  };



  const handleDownload = () => {

    if (!code) return;

    const blob = new Blob([code], { type: mime });

    const a = document.createElement("a");

    a.href = URL.createObjectURL(blob);

    a.download = filename;

    a.click();

    URL.revokeObjectURL(a.href);

  };



  return (

    <div style={{ marginBottom: 20 }}>

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6, flexWrap: "wrap", gap: 8 }}>

        <strong style={{ fontSize: 13.5 }}>{label}</strong>

        <div style={{ display: "flex", gap: 8 }}>

          <button type="button" onClick={() => setRaw(!raw)} disabled={!code} style={{ fontSize: 12 }}>

            {raw ? "Source" : "Raw"}

          </button>

          <button type="button" onClick={handleCopy} disabled={!code} style={{ fontSize: 12 }}>

            {copied ? "Copied!" : "Copy code"}

          </button>

          <button type="button" onClick={handleDownload} disabled={!code} style={{ fontSize: 12 }}>

            {downloadLabel}

          </button>

        </div>

      </div>

      {error && <p style={{ color: "var(--accent-red)", fontSize: 13 }}>{error}</p>}

      <pre

        style={{

          maxHeight: 240,

          overflow: "auto",

          background: "#1c1e26",

          color: "#e8e9ee",

          borderRadius: 6,

          padding: "12px 14px",

          fontFamily: "monospace",

          fontSize: 11.5,

          lineHeight: 1.5,

          margin: 0,

        }}

      >

        {code ? raw ? code : <HighlightedCode code={code} /> : error ? "" : "Loading…"}

      </pre>

    </div>

  );

}



function WidgetDownloadPanel({ token }) {

  const [downloadError, setDownloadError] = useState(null);

  return (

    <div>

      <p>

        Both are functionally identical (minified: whitespace, comments, and internal variable names all

        compressed for a smaller file), never anything a third party's own code needs to change for. Use

        whichever fits a given build process.

      </p>

      {/* A WORKING PAGE, offered before the widget source, because it
          is what a partner actually starts from. Every API section
          wired up and rendering — meant to be pulled apart, restyled
          and rearranged rather than used as-is. Added 19 Sep 2026. */}
      <WidgetCodeBlock
        url="/embed/v1/589comps-example.html"
        filename="589comps-example.html"
        label="Example page — every section, working"
      />
      <WidgetCodeBlock url="/embed/v1/589comps-widget.js" filename="589comps-widget.js" label="Raw" />

      <WidgetCodeBlock

        url="/embed/v1/589comps-widget.min.js"

        filename="589comps-widget.min.js"

        label="Minified"

      />

      <h4 style={{ marginTop: 28, marginBottom: 4 }}>Quickest start: demo session (no backend needed)</h4>

      <p style={{ opacity: 0.7, fontSize: 13, marginTop: 0 }}>

        No API key, no server-side code, no environment variables — just a demo token from the Partners tab (see
        "Partner demo sessions" there) and these two files. Shows demo competitions to any visitor with no login
        step at all, and they can buy pixels and guess as well as watch. The recommended place to start; the
        full integration below covers logged-in visitors and real competitions.

      </p>

      <WidgetCodeBlock url="/embed/example-demo.html" filename="example-demo.html" label="A complete, no-backend demo page" />

      {/* REPLAYS, optional and separate. A partner can ship without it
          and add it later — which is why it is its own script rather
          than part of the main widget. */}
      <WidgetCodeBlock url="/embed/v1/589comps-replay.js" filename="589comps-replay.js" label="The replay widget (optional)" />

      <WidgetCodeBlock url="/embed/v1/589comps-replay.min.js" filename="589comps-replay.min.js" label="The replay widget, minified" />

      <WidgetCodeBlock url="/embed/example-replays.html" filename="example-replays.html" label="A complete replays page" />

      <div

        id="download-demo"

        style={{

          border: "1px solid var(--border-color)",

          borderRadius: 8,

          padding: "14px 16px",

          marginTop: 16,

        }}

      >

        <h4 style={{ margin: "0 0 4px" }}>Download</h4>

        <p style={{ opacity: 0.7, fontSize: 13, marginTop: 0, marginBottom: 10 }}>

          Both files together in one folder, plus a short setup readme.

        </p>

        <button

          type="button"

          onClick={() =>

            downloadWithAuth(

              `${API_BASE}/api/widget-demo-example.zip`,

              token,

              "competition-widget-demo-example.zip",

              setDownloadError

            )

          }

          style={{ fontSize: 13.5, fontWeight: 600, background: "none", border: "none", color: "var(--accent-blue)", cursor: "pointer", padding: 0 }}

        >

          ⬇ Download demo example (.zip)

        </button>

        {downloadError && <p style={{ color: "var(--accent-red)", fontSize: 12.5, margin: "6px 0 0" }}>{downloadError}</p>}

        <p style={{ fontSize: 12.5, opacity: 0.7, margin: "10px 0 4px" }}>Includes:</p>

        <ul style={{ fontSize: 12.5, opacity: 0.85, margin: 0, paddingLeft: 18 }}>

          <li>

            <code>example-demo.html</code> — the complete demo page

          </li>

          <li>

            <code>589comps-widget.js</code> — the widget itself

          </li>

          <li>

            <code>readme.md</code> — short setup instructions

          </li>

        </ul>

      </div>



      <h4 style={{ marginTop: 28, marginBottom: 4 }}>Full integration: server-to-server vouch</h4>

      <p style={{ opacity: 0.7, fontSize: 13, marginTop: 0 }}>

        Three files that work together — download all three into the same folder on a PHP-capable server (see the

        setup comments in each) to see a real, working embed within minutes, running against this site's own

        simulated demo competitions via a test API key from the Partners tab.

      </p>

      <WidgetCodeBlock url="/embed/example-token.php" filename="example-token.php" label="Backend (PHP) — the server-to-server vouch call" />

      <WidgetCodeBlock url="/embed/example.html" filename="example.html" label="Frontend — full interactive widget" />

      <WidgetCodeBlock

        url="/embed/example-slider.html"

        filename="example-slider.html"

        label="Frontend — info card slider variant"

      />

      <div

        id="download"

        style={{

          border: "1px solid var(--border-color)",

          borderRadius: 8,

          padding: "14px 16px",

          marginTop: 16,

        }}

      >

        <h4 style={{ margin: "0 0 4px" }}>Download</h4>

        <p style={{ opacity: 0.7, fontSize: 13, marginTop: 0, marginBottom: 10 }}>

          Download every file above, already together in one folder, plus a short setup readme — no need to save

          each one individually.

        </p>

        <button

          type="button"

          onClick={() => downloadWithAuth(`${API_BASE}/api/widget-examples.zip`, token, "competition-widget-examples.zip", setDownloadError)}

          style={{ fontSize: 13.5, fontWeight: 600, background: "none", border: "none", color: "var(--accent-blue)", cursor: "pointer", padding: 0 }}

        >

          ⬇ Download example files (.zip)

        </button>

        {downloadError && <p style={{ color: "var(--accent-red)", fontSize: 12.5, margin: "6px 0 0" }}>{downloadError}</p>}

        <p style={{ fontSize: 12.5, opacity: 0.7, margin: "10px 0 4px" }}>

          Requires PHP 8+ with the curl and pdo_sqlite extensions (both the default on essentially any current PHP hosting), and a

          server you can set environment variables on. Includes:

        </p>

        <ul style={{ fontSize: 12.5, opacity: 0.85, margin: 0, paddingLeft: 18 }}>

          <li>

            <code>example.html</code> — the full competition page

          </li>

          <li>

            <code>example-slider.html</code> — the same page, info cards shown as a slider instead

          </li>

          <li>

            <code>example-token.php</code> — the server-side backend both HTML files call

          </li>

          <li>

            <code>589comps-widget.js</code> — the widget itself

          </li>

          <li>

            <code>example-replays.html</code> and <code>589comps-replay.js</code> — the replay page and its widget

          </li>

          <li>

            <code>589comps-account.js</code> — the account navigation widget: the visitor&rsquo;s account in a
            modal with tabs or a header menu (previewed on Partners &rarr; Quick Start)

          </li>

          <li>

            <code>README.md</code> — short, step-by-step setup instructions covering everything above

          </li>

        </ul>

      </div>

    </div>

  );

}



// Runs the REAL widget JS (not a hand-written approximation) against a

// small, in-memory mock backend that intercepts every fetch() call it

// makes — genuinely accurate to what a third party would see, and can

// never drift out of sync with the real widget the way a separately

// maintained static mockup inevitably would, since this always loads

// whatever the current, real /embed/v1/589comps-widget.js is.

// infoCardsLayout is passed straight through to the real mount() call,

// so the same function builds both the standard and slider previews.

function buildPreviewHtml({ sections, infoCardsLayout } = {}) {

  const effectiveSections = sections || {

    picker: true,

    guessForm: true,

    freeEntry: true,

    competitionStatus: true,

    thisCompetition: true,

    lastEntries: true,

    recentPurchases: true,

    myStats: true,

  };

  return `<!DOCTYPE html>

<html>

<head>

<meta charset="UTF-8" />

<style>

  body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; margin: 0; padding: 24px; background: #f7f7f9; color: #1c1e26; }

  h1 { font-size: 18px; margin: 0 0 4px; }

  .site-note { color: #4a4d5c; font-size: 13px; margin: 0 0 20px; }

</style>

</head>

<body>



<h1>Example Third-Party Site</h1>

<p class="site-note">Everything below this line is the real widget — running its own, actual code, exactly as it would on a real page, with a small mocked backend standing in for the real API.</p>



<div id="competition"></div>



<script>

(function () {

  // Fixed, arbitrary base URL — NOT window.location.origin. This HTML

  // is embedded via <iframe srcDoc>, which has an opaque origin

  // ("null"), so window.location.origin isn't a valid URL base inside

  // it and throws. A fixed origin works regardless of how this page

  // ends up hosted, since window.fetch is overridden globally below

  // and never makes a real network request either way.

  var PREVIEW_ORIGIN = "https://preview.invalid";



  // ---- Tiny in-memory mock backend, intercepting every fetch() call

  // the real widget makes (see fetchJson in the widget itself — it's

  // the one, single place that ever calls fetch, so this catches

  // everything). State here is per-page-load only, not persisted —

  // good enough for a preview, not meant to survive a refresh.

  var mockState = {

    competitionId: "preview-comp-482913",

    imageWidth: 1000,

    imageHeight: 750,

    ownedPixels: 0,

    guessesUsed: 0,

    // FREE ENTRY AS THE SERVER NOW REPORTS IT (1.413.0): the guess's

    // credit is claimed, the pixel is used, and what is left is credit.

    freeClaimed: false,

    freeCredits: 0,

    freePixelUsed: false,

    myRegions: [],

  };



  // Matches the real backend's API_FIELD_REGISTRY labels exactly (not

  // just "good enough" wording) — this is what a third party actually

  // sees from the real API, so the preview should show precisely that,

  // not the raw, underscored field key.

  var FIELD_LABELS = {

    competition_id: "Competition ID",

    title: "Title",

    image_width: "Image width",

    image_height: "Image height",

    price_per_pixel: "Price per pixel",

    entry_price: "Entry cost (credits)",

    prize_money: "Prize money",

    answer_mode: "Answer mode",

    free_entry_enabled: "Free entry enabled (site-wide, no purchase necessary)",

    currency_symbol: "Currency symbol",

    currency_code: "Currency code (ISO 4217)",

    status: "Status",

    type: "Type",

    private_view_length: "Private view length",

    converts_to_lottery_in: "Converts to lottery in",

    started: "Started",

    finishes: "Finishes",

    images_revealed: "Tiles revealed",

    entries_submitted: "Entries submitted",

    users_entered: "Unique entrants",

  };



  function wrap(obj) {

    var out = {};

    for (var k in obj) out[k] = { value: obj[k], label: FIELD_LABELS[k] || k };

    return out;

  }



  function jsonResponse(body, status) {

    return Promise.resolve({

      ok: !status || status < 400,

      status: status || 200,

      json: function () { return Promise.resolve(body); },

    });

  }

  // The shape /api/v1/auth/my-guess-status and /api/v1/guess-quote share.

  function mockGuessStatus() {

    return {

      competitionId: mockState.competitionId,

      isFree: mockState.freeCredits >= 5,

      creditsPerEntry: 5,

      entriesUsed: mockState.guessesUsed,

      entriesAvailable: Math.floor(mockState.freeCredits / 5),

      freeCredits: mockState.freeCredits,

      creditBalance: mockState.freeCredits,

      canTopUp: false,

      creditsEarnedHere: 0,

      amountSpent: mockState.ownedPixels,

      freeEntry: {

        offered: true,

        pixelOffered: true,

        claimed: mockState.freeClaimed,

        pixelUsed: mockState.freePixelUsed,

        credits: 5,

        creditsRemaining: mockState.freeCredits,

      },

    };

  }




  var originalFetch = window.fetch;

  window.fetch = function (url, options) {

    var path = new URL(String(url), PREVIEW_ORIGIN).pathname;

    var method = (options && options.method) || "GET";



    if (path === "/api/v1/competition-details") {

      return jsonResponse(wrap({

        competition_id: mockState.competitionId,

        title: "#482913",

        image_width: mockState.imageWidth,

        image_height: mockState.imageHeight,

        price_per_pixel: 1,

        entry_price: 1,

        prize_money: 250,

        answer_mode: "Exact match required",

        free_entry_enabled: true,

        currency_symbol: "£",

        currency_code: "GBP",

      }));

    }

    if (path === "/api/v1/competition-status") {

      return jsonResponse(wrap({

        status: "In Play",

        type: "Competition",

        private_view_length: "1 minute",

        converts_to_lottery_in: "58 minutes",

        started: new Date(Date.now() - 15 * 60000).toLocaleString("en-GB"),

        finishes: new Date(Date.now() + 45 * 60000).toLocaleString("en-GB"),

      }));

    }

    if (path === "/api/v1/competition-progress") {

      return jsonResponse(wrap({

        images_revealed: 214 + mockState.ownedPixels,

        entries_submitted: 37 + mockState.guessesUsed,

        users_entered: 19,

      }));

    }

    if (path === "/api/v1/latest-entries") {

      var names = ["j***", "s***", "m***", "a***", "k***", "d***"];

      var body = {};

      names.forEach(function (n, i) {

        body["entry_" + (i + 1)] = {

          value: { user: n, entered_at: new Date(Date.now() - i * 4 * 60000).toLocaleString("en-GB") },

          label: "Entry " + (i + 1),

        };

      });

      return jsonResponse(body);

    }

    if (path === "/api/v1/recent-purchases") {

      var purchaseMocks = [

        { user: "r***", amount: 4.32, details: "18×16px area" },

        { user: "t***", amount: 12.0, details: "40×30px area" },

        { user: "b***", amount: 1.5, details: "5×5px area" },

        { user: "n***", amount: 8.75, details: "25×35px area" },

        { user: "p***", amount: 22.4, details: "56×40px area" },

        { user: "w***", amount: 3.0, details: "10×30px area" },

      ];

      var purchaseBody = {};

      purchaseMocks.forEach(function (p, i) {

        purchaseBody["purchase_" + (i + 1)] = {

          value: { user: p.user, amount: p.amount, details: p.details, purchased_at: new Date(Date.now() - i * 3 * 60000).toLocaleString("en-GB") },

          label: "Purchase " + (i + 1),

        };

      });

      return jsonResponse(purchaseBody);

    }

    if (path === "/api/v1/auth/me") {

      return jsonResponse({

        id: "preview-user-id",

        email: "preview@example.com",

        role: "user",

        mode: "demo",

        defaultSpendAmount: 25,

        payoutMethod: null,

        payoutDetails: null,

        themePreference: null,

      });

    }

    if (path === "/api/v1/auth/my-guess-status" || path === "/api/v1/guess-quote") {

      return jsonResponse(mockGuessStatus());

    }

    if (path === "/api/v1/free-entry/claim") {

      if (mockState.freeClaimed) return jsonResponse({ error: "You have already taken the free entry." }, 409);

      mockState.freeClaimed = true;

      mockState.freeCredits += 5;

      return jsonResponse({ credits: 0, free: mockState.freeCredits, total: mockState.freeCredits });

    }

    if (path === "/api/v1/auth/my-entries") {

      return jsonResponse([]);

    }

    if (path.indexOf("/my-regions") !== -1) {

      return jsonResponse(mockState.myRegions);

    }

    if (path === "/api/v1/quote") {

      return jsonResponse({ price: 4.32, newPixels: 288, alreadyOwnedPixels: 0, available: true, creditsGained: 6 });

    }

    if (path === "/api/v1/purchase" || path === "/api/v1/free-entry") {

      mockState.ownedPixels += 1;

      if (path === "/api/v1/free-entry") mockState.freePixelUsed = true;

      var region = {

        x: 100, y: 100, w: 18, h: 16,

        tile: "data:image/svg+xml;base64," + btoa('<svg xmlns="http://www.w3.org/2000/svg" width="18" height="16"><rect width="18" height="16" fill="#556"/></svg>'),

        isPublic: false,

        privateUntil: Date.now() + 60000,

        ownerToken: "preview-owner-token-" + mockState.ownedPixels,

      };

      mockState.myRegions.push(region);

      return jsonResponse(region);

    }

    if (path === "/api/v1/guess") {

      mockState.guessesUsed += 1;

      if (mockState.freeCredits >= 5) mockState.freeCredits -= 5;

      return jsonResponse({ correct: false, competitionId: mockState.competitionId });

    }



    // Anything unrecognized (shouldn't normally happen) — fail soft

    // rather than let a real network request escape the preview.

    return jsonResponse({ error: "Not available in preview." }, 404);

  };



  // The picker's own cover/tile images are set via a plain <img>.src

  // assignment, not fetch() — intercepted separately here so the

  // picker doesn't just show a broken-image icon. Built to actually

  // look like an in-progress competition (a scatter of already-

  // revealed tiles over a mystery background, plus a text overlay) —

  // a flat, empty-looking placeholder would badly undersell what the

  // real thing looks like to anyone previewing this.

  var tileColors = ["#ff8a5c", "#5cc9ff", "#8aff8a", "#ffe66d", "#d68aff", "#ff8ac2"];

  var tilesSvg = "";

  for (var t = 0; t < 26; t++) {

    var tw = 24 + Math.floor(Math.random() * 46);

    var th = 24 + Math.floor(Math.random() * 46);

    var tx = Math.floor(Math.random() * Math.max(1, mockState.imageWidth - tw));

    var ty = Math.floor(Math.random() * Math.max(1, mockState.imageHeight - th));

    var col = tileColors[t % tileColors.length];

    tilesSvg +=

      '<rect x="' + tx + '" y="' + ty + '" width="' + tw + '" height="' + th + '" fill="' + col + '" opacity="0.55" rx="2"/>';

  }

  var placeholderSvg =

    "data:image/svg+xml;base64," +

    btoa(

      '<svg xmlns="http://www.w3.org/2000/svg" width="' + mockState.imageWidth + '" height="' + mockState.imageHeight + '">' +

      '<defs><linearGradient id="g" x1="0" y1="0" x2="1" y2="1"><stop offset="0" stop-color="#2c2f3a"/><stop offset="1" stop-color="#1c1e26"/></linearGradient></defs>' +

      '<rect width="100%" height="100%" fill="url(#g)"/>' +

      tilesSvg +

      '<text x="50%" y="46%" text-anchor="middle" fill="#e8e9ee" font-family="sans-serif" font-size="34" font-weight="700" opacity="0.85">Mystery Image</text>' +

      '<text x="50%" y="54%" text-anchor="middle" fill="#c7c9d4" font-family="sans-serif" font-size="16" opacity="0.75">Buy pixels to reveal your own tiles</text>' +

      "</svg>"

    );

  var srcDescriptor = Object.getOwnPropertyDescriptor(HTMLImageElement.prototype, "src");

  Object.defineProperty(HTMLImageElement.prototype, "src", {

    set: function (value) {

      if (typeof value === "string" && value.indexOf("/competitions/") !== -1 && value.indexOf("/image") !== -1) {

        srcDescriptor.set.call(this, placeholderSvg);

      } else {

        srcDescriptor.set.call(this, value);

      }

    },

    get: srcDescriptor.get,

  });

})();

</script>



<script src="/embed/v1/589comps-widget.js"></script>

<script>

  Comps589Widget.mount(document.getElementById("competition"), {

    baseUrl: "https://preview.invalid",

    token: "preview-token",

    sections: ${JSON.stringify(effectiveSections)},${infoCardsLayout ? `\n    infoCardsLayout: ${JSON.stringify(infoCardsLayout)},` : ""}

  });

</script>



</body>

</html>

`;

}



function PartnersPreviewPanel() {

  const [tab, setTab] = useState("standard");

  const [mode, setMode] = useState("preview");

  const activeStyle = { borderColor: "var(--accent-blue)", color: "var(--accent-blue)" };

  const tabActiveStyle = { borderColor: "var(--accent-blue)", color: "var(--accent-blue)", fontWeight: 600 };



  const standardHtml = buildPreviewHtml({});

  const sliderHtml = buildPreviewHtml({

    sections: {

      picker: false,

      guessForm: false,

      freeEntry: false,

      competitionStatus: true,

      thisCompetition: true,

      lastEntries: true,

      recentPurchases: true,

      myStats: true,

    },

    infoCardsLayout: "slider",

  });

  const isLivePreviewTab = tab === "standard" || tab === "slider";

  const currentHtml = tab === "standard" ? standardHtml : sliderHtml;



  return (

    <div style={{ marginTop: 16, maxWidth: 760 }}>

      <h3 style={{ margin: "0 0 4px" }}>Preview</h3>

      <p style={{ opacity: 0.7, fontSize: 13, marginBottom: 14 }}>

        The Standard and Slider tabs run the real, current widget JS against a small mocked backend — genuinely

        accurate to what a third party would actually see, not a separately hand-maintained approximation. The

        other three tabs are the downloadable starter files themselves (also available from the Integration tab).

      </p>

      <div style={{ display: "flex", gap: 8, marginBottom: 12, flexWrap: "wrap" }}>

        {[

          { id: "standard", label: "Standard" },

          { id: "slider", label: "Slider layout" },

          { id: "example-html", label: "example.html" },

          { id: "example-php", label: "example-token.php" },

          { id: "example-slider-html", label: "example-slider.html" },

        ].map((t) => (

          <button key={t.id} type="button" onClick={() => setTab(t.id)} style={tab === t.id ? tabActiveStyle : undefined}>

            {t.label}

          </button>

        ))}

      </div>



      {isLivePreviewTab && (

        <>

          <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>

            <button type="button" onClick={() => setMode("preview")} style={mode === "preview" ? activeStyle : undefined}>

              Preview

            </button>

            <button type="button" onClick={() => setMode("raw")} style={mode === "raw" ? activeStyle : undefined}>

              Raw HTML

            </button>

          </div>

          {mode === "preview" ? (

            <iframe

              title="Widget preview"

              srcDoc={currentHtml}

              style={{

                width: "100%",

                height: 560,

                border: "1px solid var(--border-color)",

                borderRadius: 8,

                background: "#fff",

              }}

            />

          ) : (

            <textarea

              readOnly

              value={currentHtml}

              style={{

                width: "100%",

                height: 560,

                fontFamily: "monospace",

                fontSize: 12,

                padding: 12,

                background: "var(--bg-input)",

                border: "1px solid var(--border-color-light)",

                borderRadius: 8,

                color: "var(--text-primary)",

                resize: "vertical",

              }}

            />

          )}

        </>

      )}



      {tab === "example-html" && (

        <WidgetCodeBlock url="/embed/example.html" filename="example.html" label="Frontend — full interactive widget" />

      )}

      {tab === "example-php" && (

        <WidgetCodeBlock

          url="/embed/example-token.php"

          filename="example-token.php"

          label="Backend (PHP) — the server-to-server vouch call"

        />

      )}

      {tab === "example-slider-html" && (

        <WidgetCodeBlock

          url="/embed/example-slider.html"

          filename="example-slider.html"

          label="Frontend — info card slider variant"

        />

      )}

    </div>

  );

}



function PartnersFaqPanel() {

  const h2 = { fontSize: 18, margin: "30px 0 14px", paddingBottom: 6, borderBottom: "1.5px solid var(--text-primary)" };



  return (

    <div style={{ marginTop: 16, maxWidth: 720 }}>

      <h3 style={{ margin: 0 }}>FAQ</h3>

      <p style={{ opacity: 0.7, fontSize: 13, marginTop: 4 }}>

        The questions that actually come up building a third-party integration against this API — see the

        Integration tab for the full reference.

      </p>



      <h2 style={h2}>Signing a visitor in</h2>

      <FaqQ q="How does a visitor get signed in?">

        Through the server-to-server vouch only. Your own site logs the visitor in as it always does; your

        backend then calls <code>/api/v1/partner/session</code> with your API key and your own

        <code>externalUserId</code>, and redeems the code it gets back at <code>/api/v1/auth/exchange</code>. The

        isolated popup login, which signed people in with an account on this site, was retired on 20 September

        2026: the partner API serves only accounts your integration created.

      </FaqQ>

      <FaqQ q="What does the widget show a visitor who is not signed in?">

        A "Log in to play" button that calls the <code>onLoginRequested</code> handler you pass to

        <code>mount()</code> — typically sending them to your own login page. Without a handler it shows the

        message alone. Once they are signed in, mount the widget with their token.

      </FaqQ>

      <FaqQ q="Can the same person end up with multiple, separate competition accounts?">

        Only if you vouch for them under different <code>externalUserId</code> values, or in different modes.

        Linking is by your own <code>externalUserId</code>, not by email, and the same id vouched in Test and in

        Live gets two separate accounts — deliberately, so test and live stay isolated.

      </FaqQ>

      <FaqQ q="What if the email I send for a new linked user already belongs to someone else?">

        The vouch call fails with a 409. Worth deciding in advance how your integration handles this — e.g.

        retrying without an email, or logging it for investigation.

      </FaqQ>



      <h2 style={h2}>Reading competition data</h2>

      <FaqQ q="Do I need an API key just to read competition data?">

        No. Every endpoint in the Integration Guide's Section 3 (status, details, progress, image, latest

        entries, queue, last result) is fully public — no login, no API key.

      </FaqQ>

      <FaqQ q="Can I choose which fields are visible in the public data?">

        Not from your side — that's controlled by this site's own root, via its Root → Partners → API tab. If a

        field you need is currently switched off, that's a conversation to have with whoever runs this site, not

        something your integration can change.

      </FaqQ>

      <FaqQ q='A field I expected is missing from a response — is that a bug?'>

        Almost certainly not. A disabled field is left out of the response entirely, not set to <code>null</code>{" "}

        — check whether a key exists before reading it, rather than assuming every documented field is always

        present.

      </FaqQ>



      <h2 style={h2}>Sessions & reliability</h2>

      <FaqQ q="What happens if a session token stops working unexpectedly?">

        Sessions are held in memory on this site's side, not persisted — a restart or redeploy invalidates every

        existing session at once. Your integration should treat a 401 as "log in again", not assume a token

        lasts forever.

      </FaqQ>

      <FaqQ q="Is there a rate limit?">

        Yes — login/signup attempts and the partner vouch call are both rate-limited. Sized for normal usage

        patterns (real visitors logging in), not bulk or automated calls.

      </FaqQ>

      <FaqQ q="Can I use this over plain HTTP instead of HTTPS?">

        No — every request in this guide should be made over HTTPS.

      </FaqQ>



      <h2 style={h2}>Security</h2>

      <FaqQ q="Is my API key safe to use in my frontend JavaScript?">

        Never. It can vouch for any of your users — it must stay server-side only, never in frontend code, a

        mobile app bundle, or a public repository.

      </FaqQ>

      <FaqQ q="Payment Details includes bank/PayPal info — is that safe to collect through my own UI?">

        The endpoint itself is the same one this site's own account page uses, so it's not introducing new

        exposure — but it is sensitive personal/financial data, and should be handled in your own integration

        with the same care as any other sensitive form data (not logged, not cached client-side longer than

        needed).

      </FaqQ>



      <h2 style={h2}>Testing & troubleshooting</h2>

      <FaqQ q="How do I test my integration before going live?">

        Loop Arcade (a real, working reference site) demonstrates both login methods and the full My Account

        area end to end, using the embeddable widget itself rather than a separate, custom-built

        implementation — worth comparing your own integration against it if something isn't behaving as

        expected.

      </FaqQ>

      <FaqQ q="Is this API versioned — will it change unexpectedly?">

        Yes — every endpoint documented in this guide, including login, account, and purchase/guess actions, now

        lives under <code>/api/v1/</code>. If a future change ever needs to break this contract, it'll ship as a

        new <code>/api/v2/</code> alongside it rather than changing <code>/v1/</code> in place — existing

        integrations keep working on <code>/v1/</code> for as long as needed, with no forced, same-moment cutover.

        The widget script and the isolated-popup auth helper follow the same principle, served from{" "}

        <code>/embed/v1/</code>.

      </FaqQ>

    </div>

  );

}



// Root-only tools for populating a realistic-looking site to demo or test

// against, without manually creating everything by hand. Each generate

// button just fires its generator and reports how many were created — the

// generators themselves pick realistic-looking names/themes and

// reasonable defaults (site's configured pricing, fog/cover settings,

// standard canvas size), nothing further to configure here. Simulate

// stays disabled until both a mock user and a mock competition exist —

// the backend also enforces this, this is just what keeps the button from

// being clickable in a state that would only fail anyway.

function MockDataPanel({ authHeaders, token, modeStatus, canEdit }) {

  const [busy, setBusy] = useState(null); // which button, if any, is mid-request

  const [status, setStatus] = useState(null); // { hasMockUsers, hasMockCompetitions, mockUserCount, mockCompetitionCount, mockSimulationDataCount, simulationRunning }

  const { confirm, confirmModal } = useConfirm();

  // Every action below creates or deletes demo-tagged data — same

  // frozen-state principle as everywhere else in the admin panel,

  // just applied to a whole category of actions rather than one

  // record at a time.


  // ---- IT ASKED FOR A MODE NOBODY CAN BE IN (1.819.0) ----
  //
  // This read `adminMode(modeStatus) === "demo"`, and the comment above said
  // it "requires actually being in Demo mode, not just having the
  // manage_mock_data permission". Demo stopped being a mode an admin can be
  // in: ADMIN_MODES is ["test", "live"] and rootPreferredModeFrom sends
  // anything else to Test. So the condition could never be true, every
  // control below was permanently disabled, and the notice told the admin to
  // "switch to Demo mode" - advice that cannot be followed.
  //
  // THE SERVER MADE THIS DECISION ON 11 SEP 2026 and the client was not
  // changed with it. admin.demoComps.start carries the account: "demo is no
  // longer a mode an admin can be in, so the check could never pass and this
  // action would have been unreachable rather than protected." Its gate was
  // removed and the permission left to do the work. This is that removal, one
  // layer up.
  //
  // WHAT PROTECTS IT NOW IS WHAT PROTECTED IT THEN: manage_mock_data at edit
  // level - the `canEdit` this panel is already handed, and what the server
  // enforces on every one of these actions. The mode was never the
  // protection: a generated demo account signs in on the demo ROUTE TOKEN and
  // is refused on the live page by accountPageRefusal, which compares the
  // account mode to the PAGE's rather than to the site's.
  const canUseMockTools = canEdit;
  // TEST-MODE BUYERS are the Test equivalent, and are gated on Test
  // mode for the same reason the demo tools are gated on Demo: a
  // generated account signs in only in the mode it belongs to, and never
  // on a live site.
  const canUseTestUserTools = adminMode(modeStatus) === "test";



  const refreshStatus = () => {

    wsRequest("admin.mockData.status", {}, token)

      .then(setStatus)

      .catch(() => {});

  };



  useEffect(refreshStatus, []);



  // Real-time refresh — simulation can auto-stop on its own once every

  // mock competition resolves, a mock competition/user can be generated,

  // removed, or resolved from another admin session too, and the

  // simulation's own reminder sign-ups/sends change what's counted as

  // leftover simulation data.

  useEffect(() => onBroadcast((message) => {

    if (["competitions-changed", "users-changed", "reminders-changed"].includes(message.type)) refreshStatus();

  }), []);



  const run = async (action, label) => {

    setBusy(action);

    try {

      const data = await wsRequest(action, {}, token);

      // REPORTS WHAT ACTUALLY HAPPENED. This was hardcoded to
      // "Created ${data.created}", written when every caller generated
      // something — so Remove Test Users announced "Created undefined
      // test users." Both halves wrong: the verb, and a field that
      // action does not return (it answers with `removed`).
      //
      // Reading the response rather than assuming it means a future
      // action returning either shape says something true.
      const made = data?.created;
      const gone = data?.removed;
      showToast(
        typeof made === "number"
          ? `Created ${made} ${label}.`
          : typeof gone === "number"
            ? `Removed ${gone} ${label}.`
            : "Done."
      );

      refreshStatus();

    } catch (err) {

      showToast(err.message || "Could not reach backend.", "error");

    }

    setBusy(null);

  };



  


  // Shared by every Auto Load / Auto Delete checkbox below — setting

  // is "autoLoad" or "autoDelete", item is "competitions" or "users".

  // Pause leaves the simulation RUNNING, so the demo-route link stays
  // valid - it just stops every tick doing anything. That is the whole
  // point: stopping clears the token, which is why a mock competition
  // could never be held still to look at.
  const togglePause = async () => {
    setBusy("pause");
    try {
      const data = await wsRequest("admin.mockData.setSimulationPaused", { paused: !status?.simulationPaused }, token);
      setStatus((s) => ({ ...s, simulationPaused: data.simulationPaused }));
      showToast(data.simulationPaused ? "Simulation paused - the demo link stays live." : "Simulation resumed.");
    } catch (err) {
      showToast(err.message || "Could not reach backend.", "error");
    }
    setBusy(null);
  };
  const toggleAuto = async (setting, item, enabled) => {

    try {

      const data = await wsRequest("admin.mockData.setAutoToggle", { setting, item, enabled }, token);

      setStatus((s) => ({ ...s, [setting]: data[setting] }));

    } catch (err) {

      showToast(err.message || "Could not reach backend.", "error");

    }

  };



  const remove = async (action, label) => {

    setBusy(action);

    try {

      const data = await wsRequest(action, {}, token);

      showToast(`Removed ${data.removed} ${label}.`);

      refreshStatus();

    } catch (err) {

      showToast(err.message || "Could not reach backend.", "error");

    }

    setBusy(null);

  };



  const buttonStyle = { marginRight: 10, marginBottom: 10 };

  const canSimulate = status?.hasMockUsers && status?.hasMockCompetitions;



  return (

    <div style={{ marginTop: 20 }}>

      <div style={{ fontSize: 13, marginBottom: 8 }}>Generate mock data</div>

      <p style={{ fontSize: 12, opacity: 0.6, marginTop: -4, marginBottom: 14, maxWidth: 600 }}>

        Root-only tools for populating a realistic-looking site to demo or test against, without creating

        everything by hand. Every generated account uses the password <code>password1234</code>.

      </p>

      {!canUseMockTools && !canUseTestUserTools && (

        <p style={{ fontSize: 12.5, color: "var(--accent-yellow)", marginTop: -6, marginBottom: 14 }}>

          You need Mock Data at edit level for the mock data tools, or Test mode for test users.

          They only ever create or remove generated data, and the demo tools work in any mode - a

          generated demo account signs in on its own replay link and is refused on the live site.

        </p>

      )}

      {canUseTestUserTools && (

        <p style={{ fontSize: 12.5, color: "var(--accent-blue)", marginTop: -6, marginBottom: 14 }}>

          In Test mode, only the test user tools apply. Test users sign in on the Test site with

          the password <code>password1234</code> — which is why they will not sign in once the site

          is live. Sign-up is closed while the site is not live, so this is how to get a buyer

          account for checking the buyer side.

        </p>

      )}

      <ViewOnlyGate canEdit={canEdit} showNotice>

        {/* "Generate Mock Users" and "Generate Mock Competitions" were
            here. The demo is one button now: Generate on the queue panel
            wipes and rebuilds both in a single step, from any mode.
        
            The ACTIONS remain, and the destructive suite uses them. */}

        <button

          style={buttonStyle}

          disabled={!!busy || !canUseTestUserTools}

          title={
            canUseTestUserTools
              ? "Five buyer accounts that work in Test mode, so you can check the buyer side without opening sign-ups"
              : "Switch to Test mode to generate test users"
          }

          onClick={() => run("admin.mockData.generateTestUsers", "test users")}

        >

          {busy === "admin.mockData.generateTestUsers" ? "Generating…" : "Generate Test Users"}

        </button>

        <button

          style={buttonStyle}

          disabled={!!busy || !canUseTestUserTools}

          title={
            canUseTestUserTools
              ? "Removes only generated test accounts — a real buyer is never in scope"
              : "Switch to Test mode to remove test users"
          }

          onClick={() => run("admin.mockData.removeTestUsers", "test users")}

        >

          {busy === "admin.mockData.removeTestUsers" ? "Removing…" : "Remove Test Users"}

        </button>

        {/* "Generate Mock Admin Users" was here. It made three admin
            accounts with an arbitrary permission set and a shared fixed
            password, to show what a non-root admin sees.
        
            Account Management does that better: root creates one admin,
            chooses exactly which permissions it has, and sets its own
            password. Three throwaway accounts with permissions nobody
            picked is a worse answer to the same question. */}




      </ViewOnlyGate>

      {/* THE "START SIMULATION" BUTTON WAS REMOVED on 29 Aug 2026 as
          duplicated navigation. The demo queue's Play button already
          starts the simulation with it and stops it with it — that link
          has existed on both sides for a while — so two controls for one
          thing only invited them to disagree.

          What that button had and Play does not: it REFUSED with "generate
          mock users and competitions first". Play deliberately starts the
          queue anyway and skips the simulation, because a hand-made demo
          competition is legitimate to run alone. So the note below says
          what is needed instead of a control refusing after the fact. */}
      <p style={{ fontSize: 12, opacity: 0.6, marginTop: 4, marginBottom: 14, maxWidth: 600 }}>

        The simulation runs generated mock users making real purchases and guesses against whichever

        mock competition is active — never a real one, even if left running. It starts and stops with

        the <strong>Demo queue's Play button</strong> on the Queue Control Panel, and needs at least

        one mock user and one mock competition to have anything to act with.

        {status && !canSimulate && (
          <>
            {" "}
            <span style={{ color: "var(--accent-yellow)" }}>
              Generate both above first, or the queue will run with nothing happening in it.
            </span>
          </>
        )}

      </p>

      {/*
        The demo preview link used to be shown here. It now lives in the
        Queue Control Panel under Competitions, beside the Test link and
        the queues both of them reach — one place for both rather than
        one here and one there.

        What that panel does NOT repeat, and is worth keeping written
        down: the link is never advertised anywhere, logging in still
        requires one of the demo accounts above, and it goes away when
        the simulation stops, so a link copied from an earlier round
        stops working the moment a new one starts.
      */}



      <hr style={{ border: "none", borderTop: "1px solid var(--border-color)", margin: "16px 0" }} />



      {/* Each button carries its own explanation on the ⓘ to its right,

          replacing the paragraph that used to sit under all three. Same

          native title= tooltips used elsewhere in this panel. The button's

          OWN title is left for the "why is this disabled" reason, which is

          transient and specific — mixing the two onto one element would mean

          the permanent explanation vanishes exactly when the button greys

          out and someone is most likely to be wondering about it. Buttons

          wrap in a row each so the marker stays beside its own button rather

          than drifting onto another line at a narrow width. */}

      {/* THE THREE REMOVE BUTTONS WERE HERE — Remove Mock Users,
          Remove Mock Competitions, Remove Mock Data.
      
          The demo is one button now: Generate on the queue panel wipes
          all three and rebuilds. Clearing without rebuilding was never
          something anybody wanted on its own, and leftover mock data
          costs nothing while it waits to be replaced.
      
          The ACTIONS remain — the destructive suite uses all three to
          set up and tear down. It is the buttons that went. */}



      <hr style={{ border: "none", borderTop: "1px solid var(--border-color)", margin: "16px 0" }} />



      <div style={{ fontSize: 13, marginBottom: 8 }}>Auto Load / Auto Delete</div>

      {/* One short line kept permanently visible rather than moved into a

          tooltip with everything else. Tooltips are hover-only — they don't

          exist on a touch device — and "these only ever act on restart" is

          the one thing someone must not have to discover by hovering, since

          the whole point of the recent change was that data stopped

          disappearing on its own. Everything else (what each individual

          checkbox does, and why Mock Data has no Auto Load) moved onto the

          checkboxes themselves via the same native title= tooltips already

          used elsewhere in this panel. */}

      <p style={{ fontSize: 12, opacity: 0.6, marginTop: -4, marginBottom: 12, maxWidth: 600 }}>

        {/* THIS SAID "backend restart only" AND IT IS NOT TRUE.

            Auto Delete does act at restart. Auto Load for competitions
            also runs in the once-a-second maintenance ticker: whenever
            the demo queue is running and nothing is queued, it generates
            another five. Somebody reading the old sentence would leave
            the box ticked expecting nothing to happen until a restart. */}

        <strong>Auto Delete</strong> acts on backend restart only. <strong>Auto Load</strong> for

        competitions also runs continuously: while the Demo queue is running and nothing is queued,

        it generates another five. Leave it unticked to end a round with the queue empty and add the

        next batch yourself.

        <br />

        <strong>Both apply to the Demo queue only.</strong> Test and Live are never topped up or

        cleared automatically. Hover any checkbox for what it does.

      </p>

      <ViewOnlyGate canEdit={canEdit}>

        {[

          {

            item: "competitions",

            label: "Mock Competitions",

            autoLoadTip:

              "On backend restart, generates a fresh batch of mock competitions if none exist. Also keeps generating a new batch whenever the queue runs out, rather than letting it stop — this is the one setting that acts while the server is running, and it only ever adds, never deletes.",

            autoDeleteTip:

              "On backend restart, wipes every existing mock competition. With Auto Load on too, that's \"start completely fresh every restart\"; on its own, it's \"clear out and don't regenerate\". A finished batch stays put until you restart or remove it yourself with the buttons above.",

          },

          {

            item: "users",

            label: "Mock Users",

            autoLoadTip: "On backend restart, generates a fresh set of mock user accounts if none exist. Unlike competitions, there's no top-up while the server is running.",

            autoDeleteTip: "On backend restart, deletes every mock user account. Real accounts are never touched — only ones the generator created.",

          },

          {

            item: "data",

            label: "Mock Data",

            autoLoadTip: null,

            autoDeleteTip:

              "On backend restart, wipes simulation activity — guess counts, spending totals, entry history. Independent of the other two: you can clear the activity while keeping the competitions and users that produced it.",

            noAutoLoadTip:

              "No Auto Load here — simulation activity is never generated directly, only ever produced as a side effect of the simulation actually running.",

          },

        ].map(({ item, label, autoLoadTip, autoDeleteTip, noAutoLoadTip }) => (

          <div key={item} style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 8, fontSize: 13 }}>

            <span style={{ minWidth: 140 }}>{label}</span>

            {autoLoadTip ? (

              <label

                title={autoLoadTip}

                style={{ display: "flex", alignItems: "center", gap: 5, cursor: canUseMockTools ? "pointer" : "default" }}

              >

                <input

                  type="checkbox"

                  disabled={!canUseMockTools}

                  checked={!!status?.autoLoad?.[item]}

                  onChange={(e) => toggleAuto("autoLoad", item, e.target.checked)}

                />

                Auto Load

              </label>

            ) : (

              // Placeholder rather than nothing, so the Auto Delete boxes stay

              // in one straight column across all three rows — and so the

              // absence of Auto Load here is itself explained on hover rather

              // than just looking like something missing.

              <span title={noAutoLoadTip} style={{ display: "flex", alignItems: "center", gap: 5, opacity: 0.35, cursor: "help" }}>

                <input type="checkbox" disabled checked={false} readOnly style={{ visibility: "hidden" }} />

                Auto Load

              </span>

            )}

            <label

              title={autoDeleteTip}

              style={{ display: "flex", alignItems: "center", gap: 5, cursor: canUseMockTools ? "pointer" : "default" }}

            >

              <input

                type="checkbox"

                disabled={!canUseMockTools}

                checked={!!status?.autoDelete?.[item]}

                onChange={(e) => toggleAuto("autoDelete", item, e.target.checked)}

              />

              Auto Delete

            </label>

          </div>

        ))}

      </ViewOnlyGate>

      {confirmModal}

    </div>

  );

}





// Root/delegated-admin management of every standalone, publicly-served

// page this site has — legal documents (Terms & Conditions,

// Disclaimers, Privacy Policy — a fixed set of exactly three, HTML

// and/or an uploaded PDF, gated by manage_settings) alongside content

// pages (About, FAQ, or anything else — a genuinely dynamic list root

// can add to and remove from freely, HTML only, gated by

// manage_content). Kept as one unified list — one place to see every

// public-facing page this site serves — but the two keep their own,

// separate edit permission and capabilities under the hood, since

// they're genuinely different kinds of thing: legal documents can

// never be added or removed, only their content cleared; content

// pages can be created and deleted freely, but never carry a PDF.

// canEditLegal was a separate prop until 18 Aug 2026, gated on

// manage_settings so that only someone trusted with sitewide settings

// could rewrite the terms and privacy documents. Legal docs are content,

// and the Content tab owns content, so they now follow this panel's own

// permission — deliberately widening who can edit them. The backend

// moved with it (four legal-doc routes and three WS handlers), so the UI

// never offers an edit the server will refuse.

// WHICH PROVIDER IS ACTIVE IN A GIVEN MODE.
//
// `active` became per-mode on 18 Sep 2026 — `{ test, live }` rather than
// one string. Reading it directly would compare an OBJECT to
// "cashflows" and always be false, so every "in use" badge would go out.
//
// A string is a config the server has not migrated yet; it meant both.
function activeProviderIn(config, mode) {
  const a = config?.active;
  if (!a) return null;
  if (typeof a === "string") return a;
  return (mode === "live" ? a.live : a.test) ?? null;
}

// WHERE THIS PARTNER'S USERS PLAY.
//
// A partner-linked buyer uses the widget on the partner's own site, not
// this one. Until 18 Sep 2026 every email sent to them linked to THIS
// site's homepage — not vague for them, simply the wrong site.
//
// A domain is not a page: the partner may host the widget at
// /competitions, so this is configured rather than derived from the key
// set's domain. It IS checked against that domain, by the same rule that
// governs where a checkout may return to.
function PartnerSettingsPanel({ token, canEdit }) {
  // ONE ADDRESS PER SITE since 19 Sep 2026: the key carries a test
  // domain and a live domain, and a partner's two sites do not share a
  // competition page.
  const [landing, setLanding] = useState({ test: "", live: "" });
  const [fromKey, setFromKey] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    wsRequest("admin.partner.get", {}, token)
      .then((data) => {
        if (cancelled) return;
        // PREFILLED FROM THE API KEY'S OWN DOMAIN when nothing is set.
        //
        // Exactly the key's domain, not its parent: the save rule
        // accepts the registered domain and anything BELOW it, so a key
        // registered to test.589comps.app would refuse 589comps.app.
        // Defaulting to the parent would prefill a value that fails on
        // Save.
        //
        // Either of the key's two domains will do — a partner is one
        // business, and both point at the same site.
        //
        // Requested 19 Sep 2026.
        // ONE KEY, TWO DOMAINS since 19 Sep 2026 — and the one in play
        // is whichever the readiness flag selects, the same rule the
        // server applies to returnUrls.
        const k = data?.key;
        const stored = data?.landingUrl || {};
        // EACH PREFILLED FROM ITS OWN KEY DOMAIN. A live landing
        // address must be on the LIVE domain, so suggesting the test
        // domain there would offer something the save would refuse.
        const suggest = (d) => (d ? `https://${d}/` : "");
        setLanding({
          test: stored.test || suggest(k?.testDomain),
          live: stored.live || suggest(k?.liveDomain),
        });
        setFromKey(
          !stored.test && !stored.live && !!(k?.testDomain || k?.liveDomain)
        );
        setLoaded(true);
      })
      .catch(() => setLoaded(true));
    return () => {
      cancelled = true;
    };
  }, [token]);

  const save = async () => {
    setBusy(true);
    try {
      // BOTH ADDRESSES IN ONE SAVE. Each is checked against its own
      // key domain on the server, so a live address on the test domain
      // is refused rather than quietly accepted.
      const data = await wsRequest(
        "admin.partner.setLandingUrl",
        { test: landing.test, live: landing.live },
        token
      );
      const saved = data?.landingUrl || {};
      setLanding({ test: saved.test || "", live: saved.live || "" });
      setFromKey(false);
      showToast(
        saved.test || saved.live
          ? "Saved."
          : "Cleared — emails will link to this site.",
        "success"
      );
    } catch (err) {
      showToast(err?.message || "Could not save those addresses.", "error");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ border: "1px solid var(--border-color)", borderRadius: 8, padding: 16 }}>
      <h3 style={{ marginTop: 0 }}>Where the partner's users play</h3>
      <p style={{ fontSize: 13, opacity: 0.85, marginTop: 0 }}>
        A buyer who arrived through the partner's integration plays on the
        partner's own site, not this one. Emails to those buyers link there
        instead — the reminder before a competition starts, and anywhere else a
        competition is linked.
      </p>
      <p style={{ fontSize: 12.5, opacity: 0.75 }}>
        One address per site, matching the two domains on their API key.
        The live address is used only once the partner is marked ready AND
        this site is in Live mode; until then they go to the test one.
        Leave both blank and they are sent here.
      </p>
      <p style={{ fontSize: 12.5, opacity: 0.75, marginTop: 0 }}>
        Each must be on its OWN domain — the live address on the live
        domain, the test address on the test one — and needs the full form:{" "}
        <code>https://</code>, the host, and any path.
      </p>
      {/* ONE BOX PER SITE. Which is used follows the same rule as
          everything else: the live address only once the partner is
          marked ready AND this site is live. */}
      {[
        { key: "test", label: "Test site" },
        { key: "live", label: "Live site" },
      ].map((row) => (
        <div
          key={row.key}
          style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", marginBottom: 8 }}
        >
          <label style={{ fontSize: 12.5, minWidth: 68 }}>{row.label}</label>
          <input
            type="url"
            placeholder="https://example.com/competitions"
            value={landing[row.key]}
            onChange={(e) =>
              setLanding((prev) => ({ ...prev, [row.key]: e.target.value }))
            }
            disabled={!canEdit || busy || !loaded}
            style={{ fontSize: 13, minWidth: 320 }}
          />
        </div>
      ))}
      <button type="button" onClick={save} disabled={!canEdit || busy || !loaded}>
        Save
      </button>
      {fromKey && (
        <p style={{ fontSize: 12.5, opacity: 0.75, marginBottom: 0 }}>
          Taken from the domain on the partner's API key, which is where
          emails link today. Save it to pin that address, or edit it first if
          their widget sits somewhere more specific.
        </p>
      )}
    </div>
  );
}

// THE SECTIONS, as lib/site-export.js lists them — same keys, same order.
// A test holds the two lists together.
const SITE_EXPORT_SECTIONS = [
  { key: "settings", label: "Sitewide settings", kind: "config", defaultOn: true },
  { key: "contentPages", label: "Content pages", kind: "config", defaultOn: true },
  { key: "legalDocs", label: "Legal documents", kind: "config", defaultOn: true },
  { key: "emailContent", label: "Email templates", kind: "config", defaultOn: true },
  { key: "emailSchedules", label: "Scheduled emails", kind: "config", defaultOn: true },
  { key: "themes", label: "Themes", kind: "config", defaultOn: true },
  { key: "logos", label: "Site logos", kind: "config", defaultOn: true },
  { key: "apiSettings", label: "Public API settings", kind: "config", defaultOn: true },
  { key: "users", label: "Users and admins", kind: "data", defaultOn: true },
  { key: "competitions", label: "Competitions (with their images, purchases and entries)", kind: "data", defaultOn: true, note: "Answers and hidden images are encrypted and can only be restored to this site" },
  { key: "entryCredits", label: "Entry credit balances and history", kind: "data", defaultOn: true },
  { key: "payments", label: "Payments", kind: "data", defaultOn: true },
  { key: "reminders", label: "Reminder subscriptions", kind: "data", defaultOn: true },
  // WITHOUT THIS a restore puts every bounced and complained address back
  // to "subscribed" and starts emailing them again (1.491.0).
  { key: "emailSubscribers", label: "Email subscribers and their status", kind: "data", defaultOn: true },
  { key: "replays", label: "Replays", kind: "data", defaultOn: true, note: "Their hidden images are encrypted and can only be restored to this site" },
  { key: "paymentProviders", label: "Payment providers", kind: "credential", defaultOn: false },
  { key: "emailProviders", label: "Email providers", kind: "credential", defaultOn: false },
  { key: "smsProviders", label: "SMS providers", kind: "credential", defaultOn: false },
  { key: "partner", label: "Partner (API key, landing address, page layout)", kind: "credential", defaultOn: false },
  { key: "activityLog", label: "Activity log", kind: "history", defaultOn: false },
  { key: "emailEvents", label: "Email delivery events", kind: "history", defaultOn: false },
  { key: "adminIssues", label: "Admin issues and site notifications", kind: "history", defaultOn: false },
  { key: "providerLog", label: "Payment provider log", kind: "history", defaultOn: false },
];
const SITE_EXPORT_GROUPS = [
  { kind: "config", title: "Configuration" },
  { kind: "data", title: "Data" },
  { kind: "credential", title: "Providers and partner — encrypted with a passphrase" },
  { kind: "history", title: "History" },
];

// EXPORT AND IMPORT THE WHOLE SITE, section by section (1.396.0).
//
// In Server Admin, behind the Server Admin permission at edit level — a
// full export is a copy of every account and every payment. Moved here
// from Sitewide on 20 Sep 2026, when it grew from settings and content to
// everything.
//
// An imported section REPLACES what the site has. The person importing is
// always kept, so importing users cannot lock them out.
// ---- THE IMPORT LIMIT, AND THE MEMORY CEILING IT IS BOUNDED BY (1.898.0) ----
//
// TWO FIGURES, ONE EDITABLE. The import limit is a setting; the memory ceiling
// is a pm2 option applied when the process starts, so it is shown and not
// offered - nothing on the admin panel restarts the site as a side effect of
// saving a number. The card says where it comes from and what applies it, so
// that read-only does not read as missing.
//
// THE ARITHMETIC IS SHOWN, not just the verdict. An operator refused a limit
// wants to know what to change, and the server's refusal already names the
// figure; printing the sum here means the refusal is not the first time they
// see how the two relate.
//
// ITS OWN ACTION PAIR ON manage_server, not the Sitewide save - which demands
// less than the import routes this bounds. See admin.serverLimits.get.
// ---- WHAT THIS BOX IS, AND WHEN IT NEEDS MORE (1.930.0) ----
//
// FIRST IN THE TAB, because it is the orienting fact. Everything below it - the
// controls, the backups, the import ceiling - is about a machine whose size the
// panel never mentioned. install.sh has reported cores and RAM since 1.895.0,
// once, to whoever was at the terminal; nothing has shown it since, and the
// backend read neither until this release.
//
// YELLOW AND NOT RED on a trigger that applies. install.sh added a FIFTH state
// for exactly this, with the reason recorded in its own tests: "a core count is
// neither [ OK ] nor [MISS]: one core is supported - production is one - so
// [MISS] would be false, and [ OK ] would claim the box is as it should be." A
// red card would say this box is broken. It is not broken, it is SMALL, and
// that is a fact with consequences.
//
// NO HARDCODED COLOURS. Every one is a theme variable, because a panel line
// written in a literal colour was invisible on the dark theme on 23 Oct 2026.
function MachinePanel({ token }) {
  const [m, setM] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    // TOLERANT OF FAILURE, like the mongo readout inside getServerStatus: a
    // card missing one line beats a card that never loads.
    wsRequest("admin.server.machine", {}, token)
      .then((d) => { setM(d); setError(""); })
      .catch((e) => setError(e?.message || "Could not read this machine."));
  }, [token]);

  const shell = (inner) => (
    <div style={{ border: "1px solid var(--border-color)", borderRadius: 8, padding: 14, marginBottom: 20, maxWidth: 500, flex: "0 1 500px" }}>
      <h4 style={{ marginTop: 0 }}>This machine</h4>
      {inner}
    </div>
  );

  if (!m) {
    return shell(<p style={{ fontSize: 13, color: "var(--text-secondary)" }}>{error || "Loading..."}</p>);
  }

  const row = (label, value) => (
    <div style={{ display: "flex", justifyContent: "space-between", gap: 12, padding: "3px 0", fontSize: 13 }}>
      <span style={{ color: "var(--text-secondary)" }}>{label}</span>
      <span style={{ textAlign: "right" }}>{value}</span>
    </div>
  );

  return shell(
    <>
      {row("Cores", m.note && m.note.text ? m.note.text : "could not be read")}
      {m.hostModel ? row("Host processor", m.hostModel) : null}
      {m.memMb ? row("RAM", m.memMb + " MB") : null}
      {m.ceilingMb ? row("pm2 restarts the backend at", m.ceilingMb + " MB") : null}
      {m.hostModel && m.cores ? (
        <p style={{ fontSize: 12, color: "var(--text-secondary)", marginTop: 10, marginBottom: 0 }}>
          This guest gets {m.cores} core{m.cores === 1 ? "" : "s"} of the processor named above. One
          vCPU of a much larger host means the hypervisor can deschedule the whole guest, and the
          steal time that costs is not visible from inside it.
        </p>
      ) : null}
      {m.memMb && m.ceilingMb ? (
        <p style={{ fontSize: 12, color: "var(--text-secondary)", marginTop: 8, marginBottom: 0 }}>
          The ceiling was derived from this box{"'"}s RAM at install time and is applied by pm2 when
          the process starts, so a box resized since carries RAM the ceiling knows nothing about.
        </p>
      ) : null}

      <h5 style={{ marginBottom: 6, marginTop: 16 }}>When it needs more</h5>
      {(m.triggers || []).map((t) => (
        <div
          key={t.id}
          style={{
            borderLeft: "3px solid " + (t.fires ? "var(--accent-yellow)" : "var(--border-color)"),
            paddingLeft: 10,
            marginBottom: 10,
          }}
        >
          <div style={{ fontSize: 13, fontWeight: 600 }}>
            {t.title}
            {t.fires ? " - applies to this box" : ""}
            {t.unknown ? " - could not be measured here" : ""}
          </div>
          {(t.evidence || []).map((e, i) => (
            <p key={i} style={{ fontSize: 12, color: "var(--text-secondary)", margin: "4px 0 0" }}>{e}</p>
          ))}
        </div>
      ))}
    </>
  );
}

function ServerLimitsPanel({ token, canEdit, modeStatus }) {
  const [budget, setBudget] = useState(null);
  const [error, setError] = useState("");
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  // THE LIVE CEILING, read once rather than polled. It costs a `pm2 jlist`,
  // and a declared ceiling that is not in force changes only on a restart - so
  // there is nothing for a poll to catch that opening the tab again would not.
  const [liveCeiling, setLiveCeiling] = useState(null);
  // THE SAME RULE AS THE SERVER'S assertSettingsEditable: refused in Demo only.
  const editable = canEdit && adminMode(modeStatus) !== "demo";

  const load = () => {
    wsRequest("admin.serverLimits.get", {}, token)
      .then((d) => {
        setBudget(d);
        setDraft(String(d.importLimitMb));
        setError("");
      })
      .catch((e) => setError(e?.message || "Could not read the limits."));
  };

  useEffect(() => {
    load();
    // TOLERANT OF FAILURE, like the mongo readout inside getServerStatus: a
    // card missing one line beats a card that never loads.
    wsRequest("admin.server.status", {}, token)
      .then((d) => setLiveCeiling(d?.backend?.maxMemoryRestart ?? null))
      .catch(() => setLiveCeiling(null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const save = async () => {
    setBusy(true);
    try {
      const d = await wsRequest("admin.serverLimits.set", { importLimitMb: Number(draft) }, token);
      setBudget(d);
      setDraft(String(d.importLimitMb));
      setError("");
      showToast(`Site import limit saved: ${d.importLimitMb} MB.`, "success");
    } catch (e) {
      // THE SERVER'S OWN MESSAGE, which names the ceiling the request would
      // have needed. Rewording it here would lose the only actionable part.
      setError(e?.message || "The limit was not saved.");
    } finally {
      setBusy(false);
    }
  };

  if (!budget) {
    return (
      <div style={{ border: "1px solid var(--border-color)", borderRadius: 8, padding: 14, marginBottom: 20, maxWidth: 500 }}>
        <h4 style={{ marginTop: 0 }}>Import and memory limits</h4>
        <p style={{ fontSize: 13, opacity: 0.7 }}>{error || "Loading..."}</p>
      </div>
    );
  }

  // WHETHER WHAT IS DECLARED IS WHAT IS RUNNING. pm2 reports its own figure as
  // a string with a unit, or as bytes on some versions, so compare the digits
  // rather than the formatting.
  const liveMb = liveCeiling == null ? null : Number(String(liveCeiling).replace(/[^0-9]/g, ""));
  const ceilingNotLive = liveMb != null && liveMb !== budget.ceilingMb;
  const changed = String(budget.importLimitMb) !== draft.trim();

  return (
    <div style={{ border: "1px solid var(--border-color)", borderRadius: 8, padding: 14, marginBottom: 20, maxWidth: 500 }}>
      <h4 style={{ marginTop: 0 }}>Import and memory limits</h4>
      <p style={{ fontSize: 13, opacity: 0.7, marginTop: -6 }}>
        How large a site import this box accepts, and the memory ceiling that bounds it. A real export
        measured 8.95 MB, but that was dummy data - raise the limit as the site grows.
      </p>

      {error && <p style={{ color: "var(--accent-red)", fontSize: 13 }}>{error}</p>}

      <label style={{ display: "block", fontSize: 13, marginTop: 10 }}>
        Largest site import accepted (MB)
        <input
          type="number"
          min={budget.minImportMb}
          max={budget.maxImportMb}
          step={1}
          value={draft}
          disabled={!editable || busy}
          onChange={(e) => setDraft(e.target.value)}
          style={{ display: "block", width: 120, marginTop: 4 }}
        />
      </label>
      <p style={{ fontSize: 12, opacity: 0.7, marginTop: 4 }}>
        Between {budget.minImportMb} and {budget.maxImportMb} MB on this box. Applies to the next import -
        no restart needed.
      </p>

      {budget.importLimitClamped && (
        <p style={{ color: "var(--accent-red)", fontSize: 13 }}>
          Saved as {budget.importLimitMb} MB, but this box is using {budget.effectiveImportLimitMb} MB - the
          memory ceiling was lowered after it was saved. Save a value within the range above to clear this.
        </p>
      )}

      <button type="button" onClick={save} disabled={!editable || busy || !changed} style={{ marginTop: 6 }}>
        {busy ? "Saving..." : "Save limit"}
      </button>
      {!editable && (
        <p style={{ opacity: 0.7, fontSize: 13, marginTop: 6 }}>
          {canEdit
            ? `Leave Demo mode to change this - you're currently browsing in ${modeLabel(adminMode(modeStatus))} mode.`
            : "Needs edit permission on Server Admin."}
        </p>
      )}

      <hr style={{ margin: "14px 0", border: 0, borderTop: "1px solid var(--border-color)" }} />

      <h4 style={{ margin: "0 0 4px" }}>Memory ceiling: {budget.ceilingMb} MB</h4>
      <p style={{ fontSize: 13, opacity: 0.7, marginTop: 0 }}>
        Read-only here. It comes from {budget.ceilingSource}, and is applied by {budget.ceilingAppliedBy} -
        changing it is a server-side edit, not a setting, because applying it restarts the site.
      </p>
      {ceilingNotLive && (
        <p style={{ color: "var(--accent-red)", fontSize: 13 }}>
          pm2 is running {liveMb} MB, not the {budget.ceilingMb} MB declared. A restart will apply the
          declared figure.
        </p>
      )}
      <p style={{ fontSize: 12, opacity: 0.7 }}>
        Why those two numbers are related: the backend sits at about {budget.floorMb} MB and holds up
        to {budget.worstHeld} competitions at about {budget.perCompetitionMb} MB each, so{" "}
        {budget.steadyWorstMb} MB is its steady worst case. An import costs roughly{" "}
        {budget.importPeakFactor}x the file on top of that, which is what leaves {budget.maxImportMb} MB
        for an import under a {budget.ceilingMb} MB ceiling.
      </p>
    </div>
  );
}

function SiteDataTransferPanel({ token, canEdit, modeStatus }) {
  const [busy, setBusy] = useState(false);
  const [exportChosen, setExportChosen] = useState(
    () => new Set(SITE_EXPORT_SECTIONS.filter((x) => x.defaultOn).map((x) => x.key))
  );
  const [passphrase, setPassphrase] = useState("");
  const [confirmPassphrase, setConfirmPassphrase] = useState("");
  const [importFile, setImportFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [importChosen, setImportChosen] = useState(new Set());
  const [importPassphrase, setImportPassphrase] = useState("");
  const [understood, setUnderstood] = useState(false);

  // THE SAME RULE AS THE SERVER'S assertSettingsEditable: refused in Demo
  // only. 1.396.0 shipped this as Live-only, which the server never asked
  // for, and hid import from Test mode.
  const importAllowed = adminMode(modeStatus) !== "demo";
  const exportNeedsPassphrase = SITE_EXPORT_SECTIONS.some(
    (x) => x.kind === "credential" && exportChosen.has(x.key)
  );

  const toggle = (set, setter, key) => {
    const next = new Set(set);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    setter(next);
  };

  const download = async () => {
    // TYPED TWICE, because a mistyped passphrase is not discovered until
    // the day somebody needs the file.
    if (exportNeedsPassphrase && passphrase !== confirmPassphrase) {
      showToast("The two passphrases do not match.", "error");
      return;
    }
    setBusy(true);
    try {
      const res = await fetch(`${API_BASE}/api/admin/site-export`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "x-auth-token": token || "" },
        body: JSON.stringify({
          sections: [...exportChosen],
          passphrase: exportNeedsPassphrase ? passphrase : null,
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || `Export failed (${res.status}).`);
      }
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `589comps-site-${new Date().toISOString().slice(0, 10)}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(a.href);
    } catch (err) {
      showToast(err?.message || "Could not build the export.", "error");
    } finally {
      setBusy(false);
    }
  };

  const postFile = async (url, extra) => {
    const form = new FormData();
    form.append("file", importFile);
    for (const [k, v] of Object.entries(extra || {})) form.append(k, v);
    const res = await fetch(`${API_BASE}${url}`, {
      method: "POST",
      headers: { "x-auth-token": token || "" },
      body: form,
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.error || `Request failed (${res.status}).`);
    return data;
  };

  // READ FIRST, APPLY SECOND — importing overwrites, so the operator sees
  // what the file holds before choosing what to take from it.
  const inspect = async (file) => {
    setImportFile(file);
    setPreview(null);
    setUnderstood(false);
    setBusy(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const res = await fetch(`${API_BASE}/api/admin/site-import/inspect`, {
        method: "POST",
        headers: { "x-auth-token": token || "" },
        body: form,
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || `Could not read that file (${res.status}).`);
      setPreview(data);
      // Everything the file holds is ticked, except the history logs —
      // the same default as on export.
      const inFile = [...data.sections, ...data.credentialSections];
      setImportChosen(
        new Set(
          inFile.filter((k) => SITE_EXPORT_SECTIONS.find((x) => x.key === k)?.kind !== "history")
        )
      );
    } catch (err) {
      setImportFile(null);
      showToast(err?.message || "Could not read that file.", "error");
    } finally {
      setBusy(false);
    }
  };

  const apply = async () => {
    setBusy(true);
    try {
      const res = await postFile("/api/admin/site-import/apply", {
        sections: JSON.stringify([...importChosen]),
        passphrase: importPassphrase,
      });
      setPreview(null);
      setImportFile(null);
      setImportPassphrase("");
      setUnderstood(false);
      showToast(
        res.restarting
          ? `Imported ${res.applied.length} section(s). The server is restarting to load them — this panel reconnects in a few seconds.`
          : `Imported ${res.applied.length} section(s).`,
        "success"
      );
    } catch (err) {
      showToast(err?.message || "The import failed.", "error");
    } finally {
      setBusy(false);
    }
  };

  const inFile = preview ? new Set([...preview.sections, ...preview.credentialSections]) : new Set();
  const importNeedsPassphrase =
    preview &&
    (SITE_EXPORT_SECTIONS.some((x) => x.kind === "credential" && importChosen.has(x.key)) ||
      (importChosen.has("users") && preview.hasTotpSecrets));
  const restartNeeded = [...importChosen].some(
    (k) => !["settings", "contentPages", "legalDocs", "emailContent", "themes", "logos", "apiSettings", "paymentProviders", "emailProviders", "smsProviders"].includes(k)
  );

  const sectionList = (chosen, setChosen, only) =>
    SITE_EXPORT_GROUPS.map((g) => {
      const items = SITE_EXPORT_SECTIONS.filter((x) => x.kind === g.kind && (!only || only.has(x.key)));
      if (!items.length) return null;
      return (
        <div key={g.kind} style={{ marginBottom: 8 }}>
          <div style={{ fontSize: 12, fontWeight: 600, opacity: 0.75, margin: "6px 0 3px" }}>{g.title}</div>
          {items.map((x) => (
            <label key={x.key} style={{ display: "flex", gap: 6, alignItems: "center", fontSize: 13, margin: "2px 0" }}>
              <input
                type="checkbox"
                checked={chosen.has(x.key)}
                disabled={busy || !canEdit}
                onChange={() => toggle(chosen, setChosen, x.key)}
              />
              <span>
                {x.label}
                {preview && only && preview.counts?.[x.key] != null && (
                  <span style={{ opacity: 0.6 }}> — {preview.counts[x.key]}</span>
                )}
                {/* WHAT DOES NOT COME OUT (1.512.0). Asked for on 27 Sep
                    2026 alongside sealing them: "the user should be shown
                    that these won't be exported". Beside the tick box
                    rather than in the paragraph above, because that is
                    where the choice is actually made. */}
                {x.note && (
                  <span style={{ display: "block", fontSize: 12, opacity: 0.65 }}>{x.note}</span>
                )}
              </span>
            </label>
          ))}
        </div>
      );
    });

  return (
    <div style={{ border: "1px solid var(--border-color)", borderRadius: 8, padding: 16, marginBottom: 20 }}>
      <h3 style={{ marginTop: 0 }}>Export and import</h3>
      <p style={{ fontSize: 13, opacity: 0.8, marginTop: 0 }}>
        Everything on the site, as one JSON file — tick the sections you want. Providers and the
        partner are encrypted with a passphrase you choose; <strong>lose it and they cannot be
        recovered</strong>. The rest of the file, including users' email addresses and password
        hashes, is readable by
        whoever holds it, so keep it somewhere safe. Route tokens, the site address and the
        live-payments switch always stay behind.
      </p>

      <strong style={{ fontSize: 13.5 }}>Export</strong>
      <div style={{ margin: "6px 0 10px" }}>
        {sectionList(exportChosen, setExportChosen, null)}
        <div style={{ display: "flex", gap: 8, fontSize: 12 }}>
          <button type="button" disabled={busy || !canEdit} onClick={() => setExportChosen(new Set(SITE_EXPORT_SECTIONS.map((x) => x.key)))}>
            Tick all
          </button>
          <button type="button" disabled={busy || !canEdit} onClick={() => setExportChosen(new Set())}>
            Untick all
          </button>
        </div>
      </div>
      {exportNeedsPassphrase && (
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 10 }}>
          <input type="password" placeholder="Passphrase (8+ characters)" value={passphrase} onChange={(e) => setPassphrase(e.target.value)} style={{ fontSize: 13 }} />
          <input type="password" placeholder="Type it again" value={confirmPassphrase} onChange={(e) => setConfirmPassphrase(e.target.value)} style={{ fontSize: 13 }} />
        </div>
      )}
      {exportChosen.has("users") && !exportNeedsPassphrase && (
        <p style={{ fontSize: 12.5, opacity: 0.8, marginTop: 0 }}>
          Without a passphrase, two-factor secrets are left out: anybody with two-factor on will have
          it switched off by the import, and admins are asked to set it up again.
        </p>
      )}
      <button
        type="button"
        onClick={download}
        disabled={busy || !canEdit || exportChosen.size === 0 || (exportNeedsPassphrase && passphrase.length < 8)}
      >
        Export to file
      </button>

      <div style={{ marginTop: 16, borderTop: "1px solid var(--border-color)", paddingTop: 12 }}>
        <strong style={{ fontSize: 13.5 }}>Import</strong>
        {!importAllowed ? (
          <p style={{ fontSize: 12.5, opacity: 0.8 }}>Importing is not possible in Demo mode.</p>
        ) : (
          canEdit && (
            <div style={{ marginTop: 6 }}>
              <label style={{ fontSize: 13, cursor: "pointer" }}>
                <input
                  type="file"
                  accept="application/json,.json"
                  style={{ display: "none" }}
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    e.target.value = "";
                    if (f) inspect(f);
                  }}
                />
                <span style={{ textDecoration: "underline", color: "var(--accent-blue)" }}>Choose a file to import…</span>
              </label>
            </div>
          )
        )}

        {preview && (
          <div style={{ marginTop: 10 }}>
            {preview.exportedFrom && (
              <p style={{ fontSize: 12.5, opacity: 0.75, marginTop: 0 }}>
                Exported from “{preview.exportedFrom}”
                {preview.exportedAt ? ` on ${new Date(preview.exportedAt).toLocaleString()}` : ""}.
              </p>
            )}
            <div style={{ fontSize: 12.5, marginBottom: 6 }}>
              Tick what to import. <strong>Each ticked section replaces what this site has</strong> — your
              own account is always kept.
            </div>
            {sectionList(importChosen, setImportChosen, inFile)}
            {importChosen.has("settings") && preview.settingChanges?.length > 0 && (
              <p style={{ fontSize: 12.5, opacity: 0.8 }}>
                Settings that would change: {preview.settingChanges.slice(0, 8).map((c) => c.key).join(", ")}
                {preview.settingChanges.length > 8 ? ", …" : ""}
              </p>
            )}
            {preview.refusedSettings?.length > 0 && (
              <p style={{ fontSize: 12.5, color: "var(--accent-red)" }}>
                Refused, and will not be applied: {preview.refusedSettings.join(", ")}.
              </p>
            )}
            {importNeedsPassphrase && (
              <div style={{ margin: "8px 0" }}>
                <div style={{ fontSize: 12.5, opacity: 0.85, marginBottom: 4 }}>
                  The encrypted part of this file needs its passphrase.
                  {preview.hasTotpSecrets && importChosen.has("users")
                    ? " Without it, users are imported with two-factor switched off."
                    : ""}
                </div>
                <input type="password" placeholder="Passphrase" value={importPassphrase} onChange={(e) => setImportPassphrase(e.target.value)} style={{ fontSize: 13 }} />
              </div>
            )}
            {restartNeeded && (
              <p style={{ fontSize: 12.5, color: "var(--accent-yellow)" }}>
                The server restarts to load users, competitions and the other data sections. It takes a
                few seconds; anybody on the site will reconnect.
              </p>
            )}
            <label style={{ display: "flex", gap: 6, alignItems: "center", fontSize: 13, margin: "8px 0" }}>
              <input type="checkbox" checked={understood} onChange={(e) => setUnderstood(e.target.checked)} />
              <span>
                I understand the ticked sections replace what this site has now — Live and Test data
                alike, whichever mode I am in.
              </span>
            </label>
            <div style={{ display: "flex", gap: 10 }}>
              <button type="button" onClick={() => { setPreview(null); setImportFile(null); }} disabled={busy}>
                Cancel
              </button>
              <button
                type="button"
                onClick={apply}
                disabled={
                  busy ||
                  !understood ||
                  importChosen.size === 0 ||
                  (SITE_EXPORT_SECTIONS.some((x) => x.kind === "credential" && importChosen.has(x.key)) && !importPassphrase)
                }
              >
                Import and replace
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function ContentPagesPanel({ token, canEdit, modeStatus }) {

  const [pages, setPages] = useState(null);

  const [legalDocs, setLegalDocs] = useState(null);

  const [editingSlug, setEditingSlug] = useState(null); // null = nothing open, "__new__" = creating, or an existing page's own slug

  const [editingLegalKey, setEditingLegalKey] = useState(null);

  const [draftSlug, setDraftSlug] = useState("");

  const [draftTitle, setDraftTitle] = useState("");

  const [draftHtml, setDraftHtml] = useState("");

  // TWO CHECKBOXES, NOT ONE DROPDOWN. A page can be linked from both
  // regions, and a page linked from neither is unpublished — its route
  // 404s rather than staying quietly reachable.
  const [draftInHeader, setDraftInHeader] = useState(false);
  const [draftInFooter, setDraftInFooter] = useState(false);

  const [legalHtmlDrafts, setLegalHtmlDrafts] = useState({});

  const [busy, setBusy] = useState(false);

  const [busyLegalKey, setBusyLegalKey] = useState(null);

  const [expandedGroups, setExpandedGroups] = useState({}); // group key -> bool, undefined/false = collapsed

  const toggleGroup = (key) => setExpandedGroups((prev) => ({ ...prev, [key]: !prev[key] }));



  const refresh = () => {

    // admin.content.list, NOT content.list — the public one hides
    // unpublished pages, and an admin who cannot see them cannot publish
    // them again.
    wsRequest("admin.content.list", {}, token)

      .then(setPages)

      .catch(() => showToast("Could not reach backend.", "error"));

    wsRequest("legalDocs.list")

      .then((data) => {

        setLegalDocs(data);

        // Seed the editor with whatever's currently saved, the first

        // time we see each document — don't stomp on unsaved edits

        // afterward.

        setLegalHtmlDrafts((prev) => {

          const next = { ...prev };

          for (const d of data) {

            if (!(d.key in next)) next[d.key] = d.html || "";

          }

          return next;

        });

      })

      .catch(() => showToast("Could not reach backend.", "error"));

  };



  useEffect(refresh, []);



  // DEMO ONLY. These are settings, and the server agrees — see
  // assertSettingsEditable. Freezing them in Test mode meant switching
  // to Live to save a form and back again to carry on testing.
  const frozen = adminMode(modeStatus) === "demo";



  const startNew = () => {

    setEditingSlug("__new__");

    setDraftSlug("");

    setDraftTitle("");

    setDraftHtml("");

    setDraftInHeader(false);
    setDraftInFooter(false);

  };



  const startEdit = (page) => {

    setEditingSlug(page.slug);

    setDraftSlug(page.slug);

    setDraftTitle(page.title);

    setDraftHtml(page.html || "");

    setDraftInHeader(!!page.inHeader);
    setDraftInFooter(!!page.inFooter);

  };



  const cancel = () => setEditingSlug(null);



  const save = async () => {

    setBusy(true);

    try {

      if (editingSlug === "__new__") {

        await wsRequest("admin.content.create", { slug: draftSlug.trim().toLowerCase(), title: draftTitle, html: draftHtml, inHeader: draftInHeader, inFooter: draftInFooter}, token);

      } else {

        await wsRequest("admin.content.update", { slug: editingSlug, title: draftTitle, html: draftHtml, inHeader: draftInHeader, inFooter: draftInFooter}, token);

      }

      showToast("Saved.");

      setEditingSlug(null);

      refresh();

    } catch (err) {

      showToast(err.message || "Could not reach backend.", "error");

    }

    setBusy(false);

  };



  const remove = async (slug) => {

    if (!window.confirm("Delete this page? This can't be undone.")) return;

    setBusy(true);

    try {

      await wsRequest("admin.content.delete", { slug }, token);

      showToast("Deleted.");

      if (editingSlug === slug) setEditingSlug(null);

      refresh();

    } catch (err) {

      showToast(err.message || "Could not reach backend.", "error");

    }

    setBusy(false);

  };



  const saveLegalHtml = async (key) => {

    setBusyLegalKey(`${key}-html`);

    try {

      await wsRequest("admin.legalDocs.saveHtml", { key, html: legalHtmlDrafts[key] || "" }, token);

      showToast("Saved.");

      refresh();

    } catch (err) {

      showToast(err.message || "Could not reach backend.", "error");

    }

    setBusyLegalKey(null);

  };



  const removeLegalHtml = async (key) => {

    setBusyLegalKey(`${key}-remove-html`);

    try {

      await wsRequest("admin.legalDocs.deleteHtml", { key }, token);

      setLegalHtmlDrafts({ ...legalHtmlDrafts, [key]: "" });

      showToast("Removed.");

      refresh();

    } catch (err) {

      showToast(err.message || "Could not reach backend.", "error");

    }

    setBusyLegalKey(null);

  };



  const uploadLegalPdf = async (key, file) => {

    if (!file) return;

    setBusyLegalKey(`${key}-pdf`);

    try {

      const body = new FormData();

      body.append("file", file);

      const res = await fetch(`${API_BASE}/api/admin/legal-docs/${key}/pdf`, {

        method: "POST",

        headers: { "x-auth-token": token },

        body,

      });

      const data = await res.json();

      if (!res.ok) {

        if (res.status === 428) triggerMandatoryTotpRequired();

        if (res.status === 401) triggerSessionInvalid();

        showToast(data.error, "error");

      } else {

        showToast("Saved.");

        refresh();

      }

    } catch {

      showToast("Could not reach backend.", "error");

    }

    setBusyLegalKey(null);

  };



  const removeLegalPdf = async (key) => {

    setBusyLegalKey(`${key}-remove-pdf`);

    try {

      await wsRequest("admin.legalDocs.deletePdf", { key }, token);

      showToast("Removed.");

      refresh();

    } catch (err) {

      showToast(err.message || "Could not reach backend.", "error");

    }

    setBusyLegalKey(null);

  };



  const inputStyle = {

    width: "100%",

    padding: 8,

    background: "var(--bg-input)",

    border: "1px solid var(--border-color-light)",

    borderRadius: 6,

    color: "var(--text-primary)",

    boxSizing: "border-box",

  };



  return (

    <div style={{ border: "1px solid var(--border-color)", borderRadius: 8, padding: 14, marginBottom: 20, maxWidth: 640 }}>

      <h4 style={{ marginTop: 0 }}>Content pages</h4>

      <p style={{ fontSize: 12, opacity: 0.6, marginTop: -6 }}>

        Every standalone page this site serves — legal documents and anything else — each at its own URL, working

        as a shared link even outside this site.

      </p>

      {legalDocs === null || pages === null ? (

        <p>Loading…</p>

      ) : (

        <>

          {frozen && (

            <p style={{ opacity: 0.7, fontSize: 13, marginBottom: 10 }}>

              Leave Demo mode to manage this — you're currently browsing in{" "}
              {modeLabel(adminMode(modeStatus))} mode.

            </p>

          )}



          {(() => {

            const renderLegalRow = (d) => (

              <div key={d.key} style={{ border: "1px solid var(--border-color)", borderRadius: 8, padding: 12, marginBottom: 10 }}>

                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>

                  <div>

                    <strong style={{ fontSize: 14 }}>{d.label}</strong>

                    <span style={{ fontSize: 11, opacity: 0.6, marginLeft: 8 }}>Legal document</span>

                    {d.htmlUrl && <div style={{ fontSize: 12, opacity: 0.6, marginTop: 2 }}>{`${window.location.origin}${d.htmlUrl}`}</div>}

                    {d.pdfUrl && <div style={{ fontSize: 12, opacity: 0.6, marginTop: 2 }}>{`${window.location.origin}${d.pdfUrl}`} (PDF)</div>}

                  </div>

                  <div style={{ display: "flex", gap: 8, flexShrink: 0 }}>

                    {d.htmlUrl && <button onClick={() => navigator.clipboard.writeText(`${window.location.origin}${d.htmlUrl}`)}>Copy HTML link</button>}

                    {d.pdfUrl && <button onClick={() => navigator.clipboard.writeText(`${window.location.origin}${d.pdfUrl}`)}>Copy PDF link</button>}

                    {editingLegalKey === d.key ? (

                      <button onClick={() => setEditingLegalKey(null)}>Close</button>

                    ) : (

                      <button onClick={() => setEditingLegalKey(d.key)}>Edit</button>

                    )}

                  </div>

                </div>

                {editingLegalKey === d.key && (

                  <div style={{ marginTop: 12, paddingTop: 12, borderTop: "1px solid var(--border-color-subtle)" }}>

                    <div style={{ fontSize: 13, opacity: 0.8, marginBottom: 6 }}>HTML content</div>

                    <HtmlEditor

                      value={legalHtmlDrafts[d.key] ?? ""}

                      onChange={(html) => setLegalHtmlDrafts({ ...legalHtmlDrafts, [d.key]: html })}

                      disabled={frozen}

                    />

                    <div style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 8, marginBottom: d.hasHtml ? 12 : 14 }}>

                      <button onClick={() => saveLegalHtml(d.key)} disabled={busyLegalKey === `${d.key}-html` || !legalHtmlDrafts[d.key]?.trim()}>

                        {busyLegalKey === `${d.key}-html` ? "Saving…" : "Save HTML"}

                      </button>

                      {d.hasHtml && (

                        <button onClick={() => removeLegalHtml(d.key)} disabled={busyLegalKey === `${d.key}-remove-html`}>

                          {busyLegalKey === `${d.key}-remove-html` ? "…" : "Remove HTML"}

                        </button>

                      )}

                    </div>

                    <div style={{ fontSize: 13, opacity: 0.8, marginBottom: 6, paddingTop: 10, borderTop: "1px solid var(--border-color-subtle)" }}>

                      PDF {d.hasPdf ? "" : "(optional)"}

                    </div>

                    {d.hasPdf && (

                      <button onClick={() => removeLegalPdf(d.key)} disabled={busyLegalKey === `${d.key}-remove-pdf`} style={{ marginBottom: 8 }}>

                        {busyLegalKey === `${d.key}-remove-pdf` ? "…" : "Remove PDF"}

                      </button>

                    )}

                    <input

                      type="file"

                      accept="application/pdf"

                      onChange={(e) => uploadLegalPdf(d.key, e.target.files[0])}

                      disabled={busyLegalKey === `${d.key}-pdf`}

                      style={{ fontSize: 12, display: "block" }}

                    />

                  </div>

                )}

              </div>

            );



            const renderPageRow = (p) => (

              <div key={p.slug} style={{ border: "1px solid var(--border-color)", borderRadius: 8, padding: 12, marginBottom: 10 }}>

                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>

                  <div>

                    <strong style={{ fontSize: 14 }}>{p.title}</strong>

                    <div style={{ fontSize: 12, opacity: 0.6, marginTop: 2 }}>{`${window.location.origin}${p.url}`}</div>

                  </div>

                  <div style={{ display: "flex", gap: 8, flexShrink: 0 }}>

                    <button onClick={() => navigator.clipboard.writeText(`${window.location.origin}${p.url}`)}>Copy link</button>

                    {editingSlug === p.slug ? (

                      <button onClick={cancel}>Close</button>

                    ) : (

                      <button onClick={() => startEdit(p)}>Edit</button>

                    )}

                    <button onClick={() => remove(p.slug)} disabled={busy}>

                      Delete

                    </button>

                  </div>

                </div>

                {editingSlug === p.slug && (

                  <div style={{ marginTop: 12, paddingTop: 12, borderTop: "1px solid var(--border-color-subtle)" }}>

                    <div style={{ fontSize: 13, opacity: 0.8, marginBottom: 6 }}>Title</div>

                    <input value={draftTitle} onChange={(e) => setDraftTitle(e.target.value)} style={{ ...inputStyle, marginBottom: 10 }} />

                    <div style={{ fontSize: 13, opacity: 0.8, marginBottom: 6 }}>Link shown in</div>

                    <label style={{ display: "flex", gap: 6, alignItems: "center", marginBottom: 4 }}>
                      <input
                        type="checkbox"
                        checked={draftInHeader}
                        onChange={(e) => setDraftInHeader(e.target.checked)}
                      />
                      <span>Link from the header</span>
                    </label>
                    <label style={{ display: "flex", gap: 6, alignItems: "center" }}>
                      <input
                        type="checkbox"
                        checked={draftInFooter}
                        onChange={(e) => setDraftInFooter(e.target.checked)}
                      />
                      <span>Link from the footer</span>
                    </label>
                    {!draftInHeader && !draftInFooter && (
                      <div style={{ fontSize: 12.5, opacity: 0.75, marginTop: 4 }}>
                        Unpublished — the page will not be listed anywhere and its
                        address returns "not found".
                      </div>
                    )}

                    <div style={{ fontSize: 13, opacity: 0.8, marginBottom: 6 }}>Content</div>

                    <HtmlEditor value={draftHtml} onChange={setDraftHtml} disabled={frozen} />

                    <button onClick={save} disabled={busy || !draftTitle.trim()} style={{ marginTop: 10 }}>

                      {busy ? "Saving…" : "Save"}

                    </button>

                  </div>

                )}

              </div>

            );



            // A PAGE CAN BE IN BOTH, so these groups overlap by design —
            // the same page appears under Header and under Footer, which
            // is what "linked from both" means.
            const headerPages = pages.filter((p) => p.inHeader);

            const footerPages = pages.filter((p) => p.inFooter);

            // LINKED FROM NEITHER = UNPUBLISHED. Its route 404s.
            const unplacedPages = pages.filter((p) => !p.inHeader && !p.inFooter);



            const GroupHeader = ({ groupKey, label, count }) => (

              <button

                onClick={() => toggleGroup(groupKey)}

                style={{

                  display: "flex",

                  alignItems: "center",

                  gap: 6,

                  width: "100%",

                  textAlign: "left",

                  background: "var(--bg-panel-alt)",

                  border: "1px solid var(--border-color-light)",

                  borderRadius: 6,

                  padding: "8px 10px",

                  fontSize: 13,

                  fontWeight: 600,

                  color: "var(--text-primary)",

                  cursor: "pointer",

                }}

              >

                <span style={{ fontSize: 11, opacity: 0.7 }}>{expandedGroups[groupKey] ? "▾" : "▸"}</span>

                {label}

                <span style={{ opacity: 0.6, fontWeight: 400 }}>({count})</span>

              </button>

            );



            return (

              <>

                <div style={{ marginBottom: 12 }}>

                  <GroupHeader groupKey="header" label="Header" count={headerPages.length} />

                  {expandedGroups.header && (

                    <div style={{ marginTop: 8 }}>

                      <ViewOnlyGate canEdit={canEdit} showNotice>

                        <fieldset disabled={frozen} style={{ border: "none", padding: 0, margin: 0, opacity: frozen ? 0.55 : 1 }}>

                          {headerPages.length === 0 ? <p style={{ opacity: 0.6, fontSize: 13 }}>No pages here yet.</p> : headerPages.map(renderPageRow)}

                        </fieldset>

                      </ViewOnlyGate>

                    </div>

                  )}

                </div>



                <div style={{ marginBottom: 12 }}>

                  <GroupHeader groupKey="footer" label="Footer" count={legalDocs.length + footerPages.length} />

                  {expandedGroups.footer && (

                    <div style={{ marginTop: 8 }}>

                      <ViewOnlyGate canEdit={canEdit} showNotice>

                        <fieldset disabled={frozen} style={{ border: "none", padding: 0, margin: 0, opacity: frozen ? 0.55 : 1 }}>

                          {legalDocs.map(renderLegalRow)}

                        </fieldset>

                      </ViewOnlyGate>

                      <ViewOnlyGate canEdit={canEdit} showNotice>

                        <fieldset disabled={frozen} style={{ border: "none", padding: 0, margin: 0, opacity: frozen ? 0.55 : 1 }}>

                          {footerPages.length === 0 ? <p style={{ opacity: 0.6, fontSize: 13 }}>No content pages here yet.</p> : footerPages.map(renderPageRow)}

                        </fieldset>

                      </ViewOnlyGate>

                    </div>

                  )}

                </div>



                <div style={{ marginBottom: 16 }}>

                  <GroupHeader groupKey="none" label="Unpublished" count={unplacedPages.length} />

                  {expandedGroups.none && (

                    <div style={{ marginTop: 8 }}>

                      <ViewOnlyGate canEdit={canEdit} showNotice>

                        <fieldset disabled={frozen} style={{ border: "none", padding: 0, margin: 0, opacity: frozen ? 0.55 : 1 }}>

                          {unplacedPages.length === 0 ? <p style={{ opacity: 0.6, fontSize: 13 }}>No pages here yet.</p> : unplacedPages.map(renderPageRow)}

                        </fieldset>

                      </ViewOnlyGate>

                    </div>

                  )}

                </div>

              </>

            );

          })()}



          <ViewOnlyGate canEdit={canEdit}>

            <fieldset disabled={frozen} style={{ border: "none", padding: 0, margin: 0, opacity: frozen ? 0.55 : 1 }}>

              {editingSlug === "__new__" ? (

                <div style={{ border: "1px solid var(--border-color)", borderRadius: 8, padding: 12 }}>

                  <div style={{ fontSize: 13, opacity: 0.8, marginBottom: 6 }}>Slug (part of the URL — lowercase letters, numbers, and hyphens only, e.g. "about")</div>

                  <input value={draftSlug} onChange={(e) => setDraftSlug(e.target.value)} style={{ ...inputStyle, marginBottom: 10 }} placeholder="about" />

                  <div style={{ fontSize: 13, opacity: 0.8, marginBottom: 6 }}>Title</div>

                  <input value={draftTitle} onChange={(e) => setDraftTitle(e.target.value)} style={{ ...inputStyle, marginBottom: 10 }} placeholder="About us" />

                  <div style={{ fontSize: 13, opacity: 0.8, marginBottom: 6 }}>Link shown in</div>

                  <label style={{ display: "flex", gap: 6, alignItems: "center", marginBottom: 4 }}>
                      <input
                        type="checkbox"
                        checked={draftInHeader}
                        onChange={(e) => setDraftInHeader(e.target.checked)}
                      />
                      <span>Link from the header</span>
                    </label>
                    <label style={{ display: "flex", gap: 6, alignItems: "center" }}>
                      <input
                        type="checkbox"
                        checked={draftInFooter}
                        onChange={(e) => setDraftInFooter(e.target.checked)}
                      />
                      <span>Link from the footer</span>
                    </label>
                    {!draftInHeader && !draftInFooter && (
                      <div style={{ fontSize: 12.5, opacity: 0.75, marginTop: 4 }}>
                        Unpublished — the page will not be listed anywhere and its
                        address returns "not found".
                      </div>
                    )}

                  <div style={{ fontSize: 13, opacity: 0.8, marginBottom: 6 }}>Content</div>

                  <HtmlEditor value={draftHtml} onChange={setDraftHtml} disabled={frozen} />

                  <div style={{ display: "flex", gap: 8, marginTop: 10 }}>

                    <button onClick={save} disabled={busy || !draftTitle.trim() || !draftSlug.trim()}>

                      {busy ? "Saving…" : "Create page"}

                    </button>

                    <button onClick={cancel}>Cancel</button>

                  </div>

                </div>

              ) : (

                <button onClick={startNew}>Add new page</button>

              )}

            </fieldset>

          </ViewOnlyGate>

        </>

      )}

    </div>

  );

}



// Root/delegated-admin (via manage_settings) theme picker — a list of

// named color-scheme presets (the two built-in Dark/Light plus any

// custom ones created here), with one settable as the site's

// active/default. When a site-wide theme is active, every visitor

// sees it regardless of their own dark/light preference (see

// ThemeContext.jsx for how it's actually applied) — "None" means no

// override, so each account's own existing light/dark toggle applies

// exactly as it did before this feature existed.

const THEME_COLOR_GROUPS = [

  { label: "Backgrounds", keys: ["bg-page", "bg-panel", "bg-panel-alt", "bg-input", "bg-toolbar"] },

  { label: "Borders", keys: ["border-color", "border-color-light", "border-color-subtle"] },

  { label: "Text", keys: ["text-primary", "text-secondary", "text-dim"] },

  { label: "Accents", keys: ["accent-blue", "accent-yellow", "accent-green", "accent-red"] },

  { label: "Warning & error backgrounds", keys: ["warning-bg", "error-bg"] },

  { label: "Toast — success", keys: ["toast-success-bg", "toast-success-border", "toast-success-icon"] },

  { label: "Toast — error", keys: ["toast-error-bg", "toast-error-border", "toast-error-icon"] },

  { label: "Toast — warning", keys: ["toast-warning-bg", "toast-warning-border", "toast-warning-icon"] },

  { label: "Toast — info", keys: ["toast-info-bg", "toast-info-border", "toast-info-icon"] },

];

// Handled separately from the groups above with a plain text field,

// not a color input — these are the only two variables that carry

// transparency (rgba(), for a semi-transparent shadow/overlay), which

// a native <input type="color"> can't represent at all (hex only).

const THEME_TEXT_ONLY_KEYS = ["shadow-color", "overlay-color"];



function labelForThemeKey(key) {

  return key

    .split("-")

    .map((w) => w[0].toUpperCase() + w.slice(1))

    .join(" ");

}



// A quick, at-a-glance visual preview for one theme in the list below —

// four small swatches (page background plus the three most visually

// distinctive accent colors) rather than making someone activate a

// theme just to see what it looks like. This is what actually makes

// "pick from a gallery" work as a real concept, rather than just a

// list of names to guess at.

function ThemeSwatchPreview({ variables }) {

  const swatchKeys = ["bg-page", "accent-blue", "accent-green", "accent-red"];

  return (

    <div style={{ display: "flex", gap: 3 }}>

      {swatchKeys.map((key) => (

        <div

          key={key}

          style={{

            width: 16,

            height: 16,

            borderRadius: 4,

            background: variables[key],

            border: "1px solid rgba(128,128,128,0.4)",

          }}

        />

      ))}

    </div>

  );

}



function ThemesPanel({ token, canEdit, modeStatus }) {

  const [config, setConfig] = useState(null); // { activeThemeId, themes }

  const [editingId, setEditingId] = useState(null); // null | "new" | an existing theme's id

  const [draft, setDraft] = useState(null); // { name, variables }

  const [busy, setBusy] = useState(false);

  const { confirm, confirmModal } = useConfirm();

  // No test/demo variant of the theme store exists at all, and the

  // active theme affects every real visitor's site — same reasoning

  // as the backend's own assertNotFrozen calls for every action this

  // panel triggers.

  // DEMO ONLY. These are settings, and the server agrees — see
  // assertSettingsEditable. Freezing them in Test mode meant switching
  // to Live to save a form and back again to carry on testing.
  const frozen = adminMode(modeStatus) === "demo";

  const frozenTitle = "Leave Demo mode to manage this";



  const load = () => {

    wsRequest("admin.themes.list", {}, token)

      .then(setConfig)

      .catch(() => {});

  };

  useEffect(load, [token]);



  // Duplicate is now the only way to start a new custom theme (see the

  // removed "Create new theme" button below) — always copying from a

  // real, already-coherent theme rather than 31 black swatches to fill

  // in from scratch, per the chosen "gallery + duplicate & customize"

  // approach.

  const startNew = (copyFrom) => {

    setEditingId("new");

    setDraft({ name: `${copyFrom.name} copy`, variables: { ...copyFrom.variables } });

  };

  const startEdit = (theme) => {

    setEditingId(theme.id);

    setDraft({ name: theme.name, variables: { ...theme.variables } });

  };

  const cancelEdit = () => {

    setEditingId(null);

    setDraft(null);

  };



  const setActive = async (id) => {

    setBusy(true);

    try {

      await wsRequest("admin.themes.setActive", { id }, token);

      load();

    } catch (err) {

      showToast(err.message || "Could not reach backend.", "error");

    }

    setBusy(false);

  };



  const save = async () => {

    setBusy(true);

    try {

      if (editingId === "new") {

        await wsRequest("admin.themes.create", draft, token);

        showToast("Theme created.");

      } else {

        await wsRequest("admin.themes.update", { id: editingId, ...draft }, token);

        showToast("Theme saved.");

      }

      cancelEdit();

      load();

    } catch (err) {

      showToast(err.message || "Could not reach backend.", "error");

    }

    setBusy(false);

  };



  const deleteTheme = async (theme) => {

    if (!(await confirm(`Delete the theme "${theme.name}"? This can't be undone.`))) return;

    setBusy(true);

    try {

      await wsRequest("admin.themes.delete", { id: theme.id }, token);

      showToast("Theme deleted.");

      load();

    } catch (err) {

      showToast(err.message || "Could not reach backend.", "error");

    }

    setBusy(false);

  };



  if (config === null) return <p>Loading…</p>;



  return (

    <div style={{ marginTop: 24, paddingTop: 20, borderTop: "1px solid var(--border-color)" }}>

      <h3>Theme</h3>

      <p style={{ opacity: 0.7, fontSize: 13, marginTop: -6, marginBottom: 14 }}>

        Pick one theme as the site's active look — every visitor sees it regardless of their own light/dark

        preference. "None" leaves each account's own toggle in control, same as before this existed. Don't see

        one you like as-is? Duplicate the closest match below and fine-tune the copy.

      </p>



      <ViewOnlyGate canEdit={canEdit} showNotice>

        {frozen && (

          <p style={{ opacity: 0.7, fontSize: 13, marginBottom: 10 }}>

            Leave Demo mode to manage this — you're currently browsing in{" "}
            {modeLabel(adminMode(modeStatus))} mode.

          </p>

        )}

        <fieldset disabled={frozen} style={{ border: "none", padding: 0, margin: 0, opacity: frozen ? 0.55 : 1 }}>

        <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 16 }}>

          <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13 }}>

            <input type="radio" name="active-theme" checked={!config.activeThemeId} disabled={busy} onChange={() => setActive(null)} />

            None — each account's own light/dark preference applies

          </label>

          {config.themes.map((theme) => (

            <div key={theme.id} style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>

              <ThemeSwatchPreview variables={theme.variables} />

              <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13 }}>

                <input

                  type="radio"

                  name="active-theme"

                  checked={config.activeThemeId === theme.id}

                  disabled={busy}

                  onChange={() => setActive(theme.id)}

                />

                {theme.name} {theme.builtIn && <span style={{ opacity: 0.55 }}>(built-in)</span>}

              </label>

              <button type="button" disabled={busy} onClick={() => startNew(theme)}>

                Duplicate

              </button>

              {!theme.builtIn && (

                <>

                  <button type="button" disabled={busy} onClick={() => startEdit(theme)}>

                    Edit

                  </button>

                  <button type="button" disabled={busy} onClick={() => deleteTheme(theme)}>

                    Delete

                  </button>

                </>

              )}

            </div>

          ))}

        </div>



        {editingId !== null && draft && (

          <div style={{ border: "1px solid var(--border-color)", borderRadius: 8, padding: 14, marginTop: 12, maxWidth: 640 }}>

            <label style={{ display: "block", fontSize: 12.5, marginBottom: 3 }}>Theme name</label>

            <input

              type="text"

              value={draft.name}

              disabled={busy}

              onChange={(e) => setDraft({ ...draft, name: e.target.value })}

              style={{ width: "100%", padding: "6px 8px", fontSize: 13, marginBottom: 14, boxSizing: "border-box" }}

            />



            {THEME_COLOR_GROUPS.map((group) => (

              <div key={group.label} style={{ marginBottom: 14 }}>

                <div style={{ fontSize: 12.5, opacity: 0.7, marginBottom: 6 }}>{group.label}</div>

                <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>

                  {group.keys.map((key) => (

                    <label key={key} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12.5 }}>

                      <input

                        type="color"

                        value={draft.variables[key] || "#000000"}

                        disabled={busy}

                        onChange={(e) => setDraft({ ...draft, variables: { ...draft.variables, [key]: e.target.value } })}

                      />

                      {labelForThemeKey(key)}

                    </label>

                  ))}

                </div>

              </div>

            ))}



            <div style={{ marginBottom: 14 }}>

              <div style={{ fontSize: 12.5, opacity: 0.7, marginBottom: 6 }}>

                Shadows & overlays — rgba(), since these need transparency a plain color picker can't represent

              </div>

              {THEME_TEXT_ONLY_KEYS.map((key) => (

                <label key={key} style={{ display: "block", fontSize: 12.5, marginBottom: 6 }}>

                  {labelForThemeKey(key)}

                  <input

                    type="text"

                    value={draft.variables[key] || ""}

                    placeholder="rgba(0, 0, 0, 0.5)"

                    disabled={busy}

                    onChange={(e) => setDraft({ ...draft, variables: { ...draft.variables, [key]: e.target.value } })}

                    style={{ display: "block", width: "100%", maxWidth: 300, padding: "6px 8px", fontSize: 13, marginTop: 3, boxSizing: "border-box" }}

                  />

                </label>

              ))}

            </div>



            <button type="button" disabled={busy || !draft.name.trim()} onClick={save}>

              {busy ? "Saving…" : editingId === "new" ? "Create theme" : "Save changes"}

            </button>{" "}

            <button type="button" disabled={busy} onClick={cancelEdit}>

              Cancel

            </button>

          </div>

        )}

        </fieldset>

      </ViewOnlyGate>

      {confirmModal}

    </div>

  );

}



// Root-configurable defaults for new competitions — moved out of site

// branding since these are competition-specific, not site-wide identity.

// Competition Management -> Competition History. A read-only record of

// every competition that has actually finished, newest first, paginated

// with the same shared controls every other admin list uses.

//

// Mode-scoped like the rest of the panel: browsing in Demo mode lists

// demo competitions, Live mode lists real ones. The backend enforces

// this too — this isn't a display-only filter.

//

// The Playback column opens the same player the public Demo Comps

// replay link uses — see CompetitionReplayModal, which is built from

// the shared engine in replayShared.jsx rather than a second copy of

// it. Enabled from hasReplayData: a competition that ended with no

// purchases and no guesses would open to an empty replay, so the button

// stays disabled rather than offering a dead end.

function CompetitionHistoryPanel({ token, modeStatus }) {
  // WHICH MODES THE LIST CARRIES (1.437.0), as the server reports them, so
  // the heading says so when the Sitewide setting adds Test ones in Live.
  const [listedModes, setListedModes] = useState(null);

  const symbol = useCurrencySymbol();

  // Which competition's playback is open, if any — id only, since the

  // modal fetches its own data.

  const [replayId, setReplayId] = useState(null);

  // Root's configured starting point for every paginated admin list —

  // see usePaginatedList for how a viewer's own choice takes over.

  const [siteDefaults, setSiteDefaults] = useState(null);



  useEffect(() => {

    wsRequest("siteSettings.get")

      .then((data) => setSiteDefaults({ mode: data.listDisplayMode, pageSize: data.listPageSize }))

      .catch(() => {});

  }, []);



  const {

    items: history,

    page,

    setPage,

    mode,

    setMode,

    pageSize,

    setPageSize,

    totalMatching,

    totalPages,

    loading,

    loadMore,

    hasMore,

    reload,

  } = usePaginatedList({

    listKey: "competition-history",

    defaultMode: siteDefaults?.mode,

    defaultPageSize: siteDefaults?.pageSize,

    // Switching Live/Demo mode changes what this list is even showing, so

    // it resets to page 1 rather than leaving someone on page 4 of a list

    // that no longer has 4 pages.

    deps: [adminMode(modeStatus)],

    fetchPage: ({ offset, limit }) =>
      wsRequest("admin.competitions.history", { offset, limit }, token).then((data) => {
        setListedModes(Array.isArray(data?.listedModes) ? data.listedModes : null);
        return data;
      }),

  });



  useEffect(

    () =>

      onBroadcast((message) => {

        if (message.type === "competitions-changed") reload();

      }),

    [reload]

  );



  const th = { textAlign: "left", padding: "8px 10px", borderBottom: "1px solid var(--border-color-light)", fontSize: 12, opacity: 0.7, whiteSpace: "nowrap" };

  const td = { textAlign: "left", padding: "8px 10px", borderBottom: "1px solid var(--border-color-subtle)", fontSize: 13 };



  return (

    <div>

      <h3 style={{ marginTop: 0 }}>Competition History</h3>

      <p style={{ fontSize: 12, opacity: 0.6, marginTop: -6, maxWidth: 620 }}>

        Every competition that has finished, newest first. Shows{" "}

        <strong>{modeLabel(adminMode(modeStatus))}</strong> competitions
        {(listedModes || []).includes("test") && adminMode(modeStatus) !== "test" ? (
          <>
            {" "}
            and, labelled, <strong>Test</strong> ones — the Sitewide setting "List Test Competitions while in
            live mode" is on.
          </>
        ) : (
          <>, matching the mode you're browsing in.</>
        )}

      </p>

      <ListDisplayControls

        listKey="competition-history"

        mode={mode}

        setMode={setMode}

        pageSize={pageSize}

        setPageSize={setPageSize}

        page={page}

        setPage={setPage}

        totalPages={totalPages}

        totalMatching={totalMatching}

        itemsCount={history?.length || 0}

        loading={loading}

        loadMore={loadMore}

        hasMore={hasMore}

      />

      {(history?.length || 0) === 0 ? (

        <p style={{ opacity: 0.7 }}>{loading ? "Loading…" : "No finished competitions yet."}</p>

      ) : (

        <div style={{ overflowX: "auto" }}>

          <table style={{ borderCollapse: "collapse", width: "100%" }}>

            <thead>

              <tr>

                <th style={th}>Title</th>

                <th style={th}>Ended</th>

                <th style={th}>Answer</th>

                <th style={th}>Winner</th>

                <th style={th}>Prize</th>

                <th style={th}>Activity</th>

                <th style={th}>Playback</th>

              </tr>

            </thead>

            <tbody>

              {history.map((c) => (

                <tr key={c.id}>

                  <td style={td}>
                    {c.title}
                    <OtherModeTag mode={c.mode} viewing={adminMode(modeStatus)} />
                  </td>

                  <td style={td} title={siteDateTime(c.answeredAt)}>

                    {siteDate(c.answeredAt)}

                  </td>

                  <td style={td}>{c.answer}</td>

                  <td style={td}>

                    {c.winnerUsername || <span style={{ opacity: 0.5 }}>—</span>}

                    {c.wonByLottery && (

                      <span style={{ opacity: 0.6, fontSize: 12 }} title="Nobody guessed it in time — a winning ticket was drawn at random from purchased pixels">

                        {" "}

                        (lottery)

                      </span>

                    )}

                  </td>

                  <td style={td}>

                    {symbol}

                    {(c.prizeMoney || 0).toFixed(2)}

                    {c.prizePaid && (

                      <span style={{ color: "var(--accent-green)", fontSize: 12 }} title="Prize paid">

                        {" "}

                        ✓

                      </span>

                    )}

                  </td>

                  <td style={td} title="Pixel purchases and guesses recorded during this competition">

                    {c.regionCount} / {c.guessCount}

                  </td>

                  <td style={td}>

                    <button

                      style={{ fontSize: 12 }}

                      disabled={!c.hasReplayData}

                      onClick={() => setReplayId(c.id)}

                      title={

                        c.hasReplayData

                          ? "Replay this competition as it happened"

                          : "Nothing to play back — this competition recorded no purchases or guesses"

                      }

                    >

                      ▶ Play

                    </button>

                  </td>

                </tr>

              ))}

            </tbody>

          </table>

        </div>

      )}

      {replayId && <CompetitionReplayModal competitionId={replayId} token={token} onClose={() => setReplayId(null)} />}

    </div>

  );

}




function SettingsCard({ title, children }) {
  return (
    <div
      style={{
        background: "var(--bg-panel, rgba(127,127,127,0.04))",
        border: "1px solid var(--border-color)",
        borderRadius: 10,
        padding: "14px 16px",
      }}
    >
      {title && (
        <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 10 }}>{title}</div>
      )}
      {children}
    </div>
  );
}

function CompetitionDefaultsPanel({ authHeaders, canEdit, modeStatus }) {

  const symbol = useCurrencySymbol();

  const token = authHeaders["x-auth-token"];

  const [settings, setSettings] = useState(null);

  const [defaultPricePerPixel, setDefaultPricePerPixel] = useState("");

  const [creditSettingsError, setCreditSettingsError] = useState(null);
  // HOW LONG A BUYER CAN WATCH BACK A COMPETITION THEY PLAYED.
  const [replayForever, setReplayForever] = useState(false);
  const [replayAmount, setReplayAmount] = useState("12");
  const [replayUnit, setReplayUnit] = useState("months");
  const [replayBusy, setReplayBusy] = useState(false);
  const [creditsPerMajorUnit, setCreditsPerMajorUnit] = useState("");
  const [creditsPerMajorUnitOnPixels, setCreditsPerMajorUnitOnPixels] = useState("");
  const [defaultCreditsPerEntry, setDefaultCreditsPerEntry] = useState("");
  const [minCreditTopUpCredits, setMinCreditTopUpCredits] = useState("");
  const [maxCreditTopUpCredits, setMaxCreditTopUpCredits] = useState("");

  const [defaultPrizeMoney, setDefaultPrizeMoney] = useState("");
  const [startGraceSeconds, setStartGraceSeconds] = useState(30);
  const [deadAirMinutes, setDeadAirMinutes] = useState(1);

  

  

  

  const [reminderShowCountdown, setReminderShowCountdown] = useState(true);
  const [notifyOnConfirmed, setNotifyOnConfirmed] = useState(true);
  const [notifyOnStart, setNotifyOnStart] = useState(false);
  // THE LOCK-IN, read only, so this panel can stop you reaching a state it
  // would have to refuse.
  //
  // It lives in its own card and its own form. Saving THIS panel used to
  // be refused outright when "confirmed" was ticked with no lock — which
  // is correct, but it meant one invalid control blocked the other three:
  // changing the countdown checkbox silently failed because the save it
  // travelled in never landed.
  //
  // Disabling is better than refusing. The state cannot be reached, so
  // the save always works.
  const [lockInMinutes, setLockInMinutes] = useState(0);

  const [reminderCaptureEmail, setReminderCaptureEmail] = useState(true);

  const [reminderLeadMinutes, setReminderLeadMinutes] = useState(60);


  // ---- WHAT A LATE ENTRY IS TOLD, AS A DEFAULT (1.799.0) ----
  //
  // Asked for on 14 Oct 2026. It behaves exactly like lotteryEnabled below it:
  // a default for a NEW competition, stored on that competition when it is
  // made, and never consulted again afterwards - so changing it here leaves
  // every competition already created saying what it was given.
  const [missedNoticeMode, setMissedNoticeMode] = useState("text");
  const [missedNoticeText, setMissedNoticeText] = useState("");
  const [lotteryEnabled, setLotteryEnabled] = useState(true);

  const [freeEntryEnabled, setFreeEntryEnabled] = useState(false);
  // WHICH free routes the compliance switch provides. Both default on, so
  // a site that had free entry enabled before these existed is unchanged.
  // At least one must stay on — the backend refuses otherwise, and the
  // form below explains why rather than silently disabling a box.
  const [freeEntryGuess, setFreeEntryGuess] = useState(true);
  const [freeEntryPixel, setFreeEntryPixel] = useState(true);

  const [busy, setBusy] = useState(false);



  const refresh = () => {

    wsRequest("siteSettings.get")

      .then((data) => {

        setSettings(data);

        setDefaultPricePerPixel(data.defaultPricePerPixel);

        setDefaultPrizeMoney(data.defaultPrizeMoney);
        setStartGraceSeconds(data.startGraceSeconds ?? 30);

        setDeadAirMinutes(data.deadAirMinutes ?? 1);

        

        

        

        setReminderShowCountdown(data.reminderShowCountdown);

        setNotifyOnConfirmed(!!data.notifyOnConfirmed);

        setNotifyOnStart(!!data.notifyOnStart);

        setLockInMinutes(Number(data.competitionLockInMinutes) || 0);


        setReminderCaptureEmail(data.reminderCaptureEmail);

        setReminderLeadMinutes(data.reminderLeadMinutes);


        // THE MODULE ANSWERS FOR AN ABSENT KEY, so the payload already carries
        // a usable mode and wording on a site that has never set either.
        setMissedNoticeMode(data.defaultMissedNoticeMode || "text");
        // THE BOX SHOWS WHAT WOULD BE USED, NOT A BLANK, and its placeholder
        // says what a blank means. Sending the default back unchanged is
        // harmless: the competition form seeds from it either way.
        setMissedNoticeText(data.defaultMissedNoticeText || "");
        setLotteryEnabled(data.lotteryEnabled);

        setFreeEntryEnabled(data.freeEntryEnabled);

        setFreeEntryGuess(data.freeEntryGuess !== false);

        setFreeEntryPixel(data.freeEntryPixel !== false);

      })

      .catch(() => {});

    // THE CREDIT SETTINGS ARE PRIVATE, so they come from a different
    // endpoint — and it has to be called from HERE, where the state and
    // the card actually live.
    //
    // It was first written into SiteSettingsPanel, a different component.
    // The setters below do not exist there, so it threw a ReferenceError
    // inside the promise, and a `.catch(() => {})` swallowed it: the card
    // saved correctly and came back blank with nothing to say why.
    //
    // check-frontend cannot catch this. It approximates scope at MODULE
    // level, so a name declared in one function and read in another
    // passes — its own comment says as much. Reported 6 Sep.
    wsRequest("admin.siteSettings.private", {}, token)
      .then((priv) => {
        setCreditsPerMajorUnit(priv.creditsPerMajorUnit);
        setCreditsPerMajorUnitOnPixels(priv.creditsPerMajorUnitOnPixels);
        setDefaultCreditsPerEntry(priv.defaultCreditsPerEntry);
        setMinCreditTopUpCredits(priv.minCreditTopUpCredits);
        setMaxCreditTopUpCredits(priv.maxCreditTopUpCredits);
        const r = priv.replayRetention || {};
        setReplayForever(!!r.forever);
        setReplayAmount(String(r.amount ?? 12));
        setReplayUnit(r.unit || "months");
        setCreditSettingsError(null);
      })
      .catch((err) => {
        setCreditSettingsError(err?.message || "Could not load the credit settings.");
      });

  };



  useEffect(refresh, []);



  // ITS OWN SAVE, since it now has its own card. Saving the prices used to
  // carry this along with them, which is exactly the confusion splitting
  // the card is meant to remove.
  const saveReplayRetention = async (e) => {
    e.preventDefault();
    setReplayBusy(true);
    try {
      await wsRequest(
        "admin.siteSettings.update",
        {
          replayRetention: {
            forever: replayForever,
            amount: Number(replayAmount),
            unit: replayUnit,
          },
        },
        token
      );
      showToast("Saved.");
    } catch (err) {
      showToast(err.message || "Could not reach backend.", "error");
    }
    setReplayBusy(false);
  };

  const saveStartGrace = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await wsRequest("admin.siteSettings.update", { startGraceSeconds }, token);
      showToast("Saved.");
      refresh();
    } catch (err) {
      showToast(err.message || "Could not reach backend.", "error");
    }
    setBusy(false);
  };

  // Dead air after a competition — its own card and its own Save, like
  // Starting late. Both are timing rules, but an operator changes them
  // for unrelated reasons.
  const saveDeadAir = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await wsRequest("admin.siteSettings.update", { deadAirMinutes: Number(deadAirMinutes) }, token);
      showToast("Saved.");
      refresh();
    } catch (err) {
      showToast(err.message || "Could not reach backend.", "error");
    }
    setBusy(false);
  };

  const saveDefaultPricing = async (e) => {

    e.preventDefault();

    setBusy(true);

    try {

      await wsRequest("admin.siteSettings.update", { defaultPricePerPixel, defaultPrizeMoney }, token);

      showToast("Saved.");

      refresh();

    } catch (err) {

      showToast(err.message || "Could not reach backend.", "error");

    }

    setBusy(false);

  };



  const saveCreditSettings = async (e) => {

    e.preventDefault();

    setBusy(true);

    try {

      await wsRequest("admin.siteSettings.update", { creditsPerMajorUnit, creditsPerMajorUnitOnPixels, defaultCreditsPerEntry, minCreditTopUpCredits, maxCreditTopUpCredits }, token);

      showToast("Saved.");

      refresh();

    } catch (err) {

      showToast(err.message || "Could not reach backend.", "error");

    }

    setBusy(false);

  };



  const saveReminderDefaults = async (e) => {

    e.preventDefault();

    setBusy(true);

    try {

      await wsRequest(

        "admin.siteSettings.update",

        { // THE COUNTDOWN IS OMITTED THE SAME WAY (1.514.0). The box now
          // shows unticked while the stored value stays as it was, so
          // sending what the box shows would turn off a setting the
          // operator never touched - the same trap the note below
          // describes for notifyOnConfirmed.
          ...(lockInMinutes > 0 ? { reminderShowCountdown } : {}),
          reminderCaptureEmail, reminderLeadMinutes, // OMITTED, not sent as false, when there is no lock.
        //
        // The note promises the choice is remembered and applies again as
        // soon as a lock is set. Sending false would wipe it — the panel
        // would be quietly turning off a setting the operator never
        // touched, which is exactly what "remembered" rules out.
        //
        // Undefined means "no change", so the stored value survives and
        // the backend refusal never fires.
        ...(lockInMinutes > 0 ? { notifyOnConfirmed } : {}), notifyOnStart },

        token

      );

      showToast("Saved.");

      refresh();

    } catch (err) {

      showToast(err.message || "Could not reach backend.", "error");

    }

    setBusy(false);

  };



  // ITS OWN FORM AND ITS OWN SAVE, like every other card here. The panel's own
  // note says why: one invalid control in a shared save blocks the others, and
  // a checkbox that silently fails because the save it travelled in never
  // landed is the fault that note was written about.
  const saveMissedNotice = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await wsRequest(
        "admin.siteSettings.update",
        { defaultMissedNoticeMode: missedNoticeMode, defaultMissedNoticeText: missedNoticeText },
        token
      );
      showToast("Saved.");
      refresh();
    } catch (err) {
      showToast(err.message || "Could not save.");
    } finally {
      setBusy(false);
    }
  };

  const saveLotterySettings = async (e) => {

    e.preventDefault();

    setBusy(true);

    try {

      await wsRequest("admin.siteSettings.update", { lotteryEnabled }, token);

      showToast("Saved.");

      refresh();

    } catch (err) {

      showToast(err.message || "Could not reach backend.", "error");

    }

    setBusy(false);

  };



  const saveFreeEntrySettings = async (e) => {

    e.preventDefault();

    setBusy(true);

    try {

      await wsRequest("admin.siteSettings.update", { freeEntryEnabled, freeEntryGuess, freeEntryPixel }, token);

      showToast("Saved.");

      refresh();

    } catch (err) {

      showToast(err.message || "Could not reach backend.", "error");

    }

    setBusy(false);

  };



  // No test/demo variant of any of this exists at all — same

  // reasoning as the backend's own assertNotFrozen check on

  // admin.siteSettings.update.

  // DEMO ONLY. These are settings, and the server agrees — see
  // assertSettingsEditable. Freezing them in Test mode meant switching
  // to Live to save a form and back again to carry on testing.
  const frozen = adminMode(modeStatus) === "demo";

  const frozenTitle = "Leave Demo mode to manage this";



  const inputStyle = {

    padding: 8,

    background: "var(--bg-input)",

    border: "1px solid var(--border-color-light)",

    borderRadius: 6,

    color: "var(--text-primary)",

  };



  return (

    <div style={{ border: "1px solid var(--border-color)", borderRadius: 8, padding: 14, marginBottom: 20 }}>

      <h4 style={{ marginTop: 0 }}>Competition defaults</h4>

      <p style={{ fontSize: 12, opacity: 0.6, marginTop: -6 }}>

        These pre-fill the create-competition form — an admin can still change them per competition.

      </p>



      {settings === null ? (

        <p>Loading…</p>

      ) : (

        <ViewOnlyGate canEdit={canEdit} showNotice>

          {frozen && (

            <p style={{ opacity: 0.7, fontSize: 13, marginBottom: 10 }}>

              Leave Demo mode to manage this — you're currently browsing in{" "}
              {modeLabel(adminMode(modeStatus))} mode.

            </p>

          )}

          <fieldset disabled={frozen} style={{ border: "none", padding: 0, margin: 0, opacity: frozen ? 0.55 : 1 }}>

          {/* CARDS IN A GRID, not a stack.

              auto-fit with a minimum width means the browser decides how
              many columns fit: three on a wide screen, one on a phone,
              with no media query and nothing to keep in step. The page
              was a single column of forms with most of the width unused.

              align-items was START, so a short card kept its own height and
              a row could look ragged. STRETCH since 1.265.0: every card in
              a row now matches the tallest in that row, which is what makes
              the grid read as a grid.

              THE TRADE, so nobody reverses this without knowing it: a short
              card beside a tall one now carries empty space at the bottom.
              That was the reason for start in the first place. Even edges
              won, because the ragged version made cards look unfinished
              rather than merely short. */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(290px, 1fr))",
              gap: 12,
              alignItems: "stretch",
            }}
          >

          <SettingsCard title="Pricing">
          <form onSubmit={saveDefaultPricing}>

            <label style={{ fontSize: 13, display: "block", marginBottom: 10 }}>

              Default price per pixel

              <input

                type="number"

                min="0.01"

                step="0.01"

                required

                value={defaultPricePerPixel}

                onChange={(e) => setDefaultPricePerPixel(e.target.value)}

                style={{ ...inputStyle, display: "block", marginTop: 4, width: "100%" }}

              />

            </label>

            {/* The default entry price moved to the Credits card, where it
                belongs beside the rates it is expressed in. */}

            <label style={{ fontSize: 13, display: "block", marginBottom: 6 }}>

              Default prize money

              <input

                type="number"

                min="0"

                step="1"

                required

                value={defaultPrizeMoney}

                onChange={(e) => setDefaultPrizeMoney(e.target.value)}

                style={{ ...inputStyle, display: "block", marginTop: 4, width: "100%" }}

              />

            </label>

            <div style={{ fontSize: 12, opacity: 0.6, marginBottom: 8 }}>

              Minimum {symbol}0.01 for the two prices. Prize money may be {symbol}0 for a competition with no cash

              prize.

            </div>




            {/*

              The relationship between these two is easy to get wrong and

              invisible until afterwards: every image is 1000x750, so at

              1.00 per pixel the whole image is worth 750,000 against

              whatever the prize is. Spelling it out here as well as on

              the creation form, because this is where the number that

              every new competition starts from actually gets set.

            */}

            {Number(defaultPricePerPixel) > 0 && (

              <div style={{ fontSize: 12, opacity: 0.75, marginBottom: 10 }}>

                At {symbol}

                {Number(defaultPricePerPixel).toFixed(2)} per pixel a 100&times;100 patch costs {symbol}

                {(Number(defaultPricePerPixel) * 10000).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}

                {Number(defaultPrizeMoney) > 0 &&

                  Number(defaultPricePerPixel) * 10000 > Number(defaultPrizeMoney) &&

                  ` — more than the ${symbol}${Number(defaultPrizeMoney).toLocaleString()} prize`}

                .

              </div>

            )}

            <button type="submit" disabled={busy}>

              Save

            </button>

          </form>
          </SettingsCard>

          {/* SPLIT OUT OF PRICING on 1 Sep 2026. It is a timing rule that
              happened to live in the pricing form, so it saved with the
              prices. Its own card means its own Save — which is the point:
              the button now covers what the card shows. */}
          <SettingsCard title="Starting late">
          <form onSubmit={saveStartGrace}>
            <label style={{ fontSize: 13, display: "block", marginBottom: 6 }}>

              How late a competition may start (seconds)

              <input

                type="number"

                min="0"

                step="1"

                required

                value={startGraceSeconds}

                onChange={(e) => setStartGraceSeconds(e.target.value)}

                style={{ ...inputStyle, display: "block", marginTop: 4, width: "100%" }}

              />

            </label>

            {/*
              The ceiling matters more than the floor. Missing a slot by
              seconds is arithmetic and should not discard prepared work -
              but a LONG grace quietly reintroduces sliding, which is the
              whole thing scheduling removes.
            */}
            <div style={{ fontSize: 12, opacity: 0.6, marginBottom: 8 }}>

              A competition that has not started this many seconds after its scheduled time moves to "To reschedule"

              instead of starting late. Keep it short - a long window brings back the moving start times that

              scheduling exists to remove.

            </div>
            <button type="submit" disabled={busy} style={{ marginTop: 12 }}>
              Save
            </button>
          </form>
          </SettingsCard>

          <SettingsCard title="Dead air after a competition">
          <form onSubmit={saveDeadAir}>
            <label style={{ fontSize: 13, display: "block", marginBottom: 6 }}>
              Quiet time after each competition ends (minutes)
              <input
                type="number"
                min="1"
                max="240"
                step="1"
                required
                value={deadAirMinutes}
                onChange={(e) => setDeadAirMinutes(e.target.value)}
                style={{ ...inputStyle, display: "block", marginTop: 4, width: "100%" }}
              />
            </label>
            <div style={{ fontSize: 12, opacity: 0.6 }}>
              The reveal needs somewhere to live. Without a gap, the next competition starts in the
              same second the last one is answered and nobody sees the hidden image.
              <br />
              Nothing can start during it and nothing can be scheduled into it — a competition placed
              inside this window is refused, not quietly moved. Minimum one minute.
            </div>
            <button type="submit" disabled={busy} style={{ marginTop: 12 }}>
              Save
            </button>
          </form>
          </SettingsCard>

          {/* THE LOCK-IN, in the same grid rather than below it.

              It was rendered as a sibling of this whole panel, so it sat
              underneath everything in its own full-width block. It is a
              competition timing setting like the rest and belongs beside
              them. */}
          <LockInPanel token={token} wsRequest={wsRequest} canEdit={canEdit} modeStatus={modeStatus} onSaved={refresh} />
          {/* THE TILE IMAGE, in the grid too.

              It was a sibling of this whole panel with a maxWidth of 500,
              so it sat alone under everything using half the page. Inside
              the grid it spans two columns where there are two — the
              preview and its fields need more room than a settings card,
              and auto-fit clamps a span that does not fit. */}
          <DefaultCoverPanel
            authHeaders={authHeaders}
            token={token}
            canEdit={canEdit}
            modeStatus={modeStatus}
          />

            {/* CREDITS — the unit the site prices entries in.

                Replaced the "Bonus guesses" card on 5 Sep 2026. That set a
                fixed-or-percentage rate for earning FREE GUESSES, which no
                longer exist: buying pixels earns CREDIT, at a rate set here.

                All four are whole numbers of at least 1. Zero is refused
                rather than meaning "off" — a rate of zero would mean money
                buys nothing, and none of these needs an off switch. */}
            <SettingsCard title="Credits">
            <form onSubmit={saveCreditSettings}>
              {creditSettingsError && (
                <div
                  style={{
                    padding: "8px 10px",
                    marginBottom: 10,
                    borderRadius: 6,
                    border: "1px solid #d9534f",
                    background: "rgba(217, 83, 79, 0.1)",
                    fontSize: 13,
                  }}
                >
                  These values could not be loaded, so the boxes below may be blank even
                  though something is saved: {creditSettingsError}
                </div>
              )}
              <div style={{ fontSize: 13, marginBottom: 10, opacity: 0.75 }}>
                An entry costs credits. Credit is bought directly, or earned by buying
                pixels — at a more generous rate, which is the point of the second
                figure.
              </div>

              <label style={{ fontSize: 13, display: "block", marginBottom: 6 }}>
                Credits per {symbol}1 when buying credit directly
                <input
                  type="number"
                  step="1"
                  min="1"
                  value={creditsPerMajorUnit}
                  onChange={(e) => setCreditsPerMajorUnit(e.target.value)}
                  disabled={!canEdit}
                  style={{ ...inputStyle, marginTop: 4 }}
                />
              </label>

              <label style={{ fontSize: 13, display: "block", marginBottom: 6, marginTop: 12 }}>
                Credits per {symbol}1 spent on pixels
                <input
                  type="number"
                  step="1"
                  min="1"
                  value={creditsPerMajorUnitOnPixels}
                  onChange={(e) => setCreditsPerMajorUnitOnPixels(e.target.value)}
                  disabled={!canEdit}
                  style={{ ...inputStyle, marginTop: 4 }}
                />
              </label>
              {Number(creditsPerMajorUnitOnPixels) > 0 && Number(creditsPerMajorUnit) > 0 && (
                <div style={{ fontSize: 12, opacity: 0.6, marginBottom: 10 }}>
                  {Number(creditsPerMajorUnitOnPixels) > Number(creditsPerMajorUnit)
                    ? `Buying pixels is ${(
                        (Number(creditsPerMajorUnitOnPixels) / Number(creditsPerMajorUnit) - 1) *
                        100
                      ).toFixed(0)}% more generous than topping up directly.`
                    : Number(creditsPerMajorUnitOnPixels) === Number(creditsPerMajorUnit)
                      ? "The same as topping up directly — no incentive to buy pixels."
                      : "LESS generous than topping up directly, which is probably not intended."}
                </div>
              )}

              <label style={{ fontSize: 13, display: "block", marginBottom: 6, marginTop: 12 }}>
                Credits per entry, for a new competition
                <input
                  type="number"
                  step="1"
                  min="1"
                  value={defaultCreditsPerEntry}
                  onChange={(e) => setDefaultCreditsPerEntry(e.target.value)}
                  disabled={!canEdit}
                  style={{ ...inputStyle, marginTop: 4 }}
                />
              </label>
              <div style={{ fontSize: 12, opacity: 0.6, marginBottom: 10 }}>
                The default only. Each competition carries its own, editable until it locks.
              </div>

              <label style={{ fontSize: 13, display: "block", marginBottom: 6, marginTop: 12 }}>
                Smallest top-up, in credits
                <input
                  type="number"
                  step="1"
                  min="1"
                  value={minCreditTopUpCredits}
                  onChange={(e) => setMinCreditTopUpCredits(e.target.value)}
                  disabled={!canEdit}
                  style={{ ...inputStyle, marginTop: 4 }}
                />
              </label>
              {Number(minCreditTopUpCredits) > 0 && Number(creditsPerMajorUnit) > 0 && (
                <div style={{ fontSize: 12, opacity: 0.6, marginBottom: 10 }}>
                  = {symbol}
                  {(Number(minCreditTopUpCredits) / Number(creditsPerMajorUnit)).toFixed(2)} at the
                  rate above
                </div>
              )}

              {/* THE CEILING. min="0" rather than "1", because 0 means no
                  limit — the default, and what every site has until
                  somebody sets one. Refused at save if it falls below the
                  smallest, since that would refuse every top-up and do it
                  with the SMALLEST's message. */}
              <label style={{ fontSize: 13, display: "block", marginBottom: 6, marginTop: 12 }}>
                Largest top-up (0 for no limit)
                <input
                  type="number"
                  step="1"
                  min="0"
                  value={maxCreditTopUpCredits}
                  onChange={(e) => setMaxCreditTopUpCredits(e.target.value)}
                  disabled={!canEdit}
                  style={{ ...inputStyle, marginTop: 4 }}
                />
              </label>
              {Number(maxCreditTopUpCredits) > 0 && Number(creditsPerMajorUnit) > 0 && (
                <div style={{ fontSize: 12, opacity: 0.6, marginBottom: 10 }}>
                  = {symbol}
                  {(Number(maxCreditTopUpCredits) / Number(creditsPerMajorUnit)).toFixed(2)} at the
                  rate above
                </div>
              )}
              <div style={{ fontSize: 12, opacity: 0.6, marginBottom: 10 }}>
                Catches a slipped decimal point in the amount a buyer types. It applies to
                partner top-ups too, which no confirmation screen here could reach.
              </div>

              <button type="submit" disabled={busy || !canEdit}>
                {busy ? "Saving…" : "Save"}
              </button>
            </form>
            </SettingsCard>

          {/* MOVED HERE FROM FINANCE (1.264.0), into the grid as its own
              card (1.265.0). It sat below the panel at full width, which
              made the one setting that belongs with the Credits card above
              look like a separate section bolted underneath. */}
          <SettingsCard title="Selling entry credit">
            <EntryCreditSettingsPanel
              token={token}
              wsRequest={wsRequest}
              canEdit={canEdit}
              modeStatus={modeStatus}
            />
          </SettingsCard>

          <SettingsCard title="Promotion between competitions">
          <form onSubmit={saveReminderDefaults}>

            <div style={{ fontSize: 13, marginBottom: 8 }}>Promotion between competitions</div>

            <p style={{ fontSize: 12, opacity: 0.6, marginTop: -4, marginBottom: 10 }}>

              Any combination, including none — a partner promoting elsewhere needs none of this.

              Reminder EMAILS are separate: those go to whoever used the capture form below.

            </p>

            <div style={{ fontSize: 11.5, opacity: 0.5, marginBottom: 8, letterSpacing: 0.4 }}>ON THE SITE</div>

            {/* THE SAME DEPENDENCY AS notifyOnConfirmed BELOW (1.514.0),

                and until now the only one of the two that did not say so.

                Reported 27 Sep 2026: a competition created, the queue

                resumed, and no countdown - because the lock-in was zero, so

                nothing could lock and nextUpDecision had nothing

                trustworthy to count to. The rule was written here; that

                this site does not meet it was not. */}

            <label style={{ fontSize: 13, display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>

              <input

                type="checkbox"

                checked={reminderShowCountdown && lockInMinutes > 0}

                disabled={!(lockInMinutes > 0)}

                onChange={(e) => setReminderShowCountdown(e.target.checked)}

              />

              Show countdown timer to next competition

            </label>

            <p style={{ fontSize: 12, opacity: 0.6, margin: "-4px 0 10px 24px" }}>

              {lockInMinutes > 0

                ? "Only shown when the next competition is locked in — a countdown to a date that can still move would change while somebody watches it."

                : reminderShowCountdown

                  ? "Set a competition lock-in above zero to enable this — without one no competition ever locks, so there is never a date worth counting to. Your choice is remembered and applies again as soon as you set one."

                  : "Set a competition lock-in above zero to enable this — without one no competition ever locks, so there is never a date worth counting to."}

            </p>

            <label style={{ fontSize: 13, display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>

              <input

                type="checkbox"

                checked={reminderCaptureEmail}

                onChange={(e) => setReminderCaptureEmail(e.target.checked)}

              />

              Capture email address for a reminder

            </label>

            <label

              style={{

                fontSize: 13,

                display: "flex",

                alignItems: "center",

                gap: 8,

                marginLeft: 24,

                marginBottom: 10,

                opacity: reminderCaptureEmail ? 1 : 0.5,

              }}

            >

              ...if the next CONFIRMED competition is longer than

              <input

                type="number"

                min="0.1"

                step="0.1"

                required

                value={reminderLeadMinutes}

                onChange={(e) => setReminderLeadMinutes(e.target.value)}

                disabled={!reminderCaptureEmail}

                style={{ ...inputStyle, width: 70 }}

              />

              minutes till it starts

            </label>

            <p style={{ fontSize: 12, opacity: 0.6, marginTop: -4, marginBottom: 10 }}>

              A logged-in visitor already has an email on file, so they just see a checkbox to opt in — a

              logged-out visitor sees a manual email field instead. This is also when the reminder email itself

              actually goes out (that many minutes before the competition starts), so the form hides itself once

              there's no longer enough time left for a reminder at that point to be useful.

            </p>

            <div style={{ borderTop: "1px solid var(--border-color)", paddingTop: 12, marginTop: 4 }}>

              <div style={{ fontSize: 11.5, opacity: 0.5, marginBottom: 8, letterSpacing: 0.4 }}>
                NOTIFICATIONS, ON THE SITE
              </div>

              <label style={{ fontSize: 13, display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                <input
                  type="checkbox"
                  checked={notifyOnConfirmed && lockInMinutes > 0}
                  disabled={!(lockInMinutes > 0)}
                  onChange={(e) => setNotifyOnConfirmed(e.target.checked)}
                />
                When a competition is confirmed
              </label>
              <p style={{ fontSize: 12, opacity: 0.6, margin: "0 0 10px 24px" }}>
                Appears in the notification bell the moment its date freezes.{" "}
                {lockInMinutes > 0
                  ? "Confirmation is what its date freezing means."
                  : notifyOnConfirmed
                    ? "Set a competition lock-in above zero to enable this — without one there is no confirmed time to announce. Your choice is remembered and applies again as soon as you set one."
                    : "Set a competition lock-in above zero to enable this — without one there is no confirmed time to announce."}
              </p>

              <label style={{ fontSize: 13, display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                <input
                  type="checkbox"
                  checked={notifyOnStart}
                  onChange={(e) => setNotifyOnStart(e.target.checked)}
                />
                When a competition starts
              </label>
              <p style={{ fontSize: 12, opacity: 0.6, margin: "0 0 12px 24px" }}>
                Also in the bell. Claims nothing about the future, so it works with or without a lock-in.
              </p>

            </div>


            <button type="submit" disabled={busy}>

              Save

            </button>

          </form>
          </SettingsCard>

          <SettingsCard title="If someone is beaten to the answer">
          <form onSubmit={saveMissedNotice}>
            <div style={{ fontSize: 13, marginBottom: 8 }}>If someone is beaten to the answer</div>
            <label style={{ fontSize: 13, display: "block", marginBottom: 10 }}>
              Default notice
              <select
                value={missedNoticeMode}
                onChange={(e) => setMissedNoticeMode(e.target.value)}
                style={inputStyle}
              >
                <option value="text">Show a message</option>
                <option value="margin">Show how far behind they were</option>
              </select>
            </label>
            <label style={{ fontSize: 13, display: "block", marginBottom: 10 }}>
              {missedNoticeMode === "margin"
                ? "Default message (not shown while the margin is selected)"
                : "Default message"}
              <input
                type="text"
                maxLength={200}
                value={missedNoticeText}
                placeholder="Someone ahead of you won"
                onChange={(e) => setMissedNoticeText(e.target.value)}
                disabled={missedNoticeMode === "margin"}
                style={inputStyle}
              />
            </label>
            <p style={{ fontSize: 12, opacity: 0.6, marginTop: -4, marginBottom: 10 }}>
              THE DEFAULT for new competitions. Each competition carries its own choice, set when it
              is created and editable until it locks — so changing this does NOT affect anything
              already created.
              <br />
              Left blank, the site says "Someone ahead of you won". Only ever shown to someone whose
              answer was right — a wrong guess is never told a margin, because nobody beat them to
              it.
            </p>
            <button type="submit" disabled={busy}>
              Save
            </button>
          </form>
          </SettingsCard>
          <SettingsCard title="Lottery fallback">
          <form onSubmit={saveLotterySettings}>

            <div style={{ fontSize: 13, marginBottom: 8 }}>Lottery fallback</div>

            <label style={{ fontSize: 13, display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>

              <input type="checkbox" checked={lotteryEnabled} onChange={(e) => setLotteryEnabled(e.target.checked)} />

              Convert to lottery

            </label>

            <p style={{ fontSize: 12, opacity: 0.6, marginTop: -4, marginBottom: 10 }}>

              THE DEFAULT for new competitions. Each competition now carries its own choice, set when
              it is created and editable until it locks — so changing this does NOT affect anything
              already created.
              <br />
              When on, an unsolved competition converts to a lottery draw ahead of its end date and gets

              automatically drawn if still unsolved once that date arrives. When unchecked, a competition has no

              end date at all — it simply continues until someone guesses correctly, however long that takes.

              Only affects competitions that haven't started yet — a change here never alters a competition already

              active, which keeps whatever this was set to the moment it started.

            </p>

            <button type="submit" disabled={busy}>

              Save

            </button>

          </form>
          </SettingsCard>

          <SettingsCard title="UK compliance">
          <form onSubmit={saveFreeEntrySettings}>

            <div style={{ fontSize: 13, marginBottom: 8 }}>UK compliance</div>

            <label style={{ fontSize: 13, display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>

              <input

                type="checkbox"

                checked={freeEntryEnabled}

                onChange={(e) => setFreeEntryEnabled(e.target.checked)}

              />

              Enable Free Entry

            </label>

            {/* THE TWO ROUTES, only once the compliance switch is on.

                Both are genuine entry routes, to the two different ways of
                winning: the free guess is a free way to win by solving it,
                and the free pixel is an account-bound region and therefore
                a LOTTERY TICKET — a free way into the draw that happens
                when nobody solves it.

                A partner may want one and not the other. At least one must
                stay on: the backend refuses both-off, and the note below
                says so rather than a box silently disabling itself. */}
            {freeEntryEnabled && (
              <div style={{ marginLeft: 22, marginBottom: 10 }}>
                <label style={{ fontSize: 13, display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                  <input
                    type="checkbox"
                    checked={freeEntryGuess}
                    onChange={(e) => setFreeEntryGuess(e.target.checked)}
                  />
                  Allow a free entry
                </label>
                <p style={{ fontSize: 12, opacity: 0.6, marginTop: -2, marginBottom: 8 }}>
                  One free guess per competition — a free route to winning by solving it.
                </p>

                <label style={{ fontSize: 13, display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                  <input
                    type="checkbox"
                    checked={freeEntryPixel}
                    onChange={(e) => setFreeEntryPixel(e.target.checked)}
                  />
                  Allow a free pixel
                </label>
                <p style={{ fontSize: 12, opacity: 0.6, marginTop: -2, marginBottom: 0 }}>
                  One free pixel per competition. It counts as a lottery ticket, so this is a
                  free route into the draw that happens when nobody solves it.
                </p>

                {!freeEntryGuess && !freeEntryPixel && (
                  <p style={{ fontSize: 12, color: "var(--accent-yellow)", marginTop: 8, marginBottom: 0 }}>
                    At least one must stay on. With both off, the site would claim a
                    no-purchase-necessary route it does not provide — saving will be refused.
                  </p>
                )}
              </div>
            )}

            <p style={{ fontSize: 12, opacity: 0.6, marginTop: -4, marginBottom: 10 }}>

              A prize competition that requires payment to enter, with no way to enter for free, is legally

              gambling in the UK and needs a licence — this is the "no purchase necessary" route the law requires

              instead. When checked, every logged-in user gets one free pixel selection and one free guess per

              competition, on top of anything they've paid for. The free pixel is a single fixed 1×1px area (not a

              user-chosen size, unlike a normal purchase) revealed through the same private-then-public process as

              any other pixel; the free guess stacks with any bonus guesses already earned by spending, rather than

              replacing them.

            </p>

            <button type="submit" disabled={busy}>

              Save

            </button>

          </form>
          </SettingsCard>

          {/* HOW LONG A BUYER CAN WATCH BACK A COMPETITION THEY PLAYED.
              A replay is a snapshot — the round frozen as it ended,
              images and all — so it survives the competition being
              deleted and costs roughly a megabyte each. Nothing deletes
              them but this. */}
          <SettingsCard title="Replays">
            <p style={{ fontSize: 12.5, opacity: 0.75, marginTop: 0 }}>
              How long a buyer can watch back a competition they took part in. Each replay is a frozen
              copy of the round, so it keeps working after the competition itself is gone — and takes up
              space for as long as it is kept.
            </p>
            <form onSubmit={saveReplayRetention}>
              <label style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10, fontSize: 13 }}>
                <input
                  type="checkbox"
                  checked={replayForever}
                  disabled={replayBusy || frozen}
                  onChange={(e) => setReplayForever(e.target.checked)}
                />
                Keep replays forever
              </label>

              <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                <span style={{ fontSize: 13, opacity: replayForever ? 0.45 : 1 }}>Keep for</span>
                <input
                  type="number"
                  min="1"
                  max="999"
                  value={replayAmount}
                  disabled={replayForever || replayBusy || frozen}
                  onChange={(e) => setReplayAmount(e.target.value)}
                  style={{ width: 80, padding: "6px 8px" }}
                />
                <select
                  value={replayUnit}
                  disabled={replayForever || replayBusy || frozen}
                  onChange={(e) => setReplayUnit(e.target.value)}
                  style={{ padding: "6px 8px" }}
                >
                  <option value="days">Days</option>
                  <option value="weeks">Weeks</option>
                  <option value="months">Months</option>
                  <option value="years">Years</option>
                </select>
                <button type="submit" disabled={replayBusy || frozen}>
                  {replayBusy ? "Saving…" : "Save"}
                </button>
              </div>
            </form>
          </SettingsCard>



          </div>

          </fieldset>

        </ViewOnlyGate>

      )}

    </div>

  );

}



function DefaultCoverPanel({ authHeaders, token, canEdit, modeStatus }) {

  const [line1, setLine1] = useState("");

  const [line2, setLine2] = useState("");

  const [busy, setBusy] = useState(false);

  // Bumped to force a refetch after a save or a regenerate — the same

  // cache-busting parameter this used before, now driving the shared

  // authed-image hook below instead of an <img src> directly.

  const [previewNonce, setPreviewNonce] = useState(() => Date.now());



  const refresh = () => {

    wsRequest("siteSettings.get")

      .then((data) => {

        // Only seed the fields once, so this doesn't overwrite in-progress edits

        setLine1((prev) => (prev === "" && data.fogLine1 ? data.fogLine1 : prev));

        setLine2((prev) => (prev === "" && data.fogLine2 ? data.fogLine2 : prev));

      })

      .catch(() => {});

  };



  useEffect(refresh, []);



  const refreshPreview = () => setPreviewNonce(Date.now());



  // Fetched with the auth header rather than set as an <img src>. That

  // route is requirePermission-gated and auth here is header-only, which

  // a browser cannot satisfy for an image request — so the previous

  // bare <img src> 401'd every time and this preview never rendered at

  // all (confirmed against the live server with `curl -sI`). No initial

  // useEffect needed anymore either: the hook fetches on mount.

  const { src: previewUrl, failed: previewError } = useAuthedImageUrl(`${API_BASE}/api/admin/fog-preview?t=${previewNonce}`, token);



  const save = async (e) => {

    e.preventDefault();

    setBusy(true);

    try {

      await wsRequest("admin.siteSettings.update", { fogLine1: line1, fogLine2: line2 }, token);

      showToast("Saved.");

      refreshPreview();

    } catch (err) {

      showToast(err.message || "Could not reach backend.", "error");

    }

    setBusy(false);

  };



  const regenerate = async () => {

    setBusy(true);

    try {

      const data = await wsRequest("admin.defaultCover.regenerate", {}, token);

      showToast(`Regenerated ${data.count} competition(s).`);

    } catch (err) {

      showToast(err.message || "Could not reach backend.", "error");

    }

    setBusy(false);

  };



  // No test/demo variant of any of this exists at all — same

  // reasoning as the backend's own assertNotFrozen checks on

  // admin.siteSettings.update and admin.defaultCover.regenerate.

  // DEMO ONLY. These are settings, and the server agrees — see
  // assertSettingsEditable. Freezing them in Test mode meant switching
  // to Live to save a form and back again to carry on testing.
  const frozen = adminMode(modeStatus) === "demo";

  const frozenTitle = "Leave Demo mode to manage this";



  const inputStyle = {

    padding: 8,

    background: "var(--bg-input)",

    border: "1px solid var(--border-color-light)",

    borderRadius: 6,

    color: "var(--text-primary)",

    width: "100%",

    boxSizing: "border-box",

  };



  return (

    <div style={{ border: "1px solid var(--border-color)", borderRadius: 10, padding: "14px 16px", background: "var(--bg-panel, rgba(127,127,127,0.04))", gridColumn: "span 2", opacity: frozen ? 0.55 : 1 }}>

      <h4 style={{ marginTop: 0 }}>Tile image</h4>

      <p style={{ fontSize: 13, opacity: 0.7, marginTop: -6 }}>

        Two lines of text, rendered server-side as a small card and tiled across the "fog" shown over every

        competition before any purchases are made — generated fresh at the exact size each competition needs, so

        there's no upload or image sizing to get right. Saving updates every existing competition immediately, not

        just new ones. Leave both blank for a plain dark color instead.

      </p>

      <p style={{ fontSize: 12, color: "var(--accent-yellow)", marginBottom: 14 }}>

        Requires a font actually installed on the server to render this text — if the preview below shows empty

        boxes instead of readable text, the server needs a font package installed (e.g.{" "}

        <code>apt-get install -y fontconfig fonts-dejavu-core</code>), then click "Regenerate placeholder images"

        again below.

      </p>



      <ViewOnlyGate canEdit={canEdit} showNotice>

        {frozen && (

          <p style={{ opacity: 0.7, fontSize: 13, marginBottom: 10 }}>

            Leave Demo mode to manage this — you're currently browsing in{" "}
            {modeLabel(adminMode(modeStatus))} mode.

          </p>

        )}

        <fieldset disabled={frozen} style={{ border: "none", padding: 0, margin: 0, opacity: frozen ? 0.55 : 1 }}>

        <form onSubmit={save}>

          <label style={{ display: "block", marginBottom: 10 }}>

            <span style={{ fontSize: 13, opacity: 0.8 }}>Line 1</span>

            <input

              value={line1}

              onChange={(e) => setLine1(e.target.value)}

              placeholder="ACME Competitions"

              style={{ ...inputStyle, marginTop: 4 }}

            />

          </label>

          <label style={{ display: "block", marginBottom: 12 }}>

            <span style={{ fontSize: 13, opacity: 0.8 }}>Line 2</span>

            <input

              value={line2}

              onChange={(e) => setLine2(e.target.value)}

              placeholder="Select an area here"

              style={{ ...inputStyle, marginTop: 4 }}

            />

          </label>

          <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 16 }}>

            <button type="submit" disabled={busy}>

              {busy ? "Saving…" : "Save"}

            </button>

          </div>

        </form>

        </fieldset>

      </ViewOnlyGate>



      <div style={{ fontSize: 13, opacity: 0.7, marginBottom: 6 }}>Preview</div>

      {previewUrl && !previewError && (

        <img

          src={previewUrl}

          alt="Fog tile preview"

          style={{ display: "block", borderRadius: 8, marginBottom: 16, border: "1px solid var(--border-color)" }}

        />

      )}

      {previewError && <p style={{ fontSize: 13, opacity: 0.6, marginBottom: 16 }}>Could not load preview.</p>}



      <ViewOnlyGate canEdit={canEdit}>

        <fieldset disabled={frozen} style={{ border: "none", padding: 0, margin: 0, display: "flex", alignItems: "center", gap: 8, opacity: frozen ? 0.55 : 1 }}>

          <button onClick={regenerate} disabled={busy} title={frozen ? frozenTitle : undefined}>

            {busy ? "…" : "Regenerate placeholder images"}

          </button>

        </fieldset>

      </ViewOnlyGate>

      <p style={{ fontSize: 12, opacity: 0.55, marginTop: 8, marginBottom: 0 }}>

        Forces every competition's fog image to rebuild from the current text (or flat color) right now — a safety

        net for any competition whose fog image was generated a long time ago and never refreshed since.

      </p>

    </div>

  );

}



function UsersPanel({ authHeaders, searchTerm, setSearchTerm, modeStatus, canEdit }) {

  const token = authHeaders["x-auth-token"];

  const [users, setUsers] = useState(null);

  const [error, setError] = useState(null);

  // LIVE OR TEST ACCOUNTS (1.468.0). This list had no mode scoping at
  // all: live and test accounts sat in it together, unlabelled, and
  // editing a test one from Live was refused with nothing on the row
  // saying why. Every row shown is now one this admin can act on.
  const [view, setView] = useState("live");
  // ONLY ASK WHILE THE SITE IS LIVE. The filter is not drawn in Test or
  // Demo - there is nothing to choose - but the state behind it still
  // says "live", and asking for Live from Test is refused by the same
  // rule that makes the filter pointless there. Sending nothing means
  // the admin's own mode, which is the right answer in every mode.
  const askMode = adminMode(modeStatus) === "live" ? view : undefined;

  const { confirm, confirmModal } = useConfirm();



  const load = () => {

    wsRequest("admin.users.list", { mode: askMode }, token)

      // DEMO ACCOUNTS ARE NOT SHOWN HERE AT ALL.
      //
      // They are generated twenty at a time, carry one published
      // password, and are wiped and rebuilt wholesale by Generate on the
      // competition queue. Nothing in this panel can be done to one:
      // since demo stopped being a mode an admin can be in, every action
      // on them is frozen with no mode left to switch to.
      //
      // So they were filling a list of real buyers with rows offering
      // nothing. The COUNT of them still shows on the Mock Data tab,
      // which is where they belong.
      //
      // Filtered here rather than in listUsers(): the API keeps returning
      // them, so anything that legitimately needs them — the mock-data
      // tools, a future screen, a test — is unaffected.
      .then((data) => setUsers((data || []).filter((u) => (u.mode || "live") !== "demo")))

      .catch((err) => setError(err.message || "Could not reach backend."));

  };



  useEffect(load, [askMode]);



  // Real-time refresh — a user signing up, or another admin editing/

  // removing an account, happens independently of this browser tab.

  useEffect(() => onBroadcast((message) => {

    if (message.type === "users-changed") load();

  }), []);



  const patchUser = async (id, patch) => {

    try {

      await wsRequest("admin.users.edit", { id, ...patch }, token);

      load();

      return null;

    } catch (err) {

      return err.message || "Could not reach backend.";

    }

  };



  const remove = async (id, email, mode) => {

    if (!(await confirm(`Delete the ${modeLabel(mode)} account for "${email}"? This can't be undone.`))) return;

    try {

      await wsRequest("admin.users.remove", { id }, token);

      showToast(`${modeLabel(mode)} user deleted.`);

    } catch (err) {

      showToast(err.message || "Could not reach backend.", "error");

    }

    load();

  };



  // The limit is the panel's to choose: it opens on the default page and
  // asks for the whole history only when somebody presses Show all.
  const loadCredits = (id, limit) =>
    wsRequest("admin.entryCredits.forUser", limit ? { userId: id, limit } : { userId: id }, token);

  // Rejects rather than returning a message, unlike patchUser above: the
  // credits panel needs to distinguish "refused, and here is why" from
  // "succeeded", and the reason matters - a 409 means credits are switched
  // off, a 402 means the balance is too small.
  const adjustCredits = (id, credits, reason) =>
    wsRequest("admin.entryCredits.adjust", { userId: id, credits, reason }, token);

  const resetPassword = async (id, newPassword) => {

    try {

      await wsRequest("admin.users.resetPassword", { id, newPassword }, token);

      return null;

    } catch (err) {

      return err.message || "Could not reach backend.";

    }

  };



  const term = (searchTerm || "").trim().toLowerCase();

  const filteredUsers = users && term

    ? users.filter((u) => u.email.toLowerCase().includes(term) || (u.username || "").toLowerCase().includes(term))

    : users;



  return (

    <section style={{ marginTop: 20, maxWidth: 700 }}>

      <h3>General users</h3>

      <DataModeFilter value={view} onChange={setView} siteIsLive={adminMode(modeStatus) === "live"} what="accounts" />

      <input

        placeholder="Search by email or username…"

        value={searchTerm || ""}

        onChange={(e) => setSearchTerm(e.target.value)}

        style={{

          padding: 8,

          marginBottom: 14,

          width: "100%",

          background: "var(--bg-input)",

          border: "1px solid var(--border-color-light)",

          borderRadius: 6,

          color: "var(--text-primary)",

          boxSizing: "border-box",

        }}

      />

      {error && <p style={{ color: "var(--accent-red)" }}>{error}</p>}

      {filteredUsers === null ? (

        <p>Loading…</p>

      ) : filteredUsers.length === 0 ? (

        <p style={{ opacity: 0.7 }}>{term ? "No users match that search." : "No general user accounts yet."}</p>

      ) : (

        <ViewOnlyGate canEdit={canEdit} showNotice>

          {filteredUsers.map((u) => (

            <UserRow key={u.id} user={u} onSave={patchUser} onRemove={remove} onResetPassword={resetPassword} onLoadCredits={loadCredits} onAdjustCredits={adjustCredits} modeStatus={modeStatus} />

          ))}

        </ViewOnlyGate>

      )}

      {confirmModal}

    </section>

  );

}



const USER_FIELD_DEFS = [

  { key: "username", label: "Username" },

  { key: "firstName", label: "First name" },

  { key: "lastName", label: "Last name" },

  { key: "dob", label: "Date of birth", type: "date" },

  { key: "addressLine1", label: "Address line 1" },

  { key: "addressLine2", label: "Address line 2" },

  { key: "addressLine3", label: "Address line 3" },

  { key: "townCity", label: "Town / city" },

  { key: "country", label: "Country" },

  { key: "postcode", label: "Postcode" },

];



function UserRow({ user, onSave, onRemove, onResetPassword, onLoadCredits, onAdjustCredits, modeStatus }) {

  const { theme } = useTheme();

  const [email, setEmail] = useState(user.email);

  const [expanded, setExpanded] = useState(false);

  const [details, setDetails] = useState(user);

  const [busy, setBusy] = useState(false);

  const [resetOpen, setResetOpen] = useState(false);

  const [newPassword, setNewPassword] = useState("");

  // Same rule the backend's own assertNotFrozen enforces.

  const frozen = modeStatus ? modeOf(user) !== modeStatus.effectiveMode : false;

  const frozenTitle = !frozen
    ? undefined
    : modeOf(user) === "demo"
      ? "Demo data — generated and replaced wholesale by Generate on the competition queue"
      : `${modeLabel(modeOf(user))} data — switch modes to act on it`;
  // A DISABLED BUTTON IS AN INVITATION TO SWITCH MODES. That works for
  // test and live records — "switch modes to act on it" is a real
  // instruction.
  //
  // It is not for DEMO records. Demo stopped being a mode an admin can be
  // in on 11 Sep 2026, so there is no mode to switch to and the row
  // offered four things none of which can ever be done. Nothing is lost:
  // these accounts are wiped and rebuilt wholesale by Generate on the
  // queue panel, which is the only thing anybody does to them.
  const unreachable = modeOf(user) === "demo";



  const inputStyle = {

    padding: 6,

    background: "var(--bg-input)",

    border: "1px solid var(--border-color-light)",

    borderRadius: 6,

    color: "var(--text-primary)",

    colorScheme: theme, // without this, Chromium/Brave renders native date/color picker icons assuming the opposite theme's background, so they end up invisible/unclickable

  };



  const saveEmail = async () => {

    setBusy(true);

    const err = await onSave(user.id, { email });

    if (err) showToast(err, "error");

    else showToast("Saved.");

    setBusy(false);

  };



  const toggleDisabled = async () => {

    setBusy(true);

    const err = await onSave(user.id, { disabled: !user.disabled });

    if (err) showToast(err, "error");

    else showToast(user.disabled ? "User enabled." : "User disabled.");

    setBusy(false);

  };



  const saveDetails = async (e) => {

    e.preventDefault();

    setBusy(true);

    const err = await onSave(user.id, details);

    if (err) showToast(err, "error");

    else showToast("Saved.");

    setBusy(false);

  };



  return (

    <div style={{ border: "1px solid var(--border-color)", borderRadius: 8, padding: 12, marginBottom: 10, opacity: frozen ? 0.55 : 1 }}>

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>

        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>

          <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} style={inputStyle} disabled={frozen} />

          {email !== user.email && (

            <button onClick={saveEmail} disabled={busy || frozen} title={frozenTitle}>

              Save

            </button>

          )}

          {user.disabled && (

            <span style={{ color: "var(--accent-red)", fontSize: 13, border: "1px solid var(--accent-red)", borderRadius: 4, padding: "2px 6px" }}>

              disabled

            </span>

          )}

          <span

            style={{

              fontSize: 11,

              padding: "1px 6px",

              borderRadius: 10,

              border: `1px solid ${modeColor(modeOf(user))}`,

              opacity: 0.85,

            }}

          >

            {modeLabel(modeOf(user))}

          </span>

          {/* WHERE THIS ACCOUNT CAME FROM.
              A buyer vouched for by a partner through the API is not the
              same thing as somebody who signed up here, and the list had
              no way to tell — both are an email address and a mode.

              viaPartner comes from `linkedPartner`, which is written only
              by the server-to-server vouch endpoints, so it cannot be
              forged. */}
          {user.viaPartner && (
            <span
              style={{
                fontSize: 11,
                padding: "1px 6px",
                borderRadius: 10,
                border: "1px solid var(--accent-blue)",
                color: "var(--accent-blue)",
                opacity: 0.85,
                marginLeft: 6,
              }}
              title={`Created through the API by partner ${user.viaPartner}`}
            >
              API
            </span>
          )}

        </div>

        {/* Hidden entirely for a demo record — see `unreachable`. */}
        {unreachable ? (
          <span style={{ fontSize: 12.5, opacity: 0.55 }}>
            Generated — replaced by Generate on the competition queue
          </span>
        ) : (
          <div style={{ display: "flex", gap: 8 }}>
  
            <button onClick={() => setExpanded(!expanded)} disabled={busy || frozen} title={frozenTitle}>
  
              {expanded ? "Hide details" : "Edit details"}
  
            </button>
  
            <button onClick={() => setResetOpen(!resetOpen)} disabled={busy || frozen} title={frozenTitle}>
  
              {resetOpen ? "Cancel reset" : "Reset password"}
  
            </button>
  
            <button onClick={toggleDisabled} disabled={busy || frozen} title={frozenTitle}>
  
              {user.disabled ? "Enable" : "Disable"}
  
            </button>
  
            <button onClick={() => onRemove(user.id, user.email, modeOf(user))} disabled={busy || frozen} title={frozenTitle}>
  
              Remove
  
            </button>
  
          </div>
        )}

      </div>

      <div style={{ fontSize: 13, opacity: 0.7, marginTop: 6 }}>Joined {siteDateTime(user.createdAt)}</div>

      <div style={{ fontSize: 13, marginTop: 4 }}>

        {user.phoneVerified ? (

          <span style={{ color: "var(--accent-green)" }}>

            ✓ Phone verified{user.phone ? ` — ${user.phone}` : ""}

          </span>

        ) : (

          <span style={{ opacity: 0.6 }}>Phone not verified</span>

        )}

      </div>



      {resetOpen && (

        <div style={{ marginTop: 12, paddingTop: 12, borderTop: "1px solid var(--border-color)", display: "flex", gap: 8, alignItems: "center" }}>

          <input

            type="password"

            placeholder="New password (min 8 characters)"

            required

            minLength={8}

            value={newPassword}

            onChange={(e) => setNewPassword(e.target.value)}

            style={inputStyle}

          />

          <button

            onClick={async () => {

              const err = await onResetPassword(user.id, newPassword);

              if (err) {

                showToast(err, "error");

              } else {

                showToast("Password reset.");

                setNewPassword("");

              }

            }}

          >

            Set password

          </button>

        </div>

      )}



      {expanded && (

        <form onSubmit={saveDetails} style={{ marginTop: 12, paddingTop: 12, borderTop: "1px solid var(--border-color)" }}>

          {USER_FIELD_DEFS.map((f) => {

            const locked = f.key === "username" && user.username;

            return (

              <label key={f.key} style={{ display: "block", fontSize: 13, marginBottom: 8 }}>

                {f.label}

                <input

                  type={f.type || "text"}

                  value={details[f.key] || ""}

                  onChange={(e) => setDetails({ ...details, [f.key]: e.target.value })}

                  style={{ ...inputStyle, width: "100%", marginTop: 2, opacity: locked ? 0.6 : 1 }}

                  disabled={locked}

                />

                {locked && <span style={{ fontSize: 11, opacity: 0.55 }}>Can't be changed once set</span>}

              </label>

            );

          })}

          <button type="submit" disabled={busy}>

            {busy ? "Saving…" : "Save details"}

          </button>

        </form>

      )}

        {expanded && (
          <UserCredits
            userId={user.id}
            onLoad={onLoadCredits}
            onAdjust={onAdjustCredits}
            frozen={frozen}
            frozenTitle={frozenTitle}
          />
        )}

    </div>

  );

}



function ResetPasswordButton({ onReset, disabled, title }) {

  const [open, setOpen] = useState(false);

  const [newPassword, setNewPassword] = useState("");



  const inputStyle = {

    padding: 6,

    background: "var(--bg-input)",

    border: "1px solid var(--border-color-light)",

    borderRadius: 6,

    color: "var(--text-primary)",

  };



  return (

    <div style={{ display: "inline-block" }}>

      <button disabled={disabled} title={title} onClick={() => setOpen(!open)}>

        {open ? "Cancel reset" : "Reset password"}

      </button>

      {open && (

        <div style={{ marginTop: 8, display: "flex", gap: 8, alignItems: "center" }}>

          <input

            type="password"

            placeholder="New password (min 8 characters)"

            required

            minLength={8}

            value={newPassword}

            onChange={(e) => setNewPassword(e.target.value)}

            style={inputStyle}

          />

          <button

            onClick={async () => {

              const err = await onReset(newPassword);

              if (err) {

                showToast(err, "error");

              } else {

                showToast("Password reset.");

                setNewPassword("");

              }

            }}

          >

            Set password

          </button>

        </div>

      )}

    </div>

  );

}



// One permission's row in the Account Management UI — a checkbox to

// grant/revoke it entirely, plus View/View & Edit radio buttons shown

// only once granted and only for permissions with a real view/edit

// split (perm.supportsEditLevel — see the backend's own

// PERMISSION_LABELS for which ones and why). scopeId disambiguates

// the radio button `name` attributes between different admins'

// permission lists rendered on the same page at once (and "new" for

// the create-admin form) — without it, every admin's "View" radio for

// the same permission would share one native radio group and fight

// each other.

function PermissionRow({ perm, scopeId, granted, editLevel, onToggleGranted, onSetEditLevel, disabled }) {

  return (

    <div style={{ marginBottom: 8 }}>

      <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 14 }}>

        <input type="checkbox" checked={granted} disabled={disabled} onChange={(e) => onToggleGranted(e.target.checked)} />

        {perm.label}

        {perm.description && <span style={{ opacity: 0.6, fontSize: 12 }}> — {perm.description}</span>}

      </label>

      {granted && perm.supportsEditLevel && (

        <div style={{ display: "flex", gap: 16, marginLeft: 24, marginTop: 4, fontSize: 13, opacity: 0.85 }}>

          <label style={{ display: "flex", alignItems: "center", gap: 4 }}>

            <input

              type="radio"

              name={`perm-level-${scopeId}-${perm.key}`}

              checked={!editLevel}

              disabled={disabled}

              onChange={() => onSetEditLevel(false)}

            />

            View

          </label>

          <label style={{ display: "flex", alignItems: "center", gap: 4 }}>

            <input

              type="radio"

              name={`perm-level-${scopeId}-${perm.key}`}

              checked={editLevel}

              disabled={disabled}

              onChange={() => onSetEditLevel(true)}

            />

            View & Edit

          </label>

        </div>

      )}

    </div>

  );

}



function AdminAccountsPanel({ authHeaders, modeStatus }) {

  const token = authHeaders["x-auth-token"];

  const [admins, setAdmins] = useState(null);

  const [availablePermissions, setAvailablePermissions] = useState([]);

  const [newAdmin, setNewAdmin] = useState({ email: "", password: "", permissions: [], editPermissions: [] });

  const [newAdminConfirmPassword, setNewAdminConfirmPassword] = useState("");

  const [newAdminError, setNewAdminError] = useState(null);

  const newAdminConfirmPasswordRef = useRef(null);

  useEffect(() => {

    if (newAdminConfirmPasswordRef.current) {

      newAdminConfirmPasswordRef.current.setCustomValidity(

        newAdminConfirmPassword && newAdminConfirmPassword !== newAdmin.password ? "Passwords don't match." : ""

      );

    }

  }, [newAdmin.password, newAdminConfirmPassword]);

  const [busy, setBusy] = useState(false);

  const { confirm, confirmModal } = useConfirm();



  const load = () => {

    wsRequest("admin.admins.list", {}, token).then(setAdmins).catch(() => {});

    wsRequest("admin.availablePermissions.list", {}, token).then(setAvailablePermissions).catch(() => {});

  };



  useEffect(load, []);



  // Real-time refresh — another root session creating/removing an admin

  // account, or changing its permissions, happens independently of this

  // browser tab.

  useEffect(() => onBroadcast((message) => {

    if (message.type === "users-changed") load();

  }), []);



  const inputStyle = {

    width: "100%",

    padding: 8,

    marginTop: 4,

    marginBottom: 10,

    background: "var(--bg-input)",

    border: "1px solid var(--border-color-light)",

    borderRadius: 6,

    color: "var(--text-primary)",

  };



  // Creation form: granting a permission defaults to View-only for a

  // splittable one (safest, least-privilege default — root upgrades

  // to edit explicitly if needed) or full access for a non-splittable

  // one (no view-only concept for those, so there's no reason to

  // withhold it). Revoking removes from both arrays — edit access

  // without view access doesn't mean anything.

  const toggleNewAdminPermissionGranted = (perm, granted) => {

    setNewAdmin((prev) => ({

      ...prev,

      permissions: granted ? [...prev.permissions, perm.key] : prev.permissions.filter((p) => p !== perm.key),

      editPermissions: granted

        ? perm.supportsEditLevel

          ? prev.editPermissions

          : [...prev.editPermissions, perm.key]

        : prev.editPermissions.filter((p) => p !== perm.key),

    }));

  };

  const setNewAdminEditLevel = (perm, editLevel) => {

    setNewAdmin((prev) => ({

      ...prev,

      editPermissions: editLevel ? [...prev.editPermissions, perm.key] : prev.editPermissions.filter((p) => p !== perm.key),

    }));

  };



  const createAdmin = async (e) => {

    e.preventDefault();

    setNewAdminError(null);

    if (newAdmin.password !== newAdminConfirmPassword) {

      setNewAdminError("Passwords don't match.");

      return;

    }

    setBusy(true);

    try {

      await wsRequest("admin.admins.create", newAdmin, token);

      setNewAdmin({ email: "", password: "", permissions: [], editPermissions: [] });

      setNewAdminConfirmPassword("");

      showToast("Admin account created.");

      load();

    } catch (err) {

      showToast(err.message || "Could not reach backend.", "error");

    }

    setBusy(false);

  };



  const updatePermissions = async (adminId, permissions, editPermissions) => {

    try {

      await wsRequest("admin.admins.editPermissions", { id: adminId, permissions, editPermissions }, token);

      showToast("Saved.");

    } catch (err) {

      showToast(err.message || "Could not reach backend.", "error");

    }

    load();

  };



  // Root's recovery path for an admin/root account locked out of its

  // own mandatory 2FA (lost phone and backup codes both) — see

  // auth.totp.disable's own reasoning for why that account can't just

  // turn it off itself. Whoever this is done for will be walked

  // through mandatory setup again the next time they load the admin

  // panel, same as any account that's never set it up at all.

  const forceDisableTotp = async (adminId, email) => {

    if (!(await confirm(`Force-disable two-factor authentication for "${email}"? They'll be required to set it up again next time they log in.`))) return;

    try {

      await wsRequest("admin.totp.forceDisable", { userId: adminId }, token);

      showToast("Two-factor authentication force-disabled.");

    } catch (err) {

      showToast(err.message || "Could not reach backend.", "error");

    }

    load();

  };



  // Existing-admin editing: same defaulting/revoking reasoning as the

  // creation form above, applied against that admin's own current

  // permissions/editPermissions instead of local form state.

  const toggleAdminPermissionGranted = (admin, perm, granted) => {

    const permissions = granted ? [...admin.permissions, perm.key] : admin.permissions.filter((p) => p !== perm.key);

    const editPermissions = granted

      ? perm.supportsEditLevel

        ? admin.editPermissions

        : [...admin.editPermissions, perm.key]

      : admin.editPermissions.filter((p) => p !== perm.key);

    updatePermissions(admin.id, permissions, editPermissions);

  };

  const setAdminEditLevel = (admin, perm, editLevel) => {

    const editPermissions = editLevel ? [...admin.editPermissions, perm.key] : admin.editPermissions.filter((p) => p !== perm.key);

    updatePermissions(admin.id, admin.permissions, editPermissions);

  };



  const removeAdmin = async (adminId, email, mode) => {

    if (!(await confirm(`Delete the ${modeLabel(mode)} admin account for "${email}"? This can't be undone.`))) return;

    try {

      await wsRequest("admin.admins.remove", { id: adminId }, token);

      showToast(`${modeLabel(mode)} admin account removed.`);

    } catch (err) {

      showToast(err.message || "Could not reach backend.", "error");

    }

    load();

  };



  const resetAdminPassword = async (adminId, newPassword) => {

    try {

      await wsRequest("admin.admins.resetPassword", { id: adminId, newPassword }, token);

      return null;

    } catch (err) {

      return err.message || "Could not reach backend.";

    }

  };



  return (

    <section style={{ marginTop: 20, maxWidth: 700 }}>

      <h3>Admin accounts</h3>



      {admins === null ? (

        <p>Loading…</p>

      ) : admins.length === 0 ? (

        <p style={{ opacity: 0.7 }}>No admin accounts yet.</p>

      ) : (

        admins.map((admin) => {

          // Same rule the backend's own assertNotFrozen now enforces.

  // DEMO ONLY. An admin account is configuration — who may operate
  // this site — rather than data belonging to a mode. The server
  // agrees; see assertSettingsEditable.
  const frozen = adminMode(modeStatus) === "demo";

          // Demo admins can only be leftovers — the generator that made
          // them went in 1.294.0 — but while one exists, telling somebody
          // to switch to a mode that does not exist helps nobody.
          const frozenTitle = !frozen
            ? undefined
            : modeOf(admin) === "demo"
              ? "Demo account — a leftover from the removed mock-admin generator"
              : `${modeLabel(modeOf(admin))} account — switch modes to act on it`;

          return (

          <div key={admin.id} style={{ border: "1px solid var(--border-color)", borderRadius: 8, padding: 12, marginBottom: 10, opacity: frozen ? 0.55 : 1 }}>

            <div style={{ display: "flex", justifyContent: "space-between" }}>

              <div>

                <strong>{admin.email}</strong>{" "}

                <span

                  style={{

                    fontSize: 11,

                    padding: "1px 6px",

                    borderRadius: 10,

                    border: `1px solid ${modeColor(modeOf(admin))}`,

                    opacity: 0.85,

                  }}

                >

                  {modeLabel(modeOf(admin))}

                </span>

              </div>

              <div style={{ display: "flex", gap: 8 }}>

                <ResetPasswordButton

                  onReset={(newPassword) => resetAdminPassword(admin.id, newPassword)}

                  disabled={frozen}

                  title={frozenTitle}

                />

                {admin.totpEnabled && (

                  <button disabled={frozen} title={frozenTitle} onClick={() => forceDisableTotp(admin.id, admin.email)}>

                    Force-disable 2FA

                  </button>

                )}

                <button disabled={frozen} title={frozenTitle} onClick={() => removeAdmin(admin.id, admin.email, modeOf(admin))}>

                  Remove

                </button>

              </div>

            </div>

            <div style={{ marginTop: 8 }}>

              {availablePermissions.map((perm) => (

                <PermissionRow

                  key={perm.key}

                  perm={perm}

                  scopeId={admin.id}

                  granted={admin.permissions.includes(perm.key)}

                  editLevel={admin.editPermissions.includes(perm.key)}

                  onToggleGranted={(granted) => toggleAdminPermissionGranted(admin, perm, granted)}

                  onSetEditLevel={(editLevel) => setAdminEditLevel(admin, perm, editLevel)}

                  disabled={frozen}

                />

              ))}

            </div>

          </div>

          );

        })

      )}



      <h4 style={{ marginTop: 20 }}>Create new admin</h4>

      {!(adminMode(modeStatus) === "live") && (

        <p style={{ opacity: 0.7, fontSize: 13, marginBottom: 10 }}>

          Any admin account you create here is REAL — there is no Test or Demo

          equivalent, whichever mode you are browsing in.

        </p>

      )}

      <fieldset

        // A DISABLED FIELDSET DISABLES EVERYTHING INSIDE IT, whatever the
        // controls themselves say — the same trap that made the refund
        // button dead on 14 Sep.
        //
        // Demo only: a new admin account is real whichever mode you are
        // in, so requiring Live protected nothing. The note above says so.
        disabled={adminMode(modeStatus) === "demo"}

        style={{ border: "none", padding: 0, margin: 0, opacity: adminMode(modeStatus) === "demo" ? 0.55 : 1 }}

      >

      <form onSubmit={createAdmin}>

        <label>

          Email

          <input

            type="email"

            required

            value={newAdmin.email}

            onChange={(e) => setNewAdmin({ ...newAdmin, email: e.target.value })}

            style={inputStyle}

          />

        </label>

        <label>

          Password

          <input

            type="password"

            required

            minLength={8}

            value={newAdmin.password}

            onChange={(e) => setNewAdmin({ ...newAdmin, password: e.target.value })}

            style={inputStyle}

          />

        </label>

        <label>

          Confirm password

          <input

            ref={newAdminConfirmPasswordRef}

            type="password"

            required

            minLength={8}

            value={newAdminConfirmPassword}

            onChange={(e) => setNewAdminConfirmPassword(e.target.value)}

            style={inputStyle}

          />

        </label>

        {newAdminError && <div style={{ color: "var(--accent-red)", fontSize: 13, marginBottom: 10 }}>{newAdminError}</div>}

        <div style={{ marginBottom: 10 }}>

          {availablePermissions.map((perm) => (

            <PermissionRow

              key={perm.key}

              perm={perm}

              scopeId="new"

              granted={newAdmin.permissions.includes(perm.key)}

              editLevel={newAdmin.editPermissions.includes(perm.key)}

              onToggleGranted={(granted) => toggleNewAdminPermissionGranted(perm, granted)}

              onSetEditLevel={(editLevel) => setNewAdminEditLevel(perm, editLevel)}

              disabled={busy}

            />

          ))}

        </div>

        <button type="submit" disabled={busy}>

          {busy ? "Creating…" : "Create admin"}

        </button>

      </form>

      </fieldset>

      {confirmModal}

    </section>

  );

}

// QUICK START — a form that writes a partner's first page for them.
//
// Every API field on one screen with a checkbox, plus the widgets, and
// a Generate button producing HTML and JavaScript to copy. Roughly the
// example page at /embed/v1/589comps-example.html, trimmed to what was
// ticked and carrying this site's own base URL.
//
// TICKING HERE CHANGES NOTHING ABOUT THE API. This screen describes
// what to put in a sample; the API settings screen decides what the
// API serves. One screen silently changing another's settings would be
// a surprise, and the distinction is why the disabled fields are shown
// rather than hidden.
//
// AND A FIELD THAT IS OFF IS MARKED, because generated code reading a
// disabled field renders a blank — which looks exactly like a field
// the operator switched off deliberately, with nothing to say which.
// That ambiguity cost me twice in one day building the example page.
//
// Requested 19 Sep 2026.
// THE PARTNER'S SAVED LAYOUT (1.401.0).
//
// Quick Start already asked the operator which sections a partner should
// show, and threw the answer away as a copy-paste snippet. Saved here
// instead, the widget renders it and the replay can rebuild the same
// page — which is the whole reason it exists: "the partner could create
// and save [the template] ... so it's fed through the API, that way we
// know what the template is and the partner just needs to add a div that
// the template hooks up to."
function WidgetLayoutEditor({ token, canEdit, onSaved }) {
  const [state, setState] = useState(null); // { layout, sections, example, default, generatedHtml }
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  // THE TEMPLATE, EDITED AS TEXT (1.403.0). Kept beside the layout
  // rather than inside it: an operator part-way through editing markup
  // has something that does not yet parse, and that must not be what a
  // tick box saves.
  const [template, setTemplate] = useState("");
  // WHAT IS ACTUALLY STORED, kept beside what is in the box so the
  // "your markup is behind" notice can go the moment the box stops
  // holding it — pressing Rewrite answers the notice (1.519.2).
  const [savedHtml, setSavedHtml] = useState("");
  const [useTemplate, setUseTemplate] = useState(false);
  const [templateError, setTemplateError] = useState(null);
  // AND THE MARKUP ITSELF FOLDS AWAY (1.469.0). Asked for on 25 Sep
  // 2026, against a screenshot of the box with the tick box above it
  // ticked: "Show and hide this html textarea when the checkbox is
  // ticked." 1.467.0 answered the other half of that - a template
  // nobody is using has no editor at all. In use, it is fourteen rows
  // of markup sitting between the tick boxes and the Save button, on a
  // screen you come back to for the tick boxes.
  //
  // SHUT ON ARRIVAL, OPEN WHEN YOU HAVE DONE SOMETHING TO IT. Ticking
  // the box opens it, because the tick is a request to see it; so does
  // "Rewrite the markup from the tick boxes", whose only effect is in
  // that box; and so does a refusal, which is about markup nobody can
  // read while it is shut. A tick box above does NOT, even though it
  // rewrites the markup too: that is the thing you came to this screen
  // to use, and the slot count beside the button moves as you use it.
  const [markupOpen, setMarkupOpen] = useState(false);
  // Both places that put a message up go through here, so a refusal can
  // never arrive against a box that is folded away.
  const refuseTemplate = (message) => {
    setTemplateError(message);
    setMarkupOpen(true);
  };
  // THE TEST SITE AND THE LIVE SITE (1.421.0). Asked for on 21 Sep 2026:
  // "checkboxes for 'save to test site' and 'Save to live site' so when
  // the site is live and the partner wants to change the template they
  // can save it to the test site to preview it before saving to the live
  // site." The test site is the partner's test domain.
  //
  // BOTH UNTICKED, EVERY TIME (1.435.0). Asked for on 22 Sep 2026: "Have
  // these two checkboxes unchecked by default, let the user select which
  // sites get the saved template. This will help prevent mistakes." They
  // start unticked, are not pre-ticked on load, and go back to unticked
  // after each save, so no save reaches a site nobody chose. Save stays
  // disabled until at least one is ticked.
  const NO_TARGETS = { test: false, live: false };
  const [targets, setTargets] = useState(NO_TARGETS);
  const [editing, setEditing] = useState("live"); // which site's layout is in the form

  // Puts one site's saved layout into the form.
  const showLayout = (data, which) => {
    // `savedLive` is the live site's stored layout; `layout` is whatever
    // is in the form, so the two are kept apart.
    const saved = which === "test" && data.layoutTest ? data.layoutTest : data.savedLive;
    // NOTHING SAVED YET? Show the example rather than the bare
    // default, so the first thing an operator sees is a page worth
    // having. It is only a suggestion until they press Save —
    // what the widget serves is still the plain default.
    const untouched = which === "live" && JSON.stringify(saved) === JSON.stringify(data.default);
    setState({ ...data, layout: untouched ? data.example : saved, suggested: untouched });
    setUseTemplate(!!saved?.html);
    setTemplate(saved?.html || data.generatedHtml || "");
    setSavedHtml(saved?.html || "");
    setTemplateError(null);
    setMarkupOpen(false);
    setEditing(which);
  };

  const load = () =>
    wsRequest("admin.partner.getLayout", {}, token)
      .then((data) => {
        // A TEST SITE WITH ITS OWN LAYOUT is what somebody part-way
        // through a change is working on, so that is what opens.
        const which = data.layoutTest && data.domains?.separate ? "test" : "live";
        showLayout({ ...data, savedLive: data.layout }, which);
        // Nothing pre-ticked (1.435.0): the operator chooses every time.
        setTargets(NO_TARGETS);
      })
      .catch((err) => setError(err?.message || "Could not load the layout."));
  useEffect(() => {
    load();
  }, [token]);

  const layout = state?.layout;
  const labelFor = (key) => state?.sections?.find((x) => x.key === key)?.label || key;
  const isCard = (key) => state?.sections?.find((x) => x.key === key)?.fixed === false;

  // EVERY CHANGE TO THE TICK BOXES IS REFLECTED IN THE MARKUP, which is
  // what was asked for the day this shipped. The server works out what
  // the new markup is: untouched markup is regenerated whole, and
  // hand-written markup keeps everything but the slot that moved.
  //
  // ONE REQUEST WINS. Clicking three boxes quickly starts three
  // requests, and an older answer arriving late would put back markup
  // for a layout that is no longer on screen.
  const templateSeq = useRef(0);
  const followTemplate = (previous, next) => {
    const seq = (templateSeq.current += 1);
    wsRequest("admin.partner.generateLayoutTemplate", { layout: next, previous, html: template }, token)
      .then((res) => {
        if (seq !== templateSeq.current) return;
        setTemplate(res.html);
        setTemplateError(null);
      })
      .catch(() => {});
  };
  const setLayout = (next) => {
    const previous = layout;
    setState((prev) => ({ ...prev, layout: next }));
    followTemplate(previous, next);
  };
  const toggle = (key) =>
    setLayout({
      ...layout,
      sections: layout.sections.map((x) => (x.key === key ? { ...x, on: !x.on } : x)),
    });
  // ONLY THE CARDS MOVE. Free entry, the picker and the guess form sit
  // where the widget's DOM builder puts them, so offering to reorder
  // them would be offering something that does not happen.
  const move = (key, by) => {
    const cards = layout.sections.filter((x) => isCard(x.key));
    const fixed = layout.sections.filter((x) => !isCard(x.key));
    const at = cards.findIndex((x) => x.key === key);
    const to = at + by;
    if (at < 0 || to < 0 || to >= cards.length) return;
    const next = cards.slice();
    [next[at], next[to]] = [next[to], next[at]];
    setLayout({ ...layout, sections: [...fixed, ...next] });
  };

  const save = async (value) => {
    setBusy(true);
    setTemplateError(null);
    try {
      // THE TEMPLATE IS PART OF THE LAYOUT, so one Save covers both and
      // there is no half-saved state where the tick boxes moved and the
      // markup did not. The server refuses the whole thing if the
      // markup is not one it would serve, which is what the message
      // below says.
      const body = { ...(value || layout), html: useTemplate ? template : null };
      // BOTH SITES when the key has one domain: there is no separate
      // test site to keep apart.
      const to = state.domains?.separate ? targets : { test: true, live: true };
      const res = await wsRequest("admin.partner.setLayout", { layout: body, targets: to }, token);
      const shown = to.live ? res.layout : res.layoutTest;
      // What the form holds is what was just saved; the two stored
      // layouts are refreshed beside it.
      setState((prev) => ({
        ...prev,
        layout: shown,
        layoutTest: res.layoutTest,
        suggested: false,
        ...(to.live ? { savedLive: res.layout } : {}),
      }));
      setUseTemplate(!!shown?.html);
      if (shown?.html) setTemplate(shown.html);
      setEditing(to.live ? "live" : "test");
      // Back to unticked, so the next save is chosen again.
      setTargets(NO_TARGETS);
      showToast(
        to.test && to.live
          ? "Layout saved to both sites. The widget picks it up on its next load."
          : to.test
            ? "Layout saved to the test site only. The live site is unchanged."
            : "Layout saved to the live site. The test site keeps its own layout.",
        "success"
      );
      // Quick Start's summary of the saved layout follows (1.439.0).
      onSaved?.();
    } catch (err) {
      const message = err?.message || "Could not save the layout.";
      if (useTemplate) refuseTemplate(message);
      showToast(message, "error");
    }
    setBusy(false);
  };

  // THE TEST SITE FOLLOWS THE LIVE ONE AGAIN: its own layout is dropped.
  const followLive = async () => {
    setBusy(true);
    try {
      const res = await wsRequest("admin.partner.setLayout", { followLive: true }, token);
      showLayout({ ...state, savedLive: res.layout, layoutTest: null }, "live");
      setTargets(NO_TARGETS);
      showToast("The test site now shows the live site's layout.", "success");
      onSaved?.();
    } catch (err) {
      showToast(err?.message || "Could not change the test site.", "error");
    }
    setBusy(false);
  };

  // WHAT THIS SITE WOULD DRAW for the tick boxes as they stand now —
  // asked of the server so there is one implementation of it, and so
  // the starting point is markup the same Save would accept.
  const regenerate = async () => {
    setBusy(true);
    try {
      // NO html SENT, so this generates from scratch rather than keeping
      // what is in the box — which is the whole point of the button.
      const seq = (templateSeq.current += 1);
      const res = await wsRequest("admin.partner.generateLayoutTemplate", { layout }, token);
      if (seq === templateSeq.current) {
        setTemplate(res.html);
        setTemplateError(null);
        // You pressed the button whose only effect is in that box.
        setMarkupOpen(true);
      }
    } catch (err) {
      refuseTemplate(err?.message || "Could not generate the template.");
    }
    setBusy(false);
  };

  if (error) return <p style={{ color: "var(--accent-red)", fontSize: 13 }}>{error}</p>;
  if (!layout) return <p className="sub">Loading the saved layout…</p>;

  // WHAT A SHUT BOX STILL ANSWERS. Slots are what the markup is for,
  // and the count follows the tick boxes above, so ticking a card while
  // the box is folded away is not a change with nothing to show for it.
  const markupSlots = (template.match(/data-589comps-section=/g) || []).length;
  const markupLines = template ? template.split("\n").length : 0;

  const row = (x, i, cards) => (
    <div
      key={x.key}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 8,
        padding: "4px 0",
        borderBottom: "1px solid var(--border-color-subtle)",
      }}
    >
      <label style={{ display: "flex", gap: 6, alignItems: "center", fontSize: 13, flex: 1 }}>
        <input type="checkbox" checked={x.on} disabled={!canEdit || busy} onChange={() => toggle(x.key)} />
        <span>{labelFor(x.key)}</span>
      </label>
      {cards && (
        <>
          <button type="button" disabled={!canEdit || busy || i === 0} onClick={() => move(x.key, -1)} title="Move up">
            ↑
          </button>
          <button
            type="button"
            disabled={!canEdit || busy || i === cards.length - 1}
            onClick={() => move(x.key, 1)}
            title="Move down"
          >
            ↓
          </button>
        </>
      )}
    </div>
  );

  const fixed = layout.sections.filter((x) => !isCard(x.key));
  const cards = layout.sections.filter((x) => isCard(x.key));

  return (
    <div style={{ border: "1px solid var(--border-color)", borderRadius: 8, padding: 14, marginBottom: 20 }}>
      <h4 style={{ marginTop: 0 }}>The competition widget layout</h4>
      <p style={{ fontSize: 13, opacity: 0.85, marginTop: 0 }}>
        What the partner's page shows, and in what order. Saved here and served at{" "}
        <code>/api/v1/widget-layout</code>, so their page needs one <code>&lt;div&gt;</code> and no section list of its
        own — and a replay of their competition can be rebuilt in the same arrangement.
      </p>
      <p style={{ fontSize: 12.5, opacity: 0.75 }}>
        A partner can save the same thing themselves with their API key (<code>PUT /api/v1/partner/layout</code>).
        Anything passed to <code>mount()</code> still wins, so an existing integration is unaffected.
      </p>

      {state.suggested && (
        <p style={{ fontSize: 12.5, color: "var(--accent-yellow)" }}>
          Nothing has been saved yet, so this is a suggested starting point. Until you press Save the widget uses its
          plain default: the image, the guess form and no cards.
        </p>
      )}

      {state.domains?.separate ? (
        <div
          style={{
            fontSize: 12.5,
            border: "1px solid var(--border-color)",
            borderRadius: 6,
            padding: "8px 10px",
            margin: "0 0 12px",
          }}
        >
          <div>
            The partner has a <strong>test site</strong> (<code>{state.domains.test}</code>) and a{" "}
            <strong>live site</strong> (<code>{state.domains.live}</code>). A layout saved to the test site only is
            drawn there and nowhere else, so it can be looked at before it goes live.
          </div>
          {state.layoutTest ? (
            <div style={{ marginTop: 6 }}>
              <span style={{ color: "var(--accent-yellow)" }}>The test site has its own layout, not yet on the live site.</span>{" "}
              Showing:{" "}
              {/* THE TAB SAYS WHICH ONE IS BEHIND (1.520.0). This panel
                  opens on the TEST site's layout whenever one exists, so
                  a notice that only spoke about the site being edited
                  was invisible to somebody whose LIVE layout was the
                  behind one — reported 28 Sep 2026 as "installed
                  v1.519.2 and don't see the changes there", with the
                  live layout behind and the test layout already current. */}
              <button type="button" disabled={busy || editing === "test"} onClick={() => showLayout(state, "test")}>
                The test site's{state.htmlOutOfDate?.test ? " (markup behind)" : ""}
              </button>{" "}
              <button
                type="button"
                disabled={busy || editing === "live"}
                onClick={() => showLayout(state, "live")}
              >
                The live site's{state.htmlOutOfDate?.live ? " (markup behind)" : ""}
              </button>{" "}
              <button type="button" disabled={!canEdit || busy} onClick={followLive}>
                Make the test site follow the live site again
              </button>
            </div>
          ) : (
            <div style={{ marginTop: 6, opacity: 0.75 }}>Both sites show the same layout at the moment.</div>
          )}
        </div>
      ) : (
        <p style={{ fontSize: 12.5, opacity: 0.75 }}>
          The partner's key has one domain, so there is no separate test site: a save changes what every page shows.
          Give the key a test domain as well to preview a layout there first.
        </p>
      )}

      <ViewOnlyGate canEdit={canEdit} showNotice>
        <strong style={{ fontSize: 13 }}>The competition itself</strong>
        <div style={{ fontSize: 12, opacity: 0.6, margin: "2px 0 6px" }}>
          Always in this order — free entry is a click on the image, so it needs the picker.
        </div>
        {fixed.map((x) => row(x, 0, null))}

        <strong style={{ fontSize: 13, display: "block", marginTop: 12 }}>The cards beside it</strong>
        <div style={{ fontSize: 12, opacity: 0.6, margin: "2px 0 6px" }}>Shown in this order.</div>
        {cards.map((x, i) => row(x, i, cards))}

        <label style={{ display: "flex", gap: 6, alignItems: "center", fontSize: 13, marginTop: 10 }}>
          <input
            type="checkbox"
            checked={layout.infoCardsLayout === "slider"}
            disabled={!canEdit || busy}
            onChange={(e) => setLayout({ ...layout, infoCardsLayout: e.target.checked ? "slider" : "stacked" })}
          />
          <span>Show those cards as a swipeable slider rather than stacked</span>
        </label>

        <hr style={{ margin: "16px 0", border: 0, borderTop: "1px solid var(--border-color-subtle)" }} />

        <strong style={{ fontSize: 13, display: "block" }}>The template</strong>
        <p style={{ fontSize: 12.5, opacity: 0.8, margin: "4px 0 8px" }}>
          The markup both widgets — the live competition and the replay — build on the partner's page, so a
          competition looks the same while it runs and when it is watched back. An element carrying{" "}
          <code>data-589comps-section="…"</code> is a slot: that section is drawn into it and the rest of the markup is
          left alone. Start from the generated version below and change it to suit the partner's own page.
        </p>
        <p style={{ fontSize: 12, opacity: 0.65, margin: "0 0 8px" }}>
          <strong>It follows the tick boxes.</strong> While the markup is still the generated one, changing a tick box
          rewrites it whole. Once you have edited it, a tick box only adds or removes that section's slot and leaves
          everything you wrote alone — the arrows stop moving anything, because in a template the markup decides the
          order.
        </p>
        {/* THE GENERATOR GAINS SLOTS, AND SAVED MARKUP DOES NOT
            (1.519.2). The hero arrived in 1.519.0 and was invisible on a
            site that had installed it, because the markup saved the day
            before had no slot for it and nothing said so. Only shown for
            markup this site generated: markup somebody wrote is theirs,
            and being different from ours is the point of it.

            IT SPEAKS FOR BOTH SITES (1.520.0). It used to speak only
            about the site being edited, and this panel opens on the TEST
            site's whenever one exists — so somebody whose LIVE layout
            was the behind one opened the panel, saw a current test
            layout, and concluded the release had done nothing. */}
        {(() => {
          const other = editing === "live" ? "test" : "live";
          const behindHere = !!state.htmlOutOfDate?.[editing] && template === savedHtml;
          const behindOther = !!state.htmlOutOfDate?.[other];
          if (!behindHere && !behindOther) return null;
          const name = (which) => (which === "live" ? "live" : "test");
          return (
            <p
              data-layout-markup-behind={behindHere ? "here" : "other"}
              style={{
                border: "1px solid var(--accent-amber, #d4a55e)",
                borderRadius: 4,
                padding: "8px 10px",
                margin: "0 0 8px",
                fontSize: 12.5,
              }}
            >
              {behindHere && (
                <>
                  <strong>This markup is the one we generated, but from an earlier version.</strong> A newer one adds
                  slots it does not have &mdash; the hero band over the picture is one &mdash; so those sections have
                  nowhere to be drawn. Press <em>Rewrite the markup from the tick boxes</em> below, then Save layout.
                  Nothing you have written is at risk: this only appears while the markup is still ours.
                </>
              )}
              {behindHere && behindOther && " "}
              {behindOther && (
                <>
                  <strong>The {name(other)} site&rsquo;s markup is {behindHere ? "also " : ""}behind.</strong> Switch to
                  it above and do the same there &mdash; each site keeps its own markup, and this panel opens on the
                  test site&rsquo;s whenever there is one.
                </>
              )}
            </p>
          );
        })()}
        <label style={{ display: "flex", gap: 6, alignItems: "center", fontSize: 13 }}>
          <input
            type="checkbox"
            checked={useTemplate}
            disabled={!canEdit || busy}
            onChange={(e) => {
              setUseTemplate(e.target.checked);
              // The tick is a request to see it (1.469.0).
              if (e.target.checked) setMarkupOpen(true);
            }}
          />
          <span>Use this template instead of the built-in arrangement</span>
        </label>
        <p style={{ fontSize: 12, opacity: 0.65, margin: "4px 0 6px" }}>
          {useTemplate
            ? "While a template is saved, the slots below decide which cards appear and where, in both widgets. A slider comes from the class is-slider in the markup: the card slots inside that element become its slides. The slider tick box writes it into the generated markup; in markup you have edited, add or remove it yourself. Free entry, the picture and the guess form still follow their tick boxes and sit in the image slot unless given one of their own. The tick boxes also generate this markup."
            : "With this off, the tick boxes above decide the arrangement and nothing here is saved."}
        </p>
        {/* THE EDITOR APPEARS WITH THE TICK (1.467.0). Asked for on 23
            Sep 2026. It used to sit there greyed out whether or not a
            template was in use, which is a lot of screen for something
            that is doing nothing - and a greyed-out box of markup reads
            as broken rather than as switched off. */}
        {useTemplate && (
          <>
          {/* SHOWN AND HIDDEN (1.469.0) — the reasoning is beside
              markupOpen. The error sits outside the fold: a message about
              the markup must not be hidden with it. */}
          <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", margin: "2px 0 6px" }}>
            <button
              type="button"
              data-layout-markup={markupOpen ? "shown" : "hidden"}
              aria-expanded={markupOpen}
              aria-controls="layout-template-markup"
              onClick={() => setMarkupOpen((v) => !v)}
            >
              {markupOpen ? "Hide the markup" : "Show the markup"}
            </button>
            <span style={{ fontSize: 12, opacity: 0.7 }}>
              {markupSlots} slot{markupSlots === 1 ? "" : "s"}, {markupLines} line
              {markupLines === 1 ? "" : "s"}
            </span>
          </div>
          {markupOpen && (
          <textarea
            id="layout-template-markup"
            value={template}
            spellCheck={false}
            disabled={!canEdit || busy}
            onChange={(e) => setTemplate(e.target.value)}
            rows={14}
            style={{
              width: "100%",
              fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
              fontSize: 12.5,
              lineHeight: 1.5,
              padding: 8,
              borderRadius: 6,
              border: `1px solid ${templateError ? "var(--accent-red)" : "var(--border-color)"}`,
              background: "var(--bg-input)",
              color: "var(--text-primary)",
            }}
          />
          )}
          {templateError && (
            <p style={{ color: "var(--accent-red)", fontSize: 12.5, margin: "4px 0 0" }}>{templateError}</p>
          )}
          {markupOpen && (
          <p style={{ fontSize: 12, opacity: 0.65, margin: "6px 0 0" }}>
            Allowed: <code>div section article header footer aside h2 h3 h4 p span ul ol li strong em small br hr</code>{" "}
            with <code>class</code>, <code>id</code>, <code>title</code>, <code>data-589comps-section</code> and{" "}
            <code>data-589comps-repeat</code>. Anything else is refused rather than quietly removed, so nothing is saved
            that the partner did not write. Shown here as text and never rendered on this site.
          </p>
          )}
          </>
        )}

        {/* CARD TEMPLATES (1.419.0), asked for on 21 Sep 2026: "Each card
            should be able to be styled by the partner ... each of these
            could be replacer tags". The list below comes from the server,
            which is also what checks a saved template against it. */}
        <div style={{ fontSize: 12.5, marginTop: 12 }}>
          <strong>Styling a card.</strong> A card's slot with markup inside it is that card's own template: both widgets
          draw it with each <code>{"{{name}}"}</code> replaced by the value, as plain text. An empty slot gets the
          built-in card. Latest entries and Recent purchases are lists, so their placeholders go inside one element
          marked <code>data-589comps-repeat="row"</code>, which is drawn once per row. Ticking a card writes its slot
          with every field the API is serving; a value the API is not serving, or a replay cannot know, shows as a
          dash. In a replay the values are as they were at that point in the playback.
          {state.cardFields && (
            <details style={{ marginTop: 6 }}>
              <summary style={{ cursor: "pointer" }}>Placeholders each card can use</summary>
              <ul style={{ margin: "6px 0 0", paddingLeft: 18 }}>
                {Object.entries(state.cardFields).map(([card, spec]) => (
                  <li key={card} style={{ marginBottom: 4 }}>
                    <code>{card}</code> ({spec.title}
                    {spec.items ? ", one per row" : ""}):{" "}
                    {(spec.items || spec.fields).map((f, i) => (
                      <span key={f.key}>
                        {i ? ", " : ""}
                        <code>{`{{${f.key}}}`}</code> {f.label.toLowerCase()}
                      </span>
                    ))}
                  </li>
                ))}
              </ul>
            </details>
          )}
        </div>

        {state.domains?.separate && (
          <div style={{ display: "flex", gap: 16, marginTop: 14, fontSize: 13, flexWrap: "wrap" }}>
            <label style={{ display: "flex", gap: 6, alignItems: "center" }}>
              <input
                type="checkbox"
                checked={targets.test}
                disabled={!canEdit || busy}
                onChange={(e) => setTargets((t) => ({ ...t, test: e.target.checked }))}
              />
              <span>
                Save to test site (<code>{state.domains.test}</code>)
              </span>
            </label>
            <label style={{ display: "flex", gap: 6, alignItems: "center" }}>
              <input
                type="checkbox"
                checked={targets.live}
                disabled={!canEdit || busy}
                onChange={(e) => setTargets((t) => ({ ...t, live: e.target.checked }))}
              />
              <span>
                Save to live site (<code>{state.domains.live}</code>)
              </span>
            </label>
            {!targets.test && !targets.live && (
              <span style={{ opacity: 0.7 }}>Tick the site or sites to save to — nothing is ticked for you.</span>
            )}
          </div>
        )}
        <div style={{ display: "flex", gap: 10, marginTop: 14, flexWrap: "wrap" }}>
          <button
            type="button"
            onClick={() => save()}
            disabled={!canEdit || busy || (state.domains?.separate && !targets.test && !targets.live)}
          >
            {busy ? "Saving…" : "Save layout"}
          </button>
          <button type="button" onClick={regenerate} disabled={!canEdit || busy}>
            Rewrite the markup from the tick boxes
          </button>
          <button type="button" onClick={() => setLayout(state.example)} disabled={!canEdit || busy}>
            Fill in the suggested tick boxes
          </button>
          <button type="button" onClick={() => setLayout(state.default)} disabled={!canEdit || busy}>
            Clear back to the plain default
          </button>
        </div>
        {/* ASKED ON 20 SEP 2026: "what do the 4 buttons at the bottom of
            this section do?" — three of them change what is on screen and
            only one writes anything, which is not something a label can
            carry on its own. */}
        <ul style={{ fontSize: 12, opacity: 0.7, margin: "8px 0 0", paddingLeft: 18 }}>
          <li>
            <strong>Save layout</strong> — the only one that writes anything. Stores the tick boxes, the slider choice
            and, if it is switched on above, the markup, to the site or sites ticked above it. The widget picks it
            up on its next load. Saving to both makes the test site follow the live one again.
          </li>
          <li>
            <strong>Rewrite the markup from the tick boxes</strong> — throws away anything written by hand in the box
            and puts back the markup this site would draw for the tick boxes as they stand.
          </li>
          <li>
            <strong>Fill in the suggested tick boxes</strong> — sets them to the competition plus status, latest entries
            and recent purchases, as a slider. A starting point, not a save.
          </li>
          <li>
            <strong>Clear back to the plain default</strong> — the image and the guess form, no cards. What the widget
            does with no layout saved at all. Also not a save.
          </li>
        </ul>
      </ViewOnlyGate>
    </div>
  );
}

// One group of fields, or the widgets, as a card in Quick Start's grid.
const QUICK_START_CARD = {
  border: "1px solid var(--border-color)",
  borderRadius: 8,
  padding: "10px 12px",
  background: "var(--bg-panel)",
  // Whole cards only, one under the next, in the column layout.
  breakInside: "avoid",
  display: "inline-block",
  width: "100%",
  boxSizing: "border-box",
  marginBottom: 12,
};

// A SECTION YOU OPEN WHEN YOU WANT IT (1.464.0). Asked for on 23 Sep
// 2026: "Make it more clear and legible. Everything looks a a bit
// squashed up and difficult to read and understand."
//
// Three of these, all shut to start with, by decision. The point of the
// note is that a shut section still answers "have I done this?" - a
// heading and a triangle answer nothing, and a partner who cannot tell
// opens all three and is back where they started.
function QuickStartSection({ id, title, blurb, note, open, onToggle, children }) {
  return (
    <section
      data-quick-start-section={id}
      style={{
        border: "1px solid var(--border-color)",
        borderRadius: 10,
        background: "var(--bg-panel)",
        margin: "14px 0",
      }}
    >
      <button
        type="button"
        aria-expanded={open}
        aria-controls={`${id}-body`}
        onClick={onToggle}
        style={{
          display: "flex",
          alignItems: "baseline",
          gap: 10,
          width: "100%",
          textAlign: "left",
          padding: "12px 14px",
          background: "none",
          border: 0,
          borderRadius: 10,
          color: "inherit",
          font: "inherit",
          cursor: "pointer",
        }}
      >
        <span aria-hidden="true" style={{ fontSize: 12, opacity: 0.7, width: 10 }}>
          {open ? "\u25be" : "\u25b8"}
        </span>
        <span style={{ fontSize: 15, fontWeight: 600 }}>{title}</span>
        {note && <span style={{ fontSize: 12.5, opacity: 0.7 }}>{note}</span>}
      </button>
      <p style={{ margin: "-6px 14px 12px 34px", fontSize: 12.5, opacity: 0.8, lineHeight: 1.5 }}>{blurb}</p>
      {open && (
        <div id={`${id}-body`} style={{ padding: "0 14px 16px" }}>
          {children}
        </div>
      )}
    </section>
  );
}
// WHERE A WIDGET'S OPTIONS WENT (1.464.0). They used to sit under the tick
// box that turns the widget on - put there by 1.429.0 after "Selecting
// this widget doesn't give options to select the type of navigation".
// Widget Configuration is their home now, by decision ("options only - the
// widget tick boxes stay outside"), so the tick box says where rather than
// leaving that same question unanswered a second time.
function QuickStartOptionsLink({ onOpen }) {
  return (
    <button
      type="button"
      data-quick-start-options-link=""
      onClick={onOpen}
      style={{
        display: "block",
        margin: "2px 0 8px 22px",
        padding: 0,
        border: 0,
        background: "none",
        color: "var(--accent-blue)",
        font: "inherit",
        fontSize: 12,
        cursor: "pointer",
        textAlign: "left",
      }}
    >
      Options for this widget are in Widget Configuration &darr;
    </button>
  );
}
// "BEFORE YOU START" (1.447.0). Asked on 22 Sep 2026: a checklist "with a
// green tick for items that are complete and a red cross for items
// outstanding". The server decides each tick (admin.partner.
// quickStartChecklist); an outstanding item says where it is fixed.
function QuickStartChecklist({ checklist, onRefresh }) {
  const items = checklist?.items || null;
  const outstanding = items ? items.filter((i) => !i.done).length : null;
  return (
    <div style={{ ...QUICK_START_CARD, display: "block", fontSize: 13, lineHeight: 1.5 }} aria-label="Before you start">
      <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
        <strong>Before you start &mdash; your live site</strong>
        {items && (
          <span style={{ fontSize: 12, opacity: 0.8 }}>
            {outstanding === 0 ? "Everything is in place." : `${outstanding} still to do.`}
          </span>
        )}
        <button type="button" onClick={() => onRefresh()} style={{ fontSize: 12 }}>
          Check again
        </button>
      </div>
      {!items ? (
        <p style={{ margin: "6px 0 0", opacity: 0.7 }}>Checking…</p>
      ) : (
        <ul style={{ listStyle: "none", margin: "6px 0 0", padding: 0 }}>
          {items.map((i) => (
            <li key={i.id} data-done={i.done ? "yes" : "no"} style={{ display: "flex", gap: 8, padding: "2px 0" }}>
              <span
                aria-label={i.done ? "Done" : "Outstanding"}
                style={{ color: i.done ? "var(--accent-green)" : "var(--accent-red)", fontWeight: 700, width: 14, flexShrink: 0 }}
              >
                {i.done ? "✓" : "✗"}
              </span>
              <span>
                {i.label}
                {i.done && i.when ? <span style={{ opacity: 0.7 }}> &mdash; last on {siteDateTime(i.when)}</span> : null}
                {!i.done && <span style={{ opacity: 0.75 }}> &mdash; {i.fix}</span>}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
// THE STEPS, AT THE TOP OF QUICK START (1.442.0).
function QuickStartHowTo() {
  const li = { marginBottom: 6 };
  // CODE SET APART, so a name to type or look for stands out (1.445.0).
  // --bg-input and --accent-yellow read clearly in both themes.
  const code = {
    fontFamily: "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace",
    fontSize: 12,
    padding: "1px 6px",
    borderRadius: 4,
    background: "var(--bg-input)",
    border: "1px solid var(--border-color)",
    color: "var(--accent-yellow)",
    whiteSpace: "nowrap",
  };
  const C = ({ children }) => <code style={code}>{children}</code>;
  // A LINE TO COPY (1.474.0) rather than a name to look for, so it wraps
  // instead of pushing the card sideways - `code` above is nowrap.
  const L = ({ children }) => (
    <code style={{ ...code, whiteSpace: "pre-wrap", display: "block", margin: "3px 0" }}>{children}</code>
  );
  // THIS SITE'S OWN ADDRESS, the same one the generated page is built
  // with, so the script tag below is one a partner can paste.
  const base = typeof window !== "undefined" ? window.location.origin : "https://<provider-domain>";
  return (
    // THE FULL WIDTH (1.445.0): asked for on 22 Sep 2026, "make this span
    // the entire width, removing max width 820px".
    <div style={{ ...QUICK_START_CARD, display: "block", fontSize: 13, lineHeight: 1.6 }} aria-label="How to use Quick Start">
      <strong>How to use this page</strong>
      <ol style={{ margin: "6px 0 0", paddingLeft: 20 }}>
        <li style={li}>
          <strong>Get your API key.</strong> Partners &rarr; Partners API Key: generate a key with your live site&rsquo;s
          domain and, if you have one, your test site&rsquo;s. Copy it when it is shown &mdash; it is shown once.
        </li>
        {/* WHERE THE KEY GOES (1.445.0), asked for as its own step: "the
            preferable way to use it is setting it as an environment
            variable and if that's not possible then set it somewhere in the
            code". The order is the order the session script looks. */}
        <li style={li}>
          <strong>Keep the key on your server, never in a web page.</strong> Best: an environment variable called{" "}
          <C>COMPETITION_API_KEY</C>. If your host doesn&rsquo;t offer environment variables, put it in a file called{" "}
          <C>589comps-config.php</C> (Generate gives you it), in the first of these you can use: the folder above{" "}
          <C>public_html</C>, a folder called <C>private</C> beside the session script (e.g.{" "}
          <C>public_html/private/</C>), or beside the script. The script looks in that order.
        </li>
        <li style={li}>
          <strong>Choose what the API shares.</strong> Partners &rarr; API: switch the public API on and tick the fields
          your page may show. The competition widget needs Competition Details &rarr; Competition ID, Image width and
          Image height, and Competition Image switched on.
        </li>
        <li style={li}>
          <strong>Choose below, then Generate.</strong> Tick the fields and widgets for the page, pick each
          widget&rsquo;s options, and say where each site&rsquo;s server keeps the key. Ticks here only shape the code; they do not
          change what the API shares.
        </li>
        <li style={li}>
          <strong>Copy the code into your site.</strong> The HTML block is a complete page: save it as a page of your
          site (e.g. <C>competition.html</C>), or copy what is inside its <C>&lt;body&gt;</C> into a page you have. The competition is drawn into <C>&lt;div id="comps-competition"&gt;</C>. The session
          script goes on your server where its box says &mdash; its box names the file, because the name follows
          the path you save under &ldquo;Signing visitors in&rdquo; and your two sites may not use the same one &mdash; one for your live site
          and, if your key has a test domain, one for your test site; each works only on its own site; edit its{" "}
          <C>comps_current_user()</C> so it returns your signed-in visitor &mdash; unless you saved how your login
          records them in &ldquo;Your signed-in visitor&rdquo; below, when Quick Start writes it for you. Signed out,
          visitors can watch; signed in, they can play. (Not PHP? The script makes two calls any server language can make &mdash; see the Integration
          guide.)
        </li>
        {/* THE ACCOUNT SECTION ON A PAGE YOU ALREADY HAVE (1.474.0).
            Asked for on 26 Sep 2026: "can you add instructions on how to
            add the account section so the user can add the account
            widget to their page?" The generated page wires all of this
            for them; this step is for the REST of their site, where
            there is no generated page to copy. */}
        <li style={li}>
          <strong>Put the account section on your other pages.</strong> The page above already has it. To add it
          to a page you already have, four things:
          <ol type="a" style={{ margin: "4px 0 6px", paddingLeft: 20 }}>
            <li style={li}>
              <strong>Load the widget</strong>, at the end of the body:
              <L>{`<script src="${base}/embed/v1/589comps-account.min.js"></script>`}</L>
            </li>
            <li style={li}>
              <strong>Mark your own &ldquo;My account&rdquo; link.</strong> It keeps its own text and styling; the
              widget only adds what happens on a click.
              <L>{'<li data-589comps-account="header"><a href="/login">My account</a></li>'}</L>
              <C>header</C> puts each account page in a dropdown and draws it on the page;{" "}
              <C>modal</C> opens it over the page instead. An empty marked element gets the widget&rsquo;s own button.
            </li>
            <li style={li}>
              <strong>Header style needs somewhere to draw</strong> &mdash; put this wherever the account page
              should appear, usually just under your header. Modal style needs nothing.
              <L>{"<section data-589comps-account-target></section>"}</L>
            </li>
            <li style={li}>
              <strong>Start it with the visitor&rsquo;s token</strong>, the same one your session script hands the
              other widgets: <C>Comps589Account.init(&#123;&hellip;&#125;)</C>. Copy that call from the generated
              page &mdash; it already carries this site&rsquo;s address, the colours chosen below and your sign-in.
            </li>
          </ol>
          Which pages it offers, its colours and its menu are under <strong>Widget Configuration</strong>. Tick the
          replay widget and it gains a <strong>Replays</strong> tab, written for you; <strong>Buy credit</strong>,
          ticked in the same place, adds a tab where a visitor tops up their entry credit and comes back to it
          after paying.
        </li>
        <li style={li}>
          <strong>Arrange the competition, further down this page.</strong> Choose its sections and cards, or write
          your own markup, and save it to your test site, your live site or both. Your page picks it up on its next
          load &mdash; no change to its code.
        </li>
      </ol>
    </div>
  );
}
// THE COMPETITION WIDGET'S OPTIONS IN QUICK START (1.439.0).
//
// Asked on 22 Sep 2026: "Can options/features be selectable for the
// competition widget? So the partner has full control over the
// configuration." Each is one of mount()'s options, and each default is
// what the widget does when the option is left out, so the generated code
// with nothing changed behaves as the bare snippet always did.
//
// NOT OFFERED: sliderScrollSpeed. The widget still accepts it and has
// ignored it since 1.413.0, when the slider stopped scrolling itself.
// Scrolling came back in 1.440.0 as sliderCardScroll, under a new name so
// an old embed passing sliderScrollSpeed does not start moving again.
//
// WHICH SECTIONS SHOW, and stacked or slider, are NOT here: they are saved
// to the site by the layout editor, so they can change without the
// partner redeploying. The card shows what is saved and links to it.
// THE STATUS WIDGET'S WORDS (1.539.0), and they are the widget's own.
//
// Asked for on 29 Sep 2026: "There should be operator-level controls
// with our words set as defaults." Kept identical to 589comps-status.js's
// DEFAULTS, and a test reads both files and compares them - two lists
// meant to be the same, maintained apart, do not stay the same.
//
// ONLY WHAT DIFFERS IS EMITTED (see the mount below), so leaving every
// box alone writes none of our wording into a partner's page, which is
// what 1.532.0 took out of it. The control and that rule are not in
// conflict: the operator opts in, word by word.
const STATUS_WIDGET_DEFAULTS = {
  endsLabel: "Ends in",
  nextLabel: "Up next",
  startingText: "any moment now",
  idleText: "Next competition",
  waiting: ["Coming soon", "Please wait", "See below to subscribe for upcoming competitions"],
};

// THE WORDS THE OPERATOR ACTUALLY CHANGED, as lines of the mount call.
//
// Compared against the widget's own defaults rather than written out
// wholesale: an untouched panel emits NOTHING, which is what keeps
// 1.532.0's rule intact now that the words are configurable. An empty
// string is a change like any other - the widget merges with
// `o[j] !== undefined`, so a label cleared on purpose reaches it as ""
// and draws nothing.
//
// The three waiting lines travel together or not at all: the widget
// cycles the array it is given, so handing it one changed line and two
// missing ones would be a shorter cycle, not an edited one.
function statusWordLines(status) {
  const out = [];
  for (const { key } of STATUS_WIDGET_FIELDS) {
    if (status[key] !== STATUS_WIDGET_DEFAULTS[key]) {
      out.push(`  ${key}: ${JSON.stringify(status[key])},`);
    }
  }
  const waiting = status.waiting || STATUS_WIDGET_DEFAULTS.waiting;
  if (waiting.some((line, n) => line !== STATUS_WIDGET_DEFAULTS.waiting[n])) {
    out.push(`  waiting: ${JSON.stringify(waiting)},`);
  }
  return out.length ? out.join("\n") + "\n" : "";
}

// What each box says it is for, in the order they are shown.
const STATUS_WIDGET_FIELDS = [
  { key: "endsLabel", label: "Countdown label, while one is running", hint: 'shown before the clock: "Ends in 4h 12m"' },
  { key: "nextLabel", label: "Countdown label, before one starts", hint: '"Up next 2h 30m"' },
  { key: "startingText", label: "When the clock reaches zero", hint: "the words that replace the countdown" },
  { key: "idleText", label: "Status word while none is running", hint: "beside the light, where the title would be" },
];

const COMPETITION_WIDGET_DEFAULTS = {
  theme: "light",
  pollIntervalMs: 1000,
  // THE CARD SLIDER MOVING BY ITSELF (1.440.0): off, as the widget is.
  sliderAdvanceSeconds: 0,
  sliderCardScroll: "off",
  loginHandler: true,
  showSimulationEndedNotice: false,
  // THE WIDGET'S OWN CLOCKS (1.548.0): off, as the widget is, and for
  // the reason the widget is - the status widget counts the same two
  // moments on markup the partner wrote, and this page always has it.
  countdown: false,
  hooks: false,
};
function competitionMountCode(global, elementId, base, o) {
  return [
    `${global}.mount(document.getElementById(${JSON.stringify(elementId)}), {`,
    `  baseUrl: ${JSON.stringify(base)},`,
    `  token: sessionToken, // from your server's vouch; leave out while signed out`,
    `  // The page token your server minted. Every request this page makes`,
    `  // to the competition API carries it, and the widgets renew it`,
    `  // between them before it lapses.`,
    `  pageToken: comps589PageToken,`,
    `  pageTokenExpiresIn: comps589PageTokenExpiresIn,`,
    `  // "light", "dark", or "none": no colours of ours - style the rcw- classes`,
    `  // or set the --rcw-* variables in your own CSS.`,
    `  theme: ${JSON.stringify(o.theme)},`,
    `  pollIntervalMs: ${Number(o.pollIntervalMs) || 1000}, // how often it checks for changes`,
    `  // The card slider, when the saved layout uses one. Both pause while the`,
    `  // visitor is using it.`,
    `  sliderAdvanceSeconds: ${Number(o.sliderAdvanceSeconds) || 0}, // seconds per card; 0 = only when the visitor moves it`,
    `  sliderCardScroll: ${JSON.stringify(o.sliderCardScroll || "off")}, // a long card scrolls itself: "off", "slow", "medium" or "fast"`,
    ...(o.showSimulationEndedNotice
      ? [`  showSimulationEndedNotice: true, // a demo's last result says the demo has ended`]
      : []),
    ...(o.countdown
      ? [
          `  // "Ends in 4h 12m" while one runs, "Up next 2m 27s" before the next`,
          `  // one starts. Style .rcw-countdown-label and .rcw-countdown-value, or`,
          `  // pass { next: false } / { ends: false } / your own labels.`,
          `  countdown: true,`,
        ]
      : []),
    ...(o.loginHandler
      ? [
          `  // Signed out, taking part calls this: your sign-in page, as saved in`,
          `  // Quick Start (1.457.0).`,
          `  onLoginRequested: comps589RequestSignIn,`,
        ]
      : []),
    ...(o.hooks
      ? [
          `  onPurchase: (result) => {}, // pixels bought, or claimed with free entry`,
          `  onGuess: (result) => {}, // a guess answered; result.correct says how it went`,
        ]
      : []),
    `});`,
  ].join("\n");
}
// One line per site for the saved layout: which sections, and how the
// cards are laid out. Null until the layout has been read.
function describeSavedLayout(layout, sections) {
  if (!layout) return null;
  const labelOf = (key) => (sections || []).find((x) => x.key === key)?.label || key;
  const isCard = (key) => (sections || []).find((x) => x.key === key)?.fixed === false;
  const on = (layout.sections || []).filter((x) => x.on);
  const main = on.filter((x) => !isCard(x.key)).map((x) => labelOf(x.key));
  const cards = on.filter((x) => isCard(x.key)).map((x) => labelOf(x.key));
  const parts = [main.length ? main.join(", ") : "No picture or guess form"];
  parts.push(
    cards.length
      ? `cards: ${cards.join(", ")} (${layout.infoCardsLayout === "slider" ? "as a slider" : "stacked"})`
      : "no cards"
  );
  if (layout.html) parts.push("in your own markup");
  return parts.join("; ");
}
function describeSavedLayouts(data) {
  if (!data) return null;
  const live = describeSavedLayout(data.layout, data.sections);
  const test = data.layoutTest ? describeSavedLayout(data.layoutTest, data.sections) : null;
  return test ? [{ site: "Live site", text: live }, { site: "Test site", text: test }] : [{ site: null, text: live }];
}
// SIGNING VISITORS IN, FROM QUICK START (1.442.0).
//
// Asked on 22 Sep 2026: a partner whose host gives no environment variables
// (589compstest.com is one) must be able to use this page too, and "however
// the API key is used the javascript created on this page must still work".
//
// So the key never touches the page's code. The page asks the partner's
// own server for a session token (sessionFetchCode); a small script on
// that server holds the key, vouches for the signed-in visitor and hands
// back the token (sessionScriptPhp). The script reads the key from an
// environment variable when there is one, otherwise from a config file
// (configFilePhp) - above public_html by preference - so the same page
// code works either way. The pattern is 589compstest.com's session.php.
// An environment variable first, as the steps recommend (1.445.0).
//
// ONE CARD PER SITE ON THE KEY (1.447.0). Asked on 22 Sep 2026: "We have a
// test and live associated with the API key so we need two of these
// cards. So a "Live" card and a "Test" card also show the domains
// associated with the API key if they exist." Each site's server may keep
// the key differently (a host without environment variables for one), so
// the choice is per site.
//
// THE SCRIPT'S ADDRESS PER SITE TOO (1.448.0). Asked on 22 Sep 2026: it
// "should be on both "Signing visitors in" cards as we're assuming that live
// and test environments are not identical". The page stays one page: when
// the addresses differ, its code picks the one for the site it is on
// (sessionFetchCode), by the same rule as the scripts' host check.
const QUICK_START_SESSION_PATH = "/comps-session.php";
// What the page is called before the site's own name arrives (1.461.6).
const QUICK_START_DEFAULT_TITLE = "Competition";
// THE PAGE IS index.html (1.461.10). Asked on 23 Sep 2026: "instead of
// naming the html files 589-comps.html can they simply be called
// index.html ... because I want it to work when I load the domain in the
// browser." So it is, everywhere - the card says it, the zip packs it and
// a publish uploads it under that name, which is also the name a browser
// asks for when it is given a domain and nothing else.
//
// WHAT THAT MEANS ON A SITE THAT ALREADY HAS ONE: uploading replaces the
// home page. The card says so rather than leaving it to be discovered.
const QUICK_START_PAGE_FILE = "index.html";
// THE LOOK, IN A FILE OF ITS OWN (1.475.0). Asked for on 26 Sep 2026,
// after a discussion about whether a replay keeps its historical
// styling: "maybe it would be an idea to have a 589comps.css file
// specifically for styling the competitions."
//
// It was inline in the page's <head> until now, which meant a partner
// who changed it lost the change the next time the page was generated,
// and a second page of theirs - the account section on their own pages,
// say - had no styling at all. As a file it is linked, shared and
// overridable.
const QUICK_START_CSS_FILE = "589comps.css";
// WHAT THE BROWSER CALLS THE DOWNLOAD (1.461.12). The zip arrives as a
// blob and is saved by an <a download="...">, and THAT name is the one
// the person sees - a Content-Disposition from the server is never
// consulted for it. 1.461.9 put the version in the header and pinned the
// header in a test: true, and beside the point. Reported the same night,
// with Dark chosen and a file called 589comps-test-site-files.zip.
const QUICK_START_ZIP_SUFFIX = { light: "-light", dark: "-dark", none: "-unstyled" };
// The three templates by the names the tabs give them (1.464.0), so the
// preview and the "Use this one" line say the same words as the tab.
const QUICK_START_STYLE_LABELS = { none: "No styling", light: "Light", dark: "Dark" };
// THE ONE GROUP THAT GETS NO CARD (1.533.0).
//
// 1.531.0 cut the example page to two cards, on a request naming eight
// to remove. Asked on 28 Sep 2026, once the winners table stopped being
// a page of its own: "If I can remove these from the page using the
// checkboxes on the quick start page can you put the following cards
// back on the quick start templates" - and then the same eight.
//
// WHICH IS THE BETTER ANSWER, and it is the tick boxes that make it one.
// A partner who wants a page of two cards unticks the rest; a partner
// who wants all eleven has them. 1.531.0's allow-list decided for
// everybody, and it also meant a field group added to the API tomorrow
// could never appear at all.
//
// A DENY-LIST OF ONE, THEREFORE, and the one is Demo Session: a single
// flag, null outside demo mode, so on a live page its card is a heading
// with nothing under it. It was not in either list the request named;
// it was taken out with the eight on 28 Sep and stays out.
const QUICK_START_NO_CARD = ["demo-session"];
// THE WINNERS LIST'S ROW KEYS, BY THE FIELD KEY THE TICK BOXES USE
// (1.465.0). The registry names its fields in snake_case and
// buildWinnersList writes the rows in camelCase; this is the one place
// that knows both, so a generated page reads the rows it is actually
// sent. Kept in step with the winners block of API_FIELDS in server.js -
// quick-start-page-wiring.test.mjs fails if the two drift.
const QUICK_START_WINNER_KEYS = {
  competition_id: "competitionId",
  title: "title",
  winner: "winner",
  won_at: "wonAt",
  winning_entry: "winningEntry",
  prize: "prize",
  currency: "currency",
  currency_symbol: "currencySymbol",
  won_by_lottery: "wonByLottery",
  has_replay: "hasReplay",
};
function quickStartZipName(site, pageStyle) {
  return `589comps-${site}-site-files${QUICK_START_ZIP_SUFFIX[pageStyle] || ""}.zip`;
}
const QUICK_START_SIGN_IN_DEFAULTS = {
  keyWhere: { live: "env", test: "env" },
  path: { live: QUICK_START_SESSION_PATH, test: QUICK_START_SESSION_PATH },
  // WHERE THE PARTNER'S OWN SIGN-IN LIVES (1.457.0), per site, and which
  // of the three ways that site signs a visitor in (1.458.0).
  signInUrl: { live: "", test: "" },
  signInKind: { live: "own", test: "own" },
};
// The key's two domains, as the layout editor reads them (admin.partner.
// getLayout). A key with one domain gives the same for both: then there is
// no separate test site, and nothing to generate for one. A domain is
// written into PHP, so anything but a plain host name is dropped (the
// server stores them normalised - normalisePartnerDomain - this is belt and
// braces).
function quickStartSites(domains) {
  const clean = (d) => (typeof d === "string" && /^[a-z0-9.-]+\.[a-z]{2,}$/.test(d) ? d : null);
  const live = clean(domains?.live);
  const test = clean(domains?.test);
  return [
    { site: "live", label: "Live", domain: live, separate: true },
    { site: "test", label: "Test", domain: test, separate: !!test && test !== live },
  ];
}
// `test` is the test site's { domain, path } when the key has a separate
// test site whose script lives at a different address; the page then asks
// whichever script belongs to the site it is running on.
// AN EMPTY ADDRESS MEANS THE PAGE DOES NOT ASK THIS SITE FOR A SESSION
// (1.454.0, worded plainly in 1.455.0). Asked on 22 Sep 2026, after "I
// can't remove "/comps-session.php" from the input box": an empty box stays
// empty and nothing is generated for that site.
//
// WHAT IT DOES NOT MEAN. 1.454.0's wording said its visitors could only
// watch - "This isn't necessarily true. The current live site works
// different and doesn't even use PHP". A partner's site may sign visitors
// in its own way entirely, so this says what the generated page does and
// nothing about the site.
// THE SIGN-IN HANDLER THE WIDGETS CALL WHEN A SIGNED-OUT VISITOR ASKS FOR
// SOMETHING (1.457.0). Reported 22 Sep 2026: with the option ticked, "My
// account" did nothing - the generated handler was `() => null`, which
// takes the click and goes nowhere in silence. With a sign-in page saved it
// sends them there and comes back; without one it says why in the console.
// SIGNING A VISITOR IN, FROM THE PAGE (1.457.0). Reported 22 Sep 2026:
// with "Send signed-out visitors to your sign-in" ticked, "My account" did
// nothing - the generated handler was `() => null`, a function that takes
// the click and goes nowhere in silence. The page now resolves the sign-in
// page for the site it is on (they may differ, like the script address) and
// both widgets call the same handler.
// WHAT HAPPENS WHEN A SIGNED-OUT VISITOR ASKS TO TAKE PART (1.457.0, with
// a sign-in of its own since 1.458.0). Each site chooses one of three:
//
//   "page" - go to the partner's own sign-in, and come back;
//   "form" - the sign-in Quick Start wrote, on this page, with the
//            accounts in comps-accounts.php;
//   "own"  - nothing generated; the partner writes the handler.
//
// The page serves both sites, so the choice is resolved from the host it is
// running on, exactly as the session script's address is.
function signInPlanCode(live, test) {
  const js = (v) => JSON.stringify(v).replace(/</g, "\\u003c");
  const plan = (x) =>
    x.kind === "page" && x.url
      ? `{ kind: "page", url: ${js(x.url)} }`
      : x.kind === "form" && x.auth
        ? `{ kind: "form", auth: ${js(x.auth)} }`
        : `{ kind: "own" }`;
  const same = !test || plan(test.plan) === plan(live);
  return same
    ? [`var comps589SignIn = ${plan(live)};`]
    : [
        `var comps589SignInHost = location.hostname.toLowerCase();`,
        `var comps589SignIn =`,
        `  comps589SignInHost === ${js(test.domain)} || comps589SignInHost.endsWith(${js("." + test.domain)})`,
        `    ? ${plan(test.plan)}`,
        `    : ${plan(live)};`,
      ];
}
// WHETHER THERE IS ANYWHERE TO SEND A SIGNED-OUT VISITOR (1.461.5).
// Reported 22 Sep 2026: "clicking "My Account" doesn't bring up the user
// account. I thought this was something that was fixed earlier". It was,
// for a site that has told Quick Start where its sign-in is. For a site
// that has not, the generated page still installed a handler - and the
// account widget takes the click whenever a handler exists, so the click
// was swallowed, the console got a warning nobody was looking at, and the
// partner's own "My account" link did not even follow its href.
function signInPlanWorks(plan) {
  return (plan.kind === "page" && !!plan.url) || (plan.kind === "form" && !!plan.auth);
}
function signInCanHappen(live, test) {
  return signInPlanWorks(live) || !!(test && signInPlanWorks(test.plan));
}
function signInCode(live, test) {
  const usesForm = live.kind === "form" || (test && test.plan.kind === "form");
  return [
    `// Signed out, the widgets call this when the visitor asks to take part.`,
    ...signInPlanCode(live, test),
    ...(usesForm ? comps589FormCode() : []),
    `function comps589RequestSignIn() {`,
    `  if (comps589SignIn.kind === "page") {`,
    `    // Your sign-in, then back to this page; on the way back the page asks`,
    `    // your server for the session again. Rename "next" if your sign-in`,
    `    // takes the return address under another name.`,
    `    var back = encodeURIComponent(location.pathname + location.search);`,
    `    location.href = comps589SignIn.url + (comps589SignIn.url.indexOf("?") >= 0 ? "&" : "?") + "next=" + back;`,
    `    return null; // the page is leaving, so there is nothing to hand back`,
    `  }`,
    ...(usesForm
      ? [
          `  if (comps589SignIn.kind === "form") return comps589ShowSignIn();`,
        ]
      : []),
    `  // Nothing generated for this site: send the visitor to your sign-in`,
    `  // here and return the session token your server gives you. Returning`,
    `  // nothing leaves them signed out.`,
    `  console.warn("589comps: no sign-in page set, so the visitor stays signed out.");`,
    `  return null;`,
    `}`,
  ];
}
// THE FORM'S OWN CODE, when a site uses the sign-in Quick Start wrote.
function comps589FormCode() {
  return [
    ``,
    `// The sign-in Quick Start wrote (comps-auth.php). It shows the form,`,
    `// and hands the widgets the new session token once somebody signs in.`,
    `var comps589Waiting = null;`,
    `function comps589El(id) {`,
    `  return document.getElementById(id);`,
    `}`,
    `function comps589ShowSignIn() {`,
    `  var box = comps589El("comps-signin");`,
    `  if (!box) return null;`,
    `  box.hidden = false;`,
    `  box.scrollIntoView({ behavior: "smooth", block: "center" });`,
    `  var first = box.querySelector("input");`,
    `  if (first) first.focus();`,
    `  // One promise for however many times they are asked: the widgets get`,
    `  // the token the moment the form succeeds.`,
    `  if (!comps589Waiting) {`,
    `    comps589Waiting = new Promise(function (resolve) {`,
    `      comps589Waiting_resolve = resolve;`,
    `    });`,
    `  }`,
    `  return comps589Waiting;`,
    `}`,
    `var comps589Waiting_resolve = null;`,
    `function comps589AuthPost(body) {`,
    `  return fetch(comps589SignIn.auth, {`,
    `    method: "POST",`,
    `    credentials: "same-origin",`,
    `    headers: { "Content-Type": "application/json" },`,
    `    body: JSON.stringify(body),`,
    `  }).then(function (res) {`,
    `    return res.json().catch(function () { return {}; }).then(function (data) {`,
    `      if (!res.ok) throw new Error(data.error || "That did not work.");`,
    `      return data;`,
    `    });`,
    `  });`,
    `}`,
    `function comps589SignInError(message) {`,
    `  var box = comps589El("comps-signin-error");`,
    `  if (!box) return;`,
    `  box.textContent = message || "";`,
    `  box.hidden = !message;`,
    `}`,
    `function comps589SignedIn() {`,
    `  // Signed in here; now ask for the competition session it allows.`,
    `  return comps589Session().then(function (token) {`,
    `    var box = comps589El("comps-signin");`,
    `    if (box) box.hidden = true;`,
    `    var out = comps589El("comps-signout");`,
    `    if (out) out.hidden = false;`,
    `    // THE WIDGETS LEARN ABOUT IT (1.465.0). Resolving the promise`,
    `    // below only reaches whoever asked for a sign-in; a visitor who`,
    `    // used the form without being asked - the ordinary case - left`,
    `    // every widget still holding the token from page load, which was`,
    `    // none. This tells all of them, asked or not.`,
    `    comps589HandOver(token);`,
    `    if (comps589Waiting_resolve) comps589Waiting_resolve(token);`,
    `    comps589Waiting = null;`,
    `    comps589Waiting_resolve = null;`,
    `    return token;`,
    `  });`,
    `}`,
    `function comps589WireSignIn() {`,
    `  if (!comps589SignIn || comps589SignIn.kind !== "form") return;`,
    `  var forms = [`,
    `    { id: "comps-signin-form", action: "signin" },`,
    `    { id: "comps-signup-form", action: "signup" },`,
    `  ];`,
    `  forms.forEach(function (f) {`,
    `    var el = comps589El(f.id);`,
    `    if (!el) return;`,
    `    el.addEventListener("submit", function (e) {`,
    `      e.preventDefault();`,
    `      comps589SignInError("");`,
    `      var data = new FormData(el);`,
    `      comps589AuthPost({`,
    `        action: f.action,`,
    `        name: data.get("name") || "",`,
    `        email: data.get("email") || "",`,
    `        password: data.get("password") || "",`,
    `      })`,
    `        .then(comps589SignedIn)`,
    `        .catch(function (err) { comps589SignInError(err.message); });`,
    `    });`,
    `  });`,
    `  var out = comps589El("comps-signout");`,
    `  if (out) {`,
    `    out.addEventListener("click", function () {`,
    `      // THE PAGE ANSWERS FIRST, THEN RELOADS (1.465.0). It used to do`,
    `      // nothing but reload, so a signout that failed on the server,`,
    `      // or a reload that came back looking identical, both read as a`,
    `      // button that does nothing. Now the button empties the widgets`,
    `      // and hides itself as soon as it is pressed, and the reload`,
    `      // just confirms it.`,
    `      out.disabled = true;`,
    `      comps589HandOver(null);`,
    `      out.hidden = true;`,
    `      comps589AuthPost({ action: "signout" })`,
    `        .then(function () { location.reload(); })`,
    `        .catch(function () { location.reload(); });`,
    `    });`,
    `  }`,
    `}`,
  ];
}
function sessionFetchCode(path, test) {
  // The addresses are typed in Quick Start and go into a <script> in the
  // page: as JS strings with "<" escaped, so none can end the element, and
  // shown in comments without < > (1.448.0).
  const js = (v) => JSON.stringify(v).replace(/</g, "\\u003c");
  const shown = (v) => String(v).replace(/[<>]/g, "");
  const testPath = test && test.domain ? test.path || "" : null;
  const split = testPath !== null && testPath !== path;
  // Neither site gave an address: the page never asks for a session token,
  // and the widgets start without one.
  if (!split && !path) {
    return [
    `// ---- THE PAGE TOKEN, AND THE ONE PLACE IT IS ADDED (1.880.0) ----`,
    `//`,
    `// Your server mints this with the partner credential it holds - which`,
    `// never reaches a browser - and every request this page makes to the`,
    `// competition API carries it. The widgets renew it between them.`,
    `//`,
    `// ADDED HERE AND NOT AT EACH CALL. This page makes several of its own`,
    `// API calls besides the widgets - your replays, your credit balance, a`,
    `// top-up and the purchase status poll - and one of them forgetting the`,
    `// header is a request refused in the middle of a purchase.`,
    `var comps589PageToken = null;`,
    `var comps589PageTokenExpiresIn = null;`,
    `function comps589ApiHeaders(extra) {`,
    `  var h = extra || {};`,
    `  if (comps589PageToken) h["x-partner-page-token"] = comps589PageToken;`,
    `  return h;`,
    `}`,
    ``,
      `// No session address was given, so this page does not ask for a`,
      `// session token. The widgets start without one.`,
      `//`,
      `// AND NO PAGE TOKEN EITHER, which is worth knowing: minting one needs`,
      `// your API key, so it needs a script on your server. Until this page`,
      `// has one it can only show DEMO competitions, through the demo token.`,
      `// Give Quick Start an address under "Signing visitors in" and the`,
      `// script it generates mints the token as well as signing people in.`,
      `async function comps589Session() {`,
      `  return null;`,
      `}`,
    ];
  }
  const describe = (label, value) => `//   ${label}: ${value ? shown(value) : "no address given - this page does not ask for a session there"}`;
  return [
    `// ---- THE PAGE TOKEN, AND THE ONE PLACE IT IS ADDED (1.880.0) ----`,
    `//`,
    `// Your server mints this with the partner credential it holds - which`,
    `// never reaches a browser - and every request this page makes to the`,
    `// competition API carries it. The widgets renew it between them.`,
    `//`,
    `// ADDED HERE AND NOT AT EACH CALL. This page makes several of its own`,
    `// API calls besides the widgets - your replays, your credit balance, a`,
    `// top-up and the purchase status poll - and one of them forgetting the`,
    `// header is a request refused in the middle of a purchase.`,
    `var comps589PageToken = null;`,
    `var comps589PageTokenExpiresIn = null;`,
    `function comps589ApiHeaders(extra) {`,
    `  var h = extra || {};`,
    `  if (comps589PageToken) h["x-partner-page-token"] = comps589PageToken;`,
    `  return h;`,
    `}`,
    ``,
    ...(split
      ? [
          `// Each of your sites is served its own way:`,
          describe(`test site (${test.domain}, or under it)`, testPath),
          describe("live site", path),
          `const comps589Host = location.hostname.toLowerCase();`,
          `const comps589SessionPath =`,
          `  comps589Host === ${js(test.domain)} || comps589Host.endsWith(${js("." + test.domain)})`,
          `    ? ${js(testPath || null)}`,
          `    : ${js(path || null)};`,
          ``,
        ]
      : []),
    `// The visitor's session, from your own server (${split ? "this site's script, chosen above" : `${shown(path)}, below`}).`,
    `// null while nobody is signed in: the widgets then show the competition`,
    `// to watch and ask the visitor to sign in to take part.`,
    `async function comps589Session() {`,
    ...(split ? [`  if (!comps589SessionPath) return null; // no address for this site`] : []),
    `  try {`,
    `    const res = await fetch(${split ? "comps589SessionPath" : js(path)}, { credentials: "same-origin" });`,
    `    const data = await res.json().catch(() => ({}));`,
    `    // THE PAGE TOKEN FIRST, and before the signed-out returns below: it`,
    `    // comes back whether or not anybody is signed in, because a visitor`,
    `    // who is not still needs one to read the competition at all.`,
    `    if (data.pageToken) {`,
    `      comps589PageToken = data.pageToken;`,
    `      comps589PageTokenExpiresIn = data.pageTokenExpiresIn || null;`,
    `    }`,
    `    if (res.status === 401) return null; // signed out`,
    `    if (!res.ok) {`,
    `      console.warn("589comps sign-in:", data.error || res.status);`,
    `      return null;`,
    `    }`,
    `    return data.token || null;`,
    `  } catch (err) {`,
    `    console.warn("589comps sign-in:", err);`,
    `    return null;`,
    `  }`,
    `}`,
  ];
}
// comps_current_user() IS WRITTEN FOR THEM when Quick Start knows how their
// site records its signed-in visitor (a PHP session key, saved on this
// site - see normaliseUserLookup, which holds the names to plain
// identifiers because they are written into this code). Otherwise it
// returns nobody until they write it, with the examples' example-token.php
// as the guide.
// WHAT THE VISITOR RADIOS CAN SHOW (1.461.1). Reported 22 Sep 2026:
// "These radio buttons don't save, a page refresh doesn't show the saved
// radio button". The card offers two kinds - PHP's session, or none - but
// a site that has used the sign-in Quick Start has a third stored against
// it, `quick-start-accounts`, and a stored kind that matches no radio
// leaves every radio unchecked. Anything that is not the session kind is
// the "I'll write it myself" one, which is what the card can say.
// (Not exported: this sits inside the source slice widget-options.mjs
// compiles with new Function, which an `export` is not valid in - found
// by running the whole suite, 22 Sep 2026.)
function quickStartLookupDraft(saved) {
  if (!saved) return { kind: "none", idKey: "", nameKey: "", sessionName: "" };
  return {
    kind: saved.kind === "php-session" ? "php-session" : "none",
    idKey: saved.idKey || "",
    nameKey: saved.nameKey || "",
    sessionName: saved.sessionName || "",
  };
}

function currentUserPhp(lookup) {
  // THE ACCOUNTS QUICK START WROTE (1.458.0): comps-accounts.php beside
  // this script holds them, so there is nothing to fill in.
  if (lookup && lookup.kind === "quick-start-accounts") {
    return (
      `// WHO IS SIGNED IN, from the accounts Quick Start wrote\n` +
      `// (comps-accounts.php, beside this file). null while nobody is.\n` +
      `require_once __DIR__ . '/comps-accounts.php';\n` +
      `function comps_current_user() {\n` +
      `    $account = comps_accounts_current();\n` +
      `    if (!$account) return null;\n` +
      `    return ['id' => $account['id'], 'name' => $account['name'], 'email' => $account['email']];\n` +
      `}\n`
    );
  }
  if (lookup && lookup.kind === "php-session") {
    return (
      `// WHO IS SIGNED IN TO YOUR SITE, read from your login's PHP session\n` +
      `// ($_SESSION['${lookup.idKey}']), as saved in Quick Start. null while nobody is.\n` +
      `function comps_current_user() {\n` +
      `    if (session_status() !== PHP_SESSION_ACTIVE) {\n` +
      (lookup.sessionName ? `        session_name('${lookup.sessionName}');\n` : "") +
      `        session_start();\n` +
      `    }\n` +
      `    if (empty($_SESSION['${lookup.idKey}'])) return null;\n` +
      `    return [\n` +
      `        'id' => (string) $_SESSION['${lookup.idKey}'],\n` +
      `        'name' => ${lookup.nameKey ? `$_SESSION['${lookup.nameKey}'] ?? null` : "null"},\n` +
      `    ];\n` +
      `}\n`
    );
  }
  return (
    `// WHO IS SIGNED IN TO YOUR SITE: their id and display name, or null.\n` +
    `// The provider trusts whoever this vouches for, so only ever return\n` +
    `// somebody signed in to your own site. For example, with PHP sessions:\n` +
    `//   if (session_status() !== PHP_SESSION_ACTIVE) session_start();\n` +
    `//   if (empty($_SESSION['user_id'])) return null;\n` +
    `//   return ['id' => (string) $_SESSION['user_id'], 'name' => $_SESSION['user_name']];\n` +
    `// More on this in example-token.php, in the examples download\n` +
    `// (Partners -> Integration).\n` +
    `function comps_current_user() {\n` +
    `    return null;\n` +
    `}\n`
  );
}
function sessionScriptPhp(base, o) {
  // WHAT THIS FILE IS ACTUALLY CALLED (1.472.0). The name comes from the
  // path the partner saved under "Signing visitors in", so a site that
  // keeps it at /session.php gets a file that says session.php - rather
  // than one describing itself, and refusing in the name of, a file that
  // is not on their server. Reported 25 Sep 2026: "the quick start page
  // references comps-session.php but in my test site its just
  // session.php".
  const file = o.file || "comps-session.php";
  const site = o.site === "test" ? "test" : "live";
  // THE SITE IT IS FOR, checked on the server it runs on (1.447.0): the
  // two scripts differ only in which site they sign visitors in to, so one
  // uploaded to the other site would put live visitors into test accounts
  // or test visitors into live ones. With the key's domains known, a script
  // on the wrong host refuses, by the provider's own rule (a host is the
  // test site's when it is the test domain or under it).
  const hostCheck = !o.domain
    ? ""
    : `// THIS SCRIPT IS FOR YOUR ${site.toUpperCase()} SITE, ${o.domain}. On any other host it refuses,\n` +
      `// so it cannot sign visitors in to the wrong site by mistake.\n` +
      `function comps_host_is($host, $domain) {\n` +
      `    return $host === $domain || substr($host, -strlen('.' . $domain)) === '.' . $domain;\n` +
      `}\n` +
      `$host = strtolower(preg_replace('/:\\d+$/', '', $_SERVER['HTTP_HOST'] ?? ''));\n` +
      `if (!comps_host_is($host, '${o.domain}')` +
      (site === "live" && o.testDomain ? ` || comps_host_is($host, '${o.testDomain}')` : "") +
      `) {\n` +
      `    comps_fail(500, 'This ${file} is for your ${site} site, ${o.domain}. Use the one Quick Start generated for this site.');\n` +
      `}\n` +
      `\n`;
  return (
    `<?php\n` +
    `/*\n` +
    ` * ${file} - signs your visitor in to the competition provider\n` +
    ` * - FOR YOUR ${site.toUpperCase()} SITE${o.domain ? `, ${o.domain}` : ""}.\n` +
    ` * and returns the session token the widgets need. Generated by Quick Start.\n` +
    ` *\n` +
    ` * It holds your partner API key, so it runs on YOUR server and the key\n` +
    ` * never reaches a browser. Your page's code asks it for the token.\n` +
    ` *\n` +
    ` * THE KEY, looked for in this order - use whichever your host allows:\n` +
    ` *   1. the environment variable COMPETITION_API_KEY;\n` +
    ` *   2. 589comps-config.php in the folder ABOVE public_html (best: the web\n` +
    ` *      cannot reach it);\n` +
    ` *   3. 589comps-config.php in a folder called private beside this file\n` +
    ` *      (for hosts that allow only public_html);\n` +
    ` *   4. 589comps-config.php beside this file.\n` +
    ` * The first of those holding a key is used.\n` +
    ` *\n` +
    (o.userLookup
      ? ` * Nothing to edit: comps_current_user() reads your login's PHP session.\n`
      : ` * ONE THING TO EDIT: comps_current_user(), below.\n`) +
    ` */\n` +
    `\n` +
    `header('Content-Type: application/json');\n` +
    `header('Cache-Control: no-store');\n` +
    `\n` +
    `const COMPS_PROVIDER = ${JSON.stringify(base).replace(/"/g, "'")};\n` +
    `const COMPS_SITE = '${site}';\n` +
    `\n` +
    currentUserPhp(o.userLookup) +
    `\n` +
    `function comps_fail($status, $message) {\n` +
    `    http_response_code($status);\n` +
    `    echo json_encode(['error' => $message]);\n` +
    `    exit;\n` +
    `}\n` +
    `\n` +
    hostCheck +
    `$apiKey = getenv('COMPETITION_API_KEY') ?: '';\n` +
    `if (!$apiKey) {\n` +
    `    foreach ([dirname(__DIR__) . '/589comps-config.php', __DIR__ . '/private/589comps-config.php', __DIR__ . '/589comps-config.php'] as $file) {\n` +
    `        if (!is_readable($file)) continue;\n` +
    `        $config = require $file;\n` +
    `        if (is_array($config) && !empty($config['api_key'])) {\n` +
    `            $apiKey = $config['api_key'];\n` +
    `            break;\n` +
    `        }\n` +
    `    }\n` +
    `}\n` +
    `if (!$apiKey || strpos($apiKey, 'PASTE-') === 0) {\n` +
    `    comps_fail(500, 'Not set up yet: no API key in COMPETITION_API_KEY, or in 589comps-config.php above public_html, in private/ beside this script, or beside it.');\n` +
    `}\n` +
    `\n` +
    `function comps_post($path, $apiKey, $body) {\n` +
    `    $ch = curl_init(COMPS_PROVIDER . $path);\n` +
    `    curl_setopt_array($ch, [\n` +
    `        CURLOPT_RETURNTRANSFER => true,\n` +
    `        CURLOPT_POST => true,\n` +
    `        CURLOPT_HTTPHEADER => ['Content-Type: application/json', 'x-partner-api-key: ' . $apiKey],\n` +
    `        CURLOPT_POSTFIELDS => json_encode($body),\n` +
    `        CURLOPT_TIMEOUT => 10,\n` +
    `    ]);\n` +
    `    $raw = curl_exec($ch);\n` +
    `    $status = curl_getinfo($ch, CURLINFO_HTTP_CODE);\n` +
    `    $error = curl_error($ch);\n` +
    `    curl_close($ch);\n` +
    `    if ($error) comps_fail(502, 'Could not reach the competition provider: ' . $error);\n` +
    `    return [$status, json_decode($raw, true)];\n` +
    `}\n` +
    `\n` +
    `// ---- THE PAGE TOKEN THE WIDGETS SEND WITH EVERY REQUEST ----\n` +
    `//\n` +
    `// Minting one needs your API key, so it happens here on your server and\n` +
    `// the key never reaches a browser.\n` +
    `//\n` +
    `// IT IS NOT THE VISITOR'S SESSION. A visitor who is not signed in still\n` +
    `// needs one to read the competition at all, which is why this is fetched\n` +
    `// above the sign-in check rather than below it.\n` +
    `//\n` +
    `// AND IT IS NOT PER VISITOR, so it is CACHED. The token says which\n` +
    `// partner and which mode and nothing at all about who is looking, so one\n` +
    `// serves every visitor for its life. That is worth doing rather than\n` +
    `// asking on every page load: the mint is rate limited per IP, and every\n` +
    `// one of these calls comes from this one server, so a mint per page load\n` +
    `// would run your whole site into that limit. Cached, it is about one call\n` +
    `// every ten minutes however busy you are.\n` +
    `//\n` +
    `// The cache goes in the system temp directory rather than anywhere under\n` +
    `// your web root, so it is never servable, and it is written 0600. Two\n` +
    `// page loads racing an expired cache simply mint twice, which is\n` +
    `// harmless.\n` +
    `function comps_page_token($apiKey) {\n` +
    `    $key = substr(hash('sha256', COMPS_PROVIDER . '|' . COMPS_SITE . '|' . $apiKey), 0, 32);\n` +
    `    $cache = rtrim(sys_get_temp_dir(), '/') . '/589comps-page-token-' . $key . '.json';\n` +
    `    $now = time();\n` +
    `    $held = json_decode((string) @file_get_contents($cache), true);\n` +
    `    if (is_array($held) && !empty($held['token'])\n` +
    `        && !empty($held['remint_at']) && $held['remint_at'] > $now\n` +
    `        && !empty($held['expires_at']) && $held['expires_at'] > $now) {\n` +
    `        // The REAL remaining life, not the time left before this server\n` +
    `        // re-mints: the widgets renew at their own fraction of it.\n` +
    `        return ['token' => $held['token'], 'expiresIn' => $held['expires_at'] - $now];\n` +
    `    }\n` +
    `    [$status, $minted] = comps_post('/api/v1/partner/page-token', $apiKey, ['site' => COMPS_SITE]);\n` +
    `    if ($status !== 200 || empty($minted['token'])) {\n` +
    `        comps_fail($status ?: 502, $minted['error'] ?? 'The competition provider refused a page token.');\n` +
    `    }\n` +
    `    $life = (int) ($minted['expiresIn'] ?? 900);\n` +
    `    if (@file_put_contents($cache, json_encode([\n` +
    `        'token' => $minted['token'],\n` +
    `        'expires_at' => $now + $life,\n` +
    `        'remint_at' => $now + (int) floor($life * 0.7),\n` +
    `    ]), LOCK_EX) !== false) {\n` +
    `        @chmod($cache, 0600);\n` +
    `    }\n` +
    `    return ['token' => $minted['token'], 'expiresIn' => $life];\n` +
    `}\n` +
    `\n` +
    `$page = comps_page_token($apiKey);\n` +
    `\n` +
    `// Nobody signed in: no session, but the page token still goes back, because\n` +
    `// the widgets need it to show the competition to watch.\n` +
    `$user = comps_current_user();\n` +
    `if (!$user) {\n` +
    `    echo json_encode([\n` +
    `        'token' => null,\n` +
    `        'pageToken' => $page['token'],\n` +
    `        'pageTokenExpiresIn' => $page['expiresIn'],\n` +
    `    ]);\n` +
    `    exit;\n` +
    `}\n` +
    `\n` +
    `// 1. VOUCH for your visitor: the same id is the same player on every visit.\n` +
    `[$status, $vouch] = comps_post('/api/v1/partner/session', $apiKey, [\n` +
    `    'externalUserId' => (string) $user['id'],\n` +
    `    'username' => $user['name'] ?? null,\n` +
    `    // Their own email, when your site has one: receipts and results then\n` +
    `    // reach them rather than a placeholder address.\n` +
    `    'email' => $user['email'] ?? null,\n` +
    `    'site' => '${site}',\n` +
    `]);\n` +
    `if ($status !== 200 || empty($vouch['code'])) {\n` +
    `    comps_fail($status ?: 502, $vouch['error'] ?? 'The competition provider refused the sign-in.');\n` +
    `}\n` +
    `\n` +
    `// 2. EXCHANGE the one-time code for the session, here on the server.\n` +
    `[$status, $session] = comps_post('/api/v1/auth/exchange', $apiKey, ['code' => $vouch['code']]);\n` +
    `if ($status !== 200 || empty($session['token'])) {\n` +
    `    comps_fail($status ?: 502, $session['error'] ?? 'The competition provider refused the sign-in.');\n` +
    `}\n` +
    `\n` +
    `echo json_encode([\n` +
    `    'token' => $session['token'],\n` +
    `    'pageToken' => $page['token'],\n` +
    `    'pageTokenExpiresIn' => $page['expiresIn'],\n` +
    `]);\n`
  );
}
// A SIGN-IN FOR A SITE THAT HAS NONE YET (1.458.0). Asked on 22 Sep 2026:
// "I want the quick start page to be able to generate a skeleton of a
// working competition site. So if we need a form for logging in/out then
// maybe this sound be an option on the "Generate" form."
//
// So Quick Start can write the partner's own accounts too: a small
// file-backed store with PHP's own password hashing, the three actions a
// form needs, and a comps_current_user() that reads it. It is the shape
//589compstest.com has been running since 1.421.0, generalised.
//
// A STARTER, AND IT SAYS SO. A site that already has accounts keeps them
// and uses one of the other choices; this exists so a site with none can
// run a competition the same afternoon.
function accountsPhp(sessionFile = "comps-session.php") {
  return (
    `<?php\n` +
    `/*\n` +
    ` * comps-accounts.php - your site's own accounts, written by Quick Start.\n` +
    ` *\n` +
    ` * The competition provider signs in whoever this site vouches for, and\n` +
    ` * ${sessionFile} vouches only for somebody signed in HERE.\n` +
    ` *\n` +
    ` * Accounts are kept in a SQLite database with PHP's password hashing,\n` +
    ` * ABOVE public_html when that is writable (the web cannot reach it\n` +
    ` * there) and beside this file otherwise.\n` +
    ` *\n` +
    ` * IT USED TO BE A JSON FILE, AND THAT LOST REGISTRATIONS. Every\n` +
    ` * sign-up read the whole file, added one account and wrote the whole\n` +
    ` * file back, holding no lock across the three. PHP runs as several\n` +
    ` * processes, so simultaneous sign-ups each read the same file and each\n` +
    ` * wrote their own copy back: the last one won. Measured on the real\n` +
    ` * host, twenty at once kept THREE and lost seventeen - and every one of\n` +
    ` * them was answered \"ok\". SQLite keeps all twenty, because the\n` +
    ` * database enforces one email per account instead of the code checking\n` +
    ` * first and hoping.\n` +
    ` *\n` +
    ` * YOUR EXISTING ACCOUNTS ARE IMPORTED, once, the first time this runs.\n` +
    ` * The old JSON file is renamed rather than deleted, so nothing is lost\n` +
    ` * if you want it back.\n` +
    ` *\n` +
    ` * A STARTING POINT, not a finished account system: no email\n` +
    ` * confirmation, no password reset, no rate limiting. If you already\n` +
    ` * have accounts, use those instead - Quick Start can read them.\n` +
    ` */\n` +
    `\n` +
    `// Above public_html when that is writable, beside this file otherwise.\n` +
    `// The same folder the JSON store used, so an existing site keeps its\n` +
    `// data in the place it already is.\n` +
    `function comps_accounts_dir() {\n` +
    `    $outside = dirname(__DIR__) . '/589comps-data';\n` +
    `    if ((is_dir($outside) || @mkdir($outside, 0700, true)) && is_writable($outside)) {\n` +
    `        return $outside;\n` +
    `    }\n` +
    `    return __DIR__;\n` +
    `}\n` +
    `\n` +
    `// OPENED ONCE PER REQUEST and kept, because every page asks who is\n` +
    `// signed in and opening a database per question is a waste of the thing\n` +
    `// this change was made to save.\n` +
    `function comps_accounts_db() {\n` +
    `    static $db = null;\n` +
    `    if ($db !== null) return $db;\n` +
    `    if (!extension_loaded('pdo_sqlite')) {\n` +
    `        // SAID OUT LOUD RATHER THAN FALLING BACK TO THE JSON FILE. A\n` +
    `        // silent fallback would put back the exact defect this replaced,\n` +
    `        // on the hosts least able to notice. Nearly every PHP 8 build has\n` +
    `        // pdo_sqlite; if yours does not, ask your host to enable it.\n` +
    `        http_response_code(500);\n` +
    `        exit('This site needs PHP\\'s pdo_sqlite extension, which is not enabled on this server.');\n` +
    `    }\n` +
    `    $dir = comps_accounts_dir();\n` +
    `    $file = $dir . '/589comps-accounts.sqlite';\n` +
    `    $db = new PDO('sqlite:' . $file, null, null, [PDO::ATTR_ERRMODE => PDO::ERRMODE_EXCEPTION]);\n` +
    `    // WAL lets a reader carry on while somebody is signing up.\n` +
    `    $db->exec('PRAGMA journal_mode=WAL');\n` +
    `    // AND A WRITER WAITS ITS TURN RATHER THAN FAILING. Without this a\n` +
    `    // second simultaneous sign-up is refused instantly instead of\n` +
    `    // queueing, which would trade lost accounts for rejected ones.\n` +
    `    $db->exec('PRAGMA busy_timeout=5000');\n` +
    `    // email UNIQUE IS THE WHOLE FIX. The duplicate check is the\n` +
    `    // database's job now; two simultaneous sign-ups with one address\n` +
    `    // cannot both succeed however they interleave.\n` +
    `    $db->exec('CREATE TABLE IF NOT EXISTS accounts (\n` +
    `        id TEXT PRIMARY KEY,\n` +
    `        name TEXT NOT NULL,\n` +
    `        email TEXT NOT NULL UNIQUE,\n` +
    `        password TEXT NOT NULL,\n` +
    `        created INTEGER NOT NULL)');\n` +
    `    @chmod($file, 0600);\n` +
    `    comps_accounts_import_json($db, $dir);\n` +
    `    return $db;\n` +
    `}\n` +
    `\n` +
    `// THE ONE-TIME IMPORT. Only when the table is empty, so it cannot run\n` +
    `// twice or undo a later deletion. The JSON file is RENAMED rather than\n` +
    `// removed: if anything here is wrong, the accounts are still on disk.\n` +
    `function comps_accounts_import_json($db, $dir) {\n` +
    `    if ((int) $db->query('SELECT COUNT(*) FROM accounts')->fetchColumn() > 0) return;\n` +
    `    foreach ([$dir . '/accounts.json', $dir . '/589comps-accounts.json'] as $old) {\n` +
    `        if (!is_readable($old)) continue;\n` +
    `        $rows = json_decode(file_get_contents($old), true);\n` +
    `        if (!is_array($rows)) continue;\n` +
    `        $add = $db->prepare('INSERT OR IGNORE INTO accounts (id, name, email, password, created) VALUES (?, ?, ?, ?, ?)');\n` +
    `        $db->beginTransaction();\n` +
    `        foreach ($rows as $r) {\n` +
    `            if (!isset($r['id'], $r['email'], $r['password'])) continue;\n` +
    `            $add->execute([$r['id'], $r['name'] ?? '', strtolower((string) $r['email']),\n` +
    `                           $r['password'], (int) ($r['created'] ?? time())]);\n` +
    `        }\n` +
    `        $db->commit();\n` +
    `        @rename($old, $old . '.imported');\n` +
    `    }\n` +
    `}\n` +
    `\n` +
    `function comps_accounts_by_email($email) {\n` +
    `    $st = comps_accounts_db()->prepare('SELECT * FROM accounts WHERE email = ?');\n` +
    `    $st->execute([$email]);\n` +
    `    $row = $st->fetch(PDO::FETCH_ASSOC);\n` +
    `    return $row ?: null;\n` +
    `}\n` +
    `\n` +
    `function comps_accounts_by_id($id) {\n` +
    `    $st = comps_accounts_db()->prepare('SELECT * FROM accounts WHERE id = ?');\n` +
    `    $st->execute([$id]);\n` +
    `    $row = $st->fetch(PDO::FETCH_ASSOC);\n` +
    `    return $row ?: null;\n` +
    `}\n` +
    `\n` +
    `// ONE STORED HASH, for the sign-in timing defence below. See the long\n` +
    `// comment there: it must be a hash THIS host made, so it costs what a\n` +
    `// real one costs through any PHP upgrade.\n` +
    `function comps_accounts_any_hash() {\n` +
    `    $v = comps_accounts_db()->query('SELECT password FROM accounts LIMIT 1')->fetchColumn();\n` +
    `    return is_string($v) && $v !== '' ? $v : null;\n` +
    `}\n` +
    `\n` +
    `// One session for your whole site. "secure" means the cookie is sent\n` +
    `// over https only - leave it off while you are testing on http.\n` +
    `function comps_accounts_start_session() {\n` +
    `    if (session_status() === PHP_SESSION_ACTIVE) return;\n` +
    `    session_set_cookie_params([\n` +
    `        'lifetime' => 30 * 24 * 3600,\n` +
    `        'path' => '/',\n` +
    `        'secure' => !empty($_SERVER['HTTPS']),\n` +
    `        'httponly' => true,\n` +
    `        'samesite' => 'Lax',\n` +
    `    ]);\n` +
    `    session_name('589comps_site');\n` +
    `    session_start();\n` +
    `}\n` +
    `\n` +
    `// The signed-in account, or null.\n` +
    `function comps_accounts_current() {\n` +
    `    comps_accounts_start_session();\n` +
    `    $id = $_SESSION['comps_account_id'] ?? null;\n` +
    `    if (!$id) return null;\n` +
    `    return comps_accounts_by_id($id);\n` +
    `}\n` +
    `\n` +
    `// What the browser may see: never the password hash.\n` +
    `function comps_accounts_public($a) {\n` +
    `    return $a ? ['id' => $a['id'], 'name' => $a['name'], 'email' => $a['email']] : null;\n` +
    `}\n`
  );
}
function authPhp() {
  return (
    `<?php\n` +
    `/*\n` +
    ` * comps-auth.php - sign up, sign in, sign out and "who am I" for the\n` +
    ` * accounts in comps-accounts.php. Written by Quick Start.\n` +
    ` *\n` +
    ` * GET  -> { account: { id, name, email } | null }\n` +
    ` * POST -> { action: "signup" | "signin" | "signout", name, email, password }\n` +
    ` */\n` +
    `header('Content-Type: application/json');\n` +
    `header('Cache-Control: no-store');\n` +
    `require __DIR__ . '/comps-accounts.php';\n` +
    `\n` +
    `function comps_auth_reply($status, $body) {\n` +
    `    http_response_code($status);\n` +
    `    echo json_encode($body);\n` +
    `    exit;\n` +
    `}\n` +
    `\n` +
    `if ($_SERVER['REQUEST_METHOD'] === 'GET') {\n` +
    `    comps_auth_reply(200, ['account' => comps_accounts_public(comps_accounts_current())]);\n` +
    `}\n` +
    `\n` +
    `$in = json_decode(file_get_contents('php://input'), true) ?: [];\n` +
    `$action = $in['action'] ?? '';\n` +
    `comps_accounts_start_session();\n` +
    `\n` +
    `if ($action === 'signout') {\n` +
    `    $_SESSION = [];\n` +
    `    session_destroy();\n` +
    `    comps_auth_reply(200, ['account' => null]);\n` +
    `}\n` +
    `\n` +
    `$email = strtolower(trim($in['email'] ?? ''));\n` +
    `$password = (string) ($in['password'] ?? '');\n` +
    `if (!filter_var($email, FILTER_VALIDATE_EMAIL)) comps_auth_reply(400, ['error' => 'Enter a valid email address.']);\n` +
    `if (strlen($password) < 8) comps_auth_reply(400, ['error' => 'The password must be at least 8 characters.']);\n` +
    `\n` +
    `$found = comps_accounts_by_email($email);\n` +
    `\n` +
    `if ($action === 'signup') {\n` +
    `    $name = trim($in['name'] ?? '');\n` +
    `    if (!preg_match('/^[A-Za-z0-9_]{3,20}$/', $name)) {\n` +
    `        comps_auth_reply(400, ['error' => 'Choose a display name of 3 to 20 letters, numbers or underscores.']);\n` +
    `    }\n` +
    `    if ($found) comps_auth_reply(409, ['error' => 'There is already an account with that email - sign in instead.']);\n` +
    `    $account = [\n` +
    `        'id' => 'u-' . bin2hex(random_bytes(8)),\n` +
    `        'name' => $name,\n` +
    `        'email' => $email,\n` +
    `        'password' => password_hash($password, PASSWORD_DEFAULT),\n` +
    `        'created' => time(),\n` +
    `    ];\n` +
    `    // ONE INSERT, AND THE DATABASE DECIDES. The check above is a\n` +
    `    // courtesy that gives a clear message when nobody is racing; it is\n` +
    `    // NOT what makes this safe, because any check-then-write has a gap\n` +
    `    // between the two. Two simultaneous sign-ups with one address both\n` +
    `    // pass that check and only one of these inserts can succeed - the\n` +
    `    // other lands here, and gets the same 409 it would have got anyway.\n` +
    `    try {\n` +
    `        $add = comps_accounts_db()->prepare('INSERT INTO accounts (id, name, email, password, created) VALUES (?, ?, ?, ?, ?)');\n` +
    `        $add->execute([$account['id'], $account['name'], $account['email'], $account['password'], $account['created']]);\n` +
    `    } catch (PDOException $e) {\n` +
    `        comps_auth_reply(409, ['error' => 'There is already an account with that email - sign in instead.']);\n` +
    `    }\n` +
    `    session_regenerate_id(true);\n` +
    `    $_SESSION['comps_account_id'] = $account['id'];\n` +
    `    comps_auth_reply(200, ['account' => comps_accounts_public($account)]);\n` +
    `}\n` +
    `\n` +
    `if ($action === 'signin') {\n` +
    `    // THE SAME ANSWER AND THE SAME TIME, so nobody can learn which\n` +
    `    // emails exist. The answer was always the same; the TIME was not.\n` +
    `    // password_verify only ran when the email was found, so a known\n` +
    `    // address was refused after one bcrypt and an unknown one came\n` +
    `    // back instantly - measured on PHP 8.4 at 257ms against 9ms, which\n` +
    `    // is a list of your customers to anyone who can run a stopwatch.\n` +
    `    //\n` +
    `    // So a miss is verified against a hash too, and $ok is computed\n` +
    `    // BEFORE the test below: || short-circuits, and putting !$found\n` +
    `    // first in the condition is what skipped the work in the first\n` +
    `    // place. It is still tested, so even an impossible match against\n` +
    `    // the stand-in hash cannot sign anybody in.\n` +
    `    //\n` +
    `    // THE STAND-IN IS A STORED HASH, NOT A CONSTANT WRITTEN HERE. A\n` +
    `    // hardcoded one carries the cost it was made with, and\n` +
    `    // PASSWORD_DEFAULT is bcrypt cost 10 before PHP 8.4 and cost 12\n` +
    `    // from 8.4 - so a constant made at cost 12 would be FOUR TIMES\n` +
    `    // SLOWER than this site's real hashes on PHP 8.3, and the oracle\n` +
    `    // would come back inverted. An existing account's hash was made by\n` +
    `    // password_hash(PASSWORD_DEFAULT) on THIS host, so it always costs\n` +
    `    // what a real one costs, through any PHP upgrade.\n` +
    `    //\n` +
    `    // With no accounts at all there is nothing to enumerate, so the\n` +
    `    // fallback below only has to cost what a bcrypt costs. It is a real\n` +
    `    // hash of a random string that was thrown away, so no password\n` +
    `    // matches it and nothing is weakened by it being published here.\n` +
    `    $stand_in = comps_accounts_any_hash()\n` +
    `        ?: '$2y$12$0ceWv5dp7fdqJCNgCLfhze428bds1lCiy.s6.ZlCqViGtr75heNqS';\n` +
    `    $ok = password_verify($password, $found ? $found['password'] : $stand_in);\n` +
    `    if (!$found || !$ok) {\n` +
    `        comps_auth_reply(401, ['error' => 'That email and password do not match an account.']);\n` +
    `    }\n` +
    `    session_regenerate_id(true);\n` +
    `    $_SESSION['comps_account_id'] = $found['id'];\n` +
    `    comps_auth_reply(200, ['account' => comps_accounts_public($found)]);\n` +
    `}\n` +
    `\n` +
    `comps_auth_reply(400, ['error' => 'Unknown action.']);\n`
  );
}
// THE FORM ITSELF (1.458.0), when a site uses the sign-in Quick Start
// wrote. No styling, like the rest of the page: the classes are the
// partner's. Hidden until somebody asks to take part, or until the sign-out
// button replaces it.
// THE SIGN OUT BUTTON GOES IN THE NAV (1.471.0) when the page has one.
// Asked for on 25 Sep 2026: "move the Sign out button so its at the right
// of the My Account link". Loose under the sign-in section it sat in the
// middle of the page, a long way from the thing it undoes.
function signInFormHtml({ signOutInNav = false } = {}) {
  return [
    '<!-- Sign in, written by Quick Start. It talks to comps-auth.php and',
    '     hands the widgets a session as soon as somebody signs in. -->',
    '<section id="comps-signin" hidden>',
    "  <h2>Sign in to take part</h2>",
    '  <p id="comps-signin-error" role="alert" hidden></p>',
    '  <form id="comps-signin-form">',
    "    <h3>I have an account</h3>",
    '    <label>Email <input name="email" type="email" autocomplete="email" required></label>',
    '    <label>Password <input name="password" type="password" autocomplete="current-password" minlength="8" required></label>',
    '    <button type="submit">Sign in</button>',
    "  </form>",
    '  <form id="comps-signup-form">',
    "    <h3>New here</h3>",
    '    <label>Display name <input name="name" pattern="[A-Za-z0-9_]{3,20}" required></label>',
    '    <label>Email <input name="email" type="email" autocomplete="email" required></label>',
    '    <label>Password <input name="password" type="password" autocomplete="new-password" minlength="8" required></label>',
    '    <button type="submit">Create an account</button>',
    "  </form>",
    "</section>",
    ...(signOutInNav ? [] : ['<button id="comps-signout" type="button" hidden>Sign out</button>']),
  ].join("\n");
}
// WHERE EACH FILE GOES, AS A FOLDER (1.461.5). Reported 22 Sep 2026: "The
// zip references a file called 589comps-config.php but doesn't include it
// or create the necessary directories if they're not there. Can you make
// sure all the relevant files and file structure is included?" The zip was
// flat, so nothing said which file belonged where, and the config file was
// only packed when the key was kept in one - although the generated script
// names it as a fallback whatever the choice.
//
// The zip is rooted at the folder that HOLDS public_html - the one a host's
// file manager opens on - because that is where the config file goes when
// it goes in the best of its three places.
const QUICK_START_WEB_ROOT = "public_html";
function quickStartPaths(sessionPath) {
  const clean = String(sessionPath || "").replace(/^\/+/, "");
  const folder = clean.includes("/") ? clean.slice(0, clean.lastIndexOf("/")) : "";
  const beside = [QUICK_START_WEB_ROOT, folder].filter(Boolean).join("/");
  return { web: QUICK_START_WEB_ROOT, beside };
}

// THE NOTE THAT GOES IN THE ZIP. Written from the choices actually made,
// not a general leaflet: the files that are in this zip, where each one
// goes, and the one line each needs editing.
function quickStartReadme({ site, domain, pageFile, files, keyWhere, sessionPath, pageStyle }) {
  // The name this site's session script really has (1.472.0) - the same
  // one the file list, the zip and the publish use.
  const sessionFile = String(sessionPath || "").split("/").pop() || "comps-session.php";
  const lines = [
    `589comps - the files for your ${site} site${domain ? ` (${domain})` : ""}`,
    "",
    // WHICH OF THE THREE IS IN HERE (1.461.9). Asked 23 Sep 2026 of a
    // download made with Dark chosen: "the version with no styling was
    // also included, why?" - it was not, but nothing in the zip said so.
    pageStyle === "none"
      ? "This zip holds the page with NO STYLING - the markup on its own."
      : `This zip holds the ${(pageStyle || "").toUpperCase()} version of the page. The light, dark and`,
    ...(pageStyle === "none"
      ? ["The light and dark versions are the other two tabs over the code box in Quick Start."]
      : ["unstyled versions are the three tabs over the code box in Quick Start; only the one", "chosen there is in this download."]),
    "",
    "WHERE THINGS GO",
    "",
    `This zip is laid out from the folder that holds ${QUICK_START_WEB_ROOT} - the one your`,
    "host's file manager opens on when you log in. Unzip it there and the files",
    "land where they belong.",
    "",
    `If your host calls the web folder something else (htdocs, www, httpdocs),`,
    `rename ${QUICK_START_WEB_ROOT} here to match before you upload.`,
    "",
    "THE FILES",
    "",
  ];
  for (const f of files) {
    lines.push(`  ${f.path}`);
    lines.push(`      ${f.where}`);
    lines.push("");
  }
  lines.push("YOUR API KEY");
  lines.push("");
  lines.push(`${sessionFile} looks for it in this order, and stops at the first:`);
  lines.push("");
  lines.push("  1. the COMPETITION_API_KEY environment variable;");
  lines.push(`  2. 589comps-config.php in the folder above ${QUICK_START_WEB_ROOT} (best: the web`);
  lines.push("     cannot reach it there);");
  lines.push(`  3. 589comps-config.php in a folder called private beside ${sessionFile};`);
  lines.push(`  4. 589comps-config.php beside ${sessionFile}.`);
  lines.push("");
  lines.push(
    keyWhere === "env"
      ? "You chose the environment variable, so 589comps-config.php is only here in"
      : "You chose the config file, so 589comps-config.php is the one to fill in:"
  );
  lines.push(
    keyWhere === "env"
      ? "case your host has no way to set one - paste your key into it and it is used"
      : "paste your key between the quotes, and leave the file where it is."
  );
  if (keyWhere === "env") lines.push("instead. Leave it out entirely if the variable is set.");
  lines.push("");
  lines.push("The key is in the provider's admin, under Partners -> Partners API Key.");
  lines.push("");
  lines.push("THE PAGE");
  lines.push("");
  // A NAME THAT CHANGED LEAVES THE OLD PAGE BEHIND (1.461.10).
  lines.push("If you uploaded this page under a different name before, delete that older");
  lines.push("copy from your site - it will still be served at its own address otherwise.");
  lines.push("");
  lines.push(`${pageFile} is a whole page. Take what you need from it into your own`);
  lines.push("template - the <script> block at the bottom is the part that matters.");
  if (sessionPath) {
    lines.push("");
    lines.push(`It asks ${sessionPath} for a session token, which is why that file has to`);
    lines.push("be reachable at exactly that address.");
  }
  return lines.join("\n") + "\n";
}

function configFilePhp(sessionFile = "comps-session.php") {
  return (
    `<?php\n` +
    `// 589comps-config.php - your partner API key, for ${sessionFile}.\n` +
    `// Put this file in ONE of these; ${sessionFile} looks in this order:\n` +
    `//   1. the folder ABOVE public_html, where the web cannot reach it (best);\n` +
    `//   2. a folder called private beside ${sessionFile}, e.g.\n` +
    `//      public_html/private/ - for hosts that allow only public_html;\n` +
    `//   3. beside ${sessionFile}.\n` +
    `// In 2 and 3 PHP runs the file rather than showing it, so the key is\n` +
    `// not served either way.\n` +
    `// The key is in the provider's admin: Partners -> Partners API Key.\n` +
    `return [\n` +
    `    'api_key' => 'PASTE-THE-PARTNER-API-KEY-HERE',\n` +
    `];\n`
  );
}
// THE REPLAY WIDGET'S CODE (1.441.0).
//
// It was `mount(el, { baseUrl })`, and the widget refuses that: it needs a
// session token and the competition to play, and throws without them. In
// one pasted script that throw also stopped every line after it, so the
// account widget never started. 1.439.0 had then given it the competition
// widget's options, none of which it takes. Now it names what it needs and
// mounts only when it has both.
// A LIST OF FIELDS, NOT A RUN OF SENTENCES (1.475.0).
//
// Asked for on 26 Sep 2026: "can we make the cards/data below look
// presentable to a prospective partner?" - of a page reading
// "Free entry enabled (site-wide, no purchase necessary): true".
//
// AN EMITTER OF ITS OWN, like winnersTableCode, so a test can build this
// and run it against a stub document rather than only read it.
function fieldListCode() {
  return [
    // A LIST OF FIELDS, NOT A RUN OF SENTENCES (1.475.0). Asked for on
    // 26 Sep 2026: "can we make the cards/data below look presentable
    // to a prospective partner?" - of a page reading
    // "Free entry enabled (site-wide, no purchase necessary): true".
    //
    // A <dl> because it reads correctly with no stylesheet at all,
    // which the unstyled template has to: the browser puts each label
    // on its own line with its value indented under it. The stylesheet
    // turns the same markup into two columns.
    "function comps589FieldValue(value) {",
    "  if (value === true) return \"Yes\";",
    "  if (value === false) return \"No\";",
    "  if (Array.isArray(value)) return value.join(\", \");",
    // AN ENTRY IS AN OBJECT, NOT A STRING (1.529.0).
    //
    // Found on 28 Sep 2026 while checking every box on the card: Latest
    // Entries and Recent Purchases answer with { user, guess,
    // entered_at } and { user, details, amount, purchased_at } - one
    // object per numbered field. String() on that is "[object Object]",
    // so all twelve of those boxes drew the same nonsense on the page.
    //
    // THE ADMIN PANEL HAS ALWAYS HANDLED IT - see formatFieldValue, which
    // joins the values for exactly this reason. The generated page never
    // got the same treatment, and nothing compared the two.
    //
    // THE VALUES, JOINED, matching the panel rather than inventing a
    // second format. A partner who wants the parts laid out separately
    // writes a card template, where {{user}} and {{guess}} are their own
    // placeholders - that is what card templates are for.
    "  if (value && typeof value === \"object\") return Object.values(value).join(\" \\u2014 \");",
    "  return String(value);",
    "}",
    // A PICTURE IS SHOWN, NOT SPELLED OUT (1.489.0). Asked for on 26 Sep
    // 2026: "the image from the previous competition should show on the
    // template pages, make it smaller though, doesn't need to take up the
    // whole page. maybe 300x300 or something". The revealed image was
    // already in the API and already ticked - it printed as sixty
    // characters of URL where the partner wanted the picture.
    //
    // BY THE SHAPE OF THE VALUE, NOT BY A FIELD NAME. Every label on this
    // page is the operator's, and this file knows none of them - the same
    // reason true renders as "Yes" here rather than anywhere that knows
    // what the field means.
    //
    // THE PATH IS RELATIVE TO THE PROVIDER. It arrives as
    // /api/v1/competitions/<id>/image, so a bare <img src> would look for
    // it on the partner's own domain and find nothing. Measured
    // 26 Sep 2026: image/jpeg, 1000x750, served to anyone.
    "const COMPS589_IMAGE = /(\\/image(\\?|$)|\\.(png|jpe?g|gif|webp|avif)(\\?|$))/i;",
    // ONE TEST, TWO CALLERS (1.525.0). The picture is lifted out of the
    // list and drawn above it, so the shape has to be asked twice - once
    // to sort the fields and once to build the node. A second copy of
    // the rule is a second thing to keep in step.
    "function comps589IsImage(value) {",
    '  const s = typeof value === "string" ? value : "";',
    '  return /^(https?:\\/\\/|\\/)/.test(s) && COMPS589_IMAGE.test(s);',
    "}",
    // A REFERENCE IS NOT A HEADLINE (1.525.0). Reported 28 Sep 2026 of
    // the Last Result card: a competition's UUID was set in the same
    // weight as the winner's name and the prize, and being the longest
    // value on the card it read as the most important thing on it.
    //
    // BY SHAPE, LIKE EVERYTHING ELSE HERE. A long run with no spaces in
    // it is an identifier whatever the operator called the field, and
    // this file knows none of their names - the same reason true renders
    // as "Yes" here rather than anywhere that knows what a field means.
    "function comps589IsReference(value) {",
    '  const s = typeof value === "string" ? value : "";',
    "  // A DIGIT, A SLASH OR A COLON, not just length: a long word is",
    "  // still a word. Without this, a one-word title like",
    '  // "Supercalifragilistic" would have been dimmed as though it were',
    "  // an id. Everything actually machine-made - a UUID, an ISO date, a",
    "  // URL, a token - carries at least one of the three.",
    "  return s.length >= 16 && !/\\s/.test(s) && /[0-9:/]/.test(s);",
    "}",
    // NO `banner` ARGUMENT ANY MORE (1.527.0). It chose between two caps
    // for a picture in a value column and one drawn above the list - and
    // since 1.525.0 lifted EVERY picture out of the list, nothing has
    // reached the second branch. A parameter whose other value cannot
    // occur is a thing to keep in step for no reason.
    "function comps589FieldNode(value, label) {",
    '  const s = typeof value === "string" ? value : "";',
    "  if (comps589IsImage(s)) {",
    '    const img = document.createElement("img");',
    '    img.src = s.charAt(0) === "/" ? BASE_URL + s : s;',
    '    img.alt = label || "";',
    '    img.loading = "lazy";',
    "    // SET HERE RATHER THAN ONLY IN THE STYLESHEET, because the",
    "    // unstyled template has no stylesheet at all and a 1000px",
    "    // picture in it would be the widest thing on the page.",
    "    //",
    "    // SMALL, NOT HUGE (1.527.0). Reported 28 Sep 2026: \"The last",
    "    // result section is far too big too. It should be in similar",
    "    // size to the one used on loop arcade, small and doesn't take up",
    "    // much space.\" Loop Arcade draws it in a 300px side panel.",
    "    // 1.525.0 took this picture out of a cramped value column and",
    "    // gave it the card's whole width, which fixed the cramping and",
    "    // overshot - on a wide page the revealed image was the biggest",
    "    // thing on it.",
    "    //",
    "    // THE SAME 300px AS THE PAGE'S OWN RAIL, so it matches the cards",
    "    // beside the competition rather than being a third measurement.",
    "    // BOTH DIMENSIONS CAPPED, so a portrait picture cannot run away",
    "    // down the page the way a landscape one would have run across.",
    '    img.style.maxWidth = "300px";',
    '    img.style.maxHeight = "240px";',
    "    // width/height auto on purpose: a cap on one side with a fixed",
    "    // other side squashes the picture wherever object-fit is not",
    "    // applied, and the unstyled template applies none.",
    '    img.style.width = "auto";',
    '    img.style.height = "auto";',
    "    return img;",
    "  }",
    "  return document.createTextNode(comps589FieldValue(value));",
    "}",
    // THE PICTURE LEADS THE CARD (1.525.0).
    //
    // Asked for on 28 Sep 2026: "can you make this a nice looking,
    // presentable card so it looks visually appealing" - of a card whose
    // biggest element, the revealed image, was crammed into the right
    // half of a two-column list with an empty label column beside it and
    // 400px of dead space under the label.
    //
    // A picture in a label/value row is the one value that cannot be a
    // row: it is not read against its label, it is looked at. So it
    // comes out of the list and goes above it, full width, with its
    // label kept underneath as a caption so nothing the operator chose
    // is lost.
    //
    // NOTHING ELSE MOVES. The order of the remaining fields is the
    // operator's and is left exactly as they gave it - only the picture
    // is promoted, and only because it is a picture.
    //
    // EVERY GROUP GOES THROUGH HERE, not just Last Result. A group with
    // no image renders exactly as it did, which is most of them.
    // WHAT GOES IN FRONT OF AN AMOUNT (1.526.0).
    //
    // FOUND BY DECLARATION, NOT BY NAME. The response says which field
    // is the currency symbol and which is the code; this page knows no
    // field names and is not about to learn one. An operator who has
    // ticked neither gets the bare number, exactly as before.
    //
    // THE SYMBOL IF IT IS SERVED, ELSE THE CODE. "GBP 1,000" says more
    // than "1000" does, and a non-breaking space keeps the code with the
    // number when the line wraps.
    "function comps589MoneyPrefix(fields) {",
    "  const has = (f) => f.value != null && f.value !== \"\";",
    "  const sym = fields.find((f) => f.currencySymbol && has(f));",
    "  if (sym) return String(sym.value);",
    "  const code = fields.find((f) => f.currencyCode && has(f));",
    '  return code ? String(code.value) + "\u00a0" : "";',
    "}",
    // A ZERO IS A REAL AMOUNT HERE, unlike in the live band, where the
    // question was whether to show a fact at all and nought meant "not
    // set". This field has already been chosen and already passed the
    // response's own null check: a prize of nothing is a result.
    "function comps589Money(value, prefix) {",
    "  const n = Number(value);",
    "  // NOT A NUMBER, NOT OUR BUSINESS. A money field carrying text is",
    "  // printed as it arrived rather than turned into NaN.",
    '  if (value === "" || value === null || !isFinite(n)) return null;',
    "  // toLocaleString ON BOTH BRANCHES. toFixed(2) does not group, so",
    '  // 1234567.5 came out "1234567.50" beside a whole prize that read',
    '  // "1,000". Found by running it against a big number, not by',
    "  // reading it.",
    "  return prefix + (Number.isInteger(n)",
    "    ? n.toLocaleString()",
    "    : n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }));",
    "}",
    "function comps589FieldList(fields) {",
    "  const shots = fields.filter((f) => comps589IsImage(f.value));",
    "  // THE CURRENCY IS READ FROM THE WHOLE RESPONSE, BEFORE ANYTHING IS",
    "  // DROPPED (1.527.0) - the symbol is one of the fields this page",
    "  // consumes rather than prints, so reading it after the filter below",
    "  // would leave every amount bare.",
    "  const money = comps589MoneyPrefix(fields);",
    // WHAT THIS PAGE IS FOR (1.527.0). Reported 28 Sep 2026: "the quick
    // start templates are supposed to be examples so a partner can see
    // what the competitions are all about. They don't need to see every
    // data point as it is."
    //
    // A field marked `supporting` is an input - the currency to put in
    // front of an amount, the measurements to reserve space with. Read
    // as a row it says nothing about the competition, and three rows
    // reading "Prize money £1,000 / Currency symbol £ / Currency code
    // GBP" is an API dump rather than an illustration.
    //
    // THE API IS UNCHANGED. Every one of them still goes out; this is
    // only about what the example page prints.
    // AND A QUESTION THAT DOES NOT APPLY IS NOT AN EMPTY ANSWER
    // (1.548.0). Reported 29 Sep 2026 with a screenshot of this list:
    // "the 'converts to lottery' doesn't do a countdown and the dashes
    // stay there so at the moment its redundant".
    //
    // That pair is one fact in two phases - how long until entries
    // freeze, then how long until the draw - so exactly one of them is
    // null every second of every competition, and the other printed a
    // dash for the whole of it. The provider's own page has always
    // written each row only inside the phase it belongs to.
    //
    // THE DASH STAYS FOR EVERYTHING ELSE: "Keep dashes when there's no
    // value", decided 29 Sep 2026. `conditional` is the provider saying
    // this particular null means something else, and it is read as a
    // flag rather than by field name, like every other rule here.
    "  const rest = fields.filter(",
    "    (f) => !comps589IsImage(f.value) && !f.supporting && !(f.conditional && (f.value === null || f.value === undefined))",
    "  );",
    '  const wrap = document.createElement("div");',
    '  wrap.className = "comps-record";',
    "  shots.forEach((f) => {",
    '    const fig = document.createElement("figure");',
    '    fig.className = "comps-shot";',
    "    fig.appendChild(comps589FieldNode(f.value, f.label));",
    '    const cap = document.createElement("figcaption");',
    "    cap.textContent = f.label;",
    "    fig.appendChild(cap);",
    "    wrap.appendChild(fig);",
    "  });",
    '  const dl = document.createElement("dl");',
    '  dl.className = "comps-fields";',
    "  rest.forEach((f) => {",
    '    const dt = document.createElement("dt");',
    "    dt.textContent = f.label;",
    '    const dd = document.createElement("dd");',
    "    if (comps589IsReference(f.value)) dd.className = \"comps-ref\";",
    "    // AN AMOUNT THE FIELD CALLED MONEY gets the currency in front of",
    "    // it and a thousands separator - \"1000\" became \"1,000\" and",
    '    // "\u00a31,000" the day the field said what it was.',
    "    const asMoney = f.money ? comps589Money(f.value, money) : null;",
    '    if (asMoney !== null) dd.appendChild(document.createTextNode(asMoney));',
    "    else dd.appendChild(comps589FieldNode(f.value, f.label));",
    "    dl.appendChild(dt);",
    "    dl.appendChild(dd);",
    "  });",
    "  wrap.appendChild(dl);",
    "  // A <div> ONLY WHEN THERE IS SOMETHING TO WRAP. With no picture",
    "  // this returns the same <dl> it always did, so a group that had no",
    "  // image has the same markup as before - and a partner's CSS",
    "  // written against it still applies.",
    "  return shots.length ? wrap : dl;",
    "}",
  ];
}

// THE WINNERS TABLE (1.471.0), for the Winners view.
//
// Asked for on 25 Sep 2026: "Can you create a Winners page next to My
// Account for the light and dark templates and show the list of winners
// and a link to the replay for each competition?" - with a screenshot of
// what was there: every winner as one long line of "Label: value \u00b7
// Label: value", which is readable at one field and unreadable at nine.
//
// AN EMITTER OF ITS OWN so it can be run rather than only read: a test
// builds this code, runs it against a stub document and presses the
// Watch. `watch` is whether there is anywhere for a replay to be
// watched - the Replays tab of 1.470.0.
// THE WATCH COLUMN WENT AT 1.473.0. Asked for on 26 Sep 2026, of the
// published page: "I want to remove a few things from the winners table
// anyway. Can you remove these columns: Competition ID / Replay recorded
// / Replay." The first two are tick boxes; this one was ours.
//
// It is not a loss of the feature it was built for. Replays live in the
// account widget's Replays tab (1.470.0), which lists the visitor's own
// finished competitions and plays any of them - the winners table was a
// second way in, to a tab that already has one. What it cost was three
// columns of width on a public page: the Watch itself, plus the two
// fields it read, which had to be ticked for it to work at all.
function winnersTableCode() {
  return [
    "// One row per winner. Dates and yes/no values are made readable",
    "// here; everything else is printed as the API sent it.",
    "function comps589WinnerValue(key, value) {",
    '  if (key === "wonAt") return new Date(value).toLocaleString();',
    '  if (typeof value === "boolean") return value ? "Yes" : "No";',
    "  return String(value);",
    "}",
    // A TABLE, NOT A RUN OF SENTENCES (1.471.0). Asked for with the
    // Winners view: a list of winners, and a link to the replay for
    // each competition. Every row used to be one long line of
    // "Label: value \u00b7 Label: value", which is readable for one
    // field and unreadable for nine - and the screenshot that came
    // with the request was of exactly that.
    // WHAT GOES IN FRONT OF THIS ROW'S AMOUNT (1.597.0).
    //
    // PER ROW, not per page. A prize is denominated when it is set, so
    // two winners from either side of a currency change carry two
    // different symbols - and the row brings its own rather than the
    // table assuming the site's.
    //
    // THE SYMBOL IF IT IS SERVED, ELSE THE CODE, which is the rule
    // comps589MoneyPrefix already follows for the field cards. An
    // operator who has ticked neither gets the bare number, exactly as
    // this table did before.
    "function comps589WinnerPrefix(w) {",
    '  if (w.currencySymbol) return String(w.currencySymbol);',
    '  return w.currency ? String(w.currency) + "\u00a0" : "";',
    "}",
    "function comps589WinnerCell(w, key, money) {",
    '  const td = document.createElement("td");',
    '  const empty = w[key] === null || w[key] === undefined || w[key] === "";',
    "  if (empty) {",
    '    td.textContent = "\u2014";',
    "    return td;",
    "  }",
    "  // comps589Money IS THE SAME ONE THE FIELD CARDS USE - grouping and",
    "  // the not-a-number escape come with it, so a table and a card never",
    "  // print the same prize two ways.",
    "  const asMoney = money ? comps589Money(w[key], comps589WinnerPrefix(w)) : null;",
    '  td.textContent = asMoney !== null ? asMoney : comps589WinnerValue(key, w[key]);',
    "  return td;",
    "}",
    "function comps589WinnersTable(rows) {",
    '  const table = document.createElement("table");',
    '  table.className = "comps-winners";',
    '  const head = document.createElement("tr");',
    "  COMPS589_WINNER_COLUMNS.forEach(([, label]) => {",
    '    const th = document.createElement("th");',
    "    th.textContent = label;",
    "    head.appendChild(th);",
    "  });",
    '  const thead = document.createElement("thead");',
    "  thead.appendChild(head);",
    "  table.appendChild(thead);",
    '  const body = document.createElement("tbody");',
    "  rows.forEach((w) => {",
    '    const tr = document.createElement("tr");',
    "    COMPS589_WINNER_COLUMNS.forEach(([key, , money]) => tr.appendChild(comps589WinnerCell(w, key, money)));",
    "    body.appendChild(tr);",
    "  });",
    "  table.appendChild(body);",
    "  return table;",
    "}",
  ];
}

// THE REPLAYS TAB, IN THE VISITOR'S ACCOUNT (1.470.0).
//
// Asked for on 25 Sep 2026: "the replays should only show on user's
// account section like they do on 589comps.com where there is a Replays
// tab there. This is where replays should be, not underneath the
// competition image. If the Replay widget isn't selected as an option
// then there should be no Replay tab on the user's account."
//
// 589comps.com does exactly this, and the account widget was built for
// it: `extraSections` (1.431.0) gives a partner their own tabs beside
// the seven, and its own documentation offers a replay list as the
// example. Loop Arcade's Replays tab is one, and this is the same shape
// with the page's own theme.
//
// WHAT CHANGED FROM 1.465.0. That release found the replay - the id comes
// from the visitor's own finished competitions, and renderSections draws
// the sections the competition ran under - and mounted it in a div under
// the competition, which is the part this replaces. The finding and the
// drawing are unchanged; the list is new, because a tab that plays back
// whichever competition happened to be newest is not an account section,
// it is the old block with a heading.
//
// EVERYTHING IS INSERTED AS TEXT. A competition title is written by
// whoever ran it; the account widget makes the same promise about
// everything it draws, and a tab of ours inside it must not be the
// exception.
//
// NO TAB WITHOUT THE WIDGET: this is emitted only when the replay widget
// is ticked, and there is nothing else on the page that would add one.
function replaysSectionCode(global, theme) {
  return [
    `// The Replays tab, drawn by the account widget when the visitor`,
    `// opens it. It hands us an empty element and a ctx: ctx.token is`,
    `// the visitor's session token as it stands now, so a sign-in during`,
    `// the visit needs nothing extra here.`,
    `//`,
    `// RETURNS A FUNCTION, which the widget calls when the visitor leaves`,
    `// this tab - another section, closing, or destroy(). That is what`,
    `// stops a replay still playing behind a tab nobody is looking at.`,
    `function comps589ReplaysSection(el, ctx) {`,
    `  var stage = document.createElement("div");`,
    `  var list = document.createElement("div");`,
    `  el.appendChild(stage);`,
    `  el.appendChild(list);`,
    `  list.textContent = "Loading\u2026";`,
    `  function stop() {`,
    `    if (comps589Handles.replay && comps589Handles.replay.destroy) {`,
    `      comps589Handles.replay.destroy();`,
    `    }`,
    `    comps589Handles.replay = null;`,
    `  }`,
    `  function watch(competitionId) {`,
    `    // One at a time: two mounted replays poll and draw over each other.`,
    `    stop();`,
    `    stage.replaceChildren();`,
    `    comps589Handles.replay = ${global}.mount(stage, {`,
    `      baseUrl: ctx.baseUrl,`,
    `      token: ctx.token,`,
    `      pageToken: comps589PageToken,`,
    `      pageTokenExpiresIn: comps589PageTokenExpiresIn,`,
    `      competitionId: competitionId,`,
    `      // The sections saved for your site, around the picture - the`,
    `      // same ones the competition showed while it was running.`,
    `      renderSections: true,`,
    `      theme: ${JSON.stringify(theme)},`,
    `    });`,
    `  }`,
    `  // Only competitions this visitor took part in, newest first.`,
    `  return fetch(ctx.baseUrl + "/api/v1/auth/my-replays", { headers: comps589ApiHeaders({ "x-auth-token": ctx.token }) })`,
    `    .then(function (res) { return res.ok ? res.json() : []; })`,
    `    .then(function (items) {`,
    `      list.replaceChildren();`,
    `      if (!items.length) {`,
    `        list.textContent =`,
    `          "Nothing to watch back yet. Take part in a competition and it will appear here once it has finished.";`,
    `        return stop;`,
    `      }`,
    `      items.forEach(function (item) {`,
    `        var row = document.createElement("div");`,
    `        var name = document.createElement("strong");`,
    `        // TEXT, NEVER MARKUP: the title is whoever ran the`,
    `        // competition's words, not ours.`,
    `        name.textContent = item.title;`,
    `        var when = document.createElement("span");`,
    `        when.textContent =`,
    `          " " + new Date(item.finishedAt).toLocaleDateString() + (item.wonByYou ? " \u2014 you won" : "");`,
    `        var button = document.createElement("button");`,
    `        button.type = "button";`,
    `        button.textContent = "Watch";`,
    `        button.addEventListener("click", function () { watch(item.competitionId); });`,
    `        row.appendChild(name);`,
    `        row.appendChild(when);`,
    `        row.appendChild(button);`,
    `        list.appendChild(row);`,
    `      });`,
    `      return stop;`,
    `    })`,
    `    .catch(function () {`,
    `      list.textContent = "Could not load your replays.";`,
    `      return stop;`,
    `    });`,
    `}`,
  ].join("\n");
}
// WHAT THE BUILD WAS MADE FROM (1.488.0), so the page can say when it is
// behind. Generate is a press, deliberately - asked for on 26 Sep 2026:
// "It looks like this is going to be impossible without automatically
// generating without the user clicking the generate button. That's not
// what we want." So the preview and the two lists below it are a
// photograph, not a mirror; what was missing is that a stale photograph
// looks exactly like a fresh one. Unticking a widget and pressing
// Download ZIP shipped a page that still had it, with nothing on screen
// saying which build that was. (The zip and the page always agreed with
// each other - both were the earlier build. Measured 26 Sep 2026, screen
// beside request body.)
//
// EXACTLY WHAT THE GENERATOR READS, and nothing more, or the page cries
// stale over something that never reaches a file:
//
//  - `picked` and `widgets` become their TICKED KEYS, sorted. The
//    generator asks `picked[k]` and `widgets[id]`, so `{a: false}` and
//    `{}` make the same files; keeping the objects whole would make
//    unticking a box and ticking it again read as a change.
//  - `savedLayout` narrows to its `domains`. That is all generate() reads
//    of it (`quickStartSites(savedLayout?.domains)`) - the competition
//    layout never enters the page, because the widget fetches it from the
//    API at runtime - so saving a layout must not mark the files behind.
//  - `checklist` narrows to each site's `userLookup`, which is the one
//    thing the generator reads from it, through userLookupFor. It is in
//    the checklist rather than in `signIn` because the server owns it,
//    and it was nearly missed: it is not one of the twelve state values
//    the generator names directly.
//  - `pageStyle` is LEFT OUT. A build carries all three templates and
//    `chosenPage` picks from them live, so "Use this one" changes what is
//    sent with no second press - measured: the zip became the dark page
//    and gained its stylesheet on its own. Including it would put a
//    warning on the page for a choice that is already honoured.
//  - EVERY LEVEL'S KEYS ARE SORTED. A fingerprint that depends on the
//    order an object happened to be written in goes off by itself.
const quickStartTicked = (obj) =>
  Object.keys(obj || {})
    .filter((k) => !!obj[k])
    .sort();
const quickStartStable = (v) => {
  if (Array.isArray(v)) return `[${v.map(quickStartStable).join(",")}]`;
  if (v && typeof v === "object") {
    return `{${Object.keys(v)
      .sort()
      .map((k) => `${JSON.stringify(k)}:${quickStartStable(v[k])}`)
      .join(",")}}`;
  }
  return JSON.stringify(v ?? null);
};
function quickStartFingerprint(s) {
  return quickStartStable({
    buyCreditTab: !!s.buyCreditTab,
    comp: s.comp ?? null,
    domains: s.savedLayout?.domains ?? null,
    groups: s.groups ?? null,
    navMenu: s.navMenu ?? null,
    navStyle: s.navStyle ?? null,
    navTheme: s.navTheme ?? null,
    ownCss: s.ownCss ?? null,
    pageTitle: s.pageTitle ?? null,
    picked: quickStartTicked(s.picked),
    signIn: s.signIn ?? null,
    // The sites the key can have, from the one place that names them.
    userLookup: quickStartSites(s.savedLayout?.domains).map(
      ({ site }) => s.checklist?.quickStart?.[site]?.userLookup ?? null
    ),
    widgets: quickStartTicked(s.widgets),
  });
}
// "a, b and c" (1.478.0): a list somebody reads out loud, for the card
// that says what a Generate will make.
const quickStartAnd = (parts) =>
  parts.length < 2 ? parts.join("") : `${parts.slice(0, -1).join(", ")} and ${parts[parts.length - 1]}`;
// One entry in the account widget's `extraSections`, written the way a
// partner would write their own (1.476.0).
const quickStartTabCode = (t) =>
  `{ id: ${JSON.stringify(t.id)}, label: ${JSON.stringify(t.label)}, render: ${t.render} }`;
// THE BUY CREDIT TAB, IN THE VISITOR'S ACCOUNT (1.476.0).
//
// Asked for on 26 Sep 2026: "589comps.com has the facility to 'Buy
// credit', can you make this available on the 'my account' section on
// quick start page templates?"
//
// 589comps.com's is an extraSection exactly like its Replays one, and
// the account widget's own documentation names this as the second thing
// a partner is expected to build:
//
//   WHAT IS NOT HERE, on purpose (add them as your own sections):
//     - Buying credit, which is a hosted checkout with a return address
//       on your site: POST /api/v1/credits/topup/start-via-partner.
//
// So this is that section, written for them.
//
// EVERY NUMBER COMES FROM /api/v1/auth/my-credits: the balance, the
// currency, the rate, and the smallest and largest top-up allowed.
// Nothing is baked into the page, because all of them are settings an
// operator can change without telling their partner.
//
// THE CURRENCY IS THE CODE, NOT A SYMBOL. my-credits returns
// `currency` ("GBP"), and the provider's own refusals are worded
// "5.00 GBP at the current rate" - so a page that says the same thing
// cannot disagree with the error it may be about to show. A symbol
// would mean a table of them here, wrong for every currency missing
// from it.
//
// WHERE THE BUYER COMES BACK TO. The checkout is hosted by the provider,
// so the visitor leaves this page. `returnUrl` is this page plus
// #account/buy-credit, which the account widget reads on load - so they
// land back on the tab they left rather than on a page that says
// nothing. The provider appends its own ?pendingPurchase=<id>, and
// `new URL(...).searchParams.set()` keeps the fragment last, so the two
// do not collide.
//
// WHO SAYS WHETHER IT WORKED. The competition widget polls
// ?pendingPurchase= itself and has said "your credit has been added"
// since it learned to tell a top-up from a pixel purchase. So on a page
// that has it, this stays out of the way and only the balance above the
// form - re-read every time the tab is drawn - changes. On a page
// WITHOUT that widget, nothing else would ever say what happened, so
// `follow` is emitted and this does the polling itself. Which of the two
// is decided here, at generate time, rather than by two pieces of code
// racing for the same query parameter.
function buyCreditSectionCode({ follow }) {
  return [
    `// The Buy credit tab, drawn by the account widget when the visitor`,
    `// opens it. ctx.token is their session as it stands now, and`,
    `// ctx.baseUrl is this site.`,
    `function comps589BuyCreditSection(el, ctx) {`,
    `  var box = document.createElement("div");`,
    `  box.className = "comps-topup";`,
    `  el.appendChild(box);`,
    `  box.textContent = "Loading\\u2026";`,
    `  // A line of text, as a <p> of its own.`,
    `  function line(className, text) {`,
    `    var p = document.createElement("p");`,
    `    if (className) p.className = className;`,
    `    p.textContent = text;`,
    `    return p;`,
    `  }`,
    `  function credits(n) { return n + " credit" + (n === 1 ? "" : "s"); }`,
    ...(follow
      ? [
          `  // BACK FROM THE PROVIDER'S PAYMENT PAGE. This page has no`,
          `  // competition widget on it, and that widget is what normally`,
          `  // reports a purchase - so with nothing here the buyer would`,
          `  // return to a page that never mentions what they just paid for.`,
          `  //`,
          `  // The webhook, not this redirect, is what actually grants the`,
          `  // credit, so the outcome is asked for rather than assumed.`,
          `  function follow(id, into) {`,
          `    var tries = 0;`,
          `    into.replaceChildren(line("", "Checking your payment\\u2026"));`,
          // ---- THE CEILING MOVES TO THE TOP OF ask() (1.869.0) ----
          //
          // It used to sit in the success path only, after the settled
          // branches, so the `.catch` had nothing bounding it. That was
          // survivable while the catch gave up on the first error, and it
          // is the reason the catch could not simply be given a retry: the
          // retry would have been unbounded.
          //
          // Checked before `tries` is incremented, so the number of
          // requests is unchanged at ten - the tenth answer schedules one
          // more ask(), which sees the ceiling and says so.
          `    function ask() {`,
          `      if (tries >= 10) {`,
          `        into.replaceChildren(line("comps-topup-note", "Still no confirmation of that payment. If it went through, your balance will update on its own \\u2014 if it does not, quote reference " + id + " and we will put it right."));`,
          `        return;`,
          `      }`,
          `      tries += 1;`,
          `      fetch(ctx.baseUrl + "/api/v1/purchase/start/" + encodeURIComponent(id) + "/status", { headers: comps589ApiHeaders() })`,
          `        .then(function (res) { return res.json(); })`,
          `        .then(function (data) {`,
          `          if (data.status === "confirmed") {`,
          `            into.replaceChildren(line("comps-topup-ok", "Payment confirmed \\u2014 your credit has been added."));`,
          `            return;`,
          `          }`,
          // ---- WHAT THIS SNIPPET TELLS A BUYER (1.868.0) ----
          //
          // THE REASONING IS HERE RATHER THAN IN THE EMITTED COMMENTS,
          // which a partner pastes into their own page and should stay
          // short. 1.867.0 fixed the same fault in the site and the
          // widget; this is the third consumer of the same route, found
          // by grepping every caller of it rather than by noticing.
          //
          // "failed" DOES NOT MEAN NO MONEY MOVED. The status route
          // re-reconciles a failed record for exactly that reason, and
          // reconcilePendingPurchase marks a purchase failed AFTER the
          // provider has said the payment SUCCEEDED when the buyer's
          // account cannot be found. This branch said "Nothing has been
          // charged." - a false statement about the buyer's own money in
          // precisely the states the backend anticipates.
          //
          // "expired" IS the status that means no money moved, by either
          // of its two routes - the provider reporting the job expired
          // unpaid, or expireStalePendingPurchases closing an abandoned
          // checkout and stamping expiredBy "ttl". It had no branch, so
          // it fell to the tenth attempt and the give-up line.
          //
          // AND "refunded" / "refund-failed" ARE NOT HANDLED, because a
          // top-up cannot hold them. Two independent reasons, both
          // asserted by quick-start-buy-credit.test.mjs so that a change
          // to either is reported here rather than silently needing a
          // branch: reconcilePendingPurchase returns inside
          // `if (current.kind === "credit-topup")` before the
          // competition-changed check is reached, and a top-up record is
          // created with `competitionId: null` while that check requires
          // a competition id. An unhandled status still degrades to the
          // give-up line, which claims nothing.
          `          // expired is the one status that means nothing was taken.`,
          `          if (data.status === "expired") {`,
          `            into.replaceChildren(line("comps-topup-note", "That payment was never completed, so nothing was charged."));`,
          `            return;`,
          `          }`,
          `          // failed does NOT mean the card was never charged, so this`,
          `          // says what is known and hands back the reference.`,
          `          if (data.status === "failed") {`,
          `            into.replaceChildren(line("comps-topup-error", "We could not complete that purchase. If your statement shows a charge for it, quote reference " + id + " and we will put it right."));`,
          `            return;`,
          `          }`,
          `          window.setTimeout(ask, 1500);`,
          `        })`,
          // ---- AND THE DEAD END BECOMES A RETRY (1.869.0) ----
          //
          // This replaced the line and RETURNED, so one failed request
          // ended the follow and the buyer was left with "Could not check
          // that payment" over a payment that was very likely fine. Both
          // of the other consumers of this route keep asking, with the
          // same reason written above each: "Transient network hiccup -
          // keep polling rather than giving up on the first failed check."
          //
          // SILENT, as both of those are. The notice already says
          // "Checking your payment", which remains true; a message about
          // the CHECK failing tells the buyer about our plumbing rather
          // than about their money. If every attempt fails they reach the
          // ceiling above, which is the honest terminal statement.
          `        .catch(function () {`,
          `          // Transient network hiccup - keep asking rather than`,
          `          // giving up on the first failed check.`,
          `          window.setTimeout(ask, 1500);`,
          `        });`,
          `    }`,
          `    ask();`,
          `  }`,
          `  // Taken out of the address bar at once, so a refresh does not`,
          `  // ask about a purchase that is long settled.`,
          `  function pendingFromUrl() {`,
          `    var id = new URLSearchParams(window.location.search).get("pendingPurchase");`,
          `    if (!id) return null;`,
          `    var url = new URL(window.location.href);`,
          `    url.searchParams.delete("pendingPurchase");`,
          `    window.history.replaceState({}, "", url.pathname + url.search + url.hash);`,
          `    return id;`,
          `  }`,
          `  var comps589Pending = pendingFromUrl();`,
        ]
      : [
          `  // NOTHING HERE FOLLOWS THE PAYMENT. The competition widget on`,
          `  // this page reads ?pendingPurchase= when the buyer comes back`,
          `  // and says whether it went through; two things watching one`,
          `  // parameter is two answers to one question.`,
        ]),
    `  // The buyer comes back to this tab: the page, then the widget's own`,
    `  // address for it. The provider adds ?pendingPurchase= in front of`,
    `  // the #, which is where a fragment belongs.`,
    `  function comps589TopUpReturn() {`,
    `    // THIS PAGE, WHOLE (1.496.0). The origin and the path alone drop`,
    `    // the query string, so a competition page addressed as`,
    `    // /competitions/?date=2026-09-29 brought the buyer back to a page`,
    `    // that no longer knew which competition they had been looking at.`,
    `    // The competition widget's own purchase has always sent`,
    `    // location.href; this is the same address, with our own tab in the`,
    `    // hash. The server keeps the path, the query AND the fragment when`,
    `    // it adds ?pendingPurchase= on the way back.`,
    `    var here = new URL(window.location.href);`,
    `    here.hash = "account/buy-credit";`,
    `    return here.toString();`,
    `  }`,
    `  return fetch(ctx.baseUrl + "/api/v1/auth/my-credits", { headers: comps589ApiHeaders({ "x-auth-token": ctx.token }) })`,
    `    .then(function (res) {`,
    `      if (!res.ok) throw new Error("balance");`,
    `      return res.json();`,
    `    })`,
    `    .then(function (info) {`,
    `      box.replaceChildren();`,
    ...(follow ? [`      var told = document.createElement("div");`, `      box.appendChild(told);`, `      if (comps589Pending) follow(comps589Pending, told);`] : []),
    `      box.appendChild(line("comps-topup-balance", "You have " + credits(Number(info.credits) || 0) + "."));`,
    `      // Switched off for the whole site: an amount box that always`,
    `      // fails is worse than saying so.`,
    `      if (!info.canTopUp) {`,
    `        box.appendChild(line("comps-empty", "This competition site is not selling entry credit at the moment."));`,
    `        return;`,
    `      }`,
    `      var rate = Number(info.creditsPerMajorUnit) || 0;`,
    `      var floor = Number(info.minCreditTopUpCredits) || 0;`,
    `      var ceiling = Number(info.maxCreditTopUpCredits) || 0;`,
    `      var money = info.currency || "";`,
    `      // The operator's limits are in CREDITS; the buyer types MONEY.`,
    `      // These are the same limits in what they are about to enter.`,
    `      var least = rate > 0 && floor > 0 ? floor / rate : 0;`,
    `      var most = rate > 0 && ceiling > 0 ? ceiling / rate : 0;`,
    `      if (rate > 0) box.appendChild(line("comps-topup-rate", "1 " + money + " buys " + credits(rate) + "."));`,
    `      // BOTH LIMITS, UP FRONT rather than only on refusal: a large`,
    `      // amount allowed because there is no ceiling looks exactly like`,
    `      // one allowed because the check is broken.`,
    `      box.appendChild(`,
    `        line(`,
    `          "comps-topup-limits",`,
    `          (floor > 0 ? "Smallest top-up: " + credits(floor) + " (" + least.toFixed(2) + " " + money + ")." : "No minimum.") +`,
    `            " " +`,
    `            (ceiling > 0 ? "Largest: " + credits(ceiling) + " (" + most.toFixed(2) + " " + money + ")." : "No maximum.")`,
    `        )`,
    `      );`,
    `      var label = document.createElement("label");`,
    `      label.className = "comps-topup-field";`,
    `      var what = document.createElement("span");`,
    `      what.textContent = "Amount to spend" + (money ? " (" + money + ")" : "");`,
    `      var amount = document.createElement("input");`,
    `      amount.type = "number";`,
    `      amount.step = "0.01";`,
    `      amount.min = "0";`,
    `      amount.placeholder = least ? least.toFixed(2) : "5.00";`,
    `      label.appendChild(what);`,
    `      label.appendChild(amount);`,
    `      var preview = line("comps-topup-preview", "");`,
    `      var problem = line("comps-topup-error", "");`,
    `      var go = document.createElement("button");`,
    `      go.type = "button";`,
    `      go.textContent = "Continue to payment";`,
    `      go.disabled = true;`,
    `      box.appendChild(label);`,
    `      box.appendChild(preview);`,
    `      box.appendChild(problem);`,
    `      box.appendChild(go);`,
    `      box.appendChild(`,
    `        line("comps-topup-note", "Payment is taken by the competition provider. You come back here when it is done.")`,
    `      );`,
    `      // CHECKED AS THEY TYPE, and the button stays disabled until it`,
    `      // passes. The provider checks the same limits again - this is so`,
    `      // the buyer finds out before being sent to a payment page.`,
    `      function check() {`,
    `        var spend = Number(amount.value);`,
    `        problem.textContent = "";`,
    `        preview.textContent = "";`,
    `        if (!amount.value.trim()) { go.disabled = true; return; }`,
    `        if (!isFinite(spend) || spend <= 0) {`,
    `          problem.textContent = "Enter an amount.";`,
    `          go.disabled = true;`,
    `          return;`,
    `        }`,
    `        var buying = rate > 0 ? Math.floor(spend * rate) : 0;`,
    `        if (floor > 0 && buying < floor) {`,
    `          problem.textContent = "The smallest top-up is " + credits(floor) + " \\u2014 about " + least.toFixed(2) + " " + money + ".";`,
    `          go.disabled = true;`,
    `          return;`,
    `        }`,
    `        if (ceiling > 0 && buying > ceiling) {`,
    `          problem.textContent = "The largest top-up is " + credits(ceiling) + " \\u2014 about " + most.toFixed(2) + " " + money + ".";`,
    `          go.disabled = true;`,
    `          return;`,
    `        }`,
    `        preview.textContent =`,
    `          spend.toFixed(2) + " " + money + " buys " + credits(buying) + ", giving you " + credits((Number(info.credits) || 0) + buying) + ".";`,
    `        go.disabled = false;`,
    `      }`,
    `      amount.addEventListener("input", check);`,
    `      amount.addEventListener("keydown", function (e) {`,
    `        if (e.key === "Enter" && !go.disabled) go.click();`,
    `      });`,
    `      go.addEventListener("click", function () {`,
    `        go.disabled = true;`,
    `        problem.textContent = "";`,
    `        fetch(ctx.baseUrl + "/api/v1/credits/topup/start-via-partner", {`,
    `          method: "POST",`,
    `          headers: comps589ApiHeaders({ "Content-Type": "application/json", "x-auth-token": ctx.token }),`,
    `          body: JSON.stringify({ amount: Number(amount.value), returnUrl: comps589TopUpReturn() }),`,
    `        })`,
    `          .then(function (res) { return res.json().then(function (data) { return { ok: res.ok, data: data }; }); })`,
    `          .then(function (r) {`,
    `            if (!r.ok || !r.data.redirectUrl) throw new Error(r.data.error || "Could not start payment.");`,
    `            window.location.href = r.data.redirectUrl;`,
    `          })`,
    `          .catch(function (err) {`,
    `            problem.textContent = err.message;`,
    `            go.disabled = false;`,
    `          });`,
    `      });`,
    `    })`,
    `    .catch(function () {`,
    `      box.replaceChildren();`,
    `      box.appendChild(line("comps-topup-error", "Could not load your balance."));`,
    `    });`,
    `}`,
  ].join("\n");
}
// THE GENERATED PAGE AS ONE BLOCK (1.441.0): the markup, then the script
// in a <script> element after it - after the widget files, whose globals it
// uses. Indented two spaces inside the element, blank lines left blank.
//
// A COMPLETE PAGE since 1.446.0. Asked on 22 Sep 2026: "wrap all the code
// in html/body tags so its a complete page ... include a title element etc
// so its fully functioning complete page. All the partner needs to do is
// configure their page before generating it and copying it into their
// website." So: doctype, language, charset, viewport and the title chosen
// in Quick Start, then the body.
// TWO STYLED VERSIONS OF THE PAGE, AND THE RAW ONE (1.461.7). Asked on
// 23 Sep 2026: "can you include two nicely styled versions? A dark one and
// a light one? ... the 3rd one being the raw template with no styling ...
// whatever the choice is that is the one that will be either uploaded or
// zipped up."
//
// WHAT IT STYLES AND WHAT IT LEAVES ALONE. Only the markup Quick Start
// writes: the header and its nav, the sections it makes, the sign-in form,
// the sign-out button. The widgets are not touched - they carry their own
// light/dark/none setting under Colours, and a stylesheet of ours reaching
// into them would fight it.
const QUICK_START_PAGE_TOKENS = {
  // FOUR MORE TOKENS AT 1.511.0, for the hero and the surfaces under it.
  // The originals are unchanged: a partner who has already generated a
  // page and edited the stylesheet keeps every colour they had.
  light: {
    bg: "#f6f7f9", panel: "#ffffff", ink: "#1b1f24", soft: "#5c6672", line: "#dfe3e8",
    accent: "#1a6dd4", accentInk: "#ffffff", errorBg: "#fdecea", errorInk: "#8c1d18", shadow: "0 1px 2px rgba(16, 24, 40, .06), 0 8px 24px rgba(16, 24, 40, .06)",
    // heroFrom IS NOT THE ACCENT (1.511.0). Built from the accent, the
    // light band measured 4.14:1 against white at its lightest point,
    // under the 4.5 the hero paragraph needs at 16px. A deeper blue only
    // for the band; the accent itself is unchanged, so every button and
    // link on the page keeps the colour a partner may already have edited.
    bg2: "#eceff4", accent2: "#3f92e8", heroFrom: "#11477f", heroInk: "#ffffff", heroTint: "rgba(255, 255, 255, .16)",
    // THE WIDGET'S OWN LIVE LIGHT (1.519.0). Not the page accent: green
    // and red mean running and not-running, and a partner who recolours
    // their accent must not accidentally recolour the state of their
    // competition. Both measured against --page-bg-2, which is what the
    // live band sits on - 4.41:1 and 4.72:1 here, 7.37:1 and 4.54:1 in
    // dark, all clear of the 3:1 a non-text mark needs.
    live: "#1a7f37", idle: "#c0392b",
  },
  dark: {
    bg: "#14171c", panel: "#1c2027", ink: "#e8ebef", soft: "#a2acba", line: "#2a3038",
    accent: "#5aa2ff", accentInk: "#0d1117", errorBg: "#3a1f1d", errorInk: "#ffb4ab", shadow: "0 1px 2px rgba(0, 0, 0, .3), 0 8px 24px rgba(0, 0, 0, .35)",
    // THE DARK HERO TAKES DARK INK (1.511.0). Its gradient is built from
    // this theme's accents, and a dark theme's accents are LIGHT - they
    // are meant to sit ON the near-black page. Near-white text on that
    // band measured 2.0:1 against the rendered pixels, which fails even
    // the large-text bar; dark ink on the same band measures 7.7:1.
    bg2: "#1a1e25", accent2: "#7cc0ff", heroFrom: "#5aa2ff", heroInk: "#0d1117", heroTint: "rgba(0, 0, 0, .14)",
    // LIGHTER ON A DARK PAGE, for the same reason every dark-theme accent
    // is: these sit ON near-black rather than on white.
    live: "#35c46a", idle: "#e2574c",
  },
};
function quickStartPageCss(mode) {
  const t = QUICK_START_PAGE_TOKENS[mode];
  if (!t) return "";
  return [
    `/* ${QUICK_START_CSS_FILE} - the ${mode} look for the page Quick Start`,
    `   generated. A starting point, not a house style: none of it is`,
    `   needed for the competition to work, and every rule here is yours`,
    `   to change.`,
    ``,
    `   GENERATED. Generating the page again writes this file again, so`,
    `   put your own rules in a stylesheet of your own, linked after this`,
    `   one - they will win on equal specificity and survive the next`,
    `   generate.`,
    ``,
    `   IT DOES NOT TOUCH THE WIDGETS. They carry their own colours, set`,
    `   in Quick Start under "Colours"; every rule below is anchored to`,
    `   the markup Quick Start writes. Nothing inside either widget is`,
    `   yours to select. */`,
    ":root {",
    `  --page-bg: ${t.bg};`,
    `  --page-panel: ${t.panel};`,
    `  --page-ink: ${t.ink};`,
    `  --page-soft: ${t.soft};`,
    `  --page-line: ${t.line};`,
    `  --page-accent: ${t.accent};`,
    `  --page-accent-ink: ${t.accentInk};`,
    `  --page-error-bg: ${t.errorBg};`,
    `  --page-error-ink: ${t.errorInk};`,
    `  --page-shadow: ${t.shadow};`,
    `  --page-bg-2: ${t.bg2};`,
    `  --page-accent-2: ${t.accent2};`,
    `  --hero-from: ${t.heroFrom};`,
    `  --hero-ink: ${t.heroInk};`,
    `  --hero-tint: ${t.heroTint};`,
    `  --page-live: ${t.live};`,
    `  --page-idle: ${t.idle};`,
    "  --page-radius: 10px;",
    "  --page-width: 960px;",
    // THE RAIL'S WIDTH AND THE GAP BESIDE IT (1.522.0). TWO grids read
    // these: the board below, which holds the heading, the live band and
    // the competition, and .comps-layout inside your saved layout, which
    // holds the picture and the cards. They are different pieces of
    // markup in different DOM trees and nothing else could keep them
    // lined up - change these two and both follow.
    "  --page-rail: 300px;",
    "  --page-gap: 20px;",
    "}",
    `html { color-scheme: ${mode}; }`,
    "body {",
    "  margin: 0;",
    // A SHORT VIEW STILL ENDS AT THE BOTTOM (1.471.0). The Winners view
    // is one table, and without this the footer sat halfway up an empty
    // page - which reads as a page that stopped rather than one that
    // ended, the thing the footer was added to prevent.
    "  min-height: 100vh;",
    "  display: flex;",
    "  flex-direction: column;",
    "  background: var(--page-bg);",
    "  color: var(--page-ink);",
    '  font: 16px/1.55 system-ui, -apple-system, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;',
    "  -webkit-text-size-adjust: 100%;",
    "}",
    "",
    "/* The bar across the top, and everything under it in one column. */",
    "body > header {",
    "  background: var(--page-panel);",
    "  border-bottom: 1px solid var(--page-line);",
    "}",
    "body > header nav {",
    "  width: min(var(--page-width), 100% - 2rem);",
    "  margin: 0 auto;",
    "  display: flex;",
    "  align-items: center;",
    "  min-height: 56px;",
    "}",
    // SCOPED TO THE PAGE'S OWN LIST (1.474.0). Reported on 26 Sep 2026
    // with a screenshot of the account menu laid out two items per row:
    // "The My Account dropdown should be list of pages, one per row."
    //
    // The widget's dropdown is a <ul class="c589n-menu"> that it appends
    // INSIDE the header, so `body > header ul` matched it and turned its
    // own block items into a wrapping flex row. Same for the link rules
    // below, including aria-current, which fought the widget's own.
    //
    // This stylesheet already says it does not touch the widgets - they
    // carry their own light/dark/none setting under Colours. The header
    // rules were the place it did so without noticing. Every one of them
    // is now anchored to > nav >, which only the markup Quick Start
    // writes can match.
    "body > header > nav > ul {",
    "  list-style: none;",
    "  display: flex;",
    "  flex-wrap: wrap;",
    "  gap: 1.25rem;",
    "  margin: 0;",
    "  padding: 0;",
    "  width: 100%;",
    "}",
    // THE RIGHT-HAND END OF THE BAR (1.471.0). It used to be whichever
    // item happened to be last; it is the account item, so that Sign out
    // - added beside it on 25 Sep 2026 - lands to its right rather than
    // pushing it back into the left-hand group.
    "body > header > nav > ul > li[data-589comps-account] { margin-left: auto; }",
    "body > header > nav > ul > li > a, body > header > nav > .site-name {",
    "  color: var(--page-ink);",
    "  text-decoration: none;",
    "  font-weight: 600;",
    "  padding: 6px 2px;",
    "  border-bottom: 2px solid transparent;",
    "}",
    "body > header > nav > ul > li > a:hover, body > header > nav > ul > li > a:focus-visible { border-bottom-color: var(--page-accent); }",
    "",
    "body > *:not(header):not(script) {",
    "  width: min(var(--page-width), 100% - 2rem);",
    "  margin: 1.5rem auto;",
    "}",
    "",
    "/* The sections Quick Start writes. The widgets inside them bring",
    "   their own look. */",
    "body > section {",
    "  background: var(--page-panel);",
    "  border: 1px solid var(--page-line);",
    "  border-radius: var(--page-radius);",
    "  box-shadow: var(--page-shadow);",
    "  padding: 1.25rem 1.25rem 1.5rem;",
    "}",
    "h1, h2, h3 { line-height: 1.25; margin: 0 0 .6rem; }",
    "h1 { font-size: 1.75rem; }",
    "h2 { font-size: 1.25rem; }",
    "h3 { font-size: 1rem; color: var(--page-soft); text-transform: uppercase; letter-spacing: .04em; }",
    "",
    "/* Sign in and sign up, side by side where there is room. */",
    "#comps-signin form {",
    "  display: grid;",
    "  gap: .75rem;",
    "  align-content: start;",
    "}",
    // HIDDEN MUST STILL MEAN HIDDEN (1.467.0). Found in a screenshot of
    // the light template: a signed-out visitor was shown the sign-in form
    // AND a Sign out button on a fresh load. Both elements are written
    // with the `hidden` attribute and revealed by the page's own script -
    // but `hidden` is only a default of display:none, and every rule
    // below sets display, which beats it. The unstyled template was
    // right all along, which is why this never showed up there.
    //
    // This is also half of what was reported on 23 Sep 2026: "there is a
    // sign out button which would suggest the user is logged in". The
    // other half - the widgets never being told about the sign-in - was
    // 1.465.0.
    "[hidden] { display: none !important; }",
    "#comps-signin {",
    "  display: grid;",
    "  gap: 1.5rem;",
    "  grid-template-columns: 1fr;",
    "}",
    "@media (min-width: 720px) {",
    "  #comps-signin { grid-template-columns: 1fr 1fr; }",
    "  #comps-signin > h2, #comps-signin > p { grid-column: 1 / -1; }",
    "}",
    "#comps-signin label { display: grid; gap: .3rem; font-size: .9rem; color: var(--page-soft); }",
    "/* Buttons size to their words, rather than stretching across the",
    "   column they happen to sit in. */",
    "#comps-signin button { justify-self: start; }",
    "#comps-signin input {",
    "  font: inherit;",
    "  padding: .6rem .7rem;",
    "  color: var(--page-ink);",
    "  background: var(--page-bg);",
    "  border: 1px solid var(--page-line);",
    "  border-radius: 8px;",
    "}",
    "#comps-signin input:focus-visible { outline: 2px solid var(--page-accent); outline-offset: 1px; }",
    "#comps-signin-error {",
    "  background: var(--page-error-bg);",
    "  color: var(--page-error-ink);",
    "  border-radius: 8px;",
    "  padding: .6rem .8rem;",
    "  margin: 0;",
    "}",
    "",
    "button, #comps-signin button, #comps-signout {",
    "  font: inherit;",
    "  font-weight: 600;",
    "  cursor: pointer;",
    "  padding: .6rem 1.1rem;",
    "  border-radius: 8px;",
    "  border: 1px solid transparent;",
    "  background: var(--page-accent);",
    "  color: var(--page-accent-ink);",
    "}",
    "#comps-signout {",
    "  background: transparent;",
    "  color: var(--page-soft);",
    "  border-color: var(--page-line);",
    "  padding: .35rem .85rem;",
    "  font-size: .9rem;",
    "  /* In the bar beside My account since 1.471.0; still a body child on",
    "     a page with no navigation, where the column rule would stretch it. */",
    "  width: auto;",
    "  display: block;",
    "}",
    "#comps-signout:hover { color: var(--page-ink); border-color: var(--page-accent); filter: none; }",
    "button:hover { filter: brightness(1.08); }",
    "button:focus-visible { outline: 2px solid var(--page-accent); outline-offset: 2px; }",
    "",
    "/* ---- A COMPLETE PAGE (1.467.0) ----",
    "   Asked for on 23 Sep 2026: the light and dark templates as complete",
    "   pages, \"nicely styled and presented so a prospective partner can",
    "   get an idea of what their site could look like using one of these",
    "   templates\". The markup is the same in all three - only this",
    "   stylesheet differs - so what follows is the part that turns it",
    "   from a column of widgets into something that reads as a site. */",
    "",
    "/* The site's name, at the left of the bar. */",
    ".site-name {",
    "  font-weight: 700;",
    "  font-size: 1.05rem;",
    "  letter-spacing: -0.01em;",
    "  color: var(--page-ink);",
    "  text-decoration: none;",
    "  margin-right: 1.5rem;",
    "  white-space: nowrap;",
    "}",
    ".site-name:hover { color: var(--page-accent); }",
    "",
    "/* The bar itself sits above everything and stays put while you read. */",
    "body > header {",
    "  position: sticky;",
    "  top: 0;",
    "  z-index: 10;",
    "  backdrop-filter: blur(6px);",
    "}",
    "body > header > nav > ul > li > a { color: var(--page-soft); text-decoration: none; }",
    "body > header > nav > ul > li > a:hover { color: var(--page-ink); }",
    "/* Which view you are on (1.471.0): Home or Winners. */",
    'body > header > nav > ul > li > a[aria-current="page"] {',
    "  color: var(--page-ink);",
    "  border-bottom-color: var(--page-accent);",
    "}",
    "",
    "/* ---- THE WINNERS TABLE (1.471.0) ----",
    "   Asked for on 25 Sep 2026 with the Winners view: a list of winners",
    "   and a link to the replay for each competition. Nine fields as one",
    "   long line of Label: value each was the shape the request arrived",
    "   with a screenshot of. */",
    ".comps-winners { width: 100%; border-collapse: collapse; font-size: .95rem; }",
    ".comps-winners th {",
    "  text-align: left;",
    "  font-size: .72rem;",
    "  text-transform: uppercase;",
    "  letter-spacing: .05em;",
    "  font-weight: 600;",
    "  color: var(--page-soft);",
    "  white-space: nowrap;",
    "  padding: .45rem .7rem;",
    "  border-bottom: 1px solid var(--page-line);",
    "}",
    '.comps-winners td { padding: .6rem .7rem; border-bottom: 1px solid var(--page-line); }',
    '.comps-winners tbody tr:last-child td { border-bottom: 0; }',
    '.comps-winners tbody tr:hover { background: var(--page-bg); }',
    "/* Nine columns on a phone scroll sideways rather than squashing into",
    "   one letter each. */",
    '[data-comps-group="winners"] { overflow-x: auto; }',
    "",
    "/* ---- THE FIELD CARDS (1.475.0) ----",
    '   Asked for on 26 Sep 2026: "can we make the cards/data below look',
    '   presentable to a prospective partner?" Each section was a run of',
    '   "Label: value" lines - this is the same markup as two columns,',
    "   with the label quiet and the value doing the talking. */",
    // AN auto TRACK TAKES THE WHOLE ROW (1.529.1). Reported 28 Sep 2026
    // of the Competition Image card, which drew its label one letter per
    // line with the value painted across it.
    //
    // THE VALUE COLUMN WAS `auto`, whose growth limit is its content's
    // MAX-content width - so free space goes to it first and the label's
    // `minmax(0, 1fr)` gets what is left. Measured in Chromium at 1440px
    // with that card's between-competitions value (a 118-character
    // sentence): the computed tracks were `0px 606px`. A zero-width
    // track does not clip, so the label overflowed it and the value was
    // drawn on top.
    //
    // NOT minmax(0, auto), WHICH IS THE SAME THING. The floor was never
    // the problem; the MAX is. Measured: `minmax(0, 1fr) minmax(0, auto)`
    // and `minmax(0, max-content)` both still compute `0px 606px`.
    //
    // fit-content(60%) CAPS IT AND CHANGES NOTHING ELSE. Below the cap a
    // fit-content track is still its content's width, so every row that
    // fitted before is laid out to the same pixel - measured on ordinary
    // values (531/75) and on a long label with a short value, both
    // unchanged. Only a value that would have taken the whole row is
    // held, at 60%, leaving the label 40%.
    //
    // THE SAME FAULT IS FIXED IN .comps-record AND .comps-feed-row
    // BELOW, and `quick-start-generated-css.test.mjs` now refuses any
    // track in this stylesheet that can grow to its max-content beside a
    // neighbour - this is the fourth time it has been found by hand.
    ".comps-fields { display: grid; grid-template-columns: minmax(0, 1fr) fit-content(60%); margin: 0; }",
    ".comps-fields dt {",
    "  color: var(--page-soft);",
    "  font-size: .92rem;",
    "  padding: .5rem .9rem .5rem 0;",
    "  border-bottom: 1px solid var(--page-line);",
    "  min-width: 0;",
    "}",
    ".comps-fields dd {",
    "  margin: 0;",
    "  font-weight: 600;",
    "  text-align: right;",
    "  padding: .5rem 0;",
    "  border-bottom: 1px solid var(--page-line);",
    "  /* A URL or an id is one long word; let it break rather than push",
    "     the card wider than the page. */",
    "  overflow-wrap: anywhere;",
    "}",
    ".comps-fields dt:last-of-type, .comps-fields dd:last-of-type { border-bottom: 0; }",
    "/* THE REVEALED PICTURE (1.489.0). The cap on its size is set on the",
    "   element itself, because the unstyled template has no stylesheet to",
    "   carry one; what is here is the look, which that template has none",
    "   of by design. */",
    ".comps-fields dd img {",
    "  display: inline-block;",
    "  border-radius: var(--page-radius);",
    "  border: 1px solid var(--page-line);",
    "  vertical-align: middle;",
    "}",
    "",
    "/* ---- A RECORD WITH A PICTURE IN IT (1.525.0) --------------------",
    "",
    '   Asked for on 28 Sep 2026: "can you make this a nice looking,',
    '   presentable card so it looks visually appealing" - of Last Result,',
    "   where the revealed image sat in the value column of a two-column",
    "   list with an empty label beside it and a tall band of dead space",
    "   under the label.",
    "",
    "   The picture leads the card now, above the fields, with its label",
    "   as the caption so nothing the operator chose to show is lost.",
    "   Only a group carrying a picture gets this; everything else is the",
    "   same list it was.",
    "",
    "   AND IT IS SMALL (1.527.0), the same 300px as the rail beside the",
    "   competition. It had the card's full width, which on a wide page",
    "   made the revealed image the biggest thing there. The cap is on",
    "   the element - the unstyled template has no stylesheet - and this",
    "   rule only dresses it. */",
    "/* THE PICTURE BESIDE THE FIELDS, NOT ABOVE THEM (1.527.0). Stacked,",
    "   a 226px picture and seven rows made the shortest possible card",
    "   623px tall - which is not what \"small and doesn't take up much",
    "   space\" looks like. Side by side it is the height of whichever is",
    "   taller, and it reads as a result rather than a list under a",
    "   photograph. auto, not 300px: the column is exactly as wide as the",
    "   picture turns out to be, so a portrait one does not leave a strip",
    "   of empty rail beside it. */",
/* AND CAPPED AT THE PICTURE'S OWN WIDTH (1.529.1). `auto` here was the
   same fault as in .comps-fields, one card over: the figure's caption is
   the operator's own label, and a long one grew this track past the
   picture it was captioning. Measured with a 66-character caption: the
   tracks came out 520px for the picture and 66px for every field beside
   it. fit-content(300px) is the cap the <img> already carries, so the
   column can still be exactly as wide as a narrow picture and can never
   be wider than a wide one. */
    ".comps-record { display: grid; grid-template-columns: fit-content(300px) minmax(0, 1fr); gap: 1rem 1.25rem; align-items: start; }",
    ".comps-shot { margin: 0; display: grid; gap: .45rem; justify-items: center; }",
    ".comps-shot img {",
    "  display: block;",
    "  /* NO width HERE. The element carries max-width/max-height and",
    "     width: auto, which keeps the picture's own proportions with or",
    "     without this stylesheet; a width declared here would be",
    "     overridden by that inline auto anyway and only mislead. */",
    "  border-radius: var(--page-radius);",
    "  border: 1px solid var(--page-line);",
    "  background: var(--page-bg-2);",
    "}",
    ".comps-shot figcaption {",
    "  font-size: .72rem;",
    "  letter-spacing: .08em;",
    "  text-transform: uppercase;",
    "  color: var(--page-soft);",
    "}",
    "/* An identifier is something you copy, not something you read: quiet,",
    "   monospaced, and out of the way of the values that carry meaning. */",
    ".comps-fields dd.comps-ref {",
    "  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;",
    "  font-size: .78rem;",
    "  font-weight: 500;",
    "  color: var(--page-soft);",
    "  letter-spacing: 0;",
    "}",
    "/* One column where two will not fit: the label above its value,",
    "   which is what a <dl> does unstyled anyway. The record stacks at",
    "   the same width, for the same reason - a 300px picture and a",
    "   label/value list will not share a phone. */",
    "@media (max-width: 560px) {",
    "  .comps-record { grid-template-columns: minmax(0, 1fr); }",
    "  .comps-fields { grid-template-columns: 1fr; }",
    "  .comps-fields dt { border-bottom: 0; padding-bottom: 0; }",
    "  .comps-fields dd { text-align: left; padding-top: .1rem; }",
    "}",
    '.comps-empty { margin: 0; color: var(--page-soft); }',

    "",
    "/* THE BUY CREDIT TAB (1.476.0). It is drawn INSIDE the account",
    "   widget, so it takes the widget's own colours where the widget sets",
    "   them (--c589n-*, from its Colours setting) and the page's where it",
    "   does not - which is Colours: None, where the partner styles the",
    "   widget themselves. Either way the tab matches what it sits in",
    "   rather than fighting it. */",
    ".comps-topup { display: grid; gap: .55rem; }",
    ".comps-topup p { margin: 0; }",
    ".comps-topup-balance { font-weight: 600; }",
    ".comps-topup-rate,",
    ".comps-topup-limits,",
    ".comps-topup-note {",
    "  color: var(--c589n-muted, var(--page-soft));",
    "  font-size: .88rem;",
    "}",
    ".comps-topup-field { display: grid; gap: .25rem; font-size: .92rem; }",
    ".comps-topup-field input {",
    "  font: inherit;",
    "  /* A money box, not a paragraph: wide enough for an amount and no",
    "     wider, whatever the section it is drawn in is. */",
    "  max-width: 12rem;",
    "  padding: .5rem .6rem;",
    "  border-radius: 8px;",
    "  border: 1px solid var(--c589n-line, var(--page-line));",
    "  background: var(--c589n-field-bg, var(--page-bg));",
    "  color: inherit;",
    "}",
    ".comps-topup button {",
    "  font: inherit;",
    "  justify-self: start;",
    "  padding: .5rem 1rem;",
    "  border: 0;",
    "  border-radius: 8px;",
    "  cursor: pointer;",
    "  background: var(--c589n-accent, var(--page-accent));",
    "  color: var(--c589n-bg, var(--page-accent-ink));",
    "}",
    "/* A DISABLED BUTTON HAS TO LOOK DISABLED: it starts that way, and",
    "   stays that way until the amount passes the operator's limits. */",
    ".comps-topup button:disabled { opacity: .5; cursor: not-allowed; }",
    ".comps-topup-error { color: var(--c589n-out, var(--page-error-ink)); font-size: .88rem; }",
    ".comps-topup-ok { color: var(--c589n-in, var(--page-ink)); font-size: .92rem; font-weight: 600; }",

    "",
    "/* Each widget and each field section reads as a card on the page. */",
    "body > section, body > div[id^=\"comps-\"] {",
    "  background: var(--page-panel);",
    "  border: 1px solid var(--page-line);",
    "  border-radius: var(--page-radius);",
    "  box-shadow: var(--page-shadow);",
    "  padding: 1.1rem 1.25rem;",
    "}",
    // AN EMPTY CARD IS STILL A CARD (1.511.0). The div rule has been here
    // since the widgets got containers; the account target is a <section>,
    // so it was never covered - and it is empty until a visitor opens an
    // account page. Every generated page in header style has therefore
    // carried a blank white bar between the bar and the first real block.
    // Measured on a generated page: 37px tall, bordered, above the hero.
    "body > section:empty, body > div[id^=\"comps-\"]:empty { display: none; }",
    "body > section > h2 {",
    "  margin: 0 0 .6rem;",
    "  font-size: 1rem;",
    "  letter-spacing: .04em;",
    "  text-transform: uppercase;",
    "  color: var(--page-soft);",
    "}",
    "",
    "/* And the page ends rather than stopping. */",
    // :last-of-type IS NOT DECORATION (1.471.0). `body > *:not(header)
    // :not(script)` above counts three type selectors and this counted
    // two, so the margin shorthand there beat this rule's margin-top -
    // which is why the 2.5rem written here at 1.467.0 never once showed
    // up on a page. The pseudo-class carries the specificity that makes
    // the line below mean something.
    "body > footer:last-of-type {",
    "  border-top: 1px solid var(--page-line);",
    "  /* auto rather than a gap: the footer takes whatever height is left",
    "     over, so it ends the page on a short view - one table, say - and",
    "     follows the content on a long one, where the section above it",
    "     brings its own margin. */",
    "  margin-top: auto;",
    "  padding: 1.5rem 0 2.5rem;",
    "  color: var(--page-soft);",
    "  text-align: center;",
    "}",
    "body > footer p { margin: .2rem 0; }",
    "",
    "/* ------------------------------------------------------------------",
    "   THE EXAMPLE SITE (1.511.0).",
    "",
    "   Asked for on 27 Sep 2026: the light and dark templates as complete",
    "   example websites, so a partner can see what the competitions would",
    "   look like on a proper website.",
    "",
    "   EVERYTHING BELOW IS ADDED, NOT REWRITTEN. The rules above carry",
    "   fixes that cost real time to find - the header rules anchored",
    "   `> nav >` because the widget injects its own <ul> into the header,",
    "   the footer's margin-top that has to out-specify the width rule,",
    "   `.comps-fields` as the ACCOUNT details grid rather than a stat row.",
    "   Layering on top keeps all of that; a rewrite would have quietly",
    "   undone it.",
    "",
    "   New classes are `comps-` prefixed like the rest, so pasting this",
    "   page into a site that already has a .card or a .hero collides with",
    "   nothing. ------------------------------------------------------ */",
    "",
    "/* Type. One size fitted every heading; a site needs a scale, and one",
    "   that holds up from a phone to a wide monitor. */",
    "h1 { font-size: clamp(2rem, 1.2rem + 3.2vw, 3.3rem); font-weight: 780; letter-spacing: -.02em; line-height: 1.12; }",
    "h2 { font-size: clamp(1.3rem, 1.05rem + 1vw, 1.8rem); font-weight: 700; letter-spacing: -.015em; }",
    "body { font-family: ui-sans-serif, system-ui, -apple-system, \"Segoe UI\", Inter, Roboto, Helvetica, Arial, sans-serif; -webkit-font-smoothing: antialiased; }",
    "/* A webfont would look sharper still. Left commented because it puts",
    "   a third-party request on your page, which is your call rather than",
    "   ours - uncomment it and add the family to the stack above.",
    "   @import url(\"https://fonts.googleapis.com/css2?family=Inter:wght@400;500;700&display=swap\"); */",
    "",
    "/* The bar stays with you, and the name reads as a wordmark. */",
    "body > header { position: sticky; top: 0; z-index: 20; }",
    "body > header > nav > .site-name { display: inline-flex; align-items: center; gap: .55rem; font-size: 1.1rem; }",
    "body > header > nav > .site-name::before {",
    "  content: \"\";",
    "  width: 1.5rem; height: 1.5rem; border-radius: 7px;",
    "  background: linear-gradient(135deg, var(--page-accent), var(--page-accent-2));",
    "  box-shadow: inset 0 0 0 1px rgba(255, 255, 255, .18);",
    "}",
    "",
    "/* THE HERO IS THE ONE FULL-BLEED THING, so it has to opt out of the",
    "   width rule above by name. A class beats any number of type",
    "   selectors, so this wins wherever it sits in the file - unlike the",
    "   footer's margin-top above, which had to be moved. */",
    "body > .comps-hero {",
    "  width: 100%; max-width: none; margin: 0;",
    "  background:",
        // AND THE GLOW AT 32%, for the same reason: it lands on the top
    // right of the band, which was the lightest point on it.
    "    radial-gradient(90rem 40rem at 78% -20%, color-mix(in srgb, var(--page-accent-2) 32%, transparent), transparent 60%),",
        // 45%, NOT 70% (1.511.0): at 70 the far end of the band measured
    // 3.9:1 against white, and the hero paragraph is 16px at its
    // smallest, which wants 4.5. Measured again at 45.
    "    linear-gradient(135deg, var(--hero-from), color-mix(in srgb, var(--page-accent-2) 45%, var(--hero-from)));",
    "  color: var(--hero-ink);",
    "  border: 0; border-radius: 0; box-shadow: none;",
    "}",
    ".comps-hero-inner {",
    "  width: min(var(--page-width), 100% - 2rem);",
    "  margin-inline: auto;",
    "  padding: clamp(2.5rem, 1rem + 6vw, 4.75rem) 0 clamp(3rem, 1rem + 7vw, 5.25rem);",
    "}",
    ".comps-hero h1 { max-width: 16ch; margin-bottom: .75rem; }",
    ".comps-hero p { font-size: clamp(1rem, .9rem + .5vw, 1.2rem); max-width: 52ch; opacity: .93; margin-bottom: 1.5rem; }",
    "/* THE LIGHT (1.521.0) - green while a competition is running, red when",
    "   none is.",
    "",
    "   AN ATTRIBUTE, NOT A CLASS (1.532.0). The status widget sets",
    "   data-state=\"live\" | \"idle\" | \"maintenance\" on whichever element",
    "   carries data-589comps-status, and invents no class of its own -",
    "   a widget that added classes to your markup would be choosing your",
    "   names for you. Until it has answered there is no attribute at all",
    "   and the dot is grey, because a light that guesses is worse than",
    "   one that waits.",
    "",
    "   THE STATE IS NOT SCOPED TO ONE CARRIER. It was .comps-eyebrow, the",
    "   hero pill, and the band over the competition carries .comps-live",
    "   instead - so its light stayed grey while the pill's worked. The",
    "   attribute goes on any element and the dot inside it follows. The",
    "   pill itself went at 1.548.0; the rule outlived it because it was",
    "   never about that one carrier.",
    "",
    "   THE RING IS NOT DECORATION, AND IT MOVED WITH THE DOT (1.548.0).",
    "   It was --hero-ink, measured against the hero pill: a tinted",
    "   lozenge over the hero gradient, where a bare dot came out at",
    "   1.06:1. The dot lives only in the band now, and --hero-ink against",
    "   the band measures 1.15:1 in light and 1.13:1 in dark - a ring",
    "   nobody can see. --page-ink is the band's own text colour and",
    "   measures 14.4:1 and 14.0:1 on the same surface, so the light still",
    "   reads as a light against whatever you recolour the band to. */",
    ".comps-live-dot {",
    "  width: .6rem; height: .6rem; border-radius: 50%; flex: none;",
    "  background: var(--page-soft);",
    // OUTLINE, NOT BORDER OR BOX-SHADOW: a border would eat into the
    // colour, and the pulse below animates box-shadow.
    "  outline: 2px solid var(--page-ink); outline-offset: 0;",
    "}",
    '[data-state="live"] .comps-live-dot {',
    "  background: var(--page-live);",
    "  animation: comps-pulse 2.4s ease-out infinite;",
    "}",
    '[data-state="idle"] .comps-live-dot { background: var(--page-idle); }',
    "@keyframes comps-pulse {",
    "  0% { box-shadow: 0 0 0 0 var(--page-live); }",
    "  70% { box-shadow: 0 0 0 .45rem transparent; }",
    "  100% { box-shadow: 0 0 0 0 transparent; }",
    "}",
    "@media (prefers-reduced-motion: reduce) {",
    '  [data-state="live"] .comps-live-dot { animation: none; }',
    "}",
    "",
    "/* THE COUNTDOWN, to this competition's finish while one runs and to",
    "   the next one starting while none does. Hidden until there is",
    "   something real to count to - no date, no clock, rather than an",
    "   invented one.",
    "",
    "   IT LIVES IN THE BAND NOW (1.535.0), and only there. The hero had a",
    "   second copy of it until three clocks on one page was one too many;",
    "   .comps-hero-count went with the markup, and these two classes",
    "   stayed because the band's clock is built from them. */",
    ".comps-count-label { font-size: .85rem; opacity: .85; }",
    ".comps-count-value {",
    "  font-size: clamp(1.3rem, 1.1rem + .9vw, 1.9rem); font-weight: 700;",
    "  line-height: 1.1; font-variant-numeric: tabular-nums;",
    "}",
    ".comps-cta {",
    "  display: inline-flex; align-items: center; gap: .5rem;",
    "  padding: .75rem 1.3rem; margin-right: .6rem; border: 0; border-radius: 999px;",
    "  background: var(--page-panel); color: var(--page-accent);",
    "  font: inherit; font-weight: 680; text-decoration: none; cursor: pointer;",
    "  box-shadow: 0 6px 20px rgba(0, 0, 0, .18);",
    "}",
    ".comps-cta.comps-quiet { background: var(--hero-tint); color: var(--hero-ink); box-shadow: none; }",
    "",
    "/* The competition lifted onto the hero rather than sitting under it.",
    "",
    "   IT LIFTS THE BOARD NOW, NOT THE COMPETITION INSIDE IT (1.522.0).",
    "   This was a negative margin on .comps-stage, from when the",
    "   competition sat directly under the banner. With a heading and a",
    "   live band above it the margin pulled the competition up OVER the",
    "   band and drew across it - found by measuring the two boxes, not by",
    "   looking, because both are dark and the overlap read as a gap.",
    "",
    "   And it only applies where it means something: immediately after",
    "   the banner. Delete the banner - which the board does not need -",
    "   and nothing is lifted onto nothing. */",
    ".comps-stage { position: relative; z-index: 1; }",
    ".comps-hero + .comps-board { margin-top: clamp(-3.5rem, -2rem - 3vw, -2.25rem); position: relative; z-index: 1; }",
    "",
    "/* ---- THE BOARD (1.522.0) --------------------------------------",
    "",
    "   The competition, the heading over it and the live band between",
    "   them. Asked for on 28 Sep 2026 with a screenshot of exactly this,",
    "   pasted in by hand: \"This is the layout I want to see on the test",
    "   and live sites.\"",
    "",
    "   TWO GRIDS, ONE RAIL WIDTH. This one holds the heading, the band",
    "   and the competition; .comps-layout below - the markup in your",
    "   saved layout - holds the picture and the cards. Both read",
    "   --page-rail and --page-gap, which is the only thing keeping the",
    "   band lined up with the picture rather than running on over the",
    "   cards. */",
    ".comps-board {",
    "  display: grid;",
    "  grid-template-columns: minmax(0, 1fr) var(--page-rail);",
    "  gap: 0 var(--page-gap);",
    "  align-items: start;",
    "}",
    "/* The band belongs to the picture's column. The competition spans",
    "   both, because its own layout splits them again.",
    "",
    "   ROWS ARE STATED, NOT INFERRED. Left to auto-placement the",
    "   competition landed in the same row as the band and drew straight",
    "   over it - a full-width item and a column-pinned one do not queue",
    "   the way they read. Two items, two rows, nothing to reason about.",
    "",
    "   THERE WERE THREE until 1.535.0, when the heading we wrote over the",
    "   competition went: \"remove this from the templates, I didn't",
    "   request this.\" Its rule and the two classes inside it went with",
    "   the markup. */",
    ".comps-live { grid-column: 1; grid-row: 1; }",
    ".comps-board > [id^=\"comps-\"] { grid-column: 1 / -1; grid-row: 2; }",
    "",
    "/* THE LIVE BAND. It is joined to the picture's frame below it - one",
    "   object with a lid - so the radius and the bottom border go. */",
    ".comps-live {",
    "  display: flex; align-items: center; gap: .65rem; flex-wrap: wrap;",
    "  margin: 0; padding: .8rem 1.1rem;",
    "  background: var(--page-bg-2);",
    "  border: 1px solid var(--page-line);",
    "  border-bottom: 0;",
    "  border-radius: var(--page-radius) var(--page-radius) 0 0;",
    "  color: var(--page-ink);",
    "}",
    ".comps-live-text { font-weight: 700; font-size: .78rem; letter-spacing: .08em; text-transform: uppercase; }",
    ".comps-live-title { color: var(--page-soft); font-size: .85rem; min-width: 0; overflow-wrap: anywhere; }",
    "/* THE ROTATING FACTS. A divider before them rather than a character",
    "   in the markup, so an empty span leaves no stray pipe behind - and",
    "   a minimum width so the band does not jump about as each one is",
    "   typed. 11em holds the longest of them at this size. */",
    ".comps-live-facts {",
    "  font-size: .85rem; color: var(--page-ink); font-variant-numeric: tabular-nums;",
    "  white-space: nowrap;",
    "}",
    ".comps-live-facts:not(:empty) {",
    "  min-width: 11em;",
    "  padding-left: .65rem; margin-left: .15rem;",
    "  border-left: 1px solid var(--page-line);",
    "}",
    ".comps-live-count { display: flex; align-items: baseline; gap: .5rem; margin-left: auto; }",
    ".comps-live-count[hidden] { display: none; }",
    ".comps-live .comps-count-label { font-size: .78rem; color: var(--page-soft); }",
    ".comps-live .comps-count-value { font-size: 1.15rem; font-weight: 700; color: var(--page-accent); font-variant-numeric: tabular-nums; }",
    "",
    "/* ---- THE SAVED LAYOUT'S OWN MARKUP ------------------------------",
    "",
    "   Everything from here down styles the markup in your Widget Layout,",
    "   which Quick Start generates to match this stylesheet. Change one",
    "   and change the other. None of it reaches inside either widget. */",
    // THE PAGE'S OWN INK, DECLARED RATHER THAN INHERITED (1.591.0).
    //
    // Reported 4 Oct 2026 with a screenshot of the Competition Status
    // card: "the values are there but aren't displayed correctly because
    // of the text colour". The labels read and the values did not.
    //
    // COUNTED OFF THAT SCREENSHOT, not estimated from it: 2,412 pixels of
    // #1c1e26 - the COMPETITION WIDGET's light --rcw-fg - against 145,064
    // of #1c2027, which is this stylesheet's own --page-panel. 1.02:1.
    // --page-ink appeared in that card at zero pixels, which is what
    // ruled out every explanation that did not involve the widget.
    //
    // The widget draws this markup whenever a layout is saved, and
    // .rcw-root{color:var(--rcw-fg)} colours everything nested inside it.
    // .comps-k declares var(--page-soft), so the labels survived;
    // .comps-v declared nothing and took the widget's ink. That asymmetry
    // was the entire report, and it is the tell for this whole class of
    // fault: what survives is whatever happened to declare a colour.
    //
    // ONE DECLARATION, AT THE TEMPLATE'S ROOT. templateHtmlFromLayout
    // always opens with this element, so the cards, the feed rows, the
    // tiles, the winners table and the account fields all inherit from
    // here instead of from whatever Colours is set to. Twenty-odd rules
    // below declare no colour of their own; patching .comps-v would have
    // fixed the one row that was screenshotted and left the rest exposed.
    //
    // IT STILL DOES NOT REACH INSIDE EITHER WIDGET, which is the rule
    // this section opens with. It sets a colour on a comps- class of our
    // own. The widgets' elements declare their own colours and go on
    // following Colours - right, because they are the widget's markup.
    //
    // A NO-OP WHERE IT ALREADY WORKED: body sets this exact value, so
    // with Colours: none nothing moves by a pixel. Only the themed cases
    // change, and both were unreadable - 1.02:1 for light on the dark
    // template, 1.20:1 for dark on the light one.
    ".comps-layout {",
    "  /* Your page's own ink, set here so a widget's Colours setting",
    "     cannot repaint your markup. Change it if you want it to. */",
    "  color: var(--page-ink);",
    "  display: grid;",
    "  grid-template-columns: minmax(0, 1fr) var(--page-rail);",
    "  gap: var(--page-gap);",
    "  align-items: start;",
    "}",
    ".comps-layout-main { min-width: 0; }",
    "",
    "/* The picture, sitting under the band as one object. */",
    ".comps-layout-frame {",
    "  background: var(--page-panel);",
    "  border: 1px solid var(--page-line);",
    "  border-radius: 0 0 var(--page-radius) var(--page-radius);",
    "  padding: .9rem;",
    "  overflow: hidden;",
    "}",
    // minmax(0, 1fr) AND NOT NOTHING AT ALL (1.524.0). This was a grid
    // with no column declared, so its single implicit column was `auto`
    // - sized to the widest card's max-content and bursting straight out
    // of the 300px rail. Reported 28 Sep 2026 with a screenshot: the
    // counts card ran off the right of the page, in the stacked layout
    // as well as the slider. min-width: 0 on the container was not
    // enough; the track itself had to be told it may shrink.
    ".comps-layout-cards { display: grid; grid-template-columns: minmax(0, 1fr); gap: .9rem; min-width: 0; align-content: start; }",
    "",
    ".comps-card {",
    "  background: var(--page-panel);",
    "  border: 1px solid var(--page-line);",
    "  border-radius: var(--page-radius);",
    "  padding: .9rem 1rem;",
    "}",
    ".comps-card-title {",
    "  margin: 0 0 .65rem; font-size: .72rem; font-weight: 700;",
    "  letter-spacing: .12em; text-transform: uppercase; color: var(--page-soft);",
    "}",
    "",
    "/* label on the left, value on the right */",
    ".comps-facts { list-style: none; margin: 0; padding: 0; }",
    ".comps-fact {",
    "  display: flex; justify-content: space-between; align-items: baseline;",
    "  gap: .75rem; padding: .38rem 0; border-bottom: 1px solid var(--page-line);",
    "  font-size: .85rem;",
    "}",
    ".comps-fact:last-child { border-bottom: 0; }",
    ".comps-k { color: var(--page-soft); }",
    ".comps-v { text-align: right; overflow-wrap: anywhere; }",
    "",
    "/* the counts */",
    // THE SAME FAULT ONE LEVEL DOWN: 1fr is minmax(auto, 1fr), and auto
    // will not go below its content's min-content width - so a long
    // count or a long label pushed the three tiles wider than the card.
    ".comps-tiles { list-style: none; margin: 0; padding: 0; display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: .5rem; }",
    ".comps-tile { background: var(--page-bg-2); border-radius: calc(var(--page-radius) - 4px); padding: .65rem .35rem; text-align: center; }",
    // A PLACEHOLDER IS ONE LONG WORD. The Quick Start preview draws the
    // template unfilled - {{images_revealed}} has nowhere to break - and
    // a real count never needs this.
    ".comps-tile-n { display: block; font-size: 1.3rem; line-height: 1.1; color: var(--page-accent); font-variant-numeric: tabular-nums; overflow-wrap: anywhere; }",
    ".comps-tile-l { display: block; margin-top: .2rem; font-size: .62rem; letter-spacing: .04em; text-transform: uppercase; color: var(--page-soft); }",
    "",
    "/* the feeds */",
    ".comps-feed { list-style: none; margin: 0; padding: 0; }",
    ".comps-feed-row {",
    // fit-content, NOT minmax(0, auto) (1.529.1): an auto MAXIMUM grows
    // to max-content whatever its floor says, so one long unbroken
    // username took the row. Measured: 482px for the name and 116px for
    // what they did. The cap is 40% - a name is the smaller half of the
    // line it sits on.
    "  display: grid; grid-template-columns: fit-content(40%) minmax(0, 1fr);",
    "  gap: .1rem .5rem; padding: .5rem 0;",
    "  border-bottom: 1px solid var(--page-line); font-size: .82rem;",
    "}",
    ".comps-feed-row:last-child { border-bottom: 0; }",
    ".comps-who { font-weight: 600; overflow-wrap: anywhere; }",
    ".comps-what { color: var(--page-soft); overflow-wrap: anywhere; }",
    ".comps-when { grid-column: 1 / -1; font-size: .72rem; color: var(--page-soft); opacity: .85; }",
    "",
    "",
    "/* ---- A SLIDER IS A ROW, NOT A RAIL (1.524.0) --------------------",
    "",
    "   Asked for on 28 Sep 2026: \"When the slider option is selected it",
    "   should appear directly under the competition area.\"",
    "",
    "   The rail is 300px wide because a stack of cards reads well there.",
    "   A slider does not: it is one card at a time with arrows on its",
    "   edges, and in a 300px column it was both cramped and - because the",
    "   widget sizes it from its content - spilling off the page.",
    "",
    "   So when the cards are a slider the grid drops to ONE column: the",
    "   picture takes the full width, the band above it follows, and the",
    "   slider sits directly beneath. The board is matched as well as the",
    "   layout because the two grids are meant to stay in step - see",
    "   --page-rail.",
    "",
    "   is-slider IS THE WIDGET'S OWN CONTRACT CLASS, not a new name: the",
    "   generated markup carries it, and a partner who writes their own",
    "   markup adds or removes it to the same effect. It is only ever used",
    "   here compounded with a comps- class, so it cannot catch a class of",
    "   the partner's that happens to share the name.",
    "",
    "   NO MARKUP CHANGED FOR THIS. A template already saved gets the new",
    "   arrangement on its next page load, with no regenerate and nothing",
    "   orphaned. Where :has() is not supported the cards stay in the rail,",
    "   which is exactly what they do today. */",
    ".comps-board:has(.comps-layout-cards.is-slider),",
    ".comps-layout:has(> .comps-layout-cards.is-slider) {",
    "  grid-template-columns: minmax(0, 1fr);",
    "}",
    "",
    "/* ONE BREAKPOINT, AND IT IS ABOUT THE RAIL. Below this the cards go",
    "   under the picture instead of beside it. It touches nothing in the",
    "   header - where a width cannot be chosen, because it depends on how",
    "   long the partner's site name is. */",
    "@media (max-width: 56rem) {",
    "  .comps-board, .comps-layout { grid-template-columns: minmax(0, 1fr); }",
    "}",
    "",
    "/* How it works, and anything else wanting even columns. */",
    ".comps-grid { display: grid; gap: 1rem; grid-template-columns: repeat(auto-fit, minmax(15rem, 1fr)); }",
    ".comps-step { background: var(--page-bg-2); border: 1px solid var(--page-line); border-radius: var(--page-radius); padding: 1.15rem; }",
    ".comps-step h3 { color: var(--page-ink); text-transform: none; letter-spacing: normal; font-size: 1.02rem; margin-bottom: .35rem; }",
    ".comps-step p { margin: 0; color: var(--page-soft); }",
    ".comps-step-n {",
    "  display: inline-grid; place-items: center; width: 2rem; height: 2rem; margin-bottom: .65rem;",
    "  border-radius: 8px; font-weight: 700; font-size: .9rem;",
    // SOLID, NOT A TINT (1.511.0). A 14% tint behind accent-coloured text
    // measured 3.61:1 on the light template, and the accent on the card
    // itself is only 4.36 - so no tint of it would have reached 4.5 for
    // 0.9rem text. Filled, it is 4.55 light and 8.9 dark, and it reads
    // better as a step marker.
    "  background: var(--page-accent); color: var(--page-accent-ink);",
    "}",
    ".comps-lede { color: var(--page-soft); max-width: 60ch; margin: -.2rem 0 1.3rem; }",
    "",
    "/* The footer reads as the end of a site rather than a line of text. */",
    "body > footer:last-of-type { background: var(--page-bg-2); }",
    "",
    // WIDTH MEANS WIDTH (1.511.0). Every block on this page sets a width
    // AND a padding, and without border-box the padding is added to it:
    // the card rule's 1.25rem each side made each block 40px wider than
    // it asked for. At 390px that pushed them 10px past the viewport, and
    // the hero - which is width:100% - pushed the whole page 40px past it
    // at every width. Measured: scrollWidth 1320 in a 1280 viewport.
    //
    // NOT A `*` RESET. This file says every rule in it is anchored to the
    // markup Quick Start writes, and a partner may have their own markup
    // on the same page. The widget containers are listed WITHOUT their
    // descendants for the same reason: both widgets already set
    // border-box inside their own roots, and neither root has a
    // horizontal padding for this to change.
    "body > header, body > header *,",
    "body > .comps-hero, body > .comps-hero *,",
    "body > section, body > section *,",
    "body > div[id^=\"comps-\"],",
    "body > footer, body > footer * {",
    "  box-sizing: border-box;",
    "}",
    "",
    // THE BAR ON A PHONE (1.511.0). The nav is a flex row holding the site
    // name and a <ul> that asks for width: 100%, so at 390px the list was
    // squeezed into whatever the name left over and its three items stood
    // one above the other at the right-hand edge: a 140px-tall header with
    // the name floating beside a column of links, and the page scrolling
    // 10px sideways. Measured in a browser at 390px before and after:
    // 140px tall and 10px of sideways scroll, then 90px and none.
    //
    // NO BREAKPOINT. Where the bar has to wrap depends on how long the
    // partner's site name is, which this file cannot know - "Ace" and "The
    // Great Northern Weekly Prize Company Limited" are both site names. A
    // basis of 18rem lets the list ask for a sensible width and drop to
    // its own row when there is not one, at whatever width that happens.
    //
    // AND THE NAME MAY WRAP. It has been white-space: nowrap since
    // 1.467.0, which on a phone pushed a long name straight off the side
    // of the page - 207px of sideways scroll, measured with the name
    // above. It has its own row down there, so there is nothing left for
    // nowrap to protect.
    "body > header > nav { flex-wrap: wrap; row-gap: .35rem; padding-block: .45rem; }",
    "body > header > nav > .site-name { white-space: normal; min-width: 0; }",
    "body > header > nav > ul { flex: 1 1 18rem; width: auto; }",
  ].join("\n");
}

// ---- THE PARTNER'S OWN STYLESHEET (1.592.0) ------------------------
//
// Asked for on 4 Oct 2026, after a Colours setting was found repainting
// a partner's own markup: "if the partner wants their own styling on
// their template they should be able to do it ... Provide example CSS
// which lists all the elements they can style but leave the body of the
// css elements empty so the partner can add that."
//
// NAMED AFTER THEIR SITE, so a file sitting in public_html says whose it
// is: "589 Comps" gives 589-comps.css.
function quickStartOwnCssFile(title) {
  const slug = String(title || "")
    // The title is written into the page as HTML, so it can carry markup
    // and entities. Neither belongs in a filename.
    .replace(/<[^>]*>/g, " ")
    .replace(/&[a-z]+;|&#\d+;/gi, " ")
    // "Cafe" with an accent should keep its letter, not lose it.
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    // A filename is read by a person: cap it, and never end on a hyphen.
    .slice(0, 48)
    .replace(/-+$/g, "");
  // A title made entirely of punctuation leaves nothing to name it after.
  if (!slug) return "your-styles.css";
  // AND IT MUST NOT BE THE GENERATED SHEET. A site called "589comps"
  // slugs straight onto the file Quick Start rewrites on every generate,
  // which would hand the partner a file that eats their own work.
  const name = `${slug}.css`;
  return name === QUICK_START_CSS_FILE ? `${slug}-styles.css` : name;
}

// DERIVED FROM THE STYLESHEET, NEVER LISTED. A hand-kept list of
// selectors goes stale the first time a rule is added, and the partner
// would have no way of knowing which one was missing. This reads the
// sheet Quick Start just generated, so the two cannot disagree.
//
// EMPTY BODIES ON PURPOSE: an empty rule does nothing at all, so a
// partner deletes what they do not want and fills in what they do,
// without having to work out which declarations the page relies on.
//
// WHAT IS LEFT OUT: @keyframes, because an empty one is invalid and a
// partner restyling a page is not rewriting its animations, and @import.
// WHAT IS KEPT: :root, because overriding the tokens there is the
// shortest way to restyle everything, and @media wrappers, because a
// rule that applies at one width only needs its wrapper to mean
// anything.
function quickStartOverrideCss(css) {
  const lines = [];
  const seen = new Set();
  let buf = "";
  let depth = 0;
  let atOpen = false;
  let skipAt = 0;
  let pending = null;

  for (let i = 0; i < css.length; i += 1) {
    if (css[i] === "/" && css[i + 1] === "*") {
      const end = css.indexOf("*/", i + 2);
      const body = css.slice(i + 2, end < 0 ? css.length : end);
      // Only the banner form, never ordinary prose: a comment's first
      // line is not a section heading just because it is a sentence.
      const h = body.match(/^\s*-{3,}\s*([^\n]+?)\s*-{3,}/);
      if (h && depth === 0 && !skipAt) pending = h[1].trim().replace(/\s*-+$/, "");
      i = end < 0 ? css.length : end + 1;
      buf = "";
      continue;
    }
    const ch = css[i];
    if (ch === ";" && depth === 0) { buf = ""; continue; }
    if (ch === "{") {
      depth += 1;
      const sel = buf.trim().replace(/\s+/g, " ");
      buf = "";
      if (skipAt) continue;
      if (depth === 1) {
        if (/^@keyframes/i.test(sel)) { skipAt = depth; continue; }
        if (pending) { lines.push("", `/* ---- ${pending} ---- */`); pending = null; }
        if (sel.startsWith("@")) { lines.push("", `${sel} {`); atOpen = true; continue; }
        if (!seen.has(`0|${sel}`)) { seen.add(`0|${sel}`); lines.push(`${sel} {`, "}"); }
      } else if (atOpen && depth === 2 && !seen.has(`1|${sel}`)) {
        seen.add(`1|${sel}`);
        lines.push(`  ${sel} {`, "  }");
      }
      continue;
    }
    if (ch === "}") {
      depth -= 1;
      buf = "";
      if (skipAt && depth < skipAt) { skipAt = 0; continue; }
      if (skipAt) continue;
      if (atOpen && depth === 0) { atOpen = false; lines.push("}"); }
      continue;
    }
    buf += ch;
  }
  return `${lines.join("\n").replace(/\n{3,}/g, "\n\n").trim()}\n`;
}

// The header is the partner's instructions, so it says what an empty
// rule does, where the file goes, and the one thing that is NOT in it.
function quickStartOwnCssFileBody(css, ownFile) {
  return [
    `/* ${ownFile} - everything on your page you can change.`,
    "",
    `   LINK IT AFTER ${QUICK_START_CSS_FILE}. Generating the page again`,
    "   rewrites that file; it never touches this one, so your changes",
    "   live here.",
    "",
    "   EVERY RULE BELOW IS EMPTY ON PURPOSE. An empty rule does nothing",
    "   at all, so delete the ones you do not want and fill in the ones",
    "   you do. The selectors are taken from the stylesheet Quick Start",
    "   just built, so this list cannot fall behind it.",
    "",
    "   THE SHORTEST WAY TO RESTYLE THE WHOLE PAGE is :root, at the top:",
    "   every colour, radius and width on the page reads from those.",
    "",
    "   WHAT IS NOT HERE: the two widgets' own markup. They carry their",
    "   own Colours setting - set it to None and they take no colours,",
    "   borders or backgrounds from us, leaving their rcw- classes and",
    "   --rcw-* variables yours to style. A widget adds its stylesheet",
    "   when it starts, which is after this file loads, so a rule of",
    "   yours aimed at an rcw- class has to out-rank it rather than",
    "   merely follow it. Nothing below has that problem: the widgets",
    "   never style these classes. */",
    "",
    quickStartOverrideCss(css),
  ].join("\n");
}

// A VERSION ON THE LINK, NOT ON THE FILE (1.477.1).
//
// Found on 589compstest.com on 26 Sep 2026, an hour after a publish: the
// Buy credit tab was on the page and unstyled, because the browser was
// pairing a brand-new index.html with the stylesheet it had cached
// BEFORE the publish - the host serves 589comps.css with
// `Cache-Control: public, max-age=604800`, which is seven days. Measured
// rather than guessed at: the cached copy was 9,046 bytes with no
// .comps-topup rules, the one on the server 10,731 bytes with twelve,
// published one second after the page.
//
// THE FILE'S NAME CANNOT CHANGE. The partner links it from their other
// pages, and a publish overwrites by name - a hashed filename would
// leave a trail of dead stylesheets on their server and break the links
// they wrote by hand. So the version goes on the LINK, where only the
// generated page reads it.
//
// FNV-1a, 32 bits, base 36: small, synchronous (SubtleCrypto is not) and
// deterministic. It is not guarding against anybody - it only has to
// change when the file changes and stay put when it does not, which is
// exactly what a cache needs to hear.
function quickStartCssVersion(css) {
  let h = 0x811c9dc5;
  for (let i = 0; i < css.length; i += 1) {
    h ^= css.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return (h >>> 0).toString(36);
}
// `inlineCss` IS FOR THE PREVIEW ONLY (1.475.1). The page links
// 589comps.css, which is right for a file that will sit beside it on the
// partner's server - and wrong for the preview, an <iframe srcDoc> with
// no server behind it, where a relative href resolves against the admin
// panel and 404s. Reported 26 Sep 2026: "the light and dark previews
// don't look like they're styled". The preview shows the page as it will
// look once both files are up; the box beside it, and the zip, still
// carry the real one.
// A VALUE GOING INTO A DOUBLE-QUOTED ATTRIBUTE (1.592.0). Not a URL
// validator: the partner's own href is theirs to choose, and the only
// thing that is ours is that it cannot close the attribute and start
// writing tags. "<" goes too, so a value can never open one even if an
// earlier quote were ever mishandled.
function htmlAttr(value) {
  return String(value == null ? "" : value)
    .replace(/&/g, "&amp;")
    .replace(/"/g, "&quot;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

function combineQuickStartCode(html, js, title = "Competition", style = "none", { inlineCss = false, own = null } = {}) {
  const indent = (text, by) =>
    text
      .split("\n")
      .map((line) => (line ? by + line : line))
      .join("\n");
  const safeTitle = String(title || "Competition")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
  const css = quickStartPageCss(style);
  // The note at the top of the markup belongs to the version being built.
  const body = css
    ? html.replace(
        "     No styling: every class and layout choice is yours. -->",
        `     The ${style} look comes from ${QUICK_START_CSS_FILE} beside this file:\n` +
          "     change or delete any of it. Nothing there is needed for the\n" +
          "     competition to work. -->"
      )
    : html;
  return [
    "<!DOCTYPE html>",
    '<html lang="en">',
    "<head>",
    '  <meta charset="utf-8">',
    '  <meta name="viewport" content="width=device-width, initial-scale=1">',
    `  <title>${safeTitle}</title>`,
    // LINKED, NOT INLINED (1.475.0): one file, so a partner can edit it
    // without editing the page, share it with their other pages, and
    // override it from a stylesheet of their own after it.
    ...(css
      ? inlineCss
        ? ["  <style>", indent(css, "    "), "  </style>"]
        : [`  <link rel="stylesheet" href="${QUICK_START_CSS_FILE}?v=${quickStartCssVersion(css)}">`]
      : []),
    // THE PARTNER'S OWN, AFTER OURS (1.592.0). Last wins on equal
    // specificity, so this is the whole mechanism: everything above is
    // ours and rewritten on every generate, everything here is theirs
    // and never touched. A file they link, or written into the page -
    // their choice, because a partner without somewhere to put a second
    // file still wants to change a colour.
    //
    // THE HREF IS ESCAPED, NOT VALIDATED. It is their own page and their
    // own server, so where the file lives is not ours to rule on; what
    // is ours is that a quote in it cannot break out of the attribute
    // and write markup into their page.
    ...(own && own.mode === "file" && String(own.href || "").trim()
      ? [`  <link rel="stylesheet" href="${htmlAttr(own.href)}">`]
      : own && own.mode === "inline" && String(own.css || "").trim()
        ? ["  <style>", indent(String(own.css), "    "), "  </style>"]
        : []),
    "</head>",
    "<body>",
    indent(body, "  "),
    "",
    "  <script>",
    indent(js, "    "),
    "  </script>",
    "</body>",
    "</html>",
  ].join("\n");
}
// Whether either site's saved layout shows the cards as a slider - the
// tick box's own, or an is-slider element in its markup. Null until read.
function savedLayoutHasSlider(data) {
  if (!data) return null;
  return [data.layout, data.layoutTest].some(
    (l) => !!l && (l.infoCardsLayout === "slider" || /\bis-slider\b/.test(l.html || ""))
  );
}
// WHETHER THE WIDGET WILL BE DRAWING THE PARTNER'S OWN MARKUP (1.489.0).
//
// A saved template is parsed by the widget and mounted INSIDE its root,
// which then carries `rcw-has-template` - so `.rcw-root{color:var(--rcw-fg)}`
// applies to the partner's sections as well as to the widget's own cards.
// Without a template the widget draws only its own markup, where its
// colours are nobody else's business.
//
// NOT KEYED ON THE SLIDER, though that is how it was found: is-slider only
// decides how the template's slots are arranged. Any saved template is
// drawn inside the widget.
function savedLayoutHasTemplate(data) {
  if (!data) return false;
  return [data.layout, data.layoutTest].some((l) => !!l && !!String(l.html || "").trim());
}
function CompetitionWidgetOptions({ value, set, canEdit, layoutSummary, layoutHasSlider }) {
  const radios = [
    {
      key: "theme",
      name: "Colours",
      options: [
        { id: "light", label: "Light" },
        { id: "dark", label: "Dark" },
        { id: "none", label: "None" },
      ],
    },
    {
      key: "sliderAdvanceSeconds",
      name: "Card slider: next card",
      options: [
        { id: 0, label: "Off" },
        { id: 10, label: "10 s" },
        { id: 6, label: "6 s" },
        { id: 3, label: "3 s" },
      ],
    },
    {
      key: "sliderCardScroll",
      name: "Card slider: scroll a long card",
      options: [
        { id: "off", label: "Off" },
        { id: "slow", label: "Slow" },
        { id: "medium", label: "Medium" },
        { id: "fast", label: "Fast" },
      ],
    },
    {
      key: "pollIntervalMs",
      name: "Checks for changes",
      options: [
        { id: 1000, label: "1 s" },
        { id: 2000, label: "2 s" },
        { id: 5000, label: "5 s" },
      ],
    },
  ];
  const ticks = [
    { key: "loginHandler", label: "Send signed-out visitors to your sign-in" },
    { key: "hooks", label: "Tell your page about purchases and guesses" },
    { key: "showSimulationEndedNotice", label: "Say when a demo has ended" },
    { key: "countdown", label: "Count down to the finish, and to the next competition" },
  ];
  return (
    <div
      style={{ margin: "4px 0 6px 22px", fontSize: 12, display: "grid", gap: 4 }}
      aria-label="Competition widget options"
    >
      {radios.map((g) => (
        <React.Fragment key={g.key}>
          <div role="radiogroup" aria-label={g.name} style={{ display: "flex", gap: "2px 12px", flexWrap: "wrap", alignItems: "center" }}>
            <span style={{ opacity: 0.7, flexBasis: "100%" }}>{g.name}</span>
            {g.options.map((o) => (
              <label key={o.id} style={{ display: "inline-flex", gap: 4, alignItems: "center" }}>
                <input
                  type="radio"
                  name={`quick-start-competition-${g.key}`}
                  checked={value[g.key] === o.id}
                  onChange={() => set(g.key, o.id)}
                  disabled={!canEdit}
                />
                {o.label}
              </label>
            ))}
          </div>
          {g.key === "sliderCardScroll" && (
            <span style={{ opacity: 0.7 }}>
              Both pause while the visitor is using the slider, and a card still scrolling is not replaced mid-way.
              {layoutHasSlider === false && " The saved layout stacks the cards, so these apply once it uses a slider."}
            </span>
          )}
          {g.key === "theme" && value.theme === "none" && (
            <span style={{ opacity: 0.7 }}>
              No colours, borders or backgrounds of ours: the partner styles the <code>rcw-</code> classes or sets
              the <code>--rcw-*</code> variables.
            </span>
          )}
        </React.Fragment>
      ))}
      <span style={{ opacity: 0.7, marginTop: 2 }}>In the code</span>
      {ticks.map((t) => (
        <label key={t.key} style={{ display: "flex", gap: 4, alignItems: "center" }}>
          <input type="checkbox" checked={!!value[t.key]} onChange={() => set(t.key, !value[t.key])} disabled={!canEdit} />
          {t.label}
        </label>
      ))}
      <span style={{ opacity: 0.7, marginTop: 2 }}>Sections (saved to the site, not the code)</span>
      {!layoutSummary ? (
        <span style={{ opacity: 0.6 }}>Reading the saved layout…</span>
      ) : (
        layoutSummary.map((l) => (
          <span key={l.site || "one"}>
            {l.site && <strong>{l.site}: </strong>}
            {l.text}
          </span>
        ))
      )}
      <a
        href="#widget-layout-editor"
        onClick={(e) => {
          e.preventDefault();
          document.getElementById("widget-layout-editor")?.scrollIntoView({ behavior: "smooth", block: "start" });
        }}
        style={{ color: "var(--accent-blue)" }}
      >
        Change the sections below
      </a>
    </div>
  );
}
// WHAT THE NAMES IN THIS GROUP WILL LOOK LIKE (1.517.0).
//
// Template Data already marks a ticked field the API has not enabled -
// "off in the API", in yellow - because a screen that lets you choose
// something governed elsewhere has to say so. Masking was governed
// elsewhere and said nothing.
//
// WHICH FIELDS: whichever ones the REGISTRY says carry a masked name
// (`maskedBy`), not a list kept here. A tenth winners field that
// carried a username would be covered the day it is added.
//
// THE COLOUR IS ON THE UNMASKED CASE, not the masked one, which is the
// opposite way round to "off in the API" and deliberate. Masked is the
// default and the safe one; real names on a third party's page is the
// state worth catching an operator's eye, and this panel is where those
// fields are being chosen.
function QuickStartMaskNote({ group, unmasked }) {
  const setting = (group.fields || []).map((f) => f.maskedBy).find(Boolean);
  if (!setting) return null;
  const on = !!unmasked[setting];
  // Latest Entries masks a second thing: with it off no guess text is
  // sent at all, which is a missing field rather than a shortened one.
  const alsoGuesses = setting === "lastEntriesUnmasked";
  return (
    <p
      data-quick-start-mask-note={setting}
      style={{
        fontSize: 12,
        margin: "8px 0 0",
        color: on ? "var(--accent-yellow)" : undefined,
        opacity: on ? 1 : 0.7,
      }}
    >
      {on ? (
        <>
          Real usernames{alsoGuesses ? " and guess text are" : " are"} sent — these fields carry the name the
          account actually uses. Change it under the API tab, {group.groupLabel}.
        </>
      ) : (
        <>
          Usernames are masked (&ldquo;J***&rdquo;){alsoGuesses ? " and no guess text is sent" : ""}. Turn that off
          under the API tab, {group.groupLabel}.
        </>
      )}
    </p>
  );
}

// WHAT THE LIVE BAND READS (1.524.0).
//
// Reported 28 Sep 2026, of the countdown shipped the day before: "I
// don't see this." Nothing was broken - the clock reads Finishes (ISO
// 8601), every API field is opt-in, and that one was not ticked. So the
// band drew everything it had and stayed quiet about the rest, which is
// the right behaviour on a partner's page and useless on this one.
//
// THE PAGE THAT ASKS FOR THE BAND IS WHERE THE REQUIREMENT BELONGS. It
// was written in the integration guide and in the comments of the
// generated page, neither of which is open while somebody is ticking
// boxes here.
//
// ONE LIST, AND A TEST HOLDS IT TO THE REGISTRY. Every id below is
// asserted to be a real field in server.js - see quick-start-band.test.mjs.
// A renamed field fails that test rather than quietly marking a box
// nobody can find.
const QUICK_START_BAND_FIELDS = [
  // Required: without it the status widget writes nothing at all - it
  // has been told nothing, so it says nothing (1.532.0). This one is not
  // "the light", it is the whole band.
  { id: "competition-status.status", label: "Status", needed: "the band at all - without it nothing below is drawn", required: true },
  { id: "competition-status.finishes_iso", label: "Finishes (ISO 8601)", needed: 'the "Ends in" countdown while a competition runs' },
  { id: "competition-details.title", label: "Title", needed: "the competition's name" },
  { id: "competition-details.prize_money", label: "Prize money", needed: 'the "Prize" fact' },
  { id: "competition-details.price_per_pixel", label: "Price per pixel", needed: 'the "per pixel" fact' },
  { id: "competition-details.entry_price", label: "Entry cost (credits)", needed: 'the "credits/entry" fact' },
  { id: "competition-details.credits_per_major_unit", label: "Credits bought per unit of currency", needed: "the credits-per-unit fact" },
  { id: "competition-details.currency_symbol", label: "Currency symbol", needed: "the symbol in front of the money facts" },
];

// WHETHER THE API IS SERVING THEM, read from the same `groups` response
// the field picker reads - so this panel keeps no second copy of what is
// switched on. `enabled` here means SELECTED, which is what the operator
// is about to change; the public route checks the master switch itself.
function quickStartBandMissing(groups) {
  const served = new Set(
    (groups || []).flatMap((g) => (g.fields || []).filter((f) => f.enabled).map((f) => `${g.group}.${f.key}`))
  );
  return QUICK_START_BAND_FIELDS.filter((f) => !served.has(f.id));
}

// THE SAME SHAPE AS THE REPLAY NOTE above: marked, not enforced. These
// are API settings and this is not the API tab, so the note says what is
// missing and where to change it rather than reaching across and
// changing it. Nothing here is a reason to stop generating the page -
// the band degrades field by field and always draws what it has.
function QuickStartBandNote({ groups }) {
  // NOT LOADED IS NOT "ALL OFF". The panel below only renders this once
  // the field list has arrived, but a component that reports eight
  // missing fields when handed null is one caller away from crying wolf
  // on every page load - so it answers for itself rather than trusting
  // the guard around it. Caught by rendering it with null, not by
  // reading it.
  if (!Array.isArray(groups)) return null;
  const missing = quickStartBandMissing(groups);
  if (!missing.length) return null;
  const one = missing.length === 1;
  const blind = missing.some((f) => f.required);
  return (
    <div
      data-quick-start-band-needs={missing.map((f) => f.id).join(" ")}
      style={{ fontSize: 12, color: "var(--accent-yellow)", margin: "2px 0 0 22px" }}
    >
      The live band above the competition reads the public API, and{" "}
      {one ? "one field it uses is" : `${missing.length} fields it uses are`} not switched on
      under the API tab, Competition Status and Competition Details:
      <ul style={{ margin: "4px 0 0", paddingLeft: 18 }}>
        {missing.map((f) => (
          <li key={f.id}>
            {f.label} — {f.needed}
          </li>
        ))}
      </ul>
      {blind ? (
        <p style={{ margin: "4px 0 0" }}>
          Status is the one that matters: without it the band draws nothing at all, whatever else is ticked.
        </p>
      ) : (
        <p style={{ margin: "4px 0 0" }}>
          The band still works without {one ? "it" : "them"} — {one ? "that field drops" : "each missing field drops"} its own
          part and the rest carries on.
        </p>
      )}
    </div>
  );
}

function PartnerQuickStartPanel({ token, canEdit }) {
  const [groups, setGroups] = useState(null);
  const [picked, setPicked] = useState({});
  // WHETHER THE NAMES THESE FIELDS CARRY ARE REAL (1.517.0).
  //
  // Reported 28 Sep 2026: "Show real usernames (unmasked) is an option
  // on the API but its not an option for widget data." The SETTING
  // already governs the widget - every winners row, entry and purchase
  // is built by one function that reads it, whoever asked - but the
  // screen where a partner's fields are chosen said nothing, so you
  // could tick Winner here with no way of knowing it comes out "J***".
  //
  // The three flags arrive on the same response as the field list, and
  // each field says which of them governs it (`maskedBy` in server.js),
  // so this panel keeps no list of its own.
  const [unmasked, setUnmasked] = useState({});
  // BOTH WIDGETS TICKED TO START WITH (1.419.0) — asked for on 21 Sep
  // 2026. A partner page without the competition widget has nothing to
  // play, so leaving it unticked made the default output the wrong one.
  const [widgets, setWidgets] = useState({ competition: true, replay: true, account: true, status: true });
  // The account navigation widget's two choices (1.428.0), which the
  // preview below and the generated code both follow.
  const [navStyle, setNavStyle] = useState("modal");
  const [navTheme, setNavTheme] = useState("light");
  // Header style only: the widget's drop-down, or a plain list for the
  // partner's own CSS (1.430.0).
  const [navMenu, setNavMenu] = useState("dropdown");
  // THE BUY CREDIT TAB (1.476.0), asked for on 26 Sep 2026: "589comps.com
  // has the facility to Buy credit, can you make this available on the
  // my account section on quick start page templates?"
  //
  // TICKED TO START WITH, for the same reason the widgets are: this was
  // asked for, and a page generated without touching anything should have
  // it. A site not selling entry credit gets a note beside the box rather
  // than a silently different default - what the site sells today is not
  // what it sells next month, and the tab says so itself either way.
  const [buyCreditTab, setBuyCreditTab] = useState(true);
  // THE COMPETITION WIDGET'S OWN CHOICES (1.439.0). Asked on 22 Sep 2026:
  // "Can options/features be selectable for the competition widget? So the
  // partner has full control over the configuration." Every one is a
  // mount() option, so each lands in the generated code; the defaults are
  // what the widget does with the option left out.
  const [comp, setComp] = useState(COMPETITION_WIDGET_DEFAULTS);
  const [status, setStatus] = useState(STATUS_WIDGET_DEFAULTS);
  const setStatusWord = (key, value) => setStatus((v) => ({ ...v, [key]: value }));
  const setWaitingLine = (n, value) =>
    setStatus((v) => ({ ...v, waiting: v.waiting.map((line, i) => (i === n ? value : line)) }));
  // WHERE THE PARTNER'S SERVER KEEPS THE KEY (1.442.0) - see
  // sessionScriptPhp. Per site since 1.447.0 - one card for each site on
  // the key (quickStartSites).
  const [signIn, setSignIn] = useState(QUICK_START_SIGN_IN_DEFAULTS);
  // "BEFORE YOU START" AND THE SIGNED-IN VISITOR (1.447.0) - see
  // lib/quick-start-checklist.js on the server.
  const [checklist, setChecklist] = useState(null);
  // TRUE ONCE THE SAVED PAGE OPTIONS HAVE BEEN READ (1.503.0). The effect
  // below saves on every change, and without this its first run - which
  // React fires on mount, before the load has answered - would write the
  // DEFAULTS over whatever was stored. A ref rather than state: it must
  // not cause a render, and the effect must see the new value on the very
  // next run rather than one render later.
  const pageReady = useRef(false);
  // WHO GETS TO SET `picked`, AND WHEN (1.528.0).
  //
  // Reported 28 Sep 2026: "I tried saving Widget Data but the checkboxes
  // were displayed checked after they were unchecked and the page
  // refreshed."
  //
  // TWO EFFECTS SET IT AND THEY RACE. One reads the saved page through
  // quickStartChecklist; the other reads the API's field list and
  // pre-ticks whatever the API serves, which is the right DEFAULT for a
  // partner who has never saved. They are separate round trips, so
  // whichever answered last won - and the pre-tick answering last put
  // every box back.
  //
  // WORSE THAN A DISPLAY BUG. The autosave effect fires on any change to
  // `picked` once pageReady is set, so the defaults landing late were
  // then WRITTEN BACK over the stored page: a refresh did not just look
  // wrong, it undid the save.
  //
  // SO NEITHER APPLIES ALONE. Each records its answer and the seed runs
  // when both have arrived - saved page if there is one, defaults if
  // there is not - exactly once, whatever order they return in. A later
  // reload (after a save, with a seed) finds it done and leaves the
  // boxes alone, which is also what stops a reload wiping an edit that
  // has not been saved yet.
  const pickedSeed = useRef({ done: false, haveDefaults: false, defaults: {}, haveSaved: false, saved: null });
  const seedPicked = () => {
    const s = pickedSeed.current;
    if (s.done || !s.haveDefaults || !s.haveSaved) return;
    s.done = true;
    // `{}` is a saved page with everything unticked, and it is truthy -
    // which is the whole point. Only a partner who has never saved gets
    // the defaults.
    setPicked(s.saved || s.defaults);
  };
  // THE PREVIEW OPENS ON THE TEMPLATE IN USE (1.530.0). Asked for on 28
  // Sep 2026: "if there is a template selected with the Use this one
  // button then have that template open/selected so the one in use shows
  // on page load."
  //
  // ONCE, NOT ON EVERY LOAD. loadChecklist runs again after every site
  // save, and re-seeding there would move the tab out from under
  // somebody who had opened another template to compare - the 1.450.0
  // fault, and the 1.528.0 one. A ref rather than state: it must not
  // cause a render.
  //
  // DECLARED BELOW seedPicked, NOT BESIDE pickedSeed, and that is not
  // tidiness: quick-start-picked-seed.test.mjs lifts the slice from
  // `const pickedSeed = useRef({` to the end of seedPicked and swaps
  // useRef for a stub. `String.replace` takes the FIRST match, so a
  // second useRef inside that slice went through unswapped and every
  // test in that file died on "useRef is not defined". The test now
  // swaps them all; this stays out of the slice anyway, because a lift
  // that depends on what happens to sit inside it is a trap either way.
  const previewSeeded = useRef(false);
  // ONE DRAFT PER SITE (1.449.0): the two sites are different
  // installations, so each records its visitor its own way.
  const EMPTY_LOOKUP = { kind: "none", idKey: "", nameKey: "", sessionName: "" };
  const [lookupDraft, setLookupDraft] = useState({ live: EMPTY_LOOKUP, test: EMPTY_LOOKUP });
  const [lookupBusy, setLookupBusy] = useState(null);
  // SEEDING A CARD'S BOXES OVERWRITES WHAT IS TYPED IN IT, so a reload
  // only ever seeds the cards it is asked to (1.450.0). Reported 22 Sep
  // 2026: "This form isn't saving properly please check it and the test
  // one." Saving one site reloaded the checklist and re-seeded BOTH cards,
  // so anything typed into the other card - and not yet saved - was wiped
  // as the first card saved. Now: everything on mount, and after a save
  // only the site that was saved.
  const loadChecklist = (seed = ["live", "test"]) =>
    wsRequest("admin.partner.quickStartChecklist", {}, token)
      .then((data) => {
        setChecklist(data);
        seedFtp(data, seed);
        // WHAT THE PAGE CONTAINS, PUT BACK (1.503.0).
        //
        // The same complaint 1.451.0 answered for the sign-in card, a
        // card further down: "The radio buttons aren't remembered and
        // changing the ... value isn't remembered". Everything describing
        // the generated PAGE was still component state, so configuring
        // one and refreshing started again from the defaults.
        //
        // Seeded BEFORE savedPage is marked ready, so the effect that
        // saves on change does not immediately write the defaults back
        // over what it has just read.
        const page = data?.quickStartPage;
        // HAS THIS PARTNER EVER SAVED A PAGE? (1.529.0)
        //
        // `data.quickStartPage` is never null - the route normalises it
        // on the way out, so a partner who has never saved gets the same
        // shape as one who has, with `picked: {}`. Since 1.528.1 a saved
        // page wins outright (that is what makes unticking every box
        // stick), so `{}` won every time and the pre-ticked defaults
        // below were unreachable: a first visit showed all 78 boxes
        // empty, and generating from that made a page of empty sections.
        //
        // THE SERVER SAYS WHICH IT IS. A server that does not send the
        // flag at all is read as "saved" - the old behaviour, and the
        // safe side, because applying the defaults over a saved page is
        // the fault 1.528.1 fixed and it writes itself back.
        const everSaved = data?.quickStartPageSaved !== false;
        // RECORDED, NOT APPLIED - see pickedSeed.
        pickedSeed.current.saved = everSaved ? page?.picked || {} : null;
        pickedSeed.current.haveSaved = true;
        seedPicked();
        if (page) {
          setWidgets(page.widgets || { competition: true, replay: true, account: true, status: true });
          setNavStyle(page.navStyle || "modal");
          setNavTheme(page.navTheme || "light");
          setNavMenu(page.navMenu || "dropdown");
          setBuyCreditTab(page.buyCreditTab !== false);
          setPageStyle(page.pageStyle || "none");
          setOwnCss({
            mode: page.ownCss?.mode || "none",
            href: page.ownCss?.href || "",
            css: page.ownCss?.css || "",
          });
          // The route normalises this, so a partner who has never saved
          // gets our defaults back rather than nothing - see
          // normaliseQuickStartPage. The `|| DEFAULTS` is for a server
          // old enough not to send it at all.
          setStatus(page.status || STATUS_WIDGET_DEFAULTS);
          // AND THE TAB OPENS ON IT (1.530.0). previewStyle is the tab
          // and pageStyle is the answer - browsing a template is not
          // choosing it (1.464.0) - but on the FIRST load there is
          // nothing being browsed yet, and opening on "No styling" while
          // the dark one is the one being shipped showed a page nobody
          // had chosen. Seeded only here, so Use this one still moves
          // pageStyle alone and a tab clicked since is left alone.
          if (!previewSeeded.current) {
            previewSeeded.current = true;
            setPreviewStyle(page.pageStyle || "none");
          }
          setComp({ ...COMPETITION_WIDGET_DEFAULTS, ...(page.comp || {}) });
        }
        // A SAVED TITLE WINS; with none, the site's own name, which is
        // what 1.461.6 asked for. The old rule - only over the untouched
        // default - stays for the second case.
        if (page?.pageTitle) setPageTitle(page.pageTitle);
        else if (data?.siteName) setPageTitle((cur) => (cur === QUICK_START_DEFAULT_TITLE ? data.siteName : cur));
        pageReady.current = true;
        // Seeded through quickStartLookupDraft (1.461.1), which holds a
        // stored kind to the two the radios have.
        const from = quickStartLookupDraft;
        if (seed.length) {
          setLookupDraft((prev) => ({
            ...prev,
            ...Object.fromEntries(seed.map((site) => [site, from(data?.quickStart?.[site]?.userLookup)])),
          }));
          // WHERE THE KEY IS AND WHERE THE SCRIPT LIVES ARE SAVED TOO
          // (1.451.0): they were page state until then, so they were
          // forgotten on the next load - "The radio buttons aren't
          // remembered and changing the ... value isn't remembered".
          setSignIn((prev) => ({
            keyWhere: {
              ...prev.keyWhere,
              ...Object.fromEntries(seed.map((site) => [site, data?.quickStart?.[site]?.keyWhere || "env"])),
            },
            path: {
              ...prev.path,
              ...Object.fromEntries(
                seed.map((site) => [site, data?.quickStart?.[site]?.path ?? QUICK_START_SESSION_PATH])
              ),
            },
            signInUrl: {
              ...prev.signInUrl,
              ...Object.fromEntries(seed.map((site) => [site, data?.quickStart?.[site]?.signInUrl ?? ""])),
            },
            signInKind: {
              ...prev.signInKind,
              ...Object.fromEntries(seed.map((site) => [site, data?.quickStart?.[site]?.signInKind ?? "own"])),
            },
          }));
        }
      })
      .catch(() => setChecklist(null));
  useEffect(() => {
    loadChecklist();
  }, [token]);

  // SAVED AS IT IS CHANGED (1.503.0), rather than behind a button.
  //
  // The complaint was that refreshing lost the configuration, and a Save
  // button nobody has pressed yet loses it exactly the same way. Decided
  // 27 Sep 2026.
  //
  // ONE SAVE PER CARD (1.451.0): everything that card holds goes together.
  const saveSite = async (site) => {
    setLookupBusy(site);
    try {
      const d = lookupDraft[site];
      const userLookup =
        d.kind === "php-session"
          ? { kind: "php-session", idKey: d.idKey, nameKey: d.nameKey, sessionName: d.sessionName }
          : null;
      await wsRequest(
        "admin.partner.setQuickStartSite",
        {
          site,
          keyWhere: signIn.keyWhere[site],
          path: sessionPathFor(site),
          signInKind: signIn.signInKind[site],
          signInUrl: signInUrlFor(site),
          userLookup,
        },
        token
      );
      showToast(`Saved your ${site} site. Generate again to write it into the code.`, "success");
      loadChecklist([site]);
    } catch (err) {
      // A PAGE OPEN ACROSS AN UPDATE (1.453.0): the backend answers
      // "Unknown action" for anything this page knows and it does not.
      // Say what to do rather than showing the bare words.
      showToast(
        /unknown action/i.test(err?.message || "")
          ? "This page was loaded before the site was updated. Refresh the page and try again."
          : err?.message || "Could not save that.",
        "error"
      );
    }
    setLookupBusy(null);
  };
  const userLookupFor = (site) => checklist?.quickStart?.[site]?.userLookup || null;
  // THE PAGE'S TITLE (1.446.0), for the <title> of the complete page.
  // THE PAGE'S TITLE STARTS AS THE SITE'S NAME (1.461.6). Asked on 23 Sep
  // 2026: "The title set here should be the value set under Sitewide for
  // "Site Name"". It is seeded from there and stays editable - a title
  // that has been typed over is never replaced, however often the
  // checklist reloads.
  const [pageTitle, setPageTitle] = useState(QUICK_START_DEFAULT_TITLE);
  // WHICH OF THE THREE PAGES IS THE ONE (1.461.7). Asked 23 Sep 2026: a
  // light one, a dark one and the raw template, with "whatever the choice
  // is that is the one that will be either uploaded or zipped up". The
  // markup and the script are identical in all three - only a <style>
  // block in the head differs - so switching does not regenerate.
  const [pageStyle, setPageStyle] = useState("none");
  // THE PARTNER'S OWN STYLESHEET (1.592.0). Three states rather than a
  // tick box: a partner with somewhere to put a file wants a file, and a
  // partner without wants to type into the page, and both are asking for
  // the same thing.
  const [ownCss, setOwnCss] = useState({ mode: "none", href: "", css: "" });
  const setOwnCssOption = (key, value) => setOwnCss((prev) => ({ ...prev, [key]: value }));
  // NAMED FROM THE SITE NAME, in one place, so the file offered in the
  // zip, the href suggested in the field and the link written into the
  // page are the same string and cannot drift apart.
  const ownCssFile = quickStartOwnCssFile(pageTitle);
  // THE SKELETON HAS TO COME FROM A STYLESHEET, and the unstyled
  // template has none - but its markup carries the same classes, so fall
  // back to the light sheet for the class list rather than offering a
  // partner on "none" an empty file.
  const ownCssSkeleton = () =>
    quickStartOwnCssFileBody(quickStartPageCss(pageStyle) || quickStartPageCss("light"), ownCssFile);

  // BELOW EVERY PIECE OF STATE IT WATCHES, and that is not cosmetic
  // (1.505.0). A dependency array is evaluated DURING the render, not
  // when the effect runs - so with this block above `pageTitle` and
  // `pageStyle`, naming them here read two consts still in their temporal
  // dead zone and the whole panel threw "Cannot access 'B' before
  // initialization" before it drew anything. Shipped in 1.503.0, reported
  // 27 Sep 2026 from the browser console.
  // DEBOUNCED, because several of these are text fields and a slider: a
  // write per keystroke would be a write per keystroke. 600ms is long
  // enough to cover typing and short enough that a refresh straight after
  // the last change has already been saved.
  //
  // GUARDED THREE WAYS: not before the load has answered (pageReady), not
  // for somebody who cannot edit, and not while the panel is frozen -
  // a read-only viewer's browser should not write to the partner file.
  useEffect(() => {
    if (!pageReady.current || !canEdit) return undefined;
    const id = setTimeout(() => {
      wsRequest(
        "admin.partner.setQuickStartPage",
        { page: { picked, widgets, navStyle, navTheme, navMenu, buyCreditTab, pageTitle, pageStyle, comp, status, ownCss } },
        token
        // SILENT ON FAILURE, deliberately: this fires on a toggle, and a
        // toast per failed keystroke would bury the panel. The value is
        // still on screen, and the next change tries again.
      ).catch(() => {});
    }, 600);
    return () => clearTimeout(id);
  }, [picked, widgets, navStyle, navTheme, navMenu, buyCreditTab, pageTitle, pageStyle, comp, status, ownCss, canEdit, token]);

  // WHICH SITE'S FILES ARE SHOWN (1.452.0). Asked on 22 Sep 2026: "We need
  // tabs for "Live" and "Test" on the quick start page to show the relevant
  // code. So the html box will need show that it serves both test and
  // live." The page is one page - it asks whichever script belongs to the
  // site it is on - so it stays above the tabs, and the tabs carry the
  // files, which differ per site.
  const [outputSite, setOutputSite] = useState("live");
  // THE TAB BEING LOOKED AT, AND THE ONE THAT WILL BE USED (1.464.0).
  // Asked for on 23 Sep 2026: a preview of the three templates with a
  // "Use this one" button under each. Browsing a template is therefore
  // not choosing it - previewStyle is the tab, pageStyle is the answer -
  // so a look at the dark one does not change what the zip packs.
  const [previewStyle, setPreviewStyle] = useState("none");
  // PREVIEW OR HTML, PER TEMPLATE (1.467.0). Asked for on 23 Sep 2026:
  // "For all three templates can we have a sub tab for Preview and HTML
  // so the user can swap between preview and html views?" One choice for
  // all three tabs, not one each: moving between templates to compare
  // them should not also change what you are looking at.
  const [previewView, setPreviewView] = useState("preview");
  // WHICH SECTIONS ARE OPEN (1.464.0). All closed to start with, by
  // decision: "Default state: all three collapsed."
  const [openSection, setOpenSection] = useState({});
  const toggleSection = (id) => setOpenSection((v) => ({ ...v, [id]: !v[id] }));
  const openSectionAnd = (id) => {
    setOpenSection((v) => ({ ...v, [id]: true }));
    // After the section has opened, or there is nothing to scroll to.
    setTimeout(() => {
      document.querySelector(`[data-quick-start-section="${id}"]`)?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 0);
  };
  // WHICH SITE'S SERVER IS BEING SET UP (1.464.0), for the tabs inside
  // Server Configuration - one form on screen rather than two.
  const [serverTab, setServerTab] = useState("live");
  // WHICH SITE'S SIGN-IN IS BEING SET UP (1.467.0). Its own tab rather
  // than sharing Server Configuration's: the two sections are opened for
  // different reasons and moving one should not move the other.
  const [signInTab, setSignInTab] = useState("live");
  // EVERYTHING FOR ONE SITE IN A ZIP (1.459.0). The files are generated in
  // this page, so the server is handed them and packs them; it checks the
  // names and sizes before it does (see /api/quick-start-files.zip).
  const [zipBusy, setZipBusy] = useState(false);
  // PUBLISHING BY FTP (1.460.0). The details live on the provider, the
  // password encrypted and never sent back: `ftp[site].passwordSet` is all
  // the page ever learns about it.
  const [ftp, setFtp] = useState({});
  const EMPTY_FTP = { host: "", port: "21", user: "", password: "", dir: "", secure: true, implicit: false, allowSelfSigned: false, protectData: true };
  const [ftpDraft, setFtpDraft] = useState({ live: EMPTY_FTP, test: EMPTY_FTP });
  const [ftpBusy, setFtpBusy] = useState(null);
  const ftpFor = (site) => ftp?.[site] || null;
  // BUSY IS PER CARD (1.461.1). Reported 22 Sep 2026: "Testing the ftp
  // connection on the test server seems to effect the live server form, it
  // shouldn't. Different forms". One flag disabled both cards; each card
  // now only waits on its own work, and the Generate button only waits on
  // an upload.
  const ftpBusyFor = (site) => String(ftpBusy || "").startsWith(`${site}-`);
  // THE LAST TEST'S ANSWER, KEPT ON THE CARD (1.461.1). A connection
  // failure now carries what the host said it offers, and that is
  // something to read while changing the port and the choice above it -
  // not something to catch as a toast goes past.
  const [ftpNote, setFtpNote] = useState({});
  // WHICH SITE "Publish via FTP" GOES TO (1.461.1, answered differently
  // at 1.464.0). Asked 22 Sep 2026: "Publish via FTP should have
  // checkboxes for "Live" and "Test" so the right connections are made.
  // These checkboxes should be disabled by default." The worry was a
  // button putting a test page on a live site by being the nearest thing
  // to click. There are now two buttons, each naming its own site, which
  // answers it without a tick box to forget: nothing is uploaded anywhere
  // except by pressing the button that says where.
  const publishing = String(ftpBusy || "").endsWith("-publish");
  const seedFtp = (data, seed) => {
    setFtp(data?.ftp || {});
    if (!seed.length) return;
    setFtpDraft((prev) => ({
      ...prev,
      ...Object.fromEntries(
        seed.map((site) => {
          const saved = data?.ftp?.[site];
          return [
            site,
            saved
              ? {
                  host: saved.host || "",
                  port: String(saved.port || 21),
                  user: saved.user || "",
                  password: "",
                  dir: saved.dir || "",
                  secure: saved.secure !== false,
                  protectData: saved.protectData !== false,
                  implicit: !!saved.implicit,
                  allowSelfSigned: !!saved.allowSelfSigned,
                }
              : EMPTY_FTP,
          ];
        })
      ),
    }));
  };
  const saveFtp = async (site, remove) => {
    setFtpBusy(`${site}-save`);
    try {
      const d = ftpDraft[site];
      const payload = remove
        ? { enabled: false }
        : {
            host: d.host,
            port: Number(d.port) || 21,
            user: d.user,
            password: d.password,
            dir: d.dir,
            secure: d.secure,
            implicit: d.implicit,
            allowSelfSigned: d.allowSelfSigned,
            protectData: d.protectData !== false,
          };
      await wsRequest("admin.partner.setQuickStartFtp", { site, ftp: payload }, token);
      showToast(remove ? `FTP details removed for your ${site} site.` : `FTP details saved for your ${site} site.`, "success");
      loadChecklist([site]);
    } catch (err) {
      showToast(err?.message || "Could not save those FTP details.", "error");
    }
    setFtpBusy(null);
  };
  const testFtp = async (site) => {
    setFtpBusy(`${site}-test`);
    setFtpNote((n) => ({ ...n, [site]: null }));
    try {
      const r = await wsRequest("admin.partner.quickStartFtpTest", { site }, token);
      const text =
        `Connected to your ${site} site over ${r.secure ? "FTPS" : "plain FTP"}` +
        `${r.dir ? ` and opened ${r.dir}` : ""}. ${r.names.length} item(s) there.` +
        // WHAT IT TOOK (1.461.2): a host whose data connection will not
        // accept TLS 1.3 is served on a second, pinned attempt, and the
        // admin is told - it is the difference between "it works" and "it
        // works, slowly, for a reason".
        (r.tls?.pinned ? ` Pinned to TLS ${r.tls.pinned.replace("TLSv", "")}: this host would not accept its data connection on a newer one.` : "");
      setFtpNote((n) => ({ ...n, [site]: { ok: true, text } }));
      showToast(text, "success");
    } catch (err) {
      const text = err?.message || "Could not connect.";
      setFtpNote((n) => ({ ...n, [site]: { ok: false, text } }));
      showToast(text, "error");
    }
    setFtpBusy(null);
  };
  // GENERATE, THEN UPLOAD: the button that was Generate publishes instead
  // once a site has FTP details - "the "Generate" button becomes a
  // "Publish via FTP" button".
  // The chosen one, wherever the page is wanted: the box, the zip, the
  // upload. Falls back to what an older output object held.
  const chosenPage = (out) => (out && out.pages ? out.pages[pageStyle] || out.pages.none : out && out.page) || "";
  // WHAT GOES WHERE, ANSWERED IN ONE PLACE (1.464.0). The zip, the FTP
  // publish and the list on screen all read this. Asked for on 23 Sep
  // 2026: "on generate, list all files that will be zipped/FTP'd" - and a
  // list built separately from the thing it describes is a list that goes
  // wrong quietly. Built from a generated `out`, so it can be called with
  // what was just built rather than waiting for the state to come round.
  const quickStartPlan = (out, site) => {
    if (!out) return null;
    const st = (out.sites || []).find((x) => x.site === site) || null;
    const forSite = (out.files || []).filter((f) => (out.sites || []).length < 2 || f.site === site);
    const entries = [
      {
        name: QUICK_START_PAGE_FILE,
        zipPath: `${QUICK_START_WEB_ROOT}/${QUICK_START_PAGE_FILE}`,
        publishName: QUICK_START_PAGE_FILE,
        body: chosenPage(out),
        where: `Your page. Put it wherever it should be served from inside ${QUICK_START_WEB_ROOT}.`,
      },
      // THE LOOK, BESIDE THE PAGE (1.475.0). The page links it by name,
      // so the two travel together; the unstyled template has none and
      // gets no file and no link.
      ...(quickStartPageCss(pageStyle)
        ? [
            {
              name: QUICK_START_CSS_FILE,
              zipPath: `${QUICK_START_WEB_ROOT}/${QUICK_START_CSS_FILE}`,
              publishName: QUICK_START_CSS_FILE,
              body: quickStartPageCss(pageStyle),
              where: `Beside your page - it is linked by name, so keep them together. The page links it with a ?v= that changes only when this file does, so a browser cannot serve an old copy of it beside a new page. Every rule is yours to change; put your own in a stylesheet after this one, because generating again writes this file again. Your other pages can link it too.`,
            },
          ]
        : []),
    // AND THE PARTNER'S OWN, WHEN THEY ASKED FOR A FILE (1.592.0).
    //
    // publishName IS NULL ON PURPOSE, the same as 589comps-config.php:
    // this file is the partner's work, and a second publish writing over
    // it would be the one mistake that cannot be undone from here. The
    // zip carries it so there is something to start from; after that it
    // is theirs.
    ...(ownCss.mode === "file"
      ? [
          {
            name: ownCssFile,
            zipPath: `${QUICK_START_WEB_ROOT}/${ownCssFile}`,
            publishName: null,
            body: ownCssSkeleton(),
            where: `Beside your page, linked after ${QUICK_START_CSS_FILE} so it wins. Every rule in it is empty: fill in what you want to change and delete the rest. A publish never sends this file, so your work cannot be written over from here - but it IS in the zip, so if you ever extract a later zip over your site, move your copy aside first.`,
            optional: true,
          },
        ]
      : []),
      ...forSite.map((f) => ({
        name: f.name,
        zipPath: f.path || f.name,
        // THE ONE FILE A PUBLISH NEVER SENDS (1.461.5): the partner's key
        // goes in it by hand, and a second publish would write over it.
        publishName: f.neverPublish ? null : f.name,
        body: f.body,
        where: f.where,
        optional: !!f.optional,
      })),
    ];
    const readme = {
      name: "README.txt",
      zipPath: "README.txt",
      publishName: null,
      body: quickStartReadme({
        site,
        domain: st?.domain || "",
        pageFile: `${QUICK_START_WEB_ROOT}/${QUICK_START_PAGE_FILE}`,
        files: entries.map((e) => ({ name: e.name, path: e.zipPath, where: e.where })),
        keyWhere: signIn.keyWhere[site],
        sessionPath: sessionPathFor(site),
        pageStyle,
      }),
    };
    const all = [...entries, readme];
    return {
      site,
      st,
      all,
      zipName: quickStartZipName(site, pageStyle),
      zipFiles: all.map((e) => ({ name: e.zipPath, body: e.body })),
      publishFiles: all.filter((e) => e.publishName).map((e) => ({ name: e.publishName, body: e.body })),
    };
  };
  const publishViaFtp = async (only) => {
    const built = generate();
    if (!built) return;
    // ONE NAMED SITE (1.464.0). It used to take null and mean "the ticked
    // ones", which is how a button called "Publish via FTP" came to need
    // tick boxes underneath to say what it meant. Every caller names a
    // site now, and a site with no FTP details is still refused here as
    // well as on the button.
    const sites = (built.sites || []).filter((st) => st.site === only && ftpFor(st.site));
    if (!sites.length) return;
    for (const st of sites) {
      setFtpBusy(`${st.site}-publish`);
      const files = quickStartPlan(built, st.site).publishFiles;
      try {
        const r = await wsRequest("admin.partner.quickStartPublish", { site: st.site, files }, token);
        showToast(
          `Published to your ${st.site} site: ${r.uploaded.map((u) => u.name).join(", ")}` +
            `${r.dir ? ` in ${r.dir}` : ""}.`,
          "success"
        );
      } catch (err) {
        showToast(err?.message || `Could not publish to your ${st.site} site.`, "error");
      }
      setFtpBusy(null);
    }
  };
  // THE ZIP CARRIES THE FOLDERS TOO (1.461.5). Asked on 22 Sep 2026:
  // "Can you make sure all the relevant files and file structure is
  // included?" Each file knows where it goes, so the zip is laid out from
  // the folder that holds the web root, and a README says the rest.
  const downloadSiteZip = (site, built) => {
    // GENERATES IF IT HAS TO (1.464.0), so a named download button is
    // never dead for want of a click on Generate first.
    const out = built || output || generate();
    if (!out) return;
    const plan = quickStartPlan(out, site);
    if (!plan) return;
    const files = plan.zipFiles;
    setZipBusy(true);
    fetch(`${API_BASE}/api/quick-start-files.zip`, {
      method: "POST",
      headers: { "Content-Type": "application/json", "x-auth-token": token },
      body: JSON.stringify({ site, files, pageStyle }),
    })
      .then(async (res) => {
        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          throw new Error(data.error || "Could not build the zip.");
        }
        return res.blob();
      })
      .then((blob) => {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = plan.zipName;
        a.click();
        URL.revokeObjectURL(a.href);
      })
      .catch((err) => showToast(err.message || "Could not build the zip.", "error"))
      .finally(() => setZipBusy(false));
  };
  const setCompOption = (key, value) => setComp((prev) => ({ ...prev, [key]: value }));
  // WHAT THE SAVED LAYOUT SAYS, shown where the widget is ticked. Which
  // sections show and how the cards are laid out are saved to the site
  // (the layout editor below), not written into the code, so they are
  // summarised here with a way to reach the editor.
  const [savedLayout, setSavedLayout] = useState(null);
  const loadSavedLayout = () =>
    wsRequest("admin.partner.getLayout", {}, token)
      .then(setSavedLayout)
      .catch(() => setSavedLayout(null));
  useEffect(() => {
    loadSavedLayout();
  }, [token]);
  const [output, setOutput] = useState(null);
  // WHAT THAT BUILD WAS MADE FROM (1.488.0) - see quickStartFingerprint.
  // Null until Generate has run; compared with the choices as they are on
  // every render, which is how the page knows to say it is behind.
  const [builtFrom, setBuiltFrom] = useState(null);
  const [error, setError] = useState(null);

  const WIDGETS = [
    {
      id: "competition",
      label: "Competition widget — the playable grid",
      file: "589comps-widget.min.js",
      global: "Comps589Widget",
    },
    {
      id: "replay",
      // WHERE IT GOES IS PART OF WHAT IT IS (1.470.0): a Replays tab in
      // the account widget, not a block of its own on the page.
      label: "Replay widget — a Replays tab in the account widget, to watch a finished competition back",
      file: "589comps-replay.min.js",
      global: "Comps589Replay",
    },
    {
      id: "account",
      label: "Account navigation widget — the visitor's account, in a modal or header menu",
      file: "589comps-account.min.js",
      global: "Comps589Account",
    },
    {
      id: "status",
      // THE BAND, AS A WIDGET (1.532.0). Asked for on 28 Sep 2026: "if
      // we're using other javascript for functionality to create
      // functionality outwith the scope of the existing widgets then we
      // could maybe create a new widget so if the partner wants to
      // recreate the exact functionality they know exactly what they
      // need because they selected on in the quick start page."
      //
      // NO ELEMENT OF ITS OWN: it fills elements this page marks up, the
      // way the layout template's slots do. So it is left out of the
      // block of <div>s below, and its markup is written into the hero
      // and the board instead.
      label: "Status widget — the live light, the title, the countdown and the prices, into your own markup",
      file: "589comps-status.min.js",
      global: "Comps589Status",
    },
  ];

  // WHICH WIDGETS HAVE NOTHING IN Widget Configuration (1.534.0).
  //
  //   replay - its options are the account widget's; it is a tab inside
  //            it (1.470.0).
  //   status - its options are the PARTNER'S, set in mount(). 1.532.0
  //            passes none of them from here on purpose, so a partner
  //            gets the widget's documented defaults instead of our
  //            wording written into their page.
  // The status widget came off this list in 1.539.0 - its words are the
  // operator's now. The replay stays: its options are the account
  // widget's, because it is a tab inside it (1.470.0).
  const WIDGETS_WITHOUT_OPTIONS = ["replay"];

  useEffect(() => {
    let cancelled = false;
    wsRequest("admin.api.getSettings", {}, token)
      .then((data) => {
        if (cancelled) return;
        const gs = data?.groups || [];
        setGroups(gs);
        setUnmasked({
          winnersUnmasked: !!data?.winnersUnmasked,
          lastEntriesUnmasked: !!data?.lastEntriesUnmasked,
          recentPurchasesUnmasked: !!data?.recentPurchasesUnmasked,
        });
        // PRE-TICKED TO WHAT THE API ACTUALLY SERVES, so the default
        // output is a page that works on this site today.
        const initial = {};
        gs.forEach((g) =>
          g.fields.forEach((f) => {
            if (f.enabled) initial[`${g.group}.${f.key}`] = true;
          })
        );
        // RECORDED, NOT APPLIED - see pickedSeed. Applying it here is
        // what put every box back after a save.
        pickedSeed.current.defaults = initial;
        pickedSeed.current.haveDefaults = true;
        seedPicked();
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err?.message || "Could not load the field list.");
        // A FAILED FIELD LIST MUST NOT HOLD THE SEED SHUT.
        //
        // seedPicked waits for BOTH answers. Without this the defaults
        // never arrive on a failure, the seed never runs, and `picked`
        // stays {} - while the autosave effect, already armed by
        // pageReady, writes that {} straight over the partner's saved
        // selection. A request that failed would have DELETED the page's
        // ticked fields.
        //
        // Empty defaults, not none: a saved page is truthy and wins, and
        // with no saved page either the panel opens with nothing ticked,
        // which is what a site whose field list would not load can
        // honestly offer.
        pickedSeed.current.defaults = {};
        pickedSeed.current.haveDefaults = true;
        seedPicked();
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  const toggle = (setter) => (key) =>
    setter((prev) => ({ ...prev, [key]: !prev[key] }));

  // A SITE'S SCRIPT ADDRESS (1.448.0). Empty means that site has no
  // session script at all (1.454.0), so it is taken as typed.
  const sessionPathFor = (site) => signIn.path[site] || "";
  // THE NAME THE PARTNER CHOSE (1.472.0). The file is written from the
  // path they saved, so everything that names it - the file list, the
  // zip, the publish, the notes beside each box and the generated files'
  // own comments - reads it from here rather than assuming the default.
  const sessionFileFor = (site) => sessionPathFor(site).split("/").pop() || "comps-session.php";
  // WHICH WIDGETS REALLY GO ON THE PAGE (1.470.0), lifted out of
  // generate() at 1.478.0 so the card that says what a Generate will
  // make reads the same rule the generator does. The replay is a tab
  // inside the account widget, so it needs that widget to be there.
  const onPage = (id) => !!widgets[id] && (id !== "replay" || !!widgets.account);
  // The widgets that will be on the page, named as their own tick boxes
  // name them (1.478.0), for the card at the foot that says what a
  // Generate will make.
  const onPageWidgetNames = WIDGETS.filter((w) => onPage(w.id)).map((w) =>
    w.label.split(" \u2014 ")[0].replace(/ widget$/, "").toLowerCase()
  );
  // WHERE THE PARTNER'S OWN SIGN-IN LIVES (1.457.0), per site.
  const signInUrlFor = (site) => signIn.signInUrl[site] || "";
  // HOW THAT SITE SIGNS A VISITOR IN (1.458.0), and - for the sign-in Quick
  // Start writes - where its comps-auth.php sits, beside its session script.
  const signInKindFor = (site) => signIn.signInKind[site] || "own";
  const authPathFor = (site) => {
    const p = sessionPathFor(site);
    if (!p) return "";
    const cut = p.lastIndexOf("/");
    return `${cut > 0 ? p.slice(0, cut) : ""}/comps-auth.php`;
  };
  const signInPlanFor = (site) => ({
    kind: signInKindFor(site),
    url: signInUrlFor(site),
    auth: authPathFor(site),
  });
  // THE CHOICES AS THEY ARE, NAMED ONCE (1.488.0). Both callers use this:
  // generate() to record what it built from, and the render to compare. A
  // second copy of this list is the drift it exists to catch, so there
  // isn't one. Declared as a function, not a const, because generate() is
  // above it and hoisting is what makes the order of the body irrelevant.
  //
  // String building over one field registry and a few small objects, once
  // a render; generate() itself is far heavier.
  function quickStartChoicesNow() {
    return quickStartFingerprint({
      buyCreditTab,
      checklist,
      comp,
      groups,
      navMenu,
      navStyle,
      ownCss,
      navTheme,
      pageTitle,
      picked,
      savedLayout,
      signIn,
      // THE STATUS WIDGET'S WORDS (1.539.0). The generator reads them,
      // so a page built before they were edited is out of date - and
      // quick-start-sections caught this the moment the state existed,
      // which is exactly what that test is for.
      status,
      widgets,
    });
  }
  function generate() {
    // THE KEY'S SITES (1.447.0), first: the page's code needs them as well
    // as the files (each site's script address, 1.448.0).
    const sites = quickStartSites(savedLayout?.domains);
    const testDomain = sites[1].separate ? sites[1].domain : null;
    // Does either site use the sign-in Quick Start writes (1.458.0)? Then
    // the page carries its form and its code.
    const usesSignInForm = sites.some((st) => st.separate && signInKindFor(st.site) === "form" && sessionPathFor(st.site));
    // Whether the generated page has anywhere to send a signed-out visitor
    // (1.461.5) - it decides whether the account widget gets a handler.
    const canSignIn = signInCanHappen(
      signInPlanFor("live"),
      sites[1]?.separate ? { domain: sites[1].domain, plan: signInPlanFor("test") } : null
    );
    const base = window.location.origin;
    // The title goes into markup as well as into <title> now, so it is
    // escaped once here rather than twice, differently, further down.
    const pageTitleHtml = String(pageTitle || "Competition")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;");
    const chosenGroups = (groups || [])
      .map((g) => ({
        ...g,
        fields: g.fields.filter((f) => picked[`${g.group}.${f.key}`]),
      }))
      .filter((g) => g.fields.length);

    // WHERE REPLAYS LIVE (1.470.0): in the account widget, as a Replays
    // tab - so the replay widget is on the page when the account widget
    // is, and not otherwise. Ticked without it, the page has no account
    // section for the tab to sit in, and the Widgets section says so
    // rather than the page quietly growing a block under the competition
    // again, which is what this release was asked to take away.
    const replaysInAccount = !!widgets.replay && !!widgets.account;
    // BUYING CREDIT IS A TAB IN THE SAME PLACE (1.476.0), and needs the
    // account widget for the same reason: there is nowhere else on the
    // page for it to be. Its tick box is under Widget Configuration, not
    // Widgets, because it is not a widget - it is a section of the
    // account one, written for you.
    const buyCreditInAccount = !!widgets.account && buyCreditTab;
    // Which extra tabs the account widget is given, in the order they
    // appear. One list, so the code below cannot offer a tab whose
    // function was not written above it.
    const extraTabs = [
      ...(replaysInAccount ? [{ id: "replays", label: "Replays", render: "comps589ReplaysSection" }] : []),
      ...(buyCreditInAccount ? [{ id: "buy-credit", label: "Buy credit", render: "comps589BuyCreditSection" }] : []),
    ];

    // THE WINNERS TABLE IS A SECTION, NOT A PAGE (1.533.0). It had a
    // view of its own from 1.471.0 - "Can you create a Winners page next
    // to My Account" - and that is exactly why it was not where it was
    // looked for: "its not showing on the page under the competition
    // where I expect to see it." One page again, by request.
    // THE SIGN OUT BUTTON JOINS THE NAV wherever there is one.
    const signOutInNav = usesSignInForm && !!widgets.account;

    // THE BODY OF A COMPLETE PAGE (1.446.0): the navigation first, then the
    // competition and the data, then the widget files. The replay has no
    // element of its own since 1.470.0 - the account widget draws it in
    // its Replays tab. The "My account" item sits in a real <nav><ul> now
    // that this is a page; on its own it was an <li> with no list.
    // Which view a block belongs to - nothing at all when there is only
    // one of them, so a page without winners is the page it always was.

    const html = [
      // One element: the pieces are joined by a blank line, which put one
      // in the middle of this comment.
      "<!-- Generated by the competition provider's Quick Start.\n" +
        "     No styling: every class and layout choice is yours. -->",
      // THE BAR IS THE ACCOUNT WIDGET'S AGAIN (1.533.0). 1.511.0 had to
      // widen this to `widgets.account || winnersView`, because with the
      // winners group ticked and the account widget off there was no
      // header to hold the Home and Winners links and the winners table
      // was unreachable. There are no views to switch between now, so
      // the bar is back to the one thing that needs it.
      ...(widgets.account
        ? [
            [
              "<header>",
              "  <nav>",
              // THE SITE'S NAME IN THE BAR (1.467.0). Asked for on 23 Sep
              // 2026: the light and dark templates as complete pages
              // "with the page title also showing in the top nav as the
              // name of the site". In the SHARED markup, not only in the
              // styled versions: switching template is meant to change
              // how the page looks and never what it says. Unstyled it is
              // a plain link, which is what the raw template should be.
              `    <a class="site-name" href="/">${pageTitleHtml}</a>`,
              "    <ul>",
              // HOME AND WINNERS ARE THE TWO VIEWS (1.471.0). Without a
              // winners table there is only one thing to be on, so Home
              // stays the plain link to the site root it has always been.
              // ONE VIEW, SO ONE HOME LINK (1.533.0) - the plain link to
              // the site root it was before 1.471.0 gave the page a
              // second view to switch between.
              '      <li><a href="/">Home</a></li>',
              // YOUR OWN "MY ACCOUNT", MARKED (1.430.0): the item keeps its
              // own text and styling; the widget adds what happens on click.
              // Only when there is an account widget to mark for (1.511.0).
              ...(widgets.account
                ? [
                    "      <!-- YOUR OWN \"My account\" item, marked: it keeps its own text and",
                    "           styling. Empty it to get the widget's own button instead. -->",
                    `      <li data-589comps-account="${navStyle}"${
                      navStyle === "header" && navMenu === "list" ? ' data-589comps-account-menu="list"' : ""
                    }><a href="/login">My account</a></li>`,
                  ]
                : []),
              // AND SIGN OUT BESIDE IT (1.471.0), at the right of the bar
              // with it rather than loose in the middle of the page.
              ...(signOutInNav
                ? ['      <li><button id="comps-signout" type="button" hidden>Sign out</button></li>']
                : []),
              "    </ul>",
              "  </nav>",
              "</header>",
              ...(widgets.account && navStyle === "header"
                ? [
                    "",
                    "<!-- Where the chosen account section is drawn. A view of its",
                    "     own (1.474.0): choosing an account page hides the rest,",
                    "     and Home or Winners hides it again. -->",
                    `<section data-589comps-account-target></section>`,
                  ]
                : []),
            ].join("\n"),
          ]
        : []),
      // THE FRONT OF THE SITE (1.511.0). Asked for on 27 Sep 2026: the
      // light and dark templates as complete example websites, so a
      // partner can feel what their page would be like.
      //
      // IN THE SHARED MARKUP, like the site name at 1.467.0, because
      // switching template changes how the page LOOKS and never what it
      // SAYS. Unstyled it is a heading, a paragraph and two links, which
      // is a fair thing to hand somebody starting from nothing.
      //
      // ONLY WHERE THERE IS A COMPETITION. A partner who picked the
      // replay widget alone has none to introduce, and a hero announcing
      // one would be the page saying something untrue.
      //
      // THE WORDS ARE A PLACEHOLDER, meant to be rewritten - but real
      // ones, because an example site full of lorem ipsum shows nobody
      // what their site would feel like.
      //
      // AND IT BELONGS TO THE HOME VIEW. Without view("home") the hero
      // stays on screen when somebody clicks Winners, so the winners
      // table would sit under a banner announcing a competition, with
      // "How it works" below it. Every block the page writes has to
      // declare its view or it belongs to all of them.
      ...(onPage("competition")
        ? [
            [
              "<!-- The front of the page. Yours to rewrite: nothing here is",
              "     read by the competition, and deleting the whole section",
              "     leaves everything else working. -->",
              `<section class="comps-hero">`,
              '  <div class="comps-hero-inner">',
              // NOTHING IN THE HERO IS FILLED IN (1.548.0). Reported 29
              // Sep 2026 with a screenshot of the "In Play" pill:
              // "remove this from the hero section and any other data
              // that appears on the hero dynamically."
              //
              // 1.521.0 put a light and a clock here; 1.535.0 took the
              // clock out and kept the light; this takes the light too.
              // What is left is a heading, a paragraph and two links -
              // words a partner rewrites, with nothing behind them that
              // the site writes into. The band over the competition
              // carries the light and the clock, and carries them alone.
              //
              // WHICH IS WHY THIS BLOCK IS NO LONGER GATED on the status
              // widget being ticked: the hero has stopped needing it.
              "    <h1>Uncover the picture. Win the prize.</h1>",
              "    <p>Buy a patch of the hidden image, reveal what is underneath, and be the",
              "       first to name it. Every pixel you uncover is yours to see before",
              "       anyone else.</p>",
              '    <a class="comps-cta" href="#comps-competition">Take part</a>',
              '    <a class="comps-cta comps-quiet" href="#comps-how">How it works</a>',
              "  </div>",
              "</section>",
            ].join("\n"),
          ]
        : []),
      // ONE DIV, FOR THE COMPETITION. The account widget marks its own
      // item in the nav above, and the replay is a tab inside it (1.470.0).
      //
      // THE COMPETITION CARRIES comps-stage (1.511.0) so the stylesheet
      // can lift it up over the hero. A class the raw template ignores.
      // THE BOARD (1.522.0): the heading, the live band and the
      // competition, in a grid whose right-hand column is the same width
      // as the one inside the saved layout - so the band lines up with
      // the picture and stops short of the cards beside it. ONE variable
      // drives both grids (--page-rail), which is what keeps them in
      // step; they are different DOM trees and nothing else could.
      //
      // THE BAND IS OUT HERE, NOT IN THE LAYOUT, on purpose. The widget
      // sets the layout aside between competitions, so a band inside it
      // could only ever be seen green - and a light that cannot go red
      // is decoration. Out here it is yours, it always shows, and the
      // script at the foot of the page fills it.
      ...(onPage("competition") ? [`<section class="comps-board">`] : []),
      ...(onPage("competition")
        ? [
            [
              // NO HEADING OF OURS OVER THE COMPETITION (1.535.0).
              // Reported 28 Sep 2026 of "TODAY'S COMPETITION": "remove
              // this from the templates, I didn't request this." It was
              // a rule and a word we wrote, sitting above a widget that
              // says what it is - the same fault as the countdowns
              // either side of it, and the same answer.
              ...(onPage("status")
                ? [
              '  <p class="comps-live" data-589comps-status>',
              '    <span class="comps-live-dot" aria-hidden="true"></span>',
              '    <span class="comps-live-text" data-589comps-status-text></span>',
              '    <span class="comps-live-title" data-589comps-title></span>',
              // THE ROTATING FACTS (1.523.0). One at a time, typed out,
              // held, replaced. Delete this span and the rest of the
              // band carries on - the script fills what it finds.
              '    <span class="comps-live-facts" data-589comps-facts></span>',
              '    <span class="comps-live-count" data-589comps-countdown hidden>',
              '      <span class="comps-count-label" data-589comps-count-label></span>',
              '      <span class="comps-count-value" data-589comps-count-value></span>',
              "    </span>",
              "  </p>",
                  ]
                : []),
            ].join("\n"),
          ]
        : []),
      // NO DIV FOR THE STATUS WIDGET (1.532.0) - it fills the band's own
      // elements above, which is what makes it a widget rather than a
      // block we drop into somebody's page.
      ...WIDGETS.filter((w) => onPage(w.id) && w.id !== "account" && w.id !== "replay" && w.id !== "status").map(
        (w) =>
          `<div id="comps-${w.id}"${w.id === "competition" ? ' class="comps-stage"' : ""}></div>`
      ),
      ...(onPage("competition") ? ["</section>"] : []),
      // THE SIGN-IN QUICK START WROTE (1.458.0), when a site uses it.
      //
      // NO VIEW OF ITS OWN (1.471.0): it is hidden until somebody asks for
      // it, and asking puts the page back on Home - so a view attribute
      // here would only be a second thing deciding whether it is shown.
      ...(usesSignInForm ? [signInFormHtml({ signOutInNav })] : []),
      // ONE SECTION PER TICKED GROUP (1.533.0), bar the one that would
      // draw an empty card - see QUICK_START_NO_CARD.
      ...chosenGroups
        .filter((g) => QUICK_START_NO_CARD.indexOf(g.group) < 0)
        .map(
          (g) =>
            `<section>\n  <h2>${g.groupLabel}</h2>\n` +
            `  <div data-comps-group="${g.group}"></div>\n</section>`
        ),
      // HOW IT WORKS (1.511.0), between the competition and the end of
      // the page. Same reasoning as the hero: an example site showing the
      // widget and nothing else does not show a partner what a page
      // AROUND the widget would feel like. Three steps because the game
      // has three, and they are the three a first-time visitor asks
      // about.
      //
      // CREDIT IS NAMED, because an entry costs it (1.553.0). Step 3 used
      // to say "Enter your answer" and stopped there, so the one thing a
      // visitor needs to know before pressing the button - that it costs
      // something, and what - was on no part of this page. Asked for on
      // 2 Oct: "How it works should say credits are used to enter."
      //
      // SAID AS THE RULE, NOT AS A PRICE. "Submitting an answer costs
      // credit" is true on every site; a number is not (creditsPerEntry
      // is per-site and changes), and neither is "you must buy some" -
      // free entry gives credit without a purchase, and some sites sell
      // it as a top-up. The amount is shown by the widget, which reads
      // it live.
      ...(onPage("competition")
        ? [
            [
              `<section id="comps-how">`,
              "  <h2>How it works</h2>",
              '  <p class="comps-lede">Three steps. The rules of each competition live with the competition itself.</p>',
              '  <div class="comps-grid">',
              '    <div class="comps-step">',
              '      <span class="comps-step-n">1</span>',
              "      <h3>Pick your patch</h3>",
              "      <p>Drag a rectangle over the hidden image. You see what is under it before anybody else does, and what you spend uncovering it earns credit.</p>",
              "    </div>",
              '    <div class="comps-step">',
              '      <span class="comps-step-n">2</span>',
              "      <h3>Watch it open up</h3>",
              "      <p>Every patch becomes public after a short private window, so the picture builds as people play.</p>",
              "    </div>",
              '    <div class="comps-step">',
              '      <span class="comps-step-n">3</span>',
              "      <h3>Name it first</h3>",
              "      <p>Think you know what it is? Submitting an answer costs credit, so uncovering the image is also how you pay to enter. Get it right before anyone else and the prize is yours.</p>",
              "    </div>",
              "  </div>",
              "</section>",
            ].join("\n"),
          ]
        : []),
      // A PAGE ENDS (1.467.0). Without this the styled versions stop at
      // whatever the last widget was, which reads as an unfinished page
      // rather than a starting point for one.
      [
        "<footer>",
        `  <p>&copy; ${new Date().getFullYear()} ${pageTitleHtml}</p>`,
        "  <p><small>Competitions run by 589 Comps.</small></p>",
        "</footer>",
      ].join("\n"),
      ...WIDGETS.filter((w) => onPage(w.id)).map(
        (w) => `<script src="${base}/embed/v1/${w.file}"></script>`
      ),
    ].join("\n\n");

    const fieldNotes = chosenGroups
      .map(
        (g) =>
          `//   ${g.group}: ` + g.fields.map((f) => f.key).join(", ")
      )
      .join("\n");

    const js = [
      "// Generated by the competition provider's Quick Start.",
      "//",
      "// Each field arrives as { value, label } — the label is written by",
      "// the operator, so this renders without knowing any field names.",
      "//",
      "// Fields included:",
      fieldNotes || "//   (none selected)",
      "",
      `const BASE_URL = ${JSON.stringify(base)};`,
      // THE ONLY WORDS THIS PAGE WRITES THAT THE API DID NOT (1.532.0),
      // gathered here so there is one place to change them and one place
      // to check. Everything else on the page is either a label the
      // operator wrote, a value the API served, or the status widget's
      // own options.
      //
      // Asked for on 28 Sep 2026: "Any javascript on the pages generated
      // by the quick start page should only use API data and not use any
      // other data." Two empty states are what is left, and an empty
      // state cannot come from an empty answer - there is nothing in it
      // to read.
      'const COMPS589_TEXT = { noWinners: "No winners yet.", nothingToShow: "Nothing to show at the moment." };',
      "const REFRESH_MS = 5000;",
      "",
      // THE GROUPS THAT ANSWER PER VIEWER (1.546.0), and the token that
      // makes them answer at all.
      //
      // Every other group is the same for everybody and is fetched with
      // no token ON PURPOSE: a token's account mode opts a request into
      // the Test or Demo queue, so sending it everywhere would change
      // what the other groups say. An allow-list, not a blanket.
      //
      // Signed out, my-stats answers four nulls rather than refusing,
      // so this page reads its other groups exactly as it always did.
      'const COMPS589_VIEWER_GROUPS = ["my-stats"];',
      "let comps589Token = null;",
      "",
      "async function comps589Fetch(group) {",
      "  try {",
      "    const mine = COMPS589_VIEWER_GROUPS.indexOf(group) >= 0 && comps589Token;",
      "    const res = await fetch(`${BASE_URL}/api/v1/${group}`, {",
      '      headers: comps589ApiHeaders(mine ? { "x-auth-token": comps589Token } : {}),',
      "    });",
      "    return res.ok ? await res.json() : null;",
      "  } catch {",
      "    return null;",
      "  }",
      "}",
      "",
      // THE FIELDS THIS PAGE WAS GENERATED WITH (1.529.0), per group.
      //
      // Reported 28 Sep 2026, of two boxes left unticked on the Last
      // Result card: "I have the first and last options on this card
      // showing the data on test site, if they're not enabled they
      // shouldn't be displaying."
      //
      // THE SECTION ONLY EVER CARRIED ITS GROUP. `data-comps-group`
      // names the group; the script fetched it and printed EVERY field
      // the API served, so the tick boxes chose which SECTIONS appeared
      // and nothing about what was in them. Competition ID and "This is
      // the newest result" were both served and both unticked, and both
      // turned up on the page.
      //
      // THE WINNERS TABLE HAS ALWAYS DONE THIS PROPERLY - see
      // COMPS589_WINNER_COLUMNS just below, which carries the ticked
      // columns. This is the same idea for the other groups.
      //
      // A GROUP THIS PAGE DOES NOT KNOW IS LEFT ALONE. A partner who
      // adds a data-comps-group of their own by hand gets what the API
      // serves, which is what they would have got before this existed.
      // And a field enabled in the API AFTER this page was written does
      // not appear until the page is generated again - the same rule the
      // saved layout already follows, and the reason the panel says when
      // the markup is behind.
      `const COMPS589_FIELDS = ${JSON.stringify(
        Object.fromEntries(chosenGroups.filter((g) => g.group !== "winners").map((g) => [g.group, g.fields.map((f) => f.key)]))
      )};`,
      "",
      // The winners columns this page was generated with: the fields
      // ticked in Quick Start, with the labels the operator gave them.
      `const COMPS589_WINNER_COLUMNS = ${JSON.stringify(
        (chosenGroups.find((g) => g.group === "winners")?.fields || [])
          // A SUPPORTING FIELD IS NOT A COLUMN (1.598.0), which is what
          // the tick box beside it already promises: "sent to your page,
          // not shown as a row on it". The field CARDS have filtered
          // these since 1.527.0; this list never did, so ticking
          // Currency symbol put a column of "£" - or, before the flags
          // reached here, a column of dashes - in the winners table.
          .filter((f) => !f.supporting)
          // A THIRD ELEMENT SINCE 1.597.0: whether the column is an
          // amount. Taken from the field's own declaration, so the table
          // knows no field names - the same rule the field cards follow.
          .map((f) => [QUICK_START_WINNER_KEYS[f.key] || f.key, f.label, !!f.money])
      )};`,
      ...winnersTableCode(),
      ...fieldListCode(),
      "async function comps589Refresh() {",
      '  const hosts = document.querySelectorAll("[data-comps-group]");',
      "  await Promise.all(",
      "    [...hosts].map(async (el) => {",
      '      const data = await comps589Fetch(el.getAttribute("data-comps-group"));',
      "      if (!data) return;",
      "      // WINNERS IS A LIST, NOT A FIELD GROUP (1.465.0).",
      "      //",
      '      // Reported 23 Sep 2026: the Winners section read "undefined:',
      '      // undefined". Every other group answers { key: { label, value } }',
      "      // and the loop below walks it. /api/v1/winners is a public list",
      "      // of rows - the registry holds one value per entry and an array",
      "      // of rows cannot be said in that shape, which is why it has a",
      "      // route of its own - so the loop found one entry, `winners`,",
      "      // whose .label and .value were both undefined and printed them.",
      "      if (Array.isArray(data.winners)) {",
      "        el.replaceChildren(",
      "          data.winners.length",
      "            ? comps589WinnersTable(data.winners)",
      '            : Object.assign(document.createElement("p"), { textContent: COMPS589_TEXT.noWinners })',
      "        );",
      "        return;",
      "      }",
      "      // ONLY THE FIELDS THIS PAGE WAS GENERATED WITH (1.529.0).",
      "      // An unknown group keeps everything - see COMPS589_FIELDS.",
      '      const want = COMPS589_FIELDS[el.getAttribute("data-comps-group")];',
      "      const shown = Object.entries(data)",
      '        .filter(([k, f]) => k !== "mode" && f && f.value !== null && f.value !== "")',
      // A FIELD THE PAGE CONSUMES IS NOT A FIELD THE TICK BOXES CHOOSE
      // (1.548.0). Reported 29 Sep 2026: "\"Prize money\" on the \"Last
      // Result\" card doesn't show the currency on front of the number".
      //
      // 1.525.0 put a currency_symbol on this group for exactly that
      // report, and 1.526.0 taught the page to find it by its own
      // declaration. Both worked. What did not is that the symbol is
      // ALSO a tick box - marked "sent to your page, not shown as a row
      // on it", which is a fair thing for an operator to untick - and
      // this filter runs BEFORE the list is built. Untick it and every
      // amount on the page silently loses its symbol.
      //
      // comps589FieldList already says it reads the currency "before
      // anything is dropped"; that was true of its own supporting
      // filter and not of this one, which had already dropped it.
      //
      // SO SUPPORTING FIELDS ARE ALWAYS KEPT HERE, and always dropped
      // from the rows there. An input the page reads is not something
      // the tick boxes should be able to take away, and keeping it
      // prints nothing extra - which is the whole meaning of the flag.
      "        .filter(([k, f]) => f.supporting || !want || want.indexOf(k) >= 0)",
      "        .map(([, f]) => f);",
      "      el.replaceChildren(",
      "        shown.length",
      "          ? comps589FieldList(shown)",
      '          : Object.assign(document.createElement("p"), {',
      '              className: "comps-empty",',
      "              textContent: COMPS589_TEXT.nothingToShow,",
      "            })",
      "      );",
      "    })",
      "  );",
      "}",
      "",
      // EVERY WIDGET'S HANDLE, DECLARED FIRST (1.471.0, moved up at
      // 1.474.0). It used to sit further down, inside the block that
      // runs the session script - after the view router below had
      // already been called once, so on a page with the account widget
      // the first thing the script did was read `.account` off an
      // undefined. `var` hoists the name and not the value, so nothing
      // parsed wrong and the page simply threw on load. FOUND BY RUNNING
      // IT, like 1.465.0's; a parse check cannot see it. (The router
      // itself went in 1.533.0; the declaration stays first, because the
      // reason it moved was never only about that one caller.)
      // The SAME condition as the block that mounts them, written the
      // same way, so the two cannot drift apart. (`usesSession` says it
      // too, but it is declared below this array.)
      ...(WIDGETS.filter((w) => onPage(w.id)).length
        ? ["", "var comps589Handles = { competition: null, account: null, replay: null };"]
        : []),
      // ONE PAGE AGAIN (1.533.0). Asked for on 28 Sep 2026, of the
      // winners table: "its not showing on the page under the
      // competition where I expect to see it" - and then, of the two
      // ways it could go: "Under the competition only - drop
      // data-comps-view=\"winners\" from that section, and the Winners
      // nav item with it. One page again."
      //
      // 1.471.0 made the page two views so a winners list could have an
      // address of its own, and 1.474.0 gave the account section a third.
      // Both are gone with it: there is one page, every section is on
      // it, and the winners table sits where the markup already put it -
      // under the competition, after the last result.
      "",
      // ---- THE LIGHT, THE CLOCK AND THE PRICES ARE A WIDGET NOW
      //      (1.532.0) ----
      //
      // Everything that used to be here - a countdown, a typewriter, a
      // money ticker and four hard-coded messages - is 589comps-status.js,
      // ticked under Widgets like any other. Asked for on 28 Sep 2026:
      // "if we're using other javascript for functionality to create
      // functionality outwith the scope of the existing widgets then we
      // could maybe create a new widget so if the partner wants to
      // recreate the exact functionality they know exactly what they
      // need because they selected on in the quick start page."
      //
      // WHICH IS WHAT AN EXAMPLE PAGE IS FOR. A partner reading this
      // script could not tell which of it was the product and which was
      // us writing JavaScript on their behalf. What is left fetches the
      // API and draws what it serves, and nothing else.
      //
      // The band's markup is still in the page above, and still yours:
      // the widget fills the elements it finds and creates none.
      "",
      "comps589Refresh();",
      "setInterval(comps589Refresh, REFRESH_MS);",
      ...(WIDGETS.filter((w) => onPage(w.id)).length
        ? [
            "",
            "// Widgets. Each needs its own options — see the integration guide;",
            "// the replay widget also needs a competitionId.",
            "//",
            "// No `sections` here on purpose: the competition widget reads the",
            "// layout saved above (GET /api/v1/widget-layout), so the page needs",
            "// one div and the arrangement can change without a deploy.",
            // THE TOKEN COMES FROM THE PARTNER'S SERVER (1.442.0), which
            // holds the key however their host allows; the widgets start
            // once it has answered. 1.441.0 declared `sessionToken = null`
            // here, which ran but never signed anybody in.
            "",
            ...sessionFetchCode(
              sessionPathFor("live"),
              sites[1].separate ? { domain: sites[1].domain, path: sessionPathFor("test") } : null
            ),
            "",
            ...signInCode(
              signInPlanFor("live"),
              sites[1].separate ? { domain: sites[1].domain, plan: signInPlanFor("test") } : null
            ),
            "",
            // EVERY WIDGET THIS PAGE MOUNTED, KEPT (1.465.0).
            //
            // Reported 23 Sep 2026: signed up through the generated form,
            // "there is a sign out button which would suggest the user is
            // logged in. Clicking My Account doesn't bring up the account
            // navigation though, it bumps be back to the login section".
            //
            // It did, and it had to. Everything below mounts once, inside
            // this one .then, with whatever token the session script gave
            // at page load - null, for a visitor who is about to sign in.
            // Signing in fetched a new token and told nobody: the account
            // widget still held null, so the click asked for a sign-in
            // again. Both widgets document the way to say otherwise -
            // updateToken on the competition widget, setToken on each
            // account handle - and this page never called either.
            "function comps589HandOver(token) {",
            "  // AND THE GROUPS THIS PAGE READS ITSELF (1.546.0). My Stats",
            "  // is one visitor's own; without this the card would sit on",
            "  // four nulls until the next refresh after a sign-in, and",
            "  // keep showing them after a sign-out.",
            "  comps589Token = token || null;",
            "  if (typeof comps589Refresh === \"function\") comps589Refresh();",
            "  if (comps589Handles.competition && comps589Handles.competition.updateToken) {",
            "    comps589Handles.competition.updateToken(token);",
            "  }",
            "  // init() returns one handle per marked element, so there may",
            "  // be more than one.",
            "  (comps589Handles.account || []).forEach(function (h) {",
            "    if (h && h.setToken) h.setToken(token);",
            "  });",
            "  // NOTHING FOR THE REPLAY HERE (1.470.0): it lives in the",
            "  // account widget's Replays tab, which is drawn when the",
            "  // visitor opens it, from the token the widget holds - the",
            "  // one the line above has just given it.",
            "}",
            "",
            // THE TAB'S OWN FUNCTION, at the top of the script beside the
            // hand-over rather than inside the session callback, because
            // `extraSections` below names it and 1.465.0 is the lesson
            // about which of those two scopes can see the other.
            ...(replaysInAccount ? [replaysSectionCode("Comps589Replay", comp.theme), ""] : []),
            // WHO REPORTS THE PAYMENT (1.476.0) is decided here, once:
            // with the competition widget on the page it does it, and
            // this tab stays out of the way; without it, nothing else
            // ever would, so the tab follows the purchase itself.
            ...(buyCreditInAccount ? [buyCreditSectionCode({ follow: !onPage("competition") }), ""] : []),
            "comps589Session().then((sessionToken) => {",
            "  // THE TOKEN THIS PAGE LOADED WITH, for the groups it reads",
            "  // itself. comps589HandOver covers signing in and out; this",
            "  // covers arriving already signed in.",
            "  comps589Token = sessionToken || null;",
            ...(usesSignInForm
              ? [
                  "  // The sign-in Quick Start wrote: its forms, and the Sign out",
                  "  // button once somebody is signed in.",
                  "  comps589WireSignIn();",
                  '  var comps589Out = document.getElementById("comps-signout");',
                  "  if (comps589Out && sessionToken) comps589Out.hidden = false;",
                ]
              : []),
            ...WIDGETS.filter((w) => onPage(w.id) && w.id !== "replay").flatMap((w) =>
              w.id === "account"
                ? // THE ACCOUNT WIDGET NEEDS THE SESSION (1.428.0): it shows
                  // one visitor's own account, so there is nothing to draw
                  // without their token.
                  // Since 1.430.0 it finds the marked item itself (init),
                  // and signed out it asks for a sign-in rather than
                  // needing a token.
                  `comps589Handles.account = ${w.global}.init({\n` +
                  `  baseUrl: ${JSON.stringify(base)},\n` +
                  `  token: sessionToken, // from your server's vouch; leave out while signed out\n` +
                  `  pageToken: comps589PageToken,\n` +
                  `  pageTokenExpiresIn: comps589PageTokenExpiresIn,\n` +
                  `  theme: ${JSON.stringify(navTheme)}, // "light", "dark" or "none" (your own CSS)\n` +
                  (canSignIn
                    ? `  // Signed out, choosing "My account" calls this: it opens your\n` +
                      `  // sign-in page, or the sign-in form on this page, as saved in\n` +
                      `  // Quick Start.\n` +
                      `  onLoginRequested: comps589RequestSignIn,\n`
                    : // NO HANDLER WHEN THERE IS NOWHERE TO SEND THEM
                      // (1.461.5): with one, the widget takes the click and
                      // the visitor gets nothing; without one it leaves the
                      // click alone, so your own "My account" link goes
                      // wherever your markup says.
                      `  // No sign-in page or form is set for this site in Quick Start, so\n` +
                      `  // nothing is passed here: the widget leaves a signed-out click on\n` +
                      `  // your "My account" alone and your own link follows its href.\n` +
                      `  // Set one in Quick Start, or add:\n` +
                      `  //   onLoginRequested: () => yourSignIn().then(token => token),\n`) +
                  // 1.431.0: the partner's own tabs beside the seven.
                  // THE REPLAYS TAB IS ONE OF THEM (1.470.0), written for
                  // you when the replay widget is ticked - and absent when
                  // it is not, which is the whole of "if the Replay widget
                  // isn't selected there should be no Replay tab".
                  (extraTabs.length
                    ? `  // ${extraTabs.map((t) => t.label).join(" and ")}: ${
                        replaysInAccount && buyCreditInAccount
                          ? "the visitor's finished competitions, and a top-up"
                          : replaysInAccount
                            ? "the visitor's finished competitions, watched back"
                            : "a top-up"
                      }\n` +
                      `  // in your page's own account section. Add your own beside ${
                        extraTabs.length > 1 ? "them" : "it"
                      }\n` +
                      `  // - see the guide, area 9.\n` +
                      // ONE TAB ON ONE LINE, two or more one per line -
                      // this is code somebody reads.
                      (extraTabs.length === 1
                        ? `  extraSections: [${extraTabs.map(quickStartTabCode).join("")}],\n`
                        : `  extraSections: [\n${extraTabs.map((t) => `    ${quickStartTabCode(t)},\n`).join("")}  ],\n`)
                    : `  // Your own tabs beside these (replays, a top-up, a game):\n` +
                      `  // extraSections: [{ id, label, render(element, ctx) }] - see the guide, area 9.\n`) +
                  `});`
                : w.id === "competition"
                  ? // THE SAME RULE FOR THE COMPETITION WIDGET (1.461.5):
                    // with nowhere to send a signed-out visitor, it gets no
                    // handler, so it does not offer a "Log in to play"
                    // button that would do nothing.
                    `comps589Handles.competition = ` +
                    competitionMountCode(w.global, `comps-${w.id}`, base, {
                      ...comp,
                      loginHandler: comp.loginHandler && canSignIn,
                    })
                  : w.id === "status"
                    ? // THE BAND (1.532.0), AND ONLY THE WORDS YOU CHANGED
                      // (1.539.0). 1.532.0 passed nothing at all, because
                      // naming every word here would put our wording back
                      // into the partner's page. Operator controls arrived
                      // on 29 Sep 2026 and the rule survives them: a box
                      // left at the widget's own default is not written
                      // out, so touching nothing still emits the one-line
                      // mount this used to emit always.
                      `comps589Handles.status = ${w.global}.mount({\n` +
                      `  baseUrl: ${JSON.stringify(base)},\n` +
                      `  pageToken: comps589PageToken,\n` +
                      `  pageTokenExpiresIn: comps589PageTokenExpiresIn,\n` +
                      statusWordLines(status) +
                      `  // Its other options stay yours: waitingHold, units, typing,\n` +
                      `  // factHold, pollIntervalMs. The defaults are in the widget\n` +
                      `  // and listed in the guide, area 10; onChange(state) hands\n` +
                      `  // you the state to draw yourself.\n` +
                      `});`
                    : // Nothing else mounts here: the replay is a tab inside
                      // the account widget above (1.470.0).
                      ""
            )
              .join("\n")
              .split("\n")
              .map((line) => (line ? `  ${line}` : line)),
            // BACK FROM A TOP-UP, IN MODAL STYLE (1.476.0). The header
            // style reads #account/<section> itself and opens on it; a
            // modal has no address of its own, so the page opens it -
            // otherwise a buyer returning from the payment page lands on
            // a page that says nothing about what they just did.
            ...(buyCreditInAccount && navStyle !== "header"
              ? [
                  '  if (location.hash === "#account/buy-credit") {',
                  "    (comps589Handles.account || []).forEach(function (h) {",
                  '      if (h && h.open) h.open("buy-credit");',
                  "    });",
                  "  }",
                ]
              : []),
            "});",
          ]
        : []),
    ].join("\n");

    // THE SERVER'S FILES, beside the page (1.442.0): each box is one file.
    const usesSession = WIDGETS.some((w) => onPage(w.id));
    // ONE SET PER SITE ON THE KEY (1.447.0): the live site's always, the
    // test site's when the key has a separate test domain (sites, above).
    const files = usesSession
      ? sites
          // A SITE WITH NO ADDRESS GETS NO FILES (1.454.0): there is
          // nothing for the page to ask, so there is nothing to generate.
          .filter((st) => st.separate && sessionPathFor(st.site))
          .flatMap((st) => [
            {
              site: st.site,
              heading: `${st.label} site${st.domain ? ` (${st.domain})` : ""}`,
              name: sessionFileFor(st.site),
              path: `${QUICK_START_WEB_ROOT}${sessionPathFor(st.site)}`,
              // THREE KINDS, NOT TWO (1.927.0). This tested the lookup for
              // truthiness and then reached for `.idKey`, which the
              // quick-start-accounts kind does not have - so a partner using
              // the published accounts system was told their visitor comes
              // from $_SESSION['undefined']. It reads as a fault in THEIR
              // setup rather than a slip in ours, and cost real attention on
              // 23 Oct 2026 before the integration was found to be working.
              //
              // backend/lib/quick-start-checklist.js owns the canonical
              // describer; this file cannot import it, so it carries the same
              // three branches and source-rules asserts neither side reaches
              // for `.idKey` without testing `kind` first.
              where: userLookupFor(st.site)?.kind === "php-session" && userLookupFor(st.site)?.idKey
                ? `On your ${st.site} site's server, at ${sessionPathFor(st.site)}. It reads your signed-in visitor from $_SESSION['${userLookupFor(st.site).idKey}'] - nothing to edit.`
                : userLookupFor(st.site)?.kind === "quick-start-accounts"
                  ? `On your ${st.site} site's server, at ${sessionPathFor(st.site)}. It reads your signed-in visitor from the Quick Start accounts system - nothing to edit.`
                  : `On your ${st.site} site's server, at ${sessionPathFor(st.site)}. Edit comps_current_user() to return your signed-in visitor (see example-token.php in the examples download, Partners → Integration).`,
              body: sessionScriptPhp(base, {
                file: sessionFileFor(st.site),
                site: st.site,
                domain: st.domain,
                testDomain: st.site === "live" ? testDomain : null,
                userLookup:
                  signInKindFor(st.site) === "form" ? { kind: "quick-start-accounts" } : userLookupFor(st.site),
              }),
            },
            // THE ACCOUNTS AND THE FORM'S SERVER SIDE (1.458.0), for a site
            // using the sign-in Quick Start writes.
            ...(signInKindFor(st.site) === "form"
              ? [
                  {
                    site: st.site,
                    heading: `${st.label} site${st.domain ? ` (${st.domain})` : ""}`,
                    name: "comps-auth.php",
                    path: `${quickStartPaths(sessionPathFor(st.site)).beside}/comps-auth.php`,
                    where: `Beside ${sessionFileFor(st.site)}, at ${authPathFor(st.site)}. Sign up, sign in and sign out for the accounts below.`,
                    body: authPhp(),
                  },
                  {
                    site: st.site,
                    heading: `${st.label} site${st.domain ? ` (${st.domain})` : ""}`,
                    name: "comps-accounts.php",
                    path: `${quickStartPaths(sessionPathFor(st.site)).beside}/comps-accounts.php`,
                    where:
                      "Beside the other two. It keeps the accounts in a SQLite database, above public_html when " +
                      "that is writable and beside itself otherwise. If the site already has a JSON accounts " +
                      "file, it is imported once and kept. A starting point, not a finished account system.",
                    body: accountsPhp(sessionFileFor(st.site)),
                  },
                ]
              : []),
            // ALWAYS, NOT ONLY WHEN IT IS THE CHOSEN HOME FOR THE KEY
            // (1.461.5). Reported 22 Sep 2026: "The zip references a file
            // called 589comps-config.php but doesn't include it".
            // comps-session.php names this file in its fallback chain and
            // in its error message however the key is kept, so a download
            // that leaves it out is a download that is missing a file its
            // own error tells you to make.
            {
              site: st.site,
              heading: `${st.label} site${st.domain ? ` (${st.domain})` : ""}`,
              name: "589comps-config.php",
              path: "589comps-config.php",
              optional: signIn.keyWhere[st.site] !== "config",
              // NEVER UPLOADED BY A PUBLISH (1.461.5). This is the one
              // file the partner edits - their API key goes in it - so a
              // second publish would put a placeholder over the key and
              // take the site down. It goes in the zip, and there once, by
              // hand.
              neverPublish: true,
              where:
                signIn.keyWhere[st.site] === "config"
                  ? `Paste your API key into it. In the folder above ${QUICK_START_WEB_ROOT} (best), or in a folder called private beside ${sessionFileFor(st.site)}, or beside it.`
                  : `Only if your host cannot set the COMPETITION_API_KEY environment variable you chose: paste your key into it and put it above ${QUICK_START_WEB_ROOT}. With the variable set, this file is ignored - the variable is looked at first.`,
              body: configFilePhp(sessionFileFor(st.site)),
            },
          ])
      : [];
    // WHAT THE PAGE IS TOLD ABOUT THE PARTNER'S OWN STYLESHEET
    // (1.592.0). Built once here so all five pages below - the three
    // real ones and the two inlined for the preview - carry the same
    // answer, and so the preview shows what the download will do.
    // THE NAME IS A SUGGESTION, NOT A RULE. It is named after their site
    // so the default is right without being typed, but where the file
    // actually sits is theirs - /assets/, a CDN, a path with a cache
    // buster on it - so a typed href wins over the generated name.
    const own = {
      mode: ownCss.mode,
      href: ownCss.mode === "file" ? ownCss.href.trim() || ownCssFile : "",
      css: ownCss.css,
    };
    const pages = {
      none: combineQuickStartCode(html, js, pageTitle, "none", { own }),
      light: combineQuickStartCode(html, js, pageTitle, "light", { own }),
      dark: combineQuickStartCode(html, js, pageTitle, "dark", { own }),
    };
    // THE SAME PAGES, FOR LOOKING AT (1.475.1): identical but for the
    // stylesheet, which is inlined because the preview iframe has no
    // server to fetch a file from. Nothing but the iframe reads these -
    // the HTML box, the Copy button, the zip and the publish all take
    // `pages`, which is what goes on the partner's server.
    const previews = {
      none: pages.none,
      light: combineQuickStartCode(html, js, pageTitle, "light", { inlineCss: true, own }),
      dark: combineQuickStartCode(html, js, pageTitle, "dark", { inlineCss: true, own }),
    };
    const built = {
      pages,
      previews,
      page: pages[pageStyle] || pages.none,
      files,
      // WHICH SITES THE CODE IS FOR (1.452.0), for the Live/Test tabs over
      // the files: the key's sites, and the domain each tab names.
      sites: sites.filter((st) => st.separate).map(({ site, label, domain }) => ({ site, label, domain })),
    };
    setOutput(built);
    // RECORDED WITH THE BUILD, from the same values it was just built
    // from (1.488.0). Set here rather than in an effect: the two must
    // move together, or the page says it is behind the instant it is not.
    setBuiltFrom(quickStartChoicesNow());
    setOutputSite("live");
    // RETURNED AS WELL AS SET (1.460.0): a publish uploads what was just
    // built, rather than waiting for the state to come round.
    return built;
  }

  // BEHIND, NOT WRONG: what is on the page and what the four buttons send
  // are the same build; it is just older than the ticks. Nothing to say
  // before the first Generate, because the buttons build for themselves
  // when there is nothing to send.
  const behindChoices = !!output && builtFrom !== null && builtFrom !== quickStartChoicesNow();
  const offCount = (groups || [])
    .flatMap((g) => g.fields.map((f) => ({ ...f, group: g.group })))
    .filter((f) => picked[`${f.group}.${f.key}`] && !f.enabled).length;
  // WHAT A CLOSED SECTION SAYS ABOUT ITSELF (1.464.0). A heading with a
  // triangle beside it tells you nothing about whether you have already
  // dealt with what is behind it; a count does.
  const totalFieldCount = (groups || []).reduce((n, g) => n + g.fields.length, 0);
  const pickedFieldCount = (groups || []).reduce(
    (n, g) => n + g.fields.filter((f) => picked[`${g.group}.${f.key}`]).length,
    0
  );
  const widgetOptionsNote = [
    ...(widgets.competition ? ["competition"] : []),
    ...(widgets.account ? ["account"] : []),
  ].join(" and ") || "nothing ticked";
  const ftpSavedNote =
    ["live", "test"].filter((site) => ftpFor(site)).map((site) => (site === "live" ? "Live" : "Test")).join(" and ") ||
    "nothing saved";
  // HOW MUCH IS BEHIND THE FOLD (1.503.0), so the heading says what
  // opening it costs. EVERY site's files, not the tab's: the tab inside
  // shows one site at a time, and the number on a shut card is about
  // what is in the card.
  const serverFileCount = (output?.files || []).length;
  const serverFileNote = serverFileCount
    ? `${serverFileCount} file${serverFileCount === 1 ? "" : "s"}`
    : "nothing generated yet";
  // THE WIDGETS THAT WILL NOT MATCH THE PAGE BEING SENT (1.461.7, kept at
  // 1.464.0): read off pageStyle, not the tab being looked at.
  const styleMismatches = [
    ...(widgets.competition && comp.theme !== "none" && comp.theme !== pageStyle ? ["the competition widget"] : []),
    ...(widgets.account && navTheme !== "none" && navTheme !== pageStyle ? ["the account widget"] : []),
  ];
  // AND THE CASE WHERE THAT IS NOT COSMETIC (1.489.0).
  //
  // Reported on 26 Sep 2026 with a screenshot of 589compstest.com: "The
  // text on the slider is there but its not visible because its not
  // white." Measured on the published page: the competition widget was
  // mounted `theme: "light"` on the dark template, and the saved layout
  // put the partner's own sections inside the widget - so
  // `.rcw-root{color:var(--rcw-fg)}` painted them at #1c1e26 on a panel
  // of #1c2027. The page's own <h2> survived because it sets a colour of
  // its own; everything that inherited disappeared.
  //
  // THE COMPETITION WIDGET ONLY. The account widget draws its own markup
  // and nobody else's.
  const widgetPaintsYourTemplate =
    widgets.competition &&
    comp.theme !== "none" &&
    comp.theme !== pageStyle &&
    savedLayoutHasTemplate(savedLayout);

  return (
    <div>
      <h3 style={{ marginTop: 0 }}>Quick Start</h3>

      {/* THE FIELDS FIRST, THE LAYOUT UNDERNEATH (1.419.0), as asked on
          21 Sep 2026. The layout's card templates are generated from the
          fields switched on in the API, so it reads naturally after them. */}
      {/* HOW TO USE THIS PAGE (1.442.0). Asked on 22 Sep 2026: "clear
          instructions on how to use the Quick Start page (at the top of
          the page)... The partner must be able to read this page and use it
          without having to ask questions." */}
      <QuickStartChecklist checklist={checklist} onRefresh={() => loadChecklist([])} />
      <QuickStartHowTo />

      {error && <p style={{ color: "var(--accent-red)", fontSize: 13 }}>{error}</p>}
      {!groups && !error && <p className="sub">Loading the field list…</p>}

      {groups && (
        <ViewOnlyGate canEdit={canEdit} showNotice>
          {/* THREE SECTIONS, ALL CLOSED TO START WITH (1.464.0). Asked
              for on 23 Sep 2026: "Make it more clear and legible.
              Everything looks a a bit squashed up and difficult to read
              and understand." Everything was on screen at once - fifty
              tick boxes and two servers' worth of forms - with nothing
              saying which of it a partner had to touch. A closed section
              still says what is inside it. */}
          {/* RENAMED "Template Data" (1.538.0), asked for on 29 Sep 2026
              against a screenshot of the header. The id did NOT move with
              it: aria-controls and the open-state key are built from it,
              and nobody sees it.

              THIS NOTE IS OUTSIDE THE ELEMENT. quick-start-sections reads
              the tag, the id and the title as three consecutive lines, so
              a comment among them does not change what it checks - it
              drops this section out of the list entirely. */}
          <QuickStartSection
            id="widget-data"
            title="Template Data"
            // WHAT THE TICKS DO, AFTER 1.531.0. They used to write one
            // section per group, and the blurb said so. The example page
            // now draws the last result and the winners table and
            // nothing else, so that sentence had become a promise the
            // generator no longer keeps. The ticks still decide what
            // those two show, and they are still carried into the page -
            // a data-comps-group written by hand shows the fields ticked
            // here and no others (1.529.0).
            blurb="Which of the API's fields the page uses. The example page draws the last result and the winners table from these; the rest are listed in the page's own script, for a section you add yourself."
            note={`${pickedFieldCount} of ${totalFieldCount} ticked`}
            open={!!openSection["widget-data"]}
            onToggle={() => toggleSection("widget-data")}
          >
            {/* ONE CARD PER GROUP, AS MANY ACROSS AS FIT (1.420.0). Asked on
                21 Sep 2026: "Can each checkbox section have its own card? Also
                use the space available on the page" — the groups ran down the
                left in one long column with the rest of the page empty. */}
            {/* COLUMNS, NOT A GRID: the groups run from 1 to 15 fields, and a
                grid row is as tall as its tallest card, which left most of
                the space as gaps. Columns pack each card under the one above
                it — measured on a 1825px screen: 490px tall against 640px. */}
            <div style={{ columns: "300px", columnGap: 16 }}>
          {groups.map((g) => (
            <div key={g.group} style={QUICK_START_CARD}>
              <strong style={{ fontSize: 13 }}>{g.groupLabel}</strong>
              <div style={{ marginTop: 6 }}>
                {g.fields.map((f) => {
                  const id = `${g.group}.${f.key}`;
                  return (
                    <label
                      key={id}
                      style={{
                        display: "block",
                        fontSize: 12.5,
                        opacity: f.enabled ? 1 : 0.65,
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={!!picked[id]}
                        onChange={() => toggle(setPicked)(id)}
                        disabled={!canEdit}
                      />{" "}
                      {f.label}
                      {!f.enabled && (
                        <span style={{ color: "var(--accent-yellow)" }}>
                          {" "}
                          — off in the API
                        </span>
                      )}
                      {/* USED, NOT PRINTED (1.527.0). The generated page
                          consumes these to format an amount or size a
                          picture and does not give them a row of their
                          own - so a tick here would otherwise look
                          ignored. Marked rather than hidden or disabled,
                          the same way a field that is off in the API is
                          marked: the tick is the operator's, and what it
                          does is ours to say. The API serves it either
                          way, which is what the wording has to convey. */}
                      {f.enabled && f.supporting && (
                        <span data-quick-start-supporting={`${g.group}.${f.key}`} style={{ opacity: 0.65 }}>
                          {" "}
                          — sent to your page, not shown as a row on it
                        </span>
                      )}
                      {/* IT COMES AND GOES (1.548.0). A conditional
                          field is null for whole phases of a
                          competition - "Converts to lottery in" the
                          moment it has converted - and the page leaves
                          the row out rather than printing a dash, so a
                          row appearing and disappearing looks like a
                          fault unless the tick box says it will. Same
                          treatment as `supporting` above, and for the
                          same reason: the tick is the operator's, and
                          what it does is ours to say. */}
                      {f.enabled && f.conditional && (
                        <span data-quick-start-conditional={`${g.group}.${f.key}`} style={{ opacity: 0.65 }}>
                          {" "}
                          — shown only while it applies, not as a dash
                        </span>
                      )}
                    </label>
                  );
                })}
              </div>
              <QuickStartMaskNote group={g} unmasked={unmasked} />
            </div>
          ))}
            </div>
          </QuickStartSection>

          {/* A SECTION OF ITS OWN (1.469.0), asked for on 25 Sep 2026:
              "Can you make this collapsible like the others on the quick
              start page?"
              1.464.0 left these in the open, reasoning that what goes on
              the page is the first choice anybody makes here. The page now
              has three sections and one card sitting between two of them,
              which reads as something that was missed rather than
              something deliberate - and a shut section that says "2 of 3
              ticked" answers the question the open card was there to
              answer.
              STILL NOT INSIDE Widget Configuration: that decision was
              about which section holds them ("options only - the widget
              tick boxes stay outside") and is unchanged. They are their
              own section, before it. */}
          <QuickStartSection
            id="widgets"
            title="Widgets"
            blurb="Which widgets go on the page — the playable grid, the visitor's account, the replay of a finished competition, which is a Replays tab inside that account, and the live status band."
            note={`${WIDGETS.filter((w) => widgets[w.id]).length} of ${WIDGETS.length} ticked`}
            open={!!openSection["widgets"]}
            onToggle={() => toggleSection("widgets")}
          >
            <div style={QUICK_START_CARD}>
              {WIDGETS.map((w) => (
                <div key={w.id}>
                  <label style={{ display: "block", fontSize: 12.5 }}>
                    <input
                      type="checkbox"
                      checked={!!widgets[w.id]}
                      onChange={() => toggle(setWidgets)(w.id)}
                      disabled={!canEdit}
                    />{" "}
                    {w.label}
                  </label>
                  {/* TWO WIDGETS HAVE NO OPTIONS HERE, FOR DIFFERENT
                      REASONS (1.534.0). Reported 28 Sep 2026 of the status
                      widget: "There are no options for this widget in
                      widget configuration." The condition was a deny-list
                      of one - the replay, whose options belong to the
                      account widget it is a tab inside - so the fourth
                      widget inherited a link to a card that does not
                      exist. Named rather than counted, because the two
                      are not the same case and the next widget should
                      have to say which it is. */}
                  {!WIDGETS_WITHOUT_OPTIONS.includes(w.id) && widgets[w.id] && (
                    <QuickStartOptionsLink onOpen={() => openSectionAnd("widget-configuration")} />
                  )}
                  {/* AND THE STATUS WIDGET SAYS WHERE ITS OPTIONS ARE
                      INSTEAD. They are the partner's, not the operator's:
                      1.532.0 deliberately passes none of them in the
                      mount, so what a partner gets is the widget's
                      documented defaults rather than our wording written
                      into their page. A tick box that led nowhere and
                      said nothing would leave the same question
                      unanswered that 1.464.0's link was written for. */}
                  {/* ITS WORDS ARE YOURS NOW (1.539.0), so this says what
                      is left with the partner rather than that everything
                      is. The link above it goes to the card. */}
                  {w.id === "status" && widgets.status && (
                    <div
                      data-quick-start-status-options=""
                      style={{ fontSize: 12, opacity: 0.75, margin: "2px 0 8px 22px" }}
                    >
                      Its wording is in Widget Configuration. The rest &mdash; the units, the poll interval,
                      the typing speed and <code>onChange</code> &mdash; stays with the partner, set where
                      they mount it, and is listed in the integration guide, area 10.
                    </div>
                  )}
                  {/* THE BAND COMES WITH THE COMPETITION (1.524.0), so its
                      requirement is stated where that box is ticked and
                      nowhere else. Same shape as the replay note below:
                      the tick is the operator's, and what it will and
                      will not draw is ours to say. */}
                  {w.id === "competition" && widgets.competition && <QuickStartBandNote groups={groups} />}
                  {/* TICKED, AND NOWHERE TO GO (1.470.0). Marked rather
                      than hidden or disabled, the same way a field that
                      is off in the API is marked: the tick is the
                      operator's, and what it does - nothing, here - is
                      ours to say. */}
                  {w.id === "replay" && widgets.replay && !widgets.account && (
                    <div
                      data-quick-start-replay-needs-account=""
                      style={{ fontSize: 12, color: "var(--accent-yellow)", margin: "2px 0 0 22px" }}
                    >
                      Replays are a tab in the account widget. Tick that one as well, or this page gets no
                      replay — there is no account section for the tab to sit in.
                    </div>
                  )}
                </div>
              ))}
            </div>
          </QuickStartSection>

          <QuickStartSection
            id="widget-configuration"
            title="Widget Configuration"
            blurb="Everything about the page itself: how the ticked widgets look and behave, the page's title, and how each of your sites signs a visitor in."
            note={widgetOptionsNote}
            open={!!openSection["widget-configuration"]}
            onToggle={() => toggleSection("widget-configuration")}
          >
            {/* Only the WIDGET half can be empty - the page and the
                sign-in cards below are always worth showing. */}
            {!widgets.competition && !widgets.account && (
              <p style={{ margin: 0, fontSize: 12.5, opacity: 0.8 }}>
                No widget options: the competition and account widgets are both unticked above. The replay widget
                has none of its own &mdash; its options are the account widget&rsquo;s &mdash; and the status
                widget&rsquo;s belong to the partner, set where they mount it. The page and your sites are below.
              </p>
            )}
            {widgets.competition && (
              <div style={QUICK_START_CARD} aria-label="Competition widget options">
                <strong style={{ fontSize: 13 }}>Competition widget</strong>
                <CompetitionWidgetOptions
                  value={comp}
                  set={setCompOption}
                  canEdit={canEdit}
                  layoutSummary={describeSavedLayouts(savedLayout)}
                  layoutHasSlider={savedLayoutHasSlider(savedLayout)}
                />
              </div>
            )}
            {/* THE STATUS WIDGET'S WORDS (1.539.0). Every box starts at the
                widget's own default and only what you change is written
                into the generated page - see the mount, and the note in
                STATUS_WIDGET_DEFAULTS on why that rule matters. */}
            {widgets.status && (
              <div style={QUICK_START_CARD} aria-label="Status widget options">
                <strong style={{ fontSize: 13 }}>Status widget</strong>
                <p style={{ margin: "4px 0 6px 22px", fontSize: 12, opacity: 0.75 }}>
                  The words it writes. Anything you leave alone is not written into the page at all, so the
                  partner gets the widget&rsquo;s own default for it.
                </p>
                <div style={{ margin: "0 0 6px 22px", fontSize: 12, display: "grid", gap: 6 }}>
                  {STATUS_WIDGET_FIELDS.map((f) => (
                    <label key={f.key} style={{ display: "grid", gap: 2 }} data-status-word={f.key}>
                      <span>
                        {f.label} <span style={{ opacity: 0.6 }}>&mdash; {f.hint}</span>
                      </span>
                      <input
                        type="text"
                        value={status[f.key]}
                        maxLength={120}
                        disabled={!canEdit}
                        placeholder={STATUS_WIDGET_DEFAULTS[f.key]}
                        onChange={(e) => setStatusWord(f.key, e.target.value)}
                        style={{ fontSize: 12 }}
                      />
                    </label>
                  ))}
                  {/* THREE, AND ALWAYS THREE. The widget cycles them in
                      order while nothing is running; a fourth would never
                      be reached and a missing one would leave a hole. */}
                  <span style={{ marginTop: 2 }}>
                    The three lines it cycles while no competition is running
                  </span>
                  {status.waiting.map((line, n) => (
                    <input
                      key={n}
                      type="text"
                      value={line}
                      maxLength={120}
                      disabled={!canEdit}
                      placeholder={STATUS_WIDGET_DEFAULTS.waiting[n]}
                      onChange={(e) => setWaitingLine(n, e.target.value)}
                      data-status-waiting={n}
                      style={{ fontSize: 12 }}
                    />
                  ))}
                </div>
              </div>
            )}
            {/* MOVED HERE FROM UNDER ITS TICK BOX (1.464.0), which is where
                1.429.0 put it after "Selecting this widget doesn't give
                options to select the type of navigation". The tick box now
                links to this section by name, so the same question still
                has an answer on screen. */}
            {widgets.account && (
              <div style={QUICK_START_CARD} aria-label="Account widget options">
                <strong style={{ fontSize: 13 }}>Account widget</strong>
                <div
                  style={{ margin: "4px 0 6px 22px", fontSize: 12, display: "grid", gap: 4 }}
                  aria-label="Account navigation options"
                >
                  {[
                    {
                      name: "Navigation",
                      value: navStyle,
                      set: setNavStyle,
                      options: [
                        { id: "modal", label: "Modal with tabs" },
                        { id: "header", label: "Header" },
                      ],
                    },
                    ...(navStyle === "header"
                      ? [
                          {
                            name: "Menu",
                            value: navMenu,
                            set: setNavMenu,
                            options: [
                              { id: "dropdown", label: "Drop-down" },
                              { id: "list", label: "Plain list" },
                            ],
                          },
                        ]
                      : []),
                    {
                      name: "Colours",
                      value: navTheme,
                      set: setNavTheme,
                      options: [
                        { id: "light", label: "Light" },
                        { id: "dark", label: "Dark" },
                        { id: "none", label: "None" },
                      ],
                    },
                  ].map((g) => (
                    <div key={g.name} role="radiogroup" aria-label={g.name} style={{ display: "flex", gap: "2px 12px", flexWrap: "wrap", alignItems: "center" }}>
                      <span style={{ opacity: 0.7, flexBasis: "100%" }}>{g.name}</span>
                      {g.options.map((o) => (
                        <label key={o.id} style={{ display: "inline-flex", gap: 4, alignItems: "center" }}>
                          <input
                            type="radio"
                            name={`quick-start-account-${g.name}`}
                            checked={g.value === o.id}
                            onChange={() => g.set(o.id)}
                            disabled={!canEdit}
                          />
                          {o.label}
                        </label>
                      ))}
                    </div>
                  ))}
                  {/* THE BUY CREDIT TAB (1.476.0). Asked for on 26 Sep
                      2026: "589comps.com has the facility to 'Buy credit',
                      can you make this available on the 'my account'
                      section on quick start page templates?"
                      HERE RATHER THAN UNDER WIDGETS, because it is not a
                      widget: it is a section of the account one, the same
                      shape as 589comps.com's own and the one the account
                      widget's documentation says a partner should write. */}
                  <label style={{ display: "flex", gap: 6, alignItems: "flex-start", marginTop: 4 }}>
                    <input
                      type="checkbox"
                      checked={buyCreditTab}
                      onChange={(e) => setBuyCreditTab(e.target.checked)}
                      disabled={!canEdit}
                    />
                    <span>
                      <strong>Buy credit</strong> &mdash; a tab where a signed-in visitor tops up their entry
                      credit, paid for on your provider&rsquo;s hosted page and returning to this tab.
                    </span>
                  </label>
                  {/* WHAT THE TAB WILL SAY TODAY. The tab asks the API
                      itself and tells the visitor plainly when the site is
                      not selling credit; this is so the partner knows
                      before they ship it, not after. */}
                  {buyCreditTab && checklist && checklist.sellsEntryCredit === false && (
                    <span style={{ color: "var(--accent-yellow)", marginLeft: 22 }}>
                      This site is not selling entry credit at the moment, so the tab will say so. Settings &rarr;
                      Credits, &ldquo;Paid entry&rdquo;, turns it on.
                    </span>
                  )}
                  <a
                    href="#account-navigation-preview"
                    onClick={(e) => {
                      e.preventDefault();
                      document.getElementById("account-navigation-preview")?.scrollIntoView({ behavior: "smooth", block: "start" });
                    }}
                    style={{ color: "var(--accent-blue)" }}
                  >
                    See the preview below
                  </a>
                </div>
              </div>
            )}
            {/* MOVED IN HERE (1.467.0), asked for on 23 Sep 2026: "can we
                move The page, Signing visitors in - Live and Signing
                visitors in - Test cards so they show in Widget
                Configuration, the live and test cards should have their
                own tabs too". They were loose on the page between the
                section that holds the widget options and the one that
                holds the FTP details, which left this section holding
                less than its name promised and the page holding more. */}
            {/* THE PAGE ITSELF (1.446.0): Generate gives a complete page. */}
            <div style={QUICK_START_CARD} aria-label="The page">
              <strong style={{ fontSize: 13 }}>The page</strong>
              <label style={{ marginTop: 4, fontSize: 12, display: "grid", gap: 2 }}>
                <span style={{ opacity: 0.7 }}>Title, shown on the browser tab</span>
                <input
                  type="text"
                  value={pageTitle}
                  onChange={(e) => setPageTitle(e.target.value)}
                  disabled={!canEdit}
                  style={{ fontSize: 12 }}
                />
              </label>
              <span style={{ display: "block", marginTop: 4, fontSize: 12, opacity: 0.8 }}>
                Starts as your site&rsquo;s name, from Sitewide &rarr; Site Name; change it if this page should say
                something else.
              </span>
              <span style={{ display: "block", marginTop: 4, fontSize: 12, opacity: 0.8 }}>
                Generate gives a complete page, packed as{" "}
                <code>
                  {QUICK_START_WEB_ROOT}/{QUICK_START_PAGE_FILE}
                </code>{" "}
                &mdash; the file a browser asks for when it is given the domain on its own, so the competition is what
                the site shows.
              </span>
              {/* index.html IS THE SITE'S FRONT DOOR (1.461.10): a publish
                  writes over whatever is at that address, so say so. */}
              <span style={{ display: "block", marginTop: 4, fontSize: 12, color: "var(--accent-yellow)" }}>
                On a site that already has a home page, uploading replaces it. Rename the file after downloading if the
                competition should live somewhere else.
              </span>
            </div>
            {/* SIGNING VISITORS IN (1.442.0) - see sessionScriptPhp. */}
            {/* ONE CARD PER SITE ON THE KEY (1.447.0): "a "Live" card and a
                "Test" card also show the domains associated with the API key
                if they exist". BEHIND TABS SINCE 1.467.0 - one site's card
                at a time, the same shape Server Configuration already
                uses, rather than two long forms one under the other. */}
            <div style={{ marginTop: 14 }}>
              <strong style={{ fontSize: 13 }}>Signing visitors in</strong>
              <div style={{ marginTop: 6 }}>
                <TabBar
                  tabs={quickStartSites(savedLayout?.domains).map((st) => ({
                    id: st.site,
                    label: st.domain ? `${st.label} — ${st.domain}` : st.label,
                  }))}
                  activeId={signInTab}
                  onSelect={setSignInTab}
                />
              </div>
            </div>
            {quickStartSites(savedLayout?.domains)
              .filter((st) => st.site === signInTab)
              .map((st) => (
              <div key={st.site} style={QUICK_START_CARD} aria-label={`Signing visitors in - ${st.label}`}>
                <strong style={{ fontSize: 13 }}>Signing visitors in &mdash; {st.label}</strong>
                <div style={{ marginTop: 4, fontSize: 12, display: "grid", gap: 4 }}>
                  <span data-domain={st.domain || ""}>
                    {st.domain ? (
                      st.separate ? (
                        <>
                          Your {st.site} site: <code>{st.domain}</code>
                        </>
                      ) : (
                        <>
                          Your API key has one domain, <code>{st.domain}</code>, so there is no separate test site and
                          nothing to generate for one.
                        </>
                      )
                    ) : (
                      <>No {st.site} domain on your API key yet (Partners &rarr; Partners API Key).</>
                    )}
                  </span>
                  {st.separate && (
                    <>
                      <span style={{ opacity: 0.8 }}>
                        A small script on this site&rsquo;s server holds the API key and signs your visitor in to your{" "}
                        {st.site} site{st.domain ? " (it refuses to run on any other)" : ""}. The page asks it for the
                        session, so the page&rsquo;s code is the same wherever the key is kept.
                      </span>
                      <div role="radiogroup" aria-label={`Where your ${st.site} site's server keeps the API key`} style={{ display: "grid", gap: 2 }}>
                        <span style={{ opacity: 0.7 }}>Where this server keeps the API key</span>
                        {[
                          { id: "env", label: "An environment variable, COMPETITION_API_KEY (best)" },
                          { id: "config", label: "A config file (no environment variables, e.g. Hostinger)" },
                        ].map((o) => (
                          <label key={o.id} style={{ display: "flex", gap: 4, alignItems: "center" }}>
                            <input
                              type="radio"
                              name={`quick-start-key-where-${st.site}`}
                              checked={signIn.keyWhere[st.site] === o.id}
                              onChange={() =>
                                setSignIn((prev) => ({ ...prev, keyWhere: { ...prev.keyWhere, [st.site]: o.id } }))
                              }
                              disabled={!canEdit}
                            />
                            {o.label}
                          </label>
                        ))}
                      </div>
                      {/* THIS SITE'S SCRIPT ADDRESS (1.448.0): the two sites'
                          servers need not be arranged alike. */}
                      <label style={{ display: "grid", gap: 2 }}>
                        <span style={{ opacity: 0.7 }}>
                          The script&rsquo;s address on this site &mdash; empty if the page should not ask this site
                          for a session
                        </span>
                        <input
                          type="text"
                          value={signIn.path[st.site]}
                          placeholder="Nothing generated for this site"
                          aria-label={`The script's address on your ${st.site} site`}
                          onChange={(e) =>
                            setSignIn((prev) => ({ ...prev, path: { ...prev.path, [st.site]: e.target.value.trim() } }))
                          }
                          disabled={!canEdit}
                          style={{ fontFamily: "ui-monospace, monospace", fontSize: 12 }}
                        />
                      </label>
                      {/* HOW THIS SITE SIGNS A VISITOR IN (1.457.0, three ways
                          since 1.458.0): their own page, the sign-in Quick
                          Start writes, or a handler they write themselves. */}
                      <div role="radiogroup" aria-label={`How your ${st.site} site signs a visitor in`} style={{ display: "grid", gap: 2 }}>
                        <span style={{ opacity: 0.7 }}>Signed out, asking to take part sends them to</span>
                        {[
                          { id: "page", label: "My own sign-in page" },
                          { id: "form", label: "A sign-in on this page, with accounts Quick Start writes" },
                          { id: "own", label: "Nothing — I'll write the handler myself" },
                        ].map((o) => (
                          <label key={o.id} style={{ display: "flex", gap: 4, alignItems: "center" }}>
                            <input
                              type="radio"
                              name={`quick-start-sign-in-kind-${st.site}`}
                              checked={signInKindFor(st.site) === o.id}
                              onChange={() =>
                                setSignIn((prev) => ({ ...prev, signInKind: { ...prev.signInKind, [st.site]: o.id } }))
                              }
                              disabled={!canEdit}
                            />
                            {o.label}
                          </label>
                        ))}
                      </div>
                      {signInKindFor(st.site) === "page" && (
                        <label style={{ display: "grid", gap: 2 }}>
                          <span style={{ opacity: 0.7 }}>Your sign-in page on this site</span>
                          <input
                            type="text"
                            value={signIn.signInUrl[st.site]}
                            placeholder="/login (or https://accounts.your-site.com/login)"
                            aria-label={`Your sign-in page on your ${st.site} site`}
                            onChange={(e) =>
                              setSignIn((prev) => ({
                                ...prev,
                                signInUrl: { ...prev.signInUrl, [st.site]: e.target.value.trim() },
                              }))
                            }
                            disabled={!canEdit}
                            style={{ fontFamily: "ui-monospace, monospace", fontSize: 12 }}
                          />
                        </label>
                      )}
                      {signInKindFor(st.site) === "form" && (
                        <span style={{ opacity: 0.8 }}>
                          Generate writes a sign-in and sign-up form into the page, and two more files for this
                          site&rsquo;s server: <code>comps-auth.php</code> and <code>comps-accounts.php</code>, which keep
                          the accounts in a file with PHP&rsquo;s own password hashing. A starting point for a site with
                          no accounts yet &mdash; no email confirmation, no password reset. Its visitors are signed in
                          from those accounts, so nothing below needs filling in.
                        </span>
                      )}
                      {signInKindFor(st.site) === "own" && (
                        <span style={{ opacity: 0.8 }}>
                          The generated handler leaves a signed-out visitor signed out and says so in the browser&rsquo;s
                          console. Replace <code>comps589RequestSignIn()</code> with your own, returning the session
                          token (or a promise of one).
                        </span>
                      )}
                      {!sessionPathFor(st.site) && (
                        <span style={{ opacity: 0.8 }}>
                          Empty: no session script is generated for this site and the page does not ask it for one. The
                          page itself is still generated, and still uploads. Give an address if you want a session &mdash;
                          anything that returns the token works, in any language; the PHP script below is one way of
                          writing it.
                        </span>
                      )}
                      {/* YOUR SIGNED-IN VISITOR, PER SITE (1.447.0, per site
                          1.449.0): saved here, so Quick Start can write
                          comps_current_user() into this site's script. */}
                      <div
                        hidden={signInKindFor(st.site) === "form"}
                        aria-label={`Your signed-in visitor on your ${st.site} site`}
                        style={{ display: "grid", gap: 4, marginTop: 6, borderTop: "1px solid var(--border-color)", paddingTop: 6 }}
                      >
                        <strong style={{ fontSize: 12.5 }}>Your signed-in visitor</strong>
                        <span style={{ opacity: 0.8 }}>
                          How does this site&rsquo;s login record who is signed in? If it keeps their id in PHP&rsquo;s
                          session &mdash; code like <code>$_SESSION['user_id'] = $user-&gt;id;</code> in your login
                          &mdash; give the names and Quick Start writes <code>comps_current_user()</code> for you. Not
                          sure? Search your login code for <code>$_SESSION</code>, or ask whoever built it.
                        </span>
                        <div role="radiogroup" aria-label={`Where your ${st.site} site's login keeps the visitor`} style={{ display: "grid", gap: 2 }}>
                          {[
                            { id: "php-session", label: "In PHP's session" },
                            { id: "none", label: "Somewhere else — I'll write comps_current_user() myself" },
                          ].map((o) => (
                            <label key={o.id} style={{ display: "flex", gap: 4, alignItems: "center" }}>
                              <input
                                type="radio"
                                name={`quick-start-user-lookup-${st.site}`}
                                checked={lookupDraft[st.site].kind === o.id}
                                onChange={() => setLookupDraft((d) => ({ ...d, [st.site]: { ...d[st.site], kind: o.id } }))}
                                disabled={!canEdit}
                              />
                              {o.label}
                            </label>
                          ))}
                        </div>
                        {lookupDraft[st.site].kind === "php-session" ? (
                          [
                            { key: "idKey", label: "The session key holding their id (required)", placeholder: "user_id" },
                            { key: "nameKey", label: "The key holding their display name (optional)", placeholder: "username" },
                            { key: "sessionName", label: "Session cookie name, if this site sets its own (optional)", placeholder: "PHPSESSID" },
                          ].map((f) => (
                            <label key={f.key} style={{ display: "grid", gap: 2 }}>
                              <span style={{ opacity: 0.7 }}>{f.label}</span>
                              <input
                                type="text"
                                value={lookupDraft[st.site][f.key]}
                                placeholder={f.placeholder}
                                aria-label={`${f.label} on your ${st.site} site`}
                                onChange={(e) =>
                                  setLookupDraft((d) => ({ ...d, [st.site]: { ...d[st.site], [f.key]: e.target.value.trim() } }))
                                }
                                disabled={!canEdit}
                                style={{ fontFamily: "ui-monospace, monospace", fontSize: 12 }}
                              />
                            </label>
                          ))
                        ) : (
                          <span style={{ opacity: 0.8 }}>
                            WordPress, a framework or your own sessions: <code>example-token.php</code> in the examples
                            download (Partners &rarr; Integration) shows how to write <code>comps_current_user()</code>.
                          </span>
                        )}
                        <button
                          type="button"
                          onClick={() => saveSite(st.site)}
                          disabled={!canEdit || lookupBusy === st.site}
                          style={{ justifySelf: "start" }}
                        >
                          {lookupBusy === st.site ? "Saving…" : "Save this site"}
                        </button>
                        <span style={{ opacity: 0.7 }}>
                          Saves this card: where the key is kept, the script&rsquo;s address and your signed-in visitor.
                        </span>
                      </div>
                    </>
                  )}
                </div>
              </div>
            ))}
          </QuickStartSection>

          {/* SERVER CONFIGURATION (1.464.0). One site's form at a time,
              behind tabs, rather than two long forms one under the other.
              Its 1.464.0 decision - "FTP only, the Signing visitors in
              cards stay where they are" - held until 23 Sep 2026, when
              those cards were asked into Widget Configuration instead.
              This section is still FTP only; what changed is where the
              cards it excluded now live. */}
          <QuickStartSection
            id="server-configuration"
            title="Server Configuration"
            blurb="Where to upload the files: your site's FTP details. Without these you can still download the ZIP and upload it yourself."
            note={ftpSavedNote}
            open={!!openSection["server-configuration"]}
            onToggle={() => toggleSection("server-configuration")}
          >
            <TabBar
              tabs={quickStartSites(savedLayout?.domains)
                .filter((st) => st.separate)
                .map((st) => ({ id: st.site, label: st.domain ? `${st.label} — ${st.domain}` : st.label }))}
              activeId={serverTab}
              onSelect={setServerTab}
            />
            {quickStartSites(savedLayout?.domains)
              .filter((st) => st.separate && st.site === serverTab)
              // PUBLISHING BY FTP (1.460.0): asked for so the files can go
              // straight to the site. The password is kept encrypted on
              // this server and never sent back.
              .map((st) => (
                  <div
                    key={st.site}
                    data-ftp-card={st.site}
                    aria-label={`FTP for your ${st.site} site`}
                    style={{ ...QUICK_START_CARD, display: "grid", gap: 6, marginTop: 10, fontSize: 12.5 }}
                  >
                    <strong style={{ fontSize: 13 }}>Publishing to your {st.label} site by FTP</strong>
                    <span style={{ opacity: 0.8 }}>
                      With these saved, <em>Publish via FTP ({st.label})</em> uploads this site&rsquo;s files for you.
                      SFTP is not supported.
                    </span>
                    {[
                      { key: "host", label: "Host", placeholder: "ftp.your-site.com" },
                      { key: "port", label: "Port", placeholder: "21" },
                      { key: "user", label: "User name", placeholder: "u123456" },
                      { key: "dir", label: "Folder to upload into", placeholder: "public_html" },
                    ].map((f) => (
                      <label key={f.key} style={{ display: "grid", gap: 2 }}>
                        <span style={{ opacity: 0.7 }}>{f.label}</span>
                        <input
                          type="text"
                          value={ftpDraft[st.site][f.key]}
                          placeholder={f.placeholder}
                          aria-label={`FTP ${f.label} for your ${st.site} site`}
                          onChange={(e) =>
                            setFtpDraft((d) => ({ ...d, [st.site]: { ...d[st.site], [f.key]: e.target.value.trim() } }))
                          }
                          disabled={!canEdit}
                          style={{ fontFamily: "ui-monospace, monospace", fontSize: 12 }}
                        />
                      </label>
                    ))}
                    <label style={{ display: "grid", gap: 2 }}>
                      <span style={{ opacity: 0.7 }}>
                        Password{ftpFor(st.site)?.passwordSet ? " — saved; leave empty to keep it" : ""}
                      </span>
                      <input
                        type="password"
                        value={ftpDraft[st.site].password}
                        autoComplete="new-password"
                        aria-label={`FTP password for your ${st.site} site`}
                        onChange={(e) =>
                          setFtpDraft((d) => ({ ...d, [st.site]: { ...d[st.site], password: e.target.value } }))
                        }
                        disabled={!canEdit}
                        style={{ fontFamily: "ui-monospace, monospace", fontSize: 12 }}
                      />
                    </label>
                    <div role="radiogroup" aria-label={`FTP security for your ${st.site} site`} style={{ display: "grid", gap: 2 }}>
                      {[
                        {
                          id: "explicit",
                          label: "FTPS — TLS after connecting (AUTH TLS, usually port 21)",
                        },
                        {
                          id: "implicit",
                          label: "FTPS — TLS from the first byte (usually port 990)",
                        },
                        { id: "plain", label: "Plain FTP — the password crosses the network in the clear" },
                      ].map((o) => (
                        <label key={o.id} style={{ display: "flex", gap: 4, alignItems: "center" }}>
                          <input
                            type="radio"
                            name={`quick-start-ftp-secure-${st.site}`}
                            checked={
                              (ftpDraft[st.site].secure ? (ftpDraft[st.site].implicit ? "implicit" : "explicit") : "plain") === o.id
                            }
                            onChange={() =>
                              setFtpDraft((d) => ({
                                ...d,
                                [st.site]: {
                                  ...d[st.site],
                                  secure: o.id !== "plain",
                                  implicit: o.id === "implicit",
                                  // The port follows the choice while it is
                                  // still one of the two defaults (1.461.0).
                                  port: ["21", "990"].includes(d[st.site].port)
                                    ? o.id === "implicit"
                                      ? "990"
                                      : "21"
                                    : d[st.site].port,
                                },
                              }))
                            }
                            disabled={!canEdit}
                          />
                          {o.label}
                        </label>
                      ))}
                    </div>
                    {ftpDraft[st.site].secure && (
                      <label style={{ display: "flex", gap: 4, alignItems: "center" }}>
                        <input
                          type="checkbox"
                          checked={ftpDraft[st.site].allowSelfSigned}
                          onChange={(e) =>
                            setFtpDraft((d) => ({ ...d, [st.site]: { ...d[st.site], allowSelfSigned: e.target.checked } }))
                          }
                          disabled={!canEdit}
                        />
                        Accept this host&rsquo;s own certificate, even if it is self-signed
                      </label>
                    )}
                    {/* A HOST THAT REFUSES TLS ON ITS DATA CONNECTIONS
                        (1.461.5). Found by the data-connection probe on
                        the real host, 22 Sep 2026: the login worked over
                        FTPS and every data connection with TLS was reset,
                        while the same one without it listed the folder. */}
                    {ftpDraft[st.site].secure && (
                      <label style={{ display: "flex", gap: 6, alignItems: "flex-start" }}>
                        <input
                          type="checkbox"
                          data-ftp-plain-data={st.site}
                          checked={ftpDraft[st.site].protectData === false}
                          onChange={(e) =>
                            setFtpDraft((d) => ({ ...d, [st.site]: { ...d[st.site], protectData: !e.target.checked } }))
                          }
                          disabled={!canEdit}
                        />
                        <span>
                          This host refuses TLS on data connections &mdash; send the files without it.
                          <span style={{ opacity: 0.75 }}>
                            {" "}
                            The login stays encrypted, so your password never crosses in the clear; the file
                            contents do. These are pages that will be public anyway, and 589comps-config.php
                            &mdash; the one file with your key in it &mdash; is never published by FTP. Only tick
                            this if Test connection says the host refuses TLS there.
                          </span>
                        </span>
                      </label>
                    )}
                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                      <button type="button" onClick={() => saveFtp(st.site, false)} disabled={!canEdit || ftpBusyFor(st.site)}>
                        {ftpBusy === `${st.site}-save` ? "Saving…" : "Save FTP details"}
                      </button>
                      <button
                        type="button"
                        onClick={() => testFtp(st.site)}
                        disabled={!canEdit || ftpBusyFor(st.site) || !ftpFor(st.site)}
                      >
                        {ftpBusy === `${st.site}-test` ? "Connecting…" : "Test connection"}
                      </button>
                      {ftpFor(st.site) && (
                        <button type="button" onClick={() => saveFtp(st.site, true)} disabled={!canEdit || ftpBusyFor(st.site)}>
                          Remove
                        </button>
                      )}
                    </div>
                    {ftpNote[st.site] && (
                      <div
                        data-ftp-note={st.site}
                        style={{
                          marginTop: 8,
                          padding: "8px 10px",
                          borderRadius: 6,
                          fontSize: 13,
                          lineHeight: 1.5,
                          border: "1px solid var(--border-color)",
                          color: ftpNote[st.site].ok ? "var(--accent-green, inherit)" : "var(--accent-red, inherit)",
                        }}
                      >
                        {ftpNote[st.site].text}
                      </div>
                    )}
                  </div>
              ))}
            {!quickStartSites(savedLayout?.domains).some((st) => st.separate && st.site === serverTab) && (
              <p style={{ marginTop: 10, fontSize: 12.5, color: "var(--accent-yellow)" }}>
                Your API key has no separate {serverTab} domain, so there is no {serverTab} site to upload to.
              </p>
            )}
          </QuickStartSection>

          {/* GENERATE, WHERE THE CHOOSING ENDS (1.487.0), and the card
              itself rather than a pointer to it. Asked for on 26 Sep
              2026: replace "When you have chosen, Generate, at the foot
              of this page" with the "Generate" card.

              WHY, reported the same day: "The user will get confused
              going to the bottom of the page to click generate then
              having to scroll back up to select something." Worse than a
              scroll, because the three TEMPLATES are chosen from the
              preview, which only exists once something has been
              generated - so the order the page asked for was scroll
              down, press, scroll up, choose, scroll down, press again.
              The button now sits under the last thing it reads, with
              everything it fills in directly below it.

              NOT BY BUILDING AS YOU TICK. That was tried first and
              turned down: "It looks like this is going to be impossible
              without automatically generating without the user clicking
              the generate button. That's not what we want." Generate is
              still a press, and still the only thing that fills the page
              in.

              A CARD SAYING WHAT IT WILL MAKE since 1.478.0, when it was
              asked for at the foot of the page: "in its own card with
              bullet points of everything that clicking the button will
              generate". The card came up here with its bullets; only its
              position changed.

              EVERY BULLET IS READ OFF THE CHOICES, not written out: the
              template, the widgets and fields ticked, each site's script
              by the name that site saved, and the sign-in pair only for a
              site using the one Quick Start writes. A list that describes
              the generator instead of reading from it is a list that goes
              quietly wrong - the same reason the file lists below are
              built from quickStartPlan.

              IT SAYS WHAT IT DOES NOT DO, TOO. Generate reaches nobody's
              server; the four buttons at the foot of the page are the
              ones that do. That is worth a sentence beside a button
              somebody is about to press for the first time.

              NO GATE OF ITS OWN: this sits inside the ViewOnlyGate that
              holds the sections it reads, which disables the button for a
              view-only admin and says so once at the top. */}
          <div
            id="quick-start-generate-card"
            style={{ ...QUICK_START_CARD, display: "block", marginTop: 18 }}
            aria-label="Generate"
          >
            <strong style={{ fontSize: 13 }}>Generate</strong>
            <p style={{ margin: "4px 0 0", fontSize: 12.5, opacity: 0.8 }}>
              Builds every file from the choices above and fills in the rest of this page: the preview, the server
              files and the two lists of what each site gets. Nothing leaves your computer until you press one of
              the four buttons at the foot of the page.
            </p>
            <ul
              data-quick-start-will-make=""
              style={{ margin: "8px 0 0", paddingLeft: 20, fontSize: 12.5, lineHeight: 1.75 }}
            >
              <li>
                <code>
                  {QUICK_START_WEB_ROOT}/{QUICK_START_PAGE_FILE}
                </code>{" "}
                &mdash; your page, with{" "}
                {onPageWidgetNames.length
                  ? // Each name is that widget's own label up to its dash, so
                    // a widget is named in one place - see WIDGETS above.
                    `the ${quickStartAnd(onPageWidgetNames)} widget${onPageWidgetNames.length > 1 ? "s" : ""} on it`
                  : "no widgets ticked yet"}
                , {pickedFieldCount} of {totalFieldCount} fields,{" "}
                {pageStyle === "none" ? (
                  <>and no styling of ours.</>
                ) : (
                  <>
                    in the <strong>{QUICK_START_STYLE_LABELS[pageStyle]}</strong> template.
                  </>
                )}
              </li>
              <li>
                {quickStartPageCss(pageStyle) ? (
                  <>
                    <code>
                      {QUICK_START_WEB_ROOT}/{QUICK_START_CSS_FILE}
                    </code>{" "}
                    &mdash; the {pageStyle} look, linked by the page with a <code>?v=</code> that changes only when
                    the file does.
                  </>
                ) : (
                  <>
                    <strong>No stylesheet.</strong> The unstyled template carries no look of ours, so there is no{" "}
                    <code>{QUICK_START_CSS_FILE}</code> to send.
                  </>
                )}
              </li>
              {WIDGETS.filter((w) => onPage(w.id)).length
                ? quickStartSites(savedLayout?.domains)
                    .filter((st) => st.separate)
                    .map((st) =>
                      sessionPathFor(st.site) ? (
                        <li key={st.site}>
                          <code>
                            {QUICK_START_WEB_ROOT}
                            {sessionPathFor(st.site)}
                          </code>{" "}
                          &mdash; the session script for your {st.site} site
                          {signInKindFor(st.site) === "form" ? (
                            <>
                              , with <code>comps-auth.php</code> and <code>comps-accounts.php</code> beside it &mdash;
                              the sign-in this page uses and the accounts behind it
                            </>
                          ) : null}
                          .
                        </li>
                      ) : (
                        <li key={st.site}>
                          <strong>Nothing for your {st.site} site.</strong> It has no script address saved under Server
                          Configuration, so the page there never asks it for a session.
                        </li>
                      )
                    )
                : null}
              <li>
                <code>589comps-config.php</code> &mdash; where your API key goes if your host cannot set an
                environment variable. In the zip, and <strong>never sent by a publish</strong>: it is the one file you
                edit.
              </li>
              <li>
                <code>README.txt</code> &mdash; in the zip only: where each file goes, and the order the key is looked
                for.
              </li>
            </ul>
            <div style={{ marginTop: 12 }}>
              <button
                type="button"
                data-quick-start-generate=""
                onClick={() => generate()}
                disabled={!canEdit || publishing}
              >
                Generate
              </button>
            </div>
            {/* THE PAGE SAYS WHEN IT IS BEHIND (1.488.0). Everything below
                this card is the build from the last press, and so is what
                the four buttons send - they agree with each other, which
                is why nothing here claims the wrong files are being sent.
                What was missing is that an old preview looks new. */}
            {behindChoices && (
              <p
                data-quick-start-behind=""
                style={{ margin: "10px 0 0", fontSize: 12.5, color: "var(--accent-yellow)" }}
              >
                <strong>Your choices have changed since this was generated.</strong> The preview and the two lists
                below show the earlier build, and that is what the four buttons at the foot of the page would send.
                Press Generate to bring them up to date.
              </p>
            )}
          </div>
        </ViewOnlyGate>
      )}

      {output && offCount > 0 && (
        <p style={{ fontSize: 12.5, color: "var(--accent-yellow)", marginTop: 14 }}>
          {offCount} ticked {offCount === 1 ? "field is" : "fields are"} switched
          off in the API. The generated code reads {offCount === 1 ? "it" : "them"}{" "}
          and will render nothing until {offCount === 1 ? "it is" : "they are"}{" "}
          enabled under Partners &rarr; API.
        </p>
      )}

      {/* ONE BOX (1.441.0). Asked on 22 Sep 2026: "Can we combine these
          two boxes so that the javascript in the bottom box is included in
          the first box". The script sits in a <script> after the widget
          files it uses, so the block is pasted into the page as it is. */}
      {output && (
        <div style={{ marginTop: 14, marginBottom: 16 }}>
          {/* THE THREE TEMPLATES, SHOWN (1.464.0). Asked for on 23 Sep
              2026: "a Preview for the 3 templates ... with a Use this one
              button under each tab, which updates the file list."
              LOOKING IS NOT CHOOSING: the tabs move previewStyle, and only
              Use this one moves pageStyle — the one the zip packs and the
              publish uploads. Before this the tab itself was the choice,
              so glancing at the dark page changed what would be sent. */}
          <div style={{ marginTop: 6 }} aria-label="How the page looks">
            <strong style={{ fontSize: 13 }}>Preview</strong>
            <div style={{ marginTop: 6 }}>
              <TabBar
                tabs={[
                  { id: "none", label: "No styling" },
                  { id: "light", label: "Light" },
                  { id: "dark", label: "Dark" },
                ]}
                activeId={previewStyle}
                onSelect={setPreviewStyle}
              />
            </div>
            {/* PREVIEW OR HTML (1.467.0), under whichever template is
                being looked at. The HTML here is that template's, not the
                chosen one's - the point of the sub-tab is to read the
                page you are considering. */}
            <div style={{ marginTop: 8, display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }} aria-label="Preview or HTML">
              <TabBar
                tabs={[
                  { id: "preview", label: "Preview" },
                  { id: "html", label: "HTML" },
                ]}
                activeId={previewView}
                onSelect={setPreviewView}
              />
              {previewView === "html" && (
                <>
                  <button
                    type="button"
                    onClick={() => navigator.clipboard?.writeText((output.pages || {})[previewStyle] || "")}
                  >
                    Copy
                  </button>
                  {/* THE SAME PAGE ON BOTH SITES (1.452.0). It reads the
                      host it is on and asks that site's own script, so
                      there is one page to copy, not one per site. */}
                  <span style={{ fontSize: 12.5, opacity: 0.8 }}>
                    {(output.sites || []).length > 1
                      ? "One page for both sites — it asks whichever session script belongs to the site it is on. Upload the same file to each."
                      : "Your live site's page."}
                  </span>
                </>
              )}
            </div>
            {/* THE PAGE ITSELF, WITH ITS SCRIPTS NOT RUN (sandbox=""). The
                widgets fetch from this site and ask the partner's own
                server for a session, neither of which means anything in an
                admin panel — and what differs between the three is the
                stylesheet in the head, which renders without them. */}
            {previewView === "preview" ? (
              <iframe
                data-quick-start-preview={previewStyle}
                title={`Preview — ${QUICK_START_STYLE_LABELS[previewStyle]}`}
                srcDoc={(output.previews || output.pages || {})[previewStyle] || ""}
                sandbox=""
                style={{
                  width: "100%",
                  height: 560,
                  border: "1px solid var(--border-color)",
                  borderRadius: 8,
                  background: "#fff",
                }}
              />
            ) : (
              <textarea
                readOnly
                data-quick-start-preview-html={previewStyle}
                value={(output.pages || {})[previewStyle] || ""}
                rows={26}
                aria-label={`HTML — ${QUICK_START_STYLE_LABELS[previewStyle]}`}
                style={{ width: "100%", fontFamily: "ui-monospace, monospace", fontSize: 12.5 }}
              />
            )}
            <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", marginTop: 8 }}>
              <button
                type="button"
                data-quick-start-use-style={previewStyle}
                onClick={() => setPageStyle(previewStyle)}
                disabled={pageStyle === previewStyle}
              >
                {pageStyle === previewStyle ? "In use" : "Use this one"}
              </button>
              <span style={{ fontSize: 12.5, opacity: 0.8 }}>
                Showing <strong>{QUICK_START_STYLE_LABELS[previewStyle]}</strong>. The zip and the publish send{" "}
                <strong>{QUICK_START_STYLE_LABELS[pageStyle]}</strong>.
              </span>
            </div>
            <p style={{ margin: "6px 0 0", fontSize: 12.5, opacity: 0.75 }}>
              {previewStyle === "none"
                ? "The markup on its own: every class and layout choice is yours."
                : `A ${previewStyle} stylesheet in the page's head, for the same markup — a starting point to change or delete. The widgets keep their own colours, set under Widget Configuration.`}{" "}
              The preview does not run the page&rsquo;s scripts, so the widgets fill themselves in on your site rather
              than here.
            </p>
            {/* YOUR OWN STYLING (1.592.0). Asked for on 4 Oct 2026: "if the
                partner wants their own styling on their template they should
                be able to do it ... a link to their css file which can be
                used to override anything the quick start page uses to style
                their template."

                IT SITS HERE, under the page being chosen, because that is
                what it overrides - not under Widget Configuration, which is
                about the widgets' own colours and is a different argument. */}
            <div style={{ marginTop: 14, paddingTop: 12, borderTop: "1px solid var(--border-color)" }}>
              <div style={{ fontSize: 12, opacity: 0.7, marginBottom: 6 }}>Your own styling</div>
              <div role="radiogroup" aria-label="Your own styling" style={{ display: "flex", gap: 16, flexWrap: "wrap", alignItems: "center" }}>
                {[
                  { id: "none", label: "None" },
                  { id: "file", label: "A file I link" },
                  { id: "inline", label: "Written into the page" },
                ].map((o) => (
                  <label key={o.id} style={{ display: "inline-flex", gap: 6, alignItems: "center", fontSize: 13 }}>
                    <input
                      type="radio"
                      name="quick-start-own-css"
                      data-quick-start-own-css={o.id}
                      checked={ownCss.mode === o.id}
                      disabled={!canEdit}
                      onChange={() => {
                        setOwnCssOption("mode", o.id);
                        // PREFILLED, BECAUSE AN EMPTY BOX TEACHES NOTHING.
                        // Only when it is empty: a partner who has written
                        // their own and looks at the other option must not
                        // come back to find it replaced.
                        if (o.id === "inline" && !ownCss.css.trim()) setOwnCssOption("css", ownCssSkeleton());
                      }}
                    />
                    {o.label}
                  </label>
                ))}
              </div>
              {ownCss.mode === "file" && (
                <div style={{ marginTop: 10 }}>
                  <label style={{ display: "block", fontSize: 12.5, opacity: 0.8, marginBottom: 4 }}>
                    Linked after <code>{QUICK_START_CSS_FILE}</code>, so it wins
                  </label>
                  <input
                    type="text"
                    data-quick-start-own-css-href=""
                    value={ownCss.href}
                    placeholder={ownCssFile}
                    disabled={!canEdit}
                    onChange={(e) => setOwnCssOption("href", e.target.value)}
                    style={{ width: "100%", maxWidth: 420, fontFamily: "ui-monospace, monospace", fontSize: 12.5 }}
                  />
                  <p style={{ margin: "6px 0 0", fontSize: 12.5, opacity: 0.75, lineHeight: 1.5 }}>
                    Left empty it is <code>{ownCssFile}</code>, named after your site, and the zip
                    carries a copy with every rule on the page listed and empty for you to fill in.
                    A publish never sends it, so your edits cannot be written over from here.
                  </p>
                </div>
              )}
              {ownCss.mode === "inline" && (
                <div style={{ marginTop: 10 }}>
                  <textarea
                    data-quick-start-own-css-text=""
                    value={ownCss.css}
                    rows={14}
                    disabled={!canEdit}
                    aria-label="Your own CSS, written into the page"
                    onChange={(e) => setOwnCssOption("css", e.target.value)}
                    style={{ width: "100%", fontFamily: "ui-monospace, monospace", fontSize: 12.5 }}
                  />
                  <p style={{ margin: "6px 0 0", fontSize: 12.5, opacity: 0.75, lineHeight: 1.5 }}>
                    Written into the page after the stylesheet, so it wins. Every rule starts empty:
                    fill in what you want to change and delete the rest. Generating again rewrites{" "}
                    <code>{QUICK_START_CSS_FILE}</code> but keeps what you type here.
                  </p>
                </div>
              )}
              <p style={{ margin: "8px 0 0", fontSize: 12.5, opacity: 0.65, lineHeight: 1.5 }}>
                This styles the markup on your page. The two widgets carry their own colours under
                Widget Configuration &mdash; set Colours to None there and their{" "}
                <code>rcw-</code> classes are yours too, though a widget adds its stylesheet when it
                starts, which is after yours loads, so a rule aimed at one of those has to out-rank
                it rather than merely follow it.
              </p>
            </div>
            {/* A DARK PAGE WITH LIGHT WIDGETS LOOKS BROKEN (1.461.7), and
                the two settings are deliberately separate - so say which
                one to change rather than quietly changing it. Keyed on the
                page that will be SENT, not the one being looked at. */}
            {pageStyle !== "none" && styleMismatches.length > 0 && (
              <p
                data-quick-start-colour-mismatch=""
                style={{ fontSize: 12.5, marginTop: 6, color: "var(--accent-yellow)" }}
              >
                {styleMismatches.join(" and ")} {styleMismatches.length > 1 ? "are" : "is"} still set to the other
                colours. {styleMismatches.length > 1 ? "They" : "It"} will not match this page &mdash; and where your
                own CSS sets a background without also setting a text colour, the other theme&rsquo;s ink lands on
                it and can come out unreadable rather than merely different. Change Colours under Widget
                Configuration, or pick None there and style them yourself.
              </p>
            )}
            {/* NOT A MATTER OF TASTE, ON A SAVED TEMPLATE (1.489.0). The
                note above reads as "these two will look different"; with
                a template the widget is drawing the partner's own
                sections, and the wrong colour there is text nobody can
                read. Said separately rather than by rewording the first,
                because both are true and only this one is urgent. */}
            {pageStyle !== "none" && widgetPaintsYourTemplate && (
              <p
                data-quick-start-template-colours=""
                style={{ fontSize: 12.5, marginTop: 6, color: "var(--accent-yellow)" }}
              >
                <strong>On your saved layout that is worse than a mismatch.</strong> The widget draws your own
                sections inside itself, so its colours land on your markup too &mdash; anything in it without a
                colour of its own is written in the other theme&rsquo;s ink on this page&rsquo;s background, which
                can come out unreadable rather than merely different. <strong>Colours: None</strong> cannot do
                this: the widget then sets no colours and everything follows your page.
              </p>
            )}
          </div>
        </div>
      )}

      <div id="widget-layout-editor" style={{ marginTop: 24 }}>
        <WidgetLayoutEditor token={token} canEdit={canEdit} onSaved={loadSavedLayout} />
      </div>

      <QuickStartAccountNavigation
        style={navStyle}
        setStyle={setNavStyle}
        theme={navTheme}
        setTheme={setNavTheme}
        menu={navMenu}
        setMenu={setNavMenu}
        canEdit={canEdit}
      />

      {/* WHAT IS SENT, AT THE FOOT OF THE PAGE (1.477.0).
          Asked for on 26 Sep 2026, with a screenshot of the button row:
          "Can we move the publish and download buttons to the bottom of
          the quick start page? After the 'Account navigation widget'
          section (which should have its own card) show the two 'The
          server files, in full' then under that show the two 'what gets
          sent' cards then at the very bottom show the 4 buttons."
          SO THE ORDER IS THE ORDER OF THE WORK: choose, look at the page,
          read what each site will be sent, then send it. The buttons used
          to sit above everything they acted on, which meant pressing one
          and scrolling down to find out what it had done. */}
      {output && (
        <>
          {/* THE SERVER'S FILES (1.442.0): one box per file, under the tab
              for the site they go on (1.452.0). In a card of its own since
              1.477.0, with the heading above the tab rather than beside
              the boxes - a card with nothing naming it reads as part of
              whatever is above it. */}
          {/* COLLAPSED UNTIL ASKED FOR (1.503.0). Asked on 27 Sep 2026:
              "There could be a large block here depending on what's been
              configured and we should only show it if the user wants it
              shown." Every file's full contents, one box each, for one or
              two sites - it is the longest thing on the page and it is
              reference material, not a step.

              QuickStartSection rather than a collapse of its own: this
              page already has five of them and a sixth that behaved
              differently would be the odd one out. openSection starts
              empty, so shut is the default without a special case. */}
          <QuickStartSection
            id="server-files"
            title="The server files, in full"
            blurb="Every file's contents, to copy by hand if you are not publishing over FTP."
            note={serverFileNote}
            open={!!openSection["server-files"]}
            onToggle={() => toggleSection("server-files")}
          >
            {/* ONE SITE'S AT A TIME (1.452.0): the tab says whose. The
                heading is the card's now, so this is the tab bar alone. */}
            {(output.sites || []).length > 1 && (
              <div style={{ marginTop: 6 }} aria-label="Which site's files">
                <TabBar
                  tabs={output.sites.map((st) => ({
                    id: st.site,
                    label: st.domain ? `${st.label} — ${st.domain}` : st.label,
                  }))}
                  activeId={outputSite}
                  onSelect={setOutputSite}
                />
              </div>
            )}
            {/* A SITE WITH NO SCRIPT HAS NO FILES (1.454.0): say so under
                its tab, rather than showing an empty space. */}
            {(output.sites || []).length > 1 &&
              !(output.files || []).some((f) => f.site === outputSite) && (
                <p style={{ fontSize: 12.5, marginTop: 12, color: "var(--accent-yellow)" }}>
                  No server file for this site: its address is empty, so the page does not ask it for a session. The
                  page above is still this site&rsquo;s page &mdash; the download and the publish send it.
                </p>
              )}
            {(output.files || [])
              .filter((f) => (output.sites || []).length < 2 || f.site === outputSite)
              .map((f) => (
                <div key={`${f.site}-${f.name}`} style={{ marginTop: 16 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                    <strong style={{ fontSize: 13 }}>
                      {f.heading} &mdash; {f.name}
                    </strong>
                    <button type="button" onClick={() => navigator.clipboard?.writeText(f.body)}>
                      Copy
                    </button>
                    <span style={{ fontSize: 12.5, opacity: 0.75 }}>{f.where}</span>
                  </div>
                  <textarea
                    readOnly
                    value={f.body}
                    rows={f.name === "589comps-config.php" ? 9 : 24}
                    aria-label={`Generated ${f.name} for the ${f.site} site`}
                    style={{ width: "100%", marginTop: 6, fontFamily: "ui-monospace, monospace", fontSize: 12.5 }}
                  />
                </div>
              ))}
          </QuickStartSection>
          {/* WHAT WILL BE SENT (1.464.0). Asked for on 23 Sep 2026: "on
              generate, list all files that will be zipped/FTP'd", as two
              lists, Live and Test. Every row comes from quickStartPlan,
              which is also what the zip and the publish are built from, so
              the list cannot promise a file that is not sent. */}
          <div
            data-quick-start-file-lists=""
            style={{
              marginTop: 18,
              display: "grid",
              gap: 14,
              gridTemplateColumns: "repeat(auto-fit, minmax(330px, 1fr))",
            }}
          >
            {quickStartSites(savedLayout?.domains).map((st) => {
              const plan = st.separate ? quickStartPlan(output, st.site) : null;
              return (
                <div key={st.site} data-quick-start-file-list={st.site} style={QUICK_START_CARD}>
                  <strong style={{ fontSize: 13 }}>{st.label} &mdash; what gets sent</strong>
                  {!plan ? (
                    <p style={{ margin: "6px 0 0", fontSize: 12.5, color: "var(--accent-yellow)" }}>
                      Your API key has no separate {st.site} domain, so there is nothing to send to a {st.site} site.
                    </p>
                  ) : (
                    <>
                      <p style={{ margin: "4px 0 8px", fontSize: 12.5, opacity: 0.8 }}>
                        {st.domain ? <code>{st.domain}</code> : "No domain on the key yet"} &middot; zip:{" "}
                        <code>{plan.zipName}</code>
                      </p>
                      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12.5 }}>
                        <thead>
                          <tr>
                            <th style={{ textAlign: "left", padding: "3px 6px 3px 0", opacity: 0.65, fontWeight: 400 }}>
                              In the zip, at
                            </th>
                            <th style={{ textAlign: "left", padding: "3px 0", opacity: 0.65, fontWeight: 400 }}>
                              Sent by FTP
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {plan.all.map((e) => (
                            <tr key={e.zipPath} data-quick-start-file={e.zipPath}>
                              <td style={{ padding: "3px 6px 3px 0", fontFamily: "ui-monospace, monospace" }}>
                                {e.zipPath}
                              </td>
                              <td style={{ padding: "3px 0", opacity: e.publishName ? 1 : 0.7 }}>
                                {e.publishName ? "yes" : "no"}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      <p style={{ margin: "8px 0 0", fontSize: 12, opacity: 0.75 }}>
                        589comps-config.php holds your API key, so a publish never sends it &mdash; a second publish
                        would write over the key you pasted in. README.txt is for you, not the server.
                      </p>
                      {!ftpFor(st.site) && (
                        <p style={{ margin: "6px 0 0", fontSize: 12, color: "var(--accent-yellow)" }}>
                          No FTP details for this site yet, so only the zip is available. Server Configuration, above.
                        </p>
                      )}
                    </>
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}

      {/* AND THE FOUR THAT SEND, LAST (1.477.0). Each names its site, and
          sits under the list of exactly what that site will be sent -
          which is the order somebody actually works in. They build the
          files first if Generate has not been pressed, so they are here
          whether or not anything has been generated yet.

          THE GATE WITHOUT ITS NOTICE: the sections above carry one
          already, and a second saying the same thing at the bottom of the
          page is noise. The fieldset still disables every button. */}
      <ViewOnlyGate canEdit={canEdit}>
        <div
          data-quick-start-actions=""
          style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center", margin: "18px 0 4px" }}
        >
          {quickStartSites(savedLayout?.domains).flatMap((st) => [
            <button
              key={`${st.site}-publish`}
              type="button"
              data-quick-start-publish={st.site}
              onClick={() => publishViaFtp(st.site)}
              disabled={!canEdit || publishing || !st.separate || !ftpFor(st.site)}
              title={
                !st.separate
                  ? `Your API key has no separate ${st.site} domain, so there is no ${st.site} site to upload to.`
                  : ftpFor(st.site)
                    ? `Uploads this site's files to ${ftpFor(st.site).host}.`
                    : `No FTP details saved for your ${st.site} site — Server Configuration, above.`
              }
            >
              {ftpBusy === `${st.site}-publish` ? "Uploading…" : `Publish via FTP (${st.label})`}
            </button>,
            <button
              key={`${st.site}-zip`}
              type="button"
              data-quick-start-zip={st.site}
              onClick={() => downloadSiteZip(st.site)}
              disabled={zipBusy || publishing || !st.separate}
              title={
                st.separate
                  ? `Packs this site's files as ${quickStartZipName(st.site, pageStyle)}.`
                  : `Your API key has no separate ${st.site} domain, so there is nothing to pack for one.`
              }
            >
              {zipBusy ? "Packing…" : `Download ZIP (${st.label})`}
            </button>,
          ])}
        </div>
        <p style={{ margin: "6px 0 0", fontSize: 12.5, opacity: 0.8 }}>
          Each sends that site&rsquo;s list above &mdash; over FTP, or as a zip to your computer &mdash; and builds
          the files first if you have not pressed Generate.
        </p>
        {/* AND AGAIN HERE (1.488.0), because this is where the press that
            matters happens and the card is a page away by now. Same
            condition, said shorter. */}
        {behindChoices && (
          <p
            data-quick-start-behind-actions=""
            style={{ margin: "6px 0 0", fontSize: 12.5, color: "var(--accent-yellow)" }}
          >
            These send the build from before your last change to the choices above. Press <strong>Generate</strong>{" "}
            first if that is not what you want.
          </p>
        )}
      </ViewOnlyGate>
    </div>
  );
}

// THE ACCOUNT NAVIGATION WIDGET, PREVIEWED (1.428.0; on the page since
// 1.429.0). Asked on 21 Sep 2026: "Can we package the nav into a widget and
// make it available on the quick start page? … We can show a preview."
// Then: "the entire page doesn't need to be shown in an iframe. Just show
// the previews on the page, also it's not necessary to take up the full
// width of the page. Make it look nice."
//
// THE REAL WIDGET, not a picture of it: /embed/v1/589comps-account.js is
// loaded into this page and mounted in a small mock-up of a partner's site.
// Three of its options make that possible without touching anything else
// here — `container` keeps the modal inside the mock-up, `useHash: false`
// leaves this page's address alone, and `fetch` answers with the made-up
// data below. Saving in the preview changes nothing anywhere.
let accountWidgetScript = null;
function loadAccountWidget() {
  if (window.Comps589Account) return Promise.resolve(window.Comps589Account);
  if (!accountWidgetScript) {
    accountWidgetScript = new Promise((resolve, reject) => {
      const tag = document.createElement("script");
      tag.src = "/embed/v1/589comps-account.js";
      tag.onload = () => (window.Comps589Account ? resolve(window.Comps589Account) : reject(new Error("The widget did not load.")));
      tag.onerror = () => {
        accountWidgetScript = null;
        reject(new Error("Could not load /embed/v1/589comps-account.js."));
      };
      document.head.appendChild(tag);
    });
  }
  return accountWidgetScript;
}

function accountPreviewFetch() {
  const now = Date.now();
  // THE CONSENTS TOO (1.516.0), or the preview shows a partner an Account
  // section missing the two ticks their visitors will actually see: the
  // widget asks /api/v1/next-up for the reminder state and draws no tick
  // when it cannot be asked, which is exactly what a 404 here produced.
  const me = { email: "jordan@example.com", username: "jordan", firstName: "Jordan", defaultSpendAmount: 5, payoutMethod: "paypal", payoutDetails: "", emailUpdatesOptIn: true, legalAgreedAt: Date.now() - 30 * 86400000 };
  // Answered from a local, and changed by the two reminder routes below,
  // so ticking it in the preview behaves as it will on the partner's own
  // page rather than springing back.
  const nextUp = { showCountdown: false, captureEmail: true, next: null, reminderSubscribed: true };
  const DATA = {
    "/api/v1/auth/me": me,
    "/api/v1/next-up": nextUp,
    "/api/v1/auth/my-wins": [{ title: "#391107", prizeMoney: 500, answeredAt: now - 86400000, wonByLottery: false, prizePaid: true }],
    "/api/v1/auth/my-entries": [
      { competitionTitle: "#503881", guess: "a lighthouse", correct: false, createdAt: now - 3600000 },
      { competitionTitle: "#391107", guess: "a sunset", correct: true, createdAt: now - 90000000 },
    ],
    "/api/v1/auth/my-payments": [
      { competitionTitle: "#503881", payments: [{ type: "pixel_purchase", amount: 12.5, currency: "GBP", details: "5x5 area", creditsEarned: 150, createdAt: now - 3000000 }] },
      { competitionId: null, competitionTitle: "Entry credit", payments: [{ type: "credit_topup", amount: 10, currency: "GBP", details: "Top-up", creditsGranted: 50, createdAt: now - 7200000 }] },
    ],
    "/api/v1/auth/my-credits": {
      credits: 46,
      otherModes: [],
      recent: [
        { at: now - 60000, type: "spend", credits: -5, balanceBefore: 51, balanceAfter: 46, competitionTitle: "#503881", reason: "entry", free: true },
        { at: now - 120000, type: "pixel", credits: 0, balanceBefore: 51, balanceAfter: 51, reason: "free pixel", free: true, pixels: 1 },
        { at: now - 2000000, type: "spend", credits: -99, balanceBefore: 150, balanceAfter: 51, competitionTitle: "#503881", reason: "entry", free: false },
        { at: now - 3000000, type: "credit", credits: 150, balanceBefore: 0, balanceAfter: 150, reason: "pixels bought", free: false },
      ],
    },
  };
  return (url, init) => {
    const path = new URL(url).pathname;
    if (path === "/api/v1/reminders/subscribe-as-user" || path === "/api/v1/reminders/unsubscribe-as-user") {
      nextUp.reminderSubscribed = path.endsWith("/subscribe-as-user");
      return Promise.resolve({ ok: true, status: 200, json: () => Promise.resolve({ ok: true }) });
    }
    if (init && init.method === "PATCH") {
      try {
        Object.assign(me, JSON.parse(init.body || "{}"));
      } catch {
        // a preview: nothing to do with a body it cannot read
      }
    }
    const body = DATA[path];
    return Promise.resolve({
      ok: body !== undefined,
      status: body !== undefined ? 200 : 404,
      json: () => Promise.resolve(body !== undefined ? body : { error: "Not available in preview." }),
    });
  };
}

// THE PREVIEW IS A PARTNER'S OWN NAV (1.430.0): six links, the last of
// them "My account", marked with data-589comps-account and set up by
// init(), exactly as the generated code does it. The item keeps the
// mock site's own styling; the plain-list menu is styled by the mock
// site's stylesheet below, not by the widget.
const ACCOUNT_PREVIEW_CSS = `
.qs-acc-site{--site-fg:#1c1e26;--site-dim:#5a5e6b;--site-bar:#fff;--site-line:#dcdee3;--site-hover:#eef1f7}
.qs-acc-site.is-dark{--site-fg:#edeae0;--site-dim:#a6a3b8;--site-bar:#1b1d2c;--site-line:#363a52;--site-hover:#242739}
.qs-acc-site .site-nav{list-style:none;display:flex;margin:0;padding:0}
.qs-acc-site .site-nav>li{position:relative}
.qs-acc-site .site-nav>li>a{display:block;padding:14px 10px;color:var(--site-dim);text-decoration:none;font-size:13px}
.qs-acc-site .site-nav>li>a:hover,.qs-acc-site .site-nav>li:last-child>a{color:var(--site-fg)}
.qs-acc-site .site-nav .c589n-submenu{position:absolute;right:0;top:100%;z-index:2;list-style:none;margin:0;padding:6px 0;min-width:180px;background:var(--site-bar);border:1px solid var(--site-line);border-top:2px solid #2d6cdf}
.qs-acc-site .site-nav .c589n-submenu a{display:block;padding:7px 14px;color:var(--site-fg);text-decoration:none;font-size:13px}
.qs-acc-site .site-nav .c589n-submenu a:hover{background:var(--site-hover)}
`;

function AccountNavPreview({ style, theme, menu }) {
  const siteRef = useRef(null);
  const bodyRef = useRef(null);
  const [error, setError] = useState(null);
  const dark = theme === "dark";

  useEffect(() => {
    let handles = [];
    let cancelled = false;
    loadAccountWidget()
      .then((Comps589Account) => {
        if (cancelled || !siteRef.current || !bodyRef.current) return;
        bodyRef.current.replaceChildren();
        handles = Comps589Account.init({
          root: siteRef.current,
          baseUrl: "https://preview.invalid",
          token: "preview",
          theme,
          fetch: accountPreviewFetch(),
          useHash: false,
          ...(style === "modal" ? { container: bodyRef.current } : {}),
          onLogout: () => {},
        });
        // Opened, so the preview shows it at work.
        handles.forEach((h) => h.open("credits"));
        setError(null);
      })
      .catch((err) => !cancelled && setError(err.message));
    return () => {
      cancelled = true;
      handles.forEach((h) => h.destroy());
    };
  }, [style, theme, menu]);

  const frameBg = dark ? "#1b1d2c" : "#f7f7f9";
  const frameFg = dark ? "#edeae0" : "#1c1e26";
  const frameLine = dark ? "#363a52" : "#dcdee3";
  return (
    <div
      ref={siteRef}
      className={"qs-acc-site" + (dark ? " is-dark" : "")}
      style={{
        width: "100%",
        maxWidth: 660,
        borderRadius: 12,
        overflow: "hidden",
        border: `1px solid ${frameLine}`,
        boxShadow: "0 12px 32px rgba(0,0,0,.18)",
        background: frameBg,
        color: frameFg,
        fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
      }}
    >
      <style>{ACCOUNT_PREVIEW_CSS}</style>
      {/* The browser around it: three dots and an address. */}
      <div style={{ display: "flex", alignItems: "center", gap: 6, padding: "8px 12px", background: dark ? "#14151f" : "#e9eaee" }}>
        {["#ff5f57", "#febc2e", "#28c840"].map((c) => (
          <span key={c} style={{ width: 10, height: 10, borderRadius: "50%", background: c, display: "inline-block" }} />
        ))}
        <span
          style={{
            marginLeft: 10,
            flex: 1,
            fontSize: 11.5,
            padding: "3px 10px",
            borderRadius: 6,
            background: dark ? "#242739" : "#fff",
            color: dark ? "#a6a3b8" : "#6b6f7c",
          }}
        >
          your-site.example
        </span>
      </div>
      {/* The partner's own header and nav; the sixth item is marked. The
          key remounts it, so each choice starts from the partner's own
          untouched markup. */}
      <div
        key={style + theme + menu}
        style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0 12px", background: dark ? "#1b1d2c" : "#fff", borderBottom: `1px solid ${frameLine}` }}
      >
        <strong style={{ fontSize: 14 }}>Your site</strong>
        <ul className="site-nav">
          {["Home", "Games", "Tournaments", "Leaderboard", "Shop"].map((n) => (
            <li key={n}>
              <a href="#" onClick={(e) => e.preventDefault()}>
                {n}
              </a>
            </li>
          ))}
          <li
            data-589comps-account={style}
            {...(style === "header" && menu === "list" ? { "data-589comps-account-menu": "list" } : {})}
          >
            <a href="#" onClick={(e) => e.preventDefault()}>
              My account
            </a>
          </li>
        </ul>
      </div>
      {/* The page. The modal opens inside it; the header style draws here. */}
      <div
        ref={bodyRef}
        {...(style === "header" ? { "data-589comps-account-target": "" } : {})}
        style={{
          position: "relative",
          height: 420,
          overflow: style === "header" ? "auto" : "hidden",
          padding: style === "header" ? "10px 16px" : 0,
          fontSize: 13,
        }}
      />
      {error && <p style={{ color: "var(--accent-red)", fontSize: 12.5, margin: 10 }}>{error}</p>}
    </div>
  );
}

function QuickStartAccountNavigation({ style, setStyle, theme, setTheme, menu, setMenu, canEdit }) {
  const STYLES = [
    {
      id: "modal",
      label: "Modal with tabs",
      about: "One “My account” button in the header. It opens a modal with a tab for each section.",
    },
    {
      id: "header",
      label: "Header",
      about:
        "A “My account” menu in the header. The chosen section is drawn on the page under it, at its own address (#account/credits), so it can be bookmarked and Back works.",
    },
  ];
  const MENUS = [
    { id: "dropdown", label: "Drop-down", about: "The widget’s own drop-down, in the chosen colours." },
    { id: "list", label: "Plain list", about: "A plain list inside the partner’s item, styled only by their own CSS — for a nav that already has drop-downs." },
  ];
  const chosen = STYLES.find((x) => x.id === style) || STYLES[0];
  // A SEGMENTED CHOICE: two or three buttons joined into one control.
  const segmented = (options, value, set, name) => (
    <div role="radiogroup" aria-label={name} style={{ display: "inline-flex", border: "1px solid var(--border-color)", borderRadius: 8, overflow: "hidden" }}>
      {options.map((o, i) => {
        const on = value === o.id;
        return (
          <button
            key={o.id}
            type="button"
            role="radio"
            aria-checked={on}
            onClick={() => set(o.id)}
            disabled={!canEdit}
            style={{
              border: "none",
              borderLeft: i ? "1px solid var(--border-color)" : "none",
              borderRadius: 0,
              margin: 0,
              padding: "7px 14px",
              fontSize: 13,
              background: on ? "var(--accent-blue)" : "transparent",
              color: on ? "#fff" : "inherit",
              cursor: canEdit ? "pointer" : "default",
            }}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
  return (
    // ITS OWN CARD (1.477.0), asked for on 26 Sep 2026. It is a block
    // about one widget, sitting between the layout editor and the files
    // that get sent; without a card of its own it read as the tail of
    // whichever of them happened to be above it.
    <div id="account-navigation-preview" style={{ ...QUICK_START_CARD, display: "block", marginTop: 24 }}>
      <h4 style={{ margin: "0 0 6px" }}>Account navigation widget</h4>
      <div style={{ display: "flex", gap: 28, flexWrap: "wrap", alignItems: "flex-start" }}>
        <div style={{ flex: "1 1 240px", maxWidth: 320, fontSize: 13 }}>
          <p style={{ marginTop: 0, opacity: 0.85, lineHeight: 1.5 }}>
            The visitor&rsquo;s own account &mdash; details, preferences, payment details, winnings,
            competitions, payment history and credit balance &mdash; and the navigation to reach it,
            in one file: <code>589comps-account.js</code>. It takes the same session token as the
            other widgets. There is no password form: a vouched visitor signs in on the
            partner&rsquo;s own site.
          </p>
          <ViewOnlyGate canEdit={canEdit} showNotice>
            <div style={{ display: "grid", gap: 14, margin: "16px 0" }}>
              <div>
                <div style={{ fontSize: 12, opacity: 0.7, marginBottom: 6 }}>Style</div>
                {segmented(STYLES, style, setStyle, "Style")}
              </div>
              {style === "header" && (
                <div>
                  <div style={{ fontSize: 12, opacity: 0.7, marginBottom: 6 }}>Menu</div>
                  {segmented(MENUS, menu, setMenu, "Menu")}
                </div>
              )}
              <div>
                <div style={{ fontSize: 12, opacity: 0.7, marginBottom: 6 }}>Colours</div>
                {segmented(
                  [
                    { id: "light", label: "Light" },
                    { id: "dark", label: "Dark" },
                    // 1.439.0: no colours of ours - the partner's CSS styles it.
                    { id: "none", label: "None" },
                  ],
                  theme,
                  setTheme,
                  "Colours"
                )}
              </div>
            </div>
          </ViewOnlyGate>
          <p style={{ opacity: 0.8, lineHeight: 1.5 }}>
            {chosen.about}
            {style === "header" && <> {(MENUS.find((m) => m.id === menu) || MENUS[0]).about}</>}
          </p>
          <p style={{ fontSize: 12.5, opacity: 0.75, lineHeight: 1.5 }}>
            <strong>Placed on the partner&rsquo;s own &ldquo;My account&rdquo;.</strong> They mark the
            item in their nav with <code>data-589comps-account</code>; it keeps its own text and
            styling, and the widget adds what happens on click. Signed out, choosing it calls their
            sign-in (<code>onLoginRequested</code>), or, without that, their link does what it always did.
          </p>
          <p style={{ fontSize: 12.5, opacity: 0.65, lineHeight: 1.5 }}>
            Tick it under Widgets above and the generated code mounts it with these choices. The
            preview is the real widget with made-up answers; nothing is saved. Colours can also be
            set from the partner&rsquo;s own stylesheet &mdash; see &ldquo;Account navigation&rdquo;
            in the Integration Guide.
          </p>
        </div>
        <div style={{ flex: "2 1 420px", display: "flex", justifyContent: "center" }}>
          <AccountNavPreview style={chosen.id} theme={theme} menu={menu} />
        </div>
      </div>
    </div>
  );
}

